import '@webcomponents/webcomponentsjs/custom-elements-es5-adapter.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import React__default, { createElement } from 'react';
import mergeRefs from 'react-merge-refs';
import { createRoot } from 'react-dom/client';
import DOMPurify from 'dompurify';
import * as ReactDOM from 'react-dom';
import ReactDOM__default, { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useAnimation as useAnimation$1, motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-web';
import ReactResizeDetector, { useResizeDetector } from 'react-resize-detector';
import deepEqual from 'deep-eql';
import arrayMove from 'array-move';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

const XIColors = {
  admin: {
    light: {
      '15': '#296ab6',
      '30': '#5788c4',
      '40': '#719acc',
      '50': '#8bacd5',
      '60': '#a3bdde',
      '70': '#bbcfe6',
      '80': '#d2dfee',
      '90': '#e9eff7',
      '95': '#f4f6fb'
    },
    primary: '#024ea9',
    dark: {
      '15': '#014392',
      '40': '#012e6a',
      '55': '#002252'
    }
  },
  orca: {
    light: {
      '15': '#465562',
      '30': '#66737e',
      '40': '#7c8790',
      '50': '#929ba3',
      '60': '#a8afb5',
      '70': '#bec3c8',
      '80': '#d3d7da',
      '90': '#e9ebed',
      '95': '#f4f5f6'
    },
    primary: '#253746',
    dark: {
      '15': '#1f2f3c',
      '40': '#16212a',
      '55': '#111920'
    }
  },
  grey: {
    light: {
      '15': '#606060',
      '30': '#7c7c7c',
      '40': '#8f8f8f',
      '50': '#a2a2a2',
      '60': '#b4b4b4',
      '70': '#c7c7c7',
      '80': '#dadada',
      '90': '#ececec',
      '95': '#f6f6f6'
    },
    primary: '#444444',
    dark: {
      '15': '#3a3a3a',
      '40': '#292929',
      '55': '#1f1f1f'
    }
  },
  purple: {
    light: {
      '15': '#813fcc',
      '30': '#ae67ff',
      '40': '#ba7dff',
      '50': '#c592ff',
      '60': '#d1a8ff',
      '70': '#dcbeff',
      '80': '#e8d4ff',
      '90': '#f3e9ff',
      '95': '#f9f4ff'
    },
    primary: '#531799',
    dark: {
      '15': '#471482',
      '40': '#320e5c',
      '55': '#250a45'
    }
  },
  blue: {
    light: {
      '15': '#2671ff',
      '30': '#4d8aff',
      '40': '#669bff',
      '50': '#80acff',
      '60': '#99bcff',
      '70': '#b3cdff',
      '80': '#ccdeff',
      '90': '#e6eeff',
      '95': '#f2f7ff'
    },
    primary: '#0058ff',
    dark: {
      '15': '#004bd9',
      '40': '#003599',
      '55': '#002873'
    }
  },
  magenta: {
    light: {
      '15': '#a83877',
      '30': '#b85b8f',
      '40': '#c2739f',
      '50': '#cc8aaf',
      '60': '#d6a1bf',
      '70': '#e0b9cf',
      '80': '#ebd0df',
      '90': '#f5e8ef',
      '95': '#faf3f7'
    },
    primary: '#99155f',
    dark: {
      '15': '#821251',
      '40': '#5c0d39',
      '55': '#45092b'
    }
  },
  green: {
    light: {
      '15': '#198b67',
      '30': '#42a083',
      '40': '#5eaf95',
      '50': '#7abda8',
      '60': '#95cbba',
      '70': '#b1d9cd',
      '80': '#cce8df',
      '90': '#e8f6f2',
      '95': '#f3fbf9'
    },
    primary: '#147255',
    dark: {
      '15': '#116148',
      '40': '#0c4433',
      '55': '#093326'
    }
  },
  teal: {
    light: {
      '15': '#1a9b8a',
      '30': '#1eb6a2',
      '40': '#3ec0af',
      '50': '#5ecbbd',
      '60': '#7ed5ca',
      '70': '#9fe0d7',
      '80': '#bfeae4',
      '90': '#dff5f2',
      '95': '#effaf8'
    },
    primary: '#158072',
    dark: {
      '15': '#126d61',
      '40': '#0d4d44',
      '55': '#093a33'
    }
  },
  yellow: {
    light: {
      '15': '#e67300',
      '30': '#ec8b14',
      '40': '#ef9c21',
      '50': '#f3ac2e',
      '60': '#f6bf5d',
      '70': '#f9d28c',
      '80': '#fbe4bb',
      '90': '#fef7ea',
      '95': '#fffbf4'
    },
    primary: '#b15800',
    dark: {
      '15': '#964b00',
      '40': '#6a3500',
      '55': '#502800'
    }
  },
  red: {
    light: {
      '15': '#d43831',
      '30': '#dc5c56',
      '40': '#e2746f',
      '50': '#e88c88',
      '60': '#eda4a1',
      '70': '#f3bcb9',
      '80': '#f8d4d2',
      '90': '#feeceb',
      '95': '#fff6f5'
    },
    primary: '#ae2e29',
    dark: {
      '15': '#942723',
      '40': '#681c19',
      '55': '#4e1512'
    }
  },
  white: {
    light: {
      '15': 'rgb(255 255 255 / 85%)',
      '30': 'rgb(255 255 255 / 70%)',
      '40': 'rgb(255 255 255 / 60%)',
      '50': 'rgb(255 255 255 / 50%)',
      '60': 'rgb(255 255 255 / 40%)',
      '70': 'rgb(255 255 255 / 30%)',
      '80': 'rgb(255 255 255 / 20%)',
      '90': 'rgb(255 255 255 / 10%)',
      '95': 'rgb(255 255 255 / 5%)'
    },
    primary: '#ffffff',
    dark: {
      '15': '#fdfdfd',
      '40': '#f6f6f6',
      '55': '#ececec'
    }
  },
  silver: '#f9fafb'
};
const XIColorUsage = {
  silver: '#f9fafb',
  cx: XIColors.blue.primary,
  mx: XIColors.teal.primary,
  ex: XIColors.purple.primary,
  px: XIColors.magenta.primary,
  cxic: XIColors.orca.primary,
  colorBorder: XIColors.grey.light[15],
  colorDivider: XIColors.orca.light[80],
  colorDisabled: XIColors.grey.light[80],
  colorBorderLight: XIColors.grey.light[80],
  colorOutline: XIColors.blue.light[15],
  colorFocus: XIColors.blue.light[95],
  colorPressed: XIColors.blue.light[90],
  colorSilver: XIColors.silver,
  colorWhite: XIColors.white.primary,
  colorHighlight: XIColors.yellow.light[80],
  colorSelectedBackground: XIColors.blue.light[95],
  colorSelectedAccent: XIColors.blue.primary,
  colorNegative: XIColors.red.dark[15],
  colorError: XIColors.red.dark[15],
  colorNeutral: XIColors.orca.light[15],
  colorPositive: XIColors.green.dark[15],
  colorSuccess: XIColors.green.dark[15],
  colorWarning: XIColors.yellow.dark[40],
  disabledBackground: XIColors.grey.light[95],
  disabledBorder: XIColors.grey.light[80],
  disabledColor: XIColors.grey.light[15],
  disabledContrastColor: XIColors.grey.light[15],
  disabledBackgroundHigh: XIColors.white.light[15],
  disabledBorderHigh: XIColors.white.light[30]
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

function __classPrivateFieldSet(receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * A decorator factory for type-checking class properties.
 *
 * NOTE: Existing getters and setters will be overridden and NOT called.
 *
 * @param t The type of the class property.
 * @returns A decorator that type-checks all values assigned to the decorated property.
 */
function type(t) {
  let value = undefined;
  return (prototype, key) => {
    // Use a timer to reduce number of warnings
    let logTimeout;
    Object.defineProperty(prototype, key, {
      configurable: true,
      get() {
        return value;
      },
      set(newValue) {
        value = newValue;
        if (!t.check(newValue)) {
          clearTimeout(logTimeout);
          logTimeout = setTimeout(() => {
            logTimeout = undefined;
            console.error(`Invalid value provided for ${prototype.constructor.name}.${key}.`, 'This will throw an exception in a future major version.', `\nExpected:`, t.toString(), `\nReceived:`, typeof newValue === 'string' ? `'${newValue}'` : newValue);
          }, 0);
        }
      }
    });
  };
}

/**
 * A type representing `Array<T>`.
 */
class ArrayType {
  constructor(elementType) {
    this.kind = 'array';
    this.type = elementType;
  }
  check(value) {
    return Array.isArray(value) && value.every(v => this.type.check(v));
  }
  toJSON() {
    return {
      array: this.type.toJSON()
    };
  }
  toString() {
    return `Array<${this.type.toString()}>`;
  }
}
/**
 * @param elementType The type of "`T`" in `Array<T>`.
 * @returns An instance of {@link ArrayType}.
 */
function $array(elementType) {
  return new ArrayType(elementType);
}

/**
 * A type representing a class `T` where checked values are not required to be instances of `T`.
 *
 * NOTE: This assumes that the class provided does its own validation.
 */
class ImplementsType {
  constructor(Constructor) {
    this.kind = 'implements';
    this.Constructor = Constructor;
  }
  check(value) {
    if (value instanceof this.Constructor) {
      return true;
    }
    if (typeof value === 'object' && value !== null) {
      new this.Constructor(value);
      return true;
    }
    return false;
  }
  toJSON() {
    return {
      implements: this.Constructor.name
    };
  }
  toString() {
    return this.Constructor.name;
  }
}
/**
 * @param Constructor The constructor of the implemented class. The class should also implement type-checking.
 * @returns An instance of {@link ImplementsType<T>}.
 */
function $implements(Constructor) {
  return new ImplementsType(Constructor);
}

/**
 * A type representing an instance of a class `T`.
 */
class InstanceOfType {
  constructor(Constructor) {
    this.kind = 'instanceof';
    this.Constructor = Constructor;
  }
  check(value) {
    return value instanceof this.Constructor;
  }
  toJSON() {
    return {
      instanceof: this.Constructor.name
    };
  }
  toString() {
    return `instanceof ${this.Constructor.name}`;
  }
}
/**
 * @param Constructor The constructor of the implemented class.
 * @returns An instance of {@link InstanceOfType<T>}.
 */
function $instanceof(Constructor) {
  return new InstanceOfType(Constructor);
}

/**
 * A type representing a generic object with some {@link Shape shape}.
 */
class ObjectType {
  constructor(shape) {
    this.shape = shape;
    this.kind = 'object';
  }
  check(value) {
    if (typeof value === 'object' && value !== null) {
      return Object.entries(this.shape).every(([key, type]) => type.check(value[key]));
    }
    return false;
  }
  toJSON() {
    return {
      object: Object.entries(this.shape).reduce((acc, [key, type]) => {
        return Object.assign(Object.assign({}, acc), {
          [key]: type.toJSON()
        });
      }, {})
    };
  }
  toString() {
    return JSON.stringify(Object.entries(this.shape).reduce((acc, [key, type]) => {
      return Object.assign(Object.assign({}, acc), {
        [key]: type.toString()
      });
    }, {}), undefined, '    ');
  }
}
/**
 *
 * @param shape The {@link Shape shape} of the object `T`.
 * @returns An instance of {@link ObjectType}.
 */
function $object(shape) {
  return new ObjectType(shape);
}

/**
 * A type representing a specific value.
 */
class EqualsType {
  constructor(value) {
    this.value = value;
    this.kind = 'equals';
  }
  check(value) {
    return this.value === value;
  }
  toJSON() {
    return {
      equals: this.value
    };
  }
  toString() {
    return typeof this.value === 'string' ? `'${this.value}'` : `${this.value}`;
  }
}
/**
 *
 * @param value The value against which equality is checked.
 * @returns An instance of {@link EqualsType}.
 */
function $equals(value) {
  return new EqualsType(value);
}

/**
 * A type representing an intersection of types. Values may be an instance of any type.
 */
class UnionType {
  constructor(...types) {
    this.kind = 'union';
    this.types = types;
  }
  check(value) {
    return this.types.some(t => t.check(value));
  }
  toJSON() {
    return {
      union: this.types.map(t => t.toJSON())
    };
  }
  toString() {
    return `(${this.types.map(t => t.toString()).join(' | ')})`;
  }
}
/**
 * @param types The types.
 * @returns An instance of {@link UnionType<T>}.
 */
function $union(...types) {
  return new UnionType(...types);
}

/**
 * A type representing a member of an array `T`.
 */
class InType extends UnionType {
  constructor(options) {
    super(...options.map(o => $equals(o)));
    this.optionsSet = new Set(options);
  }
  /**
   * Override to use more efficient check (O(1) vs. O(n)).
   */
  check(value) {
    return this.optionsSet.has(value);
  }
}
/**
 * @param options An array of allowed options.
 * @returns An instance of {@link InType}.
 */
function $in(options) {
  return new InType(options);
}

/**
 * A type representing a boolean.
 */
class BooleanType {
  constructor() {
    this.kind = 'boolean';
  }
  check(value) {
    return typeof value === 'boolean';
  }
  toJSON() {
    return {
      boolean: true
    };
  }
  toString() {
    return 'boolean';
  }
}
/**
 * @returns An instance of {@link BooleanType}.
 */
function $boolean() {
  return new BooleanType();
}

/* eslint-disable @typescript-eslint/ban-types */
/**
 * A type representing a function.
 */
class FunctionType {
  constructor() {
    this.kind = 'function';
  }
  check(value) {
    return typeof value === 'function';
  }
  toJSON() {
    return {
      function: true
    };
  }
  toString() {
    return 'function';
  }
}
/**
 * @returns An instance of {@link FunctionType}.
 */
function $function() {
  return new FunctionType();
}

/**
 * A type representing `null`.
 */
class NullType {
  constructor() {
    this.kind = 'null';
  }
  check(value) {
    return value === null;
  }
  toJSON() {
    return {
      null: true
    };
  }
  toString() {
    return 'null';
  }
}
/**
 * @returns An instance of {@link NullType}.
 */
function $null() {
  return new NullType();
}

/**
 * A type representing a number.
 */
class NumberType {
  constructor(min = undefined, max = undefined, kind) {
    this.min = min;
    this.max = max;
    this.kind = kind;
  }
  check(value) {
    return typeof value === 'number' && (this.min === undefined || this.min <= value) && (this.max === undefined || value <= this.max);
  }
  toJSON() {
    return {
      [this.kind]: Object.assign(Object.assign({}, this.min !== undefined && {
        min: this.min
      }), this.max !== undefined && {
        max: this.max
      })
    };
  }
  toString() {
    return `${this.min !== undefined ? `${this.min} <= ` : ''}${this.kind}${this.max !== undefined ? ` <= ${this.max}` : ''}`;
  }
}
/**
 * @param min The minimum allowed value (inclusive).
 * @param max The maximum allowed value (inclusive).
 * @returns An instance of {@link NumberType}.
 */
function $number(min, max) {
  return new NumberType(min, max, 'number');
}

/**
 * A type representing an integer number.
 */
class IntegerType extends NumberType {
  constructor(min, max) {
    super(min, max, 'integer');
  }
  check(value) {
    return super.check(value) && Math.round(value) === value;
  }
}
/**
 * @param min The minimum allowed value (inclusive).
 * @param max The maximum allowed value (inclusive).
 * @returns An instance of {@link IntegerType}.
 */
function $integer(min, max) {
  return new IntegerType(min, max);
}

/**
 * A type representing a string.
 */
class StringType {
  constructor(pattern) {
    this.pattern = pattern;
    this.kind = 'string';
  }
  check(value) {
    return typeof value === 'string' && (this.pattern === undefined || this.pattern.test(value));
  }
  toJSON() {
    return {
      string: {
        pattern: this.pattern
      }
    };
  }
  toString() {
    return this.pattern ? `string matching pattern ${this.pattern.source}` : 'string';
  }
}
/**
 *
 * @param pattern The expected pattern of the string.
 * @returns An instance of {@link StringType}.
 */
function $string(pattern) {
  return new StringType(pattern);
}

/**
 * A type representing `undefined`.
 */
class UndefinedType {
  constructor() {
    this.kind = 'undefined';
  }
  check(value) {
    return value === undefined;
  }
  toJSON() {
    return {
      undefined: true
    };
  }
  toString() {
    return 'undefined';
  }
}
/**
 * @returns An instance of {@link UndefinedType}.
 */
function $undefined() {
  return new UndefinedType();
}

/**
 * A type representing `unknown`.
 */
class UnknownType {
  check(value) {
    return true;
  }
  toJSON() {
    return {
      unknown: true
    };
  }
  toString() {
    return 'unknown';
  }
}
/**
 * @returns An instance of {@link UnknownType}.
 */
function $unknown() {
  return new UnknownType();
}

/**
 * @param type A type `T`.
 * @returns A type representing `T | null`.
 */
function $nullable(type) {
  return $union($null(), type);
}

/**
 * @param type A type `T`.
 * @returns A type representing `T | undefined`.
 */
function $optional(type) {
  return $union($undefined(), type);
}

/* eslint-disable @typescript-eslint/ban-types */
var _CachedConstant_supplier, _CachedConstant_cacheRef, _CacheMap_map;
/**
 * If {@link WeakRef} is defined in the current environment,
 * this will be an instance of WeakRef, else {@link PossibleWeakRef.deref deref()} always returns `undefined`.
 */
class PossibleWeakRef {
  constructor(target) {
    if (typeof window.WeakRef === 'function') {
      return new WeakRef(target);
    }
    // else ignore `target`. If we can't use a real WeakRef, we just return `undefined` in `deref()`.
  }
  deref() {
    return undefined;
  }
  get [Symbol.toStringTag]() {
    return 'WeakRef';
  }
}
/**
 * Used to cache objects that are unlikely to change often without storing them in memory indefinitely.
 *
 * The following examples relate to the `isIcons` helper function (in `libs/the-kitchen/src/lib/Visuals/Visual.model.ts` as of writing).
 *
 * Generally, I would expect the `isIcons` function to be called once per render per icon in the DOM, making this a potentially hot code path.
 *   As of writing this, there are 495 icons. Consider the following implementations:
 * - Iterate through the `_IconsOptions` array every render:
 *   - This is the simplest implementation but could result in iterating over the array many times per render (easily 1000+ array accesses).
 * - Create a Set() of the icons in an IIFE:
 *   - This resolves the iteration issue, but if the function isn't called often, the Set becomes essentially a memory leak.
 * - Re-create the Set() in an IIFE each time it has been garage collected:
 *   - This is the purpose of this class. The Set will be cached using a {@link WeakRef}, so we can keep the Set in memory, but the browser can garbage-collect it at any time to regain memory.
 */
class CachedConstant {
  get value() {
    const cachedValue = __classPrivateFieldGet(this, _CachedConstant_cacheRef, "f").deref();
    if (cachedValue) {
      return cachedValue;
    }
    const newValue = __classPrivateFieldGet(this, _CachedConstant_supplier, "f").call(this);
    __classPrivateFieldSet(this, _CachedConstant_cacheRef, new PossibleWeakRef(newValue), "f");
    return newValue;
  }
  constructor(supplier) {
    _CachedConstant_supplier.set(this, void 0);
    _CachedConstant_cacheRef.set(this, void 0);
    __classPrivateFieldSet(this, _CachedConstant_supplier, supplier, "f");
    __classPrivateFieldSet(this, _CachedConstant_cacheRef, new PossibleWeakRef(supplier()), "f");
  }
}
_CachedConstant_supplier = new WeakMap(), _CachedConstant_cacheRef = new WeakMap();
class CacheMap {
  constructor(entries) {
    _CacheMap_map.set(this, void 0);
    __classPrivateFieldSet(this, _CacheMap_map, new Map(entries === null || entries === void 0 ? void 0 : entries.map(([k, v]) => [k, new PossibleWeakRef(v)])), "f");
  }
  clear() {
    __classPrivateFieldGet(this, _CacheMap_map, "f").clear();
  }
  /**
   * @param supplier If the value is not cached, this function will be called to get an initial value which will be added to the cache.
   */
  retrieve(key, supplier) {
    var _a;
    const cached = (_a = __classPrivateFieldGet(this, _CacheMap_map, "f").get(key)) === null || _a === void 0 ? void 0 : _a.deref();
    if (cached) {
      return cached;
    }
    const newValue = supplier(key);
    if (typeof newValue === 'object' && newValue !== null) {
      __classPrivateFieldGet(this, _CacheMap_map, "f").set(key, new PossibleWeakRef(newValue));
    }
    return newValue;
  }
  async retrieveAsync(key, supplier) {
    var _a;
    const cached = (_a = __classPrivateFieldGet(this, _CacheMap_map, "f").get(key)) === null || _a === void 0 ? void 0 : _a.deref();
    if (cached) {
      return cached;
    }
    const newValue = await supplier(key);
    __classPrivateFieldGet(this, _CacheMap_map, "f").set(key, new PossibleWeakRef(newValue));
    return newValue;
  }
}
_CacheMap_map = new WeakMap();
/* eslint-enable @typescript-eslint/ban-types */

const _LightNumbersOptions = [95, 90, 80, 70, 60, 50, 40, 30, 15];
const _DarkNumbersOptions = [55, 40, 15];
const _StatusColorNamesOptions = ['red', 'green', 'yellow'];
/**
 * Silver is NOT included because it has no light/dark options
 */
const _ColorNamesOptions = ['admin', 'orca', 'blue', 'purple', 'teal', 'white', 'magenta', 'grey'];
const _AllColorNamesOptions = [..._StatusColorNamesOptions, ..._ColorNamesOptions];
const _PrimaryColorOptions = _AllColorNamesOptions.map(color => `${color}_primary`);
const _LightColorOptions = _AllColorNamesOptions.map(color => `${color}_light`).flatMap(light => _LightNumbersOptions.map(v => `${light}_${v}`));
const _DarkColorOptions = _AllColorNamesOptions.map(color => `${color}_dark`).flatMap(dark => _DarkNumbersOptions.map(v => `${dark}_${v}`));
const _AllColorsOptions = ['silver', ..._PrimaryColorOptions, ..._LightColorOptions, ..._DarkColorOptions];
const isAllColors = (() => {
  const allColors = new CachedConstant(() => new Set(_AllColorsOptions));
  return value => typeof value === 'string' && allColors.value.has(value);
})();
const _EXOptions = ['EX', 'EMPLOYEE'];
const _CXOptions = ['CX', 'CUSTOMER'];
const _MXOptions = ['MX', 'MARKETING'];
const _PXOptions = ['PX', 'PRODUCT'];
const _CXICOptions = ['CXIC'];
const _CloudsOptions = [..._EXOptions, ..._MXOptions, ..._CXICOptions, ..._CXOptions, ..._PXOptions];
function isClouds(value) {
  return typeof value === 'string' && Object.values(_CloudsOptions).includes(value);
}
var EnumClouds;
(function (EnumClouds) {
  EnumClouds["EX"] = "EX";
  EnumClouds["EMPLOYEE"] = "EMPLOYEE";
  EnumClouds["MX"] = "MX";
  EnumClouds["MARKETING"] = "MARKETING";
  EnumClouds["CX"] = "CX";
  EnumClouds["CUSTOMER"] = "CUSTOMER";
  EnumClouds["PX"] = "PX";
  EnumClouds["PRODUCT"] = "PRODUCT";
  EnumClouds["CXIC"] = "CXIC";
})(EnumClouds || (EnumClouds = {}));
const _XICloudsOptions = [..._EXOptions, ..._MXOptions, ..._CXOptions, ..._PXOptions];
function isXIClouds(value) {
  return typeof value === 'string' && Object.values(_XICloudsOptions).includes(value);
}
const _AdminOptions = ['admin'];
const _SystemOptions = ['system', 'orca'];
const _InvalidOptions = ['invalid'];
const _ColorsOptions = [..._CloudsOptions, ..._AdminOptions, ..._SystemOptions];
const _ContrastsOptions = ['normal', 'high'];
const _DisabledOptions = ['disabled'];
const _ErrorStatesOptions = ['success', 'warning', 'error'];
const _StatusStatesOptions = ['new', 'open', 'inProgress', 'closed', 'expired', 'overdue'];
var EnumErrorStates;
(function (EnumErrorStates) {
  EnumErrorStates["SUCCESS"] = "success";
  EnumErrorStates["WARNING"] = "warning";
  EnumErrorStates["ERROR"] = "error";
})(EnumErrorStates || (EnumErrorStates = {}));
const _ComponentSizesOptions = ['normal', 'large'];
const _LeftRightOptions = ['left', 'right'];
const _SentimentsOptions = ['negative', 'neutral', 'positive'];
var SentimentEnum;
(function (SentimentEnum) {
  SentimentEnum[SentimentEnum["negative"] = -1] = "negative";
  SentimentEnum[SentimentEnum["neutral"] = 0] = "neutral";
  SentimentEnum[SentimentEnum["positive"] = 1] = "positive";
})(SentimentEnum || (SentimentEnum = {}));
class ButtonObject {}
__decorate([type($string()), __metadata("design:type", String)], ButtonObject.prototype, "text", void 0);
__decorate([type($function()), __metadata("design:type", Function)], ButtonObject.prototype, "onClick", void 0);
// text
const _WhiteOptions = ['white'];
const _TextWeightsOptions = [300, 400, 600, 'light', 'normal', 'semi-bold'];
const _TextAlignmentsOptions = ['left', 'right', 'center', 'end', 'justify', 'match-parent', 'start', 'inherit', '-moz-initial', 'initial', 'revert', 'unset'];
const _TextLevelsOptions = [1, 2, 3, 4];
const _TextSizesOptions = ['small', 'normal', 'body', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
const _TextColorsOptions = [..._ColorsOptions, ..._ErrorStatesOptions, ..._DisabledOptions, ..._WhiteOptions];
const _TextWrapsOptions = ['normal', 'inherit', 'pre', '-moz-initial', 'initial', 'revert', 'unset', 'nowrap', '-moz-pre-wrap', 'break-spaces', 'pre-line', 'pre-wrap'];

class ErrorObject {}
__decorate([type($in(_ErrorStatesOptions)), __metadata("design:type", String)], ErrorObject.prototype, "state", void 0);
__decorate([type($optional($string())), __metadata("design:type", String)], ErrorObject.prototype, "text", void 0);
/** See `HTMLInputTypeAttribute` in `@types/react` for full list */
const _TextInputTypeOptions = ['email', 'hidden', 'number', 'password', 'search', 'tel', 'text', 'url'];
const _ButtonInputType = ['button', 'reset', 'submit'];
const _InputFieldVisualStateOptions = ['normal', 'condensed'];
const _TextAreaVisualStateOptions = ['normal', 'condensed'];
const defaultLocalization$1 = {
  hidePassword: 'Hide Password',
  showPassword: 'Show Password'
};

const {
  cx,
  mx,
  ex,
  px,
  colorSuccess,
  colorWarning,
  colorError,
  colorDisabled: colorDisabled$2
} = XIColorUsage;
const {
  orca: orca$3,
  admin,
  white: white$4
} = XIColors;
const normalizeColor = color => {
  switch (color) {
    case 'CX':
    case 'CUSTOMER':
      return 'cx';
    case 'EX':
    case 'EMPLOYEE':
      return 'ex';
    case 'MX':
    case 'MARKETING':
      return 'mx';
    case 'CXIC':
    case 'system':
    case 'orca':
      return 'orca';
    case 'PX':
    case 'PRODUCT':
      return 'px';
    case 'success':
    case 'warning':
    case 'error':
    case 'positive':
    case 'negative':
    case 'neutral':
    case 'invalid':
    case 'new':
    case 'inProgress':
    case 'noSentiment':
    case 'closed':
    case 'expired':
    case 'overdue':
    case 'open':
      return `kxi-${color}`;
    case 'disabled':
    case 'admin':
    case 'white':
      return color;
    default:
      devOnlyError(`The Kitchen: An incorrect value, ${color}, has been passed into the normalizeColor function.`);
      return color;
  }
};
const getColor = color => {
  switch (color) {
    case 'CX':
    case 'CUSTOMER':
      return cx;
    case 'EX':
    case 'EMPLOYEE':
      return ex;
    case 'MX':
    case 'MARKETING':
      return mx;
    case 'PX':
    case 'PRODUCT':
      return px;
    case 'CXIC':
      return orca$3.light[15];
    case 'system':
    case 'orca':
      return orca$3.primary;
    case 'admin':
      return admin.primary;
    case 'success':
      return colorSuccess;
    case 'warning':
      return colorWarning;
    case 'error':
      return colorError;
    case 'disabled':
      return colorDisabled$2;
    case 'white':
      return white$4.primary;
    default:
      devOnlyError(`The Kitchen: An incorrect value, ${color}, has been passed into the getColor function.`);
      return color;
  }
};
// intentional fall through statements
const getLightCloudColor = (cloud, num) => {
  const {
    blue,
    purple,
    teal,
    magenta
  } = XIColors;
  switch (cloud) {
    case 'CX':
    case 'CUSTOMER':
      return blue.light[num];
    case 'EX':
    case 'EMPLOYEE':
      return purple.light[num];
    case 'MX':
    case 'MARKETING':
      return teal.light[num];
    case 'PX':
    case 'PRODUCT':
      return magenta.light[num];
    default:
      return blue.light[num];
  }
};
// intentional fall through statements
const getDarkCloudColor = (cloud, num) => {
  const {
    blue,
    purple,
    teal,
    magenta
  } = XIColors;
  switch (cloud) {
    case 'CX':
    case 'CUSTOMER':
      return blue.dark[num];
    case 'EX':
    case 'EMPLOYEE':
      return purple.dark[num];
    case 'MX':
    case 'MARKETING':
      return teal.dark[num];
    case 'PX':
    case 'PRODUCT':
      return magenta.dark[num];
    default:
      return blue.dark[num];
  }
};
const capitalizeFirstLetter = s => {
  const nameArr = s.split(' ');
  const capitalizedNameArr = [];
  nameArr.forEach(item => {
    if (item) {
      const firstLetter = item[0].toUpperCase();
      item = item.substring(1);
      const newItem = firstLetter + item;
      capitalizedNameArr.push(newItem);
    }
  });
  return capitalizedNameArr.join(' ');
};
function throwIfRequiredPropNotProvided(componentName, props) {
  Object.keys(props).forEach(propName => {
    if (props[propName] === undefined) {
      throw new Error(`The Kitchen: ${propName} is a required prop, but it is omitted in ${componentName}`);
    }
  });
}
/**
 * @deprecated this has been replaced by useWarnIsDeprecated
 *
 * This function warns the user that the item they are using is deprecated and provides
 * them with the appropriate error message.
 *
 * @note This will warn each time it is called, so ensure that proper state is kept
 *
 * @param componentName The name of the component where this function is being called from
 * @param props The props that are being deprecated. Props that are undefined will not
 * cause a warning to occur
 * @param type If you are just deprecating props, pass `prop` if you are deprecating the
 * entire component pass `component`
 */
function warnIsDeprecated(componentName, props, type) {
  if (type === 'component') {
    devOnlyWarn(`The Kitchen: The component ${componentName} will be removed in the next major version, please reach out to the Kitchen team with questions.${props['component'].message ? ` ${props['component'].message}` : ''} RIP ${componentName}.`);
  } else {
    Object.keys(props).forEach(propName => {
      var _a, _b;
      if (((_a = props[propName]) === null || _a === void 0 ? void 0 : _a.value) !== undefined) {
        devOnlyWarn(`The Kitchen: ${propName} has been deprecated on ${componentName}. ${(_b = props[propName].message) !== null && _b !== void 0 ? _b : 'It may have unexpected behavior.'} The ${propName} prop will be removed in the next major version.`);
      }
    });
  }
}
function warnIconRenamed(oldName, newName) {
  devOnlyWarn(`The Kitchen: The icon '${oldName}' has been renamed to '${newName}'. In a future major version, the name '${oldName}' will no longer work.`);
}
function checkIfButtonTypeIsValid(name, type) {
  return isElementTypeValid(name, type, _ButtonInputType);
}
function checkIfTextInputTypeIsValid(name, type) {
  return isElementTypeValid(name, type, _TextInputTypeOptions);
}
function isElementTypeValid(componentName, type, typeOptions) {
  if (type === undefined) {
    return true;
  }
  const isValid = typeOptions.includes(type);
  if (!isValid) {
    devOnlyWarn(`The Kitchen: You have passed in a type "${type}" that is not expected for ${componentName}, this will have no effect.`);
  }
  return isValid;
}
const watchForEvents = () => {
  document.addEventListener('mousedown', () => {
    document.body.classList.add('using-mouse');
  });
  document.addEventListener('keydown', event => {
    if (event.keyCode === 9) {
      document.body.classList.remove('using-mouse');
    }
  });
};
const switchIconBasedOnWarning = warningType => {
  switch (warningType) {
    case 'error':
      return 'cancel_circle';
    case 'success':
      return 'circle_check_filled';
    case 'warning':
      return 'cancel_circle';
    default:
      return undefined;
  }
};
const switchColorBasedOnWarning = warningType => {
  switch (warningType) {
    case 'error':
      return colorError;
    case 'success':
      return colorSuccess;
    case 'warning':
      return colorWarning;
    default:
      return orca$3.primary;
  }
};
const throwCharErrors = char => {
  if (!char) {
    throw new Error('Char must be defined');
  }
  if (char.length !== 1) {
    throw new Error('Input should be 1 char exactly.');
  }
};
const determineCharType = char => {
  throwCharErrors(char);
  const alphaRegex = new RegExp(/[A-Za-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]/);
  if (Number.isInteger(+char) && char !== ' ') {
    return 'numeric';
  } else if (char.match(alphaRegex)) {
    return 'alpha';
  } else if (char === '*') {
    return 'alphanumeric';
  } else {
    return 'symbol';
  }
};
const switchMaskCharType = char => {
  throwCharErrors(char);
  switch (char) {
    case '9':
      return 'numeric';
    case 'a':
      return 'alpha';
    case '*':
      return 'alphanumeric';
    default:
      return 'symbol';
  }
};
const controlMaskInput = (value, mask) => {
  let maskedValue = '';
  const lastCharIndex = value.length - 1;
  const nextMaskChar = mask[lastCharIndex + 1];
  const nextMaskCharType = nextMaskChar && switchMaskCharType(nextMaskChar);
  const nextMaskCharIsSymbol = nextMaskChar && nextMaskCharType === 'symbol';
  if (value.length > maskedValue.length) {
    const char = value[lastCharIndex];
    const maskChar = mask[lastCharIndex];
    const maskType = switchMaskCharType(maskChar);
    const charType = determineCharType(char);
    const allowChange = charType && charType === maskType;
    if (value.length === 1 && maskType === 'symbol') {
      value = maskChar + value;
      if (charType === nextMaskCharType) {
        maskedValue += value;
      }
    } else {
      value = value.substring(0, lastCharIndex);
    }
    if (allowChange) {
      value += char;
      if (nextMaskCharIsSymbol) {
        value += nextMaskChar;
      }
      maskedValue += value;
    }
  } else {
    if (nextMaskCharIsSymbol) {
      value = value.substring(0, lastCharIndex);
    }
    maskedValue += value;
  }
  return maskedValue;
};
const lastChar = value => {
  return value[value.length - 1];
};
const countChars = value => {
  let numOfNonSymbols = 0;
  for (let i = 0; i < value.length; i++) {
    if (determineCharType(value[i]) !== 'symbol') {
      numOfNonSymbols++;
    }
  }
  return numOfNonSymbols;
};
const hasChildren = children => {
  return !!children && (!Array.isArray(children) || children.length > 0);
};
const boldSearchText = (value, searchTerm) => {
  const splitArr = value.split(searchTerm);
  return splitArr.map((item, i) => {
    if (i !== splitArr.length - 1) {
      return jsxs(Fragment, {
        children: [item, jsx("span", {
          className: "kxi-text-600",
          children: searchTerm
        })]
      });
    } else {
      return item;
    }
  });
};
const isProdEnvironment = () => {
  var _a;
  const envName = ((_a = window === null || window === void 0 ? void 0 : window.env) === null || _a === void 0 ? void 0 : _a.name) || '';
  if (envName === 'production' || envName === 'prod-euc1' || envName === 'prod-apse2') {
    return true;
  }
  return false;
};
const devOnlyWarn = (message, ...optionalParams) => {
  if (!isProdEnvironment()) {
    console.warn(message, ...optionalParams);
  }
};
const devOnlyError = (message, ...optionalParams) => {
  if (!isProdEnvironment()) {
    console.error(message, ...optionalParams);
  }
};
const escapeRegex = str => {
  const matches = Array.from(new Set(str.match(/([\^$.|?+*{}[\]()]{1})/gm)));
  str = str.replace('\\', '\\\\');
  matches.forEach(specialChar => {
    str = str.replace(specialChar, `\\${specialChar}`);
  });
  return str;
};
function isNotNullish(value) {
  return value != null;
}
function makeEventHandler(shouldHandle, handler, {
  preventDefault = true,
  stopPropagation = false,
  disabled = false,
  onlyIfFocused = false
} = {}) {
  return e => {
    if (disabled) return;
    if (onlyIfFocused && e.currentTarget !== document.activeElement) return;
    if (!shouldHandle(e)) return;
    if (preventDefault) e.preventDefault();
    if (stopPropagation) e.stopPropagation();
    handler(e);
  };
}
function isActivation(e) {
  return e.key === 'Enter' || e.key === ' ';
}
function ifActivation(handler, props) {
  return makeEventHandler(isActivation, handler, props);
}
function sortNumbers(arr) {
  return [...arr].sort((a, b) => a - b);
}

var fns = /*#__PURE__*/Object.freeze({
  __proto__: null,
  boldSearchText: boldSearchText,
  capitalizeFirstLetter: capitalizeFirstLetter,
  checkIfButtonTypeIsValid: checkIfButtonTypeIsValid,
  checkIfTextInputTypeIsValid: checkIfTextInputTypeIsValid,
  controlMaskInput: controlMaskInput,
  countChars: countChars,
  determineCharType: determineCharType,
  devOnlyError: devOnlyError,
  devOnlyWarn: devOnlyWarn,
  escapeRegex: escapeRegex,
  getColor: getColor,
  getDarkCloudColor: getDarkCloudColor,
  getLightCloudColor: getLightCloudColor,
  hasChildren: hasChildren,
  ifActivation: ifActivation,
  isActivation: isActivation,
  isNotNullish: isNotNullish,
  isProdEnvironment: isProdEnvironment,
  lastChar: lastChar,
  makeEventHandler: makeEventHandler,
  normalizeColor: normalizeColor,
  sortNumbers: sortNumbers,
  switchColorBasedOnWarning: switchColorBasedOnWarning,
  switchIconBasedOnWarning: switchIconBasedOnWarning,
  switchMaskCharType: switchMaskCharType,
  throwIfRequiredPropNotProvided: throwIfRequiredPropNotProvided,
  warnIconRenamed: warnIconRenamed,
  warnIsDeprecated: warnIsDeprecated,
  watchForEvents: watchForEvents
});

const radii = 90;
const XIGradients = {
  cxGradient: `linear-gradient(${radii}deg, ${XIColors.blue.primary} 0%, ${XIColors.orca.primary} 200%)`,
  pxGradient: `linear-gradient(${radii}deg, ${XIColors.magenta.primary} 0%, ${XIColors.orca.primary} 200%)`,
  exGradient: `linear-gradient(${radii}deg, ${XIColors.purple.primary} 0%, ${XIColors.orca.primary} 200%)`,
  mxGradient: `linear-gradient(${radii}deg, ${XIColors.teal.primary} 0%, ${XIColors.orca.primary} 200%)`,
  orcaGradient: `linear-gradient(${radii}deg, ${XIColors.orca.light[15]} 0%, ${XIColors.orca.dark[40]} 200%)`,
  adminGradient: `linear-gradient(${radii}deg, ${XIColors.admin.primary} 0%, ${XIColors.orca.primary} 200%)`
};

const XIShadowsDown = {
  0: '0px 1px 2px 0px rgba(37, 55, 70, 0.0), 0px 0px 1px 0px rgba(37, 55, 70, 0.0)',
  1: '0px 1px 2px 0px rgba(37, 55, 70, 0.18), 0px 0px 1px 0px rgba(37, 55, 70, 0.06)',
  2: '0px 2px 3px 0px rgba(37, 55, 70, 0.18), 0px 0px 3px 0px rgba(37, 55, 70, 0.06)',
  3: '0px 6px 6px 0px rgba(37, 55, 70, 0.18), 0px 0px 6px 0px rgba(37, 55, 70, 0.06)',
  4: '0px 9px 9px 0px rgba(37, 55, 70, 0.18), 0px 0px 9px 0px rgba(37, 55, 70, 0.06)',
  5: '0px 12px 12px 0px rgba(37, 55, 70, 0.18), 0px 0px 12px 0px rgba(37, 55, 70, 0.06)',
  6: '0px 18px 18px 0px rgba(37, 55, 70, 0.18), 0px 0px 18px 0px rgba(37, 55, 70, 0.06)',
  7: '0px 24px 24px 0px rgba(37, 55, 70, 0.18), 0px 0px 24px 0px rgba(37, 55, 70, 0.06)'
};
const XIShadowsUp = {
  0: '0px -1px 2px 0px rgba(37, 55, 70, 0.0), 0px 0px 1px 0px rgba(37, 55, 70, 0.0)',
  1: '0px -1px 2px 0px rgba(37, 55, 70, 0.18), 0px 0px 1px 0px rgba(37, 55, 70, 0.06)',
  2: '0px -2px 3px 0px rgba(37, 55, 70, 0.18), 0px 0px 3px 0px rgba(37, 55, 70, 0.06)',
  3: '0px -6px 6px 0px rgba(37, 55, 70, 0.18), 0px 0px 6px 0px rgba(37, 55, 70, 0.06)',
  4: '0px -9px 9px 0px rgba(37, 55, 70, 0.18), 0px 0px 9px 0px rgba(37, 55, 70, 0.06)',
  5: '0px -12px 12px 0px rgba(37, 55, 70, 0.18), 0px 0px 12px 0px rgba(37, 55, 70, 0.06)',
  6: '0px -18px 18px 0px rgba(37, 55, 70, 0.18), 0px 0px 18px 0px rgba(37, 55, 70, 0.06)',
  7: '0px -24px 24px 0px rgba(37, 55, 70, 0.18), 0px 0px 24px 0px rgba(37, 55, 70, 0.06)'
};
const XIShadowsLeft = {
  0: '-1px 0px 2px 0px rgba(37, 55, 70, 0.0), 0px 0px 1px 0px rgba(37, 55, 70, 0.0)',
  1: '-1px 0px 2px 0px rgba(37, 55, 70, 0.24), 0px 0px 1px 0px rgba(37, 55, 70, 0.06)',
  2: '-2px 0px 3px 0px rgba(37, 55, 70, 0.24), 0px 0px 3px 0px rgba(37, 55, 70, 0.06)',
  3: '-6px 0px 6px 0px rgba(37, 55, 70, 0.24), 0px 0px 6px 0px rgba(37, 55, 70, 0.06)',
  4: '-9px 0px 9px 0px rgba(37, 55, 70, 0.24), 0px 0px 9px 0px rgba(37, 55, 70, 0.06)',
  5: '-12px 0px 12px 0px rgba(37, 55, 70, 0.24), 0px 0px 12px 0px rgba(37, 55, 70, 0.06)',
  6: '-18px 0px 18px 0px rgba(37, 55, 70, 0.24), 0px 0px 18px 0px rgba(37, 55, 70, 0.06)',
  7: '-24px 0px 24px 0px rgba(37, 55, 70, 0.24), 0px 0px 24px 0px rgba(37, 55, 70, 0.06)'
};
const XIShadowsRight = {
  0: '1px 0px 2px 0px rgba(37, 55, 70, 0.0), 0px 0px 1px 0px rgba(37, 55, 70, 0.0)',
  1: '1px 0px 2px 0px rgba(37, 55, 70, 0.24), 0px 0px 1px 0px rgba(37, 55, 70, 0.06)',
  2: '2px 0px 3px 0px rgba(37, 55, 70, 0.24), 0px 0px 3px 0px rgba(37, 55, 70, 0.06)',
  3: '6px 0px 6px 0px rgba(37, 55, 70, 0.24), 0px 0px 6px 0px rgba(37, 55, 70, 0.06)',
  4: '9px 0px 9px 0px rgba(37, 55, 70, 0.24), 0px 0px 9px 0px rgba(37, 55, 70, 0.06)',
  5: '12px 0px 12px 0px rgba(37, 55, 70, 0.24), 0px 0px 12px 0px rgba(37, 55, 70, 0.06)',
  6: '18px 0px 18px 0px rgba(37, 55, 70, 0.24), 0px 0px 18px 0px rgba(37, 55, 70, 0.06)',
  7: '24px 0px 24px 0px rgba(37, 55, 70, 0.24), 0px 0px 24px 0px rgba(37, 55, 70, 0.06)'
};
const _ShadowsOptions = [0, 1, 2, 3, 4, 5, 6, 7];

const XITransitions = {
  hover: 'border 0.1s, box-shadow 0.1s, font-weight 0.1s, color 0.1s, background 0.1s',
  formControlHover: '.075s all ease-in-out',
  modals: 'opacity 0.2s ease-in-out'
};
const AnimatePresenceStates = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};

/**
 * A decorator factory. Returns a decorator function that
 * ensures that each property is present in the object's prototype chain,
 * even if the property isn't initialized on the object itself.
 * More documentation can be found in {@link https://confluence.inmoment.com/x/EIU6Bw Confluence}.
 *
 * @returns The decorator function.
 * @see {@link https://confluence.inmoment.com/x/EIU6Bw}
 */
function Bound() {
  /**
   * @param target The _prototype_ of the class.
   * @param propertyKey The name of the property.
   */
  return function (target, propertyKey) {
    const descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    Object.defineProperty(target, propertyKey, Object.assign(Object.assign({}, descriptor !== null && descriptor !== void 0 ? descriptor : {
      value: null,
      writable: true
    }), {
      enumerable: true
    }));
  };
}

class AriaAttributes {}
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-activedescendant", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-atomic", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-autocomplete", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-busy", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-checked", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-colcount", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-colindex", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-colspan", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-controls", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-current", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-describedby", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-details", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-disabled", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-dropeffect", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-errormessage", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-expanded", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-flowto", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-grabbed", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-haspopup", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-hidden", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-invalid", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-keyshortcuts", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-label", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-labelledby", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-level", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-live", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-modal", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-multiline", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-multiselectable", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-orientation", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-owns", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-placeholder", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-posinset", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-pressed", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-readonly", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-relevant", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-required", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-roledescription", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-rowcount", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-rowindex", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-rowspan", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AriaAttributes.prototype, "aria-selected", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-setsize", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-sort", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-valuemax", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-valuemin", void 0);
__decorate([Bound(), __metadata("design:type", Number)], AriaAttributes.prototype, "aria-valuenow", void 0);
__decorate([Bound(), __metadata("design:type", String)], AriaAttributes.prototype, "aria-valuetext", void 0);
// eslint-disable-next-line @typescript-eslint/ban-types
function DOMAttributes(SuperClass) {
  return class extends SuperClass {};
}
class DOMEvents {}
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCopy", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCopyCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCut", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCutCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPaste", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPasteCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCompositionEnd", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCompositionEndCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCompositionStart", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCompositionStartCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCompositionUpdate", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCompositionUpdateCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onFocus", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onFocusCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onBlur", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onBlurCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onChangeCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onBeforeInput", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onBeforeInputCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onInput", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onInputCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onReset", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onResetCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSubmit", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSubmitCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onInvalid", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onInvalidCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoad", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoadCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onError", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onErrorCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onKeyDown", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onKeyDownCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onKeyPress", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onKeyPressCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onKeyUp", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onKeyUpCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAbort", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAbortCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCanPlay", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCanPlayCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCanPlayThrough", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onCanPlayThroughCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDurationChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDurationChangeCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onEmptied", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onEmptiedCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onEncrypted", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onEncryptedCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onEnded", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onEndedCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoadedData", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoadedDataCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoadedMetadata", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoadedMetadataCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoadStart", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLoadStartCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPause", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPauseCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPlay", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPlayCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPlaying", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPlayingCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onProgress", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onProgressCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onRateChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onRateChangeCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSeeked", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSeekedCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSeeking", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSeekingCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onStalled", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onStalledCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSuspend", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSuspendCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTimeUpdate", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTimeUpdateCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onVolumeChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onVolumeChangeCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onWaiting", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onWaitingCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAuxClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAuxClickCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onClickCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onContextMenu", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onContextMenuCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDoubleClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDoubleClickCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDrag", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragEnd", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragEndCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragEnter", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragEnterCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragExit", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragExitCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragLeave", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragLeaveCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragOver", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragOverCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragStart", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDragStartCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDrop", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onDropCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseDown", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseDownCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseEnter", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseLeave", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseMove", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseMoveCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseOut", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseOutCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseOver", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseOverCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseUp", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onMouseUpCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSelect", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onSelectCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchCancel", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchCancelCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchEnd", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchEndCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchMove", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchMoveCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchStart", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTouchStartCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerDown", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerDownCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerMove", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerMoveCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerUp", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerUpCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerCancel", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerCancelCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerEnter", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerLeave", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerOver", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerOverCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerOut", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onPointerOutCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onGotPointerCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onGotPointerCaptureCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLostPointerCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onLostPointerCaptureCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onScroll", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onScrollCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onWheel", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onWheelCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAnimationStart", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAnimationStartCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAnimationEnd", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAnimationEndCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAnimationIteration", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onAnimationIterationCapture", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTransitionEnd", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DOMEvents.prototype, "onTransitionEndCapture", void 0);
class HTMLAttributes extends DOMAttributes(AriaAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "data-qa", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "data-testid", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HTMLAttributes.prototype, "defaultChecked", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HTMLAttributes.prototype, "defaultValue", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HTMLAttributes.prototype, "suppressContentEditableWarning", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HTMLAttributes.prototype, "suppressHydrationWarning", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "accessKey", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "className", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HTMLAttributes.prototype, "contentEditable", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "contextMenu", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "dir", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HTMLAttributes.prototype, "draggable", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HTMLAttributes.prototype, "hidden", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "id", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "lang", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "placeholder", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "slot", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HTMLAttributes.prototype, "spellCheck", void 0);
__decorate([Bound(), __metadata("design:type", Number)], HTMLAttributes.prototype, "tabIndex", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "translate", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "radioGroup", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "role", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "about", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "datatype", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HTMLAttributes.prototype, "inlist", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "prefix", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "property", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "resource", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "typeof", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "vocab", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "autoCapitalize", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "autoCorrect", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "autoSave", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "itemProp", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HTMLAttributes.prototype, "itemScope", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "itemType", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "itemID", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "itemRef", void 0);
__decorate([Bound(), __metadata("design:type", Number)], HTMLAttributes.prototype, "results", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "security", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "unselectable", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "inputMode", void 0);
__decorate([Bound(), __metadata("design:type", String)], HTMLAttributes.prototype, "is", void 0);

class DivHTMLAttributes extends HTMLAttributes {}
class DivHTMLEvents extends DOMEvents {}

const ReactSlot = props => {
  var _a, _b;
  const {
    children,
    name,
    slots
  } = props;
  const isChildrenSlot = name === 'children';
  const slotChildren = (_b = (_a = React.Children.map(slots, c => {
    var _a;
    if (typeof c === 'object' && c !== null && 'props' in c) {
      const {
        slot
      } = (_a = c.props) !== null && _a !== void 0 ? _a : {};
      if (isChildrenSlot && slot === undefined) {
        // Slotless elements go into `children` slot.
        return c;
      } else if (slot === name) {
        return c;
      } else {
        // Exclude child.
        return null;
      }
    } else {
      // Slotless non-elements go into `children` slot.
      return isChildrenSlot ? c : null;
    }
  })) === null || _a === void 0 ? void 0 : _a.filter(isNotNullish)) !== null && _b !== void 0 ? _b : [];
  return slotChildren.length > 0 ? slotChildren : children;
};

/// <reference types="node" />
/* eslint-disable testing-library/no-debugging-utils, no-console */
var _a$2, _LoggingService_console, _LoggingService_id, _LoggingService_prefix, _LoggingService_isDevMode;
/**
 * A wrapper around {@link console}'s {@link https://developer.mozilla.org/en-US/docs/Web/API/Console_API#browser_compatibility standard methods}
 *     that prefixes output with the id passed to the constructor.
 *
 * Note: In dev mode, all methods will be no-ops.
 */
class LoggingService {
  /** @param id The id used to customize output. */
  constructor(id, isDevMode = !isProdEnvironment()) {
    _LoggingService_id.set(this, void 0);
    _LoggingService_prefix.set(this, void 0);
    _LoggingService_isDevMode.set(this, void 0);
    __classPrivateFieldSet(this, _LoggingService_id, id, "f");
    __classPrivateFieldSet(this, _LoggingService_prefix, `The Kitchen (${id}):`, "f");
    __classPrivateFieldSet(this, _LoggingService_isDevMode, isDevMode, "f");
  }
  get id() {
    return __classPrivateFieldGet(this, _LoggingService_id, "f");
  }
  get isDevMode() {
    return __classPrivateFieldGet(this, _LoggingService_isDevMode, "f");
  }
  /** Prefixes a string with this instance's id. */
  format(...messages) {
    return `${__classPrivateFieldGet(this, _LoggingService_prefix, "f")} ${messages.join(' ')}`;
  }
  /** Throws an {@link Error} with the specified {@link message} prefixed by this instance's ID. */
  throw(message) {
    if (__classPrivateFieldGet(this, _LoggingService_isDevMode, "f")) {
      throw new Error(`${__classPrivateFieldGet(this, _LoggingService_prefix, "f")} ${message}`);
    }
  }
  /**
   * @param condition Whether to throw an exception.
   *     Provide a function for more expensive checks that should not be run outside of the dev environment.
   * @param message
   */
  throwIf(condition, message) {
    if (__classPrivateFieldGet(this, _LoggingService_isDevMode, "f") && (typeof condition === 'boolean' ? condition : condition())) {
      this.throw(message);
    }
  }
  // #region Console
  assert(condition, ...data) {
    this.delegate('assert', condition, __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  clear() {
    this.delegate('clear');
  }
  count(label) {
    this.delegate('count', label && this.format(label));
  }
  countReset(label) {
    this.delegate('countReset', label && this.format(label));
  }
  debug(...data) {
    this.delegate('debug', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  dir(item, options) {
    this.delegate('dir', item, options);
  }
  dirxml(...data) {
    this.delegate('dirxml', ...data);
  }
  error(...data) {
    this.delegate('error', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  group(...data) {
    this.delegate('group', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  groupCollapsed(...data) {
    this.delegate('groupCollapsed', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  groupEnd() {
    this.delegate('groupEnd');
  }
  info(...data) {
    this.delegate('info', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  log(...data) {
    this.delegate('log', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  table(tabularData, properties) {
    this.delegate('table', tabularData, properties);
  }
  time(label) {
    this.delegate('time', label && this.format(label));
  }
  timeEnd(label) {
    this.delegate('timeEnd', label && this.format(label));
  }
  timeLog(label, ...data) {
    this.delegate('timeLog', label && this.format(label), ...data);
  }
  trace(...data) {
    this.delegate('trace', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  warn(...data) {
    this.delegate('warn', __classPrivateFieldGet(this, _LoggingService_prefix, "f"), ...data);
  }
  delegate(key, ...args) {
    if (!__classPrivateFieldGet(this, _LoggingService_isDevMode, "f") || typeof process !== 'undefined') {
      return;
    }
    __classPrivateFieldGet(LoggingService, _a$2, "f", _LoggingService_console)[key](...args);
  }
}
_a$2 = LoggingService, _LoggingService_id = new WeakMap(), _LoggingService_prefix = new WeakMap(), _LoggingService_isDevMode = new WeakMap();
_LoggingService_console = {
  value: Object.assign({}, window.console)
};
/* eslint-enable testing-library/no-debugging-utils, no-console */

/**
 * Wrap each component's returned element with this class.
 *
 * Keeps errors in the Kitchen from crashing the entire component tree:
 * - In Angular/Web Component environments, this means each individual component,
 *     because each component is rendered with a different invocation to ReactDOM.render().
 * - In React environments, this means the entire webpage, because there generally will be only one invocation of ReactDOM.render.
 *
 * @example
 * export const Dropdown = forwardRef((props, ref) => {
 *   // ...
 *   // Lots of dropdown code
 *   // ...
 *
 *   return (
 *     <ErrorBoundary component={Dropdown}>
 *       // ...
 *       // normal dropdown return value
 *       // ...
 *     </ErrorBoundary>
 *   );
 * });
 * Dropdown.displayName = 'Dropdown';
 */
class ErrorBoundary extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
    this.isProd = isProdEnvironment();
    const {
      displayName
    } = props.component;
    this.logger = new LoggingService(displayName ? `${displayName} - ErrorBoundary` : 'ErrorBoundary');
  }
  render() {
    const {
      error
    } = this.state;
    if (error === undefined) {
      return this.props.children;
    }
    if (this.isProd) {
      return null;
    }
    return this.logger.format(`Something went wrong in The Kitchen. Please report this to the Kitchen team. Error: ${error.message}`);
  }
  /**
   * @see {@link React.ComponentLifecycle ComponentLifecycle interface}
   */
  componentDidCatch(error, errorInfo) {
    this.logger.error('An error was caught. This component will no longer render', {
      error,
      errorInfo
    });
  }
  /**
   * This method should be pure
   * @see {@link React.StaticLifecycle StaticLifecycle interface}
   * @see {@link React.GetDerivedStateFromError GetDerivedStateFromError type}
   * */
  static getDerivedStateFromError(error) {
    return {
      error
    };
  }
}

/// <reference types="zone.js" />
/**
 *  This was pieced together from the implementations of
 *     {@link https://github.com/angular/angular/blob/86372538ab017058cb9b2a7910e761d823bd3c16/packages/core/src/zone/ng_zone.ts#L249 NgZone}
 *     and {@link https://github.com/angular/angular/blob/f9b6a3fecc4a2f9d6135b6e7c098ba18551f57a2/packages/zone.js/lib/zone.ts#L817 Zone}
 *     to mimic the Angular code `this.ngZone.runOutsideAngular(() => {...}))`
 */
class KitchenZone {
  constructor() {
    var _a;
    this.loggingService = new LoggingService('ZoneJS');
    this.Zone = typeof Zone !== 'undefined' ? Zone : undefined;
    this.initialZone = (_a = this.Zone) === null || _a === void 0 ? void 0 : _a.current;
    if (this.initialZone !== undefined) {
      let curr = this.initialZone;
      while (curr !== null && KitchenZone.isInAngularZone(curr)) {
        curr = curr.parent;
      }
      if (curr !== null) {
        this.nonAngularZone = curr;
      }
    }
  }
  /**
   * Runs in the zone in which this instance was created.
   */
  runInInitialZone(fn) {
    return KitchenZone.tryToRunInZone(this.initialZone, fn);
  }
  /**
   * Use this if change detection in Angular is not triggering correctly.
   *
   * @example
   * this.runOutsideAngular(() => {
   *   // do some stuff
   *   this.runInAngular(() => {
   *     // do some stuff that needs to trigger change detection
   *   });
   * });
   */
  runInAngular(fn) {
    if (this.initialZone !== undefined && !KitchenZone.isInAngularZone(this.initialZone)) {
      this.loggingService.warn('`runInAngular` was called, but we are not currently in an Angular zone.');
    }
    return KitchenZone.tryToRunInZone(this.initialZone, fn);
  }
  /**
   * This is a workaround for an issue in Angular that causes {@link MutationObserver} to fall into an infinite loop.
   * This is mimicking the workaround mentioned in {@link https://github.com/angular/angular/issues/31712#issuecomment-346521952 this Angular GitHub comment}.
   *
   * @see https://jira.inmoment.com/browse/BZ-11134
   * @see {@link https://github.com/angular/angular/issues/31712 Infinite loop with MutationObserver + browser zoom}
   * @see {@link https://github.com/angular/angular/issues/31483 Infinite loop because of MutationObserver}
   * @see {@link https://github.com/angular/angular/issues/26948 Third party use of MutationObserver can cause the browser to hang}
   */
  runOutsideAngular(fn) {
    return KitchenZone.tryToRunInZone(this.nonAngularZone, fn);
  }
  static isInAngularZone(zone) {
    return zone.get('isAngularZone') === true;
  }
  static tryToRunInZone(zone, fn) {
    return zone !== undefined ? zone.run(fn) : fn();
  }
}

/** Tracks event listeners currently registered to an element. */
class EventRegistry {
  constructor() {
    /**
     * Event listeners are determined by the referential equality of their listener and options.
     * Map<type, Map<listener, Set<options>>>
     */
    this.map = new Map();
  }
  /** Call in {@link HTMLElement.addEventListener} */
  add(type, listener, options) {
    if (!this.map.has(type)) {
      this.map.set(type, new Map());
    }
    const listenersMap = this.map.get(type);
    if (!listenersMap.has(listener)) {
      listenersMap.set(listener, new Set());
    }
    const optionsSet = listenersMap.get(listener);
    optionsSet.add(options);
  }
  /** Call in {@link HTMLElement.removeEventListener} */
  remove(type, listener, options) {
    const listenersMap = this.map.get(type);
    // If event with `type`
    if (listenersMap) {
      const optionsSet = listenersMap.get(listener);
      // and `listener`
      if (optionsSet) {
        // delete `options`
        optionsSet.delete(options);
        // If no more options, delete listener
        if (optionsSet.size === 0) {
          listenersMap.delete(listener);
        }
      }
      // If no more listeners, delete type
      if (listenersMap.size === 0) {
        this.map.delete(type);
      }
    }
  }
  hasEventWithType(type) {
    return this.map.has(type);
  }
}

/* eslint-disable @typescript-eslint/no-redeclare */
/**
 * Use old-school style JS classes because we need to call `super` outside of the Angular zone,
 *     and we can't do that in true classes.
 */
const ZonelessMutationObserver = function (callback) {
  const zone = new KitchenZone();
  return zone.runOutsideAngular(() => new MutationObserver(callback));
};
ZonelessMutationObserver.prototype = MutationObserver.prototype;

/**
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace#specifying_a_function_as_the_replacement}
 * @see {@link https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid}
 */
function uuidv4() {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const baseUuid = '10000000-1000-4000-8000-100000000000';
  const randomNumbers = crypto.getRandomValues(new Uint8Array(baseUuid.length));
  return baseUuid.replace(/[018]/g, (c, offset) => {
    const n = Number(c);
    return (n ^ randomNumbers[offset] & 15 >> n / 4).toString(16);
  });
}

var _KitchenComponent_instances, _a$1, _KitchenComponent_staticLoggingService, _KitchenComponent_wasConstructed, _KitchenComponent_runOutsideAngular, _KitchenComponent_Component, _KitchenComponent_props, _KitchenComponent_events, _KitchenComponent_bug_setInternalPropertyOnObservedChange, _KitchenComponent_bug_rerenderOnObservedChange, _KitchenComponent_bug_disableReactStrictMode, _KitchenComponent_propKeys, _KitchenComponent_eventKeys, _KitchenComponent_eventProps, _KitchenComponent_attributesObserver, _KitchenComponent_internalProperties, _KitchenComponent_propObserver, _KitchenComponent_isMounted, _KitchenComponent_transcludedContent, _KitchenComponent_onSlotRender, _KitchenComponent_slotContext, _KitchenComponent_instanceId, _KitchenComponent_reactRoot, _KitchenComponent_loggingService, _KitchenComponent_mountTimeout, _KitchenComponent_renderTimeout, _KitchenComponent_unmountTimeout, _KitchenComponent_kitchenZone, _KitchenComponent_boundEventListeners, _KitchenComponent_runInSelectedZone, _KitchenComponent_mount, _KitchenComponent_mountSync, _KitchenComponent_unmount, _KitchenComponent_unmountSync, _KitchenComponent_renderAsync, _KitchenComponent_renderSync, _KitchenComponent_clearTimeouts, _KitchenComponent_getInternalProperty, _KitchenComponent_setInternalProperty, _KitchenComponent_getProps, _KitchenComponent_getEvents, _KitchenComponent_setupExternalObservableProperties, _KitchenComponent_generateObserver, _KitchenComponent_convertAttributeValue, _KitchenComponent_initializeTransclusion, _KitchenComponent_wrapEventProp, _KitchenComponent_onEvent, _KitchenComponent_getAllKeys;
const SlotContext = /*#__PURE__*/React__default.createContext(undefined);
/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements
 */
class KitchenComponent extends HTMLElement {
  static runOutsideAngular() {
    if (__classPrivateFieldGet(this, _a$1, "f", _KitchenComponent_wasConstructed) && !__classPrivateFieldGet(this, _a$1, "f", _KitchenComponent_runOutsideAngular)) {
      throw new Error('Cannot update the value of runOutsideAngular after KitchenComponents have been created');
    }
    __classPrivateFieldGet(this, _a$1, "f", _KitchenComponent_staticLoggingService).info('Running the Kitchen outside of Angular zones.');
    __classPrivateFieldSet(this, _a$1, true, "f", _KitchenComponent_runOutsideAngular);
  }
  constructor({
    Component,
    props,
    events,
    bug_rerenderOnObservedChange = false,
    bug_setInternalPropertyOnObservedChange = false,
    bug_disableReactStrictMode = false
  }) {
    super();
    _KitchenComponent_instances.add(this);
    /** The function to render the react component */
    _KitchenComponent_Component.set(this, void 0);
    /** The properties for this react component (These need to be `@Bound()` in order for the web component to know about them) */
    _KitchenComponent_props.set(this, void 0);
    /** The events for this react component (These need to be `@Bound()` in order for the web component to know about them) */
    _KitchenComponent_events.set(this, void 0);
    /**
     * {@link https://jira.inmoment.com/browse/DEV-32422 DEV-32422} fixed a bug in
     *     how we handled `observer.propName = newValue`,
     *     but some components fall into infinite loops with the bug fixed.
     *     This serves as a way to temporarily fix these components until
     *     we can change the components themselves to not get into the infinite loop.
     *     The components should ONLY update `observer` in an event handler (not in a useEffect).
     *
     * @deprecated Only use this in components where there is an issue with a non-trivial fix
     *     where setting `observer` does not work correctly.
     *     If you do use this, put in a ticket to fix the source issue.
     */
    _KitchenComponent_bug_setInternalPropertyOnObservedChange.set(this, void 0);
    /**
     * @deprecated Only use this in components where there is an issue with a non-trivial fix
     *     where setting `observer` does not work correctly.
     *     If you do use this, put in a ticket to fix the source issue.
     */
    _KitchenComponent_bug_rerenderOnObservedChange.set(this, void 0);
    _KitchenComponent_bug_disableReactStrictMode.set(this, void 0);
    _KitchenComponent_propKeys.set(this, void 0);
    _KitchenComponent_eventKeys.set(this, void 0);
    _KitchenComponent_eventProps.set(this, void 0);
    // Properties to observe changes outside of the component
    _KitchenComponent_attributesObserver.set(this, void 0);
    _KitchenComponent_internalProperties.set(this, {});
    // Properties to observe changes from inside the component
    _KitchenComponent_propObserver.set(this, void 0);
    // Determine if the component has been mounted
    _KitchenComponent_isMounted.set(this, false);
    _KitchenComponent_transcludedContent.set(this, []);
    _KitchenComponent_onSlotRender.set(this, slot => {
      if (!__classPrivateFieldGet(this, _KitchenComponent_slotContext, "f").hasRendered) {
        __classPrivateFieldSet(this, _KitchenComponent_slotContext, Object.assign(Object.assign({}, __classPrivateFieldGet(this, _KitchenComponent_slotContext, "f")), {
          hasRendered: true
        }), "f");
        __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderAsync).call(this);
      }
      const slotContent = __classPrivateFieldGet(this, _KitchenComponent_transcludedContent, "f").find(el => el.getAttribute('slot') === slot.name);
      if (slotContent) {
        for (let i = 0; i < slot.children.length; i += 1) {
          if (slotContent === slot.children[i]) {
            return;
          }
        }
        slot.appendChild(slotContent);
      }
    });
    _KitchenComponent_slotContext.set(this, {
      hasRendered: false,
      onRender: __classPrivateFieldGet(this, _KitchenComponent_onSlotRender, "f")
    });
    _KitchenComponent_instanceId.set(this, `${this.tagName.toLowerCase()}-${uuidv4()}`);
    _KitchenComponent_reactRoot.set(this, void 0);
    _KitchenComponent_loggingService.set(this, void 0);
    /**
     * It's unlikely that our component will be unmounted before the timeout set in
     *     {@link KitchenComponent.connectedCallback connectedCallback} triggers,
     *     but just in case, this will be used to clear the timeout in {@link KitchenComponent.disconnectedCallback disconnectedCallback}.
     */
    _KitchenComponent_mountTimeout.set(this, void 0);
    _KitchenComponent_renderTimeout.set(this, void 0);
    _KitchenComponent_unmountTimeout.set(this, void 0);
    /** @see https://jira.inmoment.com/browse/BZ-11134 */
    _KitchenComponent_kitchenZone.set(this, new KitchenZone());
    /**
     * Used to determine which events are actually being listened to,
     *     so that we can send only the events that we care about.
     * */
    _KitchenComponent_boundEventListeners.set(this, new EventRegistry());
    __classPrivateFieldSet(KitchenComponent, _a$1, true, "f", _KitchenComponent_wasConstructed);
    __classPrivateFieldSet(this, _KitchenComponent_Component, Component, "f");
    __classPrivateFieldSet(this, _KitchenComponent_props, props, "f");
    __classPrivateFieldSet(this, _KitchenComponent_events, events, "f");
    __classPrivateFieldSet(this, _KitchenComponent_bug_rerenderOnObservedChange, bug_rerenderOnObservedChange, "f");
    __classPrivateFieldSet(this, _KitchenComponent_bug_setInternalPropertyOnObservedChange, bug_setInternalPropertyOnObservedChange, "f");
    __classPrivateFieldSet(this, _KitchenComponent_bug_disableReactStrictMode, bug_disableReactStrictMode, "f");
    __classPrivateFieldSet(this, _KitchenComponent_loggingService, new LoggingService(this.tagName.toLowerCase()), "f");
    __classPrivateFieldSet(this, _KitchenComponent_propKeys, __classPrivateFieldGet(KitchenComponent, _a$1, "m", _KitchenComponent_getAllKeys).call(KitchenComponent, __classPrivateFieldGet(this, _KitchenComponent_props, "f")), "f");
    __classPrivateFieldSet(this, _KitchenComponent_eventKeys, __classPrivateFieldGet(KitchenComponent, _a$1, "m", _KitchenComponent_getAllKeys).call(KitchenComponent, __classPrivateFieldGet(this, _KitchenComponent_events, "f")), "f");
    // initialize the events that will be raised out through the web component
    // all events are routed through here so we can intercept, clean up, and re-raise them as custom events.
    __classPrivateFieldSet(this, _KitchenComponent_eventProps, Array.from(__classPrivateFieldGet(this, _KitchenComponent_eventKeys, "f")).reduce((acc, key) => {
      return Object.assign(Object.assign({}, acc), {
        [key]: __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_wrapEventProp).call(this, key)
      });
    }, {}), "f");
    // Set up the observer to watch if the HTML attributes on the web component are changed
    const lowercasePropKeys = new Set([...__classPrivateFieldGet(this, _KitchenComponent_propKeys, "f")].map(key => key.toLowerCase()));
    __classPrivateFieldSet(this, _KitchenComponent_attributesObserver, __classPrivateFieldGet(this, _KitchenComponent_kitchenZone, "f").runOutsideAngular(() => new ZonelessMutationObserver(changes => {
      if (!__classPrivateFieldGet(this, _KitchenComponent_isMounted, "f")) {
        return;
      }
      const changedAttributes = Array.from(new Set(changes.map(c => c.attributeName || '')));
      const attrInProps = changedAttributes.some(c => lowercasePropKeys.has(c));
      // If the attribute is a bound attribute, then update the component
      if (attrInProps) {
        __classPrivateFieldGet(this, _KitchenComponent_kitchenZone, "f").runInInitialZone(() => __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderAsync).call(this));
      }
    })), "f");
    __classPrivateFieldSet(this, _KitchenComponent_propObserver, __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_generateObserver).call(this), "f");
    __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_setupExternalObservableProperties).call(this); // Do this LAST in the constructor
  }
  addEventListener(type, listener, options) {
    super.addEventListener(type, listener, options);
    if (__classPrivateFieldGet(this, _KitchenComponent_eventKeys, "f").has(type)) {
      __classPrivateFieldGet(this, _KitchenComponent_boundEventListeners, "f").add(type, listener, options);
      if (__classPrivateFieldGet(this, _KitchenComponent_isMounted, "f")) {
        __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderAsync).call(this);
      }
    }
  }
  removeEventListener(type, listener, options) {
    super.removeEventListener(type, listener, options);
    if (__classPrivateFieldGet(this, _KitchenComponent_eventKeys, "f").has(type)) {
      __classPrivateFieldGet(this, _KitchenComponent_boundEventListeners, "f").remove(type, listener, options);
      if (__classPrivateFieldGet(this, _KitchenComponent_isMounted, "f")) {
        __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderAsync).call(this);
      }
    }
  }
  debug() {
    return {
      instanceId: __classPrivateFieldGet(this, _KitchenComponent_instanceId, "f"),
      internalProperties: Object.assign({}, __classPrivateFieldGet(this, _KitchenComponent_internalProperties, "f")),
      transcludedContent: [...__classPrivateFieldGet(this, _KitchenComponent_transcludedContent, "f")],
      propKeys: new Set(__classPrivateFieldGet(this, _KitchenComponent_propKeys, "f")),
      eventKeys: new Set(__classPrivateFieldGet(this, _KitchenComponent_eventKeys, "f"))
    };
  }
  /**
   * Invoked each time the custom element is appended into a document-connected element.
   *     This will happen each time the node is moved,
   *     and may happen before the element's contents have been fully parsed.
   *
   * WARNING: This callback may be called multiple times in the lifespan of a component, so be careful that any logic here
   *          can run multiple times without issue.
   */
  connectedCallback() {
    __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_mount).call(this);
  }
  /**
   * Invoked each time the custom element is disconnected from the document's DOM.
   *
   * WARNING: This callback may be called multiple times in the lifespan of a component, so be careful that any logic here
   *          can run multiple times without issue.
   */
  disconnectedCallback() {
    __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_unmount).call(this);
  }
  /**
   * Define the web component.  Do not error if it gets defined twice.
   */
  static define() {
    if (customElements.get(this.tag) === undefined) {
      customElements.define(this.tag, this);
    } else {
      devOnlyWarn(`The Kitchen: Web Component ${this.tag} is already defined.`);
    }
  }
}
_a$1 = KitchenComponent, _KitchenComponent_Component = new WeakMap(), _KitchenComponent_props = new WeakMap(), _KitchenComponent_events = new WeakMap(), _KitchenComponent_bug_setInternalPropertyOnObservedChange = new WeakMap(), _KitchenComponent_bug_rerenderOnObservedChange = new WeakMap(), _KitchenComponent_bug_disableReactStrictMode = new WeakMap(), _KitchenComponent_propKeys = new WeakMap(), _KitchenComponent_eventKeys = new WeakMap(), _KitchenComponent_eventProps = new WeakMap(), _KitchenComponent_attributesObserver = new WeakMap(), _KitchenComponent_internalProperties = new WeakMap(), _KitchenComponent_propObserver = new WeakMap(), _KitchenComponent_isMounted = new WeakMap(), _KitchenComponent_transcludedContent = new WeakMap(), _KitchenComponent_onSlotRender = new WeakMap(), _KitchenComponent_slotContext = new WeakMap(), _KitchenComponent_instanceId = new WeakMap(), _KitchenComponent_reactRoot = new WeakMap(), _KitchenComponent_loggingService = new WeakMap(), _KitchenComponent_mountTimeout = new WeakMap(), _KitchenComponent_renderTimeout = new WeakMap(), _KitchenComponent_unmountTimeout = new WeakMap(), _KitchenComponent_kitchenZone = new WeakMap(), _KitchenComponent_boundEventListeners = new WeakMap(), _KitchenComponent_instances = new WeakSet(), _KitchenComponent_runInSelectedZone = function _KitchenComponent_runInSelectedZone(fn) {
  if (__classPrivateFieldGet(KitchenComponent, _a$1, "f", _KitchenComponent_runOutsideAngular)) {
    return __classPrivateFieldGet(this, _KitchenComponent_kitchenZone, "f").runOutsideAngular(fn);
  } else {
    // Else run in *current* zone, which may be inside or outside of Angular
    return fn();
  }
}, _KitchenComponent_mount = function _KitchenComponent_mount() {
  __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_clearTimeouts).call(this);
  __classPrivateFieldSet(this, _KitchenComponent_mountTimeout, setTimeout(() => {
    __classPrivateFieldSet(this, _KitchenComponent_mountTimeout, undefined, "f");
    __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_mountSync).call(this);
  }, 0), "f");
}, _KitchenComponent_mountSync = function _KitchenComponent_mountSync() {
  if (__classPrivateFieldGet(this, _KitchenComponent_isMounted, "f")) {
    return;
  }
  __classPrivateFieldSet(this, _KitchenComponent_isMounted, true, "f");
  __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_initializeTransclusion).call(this);
  __classPrivateFieldGet(this, _KitchenComponent_kitchenZone, "f").runOutsideAngular(() => {
    __classPrivateFieldGet(this, _KitchenComponent_attributesObserver, "f").observe(this, {
      attributes: true
    });
  });
  __classPrivateFieldSet(this, _KitchenComponent_reactRoot, createRoot(this, {
    identifierPrefix: __classPrivateFieldGet(this, _KitchenComponent_instanceId, "f")
  }), "f");
  __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderSync).call(this);
}, _KitchenComponent_unmount = function _KitchenComponent_unmount() {
  __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_clearTimeouts).call(this);
  __classPrivateFieldSet(this, _KitchenComponent_unmountTimeout, setTimeout(() => {
    __classPrivateFieldSet(this, _KitchenComponent_unmountTimeout, undefined, "f");
    __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_unmountSync).call(this);
  }, 0), "f");
}, _KitchenComponent_unmountSync = function _KitchenComponent_unmountSync() {
  var _b;
  if (!__classPrivateFieldGet(this, _KitchenComponent_isMounted, "f")) {
    return;
  }
  __classPrivateFieldSet(this, _KitchenComponent_isMounted, false, "f");
  (_b = __classPrivateFieldGet(this, _KitchenComponent_reactRoot, "f")) === null || _b === void 0 ? void 0 : _b.unmount();
  __classPrivateFieldSet(this, _KitchenComponent_reactRoot, undefined, "f");
}, _KitchenComponent_renderAsync = function _KitchenComponent_renderAsync() {
  __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_clearTimeouts).call(this);
  __classPrivateFieldSet(this, _KitchenComponent_renderTimeout, setTimeout(() => {
    __classPrivateFieldSet(this, _KitchenComponent_renderTimeout, undefined, "f");
    __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderSync).call(this);
  }, 0), "f");
}, _KitchenComponent_renderSync = function _KitchenComponent_renderSync() {
  if (!__classPrivateFieldGet(this, _KitchenComponent_isMounted, "f")) {
    // This means there is probably some kind of memory leak
    __classPrivateFieldGet(this, _KitchenComponent_loggingService, "f").warn('Tried to update component after it has unmounted. Please report this to the Kitchen team.');
    return;
  }
  // Render the react component
  const Component = __classPrivateFieldGet(this, _KitchenComponent_Component, "f");
  const _b = Object.assign(Object.assign({}, __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_getProps).call(this)), __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_getEvents).call(this)),
    props = __rest(_b, ["constructor"]);
  const guardedComponent = jsx(SlotContext.Provider, {
    value: __classPrivateFieldGet(this, _KitchenComponent_slotContext, "f"),
    children: jsx(ErrorBoundary, {
      component: Component,
      children: jsx(Component, Object.assign({}, props))
    })
  });
  __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_runInSelectedZone).call(this, () => {
    var _b;
    (_b = __classPrivateFieldGet(this, _KitchenComponent_reactRoot, "f")) === null || _b === void 0 ? void 0 : _b.render(__classPrivateFieldGet(this, _KitchenComponent_bug_disableReactStrictMode, "f") ? guardedComponent : jsx(React__default.StrictMode, {
      children: guardedComponent
    }));
  });
}, _KitchenComponent_clearTimeouts = function _KitchenComponent_clearTimeouts() {
  clearTimeout(__classPrivateFieldGet(this, _KitchenComponent_mountTimeout, "f"));
  __classPrivateFieldSet(this, _KitchenComponent_mountTimeout, undefined, "f");
  clearTimeout(__classPrivateFieldGet(this, _KitchenComponent_renderTimeout, "f"));
  __classPrivateFieldSet(this, _KitchenComponent_renderTimeout, undefined, "f");
  clearTimeout(__classPrivateFieldGet(this, _KitchenComponent_unmountTimeout, "f"));
  __classPrivateFieldSet(this, _KitchenComponent_unmountTimeout, undefined, "f");
}, _KitchenComponent_getInternalProperty = function _KitchenComponent_getInternalProperty(propName) {
  return __classPrivateFieldGet(this, _KitchenComponent_internalProperties, "f")[propName];
}, _KitchenComponent_setInternalProperty = function _KitchenComponent_setInternalProperty(propName, val) {
  if (__classPrivateFieldGet(this, _KitchenComponent_internalProperties, "f")[propName] !== val) {
    __classPrivateFieldGet(this, _KitchenComponent_internalProperties, "f")[propName] = val;
    // Since we mount on a timeout, some properties may update before we mount, so guard against that.
    if (__classPrivateFieldGet(this, _KitchenComponent_isMounted, "f")) {
      __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderAsync).call(this);
    }
  }
}, _KitchenComponent_getProps = function _KitchenComponent_getProps() {
  const props = {};
  // set the observer if the component will allow two-way binding
  if (Object.prototype.hasOwnProperty.call(__classPrivateFieldGet(this, _KitchenComponent_props, "f"), 'observer')) {
    // If you are looking here for why the observer isn't set check the observable props mixin
    props.observer = __classPrivateFieldGet(this, _KitchenComponent_propObserver, "f");
  }
  // initialize all of the property values so we can send them to react
  __classPrivateFieldGet(this, _KitchenComponent_propKeys, "f").forEach(attrName => {
    // if the property value is set by JS, grab it
    // eslint-disable-next-line no-prototype-builtins
    const internalProp = this.hasOwnProperty(attrName) ? __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_getInternalProperty).call(this, attrName) : undefined;
    if (internalProp !== undefined) {
      props[attrName] = internalProp;
    } else {
      // if the property value is set through HTML attributes, grab it
      const attribute = this.attributes.getNamedItem(attrName);
      if (attribute) {
        const value = __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_convertAttributeValue).call(this, attrName, attribute.value);
        props[attrName] = value;
      }
    }
  });
  // If the app attempts to send in styling as a string, remove it.  React will not accept this.
  // we can probably do something to try and map this to an object at some point.
  if (typeof props.style === 'string') {
    __classPrivateFieldGet(this, _KitchenComponent_loggingService, "f").warn(`You have attempted to set the style as a string: ${props.style}.`, 'This property will not be passed through to the component.');
    delete props.style;
  }
  return props;
}, _KitchenComponent_getEvents = function _KitchenComponent_getEvents() {
  const events = {};
  __classPrivateFieldGet(this, _KitchenComponent_eventKeys, "f").forEach(key => {
    // Only send an event to the component if someone is actually listening to it.
    if (__classPrivateFieldGet(this, _KitchenComponent_boundEventListeners, "f").hasEventWithType(key)) {
      events[key] = __classPrivateFieldGet(this, _KitchenComponent_eventProps, "f")[key];
    }
  });
  return events;
}, _KitchenComponent_setupExternalObservableProperties = function _KitchenComponent_setupExternalObservableProperties() {
  __classPrivateFieldGet(this, _KitchenComponent_propKeys, "f").forEach(attrName => {
    if (Object.prototype.hasOwnProperty.call(this, attrName)) {
      return;
    }
    Object.defineProperty(this, attrName, {
      get: () => __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_getInternalProperty).call(this, attrName),
      set: val => {
        __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_setInternalProperty).call(this, attrName, val);
      }
    });
  });
}, _KitchenComponent_generateObserver = function _KitchenComponent_generateObserver() {
  const observer = {};
  __classPrivateFieldGet(this, _KitchenComponent_propKeys, "f").forEach(attrName => {
    Object.defineProperty(observer, attrName, {
      // Dispatch an event any time the observer has a property that changes
      // This is being caught in `the-kitchen-angular` library and used for 2-way binding.
      set: val => {
        /**
         * Sets our internal value so that when Angular comes back and updates the value via `kitchenComponentElement[attrName] = val`,
         *     we won't rerender, because the values will be equal.
         *
         * This prevents some components with non-ideal state management from going into infinite loops via:
         *     -> state change
         *       -> `observer.propName = newValue` (probably in an effect if the infinite loop is happening)
         *       -> this setter dispatches the propName-change event
         *       -> `the-kitchen-angular` two-way binding directive
         *       -> Angular updates the KitchenComponent instance with `kitchenComponentElement[attrName] = val`
         *       -> re-render with prop change
         *       -> update state (probably in an effect)
         *       -> (back to 'state change')
         *
         * The above data flow may change slightly from component to component,
         *     but the gist is that we should avoid updating our `observer` in a `useEffect`.
         */
        if (__classPrivateFieldGet(this, _KitchenComponent_bug_setInternalPropertyOnObservedChange, "f")) {
          __classPrivateFieldGet(this, _KitchenComponent_internalProperties, "f")[attrName] = val;
        }
        if (__classPrivateFieldGet(this, _KitchenComponent_bug_rerenderOnObservedChange, "f")) {
          __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_renderAsync).call(this);
        }
        // Dispatch this as a custom event. This is being caught in `the-kitchen-angular` library and used for 2-way binding
        this.dispatchEvent(new CustomEvent(`${attrName}-change`, {
          bubbles: false,
          cancelable: false,
          detail: val
        }));
      }
    });
  });
  return observer;
}, _KitchenComponent_convertAttributeValue = function _KitchenComponent_convertAttributeValue(attrName, value) {
  const notifyDeprecated = type => __classPrivateFieldGet(this, _KitchenComponent_loggingService, "f").warn(`The prop ${attrName} was provided as a string but looks like a ${type}: "${value}".`, `Did you write \`${attrName}="${value}"\` instead of \`[${attrName}]="${value}"\`?`, `We will attempt to coerce this to a(n) ${type}, but this behavior will be removed in a future major version.`);
  if (value === 'true' || value === 'false') {
    notifyDeprecated('boolean');
    return value === 'true';
  }
  if (!isNaN(value) && value !== '') {
    notifyDeprecated('number');
    return +value;
  }
  if (/^{.*}/.exec(value)) {
    notifyDeprecated('object');
    return JSON.parse(value);
  }
  return value;
}, _KitchenComponent_initializeTransclusion = function _KitchenComponent_initializeTransclusion() {
  if (__classPrivateFieldGet(this, _KitchenComponent_transcludedContent, "f").length !== 0) {
    return;
  }
  __classPrivateFieldSet(this, _KitchenComponent_transcludedContent, Array.from(this.children).filter(el => el.hasAttribute('slot')), "f");
}, _KitchenComponent_wrapEventProp = function _KitchenComponent_wrapEventProp(key) {
  return (...args) => {
    __classPrivateFieldGet(this, _KitchenComponent_instances, "m", _KitchenComponent_onEvent).call(this, key, args);
  };
}, _KitchenComponent_onEvent = function _KitchenComponent_onEvent(key, args) {
  const event = new CustomEvent(key, {
    detail: args[0]
  });
  if (!isProdEnvironment()) {
    // target will be `this` because we're dispatching on `this`
    Object.defineProperty(event, 'target', {
      configurable: true,
      get: () => {
        __classPrivateFieldGet(this, _KitchenComponent_loggingService, "f").warn('You are accessing $event.target, but Kitchen web component events are custom events.', 'The true target is located within $event.detail.', {
          event,
          '$event.detail': event.detail
        });
        return this;
      }
    });
  }
  // if there is 0 or 1 event argument, just pass it along
  this.dispatchEvent(event);
  if (args.length > 1) {
    // if there is more than one event argument it won't be forwarded. Having more
    // than one argument is due to incorrect configuration of the event and not
    // passing the arguments as an object.
    devOnlyWarn(`The Kitchen (${this.tagName.toLowerCase()}): The event ${key} has fired with more than one argument. The second and subsequent arguments will not be available. This is likely due to improper calling of an event. Please notify the Kitchen team of this error.`);
  }
  if (args[0] && typeof args[0] !== 'object') {
    // The argument is being passed as a primitive. This will cause issues in the
    // future when someone tries to add additional data to the event.
    devOnlyWarn(`The Kitchen (${this.tagName.toLowerCase()}): The event ${key} has fired with a non-object value. Do not use the output and please notify the Kitchen team so we can patch this issue.`);
  }
}, _KitchenComponent_getAllKeys = function _KitchenComponent_getAllKeys(obj) {
  const proto = Object.getPrototypeOf(obj);
  return new Set([...Object.keys(obj), ...(proto ? __classPrivateFieldGet(this, _a$1, "m", _KitchenComponent_getAllKeys).call(this, proto) : [])]);
};
_KitchenComponent_staticLoggingService = {
  value: new LoggingService('KitchenComponent')
};
_KitchenComponent_wasConstructed = {
  value: false
};
_KitchenComponent_runOutsideAngular = {
  value: false
};

// #region ClockDirection
const _NumberedClockDirectionOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
function isNumberedClockDirection(value) {
  return _NumberedClockDirectionOptions.includes(value);
}
// Keep these in order
const _NamedClockDirectionOptions = ['top-right', 'right-top', 'right-middle', 'right-bottom', 'bottom-right', 'bottom-middle', 'bottom-left', 'left-bottom', 'left-middle', 'left-top', 'top-left', 'top-middle' // 12
];
function isNamedClockDirection(value) {
  return _NamedClockDirectionOptions.includes(value);
}
const _ClockDirectionOptions = [..._NumberedClockDirectionOptions, ..._NamedClockDirectionOptions];
function isClockDirection(value) {
  return _ClockDirectionOptions.includes(value);
}
// #endregion ClockDirection

class LazyTransformer {
  constructor(iterable) {
    this.iterator = iterable[Symbol.iterator]();
  }
  [Symbol.iterator]() {
    return this;
  }
  next() {
    return this.iterator.next();
  }
  toArray() {
    return Array.from(this);
  }
  toSet() {
    return new Set(this);
  }
  filter(predicate) {
    return this.transform(function* (iterable) {
      for (const value of iterable) {
        if (predicate(value)) {
          yield value;
        }
      }
    });
  }
  map(transform) {
    return this.transform(function* (iterable) {
      for (const value of iterable) {
        yield transform(value);
      }
    });
  }
  find(predicate) {
    for (const value of this) {
      if (predicate(value)) {
        return value;
      }
    }
    return undefined;
  }
  some(predicate) {
    for (const value of this) {
      if (predicate(value)) {
        return true;
      }
    }
    return false;
  }
  zip(other) {
    return this.transform(function* (iterable) {
      const uno = iterable[Symbol.iterator]();
      const dos = other[Symbol.iterator]();
      while (true) {
        const {
          value: firstValue,
          done: firstDone
        } = uno.next();
        const {
          value: secondValue,
          done: secondDone
        } = dos.next();
        if (firstDone && secondDone) {
          break;
        }
        yield [!firstDone ? firstValue : undefined, !secondDone ? secondValue : undefined];
      }
    });
  }
  transform(transform) {
    return new LazyTransformer(transform(this));
  }
}
function lazy(iterable) {
  return new LazyTransformer(iterable);
}
function first(iterable) {
  for (const value of iterable) {
    return value;
  }
  return undefined;
}

/** Type-safe version of {@link Array.isArray} */
/**
 * @returns A tuple where index 0 is an array of elements for which {@link predicate} returned `false`,
 * and index 1 is an array for which it returned `true`.
 */
function partition(a, predicate) {
  return a.reduce((acc, curr) => {
    (predicate(curr) ? acc[1] : acc[0]).push(curr);
    return acc;
  }, [[], []]);
}
function last(a) {
  return a[a.length - 1];
}
function areEqual$1(a, b) {
  return a.length === b.length && a.every((el, i) => el === b[i]);
}
function sum(a) {
  return a.reduce((sum, curr) => sum + curr, 0);
}

// #region Guards
function isObject(value) {
  return typeof value === 'object' && value !== null;
}
function reduce(obj, fn, initialValue) {
  return Object.entries(obj).reduce((acc, [key, value]) => fn(acc, value, key), initialValue);
}
function filter(obj, fn) {
  return reduce(obj, (acc, value, key) => {
    if (fn(value, key)) {
      acc[key] = value;
    }
    return acc;
  }, {});
}
function map(obj, fn) {
  return reduce(obj, (acc, value, key) => {
    acc[key] = fn(value, key);
    return acc;
  }, {});
}
/** The same concept as the {@link Pick} type. */
function pick(obj, ...keys) {
  return filter(obj, (_value, key) => keys.includes(key));
}
// #endregion Array-like
/* eslint-enable @typescript-eslint/ban-types */

function shallowCopy(value) {
  if (!isObject(value)) {
    return value;
  }
  return Array.isArray(value) ? [...value] : Object.assign({}, value);
}
// eslint-disable-next-line @typescript-eslint/ban-types
function mergeObjects(obj, defaultObj) {
  const objCopy = shallowCopy(obj);
  const defaultObjCopy = shallowCopy(defaultObj);
  if (!isObject(objCopy) || !isObject(defaultObjCopy)) {
    return objCopy !== null && objCopy !== void 0 ? objCopy : defaultObjCopy;
  }
  Object.keys(defaultObjCopy).forEach(_key => {
    const key = _key;
    if (objCopy[key] === undefined) {
      objCopy[key] = defaultObjCopy[key];
    } else {
      objCopy[key] = mergeObjects(objCopy[key], defaultObjCopy[key]);
    }
  });
  return objCopy;
}
function hasOneOrMore(array) {
  return array.length >= 1;
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
/**
 * This function is meant to be used in the `default` clause of a `switch` statement
 * or the `else` clause of a chain of `if ... else if ...` statements
 * to ensure that all cases have been handled.  If not all cases have been handled,
 * then the call to this function will cause a compile error because the type of the parameter
 * is `never`.  The compile-time error should ensure that calls to this function are never executed,
 * so we throw in the body in case we somehow do end up executing it.
 */
function ensureExhaustive(value) {
  throw new Error(`Unexpected value: ${value}`);
}
function clone(value) {
  return structuredClone(value);
}
/**
 * Resizes array IN-PLACE to the specified length.
 * @param array The array to mutate.
 * @param newLength The desired length.
 * @param filler When extending array length, fill new entries with this value.
 * NOTE: This value will be used as is. If it is a reference type, all new entries will have the same reference.
 * @returns The mutated input array.
 */
function resizeArray(array, newLength, filler) {
  if (array.length < newLength) {
    array.push(...new Array(newLength - array.length).fill(null).map((_, i) => filler(i)));
  } else if (array.length > newLength) {
    array.length = newLength;
  }
  return array;
}
/**
 * @param value
 * @param low The lowest permissible value, inclusive
 * @param high The highest permissible value, inclusive
 */
function isBetween(value, low, high) {
  return value != null && low <= value && value <= high;
}
/** Wrapper around {@link DOMPurify} that allows a subset of Kitchen web components to be passed in the string. */
function sanitizeHtmlString(str) {
  return DOMPurify.sanitize(str, {
    CUSTOM_ELEMENT_HANDLING: {
      // don't use wildcards for either of these because of potential security issue
      tagNameCheck: /^xi-icon$/,
      attributeNameCheck: /^icon|color$/
    },
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false
  });
}
/**
 * Recursive function that takes a click and/or keyboard press and checks if that
 * click event belongs to an interactive element inside the base element(false),
 * or should bubble up to the base element(true)
 *
 * @param el this is the event target from mouse or keyboard event
 * @param baseElement element at which you want to stop checking
 * @returns true if the clicked element and its parents are non-tabbable
 */
function shouldElementHandleEvent(el, baseElement) {
  if (!el || !(el instanceof HTMLElement)) {
    return true;
  }
  // if it is the basic tile div
  if (el === baseElement) {
    return true;
  }
  // if it is not the basic tile and it's tabbable
  else if (isTabbable(el)) {
    return false;
  }
  // else
  return shouldElementHandleEvent(el.parentElement, baseElement);
}
/**
 * This function checks if an element is a valid tabbable object
 * @param el the element to check
 * @param shouldCheckMatch whether you should check if the element matches valid selectors
 * (this is an expensive calculation, so set to false when possible)
 * @returns true if the element given is tabbable
 */
function isTabbable(el, shouldCheckMatch = true) {
  if (shouldCheckMatch && !el.matches('button, a[href], input, select, textarea, [tabindex]')) return false;
  if (el.tabIndex < 0) return false;
  if (el.hasAttribute('disabled')) return false;
  if (el.hasAttribute('hidden')) return false;
  if (window.getComputedStyle(el).display === 'none') return false;
  if (window.getComputedStyle(el).visibility === 'hidden') return false;
  return true;
}

/**
 * From {@link https://api.jqueryui.com/tabbable-selector/ JQuery documentation}:
 *   Elements of the following type are tabbable if they do not have a negative tab index and are not disabled: input, select, textarea, button, and object.
 *   Anchors are focusable if they have an href or positive tabindex attribute.
 *   Area elements are focusable if they are inside a named map, have an href attribute, and there is a visible image using the map.
 *   All other elements are tabbable based solely on their tabindex attribute and visibility.
 */
function getTabbableElements(container) {
  if (!container) {
    return [];
  }
  // We don't need to support everything as its only what is feasibly inside a popout, so
  // as long as we support the basics and anything with a tabIndex we should be good
  const nodeList = container.querySelectorAll('button, a[href], input, select, textarea, [tabindex]');
  return Array.from(nodeList).filter(el => isTabbable(el));
}
/**
 * Finds the nth tabbable element and focuses on it.
 * If n >= numTabbableElements, then it focuses on the last tabbable element.
 * If n < 0, then it focuses on the first tabbable element.
 */
function focusOnNthTabbableElement(container, n) {
  const els = getTabbableElements(container);
  if (els.length === 0) {
    return;
  }
  els[clamp(n, 0, els.length - 1)].focus();
}
function focusFirstTabbableElement(container) {
  focusOnNthTabbableElement(container, 0);
}

/**
 * Debounces {@link callback} for {@link ms} milliseconds.
 */
function useDebouncedCallback(callback, ms = 100) {
  const callbackRef = useRefToValue(callback);
  const {
    setTimeout: setDebounceTimeout,
    clearTimeout: clearDebounceTimeout
  } = useTimeout();
  // Make sure old values of `fn` or `ms` don't execute
  React__default.useEffect(clearDebounceTimeout, [ms, clearDebounceTimeout]);
  return React__default.useCallback((...args) => {
    clearDebounceTimeout();
    setDebounceTimeout(() => void callbackRef.current(...args), ms);
  }, [callbackRef, ms, setDebounceTimeout, clearDebounceTimeout]);
}
/**
 * Similar to {@link React.useState useState}, but calls to `setState` are debounced for {@link ms} milliseconds.
 *
 * @param initialValue Passed to {@link React.useState useState}.
 * @param ms The amount of time to debounce.
 * @returns A tuple containing the current state and the debounced setState callback.
 */
function useDebouncedState(initialValue, ms) {
  const [state, setState] = React__default.useState(initialValue);
  const setStateDebounced = useDebouncedCallback(setState, ms);
  return [state, setStateDebounced];
}

/**
 * Gets the ADA contrast ratio for WCAG2.0
 *
 * In order to meet the guidelines at Level AA, text or images of text must have a
 * contrast ratio of at least 4.5:1 (or 3:1 for large text). In order to meet the
 * guidelines at the stricter Level AAA, the contrast ratio must be at least
 * 7:1 (or 4.5:1 for large text).
 *
 * Returns undefined if either parameter is undefined
 *
 * @param foreground The darker color
 * @param background The lighter color
 * @returns The ADA contrast ratio
 */
const getContrastRatio$1 = (foreground, background) => {
  if (!foreground || !background) {
    return undefined;
  }
  const foregroundRGBA = hexToRgb(foreground);
  const backgroundRGBA = hexToRgb(background);
  if (!foregroundRGBA || !backgroundRGBA) {
    return 0;
  }
  //formula comes from https://www.w3.org/TR/WCAG20/#relativeluminancedef
  const L1 = 0.2126 * backgroundRGBA.red + 0.7152 * backgroundRGBA.green * 0.0722 * backgroundRGBA.blue;
  const L2 = 0.2126 * foregroundRGBA.red + 0.7152 * (foregroundRGBA === null || foregroundRGBA === void 0 ? void 0 : foregroundRGBA.green) * 0.0722 * (foregroundRGBA === null || foregroundRGBA === void 0 ? void 0 : foregroundRGBA.blue);
  //formula comes from https://www.w3.org/TR/WCAG20/#contrast-ratiodef
  return (L1 + 0.05) / (L2 + 0.05);
};
function rgbaToHex(color, includeAlpha) {
  let red = color.red.toString(16);
  let green = color.green.toString(16);
  let blue = color.blue.toString(16);
  let alpha = Math.round(color.alpha * 255).toString(16);
  if (red.length === 1) red = '0' + red;
  if (green.length === 1) green = '0' + green;
  if (blue.length === 1) blue = '0' + blue;
  if (alpha.length === 1) alpha = '0' + alpha;
  return '#' + red + green + blue + (includeAlpha ? alpha : '');
}
function hexToRgb(hexInput) {
  const isValid = /^#?[0-9a-fA-F]{8}$|^#?[0-9a-fA-F]{6}$|^#?[0-9a-fA-F]{3,4}$/.test(hexInput);
  if (!isValid) {
    return undefined;
  }
  hexInput = hexInput.replace('#', '');
  let red, green, blue, alpha;
  switch (hexInput.length) {
    case 3:
      red = parseInt(`${hexInput.substr(0, 1)}${hexInput.substr(0, 1)}`, 16);
      green = parseInt(`${hexInput.substr(1, 1)}${hexInput.substr(1, 1)}`, 16);
      blue = parseInt(`${hexInput.substr(2, 1)}${hexInput.substr(2, 1)}`, 16);
      alpha = 1;
      break;
    case 4:
      red = parseInt(`${hexInput.substr(0, 1)}${hexInput.substr(0, 1)}`, 16);
      green = parseInt(`${hexInput.substr(1, 1)}${hexInput.substr(1, 1)}`, 16);
      blue = parseInt(`${hexInput.substr(2, 1)}${hexInput.substr(2, 1)}`, 16);
      alpha = parseInt(`${hexInput.substr(3, 1)}${hexInput.substr(3, 1)}`, 16) / 255;
      break;
    case 6:
      red = parseInt(`${hexInput.substr(0, 2)}`, 16);
      green = parseInt(`${hexInput.substr(2, 2)}`, 16);
      blue = parseInt(`${hexInput.substr(4, 2)}`, 16);
      alpha = 1;
      break;
    case 8:
      red = parseInt(`${hexInput.substr(0, 2)}`, 16);
      green = parseInt(`${hexInput.substr(2, 2)}`, 16);
      blue = parseInt(`${hexInput.substr(4, 2)}`, 16);
      alpha = parseInt(`${hexInput.substr(6, 2)}`, 16) / 255;
      break;
  }
  return isValidColor({
    red,
    green,
    blue,
    alpha
  }) ? {
    red: red,
    green: green,
    blue: blue,
    alpha: alpha
  } : undefined;
}
function isValidColor({
  red,
  green,
  blue,
  alpha
}) {
  return isValidColorChannel(red) && isValidColorChannel(green) && isValidColorChannel(blue) && isValidColorChannel(alpha);
}
function isValidColorChannel(color) {
  return typeof color === 'number' && !Number.isNaN(color);
}

const Record = ({
  object,
  depth = 0,
  visitedSet = new Set()
}) => {
  if (object === undefined) {
    return jsx(Fragment, {
      children: "undefined"
    });
  }
  if (typeof object === 'function') {
    return jsx(Fragment, {
      children: '{function}'
    });
  }
  if (typeof object !== 'object' || object === null) {
    return jsx(Fragment, {
      children: JSON.stringify(object)
    });
  }
  if (visitedSet.has(object)) {
    return jsx(Fragment, {
      children: '{circular reference}'
    });
  }
  if (object instanceof Element) {
    return jsx(Fragment, {
      children: object.outerHTML
    });
  }
  const newVisitedSet = new Set([...visitedSet, object]);
  const isArray = Array.isArray(object);
  const keys = Object.keys(object);
  return jsxs(Fragment, {
    children: [isArray ? '[' : '{', keys.length > 0 && jsx("div", {
      style: {
        position: 'relative',
        left: 32
      },
      children: (Array.isArray(object) ? keys : keys.sort()).map(key => jsx(Entry, {
        name: key,
        value: object[key],
        visitedSet: newVisitedSet,
        depth: depth + 1
      }, key))
    }), isArray ? ']' : '}']
  });
};
const Entry = ({
  name,
  value,
  depth,
  visitedSet
}) => {
  /**
   * This number was chosen with the DebugService in mind to try to balance having to manually expand entries with having too much expanded at once.
   */
  const maxInitialDepth = 2;
  const [isExpanded, setIsExpanded] = React__default.useState(depth < maxInitialDepth);
  const nameElement = jsx("b", {
    children: name
  });
  const cannotExpand = typeof value !== 'object' || value === null || typeof value === 'function' || value instanceof Element || Object.keys(value).length === 0;
  const toggle = cannotExpand ? nameElement : jsx("button", {
    className: "kxi-button kxi-button-outline kxi-small orca",
    style: {
      color: 'black',
      padding: 6
    },
    onClick: () => setIsExpanded(!isExpanded),
    children: nameElement
  });
  const isArray = Array.isArray(value);
  return jsxs("div", {
    children: [toggle, ":", ' ', cannotExpand || isExpanded ? jsx(Record, {
      object: value,
      visitedSet: visitedSet,
      depth: depth
    }) : isArray ? '[...]' : '{...}', ","]
  });
};

class Components {
  constructor() {
    this.mounted = {};
    this.crashed = {};
    /**
     * Unmounted components are stored as {@link WeakRef WeakRefs}
     *     so that we can potentially check on them after they have unmounted,
     *     but, since we won't be storing a strong reference to them,
     *     so they can still be garbage collected.
     *
     * This gives us the benefit of accessing unmounted components without the downside of increased memory.
     *     The downside instead is that we cannot guarantee that unmounted components can be accessed at any point.
     */
    this.unmounted = {};
  }
  all() {
    const all = Object.assign(Object.assign({}, this.mounted), this.crashed);
    for (const id in this.unmounted) {
      const component = this.unmounted[id].deref();
      if (component) {
        all[component.id] = component;
      }
    }
    return all;
  }
  find(id) {
    var _a, _b, _c;
    return (_c = (_a = this.mounted[id]) !== null && _a !== void 0 ? _a : (_b = this.unmounted[id]) === null || _b === void 0 ? void 0 : _b.deref()) !== null && _c !== void 0 ? _c : this.crashed[id];
  }
  mount(component) {
    this.mounted[component.id] = component;
  }
  unmount(component) {
    delete this.mounted[component.id];
    if (component.renderHistory.activeRender) {
      this.crashed[component.id] = component;
    } else {
      this.unmounted[component.id] = new PossibleWeakRef(component);
    }
    // Remove references to unmounted components that have been garbage collected
    for (const id in this.unmounted) {
      if (this.unmounted[id].deref() === undefined) {
        delete this.unmounted[id];
      }
    }
  }
  toJSON() {
    return {
      crashed: map(this.crashed, component => component.toJSON()),
      mounted: map(this.mounted, component => component.toJSON())
    };
  }
}

class LogOptions {
  constructor(localStorageKey) {
    this.onMount = false;
    this.onRender = false;
    this.onEffect = false;
    this.onUnmount = false;
    if (localStorageKey !== undefined) {
      const rawStored = window.localStorage.getItem(localStorageKey);
      if (rawStored !== null) {
        const stored = JSON.parse(rawStored);
        typeof stored.onMount === 'boolean' && (this.onMount = stored.onMount);
        typeof stored.onRender === 'boolean' && (this.onRender = stored.onRender);
        typeof stored.onEffect === 'boolean' && (this.onEffect = stored.onEffect);
        typeof stored.onUnmount === 'boolean' && (this.onUnmount = stored.onUnmount);
      }
      // This is an alternative to putting a getter and setter on every class property
      return new Proxy(this, {
        set(self, key, value) {
          if (key in self && typeof value === 'boolean') {
            self[key] = value;
            if (localStorageKey) {
              window.localStorage.setItem(localStorageKey, JSON.stringify(self));
            }
            return true;
          }
          return false;
        }
      });
    }
  }
}

var _PersistedValue_value;
class PersistedValue {
  constructor(key) {
    this.key = key;
    _PersistedValue_value.set(this, void 0);
    const persistedValue = localStorage.getItem(key);
    __classPrivateFieldSet(this, _PersistedValue_value, persistedValue !== null ? JSON.parse(persistedValue) : null, "f");
  }
  get() {
    return __classPrivateFieldGet(this, _PersistedValue_value, "f");
  }
  set(newValue) {
    __classPrivateFieldSet(this, _PersistedValue_value, newValue, "f");
    localStorage.setItem(this.key, JSON.stringify(newValue));
  }
}
_PersistedValue_value = new WeakMap();

let isSpying = 0;
function disableReactHookSpying() {
  isSpying = isSpying + 1;
}
function enableReactHookSpying() {
  isSpying = Math.max(0, isSpying - 1);
}
function spyOnReactHooks(spyFn) {
  const hooks = ['useCallback', 'useContext', 'useDebugValue', 'useEffect', 'useImperativeHandle', 'useLayoutEffect', 'useMemo', 'useReducer', 'useRef', 'useState'];
  hooks.forEach(name => {
    spyOnReactHook(name, (args, returnValue, stack) => {
      spyFn({
        name,
        args,
        returnValue,
        stack
      });
    });
  });
}
function spyOnReactHook(name, spyFn) {
  const react = React__default;
  const hook = react[name];
  react[name] = (...args) => {
    var _a;
    const returnValue = hook(...args);
    if (isSpying === 0) {
      spyFn(args, returnValue, (_a = new Error().stack) === null || _a === void 0 ? void 0 : _a.split('\n').filter(s => s.includes('use')).map(s => s.trim()));
    }
    return returnValue;
  };
  react[name].displayName = name;
}

function getReplacer() {
  const visitedSet = new Set();
  return (_key, value) => {
    if (!isObject(value)) {
      if (typeof value === 'function') {
        return '{function}';
      }
      return value;
    }
    if (visitedSet.has(value)) {
      return '{circular}';
    }
    visitedSet.add(value);
    if (value === globalThis) {
      return `{window}`;
    }
    if (value instanceof Element) {
      return `<${value.tagName.toLowerCase()} ${Array.from(value.attributes).map(attr => `${attr.nodeName}="${attr.textContent}"`).join(' ')}>`;
    }
    if (value instanceof CSSStyleDeclaration) {
      return '{CSSStyleDeclaration}';
    }
    if (value instanceof MutationObserver) {
      return '{MutationObserver}';
    }
    if (value instanceof ResizeObserver) {
      return '{ResizeObserver}';
    }
    if (value instanceof IntersectionObserver) {
      return '{IntersectionObserver}';
    }
    // React adds a lot of extra information to components and elements at runtime
    if (value instanceof React__default.Component) {
      return pick(value, 'context', 'props', 'state');
    }
    if ( /*#__PURE__*/React__default.isValidElement(value)) {
      return pick(value, 'key', 'props', 'type');
    }
    if (value instanceof DebugService) {
      return `DebugService@${value.id}`;
    }
    // See `MotionContext` in framer-motion
    for (const framerMotionKey of ['visualElement', 'initiate', 'animate']) {
      if (framerMotionKey in value) {
        return Object.assign(Object.assign({}, value), {
          [framerMotionKey]: `{${framerMotionKey}}`
        });
      }
    }
    if ('Provider' in value && 'Consumer' in value) {
      return '{React.Context}';
    }
    return value;
  };
}
function toJSON(value, visitedSet = new Set(), replacer = getReplacer()) {
  if (typeof value !== 'object' || value === null) {
    return value;
  }
  const obj = value;
  if (visitedSet.has(obj)) {
    return '{circular}';
  }
  visitedSet.add(obj);
  const copy = Array.isArray(obj) ? [...obj] : Object.assign({}, obj);
  Object.entries(copy).forEach(([key, value]) => {
    copy[key] = toJSON(replacer(key, value), visitedSet, replacer);
  });
  return copy;
}

/** Holds relevant data for one render of a component. */
class RenderEntry {
  constructor() {
    this.hooks = [];
    this.watchedData = {};
  }
  onStart(props) {
    this.props = props;
    this.startTime = Date.now();
    this.zone = typeof Zone !== 'undefined' ? Zone.current : undefined;
  }
  /** Call this after this entry's render ends but before the next render begins. */
  onEnd() {
    this.endTime = Date.now();
  }
  onHook(hookData) {
    this.hooks.push(hookData);
  }
  watch(values) {
    Object.assign(this.watchedData, values);
  }
  toJSON() {
    return Object.assign(Object.assign({
      props: toJSON(this.props),
      startTime: this.startTime,
      endTime: this.endTime,
      hooks: this.hooks ? this.hooks.map(h => toJSON(h)) : undefined
    }, Object.keys(this.watchedData).length > 0 && {
      watchedData: toJSON(this.watchedData)
    }), {
      zone: this.zone && `${this.zone.name} (isAngularZone = ${this.zone.get('isAngularZone') === true})`
    });
  }
}

class RenderHistory {
  constructor() {
    this.renders = [];
  }
  onRenderStart(props) {
    this.activeRender = new RenderEntry();
    this.activeRender.onStart(props);
  }
  onRenderEnd() {
    if (!this.activeRender) {
      return;
    }
    this.activeRender.onEnd();
    this.renders.push(this.activeRender);
    this.activeRender = undefined;
  }
  onReactHook(hookData) {
    var _a;
    (_a = this.activeRender) === null || _a === void 0 ? void 0 : _a.onHook(hookData);
  }
  watch(value) {
    var _a;
    (_a = this.activeRender) === null || _a === void 0 ? void 0 : _a.watch(value);
  }
  toJSON() {
    return Object.assign({
      renders: this.renders.map(r => r.toJSON())
    }, this.activeRender && {
      activeRender: this.activeRender.toJSON()
    });
  }
}

var _DebugService_instances, _a, _DebugService_count, _DebugService_isEnabled, _DebugService_checkMutations, _DebugService_activeInstance, _DebugService_logger, _DebugService_isDevMode, _DebugService_hasError, _DebugService_isActive_get, _DebugService_onReactHook, _DebugService_guard, _DebugService_infoIf, _DebugService_getParentComponentIds, _DebugService_getChildComponentIds, _DebugService_reactHookSpy, _DebugService_viewInModal, _DebugService_getUrl, _DebugService_warnDisabled;
function deepFreeze(value) {
  if (typeof value !== 'object' || value === null) {
    return;
  }
  // Skip refs
  if (Object.keys(value).length === 1 && 'current' in value) {
    return;
  }
  // Skip elements
  if (value instanceof Element || /*#__PURE__*/React__default.isValidElement(value)) {
    return;
  }
  if (!Object.isFrozen(value)) {
    Object.freeze(value);
  }
  Object.values(value).forEach(deepFreeze);
}
/**
 * A debugger for named React function components. `displayName` must be set for the component.
 *
 * Named function components include:
 * - {@link React.FunctionComponent}
 * - {@link React.NamedExoticComponent}:
 *   - Components returned by {@link React.forwardRef} ({@link React.ForwardRefExoticComponent})
 *   - Components returned by {@link React.memo} ({@link React.MemoExoticComponent})
 */
class DebugService extends LoggingService {
  /** If true, collects render data about components. */
  static get isEnabled() {
    var _b;
    return (_b = __classPrivateFieldGet(this, _a, "f", _DebugService_isEnabled).get()) !== null && _b !== void 0 ? _b : false;
  }
  static set isEnabled(newValue) {
    __classPrivateFieldGet(this, _a, "f", _DebugService_isEnabled).set(newValue);
    newValue ? enableReactHookSpying() : disableReactHookSpying();
  }
  static get checkMutations() {
    var _b;
    return (_b = __classPrivateFieldGet(this, _a, "f", _DebugService_checkMutations).get()) !== null && _b !== void 0 ? _b : false;
  }
  static set checkMutations(newValue) {
    __classPrivateFieldGet(this, _a, "f", _DebugService_checkMutations).set(newValue);
  }
  static get activeInstance() {
    return __classPrivateFieldGet(this, _a, "f", _DebugService_activeInstance);
  }
  constructor(Component) {
    var _b;
    const displayName = Component.displayName;
    __classPrivateFieldGet(DebugService, _a, "f", _DebugService_logger).throwIf(!displayName, 'Component without `displayName` mounted');
    super(`${displayName}-${__classPrivateFieldGet(DebugService, _a, "f", _DebugService_count)}`, __classPrivateFieldGet(DebugService, _a, "f", _DebugService_isDevMode));
    _DebugService_instances.add(this);
    /**
     * Used to track if an exception was thrown during any work done by the debug service.
     */
    _DebugService_hasError.set(this, false);
    this.logOptions = new LogOptions();
    this.renderHistory = new RenderHistory();
    __classPrivateFieldSet(_b = DebugService, _a, __classPrivateFieldGet(_b, _a, "f", _DebugService_count) + 1, "f", _DebugService_count);
    this.componentProps = {
      'data-kitchen-debug-id': this.id
    };
  }
  /** Store information for each render that is not a prop or a hook. */
  watch(values) {
    __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_guard).call(this, () => {
      this.renderHistory.watch(values);
    });
  }
  /**
   * Call this once when the component mounts.
   * Must be called after `onEffect`.
   */
  onMount() {
    __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_guard).call(this, () => {
      __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_infoIf).call(this, 'onMount', 'Mounted');
      this.mountTime = Date.now();
      DebugService.components.mount(this);
    });
  }
  /** Call this once when the component unmounts. */
  onUnmount() {
    __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_guard).call(this, () => {
      __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_infoIf).call(this, 'onUnmount', 'Unmounting');
      this.unmountTime = Date.now();
      DebugService.components.unmount(this);
    });
  }
  /** Call this once per render while the render function is executing (NOT in an effect). */
  // Everything that happens in `onRender` MUST be pure.
  onRender(props) {
    __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_guard).call(this, () => {
      __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_infoIf).call(this, 'onRender', 'Rendering', {
        props
      });
      __classPrivateFieldSet(DebugService, _a, this, "f", _DebugService_activeInstance);
      if (DebugService.checkMutations) {
        deepFreeze(props);
      }
      this.renderHistory.onRenderStart(props);
    });
  }
  /** Call this after each render in a {@link React.useEffect useEffect}. */
  onEffect() {
    __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_guard).call(this, () => {
      __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_infoIf).call(this, 'onEffect', 'Effect');
      this.renderHistory.onRenderEnd();
    });
  }
  toJSON() {
    return {
      id: this.id,
      mountTime: this.mountTime,
      unmountTime: this.unmountTime,
      renderHistory: this.renderHistory.toJSON()
    };
  }
  toString(pretty = true) {
    const children = this.getChildComponents();
    const json = Object.assign(Object.assign({}, children && {
      children
    }), this.toJSON());
    return JSON.stringify(json, getReplacer(), pretty ? ' ' : undefined);
  }
  download(pretty) {
    DebugService.download(this.id, pretty);
  }
  view() {
    __classPrivateFieldGet(DebugService, _a, "m", _DebugService_viewInModal).call(DebugService, this.toJsx());
  }
  toJsx() {
    return jsx(Record, {
      object: this.toJSON()
    });
  }
  getElement() {
    return document.querySelector(`[data-kitchen-debug-id='${this.id}']`);
  }
  getParentComponents() {
    const parentIds = new Set(__classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_getParentComponentIds).call(this));
    if (parentIds.size === 0) {
      return null;
    }
    return filter(DebugService.components.mounted, c => parentIds.has(c.id));
  }
  getParentComponent() {
    const parents = __classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_getParentComponentIds).call(this);
    return parents ? DebugService.components.mounted[parents[0]] : null;
  }
  getChildComponents() {
    const childIds = new Set(__classPrivateFieldGet(this, _DebugService_instances, "m", _DebugService_getChildComponentIds).call(this));
    if (childIds.size === 0) {
      return null;
    }
    return filter(DebugService.components.mounted, c => childIds.has(c.id));
  }
  getDirectChildComponents() {
    const childComponents = this.getChildComponents();
    return childComponents ? filter(childComponents, component => component.getParentComponent() === this) : null;
  }
  static toJSON() {
    return this.components.toJSON();
  }
  static toString(pretty = true) {
    return JSON.stringify(this.getComponentTree(), getReplacer(), pretty ? ' ' : undefined);
  }
  static toJsx() {
    return jsx(Record, {
      object: this.toJSON()
    });
  }
  static getComponentTree() {
    const mounted = Object.assign({}, this.components.mounted);
    Object.keys(mounted).forEach(id => getTree(id, mounted));
    const crashed = Object.assign({}, this.components.crashed);
    Object.keys(crashed).forEach(id => getTree(id, crashed));
    return {
      crashed,
      mounted
    };
    function getTree(id, curr, root = curr) {
      const component = root[id];
      // already visited
      if (component === undefined) {
        return;
      }
      const children = component.getDirectChildComponents();
      if (!children) {
        return;
      }
      Object.keys(children).forEach(childId => {
        getTree(childId, children, root);
        delete root[childId];
      });
      curr[id] = Object.assign(Object.assign({}, component), {
        children,
        toJSON: () => Object.assign({
          children
        }, component.toJSON()),
        getParentComponents: component.getParentComponents.bind(component),
        getParentComponent: component.getParentComponent.bind(component),
        getChildComponents: component.getChildComponents.bind(component),
        getDirectChildComponents: component.getDirectChildComponents.bind(component)
      });
    }
  }
  static download(id, pretty) {
    if (!this.isEnabled) {
      __classPrivateFieldGet(DebugService, _a, "m", _DebugService_warnDisabled).call(DebugService);
      return;
    }
    const {
      url,
      revokeUrl
    } = __classPrivateFieldGet(this, _a, "m", _DebugService_getUrl).call(this, id, pretty);
    const a = document.createElement('a');
    a.href = url;
    a.download = `theKitchen-debug${id ? `-${id}` : ''}-${Date.now()}.json`;
    a.click();
    revokeUrl();
  }
  static dump(id) {
    if (!this.isEnabled) {
      __classPrivateFieldGet(DebugService, _a, "m", _DebugService_warnDisabled).call(DebugService);
      return;
    }
    const {
      url,
      revokeUrl
    } = __classPrivateFieldGet(this, _a, "m", _DebugService_getUrl).call(this, id, true);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.click();
    revokeUrl();
  }
  static get documentation() {
    const debugInstance = {
      download: "((pretty?: boolean) => void) Downloads this component's data",
      getElement: "(() => HTMLElement | null) Returns this component's DOM element.",
      getParentComponent: "(() => DebugService | null) Returns this component's parent component.",
      getParentComponents: "(() => Record<string, DebugService> | null) Returns a subset of mounted components including this component's parent components.",
      getChildComponents: '(() => Record<string, DebugService> | null) Returns a subset of mounted components including all components that are children of this component OR children of children.',
      getDirectChildComponents: '(() => object) Returns a subset of mounted components that are direct children of this component.',
      id: "This component's ID used by the DebugService. This is different from the HTML 'id' attribute.",
      logOptions: "An object containing log options for different stages of this component's life cycle.",
      mountTime: 'The time at which this component mounted.',
      renderHistory: 'An object containing data for each render.',
      view: "Displays a modal with this component's debug information.",
      unmountTime: 'The time at which this component unmounted.'
    };
    const theKitchen = {
      checkMutations: 'A boolean indicating whether to check for prop mutations.',
      components: 'An object containing components currently in the DOM.',
      download: '((pretty?: boolean) => void) Downloads an JSON file containing all component data.',
      dump: '(() => void) Opens a page containing all component data in JSON format.',
      getComponentTree: '(() => object) Returns an tree-like object of components where each component references its own children.',
      logOptions: 'An object containing log options for all mounted Kitchen components.',
      isEnabled: 'A boolean indicating whether the DebugService is enabled. If false, no component data will be collected.',
      view: 'Displays a modal with debug information for all components.'
    };
    const logOptions = {
      onEffect: 'A boolean determining whether to log after each component render (Runs in `useEffect`).',
      onMount: 'A boolean determining whether to log when the component mounts.',
      onRender: 'A boolean determining whether to log at the beginning of each component render.',
      onUnmount: 'A boolean determining whether to log when the component unmounts.'
    };
    const howTo = {
      "find your component's ID": steps('Inspect your component in dev tools.', ["Find the element with the attribute 'data-kitchen-debug-id'. This may be a child element.", 'Note: Many of our components use other Kitchen components,', "so there may be multiple child elements with the 'data-kitchen-debug-id' attribute.", 'If this is the case, use the outermost element whose ID starts with the name of your component.'].join(' ')),
      'debug your component': steps("Copy your component's ID to the clipboard", "In the dev tools console, create a variable called 'id' with the value of this attribute as a string. Example: `const id = 'component-id';`.", {
        'Do one of the following': {
          'Option 1 (recommended)': steps('In the browser console, run `theKitchen.download();`.', "Post in #the-kitchen-inquiries Slack channel with a description of your issue, your component's ID, and the downloaded file. Link: https://inmoment.slack.com/archives/CLVEJSEKX"),
          'Option 2': steps('In the browser console, run `theKitchen.dump();`.', 'In the new tab/window that opened, find your component via its ID, and look its data for issues.'),
          'Option 3': steps("In the browser console, run `theKitchen.components.mounted[{your component ID}];` to get your component's data.", "Look through your component's data for issues.")
        }
      })
    };
    return {
      'How to...': howTo,
      theKitchen,
      component: debugInstance,
      logOptions
    };
    function steps(...steps) {
      return steps.reduce((acc, curr, index) => {
        acc[`Step ${index + 1}:`] = curr;
        return acc;
      }, {});
    }
  }
  static view() {
    __classPrivateFieldGet(DebugService, _a, "m", _DebugService_viewInModal).call(DebugService, this.toJsx());
  }
}
_a = DebugService, _DebugService_hasError = new WeakMap(), _DebugService_instances = new WeakSet(), _DebugService_isActive_get = function _DebugService_isActive_get() {
  return DebugService.isEnabled && __classPrivateFieldGet(DebugService, _a, "f", _DebugService_isDevMode) && !__classPrivateFieldGet(this, _DebugService_hasError, "f");
}, _DebugService_onReactHook = function _DebugService_onReactHook(data) {
  this.renderHistory.onReactHook(data);
}, _DebugService_guard = function _DebugService_guard(fn) {
  if (!__classPrivateFieldGet(this, _DebugService_instances, "a", _DebugService_isActive_get)) {
    return;
  }
  try {
    fn();
  } catch (e) {
    __classPrivateFieldSet(this, _DebugService_hasError, true, "f");
    this.error('An error was encountered. This component will be removed from the debug service.', e);
    DebugService.components.unmount(this);
  }
}, _DebugService_infoIf = function _DebugService_infoIf(key, ...data) {
  if (DebugService.logOptions[key] || this.logOptions[key]) {
    this.info(...data);
  }
}, _DebugService_getParentComponentIds = function _DebugService_getParentComponentIds() {
  const el = this.getElement();
  if (!el) {
    return null;
  }
  const parentIds = [];
  let curr = el.parentElement;
  while (curr !== null) {
    const id = curr.getAttribute('data-kitchen-debug-id');
    id && parentIds.push(id);
    curr = curr.parentElement;
  }
  if (parentIds.length === 0) {
    return null;
  }
  return parentIds;
}, _DebugService_getChildComponentIds = function _DebugService_getChildComponentIds() {
  const el = this.getElement();
  if (!el) {
    return null;
  }
  return Array.from(el.querySelectorAll('[data-kitchen-debug-id]')).map(e => e.getAttribute('data-kitchen-debug-id')).filter(isNotNullish);
}, _DebugService_reactHookSpy = function _DebugService_reactHookSpy(data) {
  const activeInstance = __classPrivateFieldGet(this, _a, "f", _DebugService_activeInstance);
  if (activeInstance) {
    __classPrivateFieldGet(activeInstance, _DebugService_instances, "m", _DebugService_onReactHook).call(activeInstance, data);
  }
}, _DebugService_viewInModal = function _DebugService_viewInModal(element) {
  // Disable scroll
  const bodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  const container = document.createElement('div');
  document.body.appendChild(container);
  ReactDOM__default.render(jsxs("div", {
    style: {
      position: 'absolute',
      top: window.scrollY,
      left: window.scrollX,
      height: '100%',
      width: '100%',
      background: 'white',
      zIndex: 1000000,
      overflow: 'auto'
    },
    children: [jsx("div", {
      children: jsx("button", {
        style: {
          position: 'fixed',
          top: 6,
          right: 6
        },
        onClick: () => {
          ReactDOM__default.unmountComponentAtNode(container);
          container.remove();
          document.body.style.overflow = bodyOverflow;
        },
        children: "Close"
      })
    }), element]
  }), container);
}, _DebugService_getUrl = function _DebugService_getUrl(id, pretty) {
  const toDownload = id ? this.components.find(id) : DebugService;
  if (!toDownload) {
    throw new Error(`Cannot find component with id "${id}"`);
  }
  const url = URL.createObjectURL(new Blob([toDownload.toString(pretty)], {
    type: 'application/json'
  }));
  return {
    url,
    revokeUrl: () => URL.revokeObjectURL(url)
  };
}, _DebugService_warnDisabled = function _DebugService_warnDisabled() {
  __classPrivateFieldGet(this, _a, "f", _DebugService_logger).warn('The debug service is disabled. Run `theKitchen.isEnabled = true;` to enable.');
};
_DebugService_count = {
  value: 0
};
_DebugService_isEnabled = {
  value: new PersistedValue('kitchen-debug-enabled')
};
/** If true, checks for mutations in component props. */
_DebugService_checkMutations = {
  value: new PersistedValue('kitchen-debug-mutations')
};
_DebugService_activeInstance = {
  value: void 0
};
DebugService.components = new Components();
DebugService.logOptions = new LogOptions('kitchen-debug-config');
_DebugService_logger = {
  value: new LoggingService('DebugService')
};
_DebugService_isDevMode = {
  value: !isProdEnvironment()
};
(() => {
  if (__classPrivateFieldGet(_a, _a, "f", _DebugService_isDevMode)) {
    spyOnReactHooks(data => __classPrivateFieldGet(_a, _a, "m", _DebugService_reactHookSpy).call(_a, data));
    _a.isEnabled ? enableReactHookSpying() : disableReactHookSpying();
    window.theKitchen = _a;
    const enabledMessage = _a.isEnabled ? 'The debugger is running!' : 'Debugging is disabled by default. Run `theKitchen.isEnabled = true;` in your browser console to get started!';
    __classPrivateFieldGet(_a, _a, "f", _DebugService_logger).info('Welcome to the Kitchen debugger (beta)!', enabledMessage, 'Run `theKitchen.documentation` in your browser console for more information.');
  }
})();

/**
 * Debugs a component via {@link DebugService}.
 *
 * Steps to implement the debug service in a component:
 *   1. Add the `displayName` property to your component.
 *      Example: `MyComponent.displayName = 'MyComponent';`
 *      Note that the `displayName` string does not have to be identical to the component name,
 *      but we want it the same in most cases.
 *   2. At the beginning of the render function (before the props destructuring), add the `useDebugService` hook.
 *      Example: `const debugService = useDebugService(MyComponent, props);`
 *   3. (If needed) Replace all instances of "React.use" in the render function with "use" (find and replace all)
 *   6. On the outermost reasonable native element (div, input, button, etc.) returned from the render function, spread the `componentProps`
 *      Example: `return <div {...debugService.componentProps}>...</div>;`
 *
 * @param Component The component's render function .
 * @param props The current render's `props` object.
 * @returns An object with format `{ componentProps, ...allReactHooks }`.
 *     Pass `componentProps` to the outermost element (as reasonable) returned by the render function.
 *
 * @example
 * export const MyComponent = (props: MyComponentProps) => {
 *   const debugService = useDebugService(MyComponent, props);
 *   const [text, setText] = React.useState('My component!');
 *   return <div {...debugService.componentProps}>{text}</div>
 * }
 * MyComponent.displayName = 'MyComponent'; // This is required
 */
function useDebugService(Component, props) {
  disableReactHookSpying();
  const [debugService] = React__default.useState(() => new DebugService(Component));
  debugService.onRender(props);
  React__default.useEffect(() => {
    debugService.onMount();
  }, [debugService]);
  React__default.useEffect(() => {
    debugService.onEffect();
  });
  React__default.useEffect(() => {
    return () => {
      debugService.onUnmount();
    };
  }, [debugService]);
  enableReactHookSpying();
  return debugService;
}

// /* eslint react-hooks/exhaustive-deps : error */
function useUuid() {
  const [uuid] = React.useState(() => uuidv4());
  return uuid;
}
function useIsMounted() {
  const isMountedRef = React.useRef(false);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return isMountedRef;
}
function useIsFirstRender() {
  const isFirstRenderRef = React.useRef(true);
  React.useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);
  return isFirstRenderRef.current;
}
function getXiColor(color) {
  const parts = typeof color === 'string' ? color.split('_') : color;
  return parts.reduce((acc, curr) => acc === null || acc === void 0 ? void 0 : acc[curr], XIColors);
}
/**
 * This function takes in a color name and outputs either a colorClass for standard color
 * schemes or customColor, contrastingLightColor, and activeColor for non-standard colors
 *
 * Notes:
 * - Silver is special because it doesn't have a color scheme, just a single color so its
 *   active and light color states have to be manually defined
 * - For the fill type the contrasting light color is used during hover and is 1 color
 *   step above
 * - For text and outline types the light color is used for hover and is light_95 and the
 *   active state is used when the button is "active" and hasState is true
 * - In high contrast mode all icons & icon buttons are white
 *
 * @param color The color passed in by the user
 * @param contrast The contrast option
 * @param mode The fill mode of the button (if required)
 * @returns an object of:
 * { customColor, contrastingLightColor, activeColor, colorClass };
 * Where
 *    customColor is the hex code of the custom color (or undefined if the color is standard)
 *    contrastingLightColor is the hex code of the custom color category, either light_95 or
 * 1 step above the custom color. Its used for background fills (undefined if the color is standard)
 *    activeColor is the hex code for 1 color step below the custom light color (or undefined if
 * the color is standard)
 *    colorClass is the class for the color (sets all the primary CSS variables) if the color
 * is standard, and undefined otherwise
 */
const useCustomColor = (color, contrast, mode) => {
  let customColor;
  let contrastingLightColor;
  let contrastingLightColorName;
  let iconColor;
  let activeColor;
  let activeColorName;
  let colorClass;
  if (isAllColors(color)) {
    // color will be of one of the formats: color, color_primary, color_light_##, color_dark_##
    // Split by _, then use those as properties to XIColors to get the value assigned to customColor
    // For example, if color === 'yellow_light_15', customColor = XIColors.yellow.light['15']
    const xiColorsKeys = color.split('_');
    customColor = getXiColor(color);
    if (mode === 'fill') {
      // When the mode is fill, the icon will always be in a high contrast state which is
      // currently hard-coded to white. Do this here to avoid a ternary later on.
      iconColor = '#ffffff';
    }
    if (color === 'silver' && (mode === 'text' || mode === 'outline' || mode === 'fill')) {
      // Silver is a strange edge case, manually set colors
      contrastingLightColor = XIColors.white.light['95'];
      contrastingLightColorName = 'white_light_95';
      activeColor = XIColors.white.light['90'];
      activeColorName = 'white_light_90';
    } else if (mode === 'text' || mode === 'outline') {
      // We know that the active should be light_90 and the hover should be light_95
      contrastingLightColor = getXiColor([xiColorsKeys[0], 'light', '95']);
      contrastingLightColorName = `${xiColorsKeys[0]}_light_95`;
      activeColor = getXiColor([xiColorsKeys[0], 'light', '90']);
      activeColorName = `${xiColorsKeys[0]}_light_90`;
    } else if (mode === 'fill') {
      // We have to lay out all the possible options in order from darkest to lightest in
      // order to find the color that is the next "step" up for the hover state
      const colorOptions = ['dark_55', 'dark_40', 'dark_15', 'primary', 'light_15', 'light_30', 'light_40', 'light_50', 'light_60', 'light_70', 'light_80', 'light_90', 'light_95'];
      // find the hover color that is 1 step lighter than the chosen color
      let colorOptionIndex = colorOptions.findIndex(opt => opt === (xiColorsKeys.length > 2 ? `${xiColorsKeys[1]}_${xiColorsKeys[2]}` : xiColorsKeys[1])) + 1;
      if (colorOptionIndex === colorOptions.length) {
        colorOptionIndex -= 1;
      }
      contrastingLightColor = getXiColor([xiColorsKeys[0], ...colorOptions[colorOptionIndex].split('_')]);
      contrastingLightColorName = [xiColorsKeys[0], colorOptions[colorOptionIndex]].join('_');
    }
  } else if (contrast !== 'high') {
    // if the color is not a "custom color" simply return the class that will set the
    // correct CSS variables
    colorClass = normalizeColor(color);
  }
  // for custom colors make sure they don't violate ADA requirements. IF the do throw an error
  const useContrastWarn = (contrastRatio, color1, color2, dependencies) => {
    useExecuteSideEffectIf(contrastRatio !== undefined && contrastRatio < 4.5, () => devOnlyWarn(`The Kitchen - IconButton: Non ADA Compliant color combination (contrast ratio < 4.5 between input color ${color1} and background color ${color2}). Current contrast ratio is ${contrastRatio}`), dependencies);
  };
  useContrastWarn(getContrastRatio$1(contrastingLightColor, iconColor), 'white', contrastingLightColorName, [contrastingLightColor, iconColor]);
  useContrastWarn(getContrastRatio$1(customColor, contrastingLightColor), contrastingLightColorName, color, [customColor, contrastingLightColor]);
  useContrastWarn(getContrastRatio$1(customColor, activeColor), activeColorName, color, [activeColor, customColor]);
  return {
    customColor,
    contrastingLightColor,
    activeColor,
    colorClass
  };
};
function getScreenType(width = window.innerWidth) {
  return width >= 960 ? 'desktop' : width >= 480 ? 'tablet' : 'mobile';
}
function useOnWindowResize(onResize, ms = 100) {
  const onResizeThrottled = useThrottledCallback(onResize, ms);
  React.useEffect(() => {
    const resizeHandler = () => onResizeThrottled({
      height: window.innerHeight,
      width: window.innerWidth
    });
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [onResizeThrottled]);
}
function useWindowSize() {
  const [dimensions, setDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth,
    size: getScreenType()
  });
  useOnWindowResize(React.useCallback(({
    height,
    width
  }) => setDimensions({
    height,
    width,
    size: getScreenType(width)
  }), []));
  return dimensions;
}
function useOnMouseMove(onMouseMoveInput) {
  const onMouseMoveRef = useRefToValue(onMouseMoveInput);
  React.useEffect(() => {
    const onMouseMove = e => {
      onMouseMoveRef.current(e);
    };
    window.addEventListener('mousemove', onMouseMove, {
      passive: true
    });
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);
}
function useOnMousePositionChange(onChange) {
  useOnMouseMove(e => {
    onChange({
      x: e.clientX,
      y: e.clientY
    });
  });
}
/**
 * `onMouseEnter` and `onMouseLeave` don't always fire correctly.
 * This hook listens to all mouse movements to determine whether the mouse is over the element.
 *
 * Debounced so that mouse movements between valid elements aren't interpreted as stopping hovering
 */
function useIsHoveringOverAnyOfElements(...elements) {
  const [isHovering, setIsHovering] = React.useState(false);
  useOnMousePositionChange(useDebouncedCallback(position => {
    const hoveredElement = document.elementFromPoint(position.x, position.y);
    const newIsHovering = hoveredElement !== null && elements.some(el => el !== null && el.contains(hoveredElement));
    if (isHovering !== newIsHovering) {
      setIsHovering(newIsHovering);
    }
  }, 10));
  return isHovering;
}
/**
 * Like using the `size` property in the return value of {@link useWindowSize},
 *     but it only updates when the screen crosses the threshold between types.
 */
function useScreenType() {
  const [screenType, setScreenType] = React.useState(() => getScreenType());
  const screenTypeRef = useRefToValue(screenType);
  useOnWindowResize(React.useCallback(({
    width
  }) => {
    const newScreenType = getScreenType(width);
    if (screenTypeRef.current !== newScreenType) {
      screenTypeRef.current = newScreenType;
      setScreenType(newScreenType);
    }
  }, [screenTypeRef]));
  return screenType;
}
function useIsMobile() {
  const screenType = useScreenType();
  return screenType === 'mobile';
}
function usePreventScroll(prevent, target) {
  React.useEffect(() => {
    if (!prevent || !target) {
      return;
    }
    const overflow = target.style.overflow;
    target.style.overflow = 'hidden';
    return () => {
      target.style.overflow = overflow;
    };
  }, [prevent, target]);
}
function useForceRerender() {
  // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [, forceRerender] = React.useReducer(n => n + 1, 0);
  return forceRerender;
}
/**
 * @deprecated Use {@link React.useState}.
 */
function useConstant(value) {
  return React.useRef(value).current;
}
/**
 * Returns a function which will log a warning message *only* the first time it is called.
 * This hook ignores the provided parameters after the first call.
 *
 * @deprecated Use {@link useWarnOnceIf}.
 */
function useOneTimeWarning(warningMessage, devOnly = true) {
  const alreadyWarned = React.useRef(false);
  const warnFn = React.useRef(() => {
    if (alreadyWarned.current) {
      return;
    }
    alreadyWarned.current = true;
    if (devOnly) {
      devOnlyWarn(warningMessage);
    } else {
      console.warn(warningMessage);
    }
  });
  return warnFn.current;
}
function getNamedClockDirection(direction) {
  if (isNamedClockDirection(direction)) {
    return direction;
  }
  return _NamedClockDirectionOptions[_NumberedClockDirectionOptions.indexOf(direction)];
}
/**
 * This function warns users of potential issues and of unexpected and non-tested
 * behavior. This is mainly meant for use cases where we have a complex element mapped to
 * a non-conforming field (e.g. DatePicker to input) and some of the events may not align
 * as expected, fire as expected or may have untested behavior
 *
 * @param objectToWarn An object containing the prop to warn about
 * @param componentName The name of the component where this function is being called from
 */
function useWarnUnexpectedBehavior(objectToWarn, componentName) {
  const name = Object.keys(objectToWarn)[0];
  useWarnOnceIf(!!objectToWarn['name'], `KITCHEN WARNING: ${name} was passed into the ${componentName} component. This prop is untested, unsupported, and may behave unexpectedly. If you intend on using this prop in production code please contact the Kitchen team so we can ensure that it will behave as expected.`);
}
function useWarnOnceIf(condition, ...args) {
  useExecuteSideEffectOnceIf(condition, () => devOnlyWarn(...args));
}
function useErrorOnceIf(condition, ...args) {
  useExecuteSideEffectOnceIf(condition, () => devOnlyError(...args));
}
function useErrorIfRequiredPropIsNotProvided(debugService, props, ...keys) {
  const missingKeys = keys.filter(key => props[key] === undefined);
  debugService.throwIf(missingKeys.length > 0, `The following required props are missing: ${JSON.stringify(missingKeys, undefined, '    ')}`);
}
function useExecuteSideEffectIf(condition, fn, dependencies) {
  React.useEffect(() => {
    condition && fn();
  }, dependencies);
}
function useExecuteSideEffectOnceIf(condition, fn, dependencies) {
  const hasExecuted = React.useRef(false);
  React.useEffect(() => {
    if (hasExecuted.current) {
      return;
    }
    if (condition) {
      hasExecuted.current = true;
      fn();
    }
  }, dependencies);
}
/**
 * Be careful that {@link value} does not change every render to avoid infinite loops.
 *
 * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 * @example
 * const { data } = props;
 * const [myData, setMyData] =React.useState(data);
 * // ...
 * setMyData(myUpdatedData);
 */
function useValueBackedState(value, shouldUpdate = () => true) {
  const [prevValue, setPrevValue] = React.useState(value);
  const [valueState, setValueState] = React.useState(value);
  if (!Object.is(prevValue, value) && shouldUpdate(valueState, value)) {
    setValueState(value);
    setPrevValue(value);
  }
  return [valueState, setValueState];
}
/**
 * A hook for returning an auto generated (uuid) id if the provided id is nullish
 * @param id The input id from the user
 * @returns A non-nullish id
 */
function useOptionalId(id) {
  const [autoGeneratedId, setAutoGeneratedId] = React.useState();
  if (!autoGeneratedId && !id && id !== 0) {
    const newId = uuidv4();
    setAutoGeneratedId(newId);
    return newId;
  } else if (!id && id !== 0) {
    return autoGeneratedId;
  }
  return id;
}
/**
 * Like {@link React.useMemo useMemo} but stored in a {@link React.useState state}.
 * Changes to {@link deps} will cause a re-render.
 *
 * Related to {@link useValueBackedState}.
 */
function useComputedState(factory, deps) {
  const [prevDeps, setPrevDeps] = React.useState(deps);
  const [state, setState] = React.useState(factory);
  if (prevDeps.length !== deps.length) {
    // I copied this message from React
    devOnlyError('The final argument passed to useComputedState changed size between renders. The order and size of this array must remain constant.');
  }
  if (deps.some((dep, i) => !Object.is(prevDeps[i], dep))) {
    setPrevDeps(deps);
    setState(factory());
  }
  return [state, setState];
}
/**
 * A wrapper around {@link setTimeout} that clears the timeout on unmount.
 */
function useTimeout() {
  const timeoutRef = React.useRef(undefined);
  const isMountedRef = useIsMounted();
  const retvalRef = React.useRef({
    setTimeout(handler, timeout) {
      if (!isMountedRef.current) {
        devOnlyWarn('The Kitchen (useTimeout): Tried to set timeout after unmounting');
        return;
      }
      if (timeoutRef.current !== undefined) {
        devOnlyWarn('The Kitchen (useTimeout): The timeout was restarted without being cleared. The existing timer will be canceled.');
        retvalRef.current.clearTimeout();
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined;
        if (!isMountedRef.current) {
          devOnlyWarn('The Kitchen (useTimeout): Timeout triggered after unmounting');
          return;
        }
        handler();
      }, timeout);
    },
    clearTimeout() {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  });
  React.useEffect(() => {
    return () => {
      retvalRef.current.clearTimeout();
    };
  }, [isMountedRef]);
  return retvalRef.current;
}
/**
 * Throttles {@link fn} to every {@link ms} milliseconds.
 */
function useThrottledCallback(fn, ms = 100) {
  const throttlingRef = React.useRef(false);
  const deferredArgsRef = React.useRef(undefined);
  const {
    setTimeout: setThrottleTimeout,
    clearTimeout: clearThrottleTimeout
  } = useTimeout();
  React.useEffect(clearThrottleTimeout, [fn, ms, clearThrottleTimeout]);
  return React.useCallback((...args) => {
    if (throttlingRef.current) {
      deferredArgsRef.current = args;
      return;
    }
    fn(...args);
    throttlingRef.current = true;
    deferredArgsRef.current = undefined;
    setThrottleTimeout(() => {
      throttlingRef.current = false;
      if (deferredArgsRef.current) {
        fn(...deferredArgsRef.current);
      }
    }, ms);
  }, [fn, ms, setThrottleTimeout]);
}
/**
 * Used to navigate through a list of items where some items may not be focusable.
 * If a particular list item is not focusable, the next available list item will be focused
 *     in the same direction as the navigation.
 * For example, if the navigation event is 'PreviousSibling', but the previous sibling is not focusable,
 * _that_ item's previous sibling will be tested, and so on.
 *
 * This hook can be used with {@link useKeyboardNavigation} to implement keyboard navigation in components with lists of elements,
 *     such as Dropdown, TreePicker, and LeftRightPicker.
 *
 * @param values The list items.
 * @param canNavigateTo A callback that returns whether the specified item is focusable.
 * @param navigateTo A callback that navigates to the specified item.
 * @returns The callback to invoke on a keyboard navigation event.
 */
function useListNavigation(values, canNavigateTo, navigateTo) {
  return React.useCallback((target, i) => {
    if (!values) {
      return;
    }
    let toFocus;
    switch (target) {
      case 'FirstSibling':
        toFocus = findClosestTo(values, 0, canNavigateTo, i => i + 1);
        break;
      case 'PreviousSibling':
        toFocus = findClosestTo(values, i - 1, canNavigateTo, i => i - 1);
        break;
      case 'NextSibling':
        toFocus = findClosestTo(values, i + 1, canNavigateTo, i => i + 1);
        break;
      case 'LastSibling':
        toFocus = findClosestTo(values, values.length - 1, canNavigateTo, i => i - 1);
        break;
    }
    toFocus !== undefined && navigateTo(toFocus);
    function findClosestTo(values, index, predicate, getNext) {
      if (!(index in values)) {
        return undefined;
      }
      if (predicate(values[index], index)) {
        return values[index];
      }
      return findClosestTo(values, getNext(index), predicate, getNext);
    }
  },
  // eslint treats the generic type `T` as a required variable for some reason
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [canNavigateTo, navigateTo, values]);
}
/**
 * Maps keyboard events to their respective navigation target.
 *
 * @param handler The callback invoked on keyboard navigation.
 * @returns An event callback to be passed to `onKeyDown` on the desired element.
 *
 * @see {@link KeyboardNavigationTarget}
 */
function useKeyboardNavigation(handler) {
  return React.useCallback(event => {
    const target = (() => {
      switch (event.key) {
        case 'ArrowUp':
          return 'PreviousSibling';
        case 'ArrowDown':
          return 'NextSibling';
        case 'ArrowLeft':
        case 'ArrowRight':
          return 'Parent';
        case ' ':
        case 'Enter':
          return 'Child';
        case 'Home':
          return 'FirstSibling';
        case 'End':
          return 'LastSibling';
        case 'Tab':
          return event.shiftKey ? 'ExitPrevious' : 'ExitNext';
        case 'Escape':
          return 'Escape';
        default:
          return null;
      }
    })();
    target && handler(target, event);
  }, [handler]);
}
/**
 * Creates a {@link MutableRefObject Ref} to an array of `T` that automatically updates
 * with {@link length}.
 *
 * **NOTE**: The array should not be accessed during render,
 *     because its length will not be updated to reflect {@link length} until {@link React.useEffect useEffect} has run.
 *
 * @param length The current length of the array.
 * @param filler A supplier used when expanding the length of the array.
 * @param callback Invoked when a ref is updated using
 *     {@link ArrayRefReturn.callbackRefs callbackRefs} or {@link ArrayRefReturn.callbackRef callbackRef}.
 */
function useArrayRef(length, filler, callback) {
  const ref = React.useRef([]);
  const callbackRef = React.useCallback((index, value) => {
    callback === null || callback === void 0 ? void 0 : callback(index, value);
    ref.current[index] = value;
  }, [callback]);
  const callbackRefs = React.useMemo(() => new Array(length).fill(null).map((_, i) => value => {
    callbackRef(i, value);
  }), [length, callbackRef]);
  React.useEffect(() => {
    resizeArray(ref.current, length, filler);
  }, [filler, length]);
  return {
    ref,
    callbackRef,
    callbackRefs
  };
}
/**
 * Avoid when possible. This is a hacky solution, but in some cases it is necessary.
 *
 * Returns a ref to the provided value so that the value
 *     can be used in effects without having to specify it as a dependency
 *     and without worrying about stale values.
 */
function useRefToValue(value) {
  const ref = React.useRef(value);
  ref.current = value;
  return ref;
}
/**
 * Avoid using this unless you're noticing an actual performance problem.
 *
 * Like {@link useState}, but calling `setState` only triggers a rerender when {@link isActive} is `true`.
 * If `isActive` is `false`, the most recent call to `setState` is stored until `isActive`
 * becomes `true` again, THEN the component will re-render.
 *
 * @example
 *
 * // Determines whether the component is visible
 * const [isVisible, setIsVisible] =React.useState(false);
 *
 * // The component's height in pixels
 * const [heightPx, setHeightPxDeferred] = useDeferredState(isVisible, 400);
 *
 * // If `isVisible` is false, there is no need to re-render the component yet,
 * // because the user can't see it anyway.
 * //
 *React.useEffect(() => {
 *     // ...
 *     setHeightPxDeferred(600); // Only re-renders if isVisible is true
 *     // ...
 * });
 *
 * return <div style={{ display: isVisible ? undefined : 'none' }}>...</div>
 */
function useDeferredState(isActive, initialValue) {
  const isActiveRef = useRefToValue(isActive);
  const [state, setState] = React.useState(initialValue);
  const deferredStateRef = React.useRef(state);
  if (isActive && !Object.is(state, deferredStateRef.current)) {
    setState(deferredStateRef.current);
  }
  const deferState = React.useCallback(value => {
    if (deferredStateRef.current !== value) {
      deferredStateRef.current = value;
      if (isActiveRef.current) {
        setState(value);
      }
    }
  }, [isActiveRef]);
  return [state, deferState];
}
function useWarnIsDeprecated(componentName, props, type) {
  const propLookup = React.useRef({});
  useWarnOnceIf(type === 'component', `The Kitchen: The component ${componentName} will be removed in a future major version, please reach out to the Kitchen team with questions.`, `${'component' in props && props.component.message ? props.component.message : ''}`, `RIP ${componentName}.`);
  {
    Object.keys(props).forEach(key => {
      const propName = key;
      const {
        value,
        message = 'It may have unexpected behavior.'
      } = props[propName];
      if (!propLookup.current[propName] && value !== undefined) {
        propLookup.current[propName] = true;
        devOnlyWarn(`The Kitchen: ${propName} has been deprecated on ${componentName}.`, message, `The ${propName} prop will be removed in a future major version.`);
      }
    });
  }
}
/**
 * Prefer using `@media print` CSS selector when possible
 *
 * @see https://react.dev/reference/react-dom/flushSync#usage
 */
const useIsPrinting = (() => {
  /**
   * A static reference so that components mounted during printing have the correct value.
   */
  let isPrinting = false;
  return (force = false) => {
    const [isPrintingState, setIsPrintingState] = React.useState(isPrinting);
    React.useEffect(() => {
      const setTrue = () => {
        isPrinting = true;
        ReactDOM__default.flushSync(() => setIsPrintingState(true));
      };
      const setFalse = () => {
        isPrinting = false;
        setIsPrintingState(false);
      };
      window.addEventListener('beforeprint', setTrue);
      window.addEventListener('afterprint', setFalse);
      return () => {
        window.removeEventListener('beforeprint', setTrue);
        window.removeEventListener('afterprint', setFalse);
      };
    });
    return force || isPrintingState;
  };
})();
/**
 * Traps focus while it is in {@link container} but does NOT bring focus into it.
 */
function useFocusTrap() {
  const [containerElement, setContainerElement] = React.useState(null);
  const cleanupFnsRef = React.useRef([]);
  const onMutation = useDebouncedCallback(React.useCallback(() => {
    const cleanupFns = cleanupFnsRef.current;
    cleanupFns.forEach(fn => fn());
    cleanupFns.length = 0;
    const els = getTabbableElements(containerElement);
    if (els.length === 0) {
      return;
    }
    const firstKeydown = e => {
      if (e.key === 'Tab') {
        // Wrap focus around
        if (e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          els[els.length - 1].focus();
        }
        // Trap focus on only tabbable element
        else if (els.length === 1) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    const first = els[0];
    first.addEventListener('keydown', firstKeydown);
    cleanupFns.push(() => first.removeEventListener('keydown', firstKeydown));
    if (els.length > 1) {
      const lastKeydown = e => {
        if (e.key === 'Tab') {
          // Wrap focus around
          if (!e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            els[0].focus();
          }
          // Trap focus on only tabbable element
          else if (els.length === 1) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      };
      const last = els[els.length - 1];
      last.addEventListener('keydown', lastKeydown);
      cleanupFns.push(() => last.removeEventListener('keydown', lastKeydown));
    }
  }, [containerElement]), 10);
  React.useEffect(() => {
    if (!containerElement) {
      return;
    }
    onMutation();
    const observer = new ZonelessMutationObserver(onMutation);
    observer.observe(containerElement, {
      childList: true,
      attributes: true,
      subtree: true
    });
    return () => {
      cleanupFnsRef.current.forEach(f => f());
      cleanupFnsRef.current.length = 0;
      observer.disconnect();
    };
  }, [containerElement, onMutation]);
  return {
    ref: setContainerElement
  };
}
function useFocusFirstTabbableElement(container) {
  const hasFocusedRef = React.useRef(false);
  React.useEffect(() => {
    hasFocusedRef.current = false;
  }, [container]);
  useMutationObserver(container, () => {
    if (hasFocusedRef.current) {
      return;
    }
    const tabbableElements = getTabbableElements(container);
    if (tabbableElements.length > 0) {
      tabbableElements[0].focus();
      hasFocusedRef.current = true;
    } else {
      devOnlyWarn('Could not find a tabbable element to focus. This could cause accessibility issues.');
      container === null || container === void 0 ? void 0 : container.focus();
    }
  }, {
    childList: true,
    subtree: true
  });
}

/* eslint react-hooks/exhaustive-deps : error */
function useMutationObserver(el, callback, options) {
  const {
    attributeFilter,
    attributeOldValue,
    attributes,
    characterData,
    characterDataOldValue,
    childList,
    subtree
  } = options;
  const callbackRef = useRefToValue(callback);
  React__default.useEffect(() => {
    if (!el) {
      return;
    }
    const observer = new ZonelessMutationObserver((entries, observer) => {
      callbackRef.current(el, entries, observer);
    });
    callbackRef.current(el, [], observer); // Call once when observer is created
    observer.observe(el, {
      attributeFilter,
      attributeOldValue,
      attributes,
      characterData,
      characterDataOldValue,
      childList,
      subtree
    });
    return () => {
      observer.disconnect();
    };
  }, [attributeFilter, attributeOldValue, attributes, callbackRef, characterData, characterDataOldValue, childList, el, subtree]);
}

/* eslint react-hooks/exhaustive-deps : error */
function isSlottedElement(slotName, node) {
  return node instanceof Element && node.getAttribute('slot') === slotName;
}
function getSlottedElement(slot) {
  if (!slot) {
    return null;
  }
  const name = slot.name;
  const nodes = slot.childNodes;
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (isSlottedElement(name, node)) {
      return node;
    }
  }
  return null;
}
function flattenSlot(slot) {
  const slottedElement = getSlottedElement(slot);
  return slottedElement ? Array.from(slottedElement.childNodes) : [];
}
function flattenNode(node) {
  if (node instanceof HTMLSlotElement) {
    return flattenSlot(node);
  }
  if (node instanceof Element && node.slot !== '') {
    return flattenNodes(node.childNodes);
  }
  if (node instanceof Comment) {
    return [];
  }
  return [node];
}
function flattenNodes(nodes) {
  return Array.from(nodes).flatMap(flattenNode);
}
/**
 * Finds the content of an element, flattening any encountered slots.
 * The slots are flattened so that we can easily determine whether a container functionally has any children,
 *   even when slots are present.
 *
 * For example, for the following HTML:
 * ```html
 * <container>
 *   <div>ABC</div>
 *   <div slot="before-content">Some content</div>
 *   <slot name="content">
 *     <div slot="content">
 *       Hello
 *       <div>World</div>
 *     </div>
 *   </slot>
 *   <div>abc</div>
 *   <div slot="after-content">Some more <span>content</span></div>
 * </container>
 * ```
 * the following node array will be returned:
 * ```tsx
 * [
 *   <div>ABC</div>,
 *   <div slot="before-content">Some content</div>,
 *   // slot[name=content] and div[slot=content] are not included!!!
 *   Hello,
 *   <div>World</div>
 *   <div>abc</div>
 *   <div slot="after-content">Some more <span>content</span></div>
 * ]
 * ```
 *
 * @param container The container.
 * @returns An array of content nodes.
 */
function getChildNodes(container) {
  if (!container) {
    return [];
  }
  if (container instanceof HTMLSlotElement) {
    return flattenSlot(container);
  }
  return flattenNodes(container.childNodes);
}
function useOnChildNodesChange(container, onChange) {
  const contentRef = React__default.useRef([]);
  useMutationObserver(container, currContainer => {
    const newContent = getChildNodes(currContainer);
    if (!areEqual$1(contentRef.current, newContent)) {
      contentRef.current = newContent;
      onChange(newContent, currContainer);
    }
  }, {
    childList: true,
    subtree: true
  });
}
function useOnChildNodesChange_ref(onChange) {
  const [container, setContainer] = React__default.useState(null);
  useOnChildNodesChange(container, onChange);
  return setContainer;
}
function useChildNodes(container) {
  const [content, setContent] = React__default.useState([]);
  useOnChildNodesChange(container, setContent);
  return content;
}
function useHasChildNodes(container) {
  const content = useChildNodes(container);
  return content.filter(n => !(n instanceof Comment)).length > 0;
}
function useHasChildNodes_ref() {
  const [container, setContainer] = React__default.useState(null);
  const hasChildNodes = useHasChildNodes(container);
  return {
    ref: setContainer,
    hasChildNodes
  };
}

const WebSlot = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  var _a;
  const {
      name,
      children: defaultContent
    } = props,
    ingredients = __rest(props, ["name", "children"]);
  const {
    hasRendered,
    onRender: onSlotRender
  } = (_a = React__default.useContext(SlotContext)) !== null && _a !== void 0 ? _a : {};
  const [slotElement, setSlotElement] = React__default.useState(null);
  const hasContent = useHasChildNodes(slotElement);
  const showDefaultContent = hasRendered && !hasContent;
  React__default.useLayoutEffect(() => {
    if (slotElement) {
      onSlotRender === null || onSlotRender === void 0 ? void 0 : onSlotRender(slotElement);
    }
  });
  return jsx("slot", Object.assign({
    name: name
  }, ingredients, {
    ref: mergeRefs([ref, setSlotElement]),
    children: showDefaultContent && defaultContent
  }));
});

/**
 * {@link SlotProps.children Children} will only be displayed if there is no other content in the slot.
 */
const Slot = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      children,
      name,
      reactChildren
    } = props,
    ingredients = __rest(props, ["children", "name", "reactChildren"]);
  useWarnOnceIf(reactChildren != null && ref != null, `The Kitchen (Slot[name=${name}]):`, 'A ref was passed to a slot that contains React children.', 'A <slot> element is only rendered when there is no matching React slot.', 'The ref will always be `null`.', 'Please notify the Kitchen team of this warning');
  return jsx(ReactSlot, {
    name: name,
    slots: reactChildren,
    children: jsx(WebSlot, Object.assign({
      ref: ref
    }, ingredients, {
      name: name,
      children: children
    }))
  });
});

class AccordionProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Boolean)], AccordionProps.prototype, "hasShadow", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], AccordionProps.prototype, "hasSideBorders", void 0);
__decorate([Bound(), __metadata("design:type", String)], AccordionProps.prototype, "orientation", void 0);
class AccordionEvents extends DivHTMLEvents {}
/**
 * Description: Navigation component with expandable sections.
 */
const Accordion = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(Accordion, props);
  const {
      children,
      className,
      hasShadow = false,
      hasSideBorders = false,
      orientation = 'normal'
    } = props,
    ingredients = __rest(props, ["children", "className", "hasShadow", "hasSideBorders", "orientation"]);
  return jsx("div", Object.assign({}, debugService.componentProps, ingredients, {
    ref: ref,
    className: classNames(`kxi-accordion kxi-stretch-width orca`, className, {
      'kxi-accordion-borders': hasSideBorders,
      'kxi-shadow': hasShadow,
      'kxi-accordion-inverted': orientation === 'inverted'
    }),
    children: jsx(Slot, {
      name: "children",
      reactChildren: children
    })
  }));
});
Accordion.displayName = 'Accordion';

class SpanHTMLAttributes extends HTMLAttributes {}
class SpanHTMLEvents extends DOMEvents {}

// eslint-disable-next-line @typescript-eslint/ban-types
function ObservablePropsMixin(SuperClass) {
  return class ObservableProps extends SuperClass {
    constructor() {
      super(...arguments);
      this.observer = {};
    }
  };
}

function areNumbersApproximatelyEqual(num1, num2, tolerance = 1e-6) {
  return Math.abs(num1 - num2) <= tolerance;
}
function areRectsApproximatelyEqual(rect1, rect2, tolerance = 1e-6) {
  return areNumbersApproximatelyEqual(rect1.top, rect2.top, tolerance) && areNumbersApproximatelyEqual(rect1.left, rect2.left, tolerance) && areNumbersApproximatelyEqual(rect1.right, rect2.right, tolerance) && areNumbersApproximatelyEqual(rect1.bottom, rect2.bottom, tolerance);
}
/**
 * @see https://toruskit.com/blog/how-to-get-element-bounds-without-reflow/
 */
function pollBoundingClientRect(element, callback) {
  let prevBoundingClientRect = null;
  const observer = new IntersectionObserver(([entry]) => {
    observer.disconnect();
    observer.observe(element);
    const {
      boundingClientRect
    } = entry;
    if (prevBoundingClientRect === null || !areRectsApproximatelyEqual(prevBoundingClientRect, boundingClientRect)) {
      // Only update state if rect has changed
      prevBoundingClientRect = boundingClientRect;
      callback(boundingClientRect);
    }
  });
  observer.observe(element);
  return () => {
    observer.disconnect();
  };
}
/**
 * @see https://toruskit.com/blog/how-to-get-element-bounds-without-reflow/
 */
function usePollBoundingClientRect(isActive, element, onChange) {
  React__default.useEffect(() => {
    if (!element || !isActive) {
      onChange(null);
      return;
    }
    const disconnect = pollBoundingClientRect(element, onChange);
    return disconnect;
  }, [element, isActive, onChange]);
}
function usePollBoundingClientRectState(isActive, element) {
  const [boundingClientRect, setBoundingClientRect] = React__default.useState(null);
  usePollBoundingClientRect(isActive, element, setBoundingClientRect);
  return boundingClientRect;
}

const isAnchoredAtBottom = direction => direction.startsWith('bottom');
const isAnchoredAtLeft = direction => direction.startsWith('left');
const isAnchoredAtTop = direction => direction.startsWith('top');
function getVerticallyCenteredPosition(rect) {
  var _a;
  return window.scrollY + window.innerHeight / 2 - ((_a = rect === null || rect === void 0 ? void 0 : rect.height) !== null && _a !== void 0 ? _a : 0) / 2;
}
function getHorizontallyCenteredPosition(rect) {
  var _a;
  return window.scrollX + window.innerWidth / 2 - ((_a = rect === null || rect === void 0 ? void 0 : rect.width) !== null && _a !== void 0 ? _a : 0) / 2;
}
function getCenteredPosition(rect) {
  return {
    top: getVerticallyCenteredPosition(rect),
    left: getHorizontallyCenteredPosition(rect)
  };
}
function getPopoutPosition(props) {
  const {
    anchorRect,
    direction,
    popoutRect,
    verticalOffset,
    horizontalOffset
  } = props;
  /** anchorRect adjusted for scrolling */
  const adjustedRect = {
    top: window.scrollY + anchorRect.top,
    bottom: window.scrollY + anchorRect.bottom,
    left: window.scrollX + anchorRect.left,
    right: window.scrollX + anchorRect.right,
    height: anchorRect.height,
    width: anchorRect.width
  };
  if (isAnchoredAtBottom(direction)) {
    const top = adjustedRect.bottom + verticalOffset;
    switch (direction) {
      case 'bottom-left':
        return {
          top,
          left: adjustedRect.left
        };
      case 'bottom-middle':
        return {
          top,
          left: adjustedRect.left + adjustedRect.width / 2 - popoutRect.width / 2
        };
      case 'bottom-right':
        return {
          top,
          left: adjustedRect.left + adjustedRect.width - popoutRect.width
        };
    }
  } else if (isAnchoredAtTop(direction)) {
    const top = adjustedRect.top - popoutRect.height - verticalOffset;
    switch (direction) {
      case 'top-left':
        return {
          top,
          left: adjustedRect.left
        };
      case 'top-middle':
        return {
          top,
          left: adjustedRect.left + adjustedRect.width / 2 - popoutRect.width / 2
        };
      case 'top-right':
        return {
          top,
          left: adjustedRect.left + adjustedRect.width - popoutRect.width
        };
    }
  } else if (isAnchoredAtLeft(direction)) {
    const left = adjustedRect.left - popoutRect.width - horizontalOffset;
    switch (direction) {
      case 'left-top':
        return {
          left,
          top: adjustedRect.top
        };
      case 'left-middle':
        return {
          left,
          top: adjustedRect.top + adjustedRect.height / 2 - popoutRect.height / 2
        };
      case 'left-bottom':
        return {
          left,
          top: adjustedRect.top + adjustedRect.height - popoutRect.height
        };
    }
  } else {
    const left = adjustedRect.right + horizontalOffset;
    switch (direction) {
      case 'right-top':
        return {
          left,
          top: adjustedRect.top
        };
      case 'right-middle':
        return {
          left,
          top: adjustedRect.top + adjustedRect.height / 2 - popoutRect.height / 2
        };
      case 'right-bottom':
        return {
          left,
          top: adjustedRect.top + adjustedRect.height - popoutRect.height
        };
    }
  }
}
function doesElementFitAtPosition(props) {
  const {
    rect,
    edgeClearance,
    top,
    left
  } = props;
  return top - edgeClearance >= window.scrollY && top + edgeClearance + rect.height <= window.scrollY + window.innerHeight && left - edgeClearance >= window.scrollX && left + edgeClearance + rect.width <= window.scrollX + window.innerWidth;
}
function isElementAtLeastPartiallyOnScreenAtPosition(props) {
  const {
    rect,
    edgeClearance,
    top,
    left
  } = props;
  return top + edgeClearance + rect.height >= window.scrollY && top - edgeClearance <= window.scrollY + window.innerHeight && left + edgeClearance + rect.width >= window.scrollX && left - edgeClearance <= window.scrollX + window.innerWidth;
}
function getPositionAtDirectionIfPopoutFits(props) {
  const {
    popoutRect
  } = props;
  const {
    top,
    left
  } = getPopoutPosition(props);
  return doesElementFitAtPosition(Object.assign(Object.assign({}, props), {
    rect: popoutRect,
    top,
    left
  })) ? {
    top,
    left
  } : undefined;
}
function getFittingPopoutPosition(props) {
  var _a, _b, _c;
  const {
    anchorRect,
    direction
  } = props;
  // Anchor is offscreen
  if (!isElementAtLeastPartiallyOnScreenAtPosition(Object.assign(Object.assign({}, props), {
    rect: anchorRect,
    top: anchorRect.top + window.scrollY,
    left: anchorRect.left + window.scrollX,
    edgeClearance: 0
  }))) {
    return undefined;
  }
  const findFittingDirection = (startIndex, directions) => {
    for (let i = startIndex, count = 0; count < directions.length; i = (i + 1) % directions.length, count += 1) {
      const direction = directions[i];
      const position = getPositionAtDirectionIfPopoutFits(Object.assign(Object.assign({}, props), {
        direction
      }));
      if (position) {
        return Object.assign(Object.assign({}, position), {
          direction
        });
      }
    }
    return undefined;
  };
  const leftOrder = ['left-top', 'left-middle', 'left-bottom'];
  const rightOrder = ['right-top', 'right-middle', 'right-bottom'];
  const topOrder = ['top-left', 'top-middle', 'top-right'];
  const bottomOrder = ['bottom-left', 'bottom-middle', 'bottom-right'];
  const order = (() => {
    switch (direction) {
      case 'top-middle':
      case 'top-left':
      case 'top-right':
        return [topOrder, bottomOrder, leftOrder, rightOrder];
      case 'bottom-middle':
      case 'bottom-left':
      case 'bottom-right':
        return [bottomOrder, topOrder, leftOrder, rightOrder];
      case 'left-top':
      case 'left-middle':
      case 'left-bottom':
        return [leftOrder, rightOrder, bottomOrder, topOrder];
      case 'right-top':
      case 'right-middle':
      case 'right-bottom':
        return [rightOrder, leftOrder, bottomOrder, topOrder];
    }
  })();
  return (_c = (_b = (_a = findFittingDirection(order[0].indexOf(direction), order[0])) !== null && _a !== void 0 ? _a : findFittingDirection(0, order[1])) !== null && _b !== void 0 ? _b : findFittingDirection(0, order[2])) !== null && _c !== void 0 ? _c : findFittingDirection(0, order[3]);
}
function getPartiallyFittingPopoutPosition(props) {
  const {
    anchorRect
  } = props;
  // Anchor is offscreen
  if (!isElementAtLeastPartiallyOnScreenAtPosition(Object.assign(Object.assign({}, props), {
    rect: anchorRect,
    top: anchorRect.top + window.scrollY,
    left: anchorRect.left + window.scrollX,
    edgeClearance: 0
  }))) {
    return undefined;
  }
  const fittingPosition = getFittingPopoutPosition(props);
  if (fittingPosition) {
    return fittingPosition;
  }
  const {
    direction,
    popoutRect
  } = props;
  const horizontallyCenteredPosition = getHorizontallyCenteredPosition(popoutRect);
  const verticallyCenteredPosition = getVerticallyCenteredPosition(popoutRect);
  const getPartiallyAnchoredPopoutPosition = props => {
    const {
      direction,
      popoutRect
    } = props;
    const anchoredPosition = getPopoutPosition(props);
    const isHorizontallyCentered = isAnchoredAtTop(direction) || isAnchoredAtBottom(direction);
    const partiallyAnchoredPosition = Object.assign(Object.assign({}, anchoredPosition), isHorizontallyCentered ? {
      left: horizontallyCenteredPosition
    } : {
      top: verticallyCenteredPosition
    });
    return doesElementFitAtPosition(Object.assign(Object.assign(Object.assign({}, props), {
      rect: popoutRect
    }), partiallyAnchoredPosition)) ? partiallyAnchoredPosition : undefined;
  };
  const order = (() => {
    switch (direction) {
      case 'top-middle':
      case 'top-left':
      case 'top-right':
        return ['top-middle', 'bottom-middle', 'left-middle', 'right-middle'];
      case 'bottom-middle':
      case 'bottom-left':
      case 'bottom-right':
        return ['bottom-middle', 'top-middle', 'left-middle', 'right-middle'];
      case 'left-top':
      case 'left-middle':
      case 'left-bottom':
        return ['left-middle', 'right-middle', 'bottom-middle', 'top-middle'];
      case 'right-top':
      case 'right-middle':
      case 'right-bottom':
        return ['right-middle', 'left-middle', 'bottom-middle', 'top-middle'];
    }
  })();
  for (const testDirection of order) {
    const partiallyAnchoredPosition = getPartiallyAnchoredPopoutPosition(Object.assign(Object.assign({}, props), {
      direction: testDirection
    }));
    if (partiallyAnchoredPosition) {
      return partiallyAnchoredPosition;
    }
  }
  return undefined;
}

const PositionedPopout = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const {
      allowScroll = false,
      container,
      children,
      childrenContainsSlot = false,
      containedPopoutIds,
      isVisible,
      top,
      left,
      internalPadding = 6,
      popoutId = 'kxi-xi-popout',
      preventFocus = false,
      trapFocus = true,
      onExternalClick,
      onEscape,
      onFocusOut,
      slotName = 'children',
      style: styleInput,
      renderContentWhenNotVisible: renderContentWhenNotVisibleInput = false
    } = props,
    ingredients = __rest(props, ["allowScroll", "container", "children", "childrenContainsSlot", "containedPopoutIds", "isVisible", "top", "left", "internalPadding", "popoutId", "preventFocus", "trapFocus", "onExternalClick", "onEscape", "onFocusOut", "slotName", "style", "renderContentWhenNotVisible"]);
  const hasReactChildren = hasChildren(children);
  const contentContainsSlot = !hasReactChildren || childrenContainsSlot;
  const renderContentWhenNotVisible = renderContentWhenNotVisibleInput || contentContainsSlot;
  const popoutElementRef = React__default.useRef(null);
  const {
    ref: focusTrapRef
  } = useFocusTrap();
  const [isStable, setIsStable] = React__default.useState(false);
  React__default.useEffect(() => {
    setIsStable(false);
    const popoutElement = popoutElementRef.current;
    if (!isVisible || !popoutElement) {
      return;
    }
    const disconnect = pollBoundingClientRect(popoutElement, () => {
      disconnect();
      setIsStable(true);
    });
    return disconnect;
  }, [isVisible]);
  React__default.useEffect(() => {
    const popoutElement = popoutElementRef.current;
    if (!popoutElement || !onExternalClick || !isVisible) {
      return;
    }
    const listener = event => {
      const target = event.target;
      if (popoutElement.contains(target)) {
        return;
      }
      if (containedPopoutIds !== undefined && containedPopoutIds.length > 0) {
        const containedPopoutIdList = containedPopoutIds.map(id => `[popout-id=${id}]`).join(',');
        const containedPopouts = Array.from(document.querySelectorAll(containedPopoutIdList));
        if (containedPopouts.some(el => el.contains(target))) {
          return;
        }
      }
      onExternalClick({
        event
      });
    };
    window.addEventListener('mousedown', listener);
    return () => window.removeEventListener('mousedown', listener);
  }, [onExternalClick, isVisible, containedPopoutIds]);
  React__default.useEffect(() => {
    const popoutElement = popoutElementRef.current;
    if (!isVisible || !popoutElement || !onFocusOut) {
      return;
    }
    const handleFocusOut = e => {
      const rt = e.relatedTarget;
      const t = e.target;
      if (!popoutElement.contains(rt) && (rt === null ? !popoutElement.contains(t) : true)) {
        onFocusOut({
          event: e
        });
      }
    };
    window.addEventListener('focusout', handleFocusOut);
    return () => {
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, [isVisible, onFocusOut]);
  React__default.useEffect(() => {
    if (!isVisible || !onEscape) {
      return;
    }
    const escapeListener = e => {
      if (e.key === 'Escape') {
        onEscape({
          event: e
        });
      }
    };
    document.addEventListener('keydown', escapeListener);
    return () => {
      document.removeEventListener('keydown', escapeListener);
    };
  }, [isVisible, onEscape]);
  usePreventScroll(isVisible && !allowScroll, container);
  React__default.useEffect(() => {
    const popoutElement = popoutElementRef.current;
    if (!isVisible || preventFocus || !popoutElement) {
      return;
    }
    focusFirstTabbableElement(popoutElement);
  }, [isVisible, preventFocus]);
  return isVisible || renderContentWhenNotVisible ? /*#__PURE__*/ReactDOM__default.createPortal(jsxs(Fragment, {
    children: [isVisible && !allowScroll && jsx("div", {
      className: "kxi-popout-invisible-scrim"
    }), jsx("div", Object.assign({
      role: "dialog"
    }, ingredients, {
      ref: mergeRefs([ref, popoutElementRef, ...(trapFocus ? [focusTrapRef] : [])]),
      "popout-id": popoutId,
      className: classNames(ingredients.className, 'kxi-popout', {
        'kxi-popout-hidden': !isVisible
      }),
      style: Object.assign(Object.assign({}, styleInput), {
        top,
        left,
        padding: internalPadding,
        opacity: isStable ? undefined : 0
      }),
      children: jsx(Slot, {
        "data-testid": "slot",
        name: slotName,
        reactChildren: children
      })
    }))]
  }), container) : null;
});

const AnchoredPopout = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const containerElement = document.body;
  const {
      anchorElement,
      children,
      direction: directionInput,
      edgeClearance = 6,
      isVisible,
      offset: offsetInput,
      stretch = false,
      style: styleInput
    } = props,
    ingredients = __rest(props, ["anchorElement", "children", "direction", "edgeClearance", "isVisible", "offset", "stretch", "style"]);
  const offset = typeof offsetInput === 'object' || offsetInput === undefined ? Object.assign({
    horizontal: 6,
    vertical: 6
  }, offsetInput) : {
    horizontal: offsetInput,
    vertical: offsetInput
  };
  const [popoutElement, setPopoutElement] = React__default.useState(null);
  usePollBoundingClientRectState(isVisible, containerElement); // Ignore rect just rerender
  const popoutRect = usePollBoundingClientRectState(isVisible, popoutElement);
  const anchorRect = usePollBoundingClientRectState(isVisible, anchorElement);
  useWarnOnceIf(isVisible && anchorElement == null, 'Kitchen Popout: invalid anchor element specified. The modal will appear in the center of the screen.');
  const preferredDirection = getNamedClockDirection(directionInput);
  const fittingPosition = anchorRect !== null && popoutRect !== null ? getPartiallyFittingPopoutPosition({
    anchorRect,
    popoutRect,
    direction: preferredDirection,
    horizontalOffset: offset.horizontal,
    verticalOffset: offset.vertical,
    edgeClearance
  }) : undefined;
  const bestPosition = fittingPosition !== null && fittingPosition !== void 0 ? fittingPosition : getCenteredPosition(popoutRect);
  const {
    top,
    left
  } = bestPosition;
  return jsx(PositionedPopout, Object.assign({}, ingredients, {
    ref: mergeRefs([ref, setPopoutElement]),
    container: containerElement,
    isVisible: isVisible,
    left: left,
    top: top,
    style: Object.assign(Object.assign(Object.assign({}, styleInput), stretch && anchorRect && {
      minWidth: anchorRect.width
    }), {
      maxWidth: `calc(100vw - 2 * ${clamp(edgeClearance, 30, Math.min(window.innerHeight, window.innerWidth) / 3)}px)`,
      maxHeight: `calc(100vh - 2 * ${clamp(edgeClearance, 30, Math.min(window.innerHeight, window.innerWidth) / 3)}px)`
    }),
    children: children
  }));
});

class PopoutProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Object)], PopoutProps.prototype, "anchorElement", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], PopoutProps.prototype, "isVisible", void 0);
__decorate([Bound(), __metadata("design:type", Object)], PopoutProps.prototype, "direction", void 0);
__decorate([Bound(), __metadata("design:type", Object)], PopoutProps.prototype, "internalPadding", void 0);
__decorate([Bound(), __metadata("design:type", Number)], PopoutProps.prototype, "edgeClearance", void 0);
__decorate([Bound(), __metadata("design:type", String)], PopoutProps.prototype, "popoutId", void 0);
__decorate([Bound(), __metadata("design:type", Object)], PopoutProps.prototype, "elementsToDisableScroll", void 0);
__decorate([Bound(), __metadata("design:type", String)], PopoutProps.prototype, "slotName", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], PopoutProps.prototype, "preventFocus", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], PopoutProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", Object)], PopoutProps.prototype, "offset", void 0);
__decorate([Bound(), __metadata("design:type", Array)], PopoutProps.prototype, "containedPopoutIds", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], PopoutProps.prototype, "allowScroll", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], PopoutProps.prototype, "trapFocus", void 0);
class PopoutEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], PopoutEvents.prototype, "onExternalClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], PopoutEvents.prototype, "onEscape", void 0);
__decorate([Bound(), __metadata("design:type", Function)], PopoutEvents.prototype, "onFocusOut", void 0);
/**
 * A component that handles the "popout" logic and displaying of a popout. The popout
 * attaches to an element. The popout is then displayed in a direction around the anchor
 * element. There is logic inside this component to handle small screen sizes, popouts appearing
 * off the edge of the screen, and various other possibilities. As such, the popout is
 * not guaranteed to display in the direction provided, but the one provided will be used
 * as a "first choice".
 */
const Popout = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a;
  useDebugService(Popout, props);
  const {
      _className,
      _childrenContainsSlot: childrenContainsSlot,
      _renderContentWhenNotVisible: renderContentWhenNotVisible,
      className,
      direction = 6,
      _isMobile,
      isVisible = false
    } = props,
    ingredients = __rest(props, ["_className", "_childrenContainsSlot", "_renderContentWhenNotVisible", "className", "direction", "_isMobile", "isVisible"]);
  return jsx(AnchoredPopout, Object.assign({
    ref: ref
  }, ingredients, {
    anchorElement: (_a = props.anchorElement) !== null && _a !== void 0 ? _a : null,
    childrenContainsSlot: childrenContainsSlot,
    renderContentWhenNotVisible: renderContentWhenNotVisible,
    className: classNames(_className, className),
    direction: direction,
    isVisible: isVisible
  }));
});
Popout.displayName = 'Popout';

/* eslint react-hooks/exhaustive-deps : error */
function useIntersectionObserver(el, callback, options) {
  const {
    root,
    rootMargin,
    threshold
  } = options !== null && options !== void 0 ? options : {};
  const callbackRef = useRefToValue(callback);
  React__default.useEffect(() => {
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver((entries, observer) => {
      callbackRef.current(entries, observer);
    }, {
      root,
      rootMargin,
      threshold
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [callbackRef, el, root, rootMargin, threshold]);
}
function useIsPartiallyOrFullyVisible(element) {
  const [isVisible, setIsVisible] = React__default.useState(true);
  useIntersectionObserver(element, ([{
    isIntersecting
  }]) => {
    setIsVisible(isIntersecting);
  }, {
    root: null,
    threshold: 0
  });
  return isVisible;
}
/**
 * Uses an {@link IntersectionObserver} to track the {@link Visibility visibility} of an element.
 *
 * @param props {@link IntersectionObserverInit IntersectionObserver options}
 * @returns An object containing:
 * - `ref`: A ref to be passed to the element whose visibility to track
 * - `visibility`: The element's current visibility.
 */
const useVisibilityDetector = (() => {
  // use closure to guarantee referential equality
  const defaultThreshold = [0, 1];
  return (element, props) => {
    const {
      root,
      rootMargin = '1px',
      threshold = defaultThreshold,
      onChange
    } = props;
    const onChangeRef = useRefToValue(onChange);
    const [visibility, setVisibility] = React__default.useState('none');
    const visibilityRef = useRefToValue(visibility);
    useIntersectionObserver(element, ([{
      intersectionRatio
    }]) => {
      var _a;
      const visibility = intersectionRatio === 1 ? 'full' : intersectionRatio === 0 ? 'none' : 'partial';
      if (visibility !== visibilityRef.current) {
        setVisibility(visibility);
        visibilityRef.current = visibility;
        (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, visibility);
      }
    }, {
      root,
      rootMargin,
      threshold
    });
    return visibility;
  };
})();
function useVisibilityDetectorRef(props) {
  const [el, setEl] = React__default.useState(null);
  const visibility = useVisibilityDetector(el, props);
  return {
    ref: setEl,
    visibility
  };
}

class ToolTipProps extends DivHTMLAttributes {
  constructor(props) {
    super();
    Object.assign(this, props);
  }
}
__decorate([Bound(), type($optional($in(_ClockDirectionOptions))), __metadata("design:type", Object)], ToolTipProps.prototype, "direction", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], ToolTipProps.prototype, "isOpenOnClick", void 0);
__decorate([Bound(), type($optional($union($string(), $object({})))), __metadata("design:type", Object)], ToolTipProps.prototype, "text", void 0);
__decorate([Bound(), type($optional($union($string(), $object({}))))
// TODO fix for a future major version
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
, __metadata("design:type", Object)], ToolTipProps.prototype, "title", void 0);
__decorate([Bound(), type($optional($union($string(), $number()))), __metadata("design:type", Object)], ToolTipProps.prototype, "maxWidth", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], ToolTipProps.prototype, "stretch", void 0);
__decorate([Bound(), type($optional($object({}))), __metadata("design:type", Object)], ToolTipProps.prototype, "innerStyle", void 0);
__decorate([Bound(), type($optional($number(0))), __metadata("design:type", Number)], ToolTipProps.prototype, "space", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], ToolTipProps.prototype, "enabled", void 0);
__decorate([Bound(), type($optional($string())), __metadata("design:type", String)], ToolTipProps.prototype, "slotName", void 0);
__decorate([Bound(), type($optional($nullable($instanceof(HTMLElement)))), __metadata("design:type", Object)], ToolTipProps.prototype, "anchorElement", void 0);
class ToolTipEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ToolTipEvents.prototype, "onOpenChange", void 0);
/**
 * Description: An internal wrapper for ToolTips.
 */
const ToolTipWrapper = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ToolTipWrapper, props);
  const {
      anchorElement: anchorElementInput,
      children,
      className,
      direction,
      enabled = true,
      innerStyle,
      isOpenOnClick: opensOnClick = false,
      maxWidth = 320,
      onOpenChange,
      slotName,
      space = 3,
      stretch,
      style,
      text,
      title
    } = props,
    ingredients = __rest(props, ["anchorElement", "children", "className", "direction", "enabled", "innerStyle", "isOpenOnClick", "maxWidth", "onOpenChange", "slotName", "space", "stretch", "style", "text", "title"]);
  const [popoutElement, setPopoutElement] = React.useState(null);
  const [defaultAnchorElement, setDefaultAnchorElement] = React.useState(null);
  const anchorElement = anchorElementInput !== null && anchorElementInput !== void 0 ? anchorElementInput : defaultAnchorElement;
  const isAnchorVisible = useIsPartiallyOrFullyVisible(anchorElement);
  const hasContent = !!text || !!title; // Use truthiness so that '' is considered false
  const canOpen = enabled && isAnchorVisible && (hasContent || slotName !== undefined);
  const hovered = useIsHoveringOverAnyOfElements(anchorElement, popoutElement);
  const [clicked, setClicked] = React.useState(false);
  const uuid = useUuid();
  const isOpen = canOpen && (opensOnClick ? clicked : hovered);
  React.useEffect(() => {
    onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);
  React.useEffect(() => {
    !opensOnClick && setClicked(false);
  }, [opensOnClick]);
  const titleElement = (() => {
    if (title == null) {
      return null;
    }
    return jsx("div", Object.assign({
      role: "paragraph",
      className: "kxi-tooltip-title"
    }, typeof title === 'string' ? {
      dangerouslySetInnerHTML: {
        __html: sanitizeHtmlString(title)
      }
    } : {
      children: title
    }));
  })();
  const bodyElement = (() => {
    if (text == null) {
      return null;
    }
    return jsx("div", Object.assign({
      role: "paragraph"
    }, typeof text === 'string' ? {
      dangerouslySetInnerHTML: {
        __html: sanitizeHtmlString(text)
      }
    } : {
      children: text
    }));
  })();
  return jsxs("div", Object.assign({}, debugService.componentProps, ingredients, {
    className: classNames(className, 'kxi-tooltip-wrapper kxi-grey', {
      'kxi-tooltip-wrapper-clickable': opensOnClick
    }),
    style: Object.assign({
      display: stretch ? 'block' : undefined
    }, style),
    "data-tooltipvisible": isOpen,
    onBlur: () => {
      if (opensOnClick) {
        setClicked(false);
      }
    },
    onKeyPress: e => {
      if (opensOnClick && hovered && e.key === 'Enter') {
        setClicked(!clicked);
      }
    },
    ref: ref,
    children: [jsx("div", {
      onClick: () => opensOnClick && setClicked(!clicked),
      style: Object.assign(Object.assign({}, !stretch && {
        display: 'inline-flex'
      }), innerStyle),
      id: uuid,
      ref: setDefaultAnchorElement,
      children: jsx(Slot, {
        name: "children",
        reactChildren: children
      })
    }), jsx(Popout, {
      ref: setPopoutElement,
      role: "tooltip",
      allowScroll: true,
      anchorElement: anchorElement,
      "aria-hidden": !isOpen,
      _className: "kxi-tooltip",
      _childrenContainsSlot: slotName !== undefined,
      direction: direction,
      internalPadding: 0,
      isVisible: isOpen,
      offset: space,
      popoutId: `${uuid}-popout`,
      preventFocus: true,
      trapFocus: false,
      children: jsx("div", {
        "aria-label": "tooltip text",
        className: "kxi-tooltip-text",
        style: {
          maxWidth: slotName ? undefined : maxWidth
        },
        children: slotName ? jsxs(Slot, {
          name: slotName,
          reactChildren: children,
          children: [titleElement, bodyElement]
        }) : jsxs(Fragment, {
          children: [titleElement, bodyElement]
        })
      })
    })]
  }));
});
ToolTipWrapper.displayName = 'ToolTipWrapper';

class XIIconProps extends SpanHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], XIIconProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", String)], XIIconProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], XIIconProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Object)], XIIconProps.prototype, "iconSize", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], XIIconProps.prototype, "tooltipProps", void 0);
class XIIconEvents extends SpanHTMLEvents {}
/**
 * Description: Component that displays an Icon in the Icons category.
 */
const XIIcon = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const debugService = useDebugService(XIIcon, props);
  const {
      color = 'orca',
      icon = 'no_icon',
      iconSize = 'normal',
      contrast = 'normal',
      style,
      className,
      tooltipProps
    } = props,
    ingredients = __rest(props, ["color", "icon", "iconSize", "contrast", "style", "className", "tooltipProps"]);
  const internalIconSize = (() => {
    switch (iconSize) {
      case 'normal':
        return 24;
      case 'small':
        return 18;
      case 'large':
        return 30;
      case 'xxlarge':
        return 36;
      default:
        return isNaN(iconSize) || iconSize === '' ? 24 : +iconSize;
    }
  })();
  // In a future major version remove case_management
  // In the following major version re-rename case_management_new to case_management
  useExecuteSideEffectOnceIf(icon === 'app_case_management', () => {
    warnIconRenamed('app_case_management', 'app_case_management_new');
  }, [icon]);
  useExecuteSideEffectOnceIf(icon === 'app_case_management_filled', () => {
    warnIconRenamed('app_case_management_filled', 'app_case_management_new_filled');
  }, [icon]);
  const {
    customColor,
    colorClass
  } = useCustomColor(color, contrast);
  const classList = ['kxi-icon', `kxi-icon-${icon}`, colorClass, className, {
    'kxi-high-contrast': contrast === 'high'
  }];
  const iconElement = jsx("span", Object.assign({
    role: "img",
    "aria-label": `${icon.replace(/_/g, ' ')} icon`,
    ref: ref,
    className: classNames(classList),
    style: Object.assign(Object.assign({
      fontSize: internalIconSize,
      height: internalIconSize,
      width: internalIconSize
    }, customColor && contrast === 'normal' && {
      color: customColor
    }), style),
    "data-testid": `${icon}_icon`
  }, ingredients, debugService.componentProps));
  return tooltipProps ? jsx(ToolTipWrapper, Object.assign({}, tooltipProps, {
    children: iconElement
  })) : iconElement;
});
XIIcon.displayName = 'XIIcon';

function includeTestId(testId) {
  if (!globalThis.includeTestId) {
    return undefined;
  }
  return typeof testId === 'string' ? testId : testId();
}

class AccordionItemProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], AccordionItemProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], AccordionItemProps.prototype, "noPadding", void 0);
__decorate([Bound(), __metadata("design:type", String)], AccordionItemProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], AccordionItemProps.prototype, "id", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], AccordionItemProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], AccordionItemProps.prototype, "open", void 0);
__decorate([Bound(), __metadata("design:type", String)], AccordionItemProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", String)], AccordionItemProps.prototype, "iconPlacement", void 0);
class AccordionItemEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], AccordionItemEvents.prototype, "onItemClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], AccordionItemEvents.prototype, "onOpenChange", void 0);
/**
 * Description: Item to be used solely inside of the Accordion.
 */
const AccordionItem = props => {
  const debugService = useDebugService(AccordionItem, props);
  const {
      children,
      className,
      color = 'orca',
      disabled = false,
      icon,
      iconPlacement = 'caret',
      id: idInput,
      noPadding = false,
      observer,
      onClick,
      onItemClick,
      onOpenChange,
      open = false,
      title
    } = props,
    ingredients = __rest(props, ["children", "className", "color", "disabled", "icon", "iconPlacement", "id", "noPadding", "observer", "onClick", "onItemClick", "onOpenChange", "open", "title"]);
  const [openState, setOpenState] = useValueBackedState(open && !disabled);
  const [id] = React.useState(() => idInput !== null && idInput !== void 0 ? idInput : uuidv4());
  const childRef = React.useRef(null);
  const onActivation = e => {
    if (!disabled) {
      onClick === null || onClick === void 0 ? void 0 : onClick(e);
      onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick({
        id
      });
      const newIsOpen = !openState;
      setOpenState(newIsOpen);
      onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange({
        open: newIsOpen
      });
      if (observer) {
        observer.open = newIsOpen;
      }
    }
  };
  const handleClick = e => {
    var _a;
    if (childRef.current && ((_a = e.nativeEvent.composedPath()) === null || _a === void 0 ? void 0 : _a.includes(childRef.current))) {
      return;
    }
    onActivation(e);
  };
  const handleKeyPress = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onActivation(e);
    }
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, ingredients, {
    className: classNames('kxi-accordion-section-wrapper kxi-text-normal', className, {
      'kxi-disabled': disabled
    }),
    children: [jsxs("div", {
      tabIndex: 0,
      onClick: handleClick,
      onKeyPress: handleKeyPress,
      className: classNames(`kxi-accordion-section`, {
        'kxi-disabled': disabled
      }),
      children: [jsxs("div", {
        className: classNames('kxi-accordion-text kxi-title', {
          'kxi-accordion-no-padding': noPadding
        }),
        children: [icon && iconPlacement === 'text' && jsx(XIIcon, {
          icon: icon,
          className: "kxi-accordion-icon-left"
        }), jsx("p", {
          className: `${normalizeColor(color)}`,
          "data-testid": includeTestId('accordion-title'),
          children: title
        }), jsx(Slot, {
          name: "header",
          reactChildren: children
        })]
      }), jsxs("div", {
        className: classNames('kxi-title', {
          'kxi-accordion-no-padding': noPadding
        }),
        children: [icon && iconPlacement === 'caret' && jsx(XIIcon, {
          icon: icon,
          className: "kxi-accordion-icon"
        }), jsx(XIIcon, {
          className: "kxi-accordion-open-icon",
          icon: openState ? 'single_arrow_up' : 'single_arrow_down'
        })]
      })]
    }), jsx("div", {
      ref: childRef,
      id: id,
      className: classNames(`kxi-accordion-children`, {
        'kxi-accordion-visible': openState,
        'kxi-accordion-open': openState,
        'kxi-accordion-no-padding': noPadding
      }),
      children: jsx(Slot, {
        name: "children",
        reactChildren: children
      })
    })]
  }));
};
AccordionItem.displayName = 'AccordionItem';

// IMPORTANT: This file is auto generated from utils/generateSVG.js
// Any changes you make to this file here will be overwritten.
const _AppIconsOptions = ['admin', 'admin_white', 'announcements', 'announcements_white', 'benchmark', 'benchmark_white', 'campaign_insights', 'campaign_insights_white', 'case_management', 'case_management_new', 'case_management_new_white', 'case_management_white', 'coach', 'coach_location', 'coach_white', 'cx_analyst', 'cx_analyst_white', 'cxic_admin', 'cxic_reporting', 'dashboard', 'dashboard_white', 'data_exploration', 'data_exploration_white', 'discover', 'discover_insights', 'discover_insights_white', 'discover_white', 'exec_reporting', 'exec_reporting_white', 'explore', 'explore_white', 'field_reporting', 'field_reporting_white', 'home', 'home_white', 'invitation_management', 'invitation_management_white', 'kitchen', 'market_survey', 'market_survey_white', 'mcx', 'mcx_white', 'metrics', 'metrics_white', 'microsurvey', 'microsurvey_white', 'moments', 'moments_white', 'placeholder', 'placeholder_white', 'reporting', 'reporting_white', 'research', 'research_white', 'resolve', 'resolve_white', 'review_trackers', 'review_trackers_white', 'seo', 'seo_white', 'spotlight', 'spotlight_white', 'studio', 'studio_white', 'summary', 'summary_white', 'survey', 'survey_white', 'text_analytics', 'text_analytics_white', 'watch_list', 'watch_list_white', 'workflow'];
const _AppIconsWhiteOptions = ['admin_white', 'announcements_white', 'benchmark_white', 'campaign_insights_white', 'case_management_new_white', 'case_management_white', 'coach_white', 'cx_analyst_white', 'dashboard_white', 'data_exploration_white', 'discover_insights_white', 'discover_white', 'exec_reporting_white', 'explore_white', 'field_reporting_white', 'home_white', 'invitation_management_white', 'market_survey_white', 'mcx_white', 'metrics_white', 'microsurvey_white', 'moments_white', 'placeholder_white', 'reporting_white', 'research_white', 'resolve_white', 'review_trackers_white', 'seo_white', 'spotlight_white', 'studio_white', 'summary_white', 'survey_white', 'text_analytics_white', 'watch_list_white'];
const _AppIconsColorOptions = ['admin', 'announcements', 'benchmark', 'campaign_insights', 'case_management', 'case_management_new', 'coach', 'coach_location', 'cx_analyst', 'cxic_admin', 'cxic_reporting', 'dashboard', 'data_exploration', 'discover', 'discover_insights', 'exec_reporting', 'explore', 'field_reporting', 'home', 'invitation_management', 'kitchen', 'market_survey', 'mcx', 'metrics', 'microsurvey', 'moments', 'placeholder', 'reporting', 'research', 'resolve', 'review_trackers', 'seo', 'spotlight', 'studio', 'summary', 'survey', 'text_analytics', 'watch_list', 'workflow'];

// IMPORTANT: This file is auto generated from utils/generateSVG.js
// Any changes you make to this file here will be overwritten.
const _IconsOptions = ['ab_testing', 'account', 'account_filled', 'action_planning', 'action_planning_filled', 'add_circle', 'add_circle_filled', 'analytics', 'analytics_filled', 'anomaly', 'anomaly_detection', 'anomaly_detection_filled', 'answer_distribution', 'answer_distribution_filled', 'app_admin', 'app_admin_filled', 'app_announcements', 'app_announcements_filled', 'app_benchmark', 'app_benchmark_filled', 'app_campaign_insights', 'app_campaign_insights_filled', 'app_case_management', 'app_case_management_filled', 'app_case_management_new', 'app_case_management_new_filled', 'app_coach', 'app_coach_filled', 'app_cx_analyst', 'app_cx_analyst_filled', 'app_data_exploration', 'app_data_exploration_filled', 'app_discover', 'app_discover_filled', 'app_exec_reporting', 'app_exec_reporting_filled', 'app_explore', 'app_explore_filled', 'app_field_reporting', 'app_field_reporting_filled', 'app_home', 'app_home_filled', 'app_market_survey', 'app_market_survey_filled', 'app_mcx', 'app_mcx_filled', 'app_metrics', 'app_metrics_filled', 'app_microsurvey', 'app_microsurvey_filled', 'app_moments', 'app_moments_filled', 'app_reporting', 'app_reporting_filled', 'app_resolve', 'app_resolve_filled', 'app_review_trackers', 'app_review_trackers_filled', 'app_seo', 'app_seo_filled', 'app_spotlight', 'app_summary', 'app_summary_filled', 'app_textAnalytics', 'app_textAnalytics_filled', 'apps', 'arrow_down', 'arrow_left', 'arrow_link', 'arrow_right', 'arrow_up', 'asterisk', 'attach', 'average', 'average_count', 'award', 'award_filled', 'bar_chart', 'bar_chart_filled', 'barometer', 'barometer_filled', 'brackets', 'brand_survey', 'brand_survey_filled', 'calendar', 'calendar_filled', 'cancel', 'cancel_18', 'cancel_circle', 'cancel_circle_filled', 'case', 'case_complete', 'case_complete_filled', 'case_create', 'case_create_filled', 'case_filled', 'case_remove', 'case_remove_filled', 'category_closed', 'category_open', 'certificate', 'certificate_filled', 'chart', 'chart_1', 'chart_filled', 'chart_filled_1', 'chart_horizontal', 'chart_horizontal_filled', 'check', 'checkmark', 'checkmark_18', 'circle_arrow_down', 'circle_arrow_down_filled', 'circle_arrow_left', 'circle_arrow_left_filled', 'circle_arrow_right', 'circle_arrow_right_filled', 'circle_arrow_up', 'circle_arrow_up_filled', 'circle_check', 'circle_check_filled', 'circle_navigation', 'circle_navigation_filled', 'city', 'clipboard', 'clipboard_2', 'clock', 'clock_2', 'clock_2_filled', 'clock_filled', 'coaching', 'coaching_filled', 'collections', 'collections_filled', 'comment', 'comment_filled', 'community', 'community_filled', 'condensed_card', 'condensed_card_filled', 'convert', 'cookies', 'cookies_filled', 'copy', 'copy_filled', 'count', 'crosstab', 'crosstab_filled', 'dark', 'dark_filled', 'database', 'database_connect', 'day', 'day_filled', 'desktop', 'desktop_filled', 'disable', 'distinct_count', 'divide', 'divide_filled', 'document', 'document_alert', 'document_alert_filled', 'document_bar_chart', 'document_bar_chart_filled', 'document_chart', 'document_chart_filled', 'document_coach', 'document_coach_filled', 'document_comment', 'document_comment_filled', 'document_crosstab', 'document_crosstab_filled', 'document_dashboard', 'document_dashboard_filled', 'document_export', 'document_export_filled', 'document_ext_csv', 'document_ext_excel', 'document_ext_excel_filled', 'document_ext_pdf', 'document_ext_pdf_filled', 'document_ext_txt', 'document_ext_xlm', 'document_ext_xls', 'document_ext_xml', 'document_ext_xml_filled', 'document_field_reporting', 'document_field_reporting_filled', 'document_filled', 'document_multiple', 'document_multiple_checked', 'document_multiple_checked_filled', 'document_multiple_filled', 'document_report', 'document_report_filled', 'document_resolve', 'document_resolve_filled', 'document_scorecard', 'document_scorecard_filled', 'document_settings', 'document_settings_filled', 'document_survey', 'document_survey_filled', 'document_trend', 'document_trend_filled', 'download', 'drag_handle', 'edit', 'edit_filled', 'edit_text', 'edit_text_filled', 'email', 'email_add', 'email_add_filled', 'email_checked', 'email_checked_filled', 'email_edit', 'email_edit_filled', 'email_filled', 'email_location', 'email_location_filled', 'email_location_filled_2', 'email_open', 'email_open_filled', 'email_remove', 'email_remove_filled', 'email_search', 'email_search_filled', 'email_send', 'email_send_filled', 'email_settings', 'email_settings_filled', 'enable', 'equal', 'exchange', 'exit', 'exit_filled', 'external_link', 'external_link_filled', 'eye_scan', 'eye_scan_filled', 'fab_plus', 'favorite', 'favorite_filled', 'feedback', 'feedback_filled', 'filter', 'filter_add', 'filter_add_filled', 'filter_filled', 'filter_remove', 'filter_remove_filled', 'flag', 'flag_filled', 'folder', 'folder_filled', 'folder_private', 'folder_private_filled', 'forward_30', 'full_screen', 'full_story', 'funnel_chart', 'funnel_chart_filled', 'game', 'game_filled', 'gender', 'gender_female', 'gender_male', 'globe', 'globe_filled', 'globe_warning', 'greater_than_or_equal_to', 'grid_view', 'grid_view_filled', 'history', 'hour_glass', 'hour_glass_filled', 'html', 'impact_analysis', 'indeterminate', 'indeterminate_18', 'info', 'info_filled', 'integrations', 'integrations_filled', 'invalid', 'kabob', 'kitchen', 'kitchen_filled', 'less_than_or_equal_to', 'light', 'light_filled', 'lightbulb', 'lightbulb_filled', 'link', 'list_view', 'location', 'location_filled', 'lodge', 'lodge_filled_1', 'lodge_filled_2', 'logging', 'logging_filled', 'mapping', 'mapping_complete', 'mapping_complete_filled', 'market_survey', 'market_survey_filled', 'meatball', 'meatball_filled', 'menu', 'mobile', 'mobile_filled', 'monitor', 'monitor_filled', 'more', 'more_filled', 'multiple_layer', 'navigation', 'navigation_filled', 'new_window', 'new_window_filled', 'no_icon', 'not_equal_to', 'notepad', 'notepad_filled', 'notification', 'notification_filled', 'notification_snooze', 'notification_snooze_filled', 'notifications_off', 'notifications_off_filled', 'organization', 'organization_filled', 'page_count', 'page_count_filled', 'parentheses', 'password', 'password_filled', 'password_open', 'password_open_filled', 'pause', 'pause_filled', 'percentage', 'permission', 'permission_filled', 'photo', 'photo_filled', 'pie_chart', 'pie_chart_filled', 'pin', 'pin_filled', 'play', 'play_control', 'play_control_filled', 'play_filled', 'plus', 'preview', 'preview_filled', 'previous_page', 'previous_page_filled', 'print', 'print_filled', 'product_survey', 'product_survey_filled', 'quantum_metric', 'question_mark', 'receipt', 'receipt_filled', 'refresh', 'regional_summary', 'regional_summary_filled', 'remove_circle', 'remove_circle_filled', 'reply', 'reporting', 'reporting_filled', 'research_panel', 'research_panel_filled', 'rewind_15', 'rocket', 'rocket_filled', 'role', 'role_filled', 'save', 'save_filled', 'saved_views', 'saved_views_filled', 'scale', 'scale_filled', 'schedule', 'schedule_filled', 'search', 'search_filled', 'search_suggestion', 'search_suggestion_filled', 'sentiment_negative', 'sentiment_negative_filled', 'sentiment_neutral', 'sentiment_neutral_filled', 'sentiment_positive', 'sentiment_positive_filled', 'settings', 'settings_filled', 'share', 'share_filled', 'shield', 'shield_filled', 'shield_remove', 'shield_remove_filled', 'shipping', 'shipping_filled', 'signature', 'signature_filled', 'single_arrow_down', 'single_arrow_left', 'single_arrow_right', 'single_arrow_up', 'single_layer', 'small_circle', 'small_circle_filled', 'small_down_triangle', 'small_down_triangle_filled', 'small_left_triangle', 'small_left_triangle_filled', 'small_right_triangle', 'small_right_triangle_filled', 'small_up_triangle', 'small_up_triangle_filled', 'sort', 'sort_most_relevant', 'sort_order_asc', 'sort_order_desc', 'split', 'split_filled', 'stacked_columns', 'stacked_rows', 'star', 'star_filled', 'stethoscope', 'stethoscope_filled', 'style', 'style_filled', 'support', 'support_filled', 'survey_authoring', 'survey_authoring_filled', 'tag', 'tag_filled', 'text_formatting', 'theme', 'theme_filled', 'time', 'time_filled', 'tools', 'tools_filled', 'translation', 'translation_filled', 'trash', 'trash_filled', 'tree_picker_collapse', 'tree_picker_expand', 'trend_flatten', 'trend_increase', 'unassigned', 'unassigned_filled', 'upload', 'user_female', 'user_female_filled_1', 'user_female_filled_2', 'user_male', 'user_male_filled_1', 'user_male_filled_2', 'users', 'users_filled', 'video', 'video_filled', 'view_condensed', 'view_condensed_filled', 'view_normal', 'view_normal_filled', 'view_table', 'view_table_filled', 'visibility', 'visibility_filled', 'visibility_off', 'visibility_off_filled', 'volume_off', 'volume_off_filled', 'volume_on', 'volume_on_filled', 'warning', 'warning_filled', 'width', 'width_2', 'wifi'];

// IMPORTANT: This file is auto generated from utils/generateSVG.js
// Any changes you make to this file here will be overwritten.
const _SVGsOptions = ['binoculars', 'booking', 'browser_refresh', 'burger', 'camping', 'car', 'category', 'category_cycle', 'celebration', 'certificate', 'chart', 'chart_double', 'clapper_board', 'clipboard_planning', 'clothes_shopping', 'clothing_hung', 'cogs', 'colorful_wave', 'crane_illustration', 'crystal_ball', 'cx_background', 'cx_edit_dialog_left_bg', 'discount_tag', 'display_ab_running', 'display_app', 'display_banner', 'display_bottom_center', 'display_bottom_left', 'display_bottom_right', 'display_default', 'display_embedded', 'display_middle_center', 'display_middle_left', 'display_middle_right', 'display_popup', 'display_rating', 'display_tab', 'display_top_center', 'display_top_left', 'display_top_right', 'display_window', 'document_okay', 'document_section', 'email', 'employee_engagement', 'ex_background', 'ex_edit_dialog_left_bg', 'facebook', 'file_upload', 'four_square', 'fries', 'gift_card', 'globe', 'google', 'graph', 'graph_message', 'graph_mobile_web', 'graph_progress_search', 'graph_search', 'graph_web', 'groceries', 'group', 'hand_embrace', 'hand_favorite', 'hand_money', 'hand_security', 'hand_shake', 'health_grades', 'hospital', 'human_message', 'human_okay', 'inmoment_favicon_blue', 'inmoment_favicon_magenta', 'inmoment_favicon_orca', 'inmoment_favicon_purple', 'inmoment_favicon_teal', 'inmoment_favicon_white', 'inmoment_main_full_color', 'inmoment_main_white', 'inmoment_powered_by_horizontal_full_color', 'inmoment_powered_by_horizontal_gray', 'inmoment_powered_by_horizontal_orca', 'inmoment_powered_by_horizontal_white', 'inmoment_powered_by_vertical_full_color', 'inmoment_powered_by_vertical_gray', 'inmoment_powered_by_vertical_orca', 'inmoment_powered_by_vertical_white', 'instagram', 'international', 'late_rooms', 'location', 'location_map', 'message', 'message_group', 'mobile_touch', 'money', 'mx_background', 'mx_edit_dialog_left_bg', 'negative_sentiment', 'negative_sentiment_filled', 'negative_sentiment_outlined', 'neutral_sentiment', 'neutral_sentiment_filled', 'neutral_sentiment_outlined', 'no', 'none', 'numbers_graphic', 'open_table', 'other', 'outbound_call', 'outbound_email', 'people_boy', 'people_girl', 'person_favorite', 'planet_1', 'planet_2', 'plus', 'positive_sentiment', 'positive_sentiment_filled', 'positive_sentiment_outlined', 'post_transaction', 'product_offer', 'ramen', 'receipt', 'relationship', 'secret_code_graphic', 'security', 'skew_clock', 'skew_desktop_1', 'skew_desktop_2', 'skew_desktop_3', 'skew_phone_1', 'skew_phone_2', 'slide_widget_background', 'sms', 'spoof', 'spoof_2', 'star_circle', 'star_pointed', 'stethoscope', 'survey', 'target', 'thumbs_up', 'ticket', 'timeout_survey', 'trip_advisor', 'trophy', 'trust_pilot', 'twitter', 'user_categories', 'user_categories_complete', 'user_cycle', 'video', 'vitals', 'voice_of_employee', 'web', 'window_bank', 'window_banner', 'window_banner_above', 'window_banner_inline', 'window_default', 'window_embedded', 'window_feedback_default', 'window_popup', 'xi_icon', 'yelp', 'yes', 'zomato'];

const _LottiesOptions = ['admin_loader', 'confetti_hat', 'CUSTOMER_loader', 'cx_loader', 'EMPLOYEE_loader', 'ex_loader', 'MARKETING_loader', 'mx_loader', 'orca_loader', 'px_loader', 'PRODUCT_loader', 'planet_1'];
var EnumLottiesProd;
(function (EnumLottiesProd) {
  EnumLottiesProd["admin_loader"] = "admin_loader";
  EnumLottiesProd["confetti_hat"] = "confetti_hat";
  EnumLottiesProd["CUSTOMER_loader"] = "CUSTOMER_loader";
  EnumLottiesProd["cx_loader"] = "cx_loader";
  EnumLottiesProd["EMPLOYEE_loader"] = "EMPLOYEE_loader";
  EnumLottiesProd["ex_loader"] = "ex_loader";
  EnumLottiesProd["MARKETING_loader"] = "MARKETING_loader";
  EnumLottiesProd["mx_loader"] = "mx_loader";
  EnumLottiesProd["orca_loader"] = "orca_loader";
  EnumLottiesProd["px_loader"] = "px_loader";
  EnumLottiesProd["PRODUCT_loader"] = "PRODUCT_loader";
  EnumLottiesProd["planet_1"] = "planet_1";
})(EnumLottiesProd || (EnumLottiesProd = {}));

const _IconColorsOptions = [..._ColorsOptions, ..._ErrorStatesOptions, ..._SentimentsOptions, ..._DisabledOptions, ..._AllColorsOptions, ..._StatusStatesOptions];
const isIcons = (() => {
  const iconsSet = new CachedConstant(() => new Set(_IconsOptions));
  return value => typeof value === 'string' && iconsSet.value.has(value);
})();
const isAppIcons = (() => {
  const appIconsSet = new CachedConstant(() => new Set(_AppIconsOptions));
  return value => typeof value === 'string' && appIconsSet.value.has(value);
})();

class ButtonHTMLAttributes extends HTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Boolean)], ButtonHTMLAttributes.prototype, "autoFocus", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ButtonHTMLAttributes.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", String)], ButtonHTMLAttributes.prototype, "form", void 0);
__decorate([Bound(), __metadata("design:type", String)], ButtonHTMLAttributes.prototype, "formAction", void 0);
__decorate([Bound(), __metadata("design:type", String)], ButtonHTMLAttributes.prototype, "formEncType", void 0);
__decorate([Bound(), __metadata("design:type", String)], ButtonHTMLAttributes.prototype, "formMethod", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ButtonHTMLAttributes.prototype, "formNoValidate", void 0);
__decorate([Bound(), __metadata("design:type", String)], ButtonHTMLAttributes.prototype, "formTarget", void 0);
__decorate([Bound(), __metadata("design:type", String)], ButtonHTMLAttributes.prototype, "name", void 0);
__decorate([Bound(), __metadata("design:type", String)], ButtonHTMLAttributes.prototype, "type", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ButtonHTMLAttributes.prototype, "value", void 0);
class ButtonHTMLEvents extends DOMEvents {}

class ImgHTMLAttributes extends HTMLAttributes {}
class ImgHTMLEvents extends DOMEvents {}

function assetsRootPath() {
  var _a, _b;
  return ((_b = (_a = window.env) === null || _a === void 0 ? void 0 : _a.urls) === null || _b === void 0 ? void 0 : _b.resources) || 'https://assets.inmoment.com';
}
function kitchenRootPath() {
  const assetsRoot = assetsRootPath();
  return `${assetsRoot}/assets/kitchen/v2`;
}

class XIAppIconProps extends ImgHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], XIAppIconProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", Number)], XIAppIconProps.prototype, "iconSize", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], XIAppIconProps.prototype, "stretch", void 0);
class XIAppIconEvents extends ImgHTMLEvents {}
/**
 * Description: Component that displays an Icon in the AppIcon category.
 */
const XIAppIcon = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(XIAppIcon, props);
  const {
      icon,
      iconSize = 60,
      stretch = false,
      className
    } = props,
    ingredients = __rest(props, ["icon", "iconSize", "stretch", "className"]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('XIAppIcon', {
      icon
    });
  }, [icon]);
  let style = Object.assign({}, ingredients.style);
  let classList = `${className || ''}`;
  if (!stretch) {
    style = Object.assign({
      width: iconSize,
      height: iconSize
    }, style);
  } else {
    classList += ' kxi-stretch';
  }
  return jsx("img", Object.assign({}, debugService.componentProps, {
    className: classList,
    "data-testid": `${icon}_app_icon`,
    src: `${kitchenRootPath()}/app-icons/${icon}.svg`,
    alt: icon
  }, ingredients, {
    style: style,
    ref: ref
  }));
});
XIAppIcon.displayName = 'XIAppIcon';

const CloseIcon = props => {
  const {
    className = '',
    onCancel,
    disabled = false,
    contrast = 'normal',
    focusContrast = 'normal',
    size = 24,
    icon
  } = props;
  const debugService = useDebugService(CloseIcon, props);
  const [isActive, setIsActive] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const handleClick = e => {
    e.stopPropagation();
    onCancel === null || onCancel === void 0 ? void 0 : onCancel(false);
  };
  const handleKeyDown = e => {
    if (isActivation(e)) {
      e.stopPropagation();
      e.preventDefault();
      setIsActive(true);
      onCancel === null || onCancel === void 0 ? void 0 : onCancel(true);
    }
  };
  const handleKeyUp = e => {
    if (isActivation(e)) {
      setIsActive(false);
    }
  };
  const getIcon = () => {
    var _a, _b;
    if (icon) {
      if (isHovering) {
        if (!icon.endsWith('_filled') && isIcons(`${icon}_filled`)) {
          return `${icon}_filled`;
        } else if (icon.endsWith('_filled') && isIcons((_a = icon.split('_filled')) === null || _a === void 0 ? void 0 : _a[0])) {
          return ((_b = icon.split('_filled')) === null || _b === void 0 ? void 0 : _b[0]) || icon;
        } else {
          devOnlyWarn(`The Kitchen: The icon '${icon}' does not have a matching filled version. This icon will not have any hovered indication`);
          return icon;
        }
      } else {
        return icon;
      }
    } else {
      return isHovering ? 'cancel_circle_filled' : 'cancel_circle';
    }
  };
  const usedIcon = getIcon();
  const classes = classNames('kxi-close-icon', className, {
    'kxi-high-contrast': contrast === 'high',
    'kxi-close-icon-focus-high-contrast': focusContrast === 'high',
    active: isActive
  });
  return jsx(React.Fragment, {
    children: !disabled ? jsx("div", Object.assign({}, debugService.componentProps, {
      role: "button",
      tabIndex: 0,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      onBlur: () => setIsActive(false),
      "aria-label": "cancel icon",
      "aria-disabled": disabled,
      className: classes,
      onMouseEnter: () => setIsHovering(true),
      onMouseLeave: () => setIsHovering(false),
      children: isIcons(usedIcon) ? jsx(XIIcon, {
        iconSize: size,
        icon: usedIcon,
        contrast: contrast
      }) : jsx(XIAppIcon, {
        iconSize: size,
        icon: usedIcon
      })
    })) : null
  });
};
CloseIcon.displayName = 'CloseIcon';

/**
 * Description: An internal component used for animation.
 */
const MotionDiv = props => {
  const {
      error,
      hasAnimation,
      stretch,
      className
    } = props,
    ingredients = __rest(props, ["error", "hasAnimation", "stretch", "className"]);
  const controls = useAnimation$1();
  const sequence = async () => {
    await controls.start({
      x: -6,
      transition: {
        duration: 0.08
      }
    });
    await controls.start({
      x: 6,
      transition: {
        duration: 0.08
      }
    });
    return controls.start({
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 1000,
        damping: 10
      }
    });
  };
  if (hasAnimation) {
    return jsx(motion.div, Object.assign({
      className: `kxi-animated-button ${!stretch ? 'kxi-inline-block' : ''} ${className || ''}`,
      whileHover: {
        scale: 1.01
      },
      whileTap: {
        scale: 0.95
      },
      transition: {
        scale: {
          type: 'spring',
          stiffness: 800,
          damping: 20
        }
      },
      onClick: () => void sequence(),
      animate: error ? controls : {},
      "data-testid": "default-button-animation"
    }, ingredients, {
      children: props.children
    }));
  } else {
    return jsx("div", {
      className: `${className || ''}`,
      children: props.children
    });
  }
};
class DefaultButtonProps extends ButtonHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "fill", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "buttonSize", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "sentimentIndicator", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DefaultButtonProps.prototype, "hasAnimation", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DefaultButtonProps.prototype, "hasShadows", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "iconLeft", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "iconRight", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DefaultButtonProps.prototype, "isIconRightButton", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DefaultButtonProps.prototype, "isIconLeftButton", void 0);
__decorate([Bound(), __metadata("design:type", Object)], DefaultButtonProps.prototype, "textIconAlignment", void 0);
__decorate([Bound(), __metadata("design:type", Object)], DefaultButtonProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "tone", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "wrap", void 0);
__decorate([Bound(), __metadata("design:type", Object)], DefaultButtonProps.prototype, "ref", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], DefaultButtonProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DefaultButtonProps.prototype, "tooltipDisplayOnDisabled", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DefaultButtonProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", String)], DefaultButtonProps.prototype, "tag", void 0);
class DefaultButtonEvents extends ButtonHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], DefaultButtonEvents.prototype, "onIconClick", void 0);
/**
 * Description: This is standard button and should be used whenever possible. A small button is also acceptable. Don't use all caps in buttons. Status will override all colors.
 */
const DefaultButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(DefaultButton, props);
  const {
      fill = 'fill',
      text,
      color = props.fill === 'link' ? 'CX' : 'orca',
      sentimentIndicator,
      buttonSize = 'normal',
      hasAnimation = true,
      hasShadows = false,
      stretch,
      tooltip,
      disabled,
      tabIndex,
      type,
      status,
      contrast = 'normal',
      wrap = 'nowrap',
      onClick,
      className,
      style,
      tag,
      tone = 'normal',
      isIconLeftButton,
      isIconRightButton,
      iconLeft,
      iconRight,
      textIconAlignment = 'center',
      onIconClick,
      tooltipDisplayOnDisabled,
      _appearInert: appearInert = false
    } = props,
    ingredients = __rest(props, ["fill", "text", "color", "sentimentIndicator", "buttonSize", "hasAnimation", "hasShadows", "stretch", "tooltip", "disabled", "tabIndex", "type", "status", "contrast", "wrap", "onClick", "className", "style", "tag", "tone", "isIconLeftButton", "isIconRightButton", "iconLeft", "iconRight", "textIconAlignment", "onIconClick", "tooltipDisplayOnDisabled", "_appearInert"]);
  const [hasErrorState, setHasErrorState] = React.useState(false);
  React.useEffect(() => {
    if (status) {
      if (status === 'error') {
        setHasErrorState(true);
      } else {
        setHasErrorState(false);
      }
    } else {
      setHasErrorState(false);
    }
  }, [status]);
  /**
   * Error Messages for Required Props
   */
  const validType = checkIfButtonTypeIsValid('DefaultButton', type);
  const iconSize = buttonSize === 'small' && 20 || buttonSize === 'large' && 30 || buttonSize === 'xxlarge' && 42 || 24;
  const handleClick = e => {
    if (!hasErrorState && !disabled) {
      if (onClick) onClick(e);
    }
  };
  const wrapClassName = `kxi-text-wrap-${wrap}`;
  const tagAndTextHtmlString = sanitizeHtmlString(`${tag ? `<span class="kxi-tag">${tag}:</span>` : ''}${text !== null && text !== void 0 ? text : ''}`);
  const tagAndText = () => jsx("pre", {
    className: wrapClassName,
    dangerouslySetInnerHTML: {
      __html: tagAndTextHtmlString
    }
  });
  const insertIconIfExists = () => {
    if (iconLeft || iconRight) {
      // For 'fill' buttons with normal tone, the background makes it hard to see focus rings,
      // so invert the contrast for those so that focus rings are visible.
      const invertedContrast = contrast === 'high' ? 'normal' : 'high';
      const focusContrast = fill === 'fill' && tone === 'normal' ? invertedContrast : contrast;
      const leftIcon = iconLeft ? isIconLeftButton ? jsx(CloseIcon, {
        icon: iconLeft,
        size: iconSize,
        className: "kxi-default-icon-button kxi-icon-left kxi-icon-left-button",
        disabled: disabled,
        contrast: contrast,
        focusContrast: focusContrast,
        onCancel: () => onIconClick === null || onIconClick === void 0 ? void 0 : onIconClick({
          iconSide: 'Left'
        })
      }) : jsx("div", {
        className: "kxi-default-icon-button kxi-icon-left",
        children: isIcons(iconLeft) ? jsx(XIIcon, {
          icon: iconLeft,
          iconSize: iconSize
        }) : jsx(XIAppIcon, {
          icon: iconLeft,
          iconSize: iconSize
        })
      }) : undefined;
      const rightIcon = iconRight ? isIconRightButton ? jsx(CloseIcon, {
        icon: iconRight,
        size: iconSize,
        className: "kxi-default-icon-button kxi-icon-right kxi-icon-right-button",
        disabled: disabled,
        contrast: contrast,
        focusContrast: focusContrast,
        onCancel: () => onIconClick === null || onIconClick === void 0 ? void 0 : onIconClick({
          iconSide: 'Right'
        })
      }) : jsx("div", {
        className: "kxi-default-icon-button kxi-icon-right",
        children: isIcons(iconRight) ? jsx(XIIcon, {
          icon: iconRight,
          iconSize: iconSize
        }) : jsx(XIAppIcon, {
          icon: iconRight,
          iconSize: iconSize
        })
      }) : undefined;
      return jsxs(Fragment, {
        children: [leftIcon, tagAndText(), rightIcon]
      });
    } else {
      return tagAndText();
    }
  };
  const getClassList = () => {
    const hasText = text && text.trim() !== '';
    const hasTag = tag && tag.trim() !== '';
    return classNames('kxi-button', buttonSize && `kxi-${buttonSize}`, status && `kxi-${status}`, fill && `kxi-button-${fill}`, wrapClassName, {
      'kxi-text-button': hasText || hasTag,
      'kxi-icon-left': iconLeft && hasText,
      'kxi-icon-right': iconRight && hasText,
      'kxi-high-contrast': contrast === 'high',
      'kxi-stretch-width': stretch,
      'kxi-shadows': !disabled && hasShadows,
      'kxi-tone-light': !disabled && tone === 'light',
      'kxi-button-appear-inert': appearInert
    });
  };
  // Only use the position elements on the outer style, pass other style elements through to button
  const _a = style || {},
    {
      position,
      top,
      left,
      right,
      bottom,
      display
    } = _a,
    otherStyles = __rest(_a, ["position", "top", "left", "right", "bottom", "display"]);
  const stretchDisplay = stretch ? 'inherit' : 'inline-flex';
  return jsx(ToolTipWrapper, Object.assign({}, tooltip, {
    enabled: (tooltip === null || tooltip === void 0 ? void 0 : tooltip.enabled) !== false && (tooltipDisplayOnDisabled || !disabled),
    stretch: (tooltip === null || tooltip === void 0 ? void 0 : tooltip.stretch) || stretch,
    children: jsx("div", Object.assign({
      className: color ? normalizeColor(color) : '',
      style: {
        position,
        top,
        left,
        right,
        bottom,
        display: display || stretchDisplay
      }
    }, debugService.componentProps, {
      children: jsxs(MotionDiv, {
        stretch: true,
        error: hasErrorState,
        hasAnimation: disabled ? false : hasAnimation,
        children: [jsx("button", Object.assign({
          className: `${getClassList()} ${className || ''}`,
          style: Object.assign(Object.assign({}, otherStyles), {
            justifyContent: textIconAlignment
          }),
          type: validType ? type : undefined,
          onClick: handleClick
        }, ingredients, {
          ref: ref,
          disabled: disabled,
          tabIndex: tabIndex,
          children: insertIconIfExists()
        })), sentimentIndicator && jsx("div", {
          className: `kxi-btn-sentiment-indicator ${normalizeColor(sentimentIndicator)}`
        })]
      })
    }))
  }));
});
DefaultButton.displayName = 'DefaultButton';

class AlertInlineMainProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Object)], AlertInlineMainProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", String)], AlertInlineMainProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], AlertInlineMainProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], AlertInlineMainProps.prototype, "isVisible", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AlertInlineMainProps.prototype, "buttons", void 0);
__decorate([Bound(), __metadata("design:type", Object)], AlertInlineMainProps.prototype, "width", void 0);
class AlertInlineMainEvents extends DivHTMLEvents {}
/**
 * Description: Give the user feedback that is good, bad, or a warning.
 */
const AlertInlineMain = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(AlertInlineMain, props);
  const {
      isVisible = true,
      color = 'orca',
      title = '',
      subtitle,
      width,
      buttons
    } = props,
    ingredients = __rest(props, ["isVisible", "color", "title", "subtitle", "width", "buttons"]);
  throwIfRequiredPropNotProvided('AlertInlineMain', {
    subtitle
  });
  const highlightColor = normalizeColor(color);
  const subtitleElement = (() => {
    const props = typeof subtitle === 'string' ? {
      dangerouslySetInnerHTML: {
        __html: sanitizeHtmlString(subtitle)
      }
    } : {
      children: subtitle
    };
    return jsx("p", Object.assign({
      className: "kxi-text-normal"
    }, props));
  })();
  return jsx("div", Object.assign({}, debugService.componentProps, {
    role: "alert",
    "aria-hidden": !isVisible,
    className: `${highlightColor} kxi-alert-inline-main-wrapper`
  }, ingredients, {
    ref: ref,
    children: jsx(AnimatePresence, {
      children: isVisible && jsx(motion.div, {
        animate: {
          maxHeight: 750,
          opacity: 1
        },
        initial: {
          maxHeight: 0,
          opacity: 0
        },
        exit: {
          maxHeight: 0,
          opacity: 0
        },
        transition: {
          duration: 0.3,
          ease: 'easeInOut'
        },
        "data-testid": includeTestId('kxi-alert-inline-main-content-wrapper'),
        children: jsx("div", {
          className: "kxi-color-accent",
          children: jsxs("div", {
            className: classNames('kxi-alert-inline-main-content kxi-grey', {
              'kxi-alert-inline-main-has-buttons': buttons !== undefined
            }),
            style: {
              width
            },
            children: [jsx("p", {
              className: "kxi-text-large kxi-text-600",
              dangerouslySetInnerHTML: {
                __html: sanitizeHtmlString(title)
              }
            }), subtitleElement, buttons && jsxs("div", {
              className: "kxi-alert-inline-main-button-container kxi-stretch-width",
              children: [buttons.secondary && buttons.secondary.text && jsx(DefaultButton, {
                fill: "text",
                color: "orca",
                text: buttons.secondary.text,
                buttonSize: "normal",
                onClick: buttons.secondary.onClick
              }), buttons.primary.text && jsx("div", {
                className: "kxi-alert-inline-main-primary-button",
                children: jsx(DefaultButton, {
                  fill: "outline",
                  color: color,
                  text: buttons.primary.text,
                  buttonSize: "normal",
                  onClick: buttons.primary.onClick
                })
              })]
            })]
          })
        })
      })
    })
  }));
});
AlertInlineMain.displayName = 'AlertInlineMain';

class UListHTMLAttributes extends HTMLAttributes {}
class UListHTMLEvents extends DOMEvents {}

/**
 * The duration of the animation when the progress transitions from 0% to 100%.
 *
 * (Kitchen-only note): This is a free parameter that we can modify to get the desired duration.
 */
const progressBarWidthDurationMilliseconds = 500;
class ProgressBarProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Object)], ProgressBarProps.prototype, "progress", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ProgressBarProps.prototype, "ref", void 0);
class ProgressBarEvents extends DivHTMLEvents {}
/**
 * Description: A progress bar indicating the status of some task.
 */
const ProgressBar = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ProgressBar, props);
  const {
      className,
      progress: progressInput = 0
    } = props,
    // progress is required but provide a default anyway
    ingredients = __rest(props, ["className", "progress"]);
  // #region Props validation
  useErrorIfRequiredPropIsNotProvided(debugService, props, 'progress');
  const isProgressInvalid = progress => progress < 0 || progress > 1;
  if (typeof progressInput === 'number') {
    debugService.throwIf(isProgressInvalid(progressInput), `\`progress\` should be a number between 0 and 1 (inclusive), but ${progressInput} was provided.`);
  } else {
    debugService.throwIf(isProgressInvalid(progressInput.start), `\`progress.start\` should be a number between 0 and 1 (inclusive), but ${progressInput.start} was provided.`);
    debugService.throwIf(isProgressInvalid(progressInput.stop), `\`progress.stop\` should be a number between 0 and 1 (inclusive), but ${progressInput.stop} was provided.`);
  }
  // #endregion Props validation
  const sanitizedProgressInput = typeof progressInput === 'number' ? clamp(progressInput, 0, 1) : 0;
  const [progressPercentage, setProgressPercentage] = useValueBackedState(100 * sanitizedProgressInput);
  const [prevProgressPercentage, setPrevProgressPercentage] = React.useState(progressPercentage);
  const [transitionDurationMs, setTransitionDurationMs] = React.useState(0);
  const [ariaValueNow, setAriaValueNow] = useValueBackedState(progressPercentage);
  /**
   * Ensure that transitions have a constant speed (e.g., 40 pixels per second) rather than a constant duration (e.g., 0.5 seconds).
   *     This replaces a `useLayoutEffect`.
   *
   * There's no need to update aria-valuenow during this animation because it's just to make things pretty.
   */
  if (prevProgressPercentage !== progressPercentage) {
    setPrevProgressPercentage(progressPercentage);
    if (typeof progressInput === 'number') {
      /** Amount of time to go 1%. */
      const millisecondsPerPercentage = progressBarWidthDurationMilliseconds / 100;
      /** The percent increase/decrease from the previous value. */
      const absolutePercentageChange = Math.abs(prevProgressPercentage - progressPercentage);
      const durationMilliseconds = millisecondsPerPercentage * absolutePercentageChange;
      setTransitionDurationMs(durationMilliseconds);
    }
  }
  /**
   * Updates progress smoothly when `progress` is provided as an object.
   *
   * Also updates aria-valuenow as the animation progresses.
   * */
  React.useLayoutEffect(() => {
    if (typeof progressInput === 'number') {
      return;
    }
    const {
      start = 0,
      stop = 0.99,
      duration = 10000
    } = progressInput;
    const startPercentage = 100 * start;
    const stopPercentage = 100 * stop;
    setTransitionDurationMs(0);
    setProgressPercentage(startPercentage);
    let ariaId = undefined;
    const animationId = requestAnimationFrame(startTime => {
      // Start animation
      setTransitionDurationMs(duration);
      setProgressPercentage(stopPercentage);
      // #region Update aria-valuenow live as the animation progresses
      const incrementAriaValueNow = (() => {
        /** Combines direction information with converting to a percentage */
        const multiplier = stopPercentage - startPercentage;
        return (prevValue, deltaTime) => prevValue + multiplier * (deltaTime / duration);
      })();
      /** Updates aria-valuenow until animation is complete. */
      const updateAriaValueNow = prevTime => {
        ariaId = requestAnimationFrame(currTime => {
          if (currTime - startTime < duration) {
            setAriaValueNow(prev => incrementAriaValueNow(prev, currTime - prevTime));
            updateAriaValueNow(currTime);
          } else {
            setAriaValueNow(stopPercentage);
          }
        });
      };
      // Initial call to set to start percentage
      ariaId = requestAnimationFrame(currTime => {
        setAriaValueNow(incrementAriaValueNow(startPercentage, currTime - startTime));
        updateAriaValueNow(currTime);
      });
      // #endregion Update aria-valuenow live as the animation progresses
    });
    return () => {
      cancelAnimationFrame(animationId);
      ariaId !== undefined && cancelAnimationFrame(ariaId);
    };
  }, [progressInput, setAriaValueNow, setProgressPercentage]);
  return jsx("div", Object.assign({}, ingredients, debugService.componentProps, {
    className: classNames(className, 'kxi-progress-bar orca'),
    ref: ref,
    role: "progressbar",
    "aria-valuenow": ariaValueNow,
    children: jsx("div", {
      className: "kxi-progress-bar-animation cx",
      style: Object.assign({
        width: `${progressPercentage}%`
      }, transitionDurationMs !== undefined && {
        transitionDuration: `${transitionDurationMs}ms`
      })
    })
  }));
});
ProgressBar.displayName = 'ProgressBar';

const SnackBarItem = props => {
  const {
    text,
    actionText,
    isActionTextBelow,
    mode,
    onActionClick,
    progressBarProps
  } = props;
  const isError = mode === 'error';
  return jsxs("div", {
    className: classNames(`kxi-snackbar-item kxi-snackbar-${mode}-wrapper`),
    role: isError ? 'alert' : 'status',
    children: [isError ? jsx("div", {
      className: "kxi-snackbar-error-sidebar kxi-error"
    }) : null, jsxs("div", {
      className: classNames(`kxi-snackbar-action`, {
        'kxi-snackbar-action-below': isActionTextBelow,
        'kxi-snackbar-action-has-progress-bar': progressBarProps !== undefined
      }),
      children: [jsx("div", {
        className: "kxi-snackbar-text",
        dangerouslySetInnerHTML: {
          __html: sanitizeHtmlString(text !== null && text !== void 0 ? text : '')
        }
      }), progressBarProps && jsx("div", {
        className: "kxi-snackbar-progress-bar-wrapper",
        children: jsx(ProgressBar, Object.assign({}, progressBarProps))
      }), jsx("div", {
        className: classNames('cx kxi-snackbar-dismiss-wrapper', {
          'kxi-snackbar-action-error': isError
        }),
        onClick: onActionClick,
        children: jsx("span", {
          className: "pointer",
          children: actionText
        })
      })]
    })]
  });
};

class SnackBarProps extends UListHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Boolean)], SnackBarProps.prototype, "autoDismiss", void 0);
__decorate([Bound(), __metadata("design:type", String)], SnackBarProps.prototype, "mode", void 0);
__decorate([Bound(), __metadata("design:type", String)], SnackBarProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", String)], SnackBarProps.prototype, "id", void 0);
__decorate([Bound(), __metadata("design:type", Number)], SnackBarProps.prototype, "dismissTime", void 0);
__decorate([Bound(), __metadata("design:type", String)], SnackBarProps.prototype, "actionText", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SnackBarProps.prototype, "isActionTextBelow", void 0);
__decorate([Bound(), __metadata("design:type", Object)], SnackBarProps.prototype, "progressBarProps", void 0);
class SnackBarEvents extends UListHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], SnackBarEvents.prototype, "onDismiss", void 0);
__decorate([Bound(), __metadata("design:type", Function)], SnackBarEvents.prototype, "onActionClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], SnackBarEvents.prototype, "onDisplay", void 0);
/**
 * Description: A popover notification in the bottom right hand corner. Note: 'error' will never auto-dismiss. dismissTime is in milliseconds
 */
const SnackBar = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(SnackBar, props);
  const {
      autoDismiss = false,
      className,
      mode = 'default',
      text,
      id,
      actionText = 'Dismiss',
      dismissTime = 5000,
      isActionTextBelow = false,
      progressBarProps,
      onDismiss,
      onDisplay,
      onActionClick
    } = props,
    ingredients = __rest(props, ["autoDismiss", "className", "mode", "text", "id", "actionText", "dismissTime", "isActionTextBelow", "progressBarProps", "onDismiss", "onDisplay", "onActionClick"]);
  const [snackBars, setSnackBars] = React.useState({});
  const snackBarAutoDismissTimeoutsRef = React.useRef({});
  const deleteItem = React.useCallback(id => {
    delete snackBarAutoDismissTimeoutsRef.current[id];
    setSnackBars(prev => {
      const _a = prev,
        _b = id;
        _a[_b];
        const rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
      return rest;
    });
  }, []);
  React.useEffect(() => {
    if (!id) {
      return;
    }
    const snackBar = {
      actionText,
      autoDismiss,
      dismissTime,
      id,
      isActionTextBelow,
      mode,
      progressBarProps,
      text
    };
    setSnackBars(prev => Object.assign(Object.assign({}, prev), {
      [id]: snackBar
    }));
    onDisplay === null || onDisplay === void 0 ? void 0 : onDisplay({
      snackBar,
      close: () => deleteItem(id)
    });
    if (autoDismiss && mode !== 'error' && !(id in snackBarAutoDismissTimeoutsRef.current)) {
      snackBarAutoDismissTimeoutsRef.current[id] = setTimeout(() => deleteItem(id), dismissTime);
    }
  }, [id]);
  React.useEffect(() => {
    return () => Object.values(snackBarAutoDismissTimeoutsRef.current).forEach(clearTimeout);
  }, []);
  return /*#__PURE__*/ReactDOM.createPortal(jsx("ul", Object.assign({}, ingredients, debugService.componentProps, {
    className: classNames(className, 'kxi-snackbar-wrapper orca'),
    ref: ref,
    children: jsx(AnimatePresence, {
      initial: false,
      children: Object.values(snackBars).map(item => jsx(motion.li, {
        className: "kxi-snackbar-list-item",
        initial: {
          opacity: 0,
          rotate: 180
        },
        animate: {
          y: 0,
          opacity: 1,
          scale: 1,
          transition: {
            type: 'spring',
            damping: 40,
            mass: 0.2,
            stiffness: 100
          }
        },
        exit: {
          opacity: 0,
          scale: 1,
          height: 48,
          transition: {
            ease: 'circOut',
            duration: 0.15
          }
        },
        children: jsx(SnackBarItem, Object.assign({}, item, {
          onActionClick: () => {
            onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss({
              dismissedSnackBar: item
            });
            onActionClick === null || onActionClick === void 0 ? void 0 : onActionClick({
              dismissedSnackBar: item
            });
            deleteItem(item.id);
          }
        }))
      }, item.id))
    })
  })), document.body);
});
SnackBar.displayName = 'SnackBar';

const {
  initial,
  animate,
  exit
} = AnimatePresenceStates;
class SuccessToasterProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], SuccessToasterProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], SuccessToasterProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SuccessToasterProps.prototype, "isVisible", void 0);
class SuccessToasterEvents extends DivHTMLEvents {}
/**
 * Description: Toaster that pops up and displays a success message displaying to them a good time.
 */
const SuccessToaster = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(SuccessToaster, props);
  const {
      title,
      subtitle,
      isVisible,
      className
    } = props,
    ingredients = __rest(props, ["title", "subtitle", "isVisible", "className"]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('SuccessToaster', {
      title,
      subtitle,
      isVisible
    });
  }, [isVisible, subtitle, title]);
  return /*#__PURE__*/createPortal(jsx(AnimatePresence, {
    children: isVisible && jsx(motion.div, {
      initial: initial,
      animate: animate,
      exit: exit,
      role: "dialog",
      "aria-hidden": !isVisible,
      children: jsxs("div", Object.assign({}, debugService.componentProps, {
        className: `${className || ''} kxi-grey kxi-toaster-wrapper kxi-flex-center`
      }, ingredients, {
        ref: ref,
        children: [jsxs("div", {
          className: "kxi-header-wrapper",
          children: [jsx(XIIcon, {
            iconSize: 100,
            icon: "circle_check",
            className: "kxi-success kxi-toaster-icon"
          }), jsx("div", {
            className: "kxi-toaster-title kxi-text-xlarge kxi-flex-center kxi-text-center",
            children: title
          })]
        }), jsx("div", {
          className: "kxi-toaster-footer kxi-flex-center kxi-text-normal kxi-text-center",
          children: subtitle
        })]
      }))
    }, "modal")
  }), document.body);
});
SuccessToaster.displayName = 'SuccessToaster';

class IconButtonProps extends ObservablePropsMixin(ButtonHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], IconButtonProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", String)], IconButtonProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], IconButtonProps.prototype, "fill", void 0);
__decorate([Bound(), __metadata("design:type", String)], IconButtonProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], IconButtonProps.prototype, "hasAnimation", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], IconButtonProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], IconButtonProps.prototype, "hasState", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], IconButtonProps.prototype, "active", void 0);
__decorate([Bound(), __metadata("design:type", String)], IconButtonProps.prototype, "buttonSize", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], IconButtonProps.prototype, "tooltip", void 0);
class IconButtonEvents extends ButtonHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], IconButtonEvents.prototype, "onActiveChange", void 0);
/**
 * Description: Button for when you want to use one of our icons, and make it clickable! Has an on and off state.
 */
const IconButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(IconButton, props);
  const {
      color = 'orca',
      contrast = 'normal',
      hasAnimation = true,
      icon,
      disabled,
      hasState = false,
      active = false,
      buttonSize = 'normal',
      tooltip,
      id,
      type,
      onKeyPress,
      onClick,
      onActiveChange,
      fill = 'text',
      className,
      observer
    } = props,
    ingredients = __rest(props, ["color", "contrast", "hasAnimation", "icon", "disabled", "hasState", "active", "buttonSize", "tooltip", "id", "type", "onKeyPress", "onClick", "onActiveChange", "fill", "className", "observer"]);
  const activeType = checkIfButtonTypeIsValid('IconButton', type);
  const [isActive, setIsActive] = React.useState(false);
  const [buttonHovered, setButtonHovered] = React.useState(false);
  const onActiveChangeHandler = active => {
    if (onActiveChange) {
      onActiveChange({
        isActive: active
      });
    }
    if (observer) {
      observer.active = active;
    }
  };
  React.useEffect(() => {
    if (hasState) {
      setIsActive(active);
      onActiveChangeHandler(active);
    }
  }, [active]);
  React.useEffect(() => {
    if (!icon) {
      devOnlyWarn('The Kitchen - IconButton: You have not passed in an icon to use with this component.');
    }
  }, [icon]);
  const handleClick = e => {
    if (hasState) {
      setIsActive(!isActive);
      onActiveChangeHandler(!isActive);
    }
    if (onClick) {
      onClick(e);
    }
  };
  const handleKeyPress = e => {
    if (e.key === 'Enter' && !disabled) {
      if (hasState) {
        setIsActive(!isActive);
        onActiveChangeHandler(!isActive);
      }
      if (onKeyPress) {
        onKeyPress(e);
      }
    }
  };
  const {
    customColor,
    contrastingLightColor,
    activeColor,
    colorClass
  } = useCustomColor(color, contrast, fill);
  const classList = ['kxi-icon-button', 'kxi-button', 'kxi-circle', `kxi-${buttonSize}`, fill ? `kxi-button-${fill}` : null, {
    'kxi-high-contrast': contrast === 'high',
    'kxi-button-state-active': isActive && hasState
  }, colorClass, className];
  const backgroundColorStyle = () => {
    if (customColor && contrastingLightColor) {
      if (fill === 'fill') {
        return buttonHovered ? contrastingLightColor : customColor;
      } else if (fill === 'outline') {
        return buttonHovered ? contrastingLightColor : undefined;
      } else if (fill === 'text') {
        if (isActive) {
          return buttonHovered ? contrastingLightColor : activeColor;
        } else {
          return buttonHovered ? contrastingLightColor : undefined;
        }
      }
    }
    return undefined;
  };
  const iconButton = jsx(MotionDiv, Object.assign({}, debugService.componentProps, {
    className: className,
    hasAnimation: disabled ? false : hasAnimation,
    children: jsx("button", Object.assign({
      id: id,
      className: classNames(classList),
      disabled: disabled,
      type: activeType ? type : undefined,
      ref: ref,
      onKeyPress: handleKeyPress,
      onClick: handleClick,
      "data-testid": "kxi-icon-button"
    }, ingredients, {
      style: Object.assign(Object.assign({}, customColor && {
        color: customColor,
        background: backgroundColorStyle(),
        border: fill === 'outline' ? `1px solid ${customColor}` : undefined
      }), ingredients.style),
      onMouseOver: () => setButtonHovered(true),
      onMouseLeave: () => setButtonHovered(false),
      children: jsx(XIIcon, {
        icon: icon,
        color: color,
        iconSize: buttonSize === 'large' ? 30 : buttonSize === 'normal' ? 24 : 18,
        contrast: fill === 'fill' ? contrast === 'high' ? 'normal' : 'high' : contrast
      })
    }))
  }));
  const button = tooltip ? jsx(ToolTipWrapper, Object.assign({}, tooltip, {
    style: {
      position: 'relative'
    },
    children: iconButton
  })) : iconButton;
  return button;
});
IconButton.displayName = 'IconButton';

/* eslint react-hooks/exhaustive-deps : error */
function useResizeObserver(element, onResize, options = {}) {
  const {
    box
  } = options;
  const onResizeRef = useRefToValue(onResize);
  React__default.useEffect(() => {
    if (!element) {
      return;
    }
    const observer = new ResizeObserver((entries, observer) => {
      onResizeRef.current(entries, observer);
    });
    observer.observe(element, {
      box
    });
    return () => {
      observer.disconnect();
    };
  }, [box, element, onResizeRef]);
}
function useBorderBoxResizeObserver(props) {
  const {
    element,
    handleHeight = true,
    handleWidth = true
  } = props;
  const [height, setHeightDeferred] = useDeferredState(handleHeight, 0);
  const [width, setWidthDeferred] = useDeferredState(handleWidth, 0);
  useResizeObserver(element, ([entry]) => {
    const {
      blockSize: height,
      inlineSize: width
    } = entry.borderBoxSize[0];
    setHeightDeferred(height);
    setWidthDeferred(width);
  }, {
    box: 'border-box'
  });
  return {
    height,
    width
  };
}

class GenericModalProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "allowScroll", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "allowPropagation", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "isVisualModal", void 0);
__decorate([Bound(), __metadata("design:type", String)], GenericModalProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "fullScreen", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "isVisible", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "isCancelVisible", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "canScrimClose", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "canBackdropClose", void 0);
__decorate([Bound(), __metadata("design:type", String)], GenericModalProps.prototype, "targetElement", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "useLegacy", void 0);
__decorate([Bound(), __metadata("design:type", String)], GenericModalProps.prototype, "maxWidth", void 0);
__decorate([Bound(), __metadata("design:type", String)], GenericModalProps.prototype, "minWidth", void 0);
__decorate([Bound(), __metadata("design:type", String)], GenericModalProps.prototype, "minHeight", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], GenericModalProps.prototype, "secondaryStyle", void 0);
__decorate([Bound(), __metadata("design:type", Object)], GenericModalProps.prototype, "customPadding", void 0);
__decorate([Bound(), __metadata("design:type", String)], GenericModalProps.prototype, "slotName", void 0);
class GenericModalEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], GenericModalEvents.prototype, "onClose", void 0);
/**
 * Description: Modal used for overlaying page and displaying anything.
 * Only has a background on contrast=high for legacy implementations (useLegacy=true).
 */
const GenericModal = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a;
  const debugService = useDebugService(GenericModal, props);
  const {
      allowPropagation = false,
      allowScroll = false,
      canBackdropClose = true,
      className,
      customPadding,
      isCancelVisible: isCancelVisibleInput = true,
      isVisible: isVisibleInput = false,
      maxWidth = '720px',
      minHeight,
      minWidth,
      observer,
      onClick: onClickInput,
      onClose: onCloseInput,
      secondaryStyle = false,
      slotName = 'children',
      style,
      targetElement: targetSelector = 'body'
    } = props,
    dirtyIngredients = __rest(props, ["allowPropagation", "allowScroll", "canBackdropClose", "className", "customPadding", "isCancelVisible", "isVisible", "maxWidth", "minHeight", "minWidth", "observer", "onClick", "onClose", "secondaryStyle", "slotName", "style", "targetElement"]);
  // eslint-disable-next-line prefer-const
  let {
      fullScreen,
      contrast,
      useLegacy,
      canScrimClose,
      isVisualModal
    } = dirtyIngredients,
    ingredients = __rest(dirtyIngredients, ["fullScreen", "contrast", "useLegacy", "canScrimClose", "isVisualModal"]);
  throwIfRequiredPropNotProvided('GenericModal', {
    isVisible: isVisibleInput
  });
  useWarnIsDeprecated('GenericModal', {
    useLegacy: {
      message: 'You are using the deprecated legacy configuration for GenericModal which will be removed in the near future. In order to use the newer version of the component please remove the useLegacy prop.',
      value: useLegacy
    },
    fullScreen: {
      message: useLegacy ? 'This legacy prop will be removed in the near future. To ensure forward compatibility please remove the useLegacy prop. The functionality provided by this prop can be achieved by using the minWidth & minHeight props on the non-legacy version.' : 'This prop is only for legacy implementations and is not doing anything currently. Please remove it. If full screen functionality is needed it can be accomplished using minWidth & minHeight props.',
      value: fullScreen
    },
    contrast: {
      message: useLegacy ? 'This legacy prop will be removed in the near future. To ensure forward compatibility please remove the useLegacy prop. The non-legacy version of the GenericModal has the white background by default and no option to remove it.' : 'This prop is only for legacy implementations and is not doing anything currently. Please remove it. If transparent background is required, please contact the Kitchen Team via the #kitchen-inquiries channel.',
      value: contrast
    },
    canScrimClose: {
      message: useLegacy ? 'This legacy prop will be removed in the near future. To ensure forward compatibility please remove the useLegacy prop. This prop is combined with the canBackdropClose in the non-legacy version.' : 'This prop is only for legacy implementations and is not doing anything currently. Please remove it. This prop is replaced by canBackdropClose.',
      value: canScrimClose
    },
    isVisualModal: {
      message: useLegacy ? 'This legacy prop will be removed in the near future. To ensure forward compatibility please remove the useLegacy prop. This prop is changed to secondaryStyle in the non-legacy version.' : 'This prop is only for legacy implementations and is not doing anything currently. Please remove it. This prop is replaced by secondaryStyle.',
      value: isVisualModal
    }
  }, 'prop');
  // setting these deprecated props in order to evaluate the defaults after the
  // deprecation warning. This ensures that even default values get caught by the
  // deprecation warning.
  fullScreen !== null && fullScreen !== void 0 ? fullScreen : fullScreen = false;
  contrast !== null && contrast !== void 0 ? contrast : contrast = 'normal';
  canScrimClose !== null && canScrimClose !== void 0 ? canScrimClose : canScrimClose = false;
  useLegacy !== null && useLegacy !== void 0 ? useLegacy : useLegacy = false;
  isVisualModal !== null && isVisualModal !== void 0 ? isVisualModal : isVisualModal = false;
  const isMobile = useIsMobile();
  const isCancelVisible = isCancelVisibleInput && !isMobile;
  const targetElement = (_a = document.querySelector(targetSelector)) !== null && _a !== void 0 ? _a : document.body;
  // Ignore return value, we just want to rerender on changes
  useWindowSize();
  const [isVisible, setIsVisible] = React.useState(isVisibleInput);
  const [containerElement, setContainerElement] = React.useState(null);
  const [slotElement, setSlotElement] = React.useState(null);
  const [closeButtonElement, setCloseButtonElement] = React.useState(null);
  const {
    height: closeButtonHeight = 0,
    width: closeButtonWidth = 0
  } = useBorderBoxResizeObserver({
    element: closeButtonElement
  });
  const closeButtonAnchorElement = useLegacy ? slotElement : containerElement;
  const closeButtonAnchorRect = usePollBoundingClientRectState(isVisible && isCancelVisible, closeButtonAnchorElement);
  const targetOverlayRef = React.useRef(null);
  const modalRef = React.useRef(null);
  const slotRef = React.useRef(null);
  const [dialogElement, setDialogElement] = React.useState(null);
  const {
    ref: focusTrapRef
  } = useFocusTrap();
  usePreventScroll(isVisible, targetElement);
  const targetElementRect = usePollBoundingClientRectState(isVisible, targetElement);
  const isClosingRef = React.useRef(false);
  const {
    setTimeout: setCloseTimeout
  } = useTimeout();
  const closeModal = (() => {
    const animateClose = () => {
      if (targetSelector && targetOverlayRef.current) {
        targetOverlayRef.current.style.opacity = '0';
      } else if (modalRef.current) {
        modalRef.current.style.opacity = '0';
      }
      return new Promise(resolve => setCloseTimeout(resolve, 400));
    };
    return async () => {
      if (isClosingRef.current) {
        return;
      }
      isClosingRef.current = true;
      await animateClose();
      setIsVisible(false);
      isClosingRef.current = false;
    };
  })();
  const onClose = async () => {
    if (isClosingRef.current) {
      return;
    }
    await closeModal();
    onCloseInput === null || onCloseInput === void 0 ? void 0 : onCloseInput();
    if (observer) {
      observer.isVisible = false;
    }
  };
  React.useEffect(() => {
    if (isVisibleInput === isVisible) {
      return;
    }
    if (isVisibleInput) {
      setIsVisible(true);
    } else {
      void closeModal();
    }
  }, [isVisibleInput]);
  useFocusFirstTabbableElement(isVisible ? dialogElement : null);
  const closeButtonJsxElement = isCancelVisible && !isMobile && closeButtonAnchorRect ? jsx(CloseButton, {
    ref: setCloseButtonElement,
    contrast: contrast,
    isVisualModal: isVisualModal,
    left: closeButtonAnchorRect.left - (targetElement !== document.body && targetElementRect ? targetElementRect.left : 0) - closeButtonWidth,
    onClose: () => void onClose(),
    top: closeButtonAnchorRect.top - (targetElement !== document.body && targetElementRect ? targetElementRect.top : 0) - closeButtonHeight,
    useLegacy: useLegacy
  }) : null;
  const modal = jsxs("div", Object.assign({}, debugService.componentProps, {
    role: "dialog",
    "aria-hidden": isVisible === false,
    className: classNames('kxi-grey-overlay kxi-generic-modal', !useLegacy && {
      'kxi-flexible-modal': true,
      'kxi-secondary-modal': secondaryStyle
    }),
    style: {
      display: isVisible ? undefined : 'none'
    },
    onClick: () => {
      if (canBackdropClose) {
        void onClose();
      }
    },
    onKeyDown: e => {
      if (e.key === 'Escape') {
        void onClose();
      }
    },
    ref: mergeRefs([setDialogElement, focusTrapRef]),
    "data-testid": "backdrop",
    tabIndex: -1,
    children: [!useLegacy && closeButtonJsxElement, jsxs("div", Object.assign({
      "data-testid": "modal-container"
    }, ingredients, {
      className: classNames(className, 'kxi-modal-container', allowScroll && 'kxi-generic-modal-scrollable', useLegacy && {
        'kxi-generic-modal-fullscreen': fullScreen,
        'kxi-high-contrast': contrast === 'high'
      }),
      style: Object.assign(Object.assign({}, style), !useLegacy && {
        maxWidth,
        minWidth,
        minHeight,
        paddingTop: customPadding === null || customPadding === void 0 ? void 0 : customPadding.top,
        paddingRight: customPadding === null || customPadding === void 0 ? void 0 : customPadding.right,
        paddingBottom: customPadding === null || customPadding === void 0 ? void 0 : customPadding.bottom,
        paddingLeft: customPadding === null || customPadding === void 0 ? void 0 : customPadding.left
      }),
      onClick: e => {
        onClickInput === null || onClickInput === void 0 ? void 0 : onClickInput(e);
        if (!allowPropagation) {
          e.stopPropagation();
        }
        if (useLegacy && canScrimClose) {
          void onClose();
        }
      },
      ref: mergeRefs([ref, setContainerElement]),
      children: [useLegacy && closeButtonJsxElement, jsx(Slot, {
        "data-testid": "slot",
        name: slotName,
        ref: mergeRefs([slotRef, setSlotElement]),
        reactChildren: props.children
      })]
    }))]
  }));
  return /*#__PURE__*/createPortal(jsx("div", {
    className: "kxi-generic-modal-partial-portal",
    ref: targetOverlayRef,
    style: Object.assign(Object.assign({}, isVisible && targetElement === document.body ? {
      top: window.scrollY,
      bottom: window.scrollY + window.innerHeight,
      left: window.scrollX,
      right: window.scrollX + window.innerWidth,
      height: isVisible ? window.innerHeight : 0,
      width: isVisible ? window.innerWidth : 0
    } : targetElementRect && {
      top: window.scrollY + targetElementRect.top,
      bottom: window.scrollY + targetElementRect.bottom,
      left: window.scrollX + targetElementRect.left,
      right: window.scrollX + targetElementRect.right,
      height: isVisible ? targetElementRect.height : 0,
      width: isVisible ? targetElementRect.width : 0
    }), {
      opacity: isVisible ? 1 : 0,
      visibility: isVisible ? 'initial' : 'hidden'
    }),
    children: modal
  }), document.body);
});
GenericModal.displayName = 'GenericModal';
const CloseButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    contrast,
    isVisualModal,
    left,
    onClose,
    top,
    useLegacy
  } = props;
  return jsx("div", {
    children: !useLegacy || isVisualModal ? jsx("div", {
      ref: ref,
      role: "button",
      className: "kxi-grey kxi-generic-modal-x-visual",
      onClick: onClose,
      onKeyDown: e => {
        if (isActivation(e)) {
          if (e.key === ' ') {
            e.preventDefault();
          }
          onClose();
        }
      },
      tabIndex: 0,
      style: !useLegacy || !isVisualModal ? {
        left,
        top
      } : undefined,
      children: jsx(XIIcon, {
        icon: "cancel"
      })
    }) : jsx(IconButton, {
      ref: ref,
      buttonSize: "normal",
      onClick: onClose,
      icon: "cancel",
      contrast: "normal",
      hasAnimation: false,
      "data-testid": "close-icon",
      className: `${contrast === 'high' ? 'kxi-generic-modal-contrast-high-button' : 'kxi-generic-modal-x kxi-high-contrast'} `
    })
  });
});

class WarningPopupProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Boolean)], WarningPopupProps.prototype, "isVisible", void 0);
__decorate([Bound(), __metadata("design:type", Object)], WarningPopupProps.prototype, "buttons", void 0);
__decorate([Bound(), __metadata("design:type", String)], WarningPopupProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], WarningPopupProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], WarningPopupProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", GenericModalProps)], WarningPopupProps.prototype, "genericModalProps", void 0);
class WarningPopupEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], WarningPopupEvents.prototype, "onProceed", void 0);
__decorate([Bound(), __metadata("design:type", Function)], WarningPopupEvents.prototype, "onCancel", void 0);
__decorate([Bound(), __metadata("design:type", GenericModalEvents)], WarningPopupEvents.prototype, "genericModalEvents", void 0);
/**
 * Description: Modal when you want to warn the user of something. Can be used as an 'Are you sure?' reminder.
 */
const WarningPopup = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(WarningPopup, props);
  const {
      title = 'Warning',
      className,
      style,
      subtitle,
      buttons,
      onProceed,
      onCancel,
      isVisible,
      stretch = false,
      genericModalProps,
      observer
    } = props,
    ingredients = __rest(props, ["title", "className", "style", "subtitle", "buttons", "onProceed", "onCancel", "isVisible", "stretch", "genericModalProps", "observer"]);
  const handleProceed = e => {
    if (buttons.proceed.onClick) {
      buttons.proceed.onClick(e);
    }
    if (onProceed) {
      onProceed({
        mouseEvent: e
      });
    }
  };
  const handleCancel = e => {
    if (buttons.cancel.onClick) {
      buttons.cancel.onClick(e);
    }
    if (onCancel) {
      onCancel({
        mouseEvent: e
      });
    }
  };
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('WarningPopUp', {
      isVisible,
      buttons
    });
  }, [isVisible, buttons]);
  const styledTextStyle = {
    maxHeight: stretch ? '220px' : '140px'
  };
  return jsx(GenericModal, Object.assign({
    maxWidth: stretch ? 'unset' : undefined,
    isVisible: isVisible
  }, genericModalProps, debugService === null || debugService === void 0 ? void 0 : debugService.componentProps, {
    observer: observer,
    children: jsxs("div", Object.assign({
      className: `${className || ''} kxi-warning-popup-wrapper`,
      ref: ref,
      "data-testid": "warning-popup"
    }, ingredients, {
      style: style,
      children: [jsx("div", {
        className: "kxi-warning-popup-styled-header",
        children: jsx("div", {
          className: "kxi-text-xlarge kxi-text-600",
          children: title
        })
      }), jsx("div", {
        className: "kxi-warning-popup-styled-text",
        style: styledTextStyle,
        children: jsx("div", {
          className: "kxi-text-normal",
          dangerouslySetInnerHTML: {
            __html: sanitizeHtmlString(subtitle)
          }
        })
      }), jsxs("div", {
        className: "kxi-warning-popup-button-container",
        children: [buttons.cancel.text && jsx("div", {
          className: "kxi-warning-popup-proceed-cancel-wrapper",
          children: jsx(DefaultButton, {
            color: "orca",
            fill: "text",
            text: buttons.cancel.text,
            onClick: handleCancel,
            buttonSize: "normal"
          })
        }), buttons.proceed.text && jsx("div", {
          className: "kxi-warning-popup-proceed-button-wrapper",
          children: jsx(DefaultButton, {
            color: "orca",
            fill: "fill",
            text: buttons.proceed.text,
            onClick: handleProceed,
            buttonSize: "normal"
          })
        })]
      })]
    }))
  }));
});
WarningPopup.displayName = 'WarningPopup';

// alert inline main
const _SnackBarModesOptions = ['error', 'default'];
const _AlertsColorsOptions = [..._ColorsOptions, ..._ErrorStatesOptions];

class InputHTMLAttributes extends HTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "accept", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "alt", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "autoComplete", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputHTMLAttributes.prototype, "autoFocus", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputHTMLAttributes.prototype, "capture", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputHTMLAttributes.prototype, "checked", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "crossOrigin", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputHTMLAttributes.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "form", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "formAction", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "formEncType", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "formMethod", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputHTMLAttributes.prototype, "formNoValidate", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "formTarget", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputHTMLAttributes.prototype, "height", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "list", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputHTMLAttributes.prototype, "max", void 0);
__decorate([Bound(), __metadata("design:type", Number)], InputHTMLAttributes.prototype, "maxLength", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputHTMLAttributes.prototype, "min", void 0);
__decorate([Bound(), __metadata("design:type", Number)], InputHTMLAttributes.prototype, "minLength", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputHTMLAttributes.prototype, "multiple", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "name", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "pattern", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "placeholder", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputHTMLAttributes.prototype, "readOnly", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputHTMLAttributes.prototype, "required", void 0);
__decorate([Bound(), __metadata("design:type", Number)], InputHTMLAttributes.prototype, "size", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "src", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputHTMLAttributes.prototype, "step", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputHTMLAttributes.prototype, "type", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputHTMLAttributes.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputHTMLAttributes.prototype, "width", void 0);
class InputHTMLEvents extends DOMEvents {}
__decorate([Bound(), __metadata("design:type", Function)], InputHTMLEvents.prototype, "onChange", void 0);

class CheckboxProps extends ObservablePropsMixin(InputHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Boolean)], CheckboxProps.prototype, "checked", void 0);
__decorate([Bound(), __metadata("design:type", String)], CheckboxProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], CheckboxProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], CheckboxProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", String)], CheckboxProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CheckboxProps.prototype, "isLabelLeft", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CheckboxProps.prototype, "isIndeterminate", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CheckboxProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", String)], CheckboxProps.prototype, "inputId", void 0);
class CheckboxEvents extends InputHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], CheckboxEvents.prototype, "onCheckedChange", void 0);
let mouseDown = false;
/**
 * Description: Your friendly standard InMoment neighborhood Checkbox. Does not support the 'text' attribute in status.
 */
const Checkbox = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(Checkbox, props);
  const inputEl = React.useRef(null);
  const styledCheckboxRef = React.useRef(null);
  const {
      disabled,
      tooltip,
      status,
      color = 'orca',
      _externallyControlledState: externallyControlledState = false,
      className,
      contrast = 'normal',
      label,
      isLabelLeft = false,
      checked = false,
      isIndeterminate,
      id,
      inputId,
      onChange,
      onCheckedChange: onCheckedChangeInput,
      tabIndex,
      internalProps,
      type,
      observer
    } = props,
    ingredients = __rest(props, ["disabled", "tooltip", "status", "color", "_externallyControlledState", "className", "contrast", "label", "isLabelLeft", "checked", "isIndeterminate", "id", "inputId", "onChange", "onCheckedChange", "tabIndex", "internalProps", "type", "observer"]);
  const {
    wrapper,
    outerWrapper
  } = internalProps !== null && internalProps !== void 0 ? internalProps : {};
  const _a = wrapper !== null && wrapper !== void 0 ? wrapper : {},
    {
      ref: divRef
    } = _a,
    divIngredients = __rest(_a, ["ref"]);
  if (type) {
    devOnlyWarn(`The Kitchen: You have passed in a type "${type}" that is not expected for Checkbox, this will have no effect as the type is permanently set to checkbox.`);
  }
  const finalInputId = React.useMemo(() => inputId || `${id || uuidv4()}-input`, [inputId, id]); // Use || not ?? to handle empty strings correctly
  const [internalChecked, setInternalChecked] = useValueBackedState(checked);
  const [labelMouseDown, setLabelMouseDown] = React.useState(false);
  const [labelMouseOver, setLabelMouseOver] = React.useState(false);
  // this is used to disable the focus state on click (we want active not focus)
  React.useEffect(() => {
    const forceMouseUp = () => {
      setLabelMouseDown(false);
    };
    if (styledCheckboxRef && styledCheckboxRef.current) {
      styledCheckboxRef.current.addEventListener('focus', () => {
        if (mouseDown && styledCheckboxRef && styledCheckboxRef.current) {
          styledCheckboxRef.current.blur();
        }
      });
    }
    // Accessibe Compatibility
    if (inputEl.current) {
      inputEl.current.setAttribute('style', 'display: none !important; appearance: none !important;');
    }
    document.addEventListener('mouseup', forceMouseUp);
    return () => {
      document.removeEventListener('mouseup', forceMouseUp);
    };
  }, []);
  const onCheckedChange = (e, isChecked) => {
    if (!externallyControlledState) {
      setInternalChecked(isChecked);
    }
    if (observer) {
      observer.checked = isChecked;
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
    onCheckedChangeInput === null || onCheckedChangeInput === void 0 ? void 0 : onCheckedChangeInput({
      isChecked
    });
  };
  const marginSide = isLabelLeft ? 'marginRight' : 'marginLeft';
  const textStyle = {
    color: contrast === 'high' ? 'white' : '',
    [marginSide]: 12
  };
  const errorState = typeof status === 'object' ? status === null || status === void 0 ? void 0 : status.state : status;
  const contrastClass = contrast === 'high' ? ' kxi-high-contrast' : '';
  const errorStateClass = errorState ? ` kxi-${errorState}` : '';
  const checkboxState = !internalChecked ? 'unchecked' : isIndeterminate ? 'indeterminate' : 'checked';
  const checkbox = jsxs("div", Object.assign({}, debugService.componentProps, outerWrapper, {
    className: classNames(outerWrapper === null || outerWrapper === void 0 ? void 0 : outerWrapper.className, 'kxi-checkbox-wrapper'),
    children: [jsxs("label", {
      id: id,
      htmlFor: finalInputId,
      className: `kxi-checkbox ${disabled ? 'kxi-checkbox-disabled' : ''} ${typeof status === 'object' && (status === null || status === void 0 ? void 0 : status.state) ? `kxi-border` : ''} ${errorStateClass} ${contrastClass}`,
      onMouseDown: () => setLabelMouseDown(true),
      onMouseUp: () => setLabelMouseDown(false),
      onMouseOver: () => setLabelMouseOver(true),
      onMouseLeave: () => setLabelMouseOver(false),
      children: [jsx("input", Object.assign({
        className: "kxi-hidden-checkbox",
        ref: mergeRefs([inputEl, ref]),
        type: "checkbox",
        id: finalInputId,
        onChange: e => onCheckedChange(e, e.target.checked),
        checked: internalChecked,
        disabled: disabled || false,
        role: "none"
      }, ingredients)), jsxs("div", Object.assign({
        className: classNames(`kxi-checkbox-styled-row${isLabelLeft ? '-reverse' : ''}`, errorStateClass)
      }, divIngredients, {
        ref: divRef,
        children: [jsx("div", {
          className: "kxi-checkbox-focusable",
          onKeyPress: e => {
            if (inputEl.current) {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!disabled) {
                  onCheckedChange(e, !inputEl.current.checked);
                }
              }
            }
          },
          onMouseDown: () => mouseDown = true,
          onMouseUp: () => mouseDown = false,
          tabIndex: tabIndex || 0,
          "data-checked-state": checkboxState,
          role: "checkbox",
          "aria-checked": internalChecked,
          children: jsx("div", {
            ref: styledCheckboxRef,
            className: classNames('kxi-border', 'kxi-checkbox-styled', {
              'kxi-border-active': !disabled && labelMouseDown,
              'kxi-border-hovered': !disabled && labelMouseOver,
              'kxi-checkbox-disabled': disabled,
              'kxi-checkbox-checked': internalChecked,
              [errorStateClass]: internalChecked || typeof status !== 'object'
            }, className, contrastClass, color && normalizeColor(color)),
            children: internalChecked && jsx(XIIcon, {
              icon: isIndeterminate ? 'indeterminate' : 'checkmark',
              "data-qa": isIndeterminate ? 'indeterminate' : 'checkmark',
              color: status ? typeof status === 'string' ? status : status.state : color,
              contrast: contrast === 'high' ? 'normal' : 'high',
              iconSize: 18
            })
          })
        }), label && jsx("p", {
          style: textStyle,
          children: label
        })]
      }))]
    }), status && typeof status === 'object' ? jsxs("div", {
      className: `kxi-status-container ${contrastClass}`,
      children: [status.state === 'error' && status.text && jsx(XIIcon, {
        iconSize: "normal",
        icon: "warning_filled",
        color: status === null || status === void 0 ? void 0 : status.state
      }), jsx("div", {
        className: `kxi-status-label kxi-left ${errorStateClass}`,
        children: status.text
      })]
    }) : null]
  }));
  const component = tooltip ? jsx(ToolTipWrapper, Object.assign({}, tooltip, {
    style: {
      position: 'relative'
    },
    children: checkbox
  })) : checkbox;
  return component;
});
Checkbox.displayName = 'Checkbox';

class FABProps extends ButtonHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], FABProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", String)], FABProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], FABProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], FABProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", String)], FABProps.prototype, "buttonSize", void 0);
class FABEvents extends ButtonHTMLEvents {}
/**
 * Description: Floating Action Button - used to put over the flow of the document.
 */
const FAB = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(FAB, props);
  const {
      icon,
      color = 'CX',
      contrast = 'normal',
      buttonSize = 'xxlarge',
      type,
      disabled,
      tooltip,
      className,
      style
    } = props,
    ingredients = __rest(props, ["icon", "color", "contrast", "buttonSize", "type", "disabled", "tooltip", "className", "style"]);
  const validType = checkIfButtonTypeIsValid('FAB', type);
  React.useEffect(() => {
    if (!icon) devOnlyWarn('The Kitchen - FAB: You have not passed in an icon to use with this component.');
  }, [icon]);
  const getClassList = () => {
    let classList = `kxi-fab kxi-button kxi-${buttonSize} kxi-shadows-deep kxi-button-fill`;
    classList += contrast === 'high' ? ' kxi-high-contrast' : '';
    return classList;
  };
  const getIconSize = () => {
    switch (buttonSize) {
      case 'normal':
        return 24;
      case 'xlarge':
        return 27;
      case 'xxlarge':
        return 30;
    }
  };
  const component = jsx("button", Object.assign({}, debugService.componentProps, {
    className: `${color ? normalizeColor(color) : ''} ${getClassList()} ${className || ''}`,
    style: Object.assign({}, style),
    type: validType ? type : undefined
  }, ingredients, {
    ref: ref,
    disabled: disabled,
    children: jsx(XIIcon, {
      iconSize: getIconSize(),
      icon: icon
    })
  }));
  if (tooltip) {
    return jsx(ToolTipWrapper, Object.assign({}, tooltip, {
      children: component
    }));
  }
  return component;
});
FAB.displayName = 'FAB';

class FilterButtonProps extends ButtonHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Boolean)], FilterButtonProps.prototype, "isActive", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], FilterButtonProps.prototype, "isFocused", void 0);
__decorate([Bound(), __metadata("design:type", String)], FilterButtonProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", String)], FilterButtonProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Object)], FilterButtonProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", String)], FilterButtonProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], FilterButtonProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", String)], FilterButtonProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], FilterButtonProps.prototype, "hasRemoveIcon", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], FilterButtonProps.prototype, "isRemoveIconInline", void 0);
__decorate([Bound(), __metadata("design:type", String)], FilterButtonProps.prototype, "wrap", void 0);
class FilterButtonEvents extends ButtonHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], FilterButtonEvents.prototype, "onCancelClick", void 0);
/**
 * Description: Used for the filter row, has options to indicate the status of a filter.
 */
const FilterButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(FilterButton, props);
  const {
      contrast = 'normal',
      label = '',
      hasRemoveIcon = false,
      isRemoveIconInline = false,
      text,
      disabled = false,
      isActive,
      isFocused,
      tabIndex,
      type,
      wrap = 'nowrap',
      className,
      style,
      color = 'orca',
      status,
      onCancelClick
    } = props,
    ingredients = __rest(props, ["contrast", "label", "hasRemoveIcon", "isRemoveIconInline", "text", "disabled", "isActive", "isFocused", "tabIndex", "type", "wrap", "className", "style", "color", "status", "onCancelClick"]);
  const active = checkIfButtonTypeIsValid('FilterButton', type);
  const classList = classNames(className, 'kxi-button kxi-filter-button', {
    'kxi-high-contrast': contrast === 'high',
    'kxi-filter-active': isActive,
    'kxi-button-outline': !isActive && !isFocused,
    [`kxi-${status}`]: status,
    'kxi-filter-focused': isFocused,
    'kxi-filter-remove-inline': isRemoveIconInline && hasRemoveIcon
  });
  const generateCloseIcon = () => {
    const invertedContrast = contrast === 'high' ? 'normal' : 'high';
    return jsx(CloseIcon, {
      disabled: disabled,
      contrast: contrast,
      focusContrast: isFocused ? invertedContrast : contrast,
      onCancel: onCancelClick
    });
  };
  const FilterButtonItem = (filterLabel, close) => {
    if (filterLabel.length > 0 || close) {
      return jsxs("div", {
        className: "kxi-filter-label-assembly",
        children: [filterLabel.length > 0 && jsx("div", {
          className: "kxi-label",
          children: filterLabel
        }), hasRemoveIcon && !isRemoveIconInline && generateCloseIcon()]
      });
    }
    return null;
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: `${normalizeColor(color)} ${contrast === 'high' ? 'kxi-high-contrast' : ''}`,
    children: [FilterButtonItem(label, hasRemoveIcon), jsx(MotionDiv, {
      error: false,
      hasAnimation: !disabled,
      children: jsxs("button", Object.assign({
        disabled: disabled,
        ref: ref,
        tabIndex: tabIndex || 0,
        type: active ? type : undefined,
        className: classList,
        style: Object.assign({
          whiteSpace: wrap
        }, style)
      }, ingredients, {
        children: [hasRemoveIcon && isRemoveIconInline && jsx("div", {
          className: "kxi-filter-remove-inline",
          children: generateCloseIcon()
        }), text]
      }))
    })]
  }));
});
FilterButton.displayName = 'FilterButton';

class HeadingButtonProps extends ButtonHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], HeadingButtonProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], HeadingButtonProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HeadingButtonProps.prototype, "dangerouslySetInnerHTML", void 0);
__decorate([Bound(), __metadata("design:type", String)], HeadingButtonProps.prototype, "contrast", void 0);
class HeadingButtonEvents extends ButtonHTMLEvents {}
/**
 * Description: A clickable button with the only visual being text, commonly used in Headings.
 */
const HeadingButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(HeadingButton, props);
  const {
      title,
      color = 'orca',
      contrast = 'normal',
      type,
      disabled,
      className,
      dangerouslySetInnerHTML
    } = props,
    ingredients = __rest(props, ["title", "color", "contrast", "type", "disabled", "className", "dangerouslySetInnerHTML"]);
  const active = checkIfButtonTypeIsValid('HeadingButton', type);
  const getClassList = () => {
    let classList = `kxi-button ${normalizeColor(color)}`;
    classList += contrast === 'high' ? ' kxi-high-contrast' : '';
    return classList;
  };
  const dangerousString = dangerouslySetInnerHTML ? {
    __html: sanitizeHtmlString(dangerouslySetInnerHTML.__html)
  } : undefined;
  return jsx("button", Object.assign({}, debugService.componentProps, {
    className: `${getClassList()} ${className || ''}`,
    type: active ? type : undefined,
    ref: ref,
    disabled: disabled,
    "aria-disabled": disabled,
    dangerouslySetInnerHTML: dangerousString
  }, ingredients, {
    children: dangerouslySetInnerHTML ? null : title
  }));
});
HeadingButton.displayName = 'HeadingButton';

class XISVGProps extends ImgHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], XISVGProps.prototype, "svg", void 0);
class XISVGEvents extends ImgHTMLEvents {}
/**
 * Description: Component that displays an svg.
 */
const XISVG = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(XISVG, props);
  const {
      svg,
      className
    } = props,
    ingredients = __rest(props, ["svg", "className"]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('XISVG', {
      svg
    });
  }, [svg]);
  return jsx("img", Object.assign({}, debugService.componentProps, {
    className: `kxi-stretch ${className || ''}`,
    "data-testid": `${svg}_SVG`,
    src: `${kitchenRootPath()}/svgs/${svg}.svg`
  }, ingredients, {
    alt: svg,
    ref: ref
  }));
});
XISVG.displayName = 'XISVG';

class SentimentButtonProps extends ObservablePropsMixin(ButtonHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], SentimentButtonProps.prototype, "sentiment", void 0);
__decorate([Bound(), __metadata("design:type", String)], SentimentButtonProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SentimentButtonProps.prototype, "isFocused", void 0);
__decorate([Bound(), __metadata("design:type", String)], SentimentButtonProps.prototype, "wrap", void 0);
class SentimentButtonEvents extends ButtonHTMLEvents {}
/**
 * Description: Button for displaying our various sentiments. Has a focused and non-focused state.
 */
const SentimentButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(SentimentButton, props);
  const [focused, setFocused] = React.useState(false);
  const {
      className,
      observer,
      sentiment = 'neutral',
      isFocused,
      onClick,
      text,
      wrap = 'nowrap'
    } = props,
    ingredients = __rest(props, ["className", "observer", "sentiment", "isFocused", "onClick", "text", "wrap"]);
  React.useEffect(() => {
    if (isFocused !== undefined) {
      setFocused(isFocused);
    }
  }, [isFocused]);
  const handleClick = e => {
    setFocused(v => !v);
    if (observer) {
      observer.isFocused = !focused;
    }
    onClick === null || onClick === void 0 ? void 0 : onClick(e);
  };
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('SentimentButton', {
      sentiment
    });
  }, [sentiment]);
  return jsx("div", Object.assign({}, debugService.componentProps, {
    children: jsxs("button", Object.assign({
      className: `${className || ''} kxi-button kxi-sentiment-button kxi-text-wrap-${wrap} ${normalizeColor(sentiment)} ${focused ? `kxi-high-contrast` : ''} `,
      onClick: handleClick,
      ref: ref,
      "data-testid": "0"
    }, ingredients, {
      children: [jsx("div", {
        className: "kxi-sentiment-button-svg-wrapper",
        children: jsx(XISVG, {
          svg: `${sentiment.toLowerCase()}_sentiment_outlined`
        })
      }), text || capitalizeFirstLetter(sentiment)]
    }))
  }));
});
SentimentButton.displayName = 'SentimentButton';

class DropdownObject {
  constructor(props) {
    Object.assign(this, props);
  }
}
__decorate([type($string()), __metadata("design:type", String)], DropdownObject.prototype, "title", void 0);
__decorate([type($optional($string())), __metadata("design:type", String)], DropdownObject.prototype, "subtitle", void 0);
__decorate([type($string()), __metadata("design:type", String)], DropdownObject.prototype, "id", void 0);
__decorate([type($optional($in([..._IconsOptions, ..._XICloudsOptions]))), __metadata("design:type", String)], DropdownObject.prototype, "visual", void 0);
__decorate([type($optional($boolean())), __metadata("design:type", Boolean)], DropdownObject.prototype, "isSelected", void 0);
__decorate([type($optional($boolean())), __metadata("design:type", Boolean)], DropdownObject.prototype, "disabled", void 0);
__decorate([type($optional($in([..._ErrorStatesOptions, ..._CloudsOptions, 'orca']))), __metadata("design:type", String)], DropdownObject.prototype, "color", void 0);
__decorate([type($optional($array($implements(DropdownObject)))), __metadata("design:type", Array)], DropdownObject.prototype, "submenu", void 0);
__decorate([type($optional($implements(ToolTipProps))), __metadata("design:type", ToolTipProps)], DropdownObject.prototype, "tooltip", void 0);
__decorate([type($optional($function())), __metadata("design:type", Function)], DropdownObject.prototype, "action", void 0);
class DropdownGroup {}
__decorate([type($string()), __metadata("design:type", String)], DropdownGroup.prototype, "title", void 0);
__decorate([type($array($string())), __metadata("design:type", Array)], DropdownGroup.prototype, "ids", void 0);
const _DropdownButtonTypesOptions = ['pill', 'layered', 'fab', 'icon', 'button'];
const _DropdownSelectsOptions = ['none', 'single', 'multi', 'multiSelectAll'];
const _DropdownVisualStateOptions = ['normal', 'condensed'];
var SelectAllState;
(function (SelectAllState) {
  SelectAllState[SelectAllState["SELECTED"] = 0] = "SELECTED";
  SelectAllState[SelectAllState["UNSPECIFIED"] = 1] = "UNSPECIFIED";
  SelectAllState[SelectAllState["UNSELECTED"] = 2] = "UNSELECTED";
})(SelectAllState || (SelectAllState = {}));
class InternalDropdownProps {}
__decorate([type($optional($unknown())), __metadata("design:type", Object)], InternalDropdownProps.prototype, "wrapper", void 0);
__decorate([type($optional($unknown())), __metadata("design:type", Object)], InternalDropdownProps.prototype, "menu", void 0);
__decorate([type($optional($unknown())), __metadata("design:type", Object)], InternalDropdownProps.prototype, "iconButton", void 0);
__decorate([type($optional($unknown())), __metadata("design:type", Object)], InternalDropdownProps.prototype, "fab", void 0);
__decorate([type($optional($unknown())), __metadata("design:type", Object)], InternalDropdownProps.prototype, "defaultButton", void 0);
class DropdownButtons {}
__decorate([type($optional($implements(ButtonObject))), __metadata("design:type", ButtonObject)], DropdownButtons.prototype, "primary", void 0);
__decorate([type($optional($implements(ButtonObject))), __metadata("design:type", ButtonObject)], DropdownButtons.prototype, "secondary", void 0);
class DropdownTextObject {}
__decorate([type($optional($string())), __metadata("design:type", String)], DropdownTextObject.prototype, "noResultsFound", void 0);
__decorate([type($optional($string())), __metadata("design:type", String)], DropdownTextObject.prototype, "selectAll", void 0);
__decorate([type($optional($string())), __metadata("design:type", String)], DropdownTextObject.prototype, "selected", void 0);
__decorate([type($optional($string())), __metadata("design:type", String)], DropdownTextObject.prototype, "more", void 0);
__decorate([type($optional($string())), __metadata("design:type", String)], DropdownTextObject.prototype, "search", void 0);

const useTextFieldStatus = (text, component, status, maxCharsAllowed) => {
  const [errorState, setErrorState] = React.useState(status);
  const errorStateClass = (errorState === null || errorState === void 0 ? void 0 : errorState.state) ? ` kxi-${errorState.state}` : ' kxi-no-error';
  React.useEffect(() => {
    if (maxCharsAllowed) {
      if (Number.isInteger(+maxCharsAllowed.max)) {
        if (text.length > maxCharsAllowed.max) {
          setErrorState({
            state: 'error',
            text: maxCharsAllowed.errorMessage
          });
        } else {
          setErrorState(status);
        }
      } else {
        devOnlyWarn(`The Kitchen: You have provided an object to maxCharsAllowed (${component}), but an incorrect / undefined max property. Please provide a correct max property.`);
      }
    } else {
      setErrorState(status);
    }
  }, [text, maxCharsAllowed, status, component]);
  return [errorState, setErrorState, errorStateClass];
};
const useTextFieldWarning = (component, _controlledState, onChange) => {
  React.useEffect(() => {
    if (!onChange && _controlledState) {
      devOnlyWarn(`The Kitchen: You've chosen to set _controlledState to true, and have not provided a callback function to update the text. You won't be able to type in the ${component} until you provide one.`);
    }
  }, [_controlledState, component, onChange]);
};
const useMaskedInput = (mask, observer) => {
  const [maskedValue, setMaskedValue] = React.useState('');
  const maskRef = React.useRef(null);
  React.useEffect(() => {
    if (observer && mask) {
      observer.value = maskedValue;
    }
  }, [observer, maskedValue]);
  React.useEffect(() => {
    const remainingChars = mask.substr(maskedValue.length, mask.length).split('');
    let hasOnlySymbolsLeft = true;
    if (!remainingChars.length) {
      hasOnlySymbolsLeft = false;
    }
    remainingChars.forEach(item => {
      if (switchMaskCharType(item) !== 'symbol') {
        hasOnlySymbolsLeft = false;
      }
    });
    if (hasOnlySymbolsLeft) {
      setMaskedValue(maskedValue + remainingChars.join(''));
    }
  }, [maskedValue]);
  const findNewCursorIndex = (value, selectionEnd, toGo, isDeleted) => {
    let hasGone = 0;
    // due to selectionEnd being the value AFTER something is typed, need to account for extra shift based on deletion or addition of string
    const shift = isDeleted ? 0 : 1;
    for (let i = selectionEnd; i < value.length; i++) {
      if (hasGone < toGo) {
        if (determineCharType(value[i]) !== 'symbol') {
          hasGone++;
        }
      } else {
        return i - shift;
      }
    }
    return -1;
  };
  const getMinCursor = mask => {
    for (let i = 0; i < mask.length; i++) {
      if (switchMaskCharType(mask[i]) !== 'symbol') {
        return i + 1;
      }
    }
    return -1;
  };
  const buildValue = value => {
    const typedCharIndex = value.length - 1;
    const currVal = value[typedCharIndex];
    const currMask = mask[typedCharIndex];
    if (currVal && currMask) {
      value = value.substring(0, typedCharIndex);
      const maskType = switchMaskCharType(currMask);
      const valType = determineCharType(currVal);
      const isTypeSame = maskType === valType;
      const valIsAlphaNumeric = valType === 'alpha' || valType === 'numeric';
      const isAlphaNumeric = maskType === 'alphanumeric' && valIsAlphaNumeric;
      if (isTypeSame || isAlphaNumeric) {
        if (maskType === 'symbol' && valType === 'symbol') {
          if (currVal === currMask) {
            return value += currVal;
          } else {
            return value;
          }
        }
        return value += currVal;
      } else {
        if (maskType === 'symbol') {
          value = value + currMask + currVal;
          value = buildValue(value);
        }
        return value;
      }
    } else {
      return '';
    }
  };
  const simulateTyping = value => {
    let letters = value.split('');
    // masked value is currently as previous value
    const diff = maskedValue.length - value.length;
    const isDeleted = diff > 0;
    // on deletion
    if (isDeleted) {
      const lastMaskedChar = lastChar(maskedValue);
      if (determineCharType(lastMaskedChar) === 'symbol' && diff === 1) {
        letters.pop();
      }
      letters = letters.filter(letter => {
        return determineCharType(letter) === 'alpha' || determineCharType(letter) === 'numeric';
      });
    }
    let newValue = '';
    letters.forEach(letter => {
      newValue += letter;
      newValue = buildValue(newValue);
    });
    return [newValue, isDeleted];
  };
  const handleChange = (e, onChange, onInputEnter) => {
    const {
      value,
      selectionEnd
    } = e.target;
    if (value.length <= mask.length) {
      const [newValue, isDeleted] = simulateTyping(value);
      const newNonSymbolValueCount = countChars(newValue);
      const newNonSymbolMaskedCount = countChars(maskedValue);
      const nonSymbolDiff = newNonSymbolValueCount - newNonSymbolMaskedCount;
      setMaskedValue(newValue);
      // here to ensure it runs after everything (callbacks as well as the selection)
      setTimeout(() => {
        var _a;
        let newCursor = findNewCursorIndex(newValue, selectionEnd, nonSymbolDiff, isDeleted);
        if (newCursor !== -1) {
          const minCursor = getMinCursor(mask);
          if (minCursor > newCursor && newNonSymbolValueCount === 1) {
            newCursor = minCursor;
          }
          (_a = maskRef.current) === null || _a === void 0 ? void 0 : _a.setSelectionRange(newCursor, newCursor);
        }
        if (onChange) onChange(e);
        if (onInputEnter) onInputEnter({
          text: newValue
        });
      }, 0);
    }
  };
  return {
    handleChange,
    maskedValue,
    setMaskedValue,
    simulateTyping,
    maskRef
  };
};

const defaultLocalization = {
  noResultsFound: 'No Results Found',
  selected: 'Selected',
  selectAll: 'Select All',
  more: 'more',
  search: 'Search'
};
const checkIfMultiple = select => select === 'multi' || select === 'multiSelectAll';
function isSelectable(dropdownObject) {
  const {
    disabled,
    submenu,
    action
  } = dropdownObject;
  return disabled !== true && submenu === undefined && action === undefined;
}
function forEachDropdownObject(data, fn) {
  const stack = [...data];
  while (stack.length > 0) {
    const curr = stack.shift();
    fn(curr);
    if (curr.submenu) {
      stack.unshift(...curr.submenu);
    }
  }
}
function groupData(data, groups, debugService) {
  if (data.length === 0 || groups.length === 0) {
    return data;
  }
  const idGroupMap = new Map();
  // Insert tuples manually so that we can throw if there is a duplicate
  const idGroupTuples = groups.flatMap(group => group.ids.map(id => [id, group]));
  idGroupTuples.forEach(([id, group]) => {
    debugService.throwIf(idGroupMap.has(id), `The Kitchen (Dropdown): Dropdown objects can only be present in at most one group. id="${id}"`);
    idGroupMap.set(id, group);
  });
  return _group(data, idGroupMap);
  function _group(data, idGroupMap) {
    if (data.length === 0) {
      return [];
    }
    const [object, ...rest] = data;
    const group = idGroupMap.get(object.id);
    if (group) {
      const [notInGroup, inGroup] = partition(rest, d => idGroupMap.get(d.id) === group);
      return [{
        title: group.title,
        data: [object, ...inGroup]
      }, ..._group(notInGroup, idGroupMap)];
    } else {
      return [object, ..._group(rest, idGroupMap)];
    }
  }
}
function getDropdownMenuItems(props) {
  const {
    data,
    groups,
    select,
    selectAll,
    debugService
  } = props;
  if (data.length === 0) {
    return [];
  }
  const groupedData = groupData(data, groups, debugService);
  const ungroupedData = groupedData.flatMap((menuItem, i) => {
    if ('data' in menuItem) {
      const {
        title,
        data
      } = menuItem;
      return [{
        title,
        id: `kxi-dropdown-group-header-${title}-${i}`,
        isGroupHeader: true,
        selection: SelectAllState.UNSELECTED
      }, ...data.map(d => Object.assign(Object.assign({}, d), {
        isGroupMember: true
      }))];
    }
    return menuItem;
  });
  const menuItems = ungroupedData.map(toMenuItemObject);
  if (select === 'multiSelectAll') {
    menuItems.unshift(toMenuItemObject({
      id: 'kxi-select-all',
      title: selectAll,
      isSelectAll: true
    }));
  }
  return menuItems;
  function toMenuItemObject(d) {
    var _a;
    const {
      isGroupHeader
    } = d;
    const base = {
      menuItem: d,
      id: d.id,
      disabled: (_a = d.disabled) !== null && _a !== void 0 ? _a : false
    };
    if (d.submenu) {
      return Object.assign(Object.assign({}, base), {
        submenu: d.submenu.map(toMenuItemObject)
      });
    }
    return Object.assign(Object.assign({}, base), {
      isClickable: !isGroupHeader,
      isTabbable: !isGroupHeader
    });
  }
}
/**
 * id and title must be a string.
 *
 * Only one of isSelected, submenu, and action can be provided. The precedence of these props is:
 * - submenu
 * - action
 * - isSelected
 */
function fixInvalidDropdownObject(dropdownObject, loggingService) {
  const copy = Object.assign({}, dropdownObject);
  const {
    action,
    id,
    submenu,
    title,
    isSelected
  } = copy;
  const sharedMessage = 'This will throw an exception in a future major version.';
  if (typeof id !== 'string') {
    loggingService.error('DropdownObject.id must be provided and must be a `string`', sharedMessage, dropdownObject);
    copy.id = uuidv4();
  }
  if (typeof title !== 'string') {
    loggingService.error('DropdownObject.title must be provided and must be a `string`', sharedMessage, dropdownObject);
    copy.title = '';
  }
  if (submenu) {
    if (submenu.length === 0) {
      loggingService.error('DropdownObject.submenu was provided but was an empty array. Either exclude the submenu prop completely or provide a submenu. This prop will be ignored.', sharedMessage, dropdownObject);
      delete copy.submenu;
    } else if (action !== undefined) {
      loggingService.error('DropdownObjects may not specify both `submenu` and `action` props. `action` will be ignored.', sharedMessage, dropdownObject);
      delete copy.action;
    }
  }
  if (isSelected) {
    if (submenu !== undefined) {
      loggingService.error('DropdownObjects may not specify both `isSelected` and `submenu` props. `isSelected` will be ignored.', sharedMessage, dropdownObject);
      delete copy.isSelected;
    }
    if (action !== undefined) {
      loggingService.error('DropdownObjects may not specify both `isSelected` and `action` props. `isSelected` will be ignored.', sharedMessage, dropdownObject);
      delete copy.isSelected;
    }
  }
  return copy;
}
function filterValidData(data, loggingService) {
  return data.map(d => fixInvalidDropdownObject(d, loggingService)).map(dropdownObject => {
    if (dropdownObject.submenu === undefined) {
      return dropdownObject;
    }
    return Object.assign(Object.assign({}, dropdownObject), {
      submenu: filterValidData(dropdownObject.submenu, loggingService)
    });
  });
}

class CustomPillProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], CustomPillProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", String)], CustomPillProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], CustomPillProps.prototype, "contrast", void 0);
class CustomPillEvents extends DivHTMLEvents {}
/**
 * Description: Small pill containing arbitrary strings
 */
const CustomPill = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(CustomPill, props);
  const {
      text,
      color = 'orca',
      style,
      className,
      contrast = 'normal'
    } = props,
    ingredients = __rest(props, ["text", "color", "style", "className", "contrast"]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('CustomPill', {
      text
    });
  }, [text]);
  const colorClass = normalizeColor(color);
  return jsx("div", Object.assign({}, debugService.componentProps, {
    className: `${className || ''} kxi-custom-pill ${contrast === 'high' ? 'kxi-high-contrast' : ''} ${colorClass} kxi-text-no-margin kxi-text-size-small kxi-text-left kxi-text-600 kxi-text-wrap-unset kxi-text-line-height-unset`,
    style: Object.assign({}, style)
  }, ingredients, {
    ref: ref,
    children: text
  }));
});
CustomPill.displayName = 'CustomPill';

class CloudPillProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], CloudPillProps.prototype, "cloud", void 0);
__decorate([Bound(), __metadata("design:type", String)], CloudPillProps.prototype, "contrast", void 0);
class CloudPillEvents extends DivHTMLEvents {}
/**
 * Description: Small pill indicating one of the three clouds.
 */
const CloudPill = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(CloudPill, props);
  const {
      cloud = 'CX'
    } = props,
    ingredients = __rest(props, ["cloud"]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('CloudPill', {
      cloud
    });
  }, [cloud]);
  const getInitials = () => {
    switch (cloud) {
      case 'EMPLOYEE':
      case 'EX':
        return 'EX';
      case 'MARKETING':
      case 'MX':
        return 'MX';
      case 'PRODUCT':
      case 'PX':
        return 'PX';
      case 'CUSTOMER':
      case 'CX':
      default:
        return 'CX';
    }
  };
  return jsx(CustomPill, Object.assign({}, debugService.componentProps, ingredients, {
    color: cloud,
    text: getInitials(),
    ref: ref
  }));
});
CloudPill.displayName = 'CloudPill';

const getViewBox = lottieName => {
  switch (lottieName) {
    case 'confetti_hat':
      return '0 0 360 360';
    case 'planet_1':
      return '0 0 200 200';
    case 'cx_loader':
    case 'mx_loader':
    case 'ex_loader':
    case 'px_loader':
    default:
      return '0 0 600 600';
  }
};
function normalizeLottie$1(lottie) {
  switch (lottie) {
    case 'CUSTOMER_loader':
      return 'cx_loader';
    case 'EMPLOYEE_loader':
      return 'ex_loader';
    case 'MARKETING_loader':
      return 'mx_loader';
    case 'PRODUCT_loader':
      return 'px_loader';
    default:
      return lottie;
  }
}
async function fetchAnimation(name) {
  const jsonUrl = `${kitchenRootPath()}/lotties-jsons/${name}.json`;
  const request = new XMLHttpRequest();
  request.open('GET', jsonUrl, true);
  return new Promise(resolve => {
    request.onload = function () {
      resolve(this.status === 200 ? JSON.parse(request.responseText) : null);
    };
    request.send();
  });
}

const animationCache = new CacheMap();
function useAnimation(name) {
  const lottie = normalizeLottie$1(name);
  const [animation, setAnimation] = React__default.useState(null);
  React__default.useEffect(() => {
    void (async () => {
      setAnimation(await animationCache.retrieveAsync(lottie, fetchAnimation));
    })();
  }, [lottie]);
  return animation;
}

function normalizeLottie(lottie) {
  return lottie.replace('CUSTOMER', 'cx').replace('EMPLOYEE', 'ex').replace('MARKETING', 'mx').replace('PRODUCT', 'px');
}
class XIAnimationProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], XIAnimationProps.prototype, "animation", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], XIAnimationProps.prototype, "loop", void 0);
__decorate([Bound(), __metadata("design:type", Object)], XIAnimationProps.prototype, "height", void 0);
__decorate([Bound(), __metadata("design:type", Object)], XIAnimationProps.prototype, "width", void 0);
__decorate([Bound(), __metadata("design:type", Number)], XIAnimationProps.prototype, "animationDelay", void 0);
class XIAnimationEvents extends DivHTMLEvents {}
/**
 * Description: Conveniently displays different Lottie animations we've preloaded into the platform.
 */
const XIAnimation = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(XIAnimation, props);
  const {
      animation: animationInput = 'cx_loader',
      height,
      width,
      loop = true,
      _lottieProps: lottieProps,
      animationDelay = 0,
      className,
      style: styleInput
    } = props,
    ingredients = __rest(props, ["animation", "height", "width", "loop", "_lottieProps", "animationDelay", "className", "style"]);
  useErrorIfRequiredPropIsNotProvided(debugService, props, 'animation');
  const animation = normalizeLottie(animationInput);
  const uuid = React.useId();
  const containerRef = React.useRef(null);
  const animationData = useAnimation(animation);
  React.useEffect(() => {
    const container = containerRef.current;
    if (!animationData || !container) {
      return;
    }
    // Only reload the animation if `animation` has changed or on first render (prevAnimationItem === undefined)
    const animationItem = Lottie.loadAnimation(Object.assign(Object.assign({}, lottieProps), {
      autoplay: false,
      animationData,
      container,
      loop,
      renderer: 'svg',
      rendererSettings: Object.assign(Object.assign({}, lottieProps === null || lottieProps === void 0 ? void 0 : lottieProps.rendererSettings), {
        preserveAspectRatio: 'xMidYMid meet',
        viewBoxSize: getViewBox(animation)
      })
    }));
    const timeout = setTimeout(() => {
      animationItem.play();
    }, animationDelay);
    return () => {
      clearTimeout(timeout);
      animationItem.destroy();
    };
  }, [animation, animationData, animationDelay, loop, lottieProps]);
  return jsx("div", Object.assign({
    "data-testid": "xi-animation"
  }, debugService.componentProps, ingredients, {
    className: `kxi-animation-wrapper ${className || ''}`,
    style: Object.assign(Object.assign({}, styleInput), {
      height,
      width
    }),
    id: uuid,
    ref: mergeRefs([ref, containerRef])
  }));
});
XIAnimation.displayName = 'XIAnimation';

/**
 * A wrapper around {@link XIAnimation} that displays a loading animation that remains a constant size as the width of its container changes.
 */
const LoadingAnimation = props => {
  const {
      _lottieProps: lottiePropsInput
    } = props,
    ingredients = __rest(props, ["_lottieProps"]);
  const lottieProps = React__default.useMemo(() => {
    return Object.assign(Object.assign({}, lottiePropsInput), {
      rendererSettings: Object.assign(Object.assign({}, lottiePropsInput === null || lottiePropsInput === void 0 ? void 0 : lottiePropsInput.rendererSettings), {
        viewBoxOnly: true
      })
    });
  }, [lottiePropsInput]);
  return jsx(XIAnimation, Object.assign({
    width: "100%"
  }, ingredients, {
    animation: "orca_loader",
    className: classNames(props.className, 'kxi-loading-animation'),
    _lottieProps: lottieProps
  }));
};

const MenuItem = props => {
  const {
      color: dropdownColor,
      menuObject: {
        color: menuItemColor = 'orca',
        action,
        disabled,
        id,
        isGroupHeader,
        isGroupMember,
        isLoadingIndicator,
        submenu,
        subtitle,
        title,
        tooltip,
        visual
      },
      select,
      selection
    } = props,
    ingredients = __rest(props, ["color", "menuObject", "select", "selection"]);
  const isSelected = selection === SelectAllState.SELECTED;
  const isMultiSelect = checkIfMultiple(select);
  const [titleElement, setTitleElement] = React.useState(null);
  const visualElement = (() => {
    if (!visual) {
      return null;
    }
    const disabledClass = disabled ? 'kxi-dropdown-item-disabled' : '';
    if (isIcons(visual)) {
      return jsx(XIIcon, {
        icon: visual,
        color: menuItemColor,
        className: `kxi-dropdown-item-icon ${disabledClass}`
      });
    } else if (isXIClouds(visual)) {
      return jsx(CloudPill, {
        cloud: visual,
        className: `kxi-dropdown-item-pill ${disabledClass}`
      });
    } else {
      devOnlyError(`Invalid \`visual\` provided to DropdownMenuItem with id ${id}. This will throw an exception in a future major version.`);
      return null;
    }
  })();
  const leftVisualElement = (() => {
    if (!isMultiSelect) {
      return visualElement;
    }
    const isIndeterminate = selection === SelectAllState.UNSPECIFIED;
    return !isGroupHeader && !submenu && !action && !disabled ? jsx("div", {
      className: "kxi-left-visual",
      children: jsx(Checkbox, {
        color: dropdownColor,
        // Checkbox will not show `isIndeterminate` unless `checked` is true
        checked: isIndeterminate || isSelected,
        disabled: disabled,
        _externallyControlledState: true,
        isIndeterminate: isIndeterminate,
        tabIndex: -1,
        onClick: e => e.stopPropagation()
      })
    }) : null;
  })();
  const rightVisualElement = (() => {
    let activeIcon = null;
    if (isSelected && select === 'single') {
      activeIcon = jsx(XIIcon, {
        style: {
          marginLeft: -6,
          marginRight: 6
        },
        icon: "circle_check_filled",
        color: dropdownColor
      });
    }
    return isMultiSelect && visualElement || activeIcon ? jsxs("div", {
      className: "kxi-flex-center",
      children: [isMultiSelect && visualElement, activeIcon]
    }) : null;
  })();
  return jsx(ToolTipWrapper, Object.assign({}, tooltip, {
    enabled: tooltip && !isLoadingIndicator ? tooltip.enabled : false,
    anchorElement: titleElement,
    stretch: true,
    style: {
      width: '100%'
    },
    children: jsx("div", Object.assign({
      className: classNames('kxi-dropdown-item', normalizeColor(dropdownColor), {
        'kxi-active': isSelected && select === 'single',
        'kxi-disabled': disabled,
        'kxi-dropdown-item-group': isGroupHeader,
        'kxi-dropdown-item-group-item': isGroupMember,
        'kxi-dropdown-item-loading-indicator': isLoadingIndicator
      })
    }, ingredients, {
      children: !isLoadingIndicator ? jsxs(Fragment, {
        children: [jsxs("div", {
          className: "kxi-flex-center",
          children: [leftVisualElement, jsxs("div", {
            className: classNames('kxi-dropdown-item-text', normalizeColor(menuItemColor)),
            children: [jsx("p", {
              ref: setTitleElement,
              className: classNames('kxi-title'),
              children: title
            }), subtitle && jsx("p", {
              className: classNames('kxi-subtitle'),
              children: subtitle
            })]
          })]
        }), rightVisualElement]
      }) : jsx(LoadingAnimation, {
        className: "kxi-dropdown-item-loading-animation"
      })
    }))
  }));
};
MenuItem.displayName = 'DropdownMenuItem';

const usePopoutMenuNavigation = menuItems => useListNavigation(menuItems, React.useCallback(ref => ref.canFocus(), []), React.useCallback(ref => ref.focus(), []));

class PopoutMenuItemRef {
  constructor(menuItemObject) {
    this.element = null;
    this.ref = el => this.element = el;
    this.submenu = [];
    if (menuItemObject && 'submenu' in menuItemObject) {
      this.submenu = menuItemObject.submenu.map(submenuItemObject => new PopoutMenuItemRef(submenuItemObject));
    }
  }
  canFocus() {
    return this.element !== null && this.element.tabIndex >= 0;
  }
  focus() {
    var _a;
    return (_a = this.element) === null || _a === void 0 ? void 0 : _a.focus();
  }
  getElements() {
    return [this.element, ...this.submenu.flatMap(ref => ref.getElements())];
  }
  /** For convenience */
  static fromList(menuItemObjects) {
    return Array.from(menuItemObjects).map(menuItemObject => new PopoutMenuItemRef(menuItemObject));
  }
}

const PopoutMenuItem = props => {
  const {
      idsPath,
      menuItemObject,
      onKeyboardNavigation,
      menuItemRefCallback,
      onKeyDown,
      onMenuItemClick,
      renderer,
      isVisible
    } = props,
    ingredients = __rest(props, ["idsPath", "menuItemObject", "onKeyboardNavigation", "menuItemRefCallback", "onKeyDown", "onMenuItemClick", "renderer", "isVisible"]);
  const {
    className,
    disabled,
    menuItem
  } = menuItemObject;
  let hasSubmenu = false;
  let submenu = [];
  let submenuProps = undefined;
  let isClickable = false;
  let isTabbable = true;
  if ('submenu' in menuItemObject) {
    hasSubmenu = true;
    submenu = menuItemObject.submenu;
    submenuProps = menuItemObject.submenuProps;
  } else {
    isClickable = menuItemObject.isClickable;
    isTabbable = menuItemObject.isTabbable;
  }
  const tabIndex = isTabbable && !disabled ? 0 : undefined;
  const menuItemRef = React.useRef(new PopoutMenuItemRef());
  const submenuRefCallback = (i, ref) => {
    menuItemRef.current.submenu[i] = ref;
  };
  React.useEffect(() => {
    resizeArray(menuItemRef.current.submenu, submenu.length, () => new PopoutMenuItemRef());
  }, [submenu.length]);
  React.useEffect(() => {
    menuItemRefCallback(menuItemRef.current);
  }, [menuItemRefCallback]);
  const submenuRefs = menuItemRef.current.submenu;
  const navigateToSubmenuItem = usePopoutMenuNavigation(submenuRefs);
  const enterSubmenu = () => navigateToSubmenuItem('FirstSibling', 0);
  const [hasHover, setHasHover] = React.useState(false);
  const [submenuHasHover, setSubmenuHasHover] = React.useState(false);
  const [hasFocus, setHasFocus] = React.useState(false);
  // Debounce because the submenu will close if the scrollbar is visible on macOS
  //     and scroll bar visibility is set to "Automatically based on mouse or trackpad" in settings
  //     before you have a chance to actually hover over the submenu, making it unaccessible
  const setHasHoverDebounced = useDebouncedCallback(setHasHover);
  const setSubmenuHasHoverDebounced = useDebouncedCallback(setSubmenuHasHover);
  const isSubmenuOpen = !disabled && (hasHover || submenuHasHover || hasFocus);
  return jsxs("div", Object.assign({}, ingredients, {
    "data-id": includeTestId(menuItemObject.id),
    "data-ids-path": includeTestId(() => idsPath.join(' ')),
    "data-testid": includeTestId(() => `menu item ${idsPath.concat([menuItemObject.id]).join(' ')}`),
    className: classNames('kxi-menu-item', className, {
      'kxi-menu-item-disabled': disabled,
      'kxi-menu-item-submenu': hasSubmenu,
      'kxi-menu-item-clickable': isClickable,
      'kxi-menu-item-tabbable': isTabbable
    }),
    ref: menuItemRef.current.ref,
    tabIndex: tabIndex,
    onClick: e => isClickable && !disabled && (onMenuItemClick === null || onMenuItemClick === void 0 ? void 0 : onMenuItemClick(menuItem, false, e)),
    onMouseEnter: () => hasSubmenu && setHasHoverDebounced(true),
    onMouseLeave: () => {
      if (hasSubmenu) {
        setHasHoverDebounced(false);
        setHasFocus(false);
      }
    },
    onFocus: () => hasSubmenu && setHasFocus(true),
    onBlur: () => hasSubmenu && setHasFocus(false),
    onKeyDown: event => {
      onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
      switch (event.key) {
        case 'ArrowUp':
          onKeyboardNavigation({
            target: 'PreviousSibling',
            event
          });
          break;
        case 'ArrowDown':
          onKeyboardNavigation({
            target: 'NextSibling',
            event
          });
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          onKeyboardNavigation({
            target: 'Parent',
            event
          });
          break;
        case 'Home':
          onKeyboardNavigation({
            target: 'FirstSibling',
            event
          });
          break;
        case 'End':
          onKeyboardNavigation({
            target: 'LastSibling',
            event
          });
          break;
        case 'Tab':
          onKeyboardNavigation({
            target: event.shiftKey ? 'ExitPrevious' : 'ExitNext',
            event
          });
          break;
        case ' ':
        case 'Enter':
          event.preventDefault();
          event.stopPropagation();
          if (submenu && submenu.length > 0) {
            enterSubmenu();
          } else if (isClickable && !disabled) {
            onMenuItemClick === null || onMenuItemClick === void 0 ? void 0 : onMenuItemClick(menuItem, true, event);
          }
      }
    },
    children: [renderer(menuItem), hasSubmenu && jsx(XIIcon, {
      className: "kxi-menu-submenu-icon",
      icon: "single_arrow_right"
    }), hasSubmenu && isVisible && jsx(PopoutMenu, Object.assign({
      id: `kxi-menu-item-${menuItemObject.id}-submenu`,
      idsPath: idsPath.concat([menuItemObject.id]),
      onMenuItemClick: onMenuItemClick,
      menuItemRefCallback: submenuRefCallback,
      onKeyboardNavigation: ({
        target,
        event
      }) => {
        switch (target) {
          case 'ExitPrevious':
          case 'ExitNext':
            onKeyboardNavigation({
              target,
              event
            });
            break;
          case 'Parent':
            event.stopPropagation();
            event.preventDefault();
            menuItemRef.current.focus();
            break;
        }
      }
    }, submenuProps, {
      onMouseEnter: () => setSubmenuHasHoverDebounced(true),
      onMouseLeave: () => setSubmenuHasHoverDebounced(false),
      menuItems: submenu,
      renderer: renderer,
      popoutProps: {
        direction: 'right-top',
        edgeClearance: 0,
        isVisible: isSubmenuOpen,
        anchorElement: menuItemRef.current.element,
        allowScroll: true
      }
    }))]
  }));
};

const PopoutMenu = props => {
  var _a;
  const {
      // InternalMenuProps
      menuItemRefCallback,
      onKeyboardNavigation: onKeyboardNavigationInput,
      // MenuProps
      className,
      footer = null,
      idsPath = [],
      header = null,
      listContainerProps,
      menuItems,
      menuRef: menuRefInput,
      noItemsFallback = null,
      onMenuItemClick,
      popoutProps,
      renderer
    } = props,
    ingredients = __rest(props, ["menuItemRefCallback", "onKeyboardNavigation", "className", "footer", "idsPath", "header", "listContainerProps", "menuItems", "menuRef", "noItemsFallback", "onMenuItemClick", "popoutProps", "renderer"]);
  const {
    isVisible = false
  } = popoutProps;
  const menuRef = React.useRef(null);
  const isMobile = useIsMobile();
  const {
    ref: menuItemsRef,
    callbackRefs
  } = useArrayRef(menuItems.length, () => new PopoutMenuItemRef(), menuItemRefCallback);
  const navigateToMenuItem = usePopoutMenuNavigation(menuItemsRef.current);
  const menuItemElements = menuItems.map((menuItemObject, index) => jsx(PopoutMenuItem, {
    idsPath: idsPath,
    isVisible: isVisible,
    menuItemObject: menuItemObject,
    menuItemRefCallback: callbackRefs[index],
    onMenuItemClick: onMenuItemClick,
    renderer: renderer,
    onKeyboardNavigation: ({
      target,
      event
    }) => {
      if (target === 'Parent' || target === 'ExitPrevious' || target === 'ExitNext') {
        onKeyboardNavigationInput === null || onKeyboardNavigationInput === void 0 ? void 0 : onKeyboardNavigationInput({
          target,
          event,
          index
        });
      } else {
        navigateToMenuItem(target, index);
        event.stopPropagation();
        event.preventDefault();
      }
    }
  }, menuItemObject.id));
  const body = menuItemElements.length > 0 ? menuItemElements : noItemsFallback;
  return header || body || footer ? jsx(Popout, Object.assign({}, popoutProps, {
    popoutId: ingredients.id,
    internalPadding: 0,
    _isMobile: (_a = popoutProps._isMobile) !== null && _a !== void 0 ? _a : isMobile,
    preventFocus: true,
    children: jsxs("div", Object.assign({}, ingredients, {
      className: classNames(className, 'kxi-menu', {
        'kxi-menu-has-header': header !== null,
        'kxi-menu-has-footer': footer !== null
      }),
      ref: menuRefInput ? mergeRefs([menuRefInput, menuRef]) : menuRef,
      children: [header && jsx("div", {
        className: "kxi-menu-header",
        children: header
      }), jsx("div", Object.assign({}, listContainerProps, {
        className: classNames(listContainerProps === null || listContainerProps === void 0 ? void 0 : listContainerProps.className, 'kxi-menu-body'),
        children: body
      })), footer && jsx("div", {
        className: "kxi-menu-footer",
        children: footer
      })]
    }))
  })) : null;
};

const SuggestionItem = props => {
  const {
    itemText,
    typedText,
    onClick
  } = props;
  const [preTypedText, setPreTypedText] = React.useState('');
  const [displayedTypedText, setDisplayedTypedText] = React.useState('');
  const [postTypedText, setPostTypedText] = React.useState('');
  React.useEffect(() => {
    const lowercaseItemText = itemText.toLowerCase();
    const strIndex = lowercaseItemText.indexOf(typedText.toLowerCase());
    if (strIndex > -1) {
      setPreTypedText(itemText.substring(0, strIndex));
      setDisplayedTypedText(itemText.substring(strIndex, strIndex + typedText.length));
      setPostTypedText(itemText.substring(strIndex + typedText.length));
    } else {
      setPreTypedText(itemText);
      setDisplayedTypedText('');
      setPostTypedText('');
    }
  }, [itemText, typedText]);
  const onSuggestionClick = () => {
    onClick(itemText);
  };
  return jsxs("div", {
    role: "listitem",
    className: "kxi-suggestion",
    onMouseDown: onSuggestionClick,
    children: [jsx("span", {
      className: "kxi-text-600",
      children: preTypedText
    }), jsx("span", {
      children: displayedTypedText
    }), jsx("span", {
      className: "kxi-text-600",
      children: postTypedText
    })]
  });
};

const ErrorStateUI = props => {
  const {
    prePopulateStatus,
    errorState,
    disabled,
    maxCharsAllowed,
    contrastClass,
    errorStateClass,
    text
  } = props;
  const preStatus = prePopulateStatus ? jsx("div", {
    className: "kxi-status-wrapper",
    children: jsx("div", {
      className: "kxi-status-hidden",
      children: "x"
    })
  }) : '';
  const switchIcon = state => {
    switch (state) {
      case 'success':
        return 'circle_check_filled';
      default:
        return 'warning_filled';
    }
  };
  const charCount = (maxCharsAllowed === null || maxCharsAllowed === void 0 ? void 0 : maxCharsAllowed.max) ? jsx("div", {
    className: `kxi-status-label ${errorStateClass}`,
    children: `${(text === null || text === void 0 ? void 0 : text.length) || 0}/${maxCharsAllowed.max}`
  }) : null;
  return jsx(Fragment, {
    children: errorState && !disabled && errorState.text || maxCharsAllowed ? jsx("div", {
      className: `kxi-status-wrapper ${contrastClass}`,
      children: jsx("div", {
        className: `kxi-status-container ${errorStateClass} ${contrastClass}`,
        children: (errorState === null || errorState === void 0 ? void 0 : errorState.state) ? jsxs(Fragment, {
          children: [jsxs("div", {
            className: `kxi-status-label kxi-flex-center ${errorStateClass} ${contrastClass}`,
            children: [errorState && errorState.state ? jsx(XIIcon, {
              iconSize: "normal",
              icon: switchIcon(errorState.state),
              color: errorState.state
            }) : null, jsx("p", {
              className: `kxi-error-state-text ${contrastClass}`,
              children: errorState.text
            })]
          }), charCount]
        }) : charCount
      })
    }) : preStatus
  });
};

const {
  orca: orca$2,
  white: white$3,
  grey: grey$1
} = XIColors;
const iconSize = 24;
class SearchBoxProps extends ObservablePropsMixin(InputHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], SearchBoxProps.prototype, "placeholder", void 0);
__decorate([Bound(), __metadata("design:type", String)], SearchBoxProps.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SearchBoxProps.prototype, "isFiredOnEnter", void 0);
__decorate([Bound(), __metadata("design:type", String)], SearchBoxProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Object)], SearchBoxProps.prototype, "textSize", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SearchBoxProps.prototype, "hasOutline", void 0);
__decorate([Bound(), __metadata("design:type", String)], SearchBoxProps.prototype, "outline", void 0);
__decorate([Bound(), __metadata("design:type", Array)], SearchBoxProps.prototype, "data", void 0);
__decorate([Bound(), __metadata("design:type", Number)], SearchBoxProps.prototype, "maxSuggestions", void 0);
__decorate([Bound(), __metadata("design:type", Number)], SearchBoxProps.prototype, "maxLength", void 0);
__decorate([Bound(), __metadata("design:type", Object)], SearchBoxProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SearchBoxProps.prototype, "prePopulateStatus", void 0);
__decorate([Bound(), __metadata("design:type", String)], SearchBoxProps.prototype, "componentSize", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SearchBoxProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", String)], SearchBoxProps.prototype, "type", void 0);
class SearchBoxEvents extends InputHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], SearchBoxEvents.prototype, "onCancelClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], SearchBoxEvents.prototype, "onChangeOverride", void 0);
__decorate([Bound(), __metadata("design:type", Function)], SearchBoxEvents.prototype, "onSuggestionClick", void 0);
/**
 * Description: InMoment branded text input field to be used as a search field.
 */
const SearchBox = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(SearchBox, props);
  const {
      _blurOnEnter: blurOnEnter = true,
      placeholder,
      hasOutline = true,
      outline: outlineInput,
      contrast = 'normal',
      textSize = 'normal',
      status,
      value,
      onChangeOverride,
      disabled = false,
      _iconInputSpacing: iconInputSpacing = 6,
      _iconOffset: iconOffset = 12,
      _stretchHeight: stretchHeight = false,
      isFiredOnEnter,
      onCancelClick,
      type,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      className,
      maxLength,
      data,
      maxSuggestions = 4,
      observer,
      componentSize = 'normal',
      onSuggestionClick,
      stretch,
      style: styleInput,
      _wrapperProps: wrapperProps
    } = props,
    ingredients = __rest(props, ["_blurOnEnter", "placeholder", "hasOutline", "outline", "contrast", "textSize", "status", "value", "onChangeOverride", "disabled", "_iconInputSpacing", "_iconOffset", "_stretchHeight", "isFiredOnEnter", "onCancelClick", "type", "onChange", "onFocus", "onBlur", "onKeyDown", "className", "maxLength", "data", "maxSuggestions", "observer", "componentSize", "onSuggestionClick", "stretch", "style", "_wrapperProps"]);
  const outline = outlineInput !== null && outlineInput !== void 0 ? outlineInput : hasOutline ? 'outline' : 'none';
  const isHighContrast = contrast === 'high';
  const inputRef = React.useRef(null);
  const [searchText, setSearchText] = React.useState(props.value || '');
  const [hoverState, setHoverState] = React.useState(false);
  const [focusState, setFocusState] = React.useState(false);
  const [suggestionData, setSuggestionData] = React.useState(data);
  const [topSuggestions, setTopSuggestions] = React.useState([]);
  React.useEffect(() => {
    if (!maxLength || !value || value.length <= maxLength) {
      setSearchText(value || '');
    }
  }, [value]);
  React.useEffect(() => {
    setSuggestionData(data);
  }, [data]);
  // Filter the suggestions down and map them to SuggestionItems
  // Items that start with the search term are ranked higher than those that just merely contain the search term
  React.useEffect(() => {
    if (!suggestionData) {
      // Avoid re-rendering if it's already empty
      if (topSuggestions.length > 0) {
        setTopSuggestions([]);
      }
      return;
    }
    const indexOfSearchText = suggestion => {
      var _a;
      return (_a = suggestion.text) === null || _a === void 0 ? void 0 : _a.toLowerCase().indexOf(searchText.toLowerCase());
    };
    const exactMatch = suggestion => suggestion.text === searchText;
    const matchesAtStart = suggestionData.filter(suggestion => indexOfSearchText(suggestion) === 0 && !exactMatch(suggestion));
    const matchesElsewhere = suggestionData.filter(suggestion => indexOfSearchText(suggestion) > 0);
    const allMatches = matchesAtStart.concat(matchesElsewhere).slice(0, maxSuggestions);
    setTopSuggestions(mapSuggestions(allMatches));
  }, [searchText, suggestionData]);
  const contrastClass = contrast === 'high' ? ' kxi-high-contrast' : '';
  const errorStateClass = (status === null || status === void 0 ? void 0 : status.state) ? `kxi-${status.state}` : '';
  const errorState = status;
  const mapSuggestions = suggestions => {
    return suggestions.map((suggestion, i) => jsx(SuggestionItem, {
      itemText: suggestion.text,
      typedText: searchText,
      onClick: itemText => {
        setSearchText(itemText);
        if (observer) observer.value = itemText;
        if (onSuggestionClick) {
          if (suggestion.id) onSuggestionClick({
            suggestion
          });else onSuggestionClick({
            suggestion
          });
        }
        if (onChangeOverride) {
          devOnlyWarn('The Kitchen: Usage of onChangeOverride on the SearchBox component for suggestion items is deprecated. Please use onSuggestionClick instead.');
          onChangeOverride({
            searchText
          });
        }
      }
    }, i));
  };
  const updateText = val => {
    if (disabled) return;
    if (!maxLength || val.length <= maxLength) {
      setSearchText(val);
      if (observer) {
        observer.value = val;
      }
    }
    if (!isFiredOnEnter) {
      onChangeOverride === null || onChangeOverride === void 0 ? void 0 : onChangeOverride({
        searchText: val
      });
    }
  };
  const active = checkIfTextInputTypeIsValid('SearchBox', type);
  const handleKeyPressed = e => {
    var _a;
    if (!disabled) {
      if (e.key === 'Enter') {
        onChangeOverride === null || onChangeOverride === void 0 ? void 0 : onChangeOverride({
          searchText
        });
        if (blurOnEnter) {
          (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        }
      }
    }
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
  };
  const cancelFn = withKeyboard => {
    var _a;
    if (observer) {
      observer.value = '';
    }
    onCancelClick === null || onCancelClick === void 0 ? void 0 : onCancelClick();
    onChangeOverride === null || onChangeOverride === void 0 ? void 0 : onChangeOverride({
      searchText: ''
    });
    setSearchText('');
    setHoverState(false);
    if (withKeyboard) {
      (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  };
  const handleFocus = e => {
    setFocusState(true);
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
  };
  const handleBlur = e => {
    setFocusState(false);
    setHoverState(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  };
  let searchIconColor;
  if (isHighContrast && outline === 'none' && !disabled) {
    searchIconColor = white$3.light[15];
  } else if (status) {
    searchIconColor = switchColorBasedOnWarning(status.state);
  } else if (focusState || hoverState && !disabled) {
    searchIconColor = isHighContrast ? white$3.primary : orca$2.primary;
  } else {
    searchIconColor = isHighContrast ? white$3.light[60] : grey$1.light[60];
  }
  return jsxs(Fragment, {
    children: [jsxs("div", Object.assign({}, wrapperProps, debugService.componentProps, {
      className: classNames(className, wrapperProps === null || wrapperProps === void 0 ? void 0 : wrapperProps.className, 'kxi-search-box orca', `kxi-${componentSize}`, {
        'kxi-high-contrast': isHighContrast,
        'kxi-stretch-width': stretch,
        'kxi-stretch-height': stretchHeight,
        'kxi-bottom-border': outline === 'underline'
      }),
      children: [jsx("input", Object.assign({}, ingredients, {
        "aria-label": placeholder,
        role: "searchbox",
        "aria-disabled": disabled,
        className: classNames(className, errorStateClass, `kxi-text-${textSize}`, status && `kxi-${status.state}`, {
          'kxi-border': outline === 'outline',
          'kxi-stretch-height': stretchHeight
        }),
        type: active ? type : 'search',
        color: searchIconColor,
        ref: mergeRefs([inputRef, ref]),
        onKeyDown: handleKeyPressed,
        onBlur: handleBlur,
        onFocus: handleFocus,
        value: searchText,
        placeholder: placeholder,
        onChange: e => {
          updateText(e.target.value);
          onChange === null || onChange === void 0 ? void 0 : onChange(e);
        },
        readOnly: disabled,
        disabled: disabled,
        style: Object.assign(Object.assign({}, styleInput), {
          paddingLeft: iconOffset + iconSize + iconInputSpacing
        })
      })), jsx("div", {
        className: "kxi-search-icon",
        style: {
          left: iconOffset
        },
        children: !searchText ? jsx(XIIcon, {
          contrast: contrast,
          iconSize: componentSize === 'normal' ? 24 : 30,
          icon: "search"
        }) : jsx(CloseIcon, {
          disabled: disabled,
          contrast: contrast,
          onCancel: cancelFn,
          size: componentSize === 'normal' ? 24 : 30
        })
      }), jsx(AnimatePresence, {
        children: searchText && focusState && topSuggestions.length !== 0 && jsx(motion.div, {
          initial: {
            opacity: 0
          },
          animate: {
            opacity: 1
          },
          exit: {
            opacity: 0
          },
          transition: {
            duration: 0.1
          },
          className: "kxi-suggestion-wrapper",
          children: jsx("div", {
            role: "list",
            children: topSuggestions
          })
        })
      })]
    })), jsx(ErrorStateUI, {
      errorStateClass: errorStateClass,
      errorState: errorState,
      prePopulateStatus: false,
      contrastClass: contrastClass,
      disabled: false
    })]
  });
});
SearchBox.displayName = 'SearchBox';

const MenuHeader = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      _wrapperProps,
      onKeyboardNavigation,
      onKeyDown,
      placeholder
    } = props,
    ingredients = __rest(props, ["_wrapperProps", "onKeyboardNavigation", "onKeyDown", "placeholder"]);
  return jsx("div", {
    className: "kxi-dropdown-search",
    children: jsx(SearchBox, Object.assign({}, ingredients, {
      "data-testid": "kxi-dropdown-menu-header-search",
      "aria-label": placeholder,
      _blurOnEnter: false,
      _iconOffset: 18,
      _stretchHeight: true,
      onKeyDown: e => {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            onKeyboardNavigation({
              event: e,
              target: 'ExitPrevious'
            });
          } else {
            onKeyboardNavigation({
              event: e,
              target: 'ExitNext'
            });
          }
        }
      },
      outline: "none",
      placeholder: placeholder,
      ref: ref,
      type: "text"
    }))
  });
});

/**
 *
 * @see https://stackoverflow.com/questions/61323376/how-to-know-that-a-onclick-event-is-called-from-mouseclick-or-pressing-the-enter
 */
function isButtonClickWithKeyboard(e) {
  return e.clientX === 0 && e.clientY === 0;
}
function MenuFooter(props) {
  var _a, _b, _c, _d;
  const {
    buttons,
    countSelectedText,
    onCancel,
    onConfirm,
    onKeyboardNavigation,
    primaryButtonRef,
    secondaryButtonRef,
    select,
    tabIndex
  } = props;
  const isMultiSelect = checkIfMultiple(select);
  const sharedButtonProps = {
    hasAnimation: false,
    buttonSize: 'small',
    tabIndex
  };
  const secondaryButton = isMultiSelect || (buttons === null || buttons === void 0 ? void 0 : buttons.secondary) ? jsx(DefaultButton, Object.assign({}, sharedButtonProps, {
    ref: secondaryButtonRef,
    "data-testid": "kxi-dropdown-menu-footer-secondary-button",
    style: {
      marginRight: 6
    },
    fill: isMultiSelect ? 'text' : 'no-fill',
    text: (_b = (_a = buttons === null || buttons === void 0 ? void 0 : buttons.secondary) === null || _a === void 0 ? void 0 : _a.text) !== null && _b !== void 0 ? _b : 'Cancel',
    onKeyDown: e => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          onKeyboardNavigation({
            event: e,
            target: 'ExitPrevious'
          });
        } else if (!primaryButtonRef.current) {
          // If Tab but primary button is not present
          onKeyboardNavigation({
            event: e,
            target: 'ExitNext'
          });
        }
      }
    },
    onClick: e => {
      var _a, _b;
      e.stopPropagation();
      (_b = (_a = buttons === null || buttons === void 0 ? void 0 : buttons.secondary) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
      onCancel(isButtonClickWithKeyboard(e));
    }
  })) : null;
  const primaryButton = isMultiSelect || (buttons === null || buttons === void 0 ? void 0 : buttons.primary) ? jsx(DefaultButton, Object.assign({}, sharedButtonProps, {
    ref: primaryButtonRef,
    "data-testid": "kxi-dropdown-menu-footer-primary-button",
    fill: "fill",
    text: (_d = (_c = buttons === null || buttons === void 0 ? void 0 : buttons.primary) === null || _c === void 0 ? void 0 : _c.text) !== null && _d !== void 0 ? _d : 'Apply',
    onKeyDown: e => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (!secondaryButtonRef.current) {
            // If Shift+Tab but secondary button is not present
            onKeyboardNavigation({
              event: e,
              target: 'ExitPrevious'
            });
          }
        } else {
          onKeyboardNavigation({
            event: e,
            target: 'ExitNext'
          });
        }
      }
    },
    onClick: e => {
      var _a, _b;
      e.stopPropagation();
      (_b = (_a = buttons === null || buttons === void 0 ? void 0 : buttons.primary) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
      onConfirm(isButtonClickWithKeyboard(e));
    }
  })) : null;
  const countElement = isMultiSelect ? jsx("div", {
    className: "kxi-dropdown-selection-count",
    children: countSelectedText
  }) : null;
  const buttonElements = isMultiSelect || buttons ? jsxs("div", {
    className: "kxi-dropdown-buttons",
    children: [secondaryButton, primaryButton]
  }) : null;
  return countElement || buttonElements ? jsxs("div", {
    "data-testid": "kxi-dropdown-menu-footer",
    className: classNames('kxi-dropdown-menu-footer', countElement !== null && 'kxi-dropdown-menu-footer-has-count'),
    children: [countElement, buttonElements]
  }) : null;
}

function union(set1, set2) {
  const result = new Set();
  for (const value of set1) {
    result.add(value);
  }
  for (const value of set2) {
    result.add(value);
  }
  return result;
}
function intersection(set1, set2) {
  const _set2 = toSet(set2);
  const result = new Set();
  for (const value of set1) {
    if (_set2.has(value)) {
      result.add(value);
    }
  }
  return result;
}
function difference(set1, set2) {
  const result = new Set(set1);
  for (const value of set2) {
    result.delete(value);
  }
  return result;
}
function areEqual(set1, set2) {
  if (set1.size !== set2.size) {
    return false;
  }
  for (const value of set1) {
    if (!set2.has(value)) {
      return false;
    }
  }
  return true;
}
function addOrDelete(set, value, shouldAdd) {
  if (shouldAdd) {
    set.add(value);
  } else {
    set.delete(value);
  }
}
function toggle(set, value) {
  addOrDelete(set, value, !set.has(value));
}
function toSet(iterable) {
  return iterable instanceof Set ? iterable : new Set(iterable);
}

const Menu = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a, _b;
  const {
    buttons,
    countSelectedText,
    direction,
    endOfScrollOffset,
    inMenuSearch,
    inMenuSearchProps,
    isOpen,
    localization,
    menuIngredients,
    menuItems,
    nonWhiteColor,
    onCancel,
    onConfirm,
    onEndOfScroll,
    onExternalClick,
    onKeyboardNavigation,
    onSearch,
    onMultiSelectIdsChange,
    popoutAnchorElement,
    query,
    select,
    selectableIds,
    selectedIds,
    tabIndex
  } = props;
  const searchRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const primaryButtonRef = React.useRef(null);
  const secondaryButtonRef = React.useRef(null);
  const listContainerRef = React.useRef(null);
  const {
    ref: menuItemsRef,
    callbackRef
  } = useArrayRef(menuItems.length, () => new PopoutMenuItemRef());
  /** Used so that {@link onEndOfScroll} is only called once. */
  const isAtEndOfScrollRef = React.useRef(false);
  /** Reset when the items change. */
  React.useEffect(() => {
    isAtEndOfScrollRef.current = false;
  }, [menuItems]);
  /** Trigger onEndOfScroll when the user can't scroll. */
  React.useEffect(() => {
    const el = listContainerRef.current;
    if (!el || !isOpen) {
      return;
    }
    if (el.scrollHeight <= el.clientHeight) {
      onEndOfScroll === null || onEndOfScroll === void 0 ? void 0 : onEndOfScroll();
    }
  }, [isOpen, menuItems, onEndOfScroll]);
  const navigateToMenuItem = usePopoutMenuNavigation(menuItemsRef.current);
  React.useImperativeHandle(ref, () => ({
    ref: menuRef,
    focus() {
      if (searchRef.current) {
        searchRef.current.focus();
      } else if (menuItemsRef.current.length > 0) {
        navigateToMenuItem('FirstSibling', 0);
      } else if (secondaryButtonRef.current) {
        secondaryButtonRef.current.focus();
      } else if (primaryButtonRef.current) {
        primaryButtonRef.current.focus();
      } else {
        return false;
      }
      return true;
    }
  }), [menuItemsRef, navigateToMenuItem]);
  const isMultiSelect = checkIfMultiple(select);
  /**
   * Not all selected ids may be visible when searching,
   * and we only want to determine the select all state from items that are currently visible,
   * so ignore all ids that are not currently selectable.
   */
  const selectAllState = (() => {
    const selectableSelectedIds = intersection(selectedIds, selectableIds);
    return selectableSelectedIds.size === 0 ? SelectAllState.UNSELECTED : selectableSelectedIds.size < selectableIds.size ? SelectAllState.UNSPECIFIED : SelectAllState.SELECTED;
  })();
  const onSelectAll = () => {
    const newSelectedIds = new Set(selectedIds);
    // Since our incoming data may be filtered by search, only add/delete the items we know are selectable
    if (selectAllState === SelectAllState.UNSELECTED || selectAllState === SelectAllState.UNSPECIFIED) {
      selectableIds.forEach(id => newSelectedIds.add(id));
    } else {
      selectableIds.forEach(id => newSelectedIds.delete(id));
    }
    onMultiSelectIdsChange(newSelectedIds);
  };
  const getUpdatedSelection = id => {
    if (!isMultiSelect) {
      return new Set([id]);
    }
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(id)) {
      newSelectedIds.delete(id);
    } else {
      newSelectedIds.add(id);
    }
    return newSelectedIds;
  };
  const headerElement = inMenuSearch ? jsx(MenuHeader, Object.assign({}, inMenuSearchProps, {
    ref: (inMenuSearchProps === null || inMenuSearchProps === void 0 ? void 0 : inMenuSearchProps.ref) ? mergeRefs([inMenuSearchProps.ref, searchRef]) : searchRef,
    onChangeOverride: query => {
      var _a;
      (_a = inMenuSearchProps === null || inMenuSearchProps === void 0 ? void 0 : inMenuSearchProps.onChangeOverride) === null || _a === void 0 ? void 0 : _a.call(inMenuSearchProps, query);
      onSearch(query.searchText);
    },
    onKeyboardNavigation: ({
      event,
      target
    }) => {
      if (target === 'ExitNext' && (menuItemsRef.current.some(ref => ref.canFocus()) || secondaryButtonRef.current || primaryButtonRef.current)) {
        // Another element in menu can be focused, so let default behavior happen
        return;
      }
      onKeyboardNavigation({
        event,
        target
      });
    },
    placeholder: (_a = localization.search) !== null && _a !== void 0 ? _a : defaultLocalization.search,
    tabIndex: tabIndex,
    value: query
  })) : null;
  const footerElement = isMultiSelect || buttons ? jsx(MenuFooter, {
    buttons: buttons,
    countSelectedText: countSelectedText,
    onCancel: onCancel,
    onConfirm: withKeyboard => onConfirm(selectedIds, withKeyboard),
    onKeyboardNavigation: ({
      target,
      event
    }) => {
      if (target === 'ExitPrevious' && (menuItemsRef.current.some(ref => ref.canFocus()) || searchRef.current)) {
        // Another element in menu can be focused, so let default behavior happen
        return;
      }
      onKeyboardNavigation({
        event,
        target
      });
    },
    primaryButtonRef: primaryButtonRef,
    secondaryButtonRef: secondaryButtonRef,
    select: select,
    tabIndex: tabIndex
  }) : null;
  return jsx(PopoutMenu, Object.assign({}, menuIngredients, {
    className: classNames(menuIngredients === null || menuIngredients === void 0 ? void 0 : menuIngredients.className, 'kxi-dropdown-menu'),
    header: headerElement,
    footer: footerElement,
    menuItems: menuItems,
    menuItemRefCallback: callbackRef,
    menuRef: menuRef,
    noItemsFallback: jsx("div", {
      className: "kxi-dropdown-no-results",
      children: (_b = localization === null || localization === void 0 ? void 0 : localization.noResultsFound) !== null && _b !== void 0 ? _b : defaultLocalization.noResultsFound
    }),
    listContainerProps: {
      ref: listContainerRef,
      onScroll: e => {
        const refs = menuItemsRef.current;
        const index = refs.length - endOfScrollOffset;
        const offsetHeight = index < 0 || index >= refs.length ? 0 : refs.slice(index).map(({
          element
        }) => element === null || element === void 0 ? void 0 : element.clientHeight).filter(isNotNullish).reduce((sum, height) => sum + height, 0);
        const target = e.target;
        if (target.scrollTop >= target.scrollHeight - target.clientHeight - offsetHeight || target.scrollHeight === target.clientHeight) {
          if (!isAtEndOfScrollRef.current) {
            onEndOfScroll === null || onEndOfScroll === void 0 ? void 0 : onEndOfScroll();
            isAtEndOfScrollRef.current = true;
          }
        } else {
          isAtEndOfScrollRef.current = false;
        }
      }
    },
    renderer: menuObject => jsx(MenuItem, {
      color: nonWhiteColor,
      selection: menuObject.isSelectAll ? selectAllState : selectedIds.has(menuObject.id) ? SelectAllState.SELECTED : SelectAllState.UNSELECTED,
      menuObject: menuObject,
      select: select
    }),
    onMenuItemClick: (dropdownObject, withKeyboard, e) => {
      e.stopPropagation();
      const {
        action,
        disabled,
        id,
        isSelectAll
      } = dropdownObject;
      if (disabled) {
        return;
      }
      if (isSelectAll) {
        onSelectAll();
      } else if (action) {
        action();
        onConfirm(selectedIds, withKeyboard);
      } else {
        const newSelectedIds = getUpdatedSelection(id);
        if (isMultiSelect) {
          onMultiSelectIdsChange(newSelectedIds);
        } else {
          onConfirm(newSelectedIds, withKeyboard);
        }
      }
    },
    onKeyboardNavigation: ({
      target,
      event
    }) => {
      if (target === 'ExitPrevious') {
        if (searchRef.current) {
          event.stopPropagation();
          // Focus first menu item so that focus moves to either the SearchBox close button (if present) or the SearchBox itself
          navigateToMenuItem('FirstSibling', 0);
        } else {
          onKeyboardNavigation({
            target,
            event
          });
        }
      } else if (target === 'ExitNext') {
        if (secondaryButtonRef.current) {
          secondaryButtonRef.current.focus();
          event.stopPropagation();
          event.preventDefault();
        } else if (primaryButtonRef.current) {
          primaryButtonRef.current.focus();
          event.stopPropagation();
          event.preventDefault();
        } else {
          onKeyboardNavigation({
            target,
            event
          });
        }
      } else {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    popoutProps: {
      direction,
      anchorElement: popoutAnchorElement,
      preventFocus: true,
      trapFocus: false,
      isVisible: isOpen,
      onEscape: () => onCancel(true),
      onExternalClick: e => {
        if (menuItemsRef.current.some(ref => ref.getElements().some(el => el === null || el === void 0 ? void 0 : el.contains(e.event.target)))) {
          return;
        }
        onExternalClick === null || onExternalClick === void 0 ? void 0 : onExternalClick(e);
      }
    }
  }));
});
Menu.displayName = 'DropdownMenu';

function useControlKeyboardNavigation(onKeyboardNavigation, onToggle) {
  return useKeyboardNavigation((target, event) => {
    if (target === 'ExitPrevious' || target === 'ExitNext' || target === 'Escape') {
      onKeyboardNavigation({
        target,
        event
      });
    } else if (target === 'Child') {
      event.stopPropagation();
      event.preventDefault();
      onToggle(true);
    }
  });
}

const DefaultButtonToggle = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      onKeyboardNavigation,
      isOpen,
      onToggle
    } = props,
    ingredients = __rest(props, ["onKeyboardNavigation", "isOpen", "onToggle"]);
  const onKeyboardNavigationKeyDown = useControlKeyboardNavigation(onKeyboardNavigation, onToggle);
  return jsx(DefaultButton, Object.assign({
    ref: ref,
    "data-testid": "kxi-dropdown-default-button-toggle"
  }, ingredients, {
    tooltip: isOpen ? undefined : ingredients === null || ingredients === void 0 ? void 0 : ingredients.tooltip,
    onKeyDown: e => {
      var _a;
      (_a = ingredients === null || ingredients === void 0 ? void 0 : ingredients.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(ingredients, e);
      onKeyboardNavigationKeyDown(e);
    },
    onClick: e => {
      var _a;
      (_a = ingredients === null || ingredients === void 0 ? void 0 : ingredients.onClick) === null || _a === void 0 ? void 0 : _a.call(ingredients, e);
      onToggle(false);
    }
  }));
});

const FabToggle = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      isOpen,
      onKeyboardNavigation,
      onToggle
    } = props,
    ingredients = __rest(props, ["isOpen", "onKeyboardNavigation", "onToggle"]);
  const onKeyboardNavigationKeyDown = useControlKeyboardNavigation(onKeyboardNavigation, onToggle);
  return jsx(FAB, Object.assign({
    ref: ref,
    "data-testid": "kxi-dropdown-fab-toggle"
  }, ingredients, {
    tooltip: isOpen ? undefined : ingredients === null || ingredients === void 0 ? void 0 : ingredients.tooltip,
    onKeyDown: e => {
      var _a;
      (_a = ingredients === null || ingredients === void 0 ? void 0 : ingredients.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(ingredients, e);
      onKeyboardNavigationKeyDown(e);
    },
    onClick: e => {
      var _a;
      (_a = ingredients === null || ingredients === void 0 ? void 0 : ingredients.onClick) === null || _a === void 0 ? void 0 : _a.call(ingredients, e);
      onToggle(false);
    }
  }));
});

const IconButtonToggle = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      onKeyboardNavigation,
      isOpen,
      onToggle
    } = props,
    ingredients = __rest(props, ["onKeyboardNavigation", "isOpen", "onToggle"]);
  const onKeyboardNavigationKeyDown = useControlKeyboardNavigation(onKeyboardNavigation, onToggle);
  return jsx(IconButton, Object.assign({
    ref: ref,
    "data-testid": "kxi-dropdown-icon-button-toggle"
  }, ingredients, {
    tooltip: isOpen ? undefined : ingredients === null || ingredients === void 0 ? void 0 : ingredients.tooltip,
    onKeyDown: e => {
      var _a;
      (_a = ingredients === null || ingredients === void 0 ? void 0 : ingredients.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(ingredients, e);
      onKeyboardNavigationKeyDown(e);
    },
    onClick: e => {
      var _a;
      (_a = ingredients === null || ingredients === void 0 ? void 0 : ingredients.onClick) === null || _a === void 0 ? void 0 : _a.call(ingredients, e);
      onToggle(false);
    }
  }));
});

const ButtonToggle = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    color,
    contrast,
    defaultButtonProps,
    disabled,
    fabProps,
    iconButtonProps,
    id,
    isOpen,
    label,
    nonWhiteColor,
    onKeyboardNavigation,
    onToggle,
    tooltip,
    type
  } = props;
  const sharedProps = {
    disabled,
    color: nonWhiteColor,
    contrast: color === 'white' ? 'high' : contrast,
    id,
    ref,
    isOpen,
    onKeyboardNavigation,
    onToggle
  };
  const toggle = (() => {
    switch (type) {
      case 'fab':
        return jsx(FabToggle, Object.assign({
          tooltip: tooltip
        }, fabProps, sharedProps));
      case 'icon':
        return jsx(IconButtonToggle, Object.assign({
          tooltip: tooltip
        }, iconButtonProps, sharedProps));
      case 'button':
        return jsx(DefaultButtonToggle, Object.assign({
          tooltip: tooltip
        }, defaultButtonProps, sharedProps));
      default:
        return null;
    }
  })();
  return toggle ? jsxs(Fragment, {
    children: [label && jsx("label", {
      htmlFor: id,
      className: "kxi-label",
      children: label
    }), jsx("div", {
      style: {
        position: 'relative'
      },
      children: toggle
    })]
  }) : null;
});

const handleInput = (disabled, _controlledState, e, setErrorState, setText, maxCharsAllowed, observer, mask) => {
  if (!disabled && !mask) {
    const newText = e.currentTarget.value;
    if (!_controlledState) {
      if (maxCharsAllowed && !maxCharsAllowed.allowOvertype) {
        if (newText.length <= maxCharsAllowed.max) {
          setText(newText);
          if (observer) {
            observer.value = newText;
          }
          return true;
        } else {
          setErrorState({
            state: 'error',
            text: maxCharsAllowed.errorMessage
          });
          return false;
        }
      } else {
        setText(newText);
        if (observer) {
          observer.value = newText;
        }
        return true;
      }
    } else {
      if (observer) {
        observer.value = newText;
      }
      return true;
    }
  } else {
    return false;
  }
};

class InputFieldProps extends ObservablePropsMixin(InputHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "placeholder", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputFieldProps.prototype, "textSize", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputFieldProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputFieldProps.prototype, "prePopulateStatus", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "inputId", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputFieldProps.prototype, "maxCharsAllowed", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputFieldProps.prototype, "popoutProps", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "type", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputFieldProps.prototype, "readOnly", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", Object)], InputFieldProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "mask", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputFieldProps.prototype, "errorOnIncompleteMask", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "maskErrorMessage", void 0);
__decorate([Bound(), __metadata("design:type", Array)], InputFieldProps.prototype, "suggestions", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], InputFieldProps.prototype, "selectAllOnFocus", void 0);
__decorate([Bound(), __metadata("design:type", Number)], InputFieldProps.prototype, "maxSuggestions", void 0);
__decorate([Bound(), __metadata("design:type", String)], InputFieldProps.prototype, "visualState", void 0);
class InputFieldEvents extends InputHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], InputFieldEvents.prototype, "onInputEnter", void 0);
__decorate([Bound(), __metadata("design:type", Function)], InputFieldEvents.prototype, "onIconClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], InputFieldEvents.prototype, "onSuggestionClick", void 0);
/**
 * Description: Standard InMoment branded InputField.
 */
const InputField = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(InputField, props);
  const inputRef = React.useRef(null);
  const suggestionRef = React.useRef(null);
  const externalSuggestionsRef = React.useRef(null);
  const {
      _blurOnEnter: blurOnEnter = true,
      _controlledState = false,
      disabled = false,
      placeholder,
      contrast = 'normal',
      textSize = 'normal',
      value,
      status,
      _hasReadonlyStyle: hasReadonlyStyle = true,
      _hasStatus: hasStatus = true,
      _iconProps: iconProps,
      id,
      inputId,
      label,
      _layered: layered = false,
      _leftIcon: leftIcon,
      width,
      maxLength,
      icon,
      onChange,
      onKeyDown,
      onInputEnter,
      onIconClick,
      onBlur,
      onSuggestionClick,
      onFocus,
      internalProps,
      prePopulateStatus = false,
      maskErrorMessage,
      _tooltip: tooltip,
      type,
      readOnly,
      className,
      style,
      observer,
      maxCharsAllowed,
      suggestions,
      maxSuggestions = 5,
      mask = '',
      localization = {},
      errorOnIncompleteMask = true,
      _showTooltipOnOverflow: showTooltipOnOverflow = false,
      visualState = 'normal',
      selectAllOnFocus = true,
      popoutProps
    } = props,
    ingredients = __rest(props, ["_blurOnEnter", "_controlledState", "disabled", "placeholder", "contrast", "textSize", "value", "status", "_hasReadonlyStyle", "_hasStatus", "_iconProps", "id", "inputId", "label", "_layered", "_leftIcon", "width", "maxLength", "icon", "onChange", "onKeyDown", "onInputEnter", "onIconClick", "onBlur", "onSuggestionClick", "onFocus", "internalProps", "prePopulateStatus", "maskErrorMessage", "_tooltip", "type", "readOnly", "className", "style", "observer", "maxCharsAllowed", "suggestions", "maxSuggestions", "mask", "localization", "errorOnIncompleteMask", "_showTooltipOnOverflow", "visualState", "selectAllOnFocus", "popoutProps"]);
  const {
    wrapper
  } = internalProps || {};
  const _a = wrapper || {},
    {
      ref: divRef
    } = _a,
    divIngredients = __rest(_a, ["ref"]);
  const validType = checkIfTextInputTypeIsValid('InputField', type);
  // using first a custom inputId for the label if specified, and otherwise defaulting to a generic identifier.
  const [finalInputId] = React.useState(inputId ? inputId : `${id ? id : uuidv4()}-input`);
  const [text, setText] = React.useState('');
  const [inputIcon, setInputIcon] = React.useState(icon);
  const [iconClick, setIconClick] = React.useState();
  const [suggestionHeight, setSuggestionHeight] = React.useState(45);
  const [localTexts, setLocalTexts] = React.useState(defaultLocalization$1);
  const outerStyle = width ? Object.assign({
    width
  }, style) : style;
  const {
    handleChange,
    maskedValue,
    setMaskedValue,
    simulateTyping,
    maskRef
  } = useMaskedInput(mask, observer);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [valueOverflows, setValueOverflows] = React.useState(false);
  const popoutRef = React.useRef(null);
  const {
    ref: resizeRef,
    width: inputWidth
  } = useResizeDetector({
    handleHeight: false,
    refreshMode: 'debounce',
    refreshRate: 100
  });
  React.useEffect(() => {
    if (mask) {
      if (type === 'number' || type === 'email') {
        devOnlyWarn(`The Kitchen: (InputField) - A masked input does not support type === ${type}, it has been changed to text.`);
      }
    }
  }, [mask, type]);
  React.useEffect(() => {
    if (type !== 'password') {
      setInputIcon(icon);
    }
  }, [icon]);
  if (mask && maxCharsAllowed) {
    maxCharsAllowed.max = mask.length;
  }
  const [errorState, setErrorState, errorStateClass] = useTextFieldStatus(mask ? maskedValue : text, 'InputField', status, maxCharsAllowed);
  useTextFieldWarning('InputField', _controlledState, onChange);
  React.useLayoutEffect(() => {
    if (type === 'password') {
      setInputIcon('visibility');
      setLocalTexts(Object.assign(Object.assign({}, localTexts), localization));
      setIconClick(() => (icon, setIcon) => {
        if (icon === 'visibility') {
          setIcon('visibility_off');
        } else {
          setIcon('visibility');
        }
      });
    }
  }, [type]);
  React.useLayoutEffect(() => {
    if (suggestionRef.current) {
      setSuggestionHeight(suggestionRef.current.offsetHeight);
    }
  }, [suggestionRef]);
  React.useEffect(() => {
    if (!_controlledState && !mask) {
      setText(value || '');
    }
  }, [value, _controlledState]);
  React.useEffect(() => {
    if (mask) {
      if (countChars(value || '') <= countChars(mask)) {
        const [newValue] = simulateTyping(value || '');
        setMaskedValue(newValue);
      } else {
        let temp = value || '';
        temp = temp.substring(0, countChars(mask));
        const [newValue] = simulateTyping(temp);
        setMaskedValue(newValue);
      }
    }
  }, [value]);
  React.useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const isOverflowing = input.scrollWidth > input.offsetWidth;
    if (valueOverflows !== isOverflowing) {
      setValueOverflows(isOverflowing);
    }
  }, [valueOverflows, value, inputWidth]);
  const handleInputFieldChange = e => {
    const fireFunction = handleInput(disabled, _controlledState, e, setErrorState, setText, maxCharsAllowed, observer, mask);
    if (mask) {
      handleChange(e, onChange, onInputEnter);
    } else if (fireFunction && onChange) {
      onChange(e);
    }
  };
  const handleIconClick = () => {
    if (onIconClick) onIconClick();
    if (iconClick) iconClick(inputIcon, setInputIcon);
  };
  const handleBlur = e => {
    if (mask && maskedValue.length && maskedValue.length !== mask.length && errorOnIncompleteMask) {
      setErrorState({
        state: 'error',
        text: maskErrorMessage
      });
    }
    if (mask && value && value !== maskedValue) {
      setMaskedValue(value);
    }
    const rt = e.relatedTarget;
    if ((externalSuggestionsRef === null || externalSuggestionsRef === void 0 ? void 0 : externalSuggestionsRef.current) && rt && !externalSuggestionsRef.current.contains(rt)) {
      setShowSuggestions(false);
    }
    if (onBlur) onBlur(e);
  };
  const inputValue = _controlledState ? value || '' : text;
  const showFirstSuggestion = inputValue !== '' && (suggestions === null || suggestions === void 0 ? void 0 : suggestions.findIndex(s => s.value === inputValue)) === -1;
  const contrastClass = contrast === 'high' ? ' kxi-high-contrast' : '';
  const getType = () => {
    if (!validType) return undefined;
    const isPasswordVisible = type === 'password' && inputIcon !== 'visibility';
    const isMaskInvalid = mask && (type === 'number' || type === 'email');
    const turnTypetoText = isPasswordVisible || isMaskInvalid;
    if (turnTypetoText) {
      return 'text';
    }
    return type;
  };
  const getMaxLength = () => {
    let max = maxLength;
    if (mask) max = mask.length;else if (maxCharsAllowed) max = maxCharsAllowed.max;
    return max;
  };
  const selectSuggestion = React.useCallback(suggestion => {
    onSuggestionClick === null || onSuggestionClick === void 0 ? void 0 : onSuggestionClick({
      suggestion
    });
    setShowSuggestions(false);
  }, [onSuggestionClick]);
  /** `<input>` element and icons */
  const inputElement = jsxs(Fragment, {
    children: [leftIcon ? jsx(XIIcon, Object.assign({
      iconSize: 24,
      contrast: contrast
    }, leftIcon, {
      className: classNames(leftIcon.className, 'kxi-input-left-icon', visualState === 'condensed' && 'kxi-condensed', layered && 'kxi-layered')
    })) : null, (inputIcon || iconProps) && jsx(XIIcon, Object.assign({
      iconSize: 24,
      contrast: contrast,
      icon: inputIcon
    }, iconProps, {
      onClick: e => {
        var _a;
        (_a = iconProps === null || iconProps === void 0 ? void 0 : iconProps.onClick) === null || _a === void 0 ? void 0 : _a.call(iconProps, e);
        handleIconClick();
      },
      className: classNames(iconProps === null || iconProps === void 0 ? void 0 : iconProps.className, 'kxi-input-icon', visualState === 'condensed' && 'kxi-condensed', layered && 'kxi-layered')
    })), jsx("input", Object.assign({
      ref: mergeRefs([inputRef, resizeRef, ref, maskRef]),
      id: finalInputId,
      className: classNames(className, 'kxi-input kxi-border', contrastClass, errorStateClass, `kxi-text-${textSize}`, {
        'kxi-layered': layered,
        'kxi-condensed': visualState === 'condensed',
        'kxi-readonly': readOnly && hasReadonlyStyle,
        'kxi-readonly-border': !hasReadonlyStyle // preserve hover behavior (see common.scss)
      }),
      disabled: disabled,
      placeholder: placeholder,
      readOnly: readOnly,
      value: mask ? maskedValue : inputValue,
      maxLength: !(maxCharsAllowed === null || maxCharsAllowed === void 0 ? void 0 : maxCharsAllowed.allowOvertype) ? getMaxLength() : undefined,
      onChange: handleInputFieldChange,
      onBlur: handleBlur,
      "data-testid": "inputField",
      type: getType()
    }, ingredients, {
      onFocus: e => {
        var _a;
        if (selectAllOnFocus) {
          (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.select();
        }
        setShowSuggestions(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
      },
      onKeyDown: e => {
        var _a;
        const {
          key
        } = e;
        if (suggestions && suggestions.length > 0) {
          if (showSuggestions) {
            if (key === 'ArrowDown') {
              focusOnNthTabbableElement(externalSuggestionsRef.current, 0);
              e.preventDefault();
              return;
            } else if (key === 'Escape') {
              setShowSuggestions(false);
              e.stopPropagation();
              e.preventDefault();
              return;
            }
          } else {
            if (key !== 'Escape' && key !== 'Tab') {
              setShowSuggestions(true);
              return;
            }
          }
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        if (key === 'Enter' && !disabled) {
          onInputEnter === null || onInputEnter === void 0 ? void 0 : onInputEnter({
            text: type === 'number' ? +text : text
          });
          if (blurOnEnter) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
          }
        }
      }
    }))]
  });
  const isTooltipEnabled = (tooltip === null || tooltip === void 0 ? void 0 : tooltip.enabled) !== false;
  const overflowTooltipText = valueOverflows && showTooltipOnOverflow ? jsx("p", {
    className: "kxi-input-field-tooltip-value",
    children: value
  }) : undefined;
  const tooltipPropText = tooltip && isTooltipEnabled && !!tooltip.text ? typeof tooltip.text === 'string' ? jsx("p", {
    className: "kxi-input-field-tooltip-input",
    dangerouslySetInnerHTML: {
      __html: sanitizeHtmlString(tooltip.text)
    }
  }) : jsx("p", {
    className: "kxi-input-field-tooltip-input",
    children: tooltip.text
  }) : undefined;
  const tooltipText = overflowTooltipText || tooltipPropText ? jsxs("div", {
    className: "kxi-input-field-tooltip-text",
    children: [overflowTooltipText, tooltipPropText]
  }) : undefined;
  const hasTooltip = tooltipText !== undefined || (tooltip === null || tooltip === void 0 ? void 0 : tooltip.title) !== undefined;
  return jsxs("div", Object.assign({
    "aria-label": value || placeholder,
    className: `kxi-input-outer-wrapper orca ${visualState === 'condensed' ? 'kxi-condensed' : ''}`,
    style: outerStyle
  }, divIngredients, debugService.componentProps, {
    ref: divRef,
    children: [label ? jsx("label", {
      className: classNames(`kxi-label kxi-${contrast}-contrast`, layered && 'kxi-layered'),
      htmlFor: finalInputId,
      children: label
    }) : null, jsx(ToolTipWrapper, Object.assign({}, tooltip, {
      enabled: hasTooltip && isTooltipEnabled,
      text: tooltipText,
      stretch: true,
      children: inputElement
    })), jsx(Popout, Object.assign({
      direction: 7,
      edgeClearance: 0,
      stretch: true,
      onFocusOut: () => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      }
    }, popoutProps, {
      preventFocus: true,
      allowScroll: true,
      isVisible: showSuggestions && suggestions && suggestions.length > 0,
      anchorElement: inputRef.current,
      trapFocus: false,
      ref: popoutRef,
      children: jsxs("div", {
        className: "kxi-input-suggestions",
        style: {
          width: width,
          maxHeight: maxSuggestions * suggestionHeight
        },
        ref: externalSuggestionsRef,
        children: [showFirstSuggestion && jsx("div", {
          className: "kxi-input-suggestion kxi-text-600",
          tabIndex: 0,
          onClick: e => {
            e.stopPropagation();
            setShowSuggestions(false);
          },
          onKeyDown: e => {
            switch (e.key) {
              case 'Enter':
              case ' ':
                setShowSuggestions(false);
                break;
              case 'ArrowDown':
                e.preventDefault();
                focusOnNthTabbableElement(externalSuggestionsRef.current, 1);
                break;
              case 'ArrowUp':
                e.preventDefault();
                focusOnNthTabbableElement(externalSuggestionsRef.current, -1);
                break;
            }
          },
          children: inputValue
        }, "raw-text"), suggestions === null || suggestions === void 0 ? void 0 : suggestions.map((s, i, arr) => jsx("div", {
          ref: s.value ? suggestionRef : null,
          className: "kxi-input-suggestion",
          tabIndex: 0,
          onClick: e => {
            e.stopPropagation();
            selectSuggestion(s);
          },
          onKeyDown: e => {
            var _a, _b;
            switch (e.key) {
              case 'Enter':
              case ' ':
                e.preventDefault();
                selectSuggestion(s);
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                break;
              case 'ArrowDown':
                e.preventDefault();
                focusOnNthTabbableElement(externalSuggestionsRef.current, i + 1 + (showFirstSuggestion ? 1 : 0));
                break;
              case 'ArrowUp':
                e.preventDefault();
                focusOnNthTabbableElement(externalSuggestionsRef.current, i - 1 + (showFirstSuggestion ? 1 : 0));
                break;
              case 'Tab':
                if (i === arr.length - 1) {
                  e.preventDefault();
                  (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                }
                break;
            }
          },
          children: jsx("span", {
            children: s.value && inputValue ? boldSearchText(s.value, inputValue) : s.value
          })
        }, s.key))]
      })
    })), hasStatus ? jsx(ErrorStateUI, {
      prePopulateStatus: prePopulateStatus,
      errorStateClass: errorStateClass,
      contrastClass: contrastClass,
      errorState: errorState,
      maxCharsAllowed: maxCharsAllowed,
      disabled: disabled,
      text: mask ? maskedValue : text
    }) : null]
  }));
});
InputField.displayName = 'InputField';

/**
 * A wrapper around {@link InputField} that accepts and formats an array-valued `value`.
 */
const ArrayValuedInputField = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const {
      value = '',
      localization
    } = props,
    ingredients = __rest(props, ["value", "localization"]);
  const _a = localization !== null && localization !== void 0 ? localization : {},
    {
      selected = 'Selected',
      more = 'more'
    } = _a,
    inputFieldLocalization = __rest(_a, ["selected", "more"]);
  const arrayValue = React__default.useMemo(() => typeof value === 'string' ? [value] : value, [value]);
  const inputRef = React__default.useRef(null);
  const {
    ref: resizeRef,
    width
  } = useResizeDetector({
    handleHeight: false,
    refreshMode: 'debounce',
    refreshRate: 100
  });
  const [previewValue, setPreviewValue] = useComputedState(() => arrayValue.length === 0 ? '' : arrayValue.length === 1 ? arrayValue[0] : '', [arrayValue]);
  const countSelectedText = `${arrayValue.length} ${selected}`;
  React__default.useEffect(() => {
    const input = inputRef.current;
    if (!input || arrayValue.length <= 1) {
      return;
    }
    const originalValue = input.value;
    /**
     * Finds the longest (# items shown) representation of the selected objects that fits in our available space.
     *
     * Note that this function mutates the `value` of the `<input>` element so that we don't have to call
     *     {@link setPreviewValue} until we have a value that works.
     *
     * For example, a {@link selectedTitlesArray} of
     *     `['Item 0', 'Item 1', 'Item 2', 'Item 3']`
     *     will result in one of the following values being displayed in the input:
     * - Item 0, Item 1, Item 2, Item 3
     * - Item 0, Item 1, Item 2 (+1 more)
     * - Item 0, Item 1 (+2 more)
     * - Item 0 (+3 more)
     * - 4 selected
     */
    const getPreviewText = (values, hiddenCount = 0) => {
      const previewText = values.join(', ');
      const originalValue = input.value;
      let newValue = hiddenCount > 0 ? `${previewText} (+${hiddenCount} ${more})` : previewText;
      input.value = newValue;
      // Doesn't fit
      if (input.scrollWidth > input.offsetWidth) {
        if (values.length > 1) {
          // Hide the last item and try again
          newValue = getPreviewText(values.slice(0, values.length - 1), hiddenCount + 1);
        } else {
          // If we're on the last item and it doesn't fit, just show the '# selected' text.
          newValue = countSelectedText;
        }
      }
      input.value = originalValue;
      return newValue;
    };
    const newValue = (() => {
      /**
       * It's unlikely that more than `maxDisplayedItems` selected titles will fit in the input field width,
       *     so start with all but the first `maxDisplayedItems` items hidden.
       *     This keeps us from potentially having to iterate over large numbers of selected items.
       *
       * Example if `maxDisplayedItems` were 2:
       *
       * ```typescript
       * const values = ['1', '2', '3', ..., '99', '100'];
       * // truncate to 2 and hide the rest
       * const previewValues = ['0', '1'];
       * const hiddenCount = 98;
       * ```
       */
      const maxDisplayedItems = 5; // This number is arbitrarily chosen
      if (arrayValue.length <= maxDisplayedItems) {
        return getPreviewText(arrayValue);
      }
      const titles = arrayValue.slice(0, maxDisplayedItems);
      const hiddenCount = arrayValue.length - maxDisplayedItems;
      return getPreviewText(titles, hiddenCount);
    })();
    if (originalValue !== newValue) {
      setPreviewValue(newValue);
    }
  }, [countSelectedText, more, arrayValue, width, setPreviewValue]);
  return jsx(InputField, Object.assign({}, ingredients, {
    localization: inputFieldLocalization,
    ref: mergeRefs([ref, inputRef, resizeRef]),
    value: previewValue,
    _showTooltipOnOverflow: true
  }));
});

const InputFieldToggle = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(InputFieldToggle, props);
  const {
      canSearch,
      className,
      color,
      disabled,
      dropdownType,
      isOpen,
      localization,
      onChange: onChangeInput,
      onClick: onClickInput,
      onKeyboardNavigation,
      onKeyDown: onKeyDownInput,
      onToggle,
      query,
      readOnly = false,
      selectedIcon,
      onSearch,
      stretch,
      tooltip,
      value: valueInput = []
    } = props,
    ingredients = __rest(props, ["canSearch", "className", "color", "disabled", "dropdownType", "isOpen", "localization", "onChange", "onClick", "onKeyboardNavigation", "onKeyDown", "onToggle", "query", "readOnly", "selectedIcon", "onSearch", "stretch", "tooltip", "value"]);
  const arrayValueInput = typeof valueInput === 'string' ? [valueInput] : valueInput;
  const value = canSearch ? query !== null && query !== void 0 ? query : arrayValueInput : arrayValueInput;
  return jsx(ArrayValuedInputField, Object.assign({
    "data-testid": "kxi-input-field"
  }, debugService.componentProps, ingredients, {
    ref: ref,
    _controlledState: true,
    _blurOnEnter: false,
    _hasStatus: false,
    _layered: dropdownType === 'layered',
    _leftIcon: arrayValueInput.length > 0 && selectedIcon && dropdownType !== 'layered' ? {
      icon: selectedIcon,
      className: classNames('kxi-selected-icon', {
        'kxi-selected-icon-disabled': disabled,
        'kxi-readonly': readOnly
      }),
      onClick: () => onToggle(false)
    } : undefined,
    _iconProps: {
      icon: 'single_arrow_down',
      className: classNames(`kxi-arrow-${isOpen ? 'open' : 'closed'}`, {
        'kxi-arrow-disabled': disabled,
        'kxi-readonly': readOnly
      }),
      onClick: () => onToggle(false)
    },
    _hasReadonlyStyle: readOnly,
    _tooltip: Object.assign(Object.assign({
      'data-testid': 'kxi-input-field-tooltip'
    }, tooltip), {
      stretch
    }),
    className: classNames(className, color === 'white' && 'kxi-white', 'kxi-border'),
    disabled: disabled,
    localization: localization,
    readOnly: readOnly || !canSearch,
    type: "text",
    value: value,
    onChange: e => {
      onChangeInput === null || onChangeInput === void 0 ? void 0 : onChangeInput(e);
      onSearch(e.target.value);
    },
    onClick: e => {
      onClickInput === null || onClickInput === void 0 ? void 0 : onClickInput(e);
      onToggle(false);
    },
    onKeyDown: e => {
      onKeyDownInput === null || onKeyDownInput === void 0 ? void 0 : onKeyDownInput(e);
      if (readOnly || disabled) {
        return;
      }
      // Slightly modified from https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            onToggle(true);
          } else {
            onKeyboardNavigation({
              target: 'ExitNext',
              event: e
            });
          }
          break;
        case 'Escape':
          onKeyboardNavigation({
            target: 'Escape',
            event: e
          });
          break;
        case 'Enter':
          onToggle(true);
          break;
        case 'Tab':
          onKeyboardNavigation({
            target: e.shiftKey ? 'ExitPrevious' : 'ExitNext',
            event: e
          });
          break;
      }
    }
  }));
});
InputFieldToggle.displayName = 'DropdownInputFieldToggle';

const Control = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a;
  const {
      buttonToggleRef,
      canSearchInputToggle,
      color,
      contrast,
      defaultButtonProps,
      disabled,
      fabProps,
      iconButtonProps,
      id
    } = props,
    _b = props.inputFieldProps,
    {
      internalProps: inputFieldInternalPropsInput
    } = _b,
    inputFieldProps = __rest(_b, ["internalProps"]),
    {
      inputFieldToggleRef,
      isOpen,
      label,
      nonWhiteColor,
      onKeyboardNavigation,
      onToggle,
      query,
      selectedIcon,
      onSearch,
      stretch,
      textSize,
      tooltip: tooltipInput,
      type
    } = props;
  const tooltip = Object.assign(Object.assign({}, tooltipInput), {
    enabled: (tooltipInput === null || tooltipInput === void 0 ? void 0 : tooltipInput.enabled) !== false && !isOpen
  });
  const [toggleElement, setToggleElement] = React.useState(null);
  const buttonRef = React.useRef(null);
  const inputFieldWrapperRef = React.useRef(null);
  const inputRef = React.useRef(null);
  /**
   * {@link toggleElement} is stored in state so that we update our parent ref.
   */
  React.useImperativeHandle(ref, () => ({
    current: toggleElement,
    contains: other => {
      var _a, _b, _c;
      return (_c = (_b = (_a = buttonRef.current) !== null && _a !== void 0 ? _a : inputFieldWrapperRef.current) === null || _b === void 0 ? void 0 : _b.contains(other)) !== null && _c !== void 0 ? _c : false;
    },
    focus: (...args) => {
      var _a, _b;
      return (_b = (_a = inputRef.current) !== null && _a !== void 0 ? _a : buttonRef.current) === null || _b === void 0 ? void 0 : _b.focus(...args);
    }
  }), [toggleElement]);
  switch (type) {
    case 'fab':
    case 'icon':
    case 'button':
      return jsx(ButtonToggle, {
        ref: mergeRefs(buttonToggleRef ? [buttonToggleRef, buttonRef, setToggleElement] : [buttonRef, setToggleElement]),
        color: color,
        contrast: contrast,
        defaultButtonProps: defaultButtonProps,
        disabled: disabled,
        fabProps: fabProps,
        iconButtonProps: iconButtonProps,
        id: id,
        isOpen: isOpen,
        label: label,
        nonWhiteColor: nonWhiteColor,
        onKeyboardNavigation: onKeyboardNavigation,
        onToggle: onToggle,
        tooltip: tooltip,
        type: type
      });
    default:
      return jsx(InputFieldToggle, Object.assign({}, inputFieldProps, {
        ref: mergeRefs(inputFieldToggleRef ? [inputFieldToggleRef, inputRef, setToggleElement] : [inputRef, setToggleElement]),
        canSearch: canSearchInputToggle,
        color: color,
        contrast: contrast,
        id: id,
        internalProps: Object.assign(Object.assign({}, inputFieldInternalPropsInput), {
          wrapper: Object.assign(Object.assign({}, inputFieldInternalPropsInput === null || inputFieldInternalPropsInput === void 0 ? void 0 : inputFieldInternalPropsInput.wrapper), {
            ref: ((_a = inputFieldInternalPropsInput === null || inputFieldInternalPropsInput === void 0 ? void 0 : inputFieldInternalPropsInput.wrapper) === null || _a === void 0 ? void 0 : _a.ref) ? mergeRefs([inputFieldInternalPropsInput.wrapper.ref, inputFieldWrapperRef]) : inputFieldWrapperRef
          })
        }),
        isOpen: isOpen,
        label: label,
        onKeyboardNavigation: onKeyboardNavigation,
        onToggle: onToggle,
        query: query,
        selectedIcon: selectedIcon,
        onSearch: onSearch,
        stretch: stretch,
        textSize: textSize,
        tooltip: tooltip,
        dropdownType: type
      }));
  }
});
Control.displayName = 'DropdownToggle';

/**
 * The component's inputs. Only `@Bound()` decorated properties are available in web components.
 */
class DropdownProps extends ObservablePropsMixin(InputHTMLAttributes) {
  constructor(props = {}) {
    super();
    for (const key in props) {
      if (key in this) {
        this[key] = props[key];
      }
    }
  }
}
__decorate([Bound(), type($optional($nullable($array($implements(DropdownObject))))), __metadata("design:type", Object)], DropdownProps.prototype, "data", void 0);
__decorate([Bound(), type($optional($union($string(), $array($string())))), __metadata("design:type", Object)], DropdownProps.prototype, "value", void 0);
__decorate([Bound(), type($optional($array($implements(DropdownGroup)))), __metadata("design:type", Array)], DropdownProps.prototype, "groups", void 0);
__decorate([Bound(), type($optional($in(_TextSizesOptions))), __metadata("design:type", Object)], DropdownProps.prototype, "textSize", void 0);
__decorate([Bound(), type($optional($implements(DropdownButtons))), __metadata("design:type", DropdownButtons)], DropdownProps.prototype, "buttons", void 0);
__decorate([Bound(), type($optional($union($in(_ColorsOptions), $in(_WhiteOptions)))), __metadata("design:type", String)], DropdownProps.prototype, "color", void 0);
__decorate([Bound(), type($optional($nullable($union($instanceof(HTMLElement), $array($nullable($instanceof(HTMLElement))))))), __metadata("design:type", Object)], DropdownProps.prototype, "elementsToDisableScroll", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], DropdownProps.prototype, "isFetchingData", void 0);
__decorate([Bound(), type($optional($nullable($implements(ErrorObject)))), __metadata("design:type", Object)], DropdownProps.prototype, "status", void 0);
__decorate([Bound(), type($optional($string())), __metadata("design:type", String)], DropdownProps.prototype, "label", void 0);
__decorate([Bound(), type($optional($in(_ClockDirectionOptions))), __metadata("design:type", Object)], DropdownProps.prototype, "direction", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], DropdownProps.prototype, "disabled", void 0);
__decorate([Bound(), type($optional($string())), __metadata("design:type", String)], DropdownProps.prototype, "placeholder", void 0);
__decorate([Bound(), type($optional($in(_DropdownVisualStateOptions))), __metadata("design:type", String)], DropdownProps.prototype, "visualState", void 0);
__decorate([Bound(), type($optional($in(_DropdownSelectsOptions))), __metadata("design:type", String)], DropdownProps.prototype, "select", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], DropdownProps.prototype, "stretch", void 0);
__decorate([Bound(), type($optional($in(_DropdownButtonTypesOptions))), __metadata("design:type", String)], DropdownProps.prototype, "type", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], DropdownProps.prototype, "fullWidthMobileMenu", void 0);
__decorate([Bound(), type($optional($in(_ContrastsOptions))), __metadata("design:type", String)], DropdownProps.prototype, "contrast", void 0);
__decorate([Bound(), type($optional($implements(DropdownTextObject))), __metadata("design:type", DropdownTextObject)], DropdownProps.prototype, "localization", void 0);
__decorate([Bound(), type($optional($boolean())), __metadata("design:type", Boolean)], DropdownProps.prototype, "inMenuSearch", void 0);
__decorate([Bound(), type($optional($implements(InternalDropdownProps))), __metadata("design:type", InternalDropdownProps)], DropdownProps.prototype, "internalProps", void 0);
__decorate([Bound(), type($optional($integer(0))), __metadata("design:type", Number)], DropdownProps.prototype, "itemSearchCount", void 0);
__decorate([Bound(), type($optional($unknown())), __metadata("design:type", Object)], DropdownProps.prototype, "inMenuSearchProps", void 0);
__decorate([Bound(), type($optional($union($equals(true), $in(_IconsOptions)))), __metadata("design:type", Object)], DropdownProps.prototype, "selectedIcon", void 0);
__decorate([Bound(), type($optional($implements(ToolTipProps))), __metadata("design:type", ToolTipProps)], DropdownProps.prototype, "tooltip", void 0);
__decorate([Bound(), type($optional($integer())), __metadata("design:type", Number)], DropdownProps.prototype, "endOfScrollOffset", void 0);
/**
 * The component's outputs. In Angular, each property is equivalent to `@Output()` decorated properties.
 */
class DropdownEvents extends InputHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], DropdownEvents.prototype, "onValueChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DropdownEvents.prototype, "onOpenChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DropdownEvents.prototype, "onEndOfScroll", void 0);
__decorate([Bound(), __metadata("design:type", Function)], DropdownEvents.prototype, "onSearch", void 0);
/**
 * Description: One stop shop for all your dropdown needs.
 */
const Dropdown = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a, _b, _c, _d, _e;
  const debugService = useDebugService(Dropdown, props);
  const [defaultData] = React.useState(() => Object.freeze([]));
  const [defaultGroups] = React.useState(() => Object.freeze([]));
  const [defaultInternalProps] = React.useState(() => Object.freeze({}));
  const [defaultId] = React.useState(debugService.id);
  const _f = Object.assign(Object.assign({}, props), new DropdownProps(props)),
    {
      observer: observerInput,
      value,
      onValueChange: onValueChangeInput,
      'data-qa': dataQa,
      'data-testid': dataTestId,
      buttons,
      color = 'CX',
      contrast = 'normal',
      data: dataInput,
      direction = 'bottom-left',
      elementsToDisableScroll: _elementsToDisableScroll = document.body,
      endOfScrollOffset = 0,
      fullWidthMobileMenu = true,
      groups = defaultGroups,
      id = defaultId,
      inMenuSearch = false,
      inMenuSearchProps,
      internalProps = defaultInternalProps,
      isFetchingData = false,
      itemSearchCount = 7,
      label,
      localization = defaultLocalization,
      _menuRef: menuRefInput,
      onEndOfScroll,
      onOpenChange: onOpenChangeInput,
      onSearch: onSearchInput,
      select = 'single',
      selectedIcon: selectedIconInput = true,
      status,
      stretch: stretchInput = false,
      textSize = 'normal',
      tooltip,
      type = 'pill'
    } = _f,
    inputToggleIngredients = __rest(_f, ["observer", "value", "onValueChange", 'data-qa', 'data-testid', "buttons", "color", "contrast", "data", "direction", "elementsToDisableScroll", "endOfScrollOffset", "fullWidthMobileMenu", "groups", "id", "inMenuSearch", "inMenuSearchProps", "internalProps", "isFetchingData", "itemSearchCount", "label", "localization", "_menuRef", "onEndOfScroll", "onOpenChange", "onSearch", "select", "selectedIcon", "status", "stretch", "textSize", "tooltip", "type"]);
  const {
    className: inputToggleClassName,
    disabled: inputToggleDisabled,
    readOnly: inputToggleReadonly,
    tabIndex: inputToggleTabIndex
  } = inputToggleIngredients;
  const menuRef = React.useRef(null);
  const toggleRef = React.useRef(null);
  const canSelectNone = select === 'none';
  const canSelectMultiple = checkIfMultiple(select);
  const observer = observerInput;
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState(undefined);
  const [toggleElement, setToggleElement] = React.useState(null);
  const isMobile = useIsMobile();
  const [errorState,, errorStateClass] = useTextFieldStatus('', 'Dropdown', status);
  const unvalidatedData = dataInput && dataInput.length > 0 ? dataInput : defaultData;
  const data = React.useMemo(() => filterValidData(unvalidatedData, debugService), [unvalidatedData, debugService]);
  useWarnIsDeprecated('Dropdown', {
    elementsToDisableScroll: {
      value: props.elementsToDisableScroll,
      message: 'elementsToDisableScroll is no longer required to prevent scrolling. You can safely remove this prop. In a future major version, use of this prop will throw an exception.'
    }
  }, 'prop');
  const dropdownObjectMap = React.useMemo(() => {
    const map = new Map();
    forEachDropdownObject(data, dropdownObject => {
      const {
        id
      } = dropdownObject;
      if (map.has(id)) {
        debugService.error(`DropdownObject with duplicate ID provided: ${id}`, 'This will throw an exception in a future major version.');
        return;
      }
      map.set(dropdownObject.id, dropdownObject);
    });
    return map;
  }, [data, debugService]);
  /** DropdownObjects selected via the deprecated {@link DropdownObject.isSelected isSelected} prop. */
  const deprecatedSelectedIds = React.useMemo(() => {
    if (canSelectNone) {
      return new Set();
    }
    const deprecatedSelectedIds = new Set();
    forEachDropdownObject(data, item => item.isSelected && deprecatedSelectedIds.add(item.id));
    return deprecatedSelectedIds;
  }, [canSelectNone, data]);
  useWarnOnceIf(deprecatedSelectedIds.size > 0, debugService.format(['`DropdownObject.isSelected` was provided on one or more dropdown objects.', 'This has been deprecated and will be removed in a future major version.', 'Use `DropdownProps.value`.'].join(' ')));
  /** DropdownObjects selected via {@link value}. */
  const valueSelectedIds = React.useMemo(() => {
    if (canSelectNone) {
      return new Set();
    }
    const idsArray = typeof value === 'string' ? [value] : value !== null && value !== void 0 ? value : [];
    const validIdsArray = idsArray.filter(id => {
      const dropdownObject = dropdownObjectMap.get(id);
      if (dropdownObject === undefined) {
        debugService.error(`Non-existent DropdownObject specified in value with id ${id}`);
        return false;
      }
      return true;
    });
    return new Set(canSelectMultiple ? validIdsArray : validIdsArray.length > 0 ? [validIdsArray[0]] : undefined);
  }, [canSelectNone, debugService, dropdownObjectMap, canSelectMultiple, value]);
  useErrorOnceIf(deprecatedSelectedIds.size > 0 && valueSelectedIds.size > 0, debugService.format('Dropdown selections may be set with either `data[].isSelected` or `value`, but not both. `value` will be used.'));
  /** Prefer {@link value} prop over {@link DropdownObject.isSelected}. */
  const selectedIdsInput = value !== undefined ? valueSelectedIds : deprecatedSelectedIds;
  const [selectedIds, setSelectedIds] = useValueBackedState(selectedIdsInput);
  const selectedItems = new Set(Array.from(selectedIds).map(id => dropdownObjectMap.get(id)).filter(isNotNullish));
  const selectedItemsArray = Array.from(selectedItems);
  const selectedTitlesArray = selectedItemsArray.map(({
    title
  }) => title);
  const searchedData = React.useMemo(() => query ? data.filter(d => d.title.toLowerCase().includes(query.toLowerCase())) : data, [data, query]);
  const selectableIds = React.useMemo(() => {
    const selectableIds = new Set();
    forEachDropdownObject(searchedData, dropdownObject => isSelectable(dropdownObject) && selectableIds.add(dropdownObject.id));
    return selectableIds;
  }, [searchedData]);
  const _menuItems = React.useMemo(() => {
    var _a;
    return getDropdownMenuItems({
      data: searchedData,
      groups,
      select,
      selectAll: (_a = localization.selectAll) !== null && _a !== void 0 ? _a : defaultLocalization.selectAll,
      debugService
    });
  }, [debugService, groups, localization.selectAll, searchedData, select]);
  const menuItems = React.useMemo(() => {
    if (!isFetchingData) {
      return _menuItems;
    }
    return _menuItems.concat([{
      id: `${id}-loading-indicator`,
      disabled: false,
      isClickable: false,
      isTabbable: true,
      menuItem: {
        isLoadingIndicator: true,
        id: `${id}-loading-indicator`,
        title: ''
      }
    }]);
  }, [_menuItems, id, isFetchingData]);
  const stretch = type === 'fab' || type === 'icon' ? false : stretchInput;
  useWarnOnceIf(stretchInput && (type === 'fab' || type === 'icon'), debugService.format('The stretch prop is not supported for `fab` and `icon` type dropdowns. `false` will be used'));
  const nonWhiteColor = color === 'white' ? 'orca' : color;
  const canSearchInputToggle = !inMenuSearch && data.length >= itemSearchCount;
  const countSelectedText = `${selectedIds.size} ${(_a = localization.selected) !== null && _a !== void 0 ? _a : defaultLocalization.selected}`;
  const selectedIcon = (() => {
    if (selectedIconInput === true) {
      if (select === 'single' && selectedItemsArray.length > 0) {
        const firstSelectedItem = selectedItemsArray[0];
        if (firstSelectedItem && isIcons(firstSelectedItem.visual)) {
          return firstSelectedItem.visual;
        }
      }
      return undefined;
    } else {
      return selectedIconInput;
    }
  })();
  const {
    wrapper: wrapperIngredients,
    menu: menuIngredients,
    fab: fabProps,
    iconButton: iconButtonProps,
    defaultButton: defaultButtonProps
  } = internalProps;
  const onOpenChange = (isOpen, withKeyboard) => {
    var _a, _b;
    onOpenChangeInput === null || onOpenChangeInput === void 0 ? void 0 : onOpenChangeInput({
      isOpen
    });
    if (withKeyboard) {
      ReactDOM__default.flushSync(() => setIsOpen(isOpen));
      if (withKeyboard) {
        if (isOpen) {
          (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        } else {
          (_b = toggleRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
      }
    } else {
      setIsOpen(isOpen);
    }
  };
  const onSearch = newQuery => {
    if (newQuery === query) {
      return;
    }
    onSearchInput === null || onSearchInput === void 0 ? void 0 : onSearchInput({
      query: newQuery
    });
    setQuery(newQuery);
  };
  const clearSearch = () => onSearch(undefined);
  const onClose = withKeyboard => onOpenChange(false, withKeyboard);
  const onClearSearchAndClose = withKeyboard => {
    onClose(withKeyboard);
    clearSearch();
  };
  const onSelectedIdsChange = ids => {
    clearSearch();
    if (areEqual(selectedIdsInput, ids)) {
      return;
    }
    const idsArray = Array.from(ids);
    const itemsArray = idsArray.map(id => dropdownObjectMap.get(id)).filter(isNotNullish);
    const idValue = canSelectMultiple ? idsArray : idsArray.length > 0 ? idsArray[0] : undefined;
    const itemsValue = canSelectMultiple ? itemsArray : itemsArray.length > 0 ? itemsArray[0] : undefined;
    onValueChangeInput === null || onValueChangeInput === void 0 ? void 0 : onValueChangeInput({
      selected: itemsValue !== null && itemsValue !== void 0 ? itemsValue : []
    });
    if (observer) {
      observer.value = idValue;
    }
  };
  const onCancel = withKeyboard => {
    setSelectedIds(selectedIdsInput);
    onClose(withKeyboard);
  };
  const onConfirm = (selectedIds, withKeyboard) => {
    onSelectedIdsChange(selectedIds);
    onClearSearchAndClose(withKeyboard);
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, wrapperIngredients, {
    className: classNames('kxi-dropdown', wrapperIngredients === null || wrapperIngredients === void 0 ? void 0 : wrapperIngredients.className, {
      'kxi-dropdown-inline': type === 'icon' && !label
    }),
    style: Object.assign(Object.assign({}, wrapperIngredients === null || wrapperIngredients === void 0 ? void 0 : wrapperIngredients.style), {
      width: stretch ? '100%' : (_b = wrapperIngredients === null || wrapperIngredients === void 0 ? void 0 : wrapperIngredients.style) === null || _b === void 0 ? void 0 : _b.width
    }),
    "data-qa": dataQa,
    "data-testid": dataTestId,
    "data-isopen": isOpen ? 'true' : undefined,
    children: [jsx(Control, {
      ref: ref => {
        var _a;
        toggleRef.current = ref;
        setToggleElement((_a = ref === null || ref === void 0 ? void 0 : ref.current) !== null && _a !== void 0 ? _a : null);
      },
      inputFieldToggleRef: ref,
      canSearchInputToggle: canSearchInputToggle,
      color: color,
      contrast: contrast,
      disabled: inputToggleDisabled,
      defaultButtonProps: defaultButtonProps,
      id: id,
      fabProps: fabProps,
      iconButtonProps: iconButtonProps,
      inputFieldProps: Object.assign(Object.assign({}, inputToggleIngredients), {
        className: classNames(inputToggleClassName, errorStateClass),
        value: selectedTitlesArray,
        localization: {
          more: (_c = localization.more) !== null && _c !== void 0 ? _c : defaultLocalization.more,
          selected: (_d = localization.selected) !== null && _d !== void 0 ? _d : defaultLocalization.selected
        }
      }),
      isOpen: isOpen,
      label: label,
      nonWhiteColor: nonWhiteColor,
      onKeyboardNavigation: ({
        target,
        event
      }) => {
        var _a;
        event.stopPropagation();
        if (target === 'ExitNext') {
          if (isOpen) {
            if ((_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.focus()) {
              event.preventDefault();
            }
          } else {
            clearSearch();
            onCancel(true);
          }
        } else {
          // For ExitPrevious, let the default behavior happen so that focus goes to the previous focusable item on the page
          clearSearch();
          onCancel(true);
        }
      },
      onToggle: withKeyboard => {
        if (!inputToggleDisabled && !inputToggleReadonly) {
          onOpenChange(!isOpen, withKeyboard);
        }
      },
      query: query,
      selectedIcon: selectedIcon,
      onSearch: newQuery => {
        onSearch(newQuery);
        onOpenChange(true, false);
      },
      stretch: stretch,
      textSize: textSize,
      tooltip: tooltip,
      type: type
    }), jsx(Menu, {
      ref: newMenuRef => {
        var _a;
        menuRef.current = newMenuRef;
        if (menuRefInput) {
          menuRefInput.current = (_a = newMenuRef === null || newMenuRef === void 0 ? void 0 : newMenuRef.ref.current) !== null && _a !== void 0 ? _a : null;
        }
      },
      buttons: buttons,
      countSelectedText: countSelectedText,
      direction: direction,
      endOfScrollOffset: endOfScrollOffset,
      inMenuSearch: inMenuSearch,
      inMenuSearchProps: inMenuSearchProps,
      isOpen: isOpen,
      localization: localization,
      menuIngredients: Object.assign(Object.assign({}, menuIngredients), {
        id: (_e = menuIngredients === null || menuIngredients === void 0 ? void 0 : menuIngredients.id) !== null && _e !== void 0 ? _e : `${id}-dropdown-menu`,
        style: Object.assign({
          minWidth: toggleElement === null || toggleElement === void 0 ? void 0 : toggleElement.clientWidth
        }, isMobile && !fullWidthMobileMenu && {
          maxWidth: toggleElement === null || toggleElement === void 0 ? void 0 : toggleElement.clientWidth
        })
      }),
      menuItems: menuItems,
      nonWhiteColor: nonWhiteColor,
      onCancel: onCancel,
      onConfirm: onConfirm,
      onEndOfScroll: onEndOfScroll,
      onExternalClick: e => {
        var _a;
        if (!((_a = toggleRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.event.target))) {
          if (areEqual(selectedIdsInput, selectedIds)) {
            onCancel(false);
          } else {
            onConfirm(selectedIds, false);
          }
        }
      },
      onKeyboardNavigation: ({
        target,
        event
      }) => {
        var _a;
        event.stopPropagation();
        (_a = toggleRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        if (target === 'ExitPrevious') {
          event.preventDefault();
        } else {
          // For ExitNext, let the default behavior happen so that focus goes to the next focusable item on the page
          clearSearch();
          onCancel(true);
        }
      },
      onSearch: onSearch,
      popoutAnchorElement: toggleElement,
      query: query,
      select: select,
      selectableIds: selectableIds,
      selectedIds: selectedIds,
      onMultiSelectIdsChange: setSelectedIds,
      tabIndex: inputToggleTabIndex
    }), jsx("div", {
      className: "kxi-dropdown-status",
      children: jsx(ErrorStateUI, {
        errorStateClass: errorStateClass,
        errorState: errorState,
        prePopulateStatus: false,
        contrastClass: contrast === 'high' ? ' kxi-high-contrast' : '',
        disabled: inputToggleDisabled || false
      })
    })]
  }));
});
Dropdown.displayName = 'Dropdown';

const Header$1 = props => {
  const {
    children,
    dropdownProps,
    onValueChange,
    semiboldTitle,
    subtitle,
    title,
    titleColor,
    titleIconProps,
    titleSeparator
  } = props;
  const hasDropdown = dropdownProps !== undefined && dropdownProps.data != null && dropdownProps.data.length > 0;
  const {
    hasChildNodes: headerAreaHasContent,
    ref: headerAreaRef
  } = useHasChildNodes_ref();
  const {
    hasChildNodes: actionAreaHasContent,
    ref: actionAreaRef
  } = useHasChildNodes_ref();
  const titleArea = title || titleIconProps || subtitle ? jsxs("div", {
    className: "kxi-basic-tile-header-title-area",
    children: [(title || titleIconProps) && jsxs("div", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 6
      },
      children: [titleIconProps && jsx(XIIcon, Object.assign({}, titleIconProps)), title && jsx("h5", {
        className: `${normalizeColor(titleColor)} ${semiboldTitle ? 'kxi-text-600' : ''}`,
        children: title
      })]
    }), subtitle && jsx("h6", {
      className: "kxi-grey kxi-text-small",
      children: subtitle
    })]
  }) : null;
  const titleAreaHasContent = titleArea !== null;
  const headerArea = jsx("div", {
    ref: headerAreaRef,
    className: "kxi-basic-tile-header-header-area",
    children: jsx(Slot, {
      name: "header",
      reactChildren: children
    })
  });
  const actionArea = jsxs("div", {
    ref: actionAreaRef,
    className: "kxi-basic-tile-header-action-area",
    children: [jsx(Slot, {
      name: "action",
      reactChildren: children
    }), hasDropdown ? jsx("div", {
      className: "kxi-basic-tile-kabob",
      onClick: e => e.stopPropagation(),
      children: jsx(Dropdown, Object.assign({
        select: "none",
        color: "orca",
        internalProps: {
          iconButton: {
            icon: 'kabob',
            buttonSize: 'normal'
          }
        },
        onValueChange: onValueChange
      }, dropdownProps, {
        type: 'icon',
        label: undefined
      }))
    }) : null]
  });
  return jsx("div", {
    className: classNames('kxi-basic-tile-header',
    // will match regex: kxi-basic-tile-header-has(-title)?(-header)?(-action)?
    `kxi-basic-tile-header-has${titleAreaHasContent ? '-title' : ''}${headerAreaHasContent ? '-header' : ''}${actionAreaHasContent ? '-action' : ''}`, {
      'kxi-basic-tile-header-separator kxi-grey': titleSeparator
    }),
    style: !titleArea && !headerAreaHasContent && !actionAreaHasContent ? {
      display: 'none'
    } : undefined,
    children: titleArea ?
    // | title    action |
    // | header--------- |
    jsxs(Fragment, {
      children: [titleArea, actionArea, headerArea]
    }) :
    // Display header and action inline together if title and subtitle are not present
    // | header    action |
    jsxs(Fragment, {
      children: [headerArea, actionArea]
    })
  });
};

const HorizontalTabBarItem = /*#__PURE__*/React.memo( /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(HorizontalTabBarItem, props);
  const {
    color,
    contrast,
    isLeftmostVisible,
    isRightmostVisible,
    maxCharsAllowed = 30,
    onClickClose,
    onClickPin,
    onClickTab,
    selected,
    tab,
    tabSpacing,
    tooltip,
    visibility,
    visualState,
    zIndex
  } = props;
  const {
    disabled = false,
    alert,
    disabledReason,
    category,
    id,
    title
  } = tab;
  const isStacked = visualState === 'stacked';
  const isHidden = visibility === 'none';
  const onClick = () => {
    if (!disabled) {
      onClickTab === null || onClickTab === void 0 ? void 0 : onClickTab();
    }
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    ref: ref,
    "data-testid": id,
    role: "tab",
    "aria-label": title,
    "aria-hidden": isHidden,
    id: `tab-${id}`,
    className: classNames('kxi-tab-bar-item', color && normalizeColor(color), {
      'kxi-high-contrast': contrast === 'high',
      'kxi-stacked': isStacked,
      'kxi-tab-bar-item-selected': selected,
      'kxi-tab-bar-item-fade-left': visibility === 'partial' && isLeftmostVisible,
      'kxi-tab-bar-item-fade-right': visibility === 'partial' && isRightmostVisible
    }),
    style: {
      marginRight: tabSpacing,
      zIndex
    },
    // Entire tab is clickable when stacked
    onClick: isStacked ? onClick : undefined,
    children: [jsxs("div", {
      className: `kxi-tab-bar-item-content ${isStacked ? 'kxi-text-xsmall' : 'kxi-text-normal'} ${disabled ? 'kxi-disabled-alpha' : ''}`,
      "aria-disabled": disabled,
      children: [jsxs("div", {
        className: "kxi-tab-bar-item-text",
        tabIndex: isHidden ? -1 : 0,
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault(); // prevent page from scrolling on space
            if (!disabled) {
              onClickTab === null || onClickTab === void 0 ? void 0 : onClickTab();
            }
          }
        },
        // Only text is clickable when stacked
        onClick: isStacked ? undefined : onClick,
        children: [jsx(TabBarItemText, {
          category: category,
          disabled: disabled,
          disabledReason: disabledReason,
          maxCharsAllowed: maxCharsAllowed,
          title: title,
          tooltip: tooltip,
          visibility: visibility
        }), alert ? jsx("div", {
          role: "alert",
          className: "kxi-tab-bar-alert"
        }, `alertDiv-${id}`) : null]
      }), jsx(TabBarItemIcon, {
        tab: tab,
        contrast: contrast,
        visibility: visibility,
        onClickHome: onClickTab,
        onClickPin: onClickPin,
        onClickClose: onClickClose
      })]
    }), isStacked ? null : jsx("div", {
      className: "kxi-tab-bar-indicator-wrapper",
      children: jsx("div", {
        className: classNames('kxi-tab-bar-indicator', {
          'kxi-tab-bar-indicator-selected': selected
        })
      })
    })]
  }));
}));
HorizontalTabBarItem.displayName = 'HorizontalTabBarItem';
const TabBarItemIcon = /*#__PURE__*/React.memo(({
  tab,
  visibility,
  contrast,
  onClickHome,
  onClickPin,
  onClickClose
}) => {
  if (tab.isHome) {
    return jsx("div", {
      style: {
        margin: 6,
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        cursor: 'pointer'
      },
      onClick: () => {
        if (!tab.disabled) {
          onClickHome === null || onClickHome === void 0 ? void 0 : onClickHome();
        }
      },
      children: jsx(XIIcon, {
        icon: "app_home",
        iconSize: "small",
        contrast: contrast
      })
    });
  } else if (tab.isPinned) {
    return jsx(IconButton, {
      icon: "pin_filled",
      buttonSize: "small",
      fill: "text",
      tabIndex: visibility === 'none' ? -1 : 0,
      contrast: contrast,
      onClick: onClickPin
    });
  } else if (tab.isCloseShown) {
    return jsx(IconButton, {
      icon: "cancel",
      buttonSize: "small",
      fill: "text",
      tabIndex: visibility === 'none' ? -1 : 0,
      contrast: contrast,
      onClick: onClickClose
    });
  } else {
    return null;
  }
});
const TabBarItemText = /*#__PURE__*/React.forwardRef(({
  visibility,
  category,
  maxCharsAllowed,
  title,
  tooltip,
  disabled,
  disabledReason
}, ref) => {
  const isVisible = visibility === 'full';
  const categoryTooLong = category ? category.length > maxCharsAllowed : false;
  const titleTooLong = title.length > maxCharsAllowed;
  const text = jsxs("div", {
    ref: ref,
    children: [category && jsx("div", {
      style: {
        fontSize: 14
      },
      children: categoryTooLong ? `${category.substr(0, maxCharsAllowed)}...` : category
    }), jsx("div", {
      style: category ? {
        fontWeight: 'bold'
      } : undefined,
      children: titleTooLong ? `${title.substr(0, maxCharsAllowed)}...` : title
    })]
  });
  if (isVisible) {
    const hasOverflow = titleTooLong || categoryTooLong;
    const sharedProps = Object.assign(Object.assign({}, tooltip), {
      children: text,
      style: {
        cursor: 'pointer',
        alignItems: 'center'
      },
      ref
    });
    if (disabled && disabledReason) {
      return jsx(ToolTipWrapper, Object.assign({}, sharedProps, {
        title: hasOverflow ? jsxs(Fragment, {
          children: [disabledReason, jsx("br", {}), jsx("br", {}), category && jsxs(Fragment, {
            children: [jsx("span", {
              style: {
                fontWeight: 200
              },
              children: category
            }), jsx("br", {})]
          }), title]
        }) : undefined,
        text: hasOverflow ? undefined : disabledReason
      }));
    }
    if (hasOverflow) {
      return jsx(ToolTipWrapper, Object.assign({}, sharedProps, {
        title: category ? undefined : title,
        text: category ? jsxs(Fragment, {
          children: [category, jsx("br", {}), jsx("span", {
            style: {
              fontWeight: 'bold'
            },
            children: title
          })]
        }) : undefined
      }));
    }
  }
  return text;
});

function ArrowButton(props) {
  const {
    direction,
    active,
    contrast,
    color,
    hiddenTabCount,
    moreTabsTemplate,
    tooltipProps,
    onClick
  } = props;
  const debugService = useDebugService(ArrowButton, props);
  let tooltipText = moreTabsTemplate !== null && moreTabsTemplate !== void 0 ? moreTabsTemplate : '';
  if (tooltipText) {
    tooltipText = tooltipText.replace(/<%n>/g, `${hiddenTabCount}`);
    if (hiddenTabCount > 1) {
      tooltipText = tooltipText.replace(/<%p\[(.*?)]>/g, '$1');
      tooltipText = tooltipText.replace(/<%s\[(.*?)]>/g, '');
    } else {
      tooltipText = tooltipText.replace(/<%p\[(.*?)]>/g, '');
      tooltipText = tooltipText.replace(/<%s\[(.*?)]>/g, '$1');
    }
  }
  const isVisible = hiddenTabCount > 0;
  const button = jsxs("div", Object.assign({}, debugService.componentProps, {
    className: classNames('kxi-horizontal-tab-bar-arrow', {
      'kxi-horizontal-tab-bar-arrow-visible': isVisible
    }, direction === 'left' ? 'kxi-horizontal-tab-bar-arrow-left' : 'kxi-horizontal-tab-bar-arrow-right'),
    children: [jsx(IconButton, {
      disabled: !isVisible,
      icon: direction === 'left' ? 'arrow_left' : 'arrow_right',
      onClick: onClick,
      contrast: contrast
    }), jsx("div", {
      className: classNames('kxi-horizontal-tab-bar-arrow-indicator-wrapper', color && normalizeColor(color)),
      children: jsx("div", {
        className: classNames('kxi-horizontal-tab-bar-arrow-indicator', {
          'kxi-horizontal-tab-bar-arrow-active': active,
          'kxi-high-contrast': contrast === 'high'
        })
      })
    })]
  }));
  return jsx(ToolTipWrapper, Object.assign({
    enabled: !!tooltipText
  }, tooltipProps, {
    title: tooltipText,
    children: button
  }));
}
ArrowButton.displayName = 'ArrowButton';

/**
 * Some general notes:
 * - This is generic enough to work anywhere there is a windowed list of elements.
 * - Scrolling causes visibility to change very quickly, so all setState calls are debounced until visibilities settle.
 * - The (leftmost|rightmost)(Fully|Partially)VisibleIndex states are the least amount of information needed to track the visibility of all items.
 *
 * An example of how the visibility states correspond to the actual tab list:
 * ```
 *                 leftmostPartial       leftmostFull                      rightmostFull    rightmostPartial
 *     HiddenItem  Partial|lyVisibleItem FullyVisibleItem FullyVisibleItem FullyVisibleItem PartiallyV|isibleItem HiddenItem
 *                        ^left edge of tab list                                right edge of tab list^
 * ```
 */
/* eslint react-hooks/exhaustive-deps:error */
const getChildrenAsArray = element => {
  var _a;
  return Array.from((_a = element === null || element === void 0 ? void 0 : element.children) !== null && _a !== void 0 ? _a : []);
};
function useTabVisibilities({
  container,
  selectedTabIndex,
  debugService
}) {
  const forceRerender = useForceRerender();
  const forceRerenderDebounced = useDebouncedCallback(forceRerender);
  const [children, setChildrenDebounced] = useDebouncedState(() => getChildrenAsArray(container));
  React__default.useEffect(() => {
    if (!container) {
      return;
    }
    setChildrenDebounced(getChildrenAsArray(container));
    const observer = new ZonelessMutationObserver(() => setChildrenDebounced(getChildrenAsArray(container)));
    observer.observe(container, {
      childList: true
    });
    return () => observer.disconnect();
  }, [container, setChildrenDebounced]);
  React__default.useEffect(() => {
    if (!container) {
      return;
    }
    container.addEventListener('scroll', forceRerenderDebounced);
    return () => container.removeEventListener('scroll', forceRerenderDebounced);
  }, [container, forceRerenderDebounced]);
  const fakeContainerRef = React__default.useMemo(() => ({
    current: container
  }), [container]);
  // We don't care about the return value we just want to re-render
  useResizeDetector({
    targetRef: fakeContainerRef,
    handleHeight: false,
    refreshMode: 'debounce',
    refreshRate: 100
  });
  const getElementVisibility = React__default.useCallback(el => {
    if (!container) {
      return 'none';
    }
    const elLeft = el.offsetLeft - container.scrollLeft;
    const elRight = elLeft + el.offsetWidth;
    const containerLeft = container.offsetLeft;
    const containerRight = container.offsetLeft + container.offsetWidth;
    return elLeft < containerLeft ? elRight < containerLeft ? 'none' : 'partial-left' : elRight < containerRight ? 'full' : elLeft > containerRight ? 'none' : 'partial-right';
  }, [container]);
  const getTabVisibility = React__default.useCallback(index => {
    if (!(index in children)) {
      return 'none';
    }
    const visibility = getElementVisibility(children[index]);
    return visibility === 'partial-left' || visibility === 'partial-right' ? 'partial' : visibility;
  }, [children, getElementVisibility]);
  const visibilities = children.map(getElementVisibility);
  const leftmostPartiallyVisibleIndex = visibilities.indexOf('partial-left');
  const leftmostFullyVisibleIndex = visibilities.indexOf('full');
  const rightmostFullyVisibleIndex = visibilities.lastIndexOf('full');
  const rightmostPartiallyVisibleIndex = visibilities.lastIndexOf('partial-right');
  const selectedTabVisibility = getTabVisibility(selectedTabIndex);
  const isIndexValid = i => i >= 0 && isFinite(i);
  const {
    tabCountBeyondLeft,
    isSelectedTabBeyondLeft
  } = (() => {
    const thresholdIndex = isIndexValid(leftmostFullyVisibleIndex) ? leftmostFullyVisibleIndex : isIndexValid(leftmostPartiallyVisibleIndex) ? leftmostPartiallyVisibleIndex : 0;
    return {
      tabCountBeyondLeft: thresholdIndex,
      isSelectedTabBeyondLeft: selectedTabIndex >= 0 && selectedTabVisibility !== 'full' && selectedTabIndex < thresholdIndex
    };
  })();
  const {
    tabCountBeyondRight,
    isSelectedTabBeyondRight
  } = (() => {
    const lastIndex = children.length - 1;
    const thresholdIndex = isIndexValid(rightmostFullyVisibleIndex) ? rightmostFullyVisibleIndex : isIndexValid(rightmostPartiallyVisibleIndex) ? rightmostPartiallyVisibleIndex : lastIndex;
    return {
      tabCountBeyondRight: lastIndex - thresholdIndex,
      isSelectedTabBeyondRight: selectedTabIndex >= 0 && selectedTabVisibility !== 'full' && selectedTabIndex > thresholdIndex
    };
  })();
  debugService.watch({
    visibilities,
    isSelectedTabBeyondLeft,
    isSelectedTabBeyondRight,
    tabCountBeyondLeft,
    tabCountBeyondRight,
    leftmostFullyVisibleIndex,
    rightmostFullyVisibleIndex,
    leftmostPartiallyVisibleIndex,
    rightmostPartiallyVisibleIndex
  });
  return {
    getTabVisibility,
    isSelectedTabBeyondLeft,
    isSelectedTabBeyondRight,
    tabCountBeyondLeft,
    tabCountBeyondRight,
    leftmostFullyVisibleIndex,
    rightmostFullyVisibleIndex,
    leftmostPartiallyVisibleIndex,
    rightmostPartiallyVisibleIndex
  };
}

class HorizontalTabBarProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Array)], HorizontalTabBarProps.prototype, "data", void 0);
__decorate([Bound(), __metadata("design:type", String)], HorizontalTabBarProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], HorizontalTabBarProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", String)], HorizontalTabBarProps.prototype, "activeTabId", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HorizontalTabBarProps.prototype, "tabSpacing", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HorizontalTabBarProps.prototype, "hasTransparentBackground", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HorizontalTabBarProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", Number)], HorizontalTabBarProps.prototype, "maxCharsAllowed", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], HorizontalTabBarProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], HorizontalTabBarProps.prototype, "arrowTooltip", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HorizontalTabBarProps.prototype, "moveOnSelectChange", void 0);
__decorate([Bound(), __metadata("design:type", String)], HorizontalTabBarProps.prototype, "moreTabsTemplate", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HorizontalTabBarProps.prototype, "addButtonProps", void 0);
__decorate([Bound(), __metadata("design:type", String)], HorizontalTabBarProps.prototype, "visualState", void 0);
class HorizontalTabBarEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], HorizontalTabBarEvents.prototype, "onTabChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], HorizontalTabBarEvents.prototype, "onTabClose", void 0);
__decorate([Bound(), __metadata("design:type", Function)], HorizontalTabBarEvents.prototype, "onTabUnpin", void 0);
const addButtonStyle = Object.freeze({
  '--kxi-horizontal-tab-bar-add-button-width': '36px'
});
/**
 * Description: Navigational component that contains clickable data.
 */
const HorizontalTabBar = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(HorizontalTabBar, props);
  const {
      activeTabId: selectedTabId,
      addButtonProps: addButtonPropsInput,
      arrowTooltip,
      color = 'orca',
      contrast = 'normal',
      data: tabs = [],
      hasTransparentBackground,
      maxCharsAllowed = 30,
      moreTabsTemplate,
      moveOnSelectChange,
      observer,
      stretch,
      tabSpacing = 0,
      tooltip,
      visualState = 'normal',
      onTabChange,
      onTabClose,
      onTabUnpin
    } = props,
    ingredients = __rest(props, ["activeTabId", "addButtonProps", "arrowTooltip", "color", "contrast", "data", "hasTransparentBackground", "maxCharsAllowed", "moreTabsTemplate", "moveOnSelectChange", "observer", "stretch", "tabSpacing", "tooltip", "visualState", "onTabChange", "onTabClose", "onTabUnpin"]);
  const isStacked = visualState === 'stacked';
  React.useEffect(() => {
    if (!props.data) {
      devOnlyError('The Kitchen (HorizontalTabBar): `data` is a required prop but was not provided.');
    }
  }, [props.data]);
  const categoryLayout = React.useMemo(() => tabs.some(t => t.category), [tabs]);
  const selectedTabIndex = React.useMemo(() => tabs.findIndex(tab => tab.id === selectedTabId), [selectedTabId, tabs]);
  /**
   * This element is used as the root for tracking visibility of each tab,
   *     so we need to rerender when it changes.
   */
  const [tabListElement, setTabListElement] = React.useState(null);
  const {
    ref: tabElementsRef,
    callbackRefs
  } = useArrayRef(tabs.length, () => null);
  const {
    getTabVisibility,
    isSelectedTabBeyondLeft,
    isSelectedTabBeyondRight,
    tabCountBeyondLeft,
    tabCountBeyondRight,
    leftmostPartiallyVisibleIndex,
    rightmostPartiallyVisibleIndex
  } = useTabVisibilities({
    container: tabListElement,
    selectedTabIndex,
    debugService
  });
  debugService.watch({
    isSelectedTabBeyondLeft,
    isSelectedTabBeyondRight,
    selectedTabIndex,
    tabCountBeyondLeft,
    tabCountBeyondRight
  });
  /**
   * @returns The tab element's position relative to the tab list container.
   */
  const getTabElementOffset = React.useCallback(el => {
    var _a;
    return el.offsetLeft - ((_a = tabListElement === null || tabListElement === void 0 ? void 0 : tabListElement.offsetLeft) !== null && _a !== void 0 ? _a : 0);
  }, [tabListElement]);
  /**
   * Scrolls to align the element to the left of the container.
   */
  const scrollToTab = React.useCallback((index, ifNeeded = false) => {
    if (!tabListElement) {
      return;
    }
    const tabEl = tabElementsRef.current[index];
    if (tabEl && (!ifNeeded || getTabVisibility(index) !== 'full')) {
      tabListElement.scrollLeft = getTabElementOffset(tabEl);
    }
  }, [getTabElementOffset, getTabVisibility, tabElementsRef, tabListElement]);
  /**
   * The selected tab will have the highest z-index,
   *     and for each tab you move away from the selected tab,
   *     the z-index drops by one.
   *     This gives an almost staircase appearance where the selected tab is the top step.
   *
   * Example: If you have 10 tabs and the tab at index 3 is selected, you get the following z-indices:
   * 3 4 5 6 5 4 3 2 1 0
   *       ^ selected tab
   */
  const zIndices = React.useMemo(() => {
    const length = tabs.length;
    if (!isStacked) {
      return new Array(length).fill(undefined);
    }
    const tabsLeftOfSelected = selectedTabIndex;
    const tabsRightOfSelected = length - 1 - selectedTabIndex;
    const selectedTabZIndex = Math.max(tabsLeftOfSelected, tabsRightOfSelected);
    return new Array(length).fill(0).map((_, i) => selectedTabZIndex - Math.abs(selectedTabIndex - i));
  }, [selectedTabIndex, tabs.length, isStacked]);
  /**
   * Scrolls left or right by the width of the tab list element
   *     but will always keep the leftmost visible tab aligned to the left of the element.
   */
  const [handleLeftArrowClick, handleRightArrowClick] = React.useMemo(() => {
    if (tabListElement) {
      /**
       * @returns The index of the tab closest to the specified position.
       */
      const getClosestIndex = position => {
        const offsets = tabElementsRef.current.map(el => el ? Math.abs(position - getTabElementOffset(el)) : Infinity);
        return offsets.indexOf(Math.min(...offsets));
      };
      return [() => scrollToTab(getClosestIndex(tabListElement.scrollLeft - tabListElement.clientWidth)), () => scrollToTab(getClosestIndex(tabListElement.scrollLeft + tabListElement.clientWidth))];
    }
    return [undefined, undefined];
  }, [getTabElementOffset, scrollToTab, tabElementsRef, tabListElement]);
  React.useEffect(() => {
    // scroll the selected tab into view if not visible
    if (moveOnSelectChange && tabListElement && selectedTabIndex >= 0) {
      scrollToTab(selectedTabIndex, true);
    }
  }, [moveOnSelectChange, selectedTabIndex, tabListElement, scrollToTab,
  // Also trigger for props likely to move tabs around
  tabs, tabSpacing, visualState, addButtonPropsInput, maxCharsAllowed, stretch]);
  const addButtonPropsInputTooltip = addButtonPropsInput === null || addButtonPropsInput === void 0 ? void 0 : addButtonPropsInput.tooltip;
  const addButtonProps = React.useMemo(() => {
    if (!addButtonPropsInputTooltip) {
      return addButtonPropsInput;
    }
    return Object.assign(Object.assign({}, addButtonPropsInput), {
      tooltip: Object.assign({}, addButtonPropsInputTooltip)
    });
  }, [addButtonPropsInput, addButtonPropsInputTooltip]);
  const changeTab = React.useCallback(tabId => {
    onTabChange === null || onTabChange === void 0 ? void 0 : onTabChange({
      tab: tabs.find(tab => tab.id === tabId)
    });
    if (observer) {
      observer.activeTabId = tabId;
    }
  }, [onTabChange, tabs, observer]);
  // For referential stability
  const tabCallbacks = React.useMemo(() => tabs.map((tab, i) => ({
    ref: callbackRefs[i],
    onClickTab: () => changeTab(tab.id),
    onClickPin: () => onTabUnpin === null || onTabUnpin === void 0 ? void 0 : onTabUnpin({
      tab
    }),
    onClickClose: () => onTabClose === null || onTabClose === void 0 ? void 0 : onTabClose({
      tab
    })
  })), [tabs, callbackRefs, changeTab, onTabUnpin, onTabClose]);
  const commonClassNames = [color && normalizeColor(color), {
    'kxi-tab-bar-transparent': hasTransparentBackground,
    'kxi-stretch-height': stretch,
    'kxi-high-contrast': contrast === 'high'
  }];
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    role: "toolbar",
    "aria-label": "horizontal tab bar",
    className: classNames('kxi-horizontal-tab-bar', commonClassNames, {
      'kxi-stacked': isStacked
    }),
    children: [isStacked ? null : jsx(ArrowButton, {
      direction: "left",
      contrast: contrast,
      color: color,
      active: isSelectedTabBeyondLeft,
      hiddenTabCount: tabCountBeyondLeft,
      moreTabsTemplate: moreTabsTemplate,
      tooltipProps: arrowTooltip,
      onClick: handleLeftArrowClick
    }), jsx("div", Object.assign({
      className: classNames('kxi-horizontal-tab-bar-tab-list', commonClassNames, {
        'kxi-horizontal-tab-bar-category-layout': categoryLayout,
        'kxi-stacked': isStacked
      })
    }, ingredients, {
      "data-testid": "outerDiv",
      ref: mergeRefs([ref, setTabListElement]),
      children: tabs.map((tab, i) => jsx(HorizontalTabBarItem, Object.assign({
        tab: tab,
        tabSpacing: tabSpacing,
        isLeftmostVisible: i === leftmostPartiallyVisibleIndex,
        isRightmostVisible: i === rightmostPartiallyVisibleIndex,
        color: color,
        tooltip: tooltip,
        selected: tab.id === selectedTabId,
        contrast: contrast,
        maxCharsAllowed: maxCharsAllowed,
        visibility: getTabVisibility(i),
        visualState: visualState,
        zIndex: zIndices[i]
      }, tabCallbacks[i]), tab.id))
    })), isStacked ? null : jsx(ArrowButton, {
      direction: "right",
      contrast: contrast,
      color: color,
      tooltipProps: arrowTooltip,
      active: isSelectedTabBeyondRight,
      hiddenTabCount: tabCountBeyondRight,
      moreTabsTemplate: moreTabsTemplate,
      onClick: handleRightArrowClick
    }), addButtonProps && !isStacked && jsx(IconButton, Object.assign({
      icon: "add_circle",
      className: "kxi-horizontal-tab-bar-add-button"
    }, addButtonProps, {
      style: addButtonStyle,
      color: color,
      contrast: contrast
    }))]
  }));
});
HorizontalTabBar.displayName = 'HorizontalTabBar';

class BasicTileProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], BasicTileProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", DropdownProps)], BasicTileProps.prototype, "dropdownProps", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "hasBorder", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "hasTopBar", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "isInteractive", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "isSelected", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "isSelectable", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BasicTileProps.prototype, "padding", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "semiboldTitle", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "stretchSlot", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BasicTileProps.prototype, "minContentHeight", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BasicTileProps.prototype, "maxContentHeight", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BasicTileProps.prototype, "minHeight", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BasicTileProps.prototype, "maxHeight", void 0);
__decorate([Bound(), __metadata("design:type", String)], BasicTileProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", String)], BasicTileProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], BasicTileProps.prototype, "titleColor", void 0);
__decorate([Bound(), __metadata("design:type", XIIconProps)], BasicTileProps.prototype, "titleIconProps", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BasicTileProps.prototype, "titleSeparator", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BasicTileProps.prototype, "tabBarProps", void 0);
__decorate([Bound(), __metadata("design:type", String)], BasicTileProps.prototype, "visualState", void 0);
class BasicTileEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], BasicTileEvents.prototype, "onValueChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], BasicTileEvents.prototype, "onIsSelectedChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], BasicTileEvents.prototype, "onBasicTilePress", void 0);
/**
 * Description: Tile that has a generic wrapper, that you can pass anything inside of as children of the component.
 *
 * Slots: The Basic Tile component has 2 slots, "action" is a slot for buttons/dropdowns
 * in the upper right hand side. "children" is the slot for the main area of the component.
 */
const BasicTile = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(BasicTile, props);
  const {
      _hasShadow: hasShadow = true,
      children,
      className,
      color = 'orca',
      disabled,
      dropdownProps,
      hasBorder = false,
      hasTopBar = true,
      isInteractive = true,
      isSelectable = true,
      isSelected: isSelectedInput = false,
      minContentHeight,
      maxContentHeight,
      minHeight,
      maxHeight,
      observer,
      onBasicTilePress,
      onClick,
      onIsSelectedChange: onIsSelectedChangeInput,
      onKeyDown,
      onValueChange,
      padding,
      semiboldTitle = false,
      stretch = false,
      stretchSlot = false,
      style,
      subtitle,
      tabBarProps,
      title,
      titleColor = 'orca',
      titleIconProps,
      titleSeparator = false,
      visualState: visualStateInput = 'condensed'
    } = props,
    ingredients = __rest(props, ["_hasShadow", "children", "className", "color", "disabled", "dropdownProps", "hasBorder", "hasTopBar", "isInteractive", "isSelectable", "isSelected", "minContentHeight", "maxContentHeight", "minHeight", "maxHeight", "observer", "onBasicTilePress", "onClick", "onIsSelectedChange", "onKeyDown", "onValueChange", "padding", "semiboldTitle", "stretch", "stretchSlot", "style", "subtitle", "tabBarProps", "title", "titleColor", "titleIconProps", "titleSeparator", "visualState"]);
  const [isSelected, setIsSelected] = useValueBackedState(isInteractive && isSelectable && isSelectedInput);
  const tileRef = React.useRef(null);
  const [contentWrapperResizeRef, setContentWrapperResizeRef] = React.useState(null);
  const {
    width
  } = useBorderBoxResizeObserver({
    element: contentWrapperResizeRef
  });
  const visualState = visualStateInput !== 'auto' ? visualStateInput : width < 335 ? 'condensed' : 'normal';
  useWarnOnceIf(dropdownProps !== undefined && 'type' in dropdownProps, debugService.format('You provided the `type` property to `dropdownProps`. This will be ignored.'));
  useWarnOnceIf(dropdownProps !== undefined && 'label' in dropdownProps, debugService.format('You provided the `label` property to `dropdownProps`. This will be ignored.'));
  useWarnOnceIf(stretch && !stretchSlot, 'Slot will stretch by default in the next major version. If you are seeing this error add `stretchSlot = true` to your basic tile component.');
  useWarnOnceIf((tabBarProps === null || tabBarProps === void 0 ? void 0 : tabBarProps.visualState) !== undefined, debugService.format("tabBarProps.visualState is not supported in this context. 'condensed' will be used."));
  useWarnOnceIf((tabBarProps === null || tabBarProps === void 0 ? void 0 : tabBarProps.hasTransparentBackground) !== undefined, debugService.format('tabBarProps.hasTransparentBackground is not supported in this context. `true` will be used.'));
  useWarnOnceIf(maxContentHeight !== undefined && padding !== undefined, 'The Kitchen (Basic Tile): While using `maxContentHeight`, it is recommended you use `margin` instead of `padding` for best styling results.');
  const handleClick = e => {
    if (disabled || !isInteractive) {
      return;
    }
    if (shouldElementHandleEvent(e.target, tileRef.current)) {
      onIsSelectedChange(!isSelected && isSelectable);
      onClick === null || onClick === void 0 ? void 0 : onClick(e);
      onBasicTilePress === null || onBasicTilePress === void 0 ? void 0 : onBasicTilePress();
    }
  };
  const onIsSelectedChange = isSelected => {
    if (disabled || !isSelectable) {
      return;
    }
    onIsSelectedChangeInput === null || onIsSelectedChangeInput === void 0 ? void 0 : onIsSelectedChangeInput({
      isSelected
    });
    setIsSelected(isSelected);
    if (observer) {
      observer.isSelected = isSelected;
    }
  };
  return jsx("div", Object.assign({}, debugService.componentProps, {
    role: "group",
    "aria-disabled": disabled,
    "data-testid": "basic-tile",
    tabIndex: isInteractive ? 0 : undefined,
    className: classNames(className, `kxi-basic-tile`, normalizeColor(color), {
      'kxi-tile-shadows': hasShadow,
      'kxi-stretch': stretch,
      'kxi-tile-non-interactive': disabled || !isInteractive,
      'kxi-basic-tile-border': hasBorder,
      'kxi-basic-tile-no-top-bar': !hasTopBar
    })
  }, ingredients, {
    ref: mergeRefs([ref, tileRef]),
    onClick: handleClick,
    onKeyDown: e => {
      onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
      if (disabled || !isInteractive) {
        return;
      }
      if ((e.key === 'Enter' || e.key === ' ') && shouldElementHandleEvent(e.target, tileRef.current)) {
        e.preventDefault();
        onIsSelectedChange(!isSelected);
        onBasicTilePress === null || onBasicTilePress === void 0 ? void 0 : onBasicTilePress();
      }
    },
    children: jsxs("div", {
      className: classNames({
        'kxi-basic-tile-selected': isSelectable && isSelected,
        'kxi-stretch': stretch
      }),
      style: {
        minHeight,
        maxHeight,
        display: 'grid',
        gridTemplateRows: hasTopBar ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
        gridTemplateColumns: 'minmax(0, 1fr)'
      },
      children: [hasTopBar ? jsx("div", {
        className: "kxi-basic-tile-top-bar"
      }) : null, jsxs("div", {
        "data-testid": "basic-tile-body",
        className: classNames('kxi-basic-tile-body', {
          'kxi-basic-tile-border': hasBorder,
          'kxi-basic-tile-selected': isSelectable && isSelected,
          'kxi-basic-tile-no-top-bar': !hasTopBar,
          'kxi-stretch': stretch
        }),
        children: [tabBarProps ? jsx("div", {
          className: "kxi-basic-tile-tabs",
          children: jsx(HorizontalTabBar, Object.assign({}, tabBarProps, {
            hasTransparentBackground: true,
            visualState: 'stacked',
            onClick: e => {
              var _a;
              (_a = tabBarProps.onClick) === null || _a === void 0 ? void 0 : _a.call(tabBarProps, e);
              // Prevent tab bar clicks from triggering chart wrapper clicks
              e.stopPropagation();
            }
          }))
        }) : null, jsxs("div", {
          ref: setContentWrapperResizeRef,
          "data-testid": "basic-tile-content-wrapper",
          className: classNames('kxi-basic-tile-content-wrapper', `kxi-${visualState}`, {
            'kxi-stretch': stretch
          }),
          children: [jsx(Header$1, {
            dropdownProps: dropdownProps,
            onValueChange: onValueChange,
            semiboldTitle: semiboldTitle,
            subtitle: subtitle,
            title: title,
            titleColor: titleColor,
            titleIconProps: titleIconProps,
            titleSeparator: titleSeparator,
            children: children
          }), jsx("div", {
            className: "kxi-basic-tile-children-wrapper",
            style: maxContentHeight ? {
              maxHeight: maxContentHeight
            } : undefined,
            children: jsx("div", {
              "data-testid": "basic-tile-content",
              className: classNames('kxi-basic-tile-content', {
                'kxi-basic-tile-stretch-slot': stretchSlot
              }),
              style: Object.assign({
                minHeight: minContentHeight,
                maxHeight: maxContentHeight,
                padding
              }, style),
              children: jsx(Slot, {
                name: "children",
                "data-testid": "slot",
                reactChildren: children
              })
            })
          })]
        })]
      })]
    })
  }));
});
BasicTile.displayName = 'BasicTile';

const CornerControl = props => {
  const {
    color,
    disabled,
    cornerControlType,
    onCornerControlClick
  } = props;
  // iconSize has to match what is set in button.scss for $icon-size
  return (
    // Buttons can't be nested in other buttons unfortunately, so fake it with a div
    jsx("div", {
      className: `kxi-button kxi-circle kxi-tile-button-corner-control ${normalizeColor(color)}`,
      tabIndex: 0,
      role: "button",
      "data-testid": "tile-button-corner-control",
      "aria-disabled": disabled,
      onClick: e => {
        if (disabled) {
          return;
        }
        e.stopPropagation();
        onCornerControlClick === null || onCornerControlClick === void 0 ? void 0 : onCornerControlClick();
      },
      onKeyDown: e => {
        if (disabled) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          onCornerControlClick === null || onCornerControlClick === void 0 ? void 0 : onCornerControlClick();
        }
      },
      children: jsx(XIIcon, {
        icon: cornerControlType === 'checkmark' ? 'checkmark_18' : 'cancel_18',
        iconSize: 18,
        color: "white_primary"
      })
    })
  );
};

function isXiIconProps(props) {
  return !(props === null || props === void 0 ? void 0 : props.icon) || isIcons(props.icon);
}
const defaultStylings = {
  normal: {
    iconSize: 58,
    padding: '6px'
  },
  large: {
    iconSize: 79,
    padding: '18px'
  },
  xlarge: {
    iconSize: 124,
    padding: '18px'
  },
  custom: {
    iconSize: undefined,
    padding: 0
  }
};
class TileButtonProps extends ObservablePropsMixin(ButtonHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], TileButtonProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TileButtonProps.prototype, "padding", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TileButtonProps.prototype, "isSelected", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TileButtonProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], TileButtonProps.prototype, "tooltipProps", void 0);
__decorate([Bound(), __metadata("design:type", String)], TileButtonProps.prototype, "contentType", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TileButtonProps.prototype, "iconProps", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TileButtonProps.prototype, "imageProps", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TileButtonProps.prototype, "size", void 0);
__decorate([Bound(), __metadata("design:type", String)], TileButtonProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TileButtonProps.prototype, "labelProps", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TileButtonProps.prototype, "hasShadow", void 0);
__decorate([Bound(), __metadata("design:type", String)], TileButtonProps.prototype, "cornerControlType", void 0);
class TileButtonEvents extends ButtonHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], TileButtonEvents.prototype, "onCornerControlClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], TileButtonEvents.prototype, "onIsSelectedChange", void 0);
/**
 * Description: A rectangular button.
 */
const TileButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(TileButton, props);
  const {
      className,
      disabled = false,
      contentType = 'icon',
      cornerControlType,
      label,
      iconProps,
      imageProps,
      labelProps: {
        truncate = false,
        fontSize: labelFontSize,
        width: labelWidth
      } = {},
      size = 'normal',
      tooltipProps: tooltipPropsInput,
      onCornerControlClick,
      onIsSelectedChange: onIsSelectedChangeInput,
      // BasicTile props
      color = 'orca',
      isSelected: isSelectedInput = false,
      hasShadow = true,
      padding: paddingInput,
      stretch = false,
      // Rest
      observer: observerInput,
      id: idInput
    } = props,
    ingredients = __rest(props, ["className", "disabled", "contentType", "cornerControlType", "label", "iconProps", "imageProps", "labelProps", "size", "tooltipProps", "onCornerControlClick", "onIsSelectedChange", "color", "isSelected", "hasShadow", "padding", "stretch", "observer", "id"]);
  const observer = observerInput;
  const customSize = typeof size === 'object' ? size : undefined;
  const sizeStr = typeof size === 'string' ? size : 'custom';
  const sizeClass = `kxi-${sizeStr}`;
  const defaults = defaultStylings[sizeStr];
  const [isSelected, setIsSelected] = useValueBackedState(isSelectedInput);
  const [labelSpanElement, setLabelSpanElement] = React.useState();
  const {
    ref: contentResizeRef,
    width: contentWidth = 0
  } = useResizeDetector({
    handleHeight: false
  });
  const defaultId = React.useId();
  const id = idInput || defaultId;
  const onIsSelectedChange = isSelected => {
    onIsSelectedChangeInput === null || onIsSelectedChangeInput === void 0 ? void 0 : onIsSelectedChangeInput({
      isSelected,
      id
    });
    setIsSelected(isSelected);
    if (observer) {
      observer.isSelected = isSelected;
    }
  };
  useWarnOnceIf((tooltipPropsInput === null || tooltipPropsInput === void 0 ? void 0 : tooltipPropsInput.direction) !== undefined, debugService.format('tooltipProps.direction is currently unsupported. 6 will be used.'));
  useWarnOnceIf(contentType === 'icon' && !iconProps, debugService.format("contentType is 'icon', but iconProps were not provided."));
  useWarnOnceIf(contentType === 'image' && !imageProps, "contentType is 'image', but imageProps were not provided.");
  return jsx(ToolTipWrapper, Object.assign({
    anchorElement: labelSpanElement,
    enabled: !disabled && truncate,
    stretch: stretch
  }, tooltipPropsInput, {
    direction: 6,
    text: label,
    children: jsxs("button", Object.assign({}, debugService.componentProps, ingredients, {
      disabled: disabled,
      onKeyDown: e => {
        if (disabled) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          onIsSelectedChange(!isSelected);
        }
      },
      onClick: e => e.stopPropagation(),
      className: classNames('kxi-button kxi-tile-button', className, stretch && 'kxi-stretch', cornerControlType && 'kxi-tile-button-has-corner-control'),
      ref: ref,
      children: [jsxs("div", {
        className: classNames('kxi-tile-button-tile-wrapper', stretch && 'kxi-stretch', label && 'kxi-tile-button-has-label', hasShadow && 'kxi-tile-button-has-shadow', disabled && 'kxi-disabled'),
        children: [jsx(BasicTile, {
          ref: contentResizeRef,
          "data-testid": "tile-button-basictile",
          hasBorder: true,
          hasTopBar: false,
          isInteractive: true,
          disabled: disabled,
          className: classNames(sizeClass, 'kxi-tile-button-tile', {
            'kxi-tile-button-has-label': label !== undefined
          }),
          color: color,
          _hasShadow: hasShadow,
          isSelectable: true,
          isSelected: isSelected,
          padding: paddingInput !== null && paddingInput !== void 0 ? paddingInput : defaults.padding,
          stretch: stretch,
          tabIndex: -1,
          onIsSelectedChange: ({
            isSelected
          }) => {
            onIsSelectedChange(isSelected);
          },
          visualState: "minimal",
          children: jsx("div", {
            className: classNames('kxi-tile-button-tile-content-wrapper', sizeClass, normalizeColor(color)),
            "data-testid": "tile-button-content",
            style: customSize,
            children: contentType === 'icon' ? isXiIconProps(iconProps) ? jsx(XIIcon, Object.assign({
              "data-testid": "tile-button-icon",
              iconSize: defaults.iconSize
            }, iconProps)) : jsx(XIAppIcon, Object.assign({
              "data-testid": "tile-button-appicon",
              iconSize: defaults.iconSize
            }, iconProps)) : jsx("img", Object.assign({
              "data-testid": "tile-button-img",
              alt: ""
            }, imageProps, {
              style: customSize !== null && customSize !== void 0 ? customSize : {
                height: defaults.iconSize,
                width: defaults.iconSize
              }
            }))
          })
        }), cornerControlType ? jsx(CornerControl, {
          color: color,
          cornerControlType: cornerControlType,
          disabled: disabled,
          onCornerControlClick: onCornerControlClick
        }) : null]
      }), label ? jsx("span", {
        "data-testid": "tile-button-label",
        onClick: () => {
          if (!disabled) {
            onIsSelectedChange(!isSelected);
          }
        },
        className: classNames('kxi-label kxi-label-inline', {
          [sizeClass]: labelFontSize === undefined,
          'kxi-stretch-width': stretch,
          'kxi-label-truncate': truncate,
          'kxi-disabled': disabled
        }),
        style: {
          fontSize: labelFontSize,
          width: labelWidth !== null && labelWidth !== void 0 ? labelWidth : truncate || !stretch ? contentWidth : undefined
        },
        ref: setLabelSpanElement,
        children: label
      }) : null]
    }))
  }));
});
TileButton.displayName = 'TileButton';

const _ButtonTypesOptions = ['fill', 'no-fill', 'outline', 'text', 'link'];
const _ButtonSizesOptions = ['small', 'normal', 'large', 'xxlarge'];
const _FilterButtonTypes = ['active', 'inactive'];
const _FilterButtonFocus = ['focus', 'blur'];
const _ButtonColorsOptions = [..._ColorsOptions, ..._ErrorStatesOptions];
const _MenuButtonSizesOptions = ['normal', 'large'];
const _TonesOptions = ['normal', 'light'];
const _FabSizesOptions = ['normal', 'xlarge', 'xxlarge'];
// #region TileButton
const _TileButtonTypesOptions = ['icon', 'image'];
const _TileButtonSizesOptions = ['normal', 'large', 'xlarge'];
const _TileButtonCornerControlTypesOptions = ['cancel', 'checkmark'];
// #endregion TileButton

const commentsLocalization = {
  andSentiment: 'and Sentiment',
  confidenceLevel: 'Confidence Level',
  confident: 'Confident',
  moreMoments: 'More Like This',
  sentiment: 'Sentiment',
  sentimentScore: 'Sentiment Score',
  tags: 'Tags'
};
const sentimentButtonsLocalization = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative'
};
const tagsLocalization = {
  add: 'Add Tag',
  addConfirm: 'Add Tag',
  cancel: 'Cancel',
  expressTagExistsError: 'This already exists as an Express tag',
  industryTagExistsError: 'This already exists as an Industry tag',
  noTags: 'No tags',
  save: 'Save'
};
const commentLocalization = {
  comments: commentsLocalization,
  sentimentButtons: sentimentButtonsLocalization,
  tags: tagsLocalization,
  emptyCommentText: 'No comment provided'
};
const noteLocalization = {
  cancel: 'Cancel',
  edit: 'Edit',
  locale: 'en-US',
  save: 'Save',
  youWrote: 'You wrote'
};
const notesLocalization = Object.assign(Object.assign({}, noteLocalization), {
  add: 'Add',
  notes: 'Notes',
  placeholder: 'Type a note and press enter to add.'
});
const experienceHeaderLocalization = {
  markValid: 'Mark Valid'
};
Object.assign(Object.assign(Object.assign({}, commentLocalization), experienceHeaderLocalization), {
  notes: notesLocalization
});
const condensedExperienceCardLocalization = {
  additionalCount: '',
  attachments: 'View Attachments',
  details: 'View Details',
  dueDate: 'Due in',
  newMessage: 'New Messages',
  notes: notesLocalization
};

const Header = ({
  _isOnExperienceCard,
  _setHighlightedCommentId,
  answerId,
  comments,
  highlightedCommentId,
  promptLabel,
  showMoreMomentsLink
}) => {
  const moreSelected = highlightedCommentId === answerId && _isOnExperienceCard;
  return jsxs("div", {
    className: `kxi-comment-header ${!promptLabel ? 'no-prompt' : ''} ${moreSelected ? 'more' : ''}`,
    children: [jsx("div", {
      children: promptLabel
    }), showMoreMomentsLink && _isOnExperienceCard ? jsx(DefaultButton, {
      className: `kxi-comment-more-wrapper ${moreSelected ? 'more' : ''}`,
      fill: "link",
      onClick: () => {
        if (_setHighlightedCommentId) _setHighlightedCommentId(answerId);
      },
      text: comments.moreMoments,
      "data-testid": "more-moments"
    }) : undefined]
  });
};

const getConfidenceAsPercent = (value, displayPercentSign = true) => {
  if (value || value === 0) {
    return Number.isInteger(value) ? `${value}${displayPercentSign ? '%' : ''}` : `${Math.floor(value * 100)}${displayPercentSign ? '%' : ''}`;
  }
};
/**
 * This function is the brains of this comment text component. It takes an array of
 * separators and then splices them together chopping and splicing where needed. It
 * mutates the choppedSeparators array (which should be initialized with a text component)
 * and makes it into a fully congruent, non-overlapping array of separators. Each
 * separator has its own tagIds, sentimentIds, and highlighting
 */
const spliceChop = (choppedSeparators, separator, hasWarned, setHasWarned) => {
  let separatorToChopIndex = choppedSeparators.findIndex(cs => cs.begin <= separator.begin && cs.end > separator.begin || cs.begin < separator.begin && cs.end >= separator.end);
  if (separatorToChopIndex === -1 && !hasWarned) {
    //intentionally not dev only
    console.warn('The Kitchen: Experience Card Comment - Comment data contains invalid begin or end indexes');
    if (setHasWarned) {
      setHasWarned(true);
    }
  }
  while (separatorToChopIndex >= 0) {
    const separatorToChop = choppedSeparators[separatorToChopIndex];
    let beginSeparator = undefined;
    let endSeparator = undefined;
    if ((separatorToChop === null || separatorToChop === void 0 ? void 0 : separatorToChop.begin) < separator.begin) {
      beginSeparator = Object.assign({}, separatorToChop);
      beginSeparator.end = separator.begin;
    }
    const middleSeparator = Object.assign({}, separatorToChop);
    middleSeparator.tagIds = middleSeparator.tagIds.concat(separator.tagIds);
    middleSeparator.sentimentIds = middleSeparator.sentimentIds.concat(separator.sentimentIds);
    middleSeparator.highlighted = middleSeparator.highlighted || separator.highlighted;
    middleSeparator.begin = separator.begin;
    if (separatorToChop.end > separator.end) {
      middleSeparator.end = separator.end;
      endSeparator = Object.assign({}, separatorToChop);
      endSeparator.begin = separator.end;
    } else if (separatorToChop.end !== separator.end) {
      middleSeparator.end = separatorToChop.end;
      endSeparator = Object.assign({}, separator);
      endSeparator.begin = separatorToChop.end;
    }
    choppedSeparators.splice(separatorToChopIndex, 1);
    if (beginSeparator) choppedSeparators.push(beginSeparator);
    if (middleSeparator) choppedSeparators.push(middleSeparator);
    if (endSeparator) {
      if (endSeparator.end <= separator.end) {
        separator = Object.assign({}, endSeparator);
        separatorToChopIndex = choppedSeparators.findIndex(
        // eslint-disable-next-line no-loop-func
        cs => cs.begin <= separator.begin && cs.end > separator.begin);
      } else {
        choppedSeparators.push(endSeparator);
        separatorToChopIndex = -1;
      }
    } else {
      separatorToChopIndex = -1;
    }
  }
};

const CommentText = props => {
  const {
    externalId,
    sentimentAnnotations,
    localization,
    text,
    activeTags: activeTagsInput,
    combinedTags: combinedTagsInput,
    activeSentiments,
    highlightedText
  } = props;
  const mapTagToCombinedTag = tagInput => {
    return tagInput.map(tag => {
      var _a;
      if (tag.id) {
        //if the tag is a `Tag` type transform it into the `combined tag` type
        return {
          tagName: tag.label,
          sentiment: tag.sentiment,
          userAdded: tag.userAdded,
          tagId: tag.id,
          positions: (_a = tag.highlighting) === null || _a === void 0 ? void 0 : _a.map(highlight => {
            return {
              beginIndex: highlight.beginIndex,
              endIndex: highlight.endIndex,
              sentimentType: 'tag'
            };
          })
        };
      } else {
        return tag;
      }
    });
  };
  const [boxElement, setBoxElement] = React.useState();
  const [commentText, setCommentText] = React.useState();
  const [activeSentimentId, setActiveSentimentId] = React.useState(undefined);
  const [textSlices, setTextSlices] = React.useState([]);
  const [highlights, setHighlights] = React.useState([]);
  const [hasWarned, setHasWarned] = React.useState(false);
  const [activeTags, setActiveTags] = React.useState(mapTagToCombinedTag(activeTagsInput));
  const [combinedTags, setCombinedTags] = React.useState(mapTagToCombinedTag(combinedTagsInput));
  React.useEffect(() => {
    setActiveTags(mapTagToCombinedTag(activeTagsInput));
    setCombinedTags(mapTagToCombinedTag(combinedTagsInput));
  }, [activeTagsInput, combinedTagsInput]);
  React.useEffect(() => {
    if (!highlightedText) {
      setHighlights([]);
      return;
    }
    const rawText = text;
    const delimiter = uuidv4();
    let highlightedTextArr = !Array.isArray(highlightedText) ? [highlightedText.trim()] : [...highlightedText].map(x => x.trim());
    highlightedTextArr = highlightedTextArr.map(value => {
      return escapeRegex(value);
    });
    // look for any of the highlighted search words within the raw text
    const regex = new RegExp(`(${highlightedTextArr.join('|')})`, 'gi');
    const foundLetters = rawText.replace(regex, `${delimiter}$1${delimiter}`).split(delimiter);
    const newHighlights = foundLetters.map((item, i) => {
      if (highlightedTextArr.find(text => text.toLowerCase() === escapeRegex(item).toLowerCase())) {
        //count indexes up to the beginning point
        const begin = foundLetters.reduce((prev, curr, index) => {
          if (index < i) {
            return prev + curr.length;
          }
          return prev;
        }, 0);
        return {
          begin,
          end: begin + item.length
        };
      }
      return undefined;
    }).filter(item => item);
    setHighlights(newHighlights);
  }, [highlightedText]);
  React.useEffect(() => {
    const sentimentBoxPosition = getSentimentBoxPosition();
    if (!sentimentAnnotations) {
      setBoxElement(undefined);
      return;
    }
    const selectedSentimentIndex = sentimentAnnotations.findIndex(sentiment => sentiment.sentimentId === activeSentimentId);
    if (selectedSentimentIndex < 0) {
      setBoxElement(undefined);
      return;
    }
    const {
      sentimentConfidence,
      sentimentScore
    } = sentimentAnnotations[selectedSentimentIndex];
    const hasSentiment = Number.isFinite(sentimentScore);
    const hasConfidence = Number.isFinite(sentimentConfidence);
    const boxElement = jsxs("div", {
      className: "kxi-comment-text-box",
      style: {
        top: sentimentBoxPosition.top,
        left: sentimentBoxPosition.left,
        visibility: sentimentBoxPosition.visibility
      },
      "data-testid": "comment-sentiment-box",
      children: [hasSentiment ? jsx("div", {
        className: "kxi-comment-text-box-inner-text",
        children: `${localization.sentimentScore}: ${sentimentAnnotations[selectedSentimentIndex].sentimentScore}`
      }) : null, hasConfidence ? jsx("div", {
        className: "kxi-comment-text-box-inner-text",
        children: `${localization.confidenceLevel}: ${getConfidenceAsPercent(sentimentConfidence)}`
      }) : null]
    });
    setBoxElement(!hasSentiment && !hasConfidence ? null : boxElement);
  }, [commentText]);
  React.useEffect(() => {
    formatText();
  }, [textSlices, activeSentimentId, activeTags]);
  React.useEffect(() => {
    setTextSlices(chopText());
  }, [text, sentimentAnnotations, combinedTags, activeTags, activeSentiments, highlights]);
  const getSentimentBoxPosition = () => {
    const defaultRetVal = {
      left: 0,
      visibility: 'hidden',
      top: 0
    };
    const commentRoot = document.getElementById(externalId);
    if (!commentRoot) {
      return defaultRetVal;
    }
    const selectedSentimentDivLefts = Array.from(commentRoot.querySelectorAll('.kxi-comment-text-active')).map(item => {
      var _a;
      return item.getBoundingClientRect().left - (((_a = item.parentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().left) || 0);
    });
    if (selectedSentimentDivLefts.length === 0) {
      return defaultRetVal;
    }
    const left = selectedSentimentDivLefts.reduce((a, b) => a < b ? a : b);
    const selectedSentimentDivBottoms = Array.from(commentRoot.querySelectorAll('.kxi-comment-text-active')).map(item => {
      var _a;
      return item.getBoundingClientRect().bottom - (((_a = item.parentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) || 0);
    });
    if (selectedSentimentDivBottoms.length === 0) {
      return defaultRetVal;
    }
    const top = selectedSentimentDivBottoms.reduce((a, b) => a < b ? b : a);
    return {
      left,
      top: top,
      visibility: 'visible'
    };
  };
  /**
   * This function is the brains of this component. It adds each individual piece into an
   * array with begin and end indexes per tag/sentiment/highlighting. It then takes those
   * separators and chops them up and combines them (spliceChop function). Finally it
   * chops the text and adds it to each chopped section and then sorts by begin index to
   * get the text in the correct order.
   */
  const chopText = () => {
    const tagPositions = [];
    combinedTags.forEach(tag => {
      if (tag.positions) {
        tag.positions.forEach(position => {
          tagPositions.push({
            tagId: tag.tagId,
            begin: position.beginIndex,
            end: position.endIndex
          });
        });
      }
    });
    const separators = [];
    tagPositions.forEach(position => {
      separators.push({
        tagIds: [position.tagId],
        sentimentIds: [],
        highlighted: false,
        begin: position.begin,
        end: position.end
      });
    });
    sentimentAnnotations === null || sentimentAnnotations === void 0 ? void 0 : sentimentAnnotations.forEach(sentimentAnnotation => {
      separators.push({
        tagIds: [],
        sentimentIds: [sentimentAnnotation.sentimentId],
        highlighted: false,
        begin: sentimentAnnotation.beginIndex,
        end: sentimentAnnotation.endIndex
      });
    });
    highlights.forEach(highlight => {
      separators.push({
        tagIds: [],
        sentimentIds: [],
        highlighted: true,
        begin: highlight.begin,
        end: highlight.end
      });
    });
    const choppedSeparators = [{
      tagIds: [],
      sentimentIds: [],
      highlighted: false,
      begin: 0,
      end: text.length
    }];
    separators.forEach(separator => {
      spliceChop(choppedSeparators, separator, hasWarned, setHasWarned);
    });
    const choppedText = [];
    choppedSeparators.forEach(separator => {
      choppedText.push(Object.assign(Object.assign({}, separator), {
        text: text.substring(separator.begin, separator.end)
      }));
    });
    return choppedText.sort((a, b) => {
      return a.begin - b.begin;
    });
  };
  const getTextClass = slice => {
    let textClass = 'kxi-comment-text-item ';
    textClass += slice.highlighted ? 'kxi-comment-highlighted ' : '';
    const activeSentimentDisplay = activeSentiments.filter(sentiment => slice.sentimentIds.includes(sentiment.sentimentId));
    if (slice.tagIds.length === 0) {
      if (sentimentAnnotations) {
        activeSentimentDisplay.forEach(sentiment => {
          textClass += `kxi-comment-text-has-sentiment kxi-${SentimentEnum[sentiment.sentiment]} `;
        });
      }
    } else {
      if (activeTags.filter(activeTag => activeTag.tagId && slice.tagIds.includes(activeTag.tagId)).length > 0) {
        textClass += 'kxi-tag-selected ';
      } else {
        textClass += 'kxi-tag ';
      }
    }
    if (activeSentimentId && slice.sentimentIds.includes(activeSentimentId)) {
      textClass += 'kxi-comment-text-active ';
      if (sentimentAnnotations) {
        activeSentimentDisplay.forEach(sentiment => {
          textClass += `kxi-${SentimentEnum[sentiment.sentiment]} `;
        });
      }
    }
    return textClass;
  };
  const formatText = () => {
    const textElements = [];
    textSlices.forEach(slice => {
      const id = uuidv4();
      const element = jsx("div", {
        className: getTextClass(slice),
        id: uuidv4(),
        onMouseEnter: () => {
          //if the text is part of an active sentiment then highlight it
          if (activeSentiments.filter(sentiment => slice.sentimentIds.includes(sentiment.sentimentId)).length > 0) {
            setActiveSentimentId(slice.sentimentIds ? activeSentiments.filter(sentiment => {
              var _a;
              return (_a = slice.sentimentIds) === null || _a === void 0 ? void 0 : _a.includes(sentiment.sentimentId);
            })[0].sentimentId : undefined);
          }
        },
        onMouseLeave: () => {
          setActiveSentimentId(undefined);
        },
        children: replaceOuterSpacesWithNonBreakingSpaces(slice.text)
      }, 'text-slice-' + id);
      textElements.push(slice.highlighted ? jsx("mark", {
        children: element
      }, 'text-slice-mark' + id) : element);
    });
    setCommentText(jsx("div", {
      children: textElements
    }));
  };
  return jsx("div", {
    className: "kxi-comment-text",
    children: jsxs("div", {
      className: "kxi-comment-text-wrapper",
      children: [jsx("div", {
        className: "kxi-comment-text-box-wrapper",
        children: boxElement
      }), jsx("div", {
        className: "kxi-visible-text",
        children: commentText
      })]
    })
  });
};
function replaceOuterSpacesWithNonBreakingSpaces(text) {
  return text.replace(/^\s/gu, '\u00A0') // Leading spaces
  .replace(/\p{Space_Separator}(?=\p{Space_Separator}*$)/gu, '\u00A0'); // Trailing spaces
}

const ImageCarouselItem = props => {
  const {
    images,
    onImagePress,
    activeIndex: activeIndexInput = 0,
    isLarge = false,
    preventDefault
  } = props;
  // how many px from the left or right the animation begins/ends with the slide fade.
  const presenceFrom = 46;
  const [activeIndex, setActiveIndex] = React__default.useState(activeIndexInput);
  const [goingBackwards, setGoingBackwards] = React__default.useState(false);
  // Bind and unbind events
  React__default.useEffect(() => {
    if (!preventDefault) {
      window.addEventListener('keyup', keyPress);
      return () => {
        window.removeEventListener('keyup', keyPress);
      };
    }
  }, [goingBackwards, activeIndex, preventDefault]);
  const keyPress = event => {
    if (event.keyCode === 39) {
      rightArrowPress();
    } else if (event.keyCode === 37) {
      leftArrowPress();
    }
  };
  React__default.useEffect(() => {
    if (activeIndexInput !== undefined) setActiveIndex(activeIndexInput);
  }, [activeIndexInput]);
  const indicators = images.map((_item, index) => {
    return jsx("div", {
      className: activeIndex === index ? 'kxi-image-carousel-indicator-active' : 'kxi-image-carousel-indicator-inactive',
      onClick: () => {
        setGoingBackwards(index < activeIndex);
        setActiveIndex(index);
      },
      "data-testid": `image-carousel-indicator-${index}`
    }, `image-carousel-indicator-${index}`);
  });
  const leftArrowPress = () => {
    setGoingBackwards(true);
    setActiveIndex(images.length > 0 ? activeIndex > 0 ? activeIndex - 1 : images.length - 1 : 0);
  };
  const rightArrowPress = () => {
    setGoingBackwards(false);
    setActiveIndex(images.length > 0 ? activeIndex < images.length - 1 ? activeIndex + 1 : 0 : 0);
  };
  return jsxs("div", {
    className: `kxi-image-carousel-wrapper ${isLarge ? 'kxi-image-carousel-wrapper-large' : ''}`,
    children: [jsxs("div", {
      className: `kxi-image-carousel-row-wrapper ${isLarge ? 'kxi-image-carousel-row-wrapper-large' : ''}`,
      children: [jsx("div", {
        className: "kxi-image-carousel-arrow kxi-image-carousel-arrow-left",
        children: jsx(IconButton, {
          icon: "single_arrow_left",
          onClick: leftArrowPress
        })
      }), jsx("div", {
        className: `kxi-image-carousel-image-size${isLarge ? '-large kxi-image-carousel-image-wrapper-large' : ''} kxi-image-carousel-image-wrapper`,
        children: jsx(AnimatePresence, {
          children: jsx(motion.img, {
            initial: {
              x: goingBackwards ? presenceFrom * -1 : presenceFrom,
              opacity: 0
            },
            animate: {
              x: 0,
              opacity: 1
            },
            exit: {
              x: goingBackwards ? presenceFrom : presenceFrom * -1,
              opacity: 0
            },
            transition: {
              ease: 'easeInOut',
              duration: 0.5
            },
            className: `kxi-image-carousel-image-size${isLarge ? '-large kxi-image-carousel-image-large' : ''} kxi-image-carousel-image`,
            src: images[activeIndex],
            onClick: () => {
              if (onImagePress) onImagePress(images[activeIndex]);
            }
          }, `image-carousel-${activeIndex}`)
        })
      }), jsx("div", {
        className: "kxi-image-carousel-arrow kxi-image-carousel-arrow-right",
        children: jsx(IconButton, {
          icon: "single_arrow_right",
          onClick: rightArrowPress
        })
      })]
    }), jsx("div", {
      className: "kxi-image-carousel-indicator-wrapper",
      children: indicators
    })]
  });
};

class ImageCarouselProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Array)], ImageCarouselProps.prototype, "images", void 0);
__decorate([Bound(), __metadata("design:type", Number)], ImageCarouselProps.prototype, "activeIndex", void 0);
__decorate([Bound(), __metadata("design:type", String)], ImageCarouselProps.prototype, "indicatorColor", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ImageCarouselProps.prototype, "genericModalProps", void 0);
class ImageCarouselEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ImageCarouselEvents.prototype, "onImagePress", void 0);
/**
 * Description: Convenient Carousel to display all your favorite images.
 */
const ImageCarousel = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  useDebugService(ImageCarousel, props);
  const printView = useIsPrinting(props._printView);
  return !printView ? jsx(ImageCarouselImpl, Object.assign({}, props, {
    ref: ref
  })) : jsx(ImageCarouselPrintView, Object.assign({}, props, {
    ref: ref
  }));
});
ImageCarousel.displayName = 'ImageCarousel';
const ImageCarouselImpl = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const debugService = useDebugService(ImageCarouselImpl, props);
  const {
      images,
      indicatorColor = 'orca',
      onImagePress,
      genericModalProps
    } = props,
    ingredients = __rest(props, ["images", "indicatorColor", "onImagePress", "genericModalProps"]);
  const [isModalOpen, setIsModalOpen] = React__default.useState(false);
  const [modalActiveImage, setModalActiveImage] = React__default.useState(0);
  throwIfRequiredPropNotProvided('ImageCarousel', {
    images
  });
  return jsxs("div", Object.assign({}, debugService === null || debugService === void 0 ? void 0 : debugService.componentProps, ingredients, {
    className: `${normalizeColor(indicatorColor)}`,
    ref: ref,
    children: [jsx(ImageCarouselItem, {
      images: images,
      onImagePress: image => {
        setIsModalOpen(true);
        let activeImage = images.findIndex(item => item === image);
        activeImage = activeImage >= 0 ? activeImage : 0;
        setModalActiveImage(activeImage);
        if (onImagePress) {
          onImagePress({
            image
          });
        }
      },
      preventDefault: isModalOpen
    }), jsx(GenericModal, Object.assign({
      useLegacy: false,
      minWidth: "80%",
      minHeight: "80%"
    }, genericModalProps, {
      isVisible: isModalOpen,
      onClose: () => setIsModalOpen(false),
      children: jsx(ImageCarouselItem, {
        images: images,
        activeIndex: modalActiveImage,
        onImagePress: image => {
          setIsModalOpen(false);
          if (onImagePress) {
            onImagePress({
              image
            });
          }
        },
        isLarge: true
      })
    }))]
  }));
});
ImageCarouselImpl.displayName = 'ImageCarouselImpl';
const ImageCarouselPrintView = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const debugService = useDebugService(ImageCarouselPrintView, props);
  return jsx("div", Object.assign({}, debugService.componentProps, {
    ref: ref,
    className: "kxi-image-carousel-print-view",
    children: props.images.map((src, i) => jsx("img", {
      src: src,
      alt: src
    }, src + i))
  }));
});
ImageCarouselPrintView.displayName = 'ImageCarouselPrintView';

const {
  white: white$2,
  orca: orca$1
} = XIColors;
const {
  colorDisabled: colorDisabled$1
} = XIColorUsage;
/**
 * A replacement for `e instanceof TouchEvent` that makes sure the `TouchEvent` class is present in the current environment.
 *
 * As of 24 July 2023:
 * - Safari does not support the `TouchEvent` API.
 * - Firefox supports the API but doesn't support the `TouchEvent` constructor, which means the `TouchEvent` global variable is `undefined`.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/TouchEvent#browser_compatibility
 */
function isTouchEvent(e) {
  return typeof e.changedTouches !== 'undefined';
}
class SliderProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Number)], SliderProps.prototype, "min", void 0);
__decorate([Bound(), __metadata("design:type", Number)], SliderProps.prototype, "max", void 0);
__decorate([Bound(), __metadata("design:type", String)], SliderProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], SliderProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SliderProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", Object)], SliderProps.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", String)], SliderProps.prototype, "unit", void 0);
__decorate([Bound(), __metadata("design:type", String)], SliderProps.prototype, "data-qa", void 0);
__decorate([Bound(), __metadata("design:type", Object)], SliderProps.prototype, "endLabels", void 0);
__decorate([Bound(), __metadata("design:type", Number)], SliderProps.prototype, "increment", void 0);
__decorate([Bound(), __metadata("design:type", Object)], SliderProps.prototype, "inflection", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SliderProps.prototype, "displayCurrentPosition", void 0);
class SliderEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], SliderEvents.prototype, "onSliderChange", void 0);
/**
 * Description: Range slider allowing you to get input as a number / range of numbers.
 */
const Slider = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a, _b, _c, _d;
  const debugService = useDebugService(Slider, props);
  const {
      min = 0,
      max = 100,
      unit = '',
      color = 'CX',
      onSliderChange,
      contrast = 'normal',
      className,
      observer,
      disabled,
      value,
      endLabels,
      displayCurrentPosition = true,
      inflection,
      increment = 1
    } = props,
    ingredients = __rest(props, ["min", "max", "unit", "color", "onSliderChange", "contrast", "className", "observer", "disabled", "value", "endLabels", "displayCurrentPosition", "inflection", "increment"]);
  if ('type' in ingredients) {
    delete ingredients.type;
    devOnlyWarn(`The Kitchen: You have passed a type, "${props.type}" into the Slider component, this will have no effect.`);
  }
  const track = React.useRef(null);
  const completedTrack = React.useRef(null);
  const draggableArea = React.useRef(null);
  const primaryKnob = React.useRef(null);
  const secondaryKnob = React.useRef(null);
  const primaryBubble = React.useRef(null);
  const secondaryBubble = React.useRef(null);
  const [trackWidth, setTrackWidth] = React.useState(0);
  const [trackLeft, setTrackLeft] = React.useState(0);
  const [draggingPrimary, setDraggingPrimary] = React.useState(false);
  const [draggingSecondary, setDraggingSecondary] = React.useState(false);
  const [bubbleMerge, setBubbleMerge] = React.useState(false);
  const [settingUp, setSettingUp] = React.useState(false);
  const [primaryActive, setPrimaryActive] = useActive();
  const [secondaryActive, setSecondaryActive] = useActive();
  const [knobClicked, setKnobClicked] = React.useState(false);
  const [primaryX, setPrimaryX] = React.useState(0);
  const [secondaryX, setSecondaryX] = React.useState(0);
  const [numMin, setNumMin] = React.useState(Number(min));
  const [numMax, setNumMax] = React.useState(Number(max));
  const [primaryLeft, setPrimaryLeft] = React.useState(numMin);
  const [secondaryLeft, setSecondaryLeft] = React.useState(numMin);
  const isRange = !!(value && typeof value !== 'number');
  const [mouseX, setMouseX] = React.useState(0);
  const [trackHover, setTrackHover] = React.useState(false);
  const [mouseDown, setMouseDown] = React.useState(false);
  const [isDraggingFromLeftTrack, setIsDraggingFromLeftTrack] = React.useState(false);
  const updateTrackLeftAndWidth = useConstant(() => {
    if (track.current) {
      setTrackWidth(track.current.clientWidth);
      setTrackLeft(track.current.getBoundingClientRect().left + document.documentElement.scrollLeft);
    }
  });
  const formatNumber = num => {
    let numDecimalPlaces = 0;
    const numStr = String(increment);
    if (numStr.includes('.')) {
      numDecimalPlaces = numStr.split('.')[1].length;
    }
    return num.toFixed(numDecimalPlaces);
  };
  const getProportionsFromPx = point => {
    return fixFloatingPoint(roundToNearest(point / trackWidth * (numMax - numMin), increment) + numMin);
  };
  const roundToNearest = (input, increment) => {
    return Math.round(input / increment) * increment;
  };
  const fixFloatingPoint = x => {
    return Number(x.toPrecision(7)); //7 is the max reliable precision for a 32 bit number
  };
  const outOfRangeWarning = (boundaryError, val0, val1) => {
    if (boundaryError === 'swapped' && typeof val0 === 'number' && typeof val1 === 'number') {
      devOnlyWarn(`The Kitchen: Your data set is flipped in Slider, value[0] = ${val0}, is greater than value[1] = ${val1}. It has been flipped back for you as this is unsupported. This may have unintended side effects.`);
    } else {
      const boundaryName = boundaryError === 'max' ? 'more' : 'less';
      const boundary = boundaryError === 'max' ? max : min;
      devOnlyError(`The Kitchen: You have passed a value (${val0}) into the Slider component that is ${boundaryName} than the ${boundaryError} (${boundary}).`);
    }
  };
  const testRange = value => {
    if (value < min) {
      outOfRangeWarning('min', value);
      return min;
    } else if (value > max) {
      outOfRangeWarning('max', value);
      return max;
    } else {
      return value;
    }
  };
  React.useEffect(() => {
    setNumMin(min);
    setNumMax(max);
  }, [min, max]);
  React.useEffect(() => {
    var _a;
    const updateMousePosition = evt => {
      var _a, _b;
      const left = (_b = (_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.left;
      const point = Math.floor(evt.clientX - (left || 0));
      if (left) {
        if (!primaryActive && !disabled) {
          setMouseX(point);
        }
      }
    };
    const handleMouseDown = () => {
      setMouseDown(true);
    };
    const handleMouseUp = () => {
      setPrimaryActive(false);
      setMouseDown(false);
    };
    window.addEventListener('mousemove', updateMousePosition);
    window.addEventListener('mouseup', handleMouseUp);
    // eslint-disable-next-line no-unused-expressions
    (_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.addEventListener('mousedown', handleMouseDown);
    return () => {
      var _a;
      window.removeEventListener('mousemove', updateMousePosition);
      window.removeEventListener('mouseup', handleMouseUp);
      // eslint-disable-next-line no-unused-expressions
      (_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);
  React.useEffect(() => {
    // this effect controls dragging by simulating events not available
    const newPoint = getProportionsFromPx(mouseX);
    // checks for mouse down, and within bounds
    if (!disabled && !isRange && mouseDown && newPoint >= numMin && newPoint <= numMax) {
      setPrimaryX(mouseX);
      setPrimaryLeft(getProportionsFromPx(mouseX));
    } else if (!disabled && isRange && mouseDown && newPoint >= numMin && newPoint <= numMax) {
      const diffLeft2 = Math.abs(secondaryLeft - newPoint);
      const diffLeft = primaryLeft - newPoint;
      // which side was clicked
      if (diffLeft2 <= diffLeft) {
        // isDragging checks what side it started to it can't go past the other knob
        if (!isDraggingFromLeftTrack) {
          setPrimaryX(secondaryX);
          setPrimaryLeft(secondaryLeft);
        } else {
          setSecondaryX(mouseX);
          setSecondaryLeft(newPoint);
        }
      } else if (diffLeft2 >= diffLeft) {
        if (isDraggingFromLeftTrack) {
          setSecondaryX(primaryX);
          setSecondaryLeft(primaryLeft);
        } else {
          setPrimaryX(mouseX);
          setPrimaryLeft(newPoint);
        }
      }
    }
  }, [mouseX]);
  React.useEffect(() => {
    var _a, _b;
    if (track.current) updateTrackLeftAndWidth();
    if (isRange) {
      const bubbleOneWidth = ((_a = primaryBubble.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
      const bubbleTwoWidth = ((_b = secondaryBubble.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0;
      if (secondaryX + bubbleTwoWidth >= primaryX - bubbleOneWidth && !bubbleMerge) {
        setBubbleMerge(true);
      } else if (secondaryX + bubbleTwoWidth < primaryX - bubbleOneWidth && bubbleMerge) {
        setBubbleMerge(false);
      }
    }
  }, [primaryX, secondaryX]);
  React.useEffect(() => {
    setPrimaryActive(draggingPrimary);
  }, [draggingPrimary]);
  React.useEffect(() => {
    setSecondaryActive(draggingSecondary);
  }, [draggingSecondary]);
  React.useEffect(() => {
    if (onSliderChange && !disabled && (draggingPrimary || draggingSecondary || primaryActive || secondaryActive)) {
      if (Array.isArray(value) && value.length === 2) {
        onSliderChange({
          value: [secondaryLeft, primaryLeft]
        });
        if (observer) {
          observer.value = [secondaryLeft, primaryLeft];
        }
      } else {
        onSliderChange({
          value: primaryLeft
        });
        if (observer) {
          observer.value = primaryLeft;
        }
      }
    }
  }, [primaryLeft, secondaryLeft]);
  const swapMax = () => {
    if (numMin > numMax) {
      const m = numMax;
      setNumMax(numMin);
      setNumMin(m);
    }
  };
  React.useLayoutEffect(() => {
    setSettingUp(true);
    if (typeof value === 'number' && track.current) {
      let tempValue = value;
      // single
      tempValue = testRange(tempValue);
      updateTrackLeftAndWidth();
      const xPos = track.current.clientWidth / (numMax - numMin) * (tempValue - numMin);
      setPrimaryX(xPos >= 0 ? xPos <= track.current.clientWidth ? xPos : track.current.clientWidth : 0);
      if (observer) {
        observer.value = tempValue;
      }
      setPrimaryLeft(tempValue);
    } else if (Array.isArray(value) && value.length === 2 && track.current) {
      let tempValue = value;
      // multi
      if (tempValue[0] > tempValue[1]) {
        //flipped array
        outOfRangeWarning('swapped', tempValue[0], tempValue[1]);
        tempValue = [tempValue[1], tempValue[0]];
      }
      tempValue[0] = testRange(tempValue[0]);
      tempValue[1] = testRange(tempValue[1]);
      updateTrackLeftAndWidth();
      if (Array.isArray(tempValue) && tempValue.length === 2) {
        const xPos = track.current.clientWidth / (numMax - numMin) * (tempValue[1] - numMin);
        const xPos2 = track.current.clientWidth / (numMax - numMin) * (tempValue[0] - numMin);
        setPrimaryX(xPos >= 0 ? xPos <= track.current.clientWidth ? xPos : track.current.clientWidth : 0);
        setSecondaryX(xPos2 >= 0 ? xPos2 <= track.current.clientWidth ? xPos2 : track.current.clientWidth : 0);
        setPrimaryLeft(tempValue[1]);
        setSecondaryLeft(tempValue[0]);
        if (observer) {
          observer.value = [tempValue[0], tempValue[1]];
        }
      } else {
        if (observer) {
          observer.value = [numMin, numMax];
        }
        setPrimaryX(track.current.clientWidth / (numMax - numMin));
        setSecondaryX(0);
        setPrimaryLeft(numMax);
        setSecondaryLeft(numMin);
      }
      // incorrect number of items
    } else if (Array.isArray(value) && value.length !== 2) {
      devOnlyError(`The Kitchen: You have passed in an array to Slider with ${value.length} elements, instead of 2. Please only place 2 items in the array.`);
    }
  }, [value]);
  const onResize = React.useCallback(() => {
    if (settingUp) {
      setSettingUp(false);
    } else {
      if (track.current) {
        updateTrackLeftAndWidth();
        setPrimaryX(track.current.clientWidth / Math.abs(min - max) * Math.abs(min - primaryLeft));
        if (isRange) setSecondaryX(track.current.clientWidth / Math.abs(min - max) * Math.abs(min - secondaryLeft));
      }
    }
  }, [numMin, numMax, settingUp]);
  const onTrackClick = evt => {
    var _a, _b, _c, _d;
    if (!disabled) {
      if (!isRange) {
        setPrimaryActive(true);
        setTrackHover(false);
        const newLeft = (_b = (_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.left;
        const xCord = evt.clientX - (newLeft || 0);
        setPrimaryX(xCord);
        setPrimaryLeft(getProportionsFromPx(xCord));
      } else {
        setPrimaryActive(true);
        setTrackHover(false);
        const newLeft = (_d = (_c = track === null || track === void 0 ? void 0 : track.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect()) === null || _d === void 0 ? void 0 : _d.left;
        const xCord = evt.clientX - (newLeft || 0);
        const newPoint = getProportionsFromPx(xCord);
        // less than the left knob
        // setting the dragging so the effect with mouseX knows which side it started
        if (newPoint <= secondaryLeft) {
          setIsDraggingFromLeftTrack(true);
          setSecondaryX(xCord);
          setSecondaryLeft(newPoint);
          // greater than the right knob
        } else if (newPoint >= primaryLeft) {
          setIsDraggingFromLeftTrack(false);
          setPrimaryX(xCord);
          setPrimaryLeft(newPoint);
          // between the knobs
        } else {
          const diffLeft2 = Math.abs(secondaryLeft - newPoint);
          const diffLeft = primaryLeft - newPoint;
          if (diffLeft2 < diffLeft) {
            setIsDraggingFromLeftTrack(true);
            setSecondaryX(xCord);
            setSecondaryLeft(newPoint);
          } else {
            setIsDraggingFromLeftTrack(false);
            setPrimaryX(xCord);
            setPrimaryLeft(newPoint);
          }
        }
      }
    }
  };
  swapMax();
  const knobStyle = isActive => {
    return {
      cursor: disabled ? 'auto' : 'pointer',
      left: isActive ? -15 : -8,
      width: isActive ? 30 : 18,
      zIndex: isActive ? 10 : 5
    };
  };
  const getOffset = () => {
    if (completedTrack.current) return completedTrack.current.clientWidth / 2;
  };
  const isBubbleActive = disabled ? false : primaryActive || secondaryActive;
  const bubbleTransform = {
    transform: `translateX(${bubbleMerge ? `calc(-${getOffset()}px + -50%)` : '-50%'}) translateX(${isBubbleActive ? 13 : 7}px`
  };
  const showBubble = trackHover && !primaryActive && getProportionsFromPx(mouseX) >= numMin && getProportionsFromPx(mouseX) <= numMax;
  const bubbleStyle = Object.assign(Object.assign({}, bubbleTransform), {
    background: isBubbleActive ? white$2.primary : 'none',
    border: isBubbleActive ? `2px solid ${getColor(color)}` : 'none',
    fontWeight: isBubbleActive ? 600 : 400,
    bottom: isBubbleActive ? 28 : 16,
    color: isBubbleActive ? orca$1.primary : undefined
  });
  const bubbleTransformTrackHover = {
    transform: `translateX(-50%) translateX(${mouseX}px)`
  };
  const bubbleStyleTwo = Object.assign(Object.assign({}, bubbleTransformTrackHover), {
    display: showBubble ? 'block' : 'none',
    userSelect: 'none',
    background: white$2.primary,
    border: `2px solid ${disabled ? colorDisabled$1 : getColor(color)}`,
    bottom: 16,
    color: isBubbleActive || showBubble ? orca$1.primary : undefined,
    zIndex: 1000
  });
  const getBubbleText = (isSpace, value1, value2) => {
    let doubleValueText;
    let singleValueText;
    if (unit === 'media') {
      value1 = Math.floor(value1);
      value2 = Math.floor(value2);
      const seconds1 = value1 % 60;
      const seconds2 = value2 % 60;
      const minutes1 = Math.floor(value1 / 60);
      const minutes2 = Math.floor(value2 / 60);
      doubleValueText = `${minutes2}:${`${seconds2 < 10 ? '0' : ''}` + seconds2} to ${minutes1}:${`${seconds1 < 10 ? '0' : ''}` + seconds1}`;
      singleValueText = `${minutes1}:${`${seconds1 < 10 ? '0' : ''}` + seconds1}`;
    } else {
      doubleValueText = `${formatNumber(value2)} to ${formatNumber(value1)} ${unit}`;
      singleValueText = `${formatNumber(value1)} ${unit}`;
    }
    return isSpace ? bubbleMerge && isRange ? singleValueText : doubleValueText : bubbleMerge && isRange ? doubleValueText : singleValueText;
  };
  const contrastClass = contrast === 'high' ? ' kxi-high-contrast' : '';
  const totalScale = max - min;
  const widthPerUnitScale = trackWidth / totalScale;
  const inflectionPointUnitsFromLeft = ((inflection === null || inflection === void 0 ? void 0 : inflection.inflectionPoint) || 0) - min;
  const inflectionPointWidthFromLeft = inflectionPointUnitsFromLeft * widthPerUnitScale;
  const inflectionPointWidthFromRight = trackWidth - inflectionPointWidthFromLeft;
  const handleKeyDown = knob => e => {
    const {
      key
    } = e;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
      return;
    }
    e.preventDefault();
    if (disabled) return;
    const isPrimaryKnob = knob === 'primary';
    if (isPrimaryKnob) {
      setPrimaryActive('temporarily_active');
      setSecondaryActive(false);
    } else {
      setSecondaryActive('temporarily_active');
      setPrimaryActive(false);
    }
    const minimum = isRange && isPrimaryKnob ? secondaryLeft : numMin;
    const maximum = isRange && !isPrimaryKnob ? primaryLeft : numMax;
    const currentLeft = isPrimaryKnob ? primaryLeft : secondaryLeft;
    const newLeft = {
      ArrowLeft: currentLeft - 1,
      ArrowRight: currentLeft + 1,
      Home: minimum,
      End: maximum
    }[key];
    const adjustedLeft = clamp(newLeft, minimum, maximum);
    const adjustedX = (adjustedLeft - numMin) / (numMax - numMin) * trackWidth;
    if (isPrimaryKnob) {
      setPrimaryX(adjustedX);
      setPrimaryLeft(adjustedLeft);
    } else {
      setSecondaryX(adjustedX);
      setSecondaryLeft(adjustedLeft);
    }
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    "aria-disabled": disabled,
    role: "slider",
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuenow": primaryLeft,
    className: "kxi-slider-container",
    children: [(((_a = endLabels === null || endLabels === void 0 ? void 0 : endLabels.left) === null || _a === void 0 ? void 0 : _a.icon) || ((_b = endLabels === null || endLabels === void 0 ? void 0 : endLabels.left) === null || _b === void 0 ? void 0 : _b.displayValue)) && jsxs("div", {
      className: `kxi-slider-end-label kxi-slider-end-label-left ${contrastClass}`,
      children: [endLabels.left.icon && jsx(XIIcon, {
        icon: endLabels.left.icon,
        color: endLabels.left.sentiment || 'orca',
        contrast: contrast
      }), endLabels.left.displayValue && jsx("p", {
        children: min
      })]
    }), jsxs("div", Object.assign({
      className: `kxi-slider orca ${className || ''}`,
      ref: ref
    }, ingredients, {
      children: [isRange ? jsxs("div", {
        style: {
          display: 'none'
        },
        children: [jsx("input", {
          readOnly: true,
          type: "range",
          value: secondaryLeft,
          name: "slider_value_1"
        }), jsx("input", {
          readOnly: true,
          type: "range",
          value: primaryLeft,
          name: "slider_value_2"
        })]
      }) : jsx("input", {
        readOnly: true,
        style: {
          display: 'none'
        },
        type: "range",
        value: secondaryLeft,
        name: "slider_value"
      }), jsx(ReactResizeDetector, {
        refreshMode: "debounce",
        refreshRate: 0,
        handleWidth: true,
        onResize: onResize,
        targetRef: track,
        children: jsx("div", {
          className: "kxi-track-bounds",
          ref: track,
          onMouseDown: onTrackClick,
          onMouseUp: () => setPrimaryActive(false),
          onMouseOver: () => {
            if (!mouseDown && !knobClicked && !disabled) setTrackHover(true);
          },
          onMouseLeave: () => {
            if (!mouseDown && !knobClicked) setPrimaryActive(false);
            setTrackHover(false);
          },
          style: {
            cursor: disabled ? 'auto' : 'pointer'
          },
          children: jsxs("div", {
            className: "kxi-track",
            onClick: onTrackClick,
            style: {
              cursor: disabled ? 'auto' : 'pointer'
            },
            children: [jsx("div", {
              onClick: onTrackClick,
              className: "kxi-track-completed",
              style: {
                cursor: disabled ? 'auto' : 'pointer',
                background: disabled ? colorDisabled$1 : getColor(color),
                left: isRange ? secondaryX : 0,
                right: trackWidth - primaryX
              },
              ref: completedTrack
            }), inflection && jsxs(Fragment, {
              children: [jsx("div", {
                className: `kxi-track-completed kxi-slider-inflection kxi-${inflection === null || inflection === void 0 ? void 0 : inflection.sentimentLeft}`,
                style: {
                  cursor: disabled ? 'auto' : 'pointer',
                  left: secondaryX,
                  right: inflectionPointWidthFromRight > trackWidth - primaryX ? inflectionPointWidthFromRight : trackWidth - primaryX
                }
              }), jsx("div", {
                className: `kxi-track-completed kxi-slider-inflection kxi-${inflection === null || inflection === void 0 ? void 0 : inflection.sentimentRight}`,
                style: {
                  cursor: disabled ? 'auto' : 'pointer',
                  left: inflectionPointWidthFromLeft > secondaryX ? inflectionPointWidthFromLeft : secondaryX,
                  right: trackWidth - primaryX
                }
              })]
            })]
          })
        })
      }), isRange && jsx(motion.div, {
        ref: primaryKnob,
        style: {
          position: 'absolute',
          zIndex: 1
        },
        drag: disabled ? false : 'x',
        animate: {
          x: secondaryX
        },
        dragConstraints: {
          left: 0,
          right: primaryX
        },
        dragElastic: 0,
        dragMomentum: false,
        onDragStart: () => {
          if (!disabled) {
            setSecondaryActive(true);
            setKnobClicked(true);
            setDraggingSecondary(true);
          }
        },
        onDrag: e => {
          var _a;
          const cursorPageX = isTouchEvent(e) ? (_a = e.changedTouches[0]) === null || _a === void 0 ? void 0 : _a.pageX : e.pageX;
          if (cursorPageX == null) return;
          if (!disabled) {
            const newX2 = clamp(cursorPageX - trackLeft, 0, primaryX);
            setSecondaryX(newX2);
            setSecondaryLeft(getProportionsFromPx(newX2));
          }
        },
        onDragEnd: () => {
          if (!disabled) {
            setKnobClicked(false);
            setDraggingSecondary(false);
          }
        },
        transition: {
          duration: 0,
          ease: 'easeInOut'
        },
        children: jsx("div", {
          "data-testid": "slider_2",
          className: classNames('kxi-knob', contrastClass, {
            disabled: disabled,
            'kxi-slider-inflection': inflection
          }, inflection && (secondaryLeft < inflection.inflectionPoint ? `kxi-${inflection.sentimentLeft}` : `kxi-${inflection.sentimentRight}`)),
          style: knobStyle(secondaryActive),
          tabIndex: 0,
          "aria-disabled": disabled,
          onKeyDown: handleKeyDown('secondary'),
          onBlur: () => setSecondaryActive(false),
          children: bubbleMerge || !displayCurrentPosition ? null : jsxs(Fragment, {
            children: [jsxs("div", {
              className: `kxi-bubble kxi-text-normal${contrastClass} ${isBubbleActive ? 'kxi-bubble-active' : ''}`,
              style: bubbleStyle,
              children: [secondaryLeft, " ", unit]
            }), jsx("div", {
              className: `kxi-bubble-space `,
              style: bubbleTransform,
              ref: secondaryBubble,
              children: getBubbleText(true, secondaryLeft, primaryLeft)
            })]
          })
        })
      }), jsx("div", {
        className: "kxi-draggable-area",
        ref: draggableArea,
        style: {
          left: secondaryX + 2
        }
      }), jsx(motion.div, {
        ref: secondaryKnob,
        style: {
          position: 'absolute',
          zIndex: 1
        },
        drag: disabled ? false : 'x',
        animate: {
          x: primaryX
        },
        dragConstraints: {
          left: secondaryX,
          right: trackWidth
        },
        dragElastic: 0,
        dragMomentum: false,
        onDragStart: () => {
          if (!disabled) {
            setKnobClicked(true);
            setDraggingPrimary(true);
          }
        },
        onDrag: e => {
          var _a;
          const cursorPageX = isTouchEvent(e) ? (_a = e.changedTouches[0]) === null || _a === void 0 ? void 0 : _a.pageX : e.pageX;
          if (cursorPageX == null) return;
          if (!disabled) {
            const newX = clamp(cursorPageX - trackLeft, isRange ? secondaryX : 0, trackWidth);
            setPrimaryX(newX);
            setPrimaryLeft(getProportionsFromPx(newX));
          }
        },
        onDragEnd: () => {
          if (!disabled) {
            setKnobClicked(false);
            setDraggingPrimary(false);
          }
        },
        transition: {
          duration: 0,
          ease: 'easeInOut'
        },
        children: jsx("div", {
          "data-testid": "slider_1",
          className: classNames('kxi-knob', contrastClass, {
            disabled: disabled,
            'kxi-slider-inflection': inflection
          }, inflection && (primaryLeft < inflection.inflectionPoint ? `kxi-${inflection.sentimentLeft}` : `kxi-${inflection.sentimentRight}`)),
          style: knobStyle(primaryActive),
          tabIndex: 0,
          "aria-disabled": disabled,
          onKeyDown: handleKeyDown('primary'),
          onBlur: () => setPrimaryActive(false),
          children: displayCurrentPosition && jsxs(Fragment, {
            children: [jsx("div", {
              className: `kxi-bubble kxi-text-normal${contrastClass}`,
              style: bubbleStyle,
              ref: primaryBubble,
              children: bubbleMerge && isRange ? draggingPrimary || draggingSecondary ? `${formatNumber(secondaryLeft)} to ${formatNumber(primaryLeft)} ${unit}` : `${secondaryLeft} to ${primaryLeft} ${unit}` : `${draggingPrimary ? formatNumber(primaryLeft) : primaryLeft} ${unit}`
            }), jsx("div", {
              className: "kxi-bubble-space",
              style: bubbleTransform,
              children: (displayCurrentPosition || isBubbleActive) && getBubbleText(true, primaryLeft, secondaryLeft)
            })]
          })
        })
      }, "slider_1"), displayCurrentPosition && jsx("div", {
        className: `kxi-bubble kxi-text-normal${contrastClass}`,
        style: bubbleStyleTwo,
        children: `${formatNumber(getProportionsFromPx(mouseX))} ${unit}`
      })]
    })), (((_c = endLabels === null || endLabels === void 0 ? void 0 : endLabels.right) === null || _c === void 0 ? void 0 : _c.icon) || ((_d = endLabels === null || endLabels === void 0 ? void 0 : endLabels.right) === null || _d === void 0 ? void 0 : _d.displayValue)) && jsxs("div", {
      className: `kxi-slider-end-label kxi-slider-end-label-right ${contrastClass}`,
      children: [endLabels.right.icon && jsx(XIIcon, {
        icon: endLabels.right.icon,
        color: endLabels.right.sentiment || 'orca',
        contrast: contrast
      }), endLabels.right.displayValue && jsx("p", {
        children: max
      })]
    })]
  }));
});
Slider.displayName = 'Slider';
function useActive() {
  const [active, setActive] = React.useState(false);
  const timeoutId = React.useRef();
  const setter = useConstant(value => {
    if (timeoutId.current != null) {
      clearTimeout(timeoutId.current);
    }
    if (value === 'temporarily_active') {
      setActive(true);
      timeoutId.current = setTimeout(() => setActive(false), 1000);
      return;
    }
    setActive(value);
  });
  return [active, setter];
}

class MediaPlayerProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Object)], MediaPlayerProps.prototype, "mediaRef", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], MediaPlayerProps.prototype, "hasFullscreen", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], MediaPlayerProps.prototype, "finishedLoading", void 0);
__decorate([Bound(), __metadata("design:type", Number)], MediaPlayerProps.prototype, "duration", void 0);
__decorate([Bound(), __metadata("design:type", Number)], MediaPlayerProps.prototype, "currentTime", void 0);
class MediaPlayerEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], MediaPlayerEvents.prototype, "onFullscreen", void 0);
/**
 * Description: This is standard button and should be used whenever possible. A small button is also acceptable. Don't use all caps in buttons. Status will override all colors.
 */
const MediaPlayer = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      mediaRef,
      duration = 0,
      finishedLoading,
      hasFullscreen = false,
      onFullscreen,
      currentTime: currentTimeInput
    } = props,
    ingredients = __rest(props, ["mediaRef", "duration", "finishedLoading", "hasFullscreen", "onFullscreen", "currentTime"]);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [isReadyRefresh, setIsReadyRefresh] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [inputEnabled, setInputEnabled] = React.useState(false);
  const playerRef = React.useRef(null);
  React.useEffect(() => {
    if (currentTimeInput !== undefined) {
      setCurrentTime(currentTimeInput);
      if (mediaRef.current) {
        mediaRef.current.currentTime = currentTimeInput;
      }
    }
  }, [currentTimeInput]);
  React.useEffect(() => {
    setInputEnabled(finishedLoading !== false);
  }, [finishedLoading]);
  const setMediaTime = time => {
    if (duration && time > duration) {
      time = duration;
    }
    if (duration && time < 0) {
      time = 0;
    }
    if (mediaRef.current) {
      mediaRef.current.currentTime = time;
    }
    setCurrentTime(time);
  };
  React.useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.muted = isMuted;
    }
  }, [isMuted, mediaRef]);
  React.useEffect(() => {
    void (async () => {
      var _a, _b;
      if (isPlaying) {
        await ((_a = mediaRef.current) === null || _a === void 0 ? void 0 : _a.play());
      } else {
        (_b = mediaRef.current) === null || _b === void 0 ? void 0 : _b.pause();
      }
    })();
  }, [isPlaying, mediaRef]);
  React.useEffect(() => {
    const media = mediaRef.current;
    if (!media || !isPlaying) {
      return;
    }
    const interval = setInterval(() => {
      setCurrentTime(media.currentTime || 0);
      if (duration && media.currentTime >= duration) {
        clearInterval(interval);
        setIsPlaying(false);
        setIsReadyRefresh(true);
      }
    }, 10);
    return () => {
      clearInterval(interval);
    };
  }, [duration, isPlaying, isReadyRefresh, mediaRef]);
  React.useEffect(() => {
    const mediaElement = mediaRef.current;
    if (!inputEnabled || !mediaElement) {
      return;
    }
    const handleKeyPress = e => {
      if (e.key === ' ' || e.key === 'Enter') {
        playMedia();
      }
    };
    if (mediaElement instanceof HTMLVideoElement) {
      document.addEventListener('keydown', handleKeyPress);
      return () => {
        document.removeEventListener('keydown', handleKeyPress);
      };
    } else if (mediaElement instanceof HTMLAudioElement) {
      const playerEl = playerRef.current;
      if (!playerEl) {
        return;
      }
      playerEl.addEventListener('keydown', handleKeyPress);
      return () => {
        playerEl.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [inputEnabled, isReadyRefresh, isPlaying, mediaRef]);
  const getTimeText = () => {
    var _a;
    const current = Math.floor(((_a = mediaRef.current) === null || _a === void 0 ? void 0 : _a.currentTime) || 0);
    const totalDuration = Math.floor(duration || 0);
    const currentSeconds = current % 60;
    const totalDurationSeconds = totalDuration % 60;
    const currentMinutes = Math.floor(current / 60);
    const totalDurationMinutes = Math.floor(totalDuration / 60);
    return `${currentMinutes}:${`${currentSeconds < 10 ? '0' : ''}` + currentSeconds} | ${totalDurationMinutes}:${`${totalDurationSeconds < 10 ? '0' : ''}` + totalDurationSeconds}`;
  };
  const playMedia = () => {
    if (inputEnabled) {
      if (isReadyRefresh) {
        setMediaTime(0);
        setIsReadyRefresh(false);
      }
      setIsPlaying(!isPlaying);
    }
  };
  return jsxs("div", Object.assign({
    className: `kxi-media-player${hasFullscreen ? '-fullscreen' : ''}`
  }, ingredients, {
    ref: mergeRefs([ref, playerRef]),
    tabIndex: 0,
    children: [jsx(XIIcon, {
      className: `kxi-play${isReadyRefresh ? ' kxi-replay' : ''}`,
      icon: isReadyRefresh ? 'refresh' : isPlaying ? 'pause_filled' : 'play_control_filled',
      onClick: () => {
        playMedia();
      },
      tabIndex: 1
    }), jsx(IconButton, {
      buttonSize: "normal",
      className: "kxi-rewind",
      icon: "rewind_15",
      hasAnimation: false,
      onClick: () => {
        var _a, _b;
        if (inputEnabled) {
          setIsReadyRefresh(false);
          setMediaTime(((_b = (_a = mediaRef.current) === null || _a === void 0 ? void 0 : _a.currentTime) !== null && _b !== void 0 ? _b : 0) - 15);
        }
      }
    }), jsx(IconButton, {
      buttonSize: "normal",
      className: "kxi-forward",
      icon: "forward_30",
      hasAnimation: false,
      onClick: () => {
        var _a;
        if (inputEnabled) {
          const media = mediaRef.current;
          setMediaTime(((_a = media === null || media === void 0 ? void 0 : media.currentTime) !== null && _a !== void 0 ? _a : 0) + 30);
          if (media && duration && media.currentTime >= duration) {
            setIsReadyRefresh(true);
          }
        }
      }
    }), jsx("div", {
      className: "kxi-media-slider",
      children: jsx(Slider, {
        max: duration,
        value: +currentTime.toFixed(1),
        disabled: !inputEnabled,
        onSliderChange: ({
          value
        }) => {
          setIsReadyRefresh(false);
          setMediaTime(value);
        },
        displayCurrentPosition: false
      })
    }), jsx("p", {
      className: "kxi-media-slider-text",
      children: getTimeText()
    }), jsx(IconButton, {
      hasAnimation: false,
      className: "kxi-volume",
      buttonSize: "normal",
      icon: isMuted ? 'volume_off' : 'volume_on',
      onClick: () => {
        if (inputEnabled) setIsMuted(!isMuted);
      }
    }), hasFullscreen && jsx(IconButton, {
      hasAnimation: false,
      className: "kxi-fullscreen",
      buttonSize: "normal",
      icon: "full_screen",
      onClick: event => {
        if (inputEnabled && onFullscreen) {
          onFullscreen({
            event
          });
        }
      }
    }), jsx("div", {
      className: "kxi-background"
    })]
  }));
});

class VideoPlayerProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], VideoPlayerProps.prototype, "source", void 0);
__decorate([Bound(), __metadata("design:type", String)], VideoPlayerProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Number)], VideoPlayerProps.prototype, "height", void 0);
__decorate([Bound(), __metadata("design:type", Number)], VideoPlayerProps.prototype, "width", void 0);
__decorate([Bound(), __metadata("design:type", Object)], VideoPlayerProps.prototype, "genericModalProps", void 0);
class VideoPlayerEvents extends DivHTMLEvents {}
/**
 * Description: InMoment themed video player.
 */
const VideoPlayer = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(VideoPlayer, props);
  const {
      source,
      contrast = 'high',
      height = 122,
      width = 216,
      genericModalProps
    } = props,
    ingredients = __rest(props, ["source", "contrast", "height", "width", "genericModalProps"]);
  const [duration, setDuration] = React.useState();
  const [isVisible, setIsVisible] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [hasNativeControls, setHasNativeControls] = React.useState(true);
  const [finishedLoading, setFinishedLoading] = React.useState(false);
  const videoElement = React.useRef(null);
  const videoWrapper = React.useRef(null);
  React.useEffect(() => {
    const handleKeyPress = e => {
      if (e.keyCode === 27 || e.key === 'Escape') {
        setIsVisible(false);
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);
  async function toggleFullScreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else if (document.webkitFullscreenElement && typeof document.webkitExitFullscreen === 'function') {
      document.webkitExitFullscreen();
    } else if (videoWrapper.current) {
      if (typeof videoWrapper.current.webkitRequestFullscreen === 'function') {
        videoWrapper.current.webkitRequestFullscreen();
      } else {
        await videoWrapper.current.requestFullscreen();
      }
    }
  }
  const onKeyDownThumbnail = e => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setIsVisible(!isVisible);
    }
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: "kxi-video-player"
  }, ingredients, {
    ref: ref,
    children: [jsxs("div", {
      className: "kxi-thumbnail",
      "data-testid": "video-thumbnail",
      style: {
        height: `${height}px`,
        width: `${width}px`
      },
      onClick: () => setIsVisible(!isVisible),
      onKeyDown: onKeyDownThumbnail,
      tabIndex: 0,
      children: [jsx("video", {
        ref: videoElement,
        onCanPlay: () => {
          var _a;
          setDuration((_a = videoElement.current) === null || _a === void 0 ? void 0 : _a.duration);
        },
        children: jsx("source", {
          src: source
        })
      }), jsx("div", {
        className: "kxi-thumbnail-icon",
        children: jsx(XIIcon, {
          contrast: contrast,
          icon: "play_control_filled",
          iconSize: 84
        })
      }), jsx("div", {
        className: "kxi-thumbnail-overlay",
        style: {
          height: `${height}px`,
          width: `${width}px`
        }
      })]
    }), jsx(GenericModal, Object.assign({}, genericModalProps, {
      isVisible: isVisible,
      onClose: () => setIsVisible(false),
      allowPropagation: true,
      children: jsxs("div", {
        className: `kxi-video${hasNativeControls ? ' kxi-video-native' : ''}${isFullscreen ? ' kxi-video-fullscreen' : ''}`,
        ref: videoWrapper,
        onClick: e => e.stopPropagation(),
        children: [jsx("video", {
          "data-testid": "video-fullScreen",
          controls: true,
          ref: videoElement,
          preload: "metadata",
          onCanPlay: () => {
            var _a;
            setFinishedLoading(true);
            setDuration((_a = videoElement.current) === null || _a === void 0 ? void 0 : _a.duration);
            if (videoElement.current) {
              // Turn off controls with JS to make video accessible with native controls if JS disabled.
              videoElement.current.controls = false;
              setHasNativeControls(false);
            }
          },
          children: jsx("source", {
            src: source
          })
        }), !hasNativeControls && jsx(MediaPlayer, {
          duration: duration,
          mediaRef: videoElement,
          hasFullscreen: true,
          onFullscreen: () => {
            setIsFullscreen(!isFullscreen);
            void toggleFullScreen();
          },
          finishedLoading: finishedLoading
        })]
      })
    }))]
  }));
});
VideoPlayer.displayName = 'VideoPlayer';

class AudioHTMLAttributes extends HTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Number)], AudioHTMLAttributes.prototype, "currentTime", void 0);
class AudioHTMLEvents extends DOMEvents {}

class AudioPlayerProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], AudioPlayerProps.prototype, "source", void 0);
__decorate([Bound(), __metadata("design:type", AudioHTMLAttributes)], AudioPlayerProps.prototype, "audioPlayerProperties", void 0);
class AudioPlayerEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", AudioHTMLEvents)], AudioPlayerEvents.prototype, "audioPlayerEvents", void 0);
/**
 * Description: InMoment themed audio player.
 */
const AudioPlayer = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(AudioPlayer, props);
  const {
      source,
      audioPlayerEvents = {},
      audioPlayerProperties
    } = props,
    ingredients = __rest(props, ["source", "audioPlayerEvents", "audioPlayerProperties"]);
  const {
    onCanPlay
  } = audioPlayerEvents;
  const [duration, setDuration] = React.useState();
  const audioElement = React.useRef(null);
  const currentTime = (audioPlayerProperties === null || audioPlayerProperties === void 0 ? void 0 : audioPlayerProperties.currentTime) !== null ? audioPlayerProperties === null || audioPlayerProperties === void 0 ? void 0 : audioPlayerProperties.currentTime : undefined;
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: "kxi-audio-player"
  }, ingredients, {
    ref: ref,
    children: [jsx("audio", Object.assign({}, audioPlayerProperties, audioPlayerEvents, {
      ref: audioElement,
      onCanPlay: e => {
        var _a;
        setDuration((_a = audioElement.current) === null || _a === void 0 ? void 0 : _a.duration);
        onCanPlay === null || onCanPlay === void 0 ? void 0 : onCanPlay(e);
      },
      "data-testid": "audioPlayer",
      children: jsx("source", {
        src: source
      })
    })), jsx(MediaPlayer, {
      duration: duration,
      mediaRef: audioElement,
      currentTime: currentTime
    })]
  }));
});
AudioPlayer.displayName = 'AudioPlayer';

const Body = ({
  activeSentiments,
  activeTagRef_deprecated,
  activeTags,
  audioSrcUrl,
  combinedTags_deprecated,
  comments,
  emptyCommentText,
  genericModalProps,
  highlightedText,
  imageSrcUrls,
  printView,
  mediaActions,
  sentimentAnnotations,
  tags,
  text,
  uuid,
  videoSrcUrl
}) => {
  const hasMedia = imageSrcUrls && imageSrcUrls.length || audioSrcUrl || videoSrcUrl;
  const returnMedia = () => {
    if (imageSrcUrls && imageSrcUrls.length) {
      return jsx(ImageCarousel, {
        images: imageSrcUrls,
        genericModalProps: genericModalProps,
        _printView: printView
      });
    } else if (videoSrcUrl) {
      const videoPlayer = jsx(VideoPlayer, {
        source: videoSrcUrl,
        genericModalProps: genericModalProps
      });
      if (mediaActions) {
        return jsxs("div", {
          children: [videoPlayer, jsx("div", {
            className: "kxi-media-actions-wrapper",
            children: mediaActions.map((action, i) => {
              return jsx(IconButton, {
                icon: action.icon,
                onClick: action.onClick,
                tooltip: action.tooltip
              }, i);
            })
          })]
        });
      }
      return videoPlayer;
    } else if (audioSrcUrl) {
      return jsx(AudioPlayer, {
        source: audioSrcUrl
      });
    }
    return null;
  };
  return jsxs("div", {
    className: `kxi-comment-body ${audioSrcUrl && !imageSrcUrls && !videoSrcUrl ? 'show-column' : ''} ${!hasMedia ? '' : 'full-width'}`,
    children: [jsx("div", {
      children: text.length ? jsx(CommentText, {
        externalId: uuid,
        sentimentAnnotations: sentimentAnnotations,
        localization: comments,
        text: text,
        activeTags: tags ? activeTags : activeTagRef_deprecated.current,
        combinedTags: tags ? tags : combinedTags_deprecated,
        activeSentiments: activeSentiments,
        highlightedText: highlightedText
      }) : jsx("div", {
        children: emptyCommentText
      })
    }), hasMedia ? jsx("div", {
      className: "kxi-comment-media-wrapper",
      children: returnMedia()
    }) : null]
  });
};

const TagItem$1 = props => {
  const {
    tagName,
    sentiment,
    userAdded = false,
    onTagClick,
    selected
  } = props;
  return jsx("div", {
    children: userAdded ? jsx("div", {
      className: "kxi-tag-item-user-tag-wrapper",
      children: jsx(DefaultButton, {
        color: "orca",
        text: tagName,
        fill: "fill",
        buttonSize: "small",
        onClick: () => {
          if (onTagClick) onTagClick(props);
        },
        className: "kxi-tag-item-user-tag kxi-tag-item-button"
      })
    }) : jsxs(Fragment, {
      children: [jsx(DefaultButton, {
        color: "orca",
        text: tagName,
        fill: selected ? 'fill' : 'outline',
        style: selected ? {
          marginLeft: 1,
          marginRight: 1
        } : undefined,
        buttonSize: "small",
        onClick: () => {
          if (onTagClick) onTagClick(props);
        },
        className: "kxi-tag-item-button"
      }), jsx("div", {
        className: `kxi-tag-item-sentiment-bar kxi-${sentiment}`,
        "data-testid": "tagSentimentBar"
      })]
    })
  });
};

const AddTagBox$1 = props => {
  const {
    localization,
    initialText,
    tagSearch,
    canDelete = false,
    onTextChange,
    onDeleteClick,
    onAddTagClick,
    textValue,
    maxSuggestions,
    suggestionsRef,
    inputRef,
    disabled,
    status
  } = props;
  const [suggestions, setSuggestions] = React__default.useState([]);
  const [customTag, setCustomTag] = React__default.useState({
    key: undefined,
    value: ''
  });
  const [inputValue, setInputValue] = React__default.useState(textValue || '');
  React__default.useLayoutEffect(() => {
    if (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) {
      inputRef.current.focus();
    }
  }, [initialText]);
  const showDelete = canDelete && inputValue;
  const searchForTags = useDebouncedCallback(React__default.useCallback(async value => {
    if (tagSearch) {
      const tags = await tagSearch(value);
      setSuggestions(tags.map(t => ({
        key: t.tagId,
        value: t.tag
      })));
    }
  }, []), 350);
  React__default.useEffect(() => {
    if (tagSearch && document.activeElement === inputRef.current && customTag.key === undefined) {
      searchForTags(inputValue);
    }
  }, [inputValue]);
  const handleTextChange = e => {
    const value = e.target.value;
    setInputValue(value);
    setCustomTag(() => ({
      key: undefined,
      value
    }));
    if (onTextChange) onTextChange(value);
  };
  const handleAddClick = () => {
    if (customTag) onAddTagClick(customTag);
  };
  const handleSuggestionClick = kvp => {
    setInputValue(kvp.value);
    setCustomTag(kvp);
    if (onTextChange) onTextChange(kvp.value);
  };
  return jsx("div", {
    className: "kxi-deprecated kxi-add-tag-box-position-set",
    ref: suggestionsRef,
    children: jsxs("div", {
      className: `kxi-add-tag-box-outer ${disabled ? 'kxi-add-tag-box-disabled' : ''}`,
      children: [jsx(InputField, {
        onChange: handleTextChange,
        _controlledState: true,
        ref: inputRef,
        value: inputValue,
        suggestions: suggestions,
        onFocus: () => searchForTags(inputValue),
        onSuggestionClick: ({
          suggestion
        }) => handleSuggestionClick(suggestion),
        maxSuggestions: maxSuggestions,
        status: disabled ? status : undefined
      }), jsxs("div", {
        className: `kxi-add-tag-box-button-div ${showDelete ? 'kxi-add-tag-box-button-div-with-trash' : 'kxi-add-tag-box-button-div-without-trash'}`,
        children: [jsx(DefaultButton, {
          "data-testid": "addTagSave",
          buttonSize: "small",
          text: showDelete ? localization.save : localization.addConfirm,
          onClick: handleAddClick,
          disabled: !(inputValue === null || inputValue === void 0 ? void 0 : inputValue.length) || disabled
        }), showDelete && jsx(IconButton, {
          icon: "trash",
          "data-testid": "addTagDelete",
          onClick: onDeleteClick
        })]
      })]
    })
  });
};

/** @deprecated */
const Tags$1 = props => {
  const {
    onTagUpdate,
    selectedTagInput,
    onTagClick,
    tags,
    tagSearch,
    localization,
    showAddTag = true,
    maxSuggestions,
    className,
    shouldPreventExpressTag
  } = props;
  const {
    add,
    industryTagExistsError = 'This already exists as an Industry tag',
    expressTagExistsError = 'This already exists as an Express tag'
  } = localization;
  const [showAddTagDialog, setShowAddTagDialog] = React__default.useState(false);
  const [addTagDialogTagId, setAddTagDialogTagId] = React__default.useState();
  const [newTagId, _setNewTagId] = React__default.useState();
  const [priorValue, setPriorValue] = React__default.useState('');
  const [existingUserTag, setExistingUserTag] = React__default.useState(false);
  const [canDelete, setCanDelete] = React__default.useState(false);
  const [textValue, setTextValue] = React__default.useState('');
  const [_, forceRerender] = React__default.useState('');
  const [addTagDisabled, setAddTagDisabled] = React__default.useState(false);
  const [addTagStatus, setAddTagStatus] = React__default.useState();
  const [existingTagData, setExistingTagData] = React__default.useState();
  const inputRef = React__default.useRef(null);
  const suggestionsRef = React__default.useRef(null);
  const tagArrayRef = React__default.useRef([]);
  const newTagIdRef = React__default.useRef(newTagId);
  const setNewTagId = data => {
    newTagIdRef.current = data;
    _setNewTagId(data);
  };
  const updateTagArray = arr => {
    tagArrayRef.current = arr;
    forceRerender(uuidv4()); // Because TagArray is a ref we lose some of the auto updating abilities
  };
  React__default.useEffect(() => {
    if (tags) {
      const tempTagArr = tags.map((tag, i) => {
        var _a;
        return Object.assign(Object.assign({}, tag), {
          selected: (_a = tagArrayRef.current[i]) === null || _a === void 0 ? void 0 : _a.selected,
          tagId: tag.tagId || '',
          _internalTagId: uuidv4()
        });
      });
      updateTagArray(tempTagArr);
    }
  }, [tags]);
  React__default.useEffect(() => {
    if (!showAddTagDialog) {
      setTextValue('');
    }
  }, [showAddTagDialog]);
  React__default.useEffect(() => {
    tagArrayRef.current.forEach(tag => {
      if (tag.tagId && (selectedTagInput === null || selectedTagInput === void 0 ? void 0 : selectedTagInput.includes(tag.tagId))) {
        tag.selected = true;
      } else {
        tag.selected = false;
      }
    });
  }, [JSON.stringify(selectedTagInput), JSON.stringify(tagArrayRef.current.filter(tag => !tag.userAdded).map(tag => tag.tagId))]);
  React__default.useEffect(() => {
    const tagIndex = tagArrayRef.current.findIndex(tag => tag._internalTagId === addTagDialogTagId && tag.userAdded);
    if (tagIndex >= 0) {
      setTextValue(tagArrayRef.current[tagIndex].tagName);
    }
  }, [addTagDialogTagId]);
  React__default.useEffect(() => {
    setCanDelete(existingUserTag);
  }, [existingUserTag]);
  const addNewTag = () => {
    if (!showAddTagDialog) {
      const newTagId = uuidv4();
      const tempTagArray = [...tagArrayRef.current];
      setAddTagDialogTagId(newTagId);
      tempTagArray.push({
        tagName: '',
        userAdded: true,
        tagId: '',
        _internalTagId: newTagId
      });
      updateTagArray(tempTagArray);
      setNewTagId(newTagId);
      setShowAddTagDialog(true);
    }
  };
  const onCancelClick = () => {
    const tempTagArray = [...tagArrayRef.current];
    if (newTagId) {
      tempTagArray.pop();
    } else {
      const tagIndex = tempTagArray.findIndex(item => item._internalTagId === addTagDialogTagId && item.userAdded);
      if (tagIndex >= 0) {
        tempTagArray[tagIndex].tagName = priorValue;
      }
    }
    updateTagArray(tempTagArray);
    setShowAddTagDialog(false);
    setExistingUserTag(false);
    setAddTagDialogTagId(undefined);
    setNewTagId(undefined);
  };
  const isTagDuplicate = tagText => {
    const duplicateTagIndex = tags.findIndex(tagItem => tagItem.tagName.toLowerCase() === tagText.toLowerCase() && !(existingUserTag && (existingTagData === null || existingTagData === void 0 ? void 0 : existingTagData.tagName.toLowerCase()) === tagText.toLowerCase()));
    const hasDuplicate = duplicateTagIndex >= 0;
    return {
      hasDuplicate,
      duplicateTag: hasDuplicate ? tags[duplicateTagIndex] : undefined,
      isIndustryTag: hasDuplicate ? !tags[duplicateTagIndex].userAdded : undefined
    };
  };
  const addTagDialogBox = jsx(AddTagBox$1, {
    initialText: addTagDialogTagId || '',
    tagSearch: tagSearch,
    inputRef: inputRef,
    suggestionsRef: suggestionsRef,
    disabled: addTagDisabled,
    status: addTagStatus,
    onTextChange: e => {
      const tempTagArray = [...tagArrayRef.current];
      let preventDisable;
      if (shouldPreventExpressTag) {
        preventDisable = shouldPreventExpressTag({
          userTagText: e
        });
      }
      if (preventDisable === 'express' || preventDisable === 'industry') {
        setAddTagDisabled(true);
        setAddTagStatus({
          state: 'error',
          text: preventDisable === 'express' ? expressTagExistsError : industryTagExistsError
        });
      } else {
        const duplicateTag = isTagDuplicate(e);
        const shouldDisable = duplicateTag.hasDuplicate;
        setAddTagDisabled(shouldDisable);
        setAddTagStatus(shouldDisable ? {
          state: 'error',
          text: duplicateTag.isIndustryTag ? industryTagExistsError : expressTagExistsError
        } : undefined);
      }
      const tagIndex = tempTagArray.findIndex(item => item._internalTagId === addTagDialogTagId && item.userAdded);
      const valueToAssign = e;
      setTextValue(valueToAssign);
      tempTagArray[tagIndex].tagName = valueToAssign;
      updateTagArray(tempTagArray);
    },
    onAddTagClick: _kvp => {
      var _a, _b, _c;
      const indexUpdated = tagArrayRef.current.findIndex(item => item._internalTagId === addTagDialogTagId && item.userAdded);
      if (newTagId && newTagId !== ((_a = tagArrayRef.current[indexUpdated]) === null || _a === void 0 ? void 0 : _a._internalTagId)) {
        const tempTagArray = [...tagArrayRef.current];
        tempTagArray.pop();
        updateTagArray(tempTagArray);
        if (onTagUpdate) onTagUpdate(tempTagArray, (_b = tagArrayRef.current[indexUpdated]) === null || _b === void 0 ? void 0 : _b.tagId);
      } else {
        if (onTagUpdate) onTagUpdate(tagArrayRef.current, (_c = tagArrayRef.current[indexUpdated]) === null || _c === void 0 ? void 0 : _c.tagId);
      }
      setShowAddTagDialog(false);
      setAddTagDialogTagId(undefined);
      setNewTagId(undefined);
      setExistingUserTag(false);
    },
    canDelete: canDelete,
    onDeleteClick: () => {
      const indexToDelete = tagArrayRef.current.findIndex(item => item._internalTagId === addTagDialogTagId && item.userAdded);
      if (indexToDelete >= 0) {
        const tempTagArray = [...tagArrayRef.current];
        tempTagArray.splice(indexToDelete, 1);
        if (onTagUpdate) onTagUpdate(tempTagArray, tagArrayRef.current[indexToDelete].tagId);
        updateTagArray(tempTagArray);
        setShowAddTagDialog(false);
        setAddTagDialogTagId(undefined);
        setNewTagId(undefined);
        setExistingUserTag(false);
      }
    },
    localization: localization,
    textValue: textValue,
    maxSuggestions: maxSuggestions
  });
  const handleTagClick = tag => {
    if (onTagClick) onTagClick(tag);
    if (tag.userAdded) {
      setExistingUserTag(true);
      setExistingTagData(tag);
      setTextValue(tag.tagName);
      setShowAddTagDialog(true);
      setAddTagDialogTagId(tag._internalTagId);
      setPriorValue(tag.tagName);
    } else {
      const tempTagArray = [...tagArrayRef.current];
      const indexToSelect = tempTagArray.findIndex(item => item._internalTagId === tag._internalTagId);
      if (indexToSelect >= 0) {
        tempTagArray[indexToSelect].selected = !tag.selected;
        updateTagArray(tempTagArray);
      }
      setShowAddTagDialog(false);
      setExistingUserTag(false);
      setExistingTagData(undefined);
      setAddTagDialogTagId(undefined);
      forceRerender(uuidv4());
      onCancelClick();
    }
  };
  React__default.useLayoutEffect(() => {
    const handleOutsideClick = e => {
      var _a;
      if (((_a = suggestionsRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) === false) {
        const tempTagArray = [...tagArrayRef.current];
        if (newTagIdRef.current) {
          tempTagArray.pop();
        } else {
          const tagIndex = tempTagArray.findIndex(item => item._internalTagId === addTagDialogTagId && item.userAdded);
          if (tagIndex >= 0) {
            tempTagArray[tagIndex].tagName = priorValue;
          }
        }
        updateTagArray(tempTagArray);
        setShowAddTagDialog(false);
        setExistingUserTag(false);
        setAddTagDialogTagId(undefined);
        setNewTagId(undefined);
        setAddTagDisabled(false);
        setAddTagStatus(undefined);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [addTagDisabled, priorValue]);
  const displayedTabArray = tagArrayRef.current.map(tag => {
    return jsxs("div", {
      className: "kxi-tag-object-wrapper",
      children: [jsx(TagItem$1, Object.assign({
        onTagClick: handleTagClick
      }, tag)), tag._internalTagId && tag._internalTagId === addTagDialogTagId ? addTagDialogBox : undefined]
    }, tag.tagId || tag._internalTagId);
  });
  return jsxs("div", {
    className: `kxi-tag-wrapper ${className || ''}`,
    children: [displayedTabArray, showAddTag ? jsxs(Fragment, {
      children: [jsx("div", {
        className: "kxi-tag-add-tag-outer-wrapper",
        children: jsx("div", {
          className: "kxi-tag-add-tag-inner-wrapper",
          children: jsx(DefaultButton, {
            color: "orca",
            className: "kxi-tag-add-tag-button",
            disabled: !!newTagId,
            text: add,
            iconLeft: newTagId ? 'add_circle_filled' : 'add_circle',
            fill: "text",
            onClick: () => addNewTag(),
            "data-testid": "addTagButton"
          })
        })
      }), addTagDialogTagId === undefined && showAddTagDialog ? addTagDialogBox : undefined]
    }) : undefined]
  });
};

const TagItem = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  var _a;
  const {
      tag
    } = props,
    rest = __rest(props, ["tag"]);
  const printView = useIsPrinting((_a = props.printView) !== null && _a !== void 0 ? _a : false);
  if (!tag) {
    return null;
  }
  return !printView ? jsx(TagItemImpl, Object.assign({
    ref: ref
  }, rest, {
    tag: tag
  })) : jsx(PrintView$1, Object.assign({
    ref: ref
  }, rest, {
    tag: tag
  }));
});
const TagItemImpl = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const {
    tag,
    onTagClick,
    selected,
    id
  } = props;
  const hasHighlighting = Boolean(tag.highlighting && tag.highlighting.length > 0);
  const hasBehaviorOnClick = Boolean(tag.userAdded || hasHighlighting);
  const buttonProps = Object.assign({
    id,
    color: 'orca',
    buttonSize: 'small',
    wrap: 'break-spaces',
    onClick: e => onTagClick === null || onTagClick === void 0 ? void 0 : onTagClick(tag, selected, e.nativeEvent)
  }, hasHighlighting ? {
    fill: selected ? 'fill' : 'outline',
    text: tag.label,
    className: 'kxi-tag-item-button',
    style: selected ? {
      marginLeft: 1,
      marginRight: 1
    } : undefined //filled buttons are 2px smaller than outline buttons
  } : {
    fill: 'fill',
    text: `<b>${tag.label || ' '}</b>`,
    className: 'kxi-tag-item-user-tag kxi-tag-item-button',
    tone: 'light',
    hasAnimation: hasBehaviorOnClick,
    _appearInert: !hasBehaviorOnClick
  });
  return jsxs("div", {
    children: [jsx(DefaultButton, Object.assign({
      ref: ref
    }, buttonProps)), tag.sentiment ? jsx("div", {
      className: `kxi-tag-item-sentiment-bar kxi-${tag.sentiment}`,
      "data-testid": "tagSentimentBar"
    }) : null]
  });
});
const PrintView$1 = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const {
    tag,
    id
  } = props;
  return jsx(DefaultButton, {
    ref: ref,
    className: "kxi-tag-item-button",
    id: id,
    color: "orca",
    buttonSize: "small",
    fill: "outline",
    text: tag.label,
    tone: "light",
    wrap: "break-spaces",
    hasAnimation: false,
    _appearInert: true
  });
});

const Tag = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a;
  const {
      setSelectedUserTag,
      tag,
      selectedTags,
      onTagClick,
      setSelectedTags,
      printView = false
    } = props,
    ingredients = __rest(props, ["setSelectedUserTag", "tag", "selectedTags", "onTagClick", "setSelectedTags", "printView"]);
  return jsx("div", Object.assign({
    className: "kxi-tag-object-wrapper"
  }, ingredients, {
    children: jsx(TagItem, {
      ref: ref,
      tag: tag,
      onTagClick: (tag, selected) => {
        if (tag.userAdded) {
          setSelectedUserTag(Object.assign(Object.assign({}, tag), {
            initialLabel: tag.label
          }));
        } else if (tag.highlighting && tag.highlighting.length > 0) {
          onTagClick(tag, selected);
          const tagIdIndex = selectedTags.findIndex(selectedTag => selectedTag.id === tag.id);
          const newTagSelectedTags = [...selectedTags];
          if (tagIdIndex !== -1) {
            newTagSelectedTags.splice(tagIdIndex, 1);
          } else {
            newTagSelectedTags.push(tag);
          }
          setSelectedTags(newTagSelectedTags);
        }
      },
      printView: printView,
      id: `kxi-tag-item-${(_a = tag === null || tag === void 0 ? void 0 : tag.id) !== null && _a !== void 0 ? _a : 'new-tag'}`,
      selected: !!tag && selectedTags.findIndex(selectedTag => selectedTag.id === tag.id) >= 0
    })
  }));
});

const AddTagBox = props => {
  const {
    localization,
    initialText,
    canDelete,
    maxSuggestions,
    isVisible,
    anchorElement,
    tagSearch,
    onTextChange,
    onDeleteClick,
    onCancel,
    onSave,
    shouldPreventExpressTag,
    onEscape
  } = props;
  const [suggestions, setSuggestions] = React__default.useState([]);
  const [inputValue, setInputValue] = useComputedState(() => initialText, [initialText, isVisible]);
  const inputRef = React__default.useRef(null);
  const saveButtonRef = React__default.useRef(null);
  const deleteButtonRef = React__default.useRef(null);
  const wrapperRef = React__default.useRef(null);
  const preventAddingTag = React__default.useMemo(() => {
    if (inputValue && shouldPreventExpressTag && inputValue !== initialText) {
      return shouldPreventExpressTag({
        userTagText: inputValue
      });
    }
    return '';
  }, [inputValue, initialText, shouldPreventExpressTag]);
  const disableSave = !(inputValue === null || inputValue === void 0 ? void 0 : inputValue.length) || !!preventAddingTag;
  const onSaveHandler = event => {
    if (inputValue) {
      if (!disableSave && inputValue !== initialText) {
        onSave(event);
      } else {
        onCancel(event);
      }
    } else {
      onDeleteClick(event);
    }
  };
  const searchForTags = async value => {
    const tags = tagSearch ? await tagSearch(value) : [];
    setSuggestions(tags.map(t => ({
      key: t.id,
      value: t.label
    })));
  };
  const searchForTagsDebounced = useDebouncedCallback(searchForTags, 100);
  const onTagLabelChange = newLabel => {
    setInputValue(newLabel);
    onTextChange(newLabel);
    searchForTagsDebounced(newLabel);
  };
  return jsx(Popout, {
    anchorElement: anchorElement,
    isVisible: isVisible,
    direction: 7,
    internalPadding: 12,
    edgeClearance: 0,
    containedPopoutIds: ['add-tag-box-suggestions'],
    onExternalClick: ({
      event
    }) => {
      onCancel(event);
    },
    onEscape: ({
      event
    }) => {
      onCancel(event);
    },
    children: jsxs("div", {
      ref: wrapperRef,
      className: classNames('kxi-add-tag-box-outer', {
        'kxi-add-tag-box-disabled': preventAddingTag
      }),
      children: [jsx(InputField, {
        _controlledState: true,
        ref: inputRef,
        value: inputValue,
        suggestions: suggestions && inputValue.length > 0 ? suggestions : undefined,
        maxSuggestions: maxSuggestions,
        status: preventAddingTag ? {
          state: 'error',
          text: preventAddingTag
        } : undefined,
        popoutProps: {
          popoutId: 'add-tag-box-suggestions'
        },
        onChange: e => {
          onTagLabelChange(e.target.value);
        },
        onFocus: () => {
          searchForTagsDebounced(inputValue);
        },
        onSuggestionClick: ({
          suggestion
        }) => {
          onTagLabelChange(suggestion.value);
        },
        onKeyDown: e => {
          if (e.key === 'Enter' && !preventAddingTag) {
            onSaveHandler(e);
          }
          if (e.key === 'Escape') {
            onEscape(e);
          }
        }
      }), jsxs("div", {
        className: classNames('kxi-add-tag-box-button-div', `kxi-add-tag-box-button-div-${canDelete ? 'with' : 'without'}-trash`),
        children: [jsx(DefaultButton, {
          ref: saveButtonRef,
          "data-testid": "addTagSave",
          buttonSize: "small",
          text: canDelete ? localization.save : localization.addConfirm,
          onClick: onSaveHandler,
          disabled: disableSave
        }), canDelete && jsx(IconButton, {
          ref: deleteButtonRef,
          icon: "trash",
          "data-testid": "addTagDelete",
          onClick: onDeleteClick
        })]
      })]
    })
  });
};

const Tab = props => {
  const {
    localization,
    maxSuggestions,
    onSelectedTagsChange,
    onTagClick,
    onTagsChange,
    onTagSearch,
    onTagUpdate,
    selectedTags,
    shouldPreventExpressTag,
    tab,
    tags
  } = props;
  const [newTagElement, setNewTagElement] = React__default.useState(null);
  const [selectedUserTag, setSelectedUserTag] = React__default.useState();
  const addTagButtonRef = React__default.useRef(null);
  /** A ref to an object mapping tag ids to their respective element. */
  const tagRefs = React__default.useRef({});
  const tagRefCallbacks = React__default.useMemo(() => tags.reduce((acc, tag) => {
    acc[tag.id] = el => tagRefs.current[tag.id] = el;
    return acc;
  }, {}), [tags]);
  const addTagBoxAnchorElement = (() => {
    var _a;
    const id = selectedUserTag === null || selectedUserTag === void 0 ? void 0 : selectedUserTag.id;
    return id ? (_a = tagRefs.current[id]) !== null && _a !== void 0 ? _a : newTagElement : newTagElement;
  })();
  const onCloseAddTagBox = event => {
    if (!selectedUserTag) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    const selectedId = selectedUserTag.id;
    setSelectedUserTag(undefined);
    setTimeout(() => {
      var _a;
      const selectedTagElement = tagRefs.current[selectedId];
      // When adding/editing a tag
      if (selectedTagElement) {
        selectedTagElement.focus();
      }
      // When cancelling add or deleting a tag
      else if (addTagButtonRef.current) {
        addTagButtonRef.current.focus();
      }
      // Last resort, try to focus the first tag
      else {
        const tag = tags.find(({
          id
        }) => tagRefs.current[id] != null);
        if (tag) {
          (_a = tagRefs.current[tag.id]) === null || _a === void 0 ? void 0 : _a.focus();
        }
      }
    }, 0);
  };
  return jsxs(Fragment, {
    children: [tags.filter(({
      tabId
    }) => tabId === tab.id).map(tag => jsx(Tag, {
      setSelectedUserTag: setSelectedUserTag,
      tag: tag,
      selectedTags: selectedTags,
      setSelectedTags: onSelectedTagsChange,
      onTagClick: onTagClick,
      ref: tagRefCallbacks[tag.id]
    }, tag.id)), selectedUserTag && selectedUserTag.tabId === tab.id && !tags.some(({
      id
    }) => id === selectedUserTag.id) && jsx(Tag, {
      ref: setNewTagElement,
      setSelectedUserTag: setSelectedUserTag,
      tag: selectedUserTag,
      selectedTags: selectedTags,
      setSelectedTags: onSelectedTagsChange,
      onTagClick: onTagClick
    }), (tab === null || tab === void 0 ? void 0 : tab.showAddTag) ? jsx(Fragment, {
      children: jsx("div", {
        className: "kxi-tag-add-tag-outer-wrapper",
        children: jsx("div", {
          className: "kxi-tag-add-tag-inner-wrapper",
          children: jsx(DefaultButton, {
            ref: addTagButtonRef,
            color: "orca",
            className: "kxi-tag-add-tag-button kxi-tag-item-button",
            disabled: !!(selectedUserTag === null || selectedUserTag === void 0 ? void 0 : selectedUserTag.id),
            text: localization.add,
            iconLeft: selectedUserTag && selectedUserTag.tabId === tab.id ? 'add_circle_filled' : 'add_circle',
            buttonSize: "small",
            fill: "text",
            onClick: () => {
              setSelectedUserTag({
                id: uuidv4(),
                label: '',
                tabId: tab === null || tab === void 0 ? void 0 : tab.id,
                userAdded: true,
                isNew: true,
                initialLabel: ''
              });
            },
            "data-testid": "addTagButton"
          })
        })
      })
    }) : undefined, jsx(AddTagBox, {
      isVisible: addTagBoxAnchorElement !== null && !!(selectedUserTag === null || selectedUserTag === void 0 ? void 0 : selectedUserTag.id),
      anchorElement: addTagBoxAnchorElement,
      initialText: (selectedUserTag === null || selectedUserTag === void 0 ? void 0 : selectedUserTag.initialLabel) || '',
      tagSearch: onTagSearch,
      onTextChange: label => {
        if (!selectedUserTag) {
          return;
        }
        setSelectedUserTag(Object.assign(Object.assign({}, selectedUserTag), {
          label
        }));
        if (!selectedUserTag.isNew) {
          const tagId = tags.findIndex(tag => tag.id === selectedUserTag.id);
          if (tagId >= 0) {
            tags[tagId].label = label;
          }
        }
      },
      onSave: e => {
        if (!selectedUserTag) {
          return;
        }
        onCloseAddTagBox(e);
        const selectedUserTagIndex = tags.findIndex(tag => tag.id === selectedUserTag.id);
        // Edit
        if (selectedUserTagIndex !== -1) {
          const tagsCopy = [...tags];
          tagsCopy.splice(selectedUserTagIndex, 1, selectedUserTag);
          onTagUpdate(tagsCopy, selectedUserTag, 'edit');
          onTagsChange(tagsCopy);
        }
        // Add
        else {
          const newTags = [...tags, selectedUserTag];
          onTagUpdate(newTags, selectedUserTag, 'add');
          onTagsChange(newTags);
        }
      },
      onCancel: onCloseAddTagBox,
      canDelete: !!selectedUserTag && tags.map(tag => tag.id).includes(selectedUserTag.id),
      onDeleteClick: e => {
        if (!selectedUserTag) {
          return;
        }
        onCloseAddTagBox(e);
        const tagIndex = tags.findIndex(tag => tag.id === selectedUserTag.id);
        if (tagIndex !== -1) {
          const newTags = [...tags];
          newTags.splice(tagIndex, 1);
          onTagUpdate(newTags, selectedUserTag, 'delete');
          onTagsChange(newTags);
        }
      },
      shouldPreventExpressTag: shouldPreventExpressTag,
      localization: localization,
      maxSuggestions: maxSuggestions,
      onEscape: onCloseAddTagBox
    })]
  });
};

const Tags = props => {
  const printView = useIsPrinting(props.printView);
  return !printView ? jsx(TagsImpl, Object.assign({}, props)) : jsx(PrintView, Object.assign({}, props));
};
const TagsImpl = props => {
  const {
    tags: tagsIncoming,
    tabs,
    localization,
    maxSuggestions,
    expanded,
    condensedView,
    activeTags,
    onExpand,
    onTagUpdate,
    onTagClick,
    onSelectedTagsUpdate,
    onTagSearch,
    shouldPreventExpressTag
  } = props;
  const [selectedTags, setSelectedTags] = useValueBackedState(activeTags || []);
  const [currentTab, setCurrentTab] = React.useState(tabs[0]);
  const [tags, setTags] = useValueBackedState(tagsIncoming);
  const onSelectedTagsChange = tags => {
    setSelectedTags(tags);
    onSelectedTagsUpdate(tags);
  };
  return condensedView ? jsx(Fragment, {
    children: tabs.map(tab => {
      return jsx(AccordionItem, {
        title: `${tab.label} (${tags.filter(tag => tag.tabId === tab.id).length})`,
        noPadding: true,
        children: jsx("div", {
          style: {
            display: 'flex',
            flexWrap: 'wrap'
          },
          children: jsx(Tab, {
            tab: tab,
            localization: localization,
            selectedTags: selectedTags,
            onSelectedTagsChange: onSelectedTagsChange,
            onTagClick: onTagClick,
            onTagUpdate: onTagUpdate,
            tags: tags,
            maxSuggestions: maxSuggestions,
            onTagSearch: onTagSearch,
            onTagsChange: setTags,
            shouldPreventExpressTag: shouldPreventExpressTag
          })
        })
      }, tab.id);
    })
  }) : jsxs("div", {
    children: [jsx("div", {
      style: {
        height: 31,
        marginTop: -4,
        marginLeft: -12,
        marginRight: 12
      },
      children: jsx(HorizontalTabBar, {
        hasTransparentBackground: true,
        activeTabId: currentTab === null || currentTab === void 0 ? void 0 : currentTab.id,
        stretch: true,
        data: tabs.map(tab => ({
          id: tab.id,
          title: `${tab.label} (${tags.filter(tag => tag.tabId === tab.id).length})`
        })),
        onTabChange: ({
          tab
        }) => {
          onExpand();
          const newTabIndex = tabs.findIndex(inTab => inTab.id === tab.id);
          if (newTabIndex !== -1) {
            setCurrentTab(tabs[newTabIndex]);
          }
        }
      })
    }), expanded ? jsxs(Fragment, {
      children: [jsx("div", {
        style: {
          borderBottom: '1px solid',
          borderColor: XIColors.grey.light[80],
          marginRight: 11.5
        }
      }), jsx("div", {
        style: {
          display: 'flex',
          flexWrap: 'wrap'
        },
        children: currentTab && jsx(Tab, {
          tab: currentTab,
          localization: localization,
          selectedTags: selectedTags,
          onSelectedTagsChange: onSelectedTagsChange,
          onTagClick: onTagClick,
          onTagUpdate: onTagUpdate,
          tags: tags,
          maxSuggestions: maxSuggestions,
          onTagSearch: onTagSearch,
          onTagsChange: setTags,
          shouldPreventExpressTag: shouldPreventExpressTag
        })
      })]
    }) : null]
  });
};
const PrintView = props => {
  const {
    tabs,
    localization: {
      noTags = 'No tags'
    },
    tags
  } = props;
  const tabsMap = tabs.reduce((acc, tab) => {
    if (acc.has(tab.id)) {
      devOnlyError(`Duplicate tab provided with id: ${tab.id}.`);
      return acc;
    }
    acc.set(tab.id, {
      tab,
      tags: []
    });
    return acc;
  }, new Map());
  tags.forEach(tag => {
    const retval = tabsMap.get(tag.tabId);
    if (!retval) {
      devOnlyError(`Tag with id "${tag.id}" has a tabId "${tag.tabId}" that does not correspond to any tab.`);
      return;
    }
    retval.tags.push(tag);
  });
  return jsx("div", {
    className: "kxi-tags-print-view",
    children: Array.from(tabsMap.values()).map(({
      tab,
      tags
    }) => {
      return jsxs("div", {
        className: "kxi-tags-tab",
        children: [jsx("div", {
          className: "kxi-tags-tab-label kxi-text-600",
          children: tab.label
        }), jsx("div", {
          className: "kxi-tags-list",
          children: tags.length > 0 ? tags.map(tag => jsx(Tag, {
            printView: true,
            tag: tag,
            setSelectedUserTag: () => {},
            selectedTags: [],
            setSelectedTags: () => {},
            onTagClick: () => {}
          }, tag.id)) : noTags
        })]
      }, tab.id);
    })
  });
};
PrintView.displayName = 'Tags (PrintView)';

const SentimentSection = props => {
  const {
    highlightedSentiments,
    onSentimentClick,
    hasSeparator,
    localization,
    sentimentAnnotations
  } = props;
  const [highlightedSentimentsState, setHighlightedSentimentsState] = React.useState([]);
  const hasAnnotation = {
    positive: sentimentAnnotations === null || sentimentAnnotations === void 0 ? void 0 : sentimentAnnotations.findIndex(item => item.sentiment === 1),
    neutral: sentimentAnnotations === null || sentimentAnnotations === void 0 ? void 0 : sentimentAnnotations.findIndex(item => item.sentiment === 0),
    negative: sentimentAnnotations === null || sentimentAnnotations === void 0 ? void 0 : sentimentAnnotations.findIndex(item => item.sentiment === -1)
  };
  React.useEffect(() => {
    setHighlightedSentimentsState(highlightedSentiments || []);
  }, [highlightedSentiments]);
  const mappedSentiments = _SentimentsOptions.map(sentiment => {
    const isFocused = highlightedSentimentsState.includes(sentiment);
    let text;
    if (localization) {
      text = localization[sentiment];
    }
    return jsx(SentimentButton, {
      disabled: hasAnnotation[sentiment] === -1,
      className: `kxi-sentiment-button ${normalizeColor(sentiment)}`,
      sentiment: sentiment,
      isFocused: isFocused,
      text: text,
      onClick: () => {
        if (onSentimentClick) onSentimentClick(sentiment);
      },
      "data-testid": `sentiment-button-${sentiment}`
    }, sentiment);
  });
  return jsxs(Fragment, {
    children: [hasSeparator ? jsx("div", {
      style: {
        borderBottom: '1px solid',
        borderColor: XIColors.grey.light[80],
        marginTop: -14,
        marginBottom: 6,
        marginLeft: -3
      }
    }) : null, jsx("div", {
      className: "kxi-sentiment-section-wrapper",
      children: mappedSentiments
    })]
  });
};

const Footer = ({
  _internalOnTagChange_deprecated,
  _isOnExperienceCard,
  activeSentiments,
  activeTagRef_deprecated,
  activeTags,
  addTagVisible,
  answerId,
  combinedTags_deprecated,
  commentOnlyClass,
  comments,
  deprecatedTags,
  getTagSuggestions,
  highlightedSentiments,
  maxSuggestions,
  observer,
  onSentimentClick,
  onTagChange,
  onTagEvent,
  printView,
  selectedTagIds,
  sentimentAnnotations,
  sentimentBarExpanded,
  sentimentButtons,
  sentimentConfidence,
  sentimentScoreDisplay,
  setActiveSentiments,
  setActiveTags,
  setHighlightedSentiments,
  shouldPreventExpressTag,
  shouldPreventUserTag,
  showCondensedView,
  tabs,
  tagLocalization,
  tags,
  tagSearch,
  text
}) => {
  const [totalTagLength_deprecated, setTotalTagLength_deprecated] = React.useState(0);
  const [expanded, setExpanded] = React.useState(sentimentBarExpanded);
  const forceUpdate = useForceRerender();
  const handleSentimentExpanded = () => {
    if (_isOnExperienceCard || tags) {
      setExpanded(state => !state);
    } else {
      setExpanded(true);
    }
  };
  const sentimentWrapperKeyDown = e => {
    if (e.currentTarget === e.target && (e.key === ' ' || e.key === 'Enter')) {
      e.preventDefault();
      handleSentimentExpanded();
    }
  };
  React.useEffect(() => {
    if (_isOnExperienceCard) {
      setExpanded(sentimentBarExpanded);
    } else {
      setExpanded(true);
    }
  }, [sentimentBarExpanded]);
  const handleTagUpdate_deprecated = (tags, tagId) => {
    setTotalTagLength_deprecated(tags.length || 0);
    const tagAnnotationsCopy = (deprecatedTags === null || deprecatedTags === void 0 ? void 0 : deprecatedTags.filter(tag => {
      if (tag.userAdded) return tags.some(tagsTag => tagsTag.tagId === tag.tagId);else return true;
    }).map(tag => {
      if (!tag.userAdded) return tag;else return {
        tag: tag.tag,
        userAdded: true,
        tagId: tag.tagId
      };
    })) || [];
    tags.forEach(tag => {
      if (tag.userAdded) {
        const annotationTagIndex = tagAnnotationsCopy === null || tagAnnotationsCopy === void 0 ? void 0 : tagAnnotationsCopy.findIndex(item => item.tagId === tag.tagId);
        if (annotationTagIndex < 0) {
          tagAnnotationsCopy.push({
            tag: tag.tagName,
            userAdded: true,
            tagId: tag.tagId
          });
        } else {
          tagAnnotationsCopy[annotationTagIndex].tag = tag.tagName;
        }
      }
    });
    if (observer) observer.tagAnnotations = tagAnnotationsCopy;
    const activeTagIds = activeTagRef_deprecated.current.map(tag => tag.tagId).filter(tagId => tagId !== undefined);
    if (onTagChange) onTagChange({
      tags: tagAnnotationsCopy,
      tagId,
      answerId,
      selectedTagIds: activeTagIds
    });
    if (_internalOnTagChange_deprecated) _internalOnTagChange_deprecated(tagAnnotationsCopy, tagId, answerId, activeTagIds);
  };
  const tagUpdate_deprecated = tag => {
    if (!tag.userAdded) {
      const newActiveTags = [...activeTagRef_deprecated.current];
      const activeTagIndex = newActiveTags.findIndex(activeTag => activeTag.tagId === tag.tagId);
      if (activeTagIndex >= 0) {
        newActiveTags.splice(activeTagIndex, 1);
      } else {
        const combinedTagIndex = combinedTags_deprecated.findIndex(activeTag => activeTag.tagId === tag.tagId);
        newActiveTags.push(combinedTags_deprecated[combinedTagIndex]);
      }
      activeTagRef_deprecated.current = newActiveTags;
      forceUpdate();
      const activeTagIds = activeTagRef_deprecated.current.map(tag => tag.tagId).filter(tagId => tagId !== undefined);
      if (onTagChange) onTagChange({
        tags: deprecatedTags || [],
        tagId: tag.tagId,
        answerId,
        selectedTagIds: activeTagIds
      });
      if (_internalOnTagChange_deprecated) _internalOnTagChange_deprecated(deprecatedTags || [], tag.tagId, answerId, activeTagIds);
    }
  };
  const handleTagUpdate = (tags, tag, event) => {
    if (observer) observer.tags = tags;
    if (onTagEvent) onTagEvent({
      tags,
      updatedTag: tag,
      answerId,
      activeTags,
      eventType: event
    });
  };
  const sentimentUpdate = (sentiment, registerClick = true) => {
    let newHighlightedSentiments;
    if (highlightedSentiments.includes(sentiment)) {
      //delete all active sentiments
      const newActiveSentiments = activeSentiments.filter(activeSentiment => SentimentEnum[activeSentiment.sentiment] !== sentiment);
      setActiveSentiments(newActiveSentiments);
      newHighlightedSentiments = highlightedSentiments.filter(highlightedSentiment => highlightedSentiment !== sentiment);
    } else {
      const newActiveSentiments = (sentimentAnnotations === null || sentimentAnnotations === void 0 ? void 0 : sentimentAnnotations.filter(activeSentiment => SentimentEnum[activeSentiment.sentiment] === sentiment)) || [];
      setActiveSentiments([...newActiveSentiments, ...activeSentiments]);
      newHighlightedSentiments = [...highlightedSentiments, sentiment];
      //add all inactive sentiments
    }
    setHighlightedSentiments(newHighlightedSentiments);
    if (onSentimentClick && registerClick) onSentimentClick({
      sentiment: sentiment
    });
  };
  const handleTagClick = tag => {
    if (!tag.userAdded) {
      const activeTagIndex = activeTags.findIndex(activeTag => activeTag.id === tag.id);
      if (activeTagIndex >= 0) {
        const outputActive = [...activeTags];
        outputActive.splice(activeTagIndex, 1);
        if (onTagEvent) onTagEvent({
          tags: tags || [],
          updatedTag: tag,
          answerId,
          activeTags: outputActive,
          eventType: 'click'
        });
      } else {
        if (onTagEvent) onTagEvent({
          tags: tags || [],
          updatedTag: tag,
          answerId,
          activeTags: [...activeTags, tag],
          eventType: 'click'
        });
      }
    }
  };
  const tagView = jsx(Tags, {
    tags: tags || [],
    tabs: tabs,
    localization: tagLocalization,
    maxSuggestions: maxSuggestions,
    onTagUpdate: handleTagUpdate,
    onTagClick: handleTagClick,
    onSelectedTagsUpdate: selectedTags => setActiveTags(selectedTags),
    onTagSearch: getTagSuggestions,
    printView: printView,
    shouldPreventExpressTag: shouldPreventUserTag,
    expanded: expanded || false,
    onExpand: () => {
      setExpanded(true);
    },
    activeTags: activeTags || [],
    condensedView: showCondensedView
  });
  const sentimentView = jsx(SentimentSection, {
    highlightedSentiments: highlightedSentiments,
    sentimentAnnotations: sentimentAnnotations,
    localization: sentimentButtons,
    hasSeparator: !!tags && !showCondensedView,
    onSentimentClick: sentimentUpdate
  });
  return text && showCondensedView && (tags || sentimentAnnotations) ? jsxs(Accordion, {
    children: [tags ? tagView : null, sentimentAnnotations ? jsx(AccordionItem, {
      title: `${comments.sentiment} ${sentimentScoreDisplay} ${typeof sentimentConfidence === 'number' && !isNaN(sentimentConfidence) ? ` (${getConfidenceAsPercent(sentimentConfidence || 0)} ${comments.confident})` : ''}`,
      noPadding: true,
      children: sentimentView
    }) : null]
  }) : text && (!showCondensedView && tags || !tags) && (tags || deprecatedTags || sentimentAnnotations) ? jsxs("div", {
    className: "kxi-comment-expandable",
    tabIndex: 0,
    onKeyDown: sentimentWrapperKeyDown,
    children: [(addTagVisible || !!deprecatedTags || !!tags) && jsx("div", {
      className: `kxi-comment-tags-wrapper ${tags ? 'kxi-comment-tags-new' : ''} ${sentimentAnnotations ? '' : 'kxi-stretch-width'} ${expanded ? 'expanded' : ''}`,
      children: jsxs("div", {
        className: "kxi-comment-tag-text",
        children: [showCondensedView ? jsxs("div", {
          className: "kxi-comment-tags-header-condensed",
          onClick: handleSentimentExpanded,
          children: [deprecatedTags && !tags ? jsx("span", {
            className: "bold",
            children: `${comments.tags} (${totalTagLength_deprecated || combinedTags_deprecated.length || 0}) ${comments.andSentiment}${sentimentScoreDisplay}`
          }) : null, showCondensedView && (sentimentAnnotations || deprecatedTags || addTagVisible) && !tags ? jsx("div", {
            className: `kxi-comment-expand-arrow ${expanded ? 'expanded' : ''}`,
            onClick: handleSentimentExpanded,
            children: jsx(XIIcon, {
              className: "single_arrow_down",
              iconSize: 24,
              icon: "single_arrow_down"
            })
          }) : undefined]
        }) : deprecatedTags && !tags ? jsxs("div", {
          className: `kxi-comment-tags-text-wrapper ${commentOnlyClass}`,
          onClick: handleSentimentExpanded,
          children: [jsx("span", {
            className: "bold",
            children: `${comments.tags} `
          }), jsxs("span", {
            className: "kxi-comment-tags-count",
            children: ["(", totalTagLength_deprecated || combinedTags_deprecated.length || 0, ")"]
          })]
        }) : null, jsx("div", {
          className: `${deprecatedTags && !tags ? 'kxi-comment-tags' : ''} ${!expanded && deprecatedTags && !tags ? 'kxi-comment-section-hidden' : ''}`,
          children: deprecatedTags && !tags ? jsx(Tags$1, {
            tags: combinedTags_deprecated,
            showAddTag: addTagVisible,
            localization: tagLocalization,
            selectedTagInput: selectedTagIds,
            maxSuggestions: maxSuggestions,
            onTagUpdate: handleTagUpdate_deprecated,
            onTagClick: tagUpdate_deprecated,
            tagSearch: tagSearch,
            shouldPreventExpressTag: shouldPreventExpressTag
          }) : tags ? tagView : null
        })]
      })
    }), sentimentAnnotations && jsxs("div", {
      className: `kxi-comment-sentiment-section-wrapper ${tags ? 'kxi-comment-tags-new' : ''} ${showCondensedView && !expanded ? 'kxi-comment-sentiment-hide' : ''}`,
      onClick: handleSentimentExpanded,
      children: [showCondensedView ? undefined : jsxs("div", {
        className: `kxi-comment-sentiment-text ${tags ? 'kxi-comment-tags-new' : ''} ${commentOnlyClass}`,
        children: [jsxs("span", {
          children: [jsxs("span", {
            className: "bold",
            children: [comments.sentiment, sentimentScoreDisplay]
          }), jsx("span", {
            children: typeof sentimentConfidence === 'number' && !isNaN(sentimentConfidence) ? ` (${getConfidenceAsPercent(sentimentConfidence || 0)} ${comments.confident})` : null
          })]
        }), tags ? jsx("div", {
          className: `kxi-comment-expand-arrow kxi-comment-tags-new ${expanded ? 'expanded' : ''}`,
          children: jsx(XIIcon, {
            className: "single_arrow_down",
            iconSize: 24,
            icon: "single_arrow_down"
          })
        }) : null]
      }), jsx("div", {
        className: `kxi-comment-sentiment-wrapper ${expanded ? '' : 'kxi-comment-section-hidden'}`,
        onClick: e => e.stopPropagation(),
        children: sentimentView
      })]
    }), _isOnExperienceCard && !showCondensedView && (sentimentAnnotations || deprecatedTags || addTagVisible) && !tags ? jsx("div", {
      className: `kxi-comment-expand-arrow ${expanded ? 'expanded' : ''}`,
      onClick: handleSentimentExpanded,
      children: jsx(XIIcon, {
        className: "single_arrow_down",
        iconSize: 24,
        icon: "single_arrow_down"
      })
    }) : undefined]
  }) : null;
};

const CommentPrintView = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(CommentPrintView, props);
  const {
    tags = [],
    tabs = [],
    localization: localizationInput = {},
    imageSrcUrls,
    sentimentScore,
    promptLabel,
    sentimentConfidence,
    text
  } = props;
  const {
    comments,
    tags: tagsLocalization,
    emptyCommentText
  } = mergeObjects(localizationInput, commentLocalization);
  useWarnOnceIf(props.tagAnnotations !== undefined, 'Print view is not supported for the deprecated `tagAnnotations` prop. Please use the `tags` prop instead.');
  const sentimentScoreDisplay = typeof sentimentScore === 'number' && !isNaN(sentimentScore) ? `: ${sentimentScore}` : null;
  const sentimentConfidenceDisplay = typeof sentimentConfidence === 'number' && !isNaN(sentimentConfidence) ? ` (${getConfidenceAsPercent(sentimentConfidence || 0)} ${comments.confident})` : null;
  const hasMedia = imageSrcUrls && imageSrcUrls.length;
  const returnMedia = () => {
    if (imageSrcUrls && imageSrcUrls.length) {
      return jsx(ImageCarousel, {
        images: imageSrcUrls,
        _printView: true
      });
    }
    return null;
  };
  const noop = () => {};
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: "kxi-comment-print-view-wrapper",
    ref: ref,
    children: [promptLabel && jsx("div", {
      className: "kxi-comment-prompt-label",
      children: promptLabel
    }), jsx("div", {
      className: "kxi-comment-text",
      children: text !== null && text !== void 0 ? text : emptyCommentText
    }), hasMedia ? jsx("div", {
      className: "kxi-comment-media-wrapper",
      children: returnMedia()
    }) : null, sentimentScoreDisplay && sentimentConfidenceDisplay && jsx("div", {
      className: "kxi-comment-sentiment",
      children: jsxs("span", {
        children: [jsxs("span", {
          className: "kxi-text-600",
          children: [comments.sentiment, sentimentScoreDisplay]
        }), jsx("span", {
          children: sentimentConfidenceDisplay
        })]
      })
    }), tags.length > 0 && jsx("div", {
      className: "kxi-comment-tags",
      children: jsx(Tags, {
        tags: tags,
        tabs: tabs,
        localization: tagsLocalization,
        expanded: false,
        onExpand: noop,
        onSelectedTagsUpdate: noop,
        onTagClick: noop,
        onTagUpdate: noop,
        printView: true
      })
    })]
  }));
});
CommentPrintView.displayName = 'CommentPrintView';

const errorOnUndefinedData = annotation => {
  for (const key in annotation) {
    if (annotation['userAdded'] !== true) {
      if (annotation[key] === undefined) {
        throw new Error(`The Kitchen: You have passed a tag object to the ExperienceCard that has ${key} as ${annotation[key]}, this will cause erroneous behavior in the card.`);
      }
    }
  }
};
class CommentProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], CommentProps.prototype, "externalId", void 0);
__decorate([Bound(), __metadata("design:type", String)], CommentProps.prototype, "answerId", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CommentProps.prototype, "promptLabel", void 0);
__decorate([Bound(), __metadata("design:type", String)], CommentProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "sentimentAnnotations", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "tagAnnotations", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "tags", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "tabs", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CommentProps.prototype, "tagSearch", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CommentProps.prototype, "getTagSuggestions", void 0);
__decorate([Bound(), __metadata("design:type", Number)], CommentProps.prototype, "maxSuggestions", void 0);
__decorate([Bound(), __metadata("design:type", String)], CommentProps.prototype, "audioSrcUrl", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "imageSrcUrls", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CommentProps.prototype, "videoSrcUrl", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "mediaActions", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CommentProps.prototype, "genericModalProps", void 0);
__decorate([Bound(), __metadata("design:type", Number)], CommentProps.prototype, "sentimentScore", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CommentProps.prototype, "sentimentConfidence", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CommentProps.prototype, "sentimentBarExpanded", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CommentProps.prototype, "showMoreMomentsLink", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CommentProps.prototype, "addTagVisible", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CommentProps.prototype, "highlightedText", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CommentProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "selectedTagIds", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CommentProps.prototype, "selectedSentiments", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CommentProps.prototype, "printView", void 0);
class CommentEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], CommentEvents.prototype, "onTagChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CommentEvents.prototype, "onTagEvent", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CommentEvents.prototype, "onSentimentClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CommentEvents.prototype, "shouldPreventExpressTag", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CommentEvents.prototype, "shouldPreventUserTag", void 0);
const Comment$1 = /*#__PURE__*/React.forwardRef((props, ref) => {
  useDebugService(Comment$1, props);
  const {
      printView = false
    } = props,
    rest = __rest(props, ["printView"]);
  const isPrinting = useIsPrinting(printView);
  return !isPrinting ? jsx(CommentImpl, Object.assign({}, rest, {
    ref: ref
  })) : jsx(CommentPrintView, Object.assign({}, rest, {
    ref: ref
  }));
});
Comment$1.displayName = 'Comment';
/**
 * Description: ExperienceCard Comment component - exposed as a standalone component.
 */
const CommentImpl = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(CommentImpl, props);
  const {
      _highlightedCommentId: highlightedCommentId,
      _internalOnTagChange_deprecated,
      _isOnExperienceCard = false,
      _setHighlightedCommentId,
      addTagVisible = true,
      answerId,
      audioSrcUrl,
      externalId,
      genericModalProps,
      getTagSuggestions,
      highlightedText,
      imageSrcUrls,
      localization: localizationInput = {},
      maxSuggestions = 5,
      mediaActions,
      observer,
      onSentimentClick,
      onTagChange,
      onTagEvent,
      promptLabel,
      selectedSentiments = [],
      selectedTagIds = [],
      sentimentAnnotations,
      sentimentBarExpanded,
      sentimentConfidence,
      sentimentScore,
      shouldPreventExpressTag,
      shouldPreventUserTag,
      showMoreMomentsLink,
      tabs = [],
      tagAnnotations: deprecatedTags,
      tags,
      tagSearch,
      text = '',
      videoSrcUrl
    } = props,
    ingredients = __rest(props, ["_highlightedCommentId", "_internalOnTagChange_deprecated", "_isOnExperienceCard", "_setHighlightedCommentId", "addTagVisible", "answerId", "audioSrcUrl", "externalId", "genericModalProps", "getTagSuggestions", "highlightedText", "imageSrcUrls", "localization", "maxSuggestions", "mediaActions", "observer", "onSentimentClick", "onTagChange", "onTagEvent", "promptLabel", "selectedSentiments", "selectedTagIds", "sentimentAnnotations", "sentimentBarExpanded", "sentimentConfidence", "sentimentScore", "shouldPreventExpressTag", "shouldPreventUserTag", "showMoreMomentsLink", "tabs", "tagAnnotations", "tags", "tagSearch", "text", "videoSrcUrl"]);
  const {
    comments,
    sentimentButtons,
    tags: tagLocalization,
    emptyCommentText
  } = mergeObjects(localizationInput, commentLocalization);
  useWarnIsDeprecated('Comment', {
    addTagVisible: {
      value: props.addTagVisible,
      message: 'Use `tabs[number].showAddTag` instead.'
    },
    onTagChange: {
      value: props.onTagChange,
      message: 'Use `onTagEvent` instead.'
    },
    shouldPreventExpressTag: {
      value: props.shouldPreventUserTag,
      message: 'Use `shouldPreventUserTag` instead.'
    },
    tagAnnotations: {
      value: deprecatedTags,
      message: 'Use the `tags` prop instead.'
    }
  }, 'prop');
  // size to switch media queries
  const screenBreakSize = 480;
  const [uuid] = React.useState(`${externalId}-${uuidv4()}`);
  const [combinedTags_deprecated, setCombinedTags_deprecated] = React.useState([]);
  const [activeSentiments, setActiveSentiments] = React.useState([]);
  const [highlightedSentiments, setHighlightedSentiments] = React.useState([]);
  const [activeTags, setActiveTags] = React.useState([]);
  const [showCondensedView, setShowCondensedView] = React.useState(false);
  const [_forceUpdate, forceUpdate] = React.useState(uuidv4());
  const activeTagRef_deprecated = React.useRef([]);
  React.useEffect(() => {
    let newHighlightedSentiments = [];
    let newActiveSentiments = activeSentiments;
    newHighlightedSentiments = selectedSentiments;
    _SentimentsOptions.forEach(sentiment => {
      if (!selectedSentiments.includes(sentiment) && highlightedSentiments.includes(sentiment)) {
        newActiveSentiments = newActiveSentiments.filter(activeSentiment => SentimentEnum[activeSentiment.sentiment] !== sentiment);
      } else if (selectedSentiments.includes(sentiment) && !highlightedSentiments.includes(sentiment)) {
        const additionalActiveSentiments = (sentimentAnnotations === null || sentimentAnnotations === void 0 ? void 0 : sentimentAnnotations.filter(activeSentiment => SentimentEnum[activeSentiment.sentiment] === sentiment)) || [];
        newActiveSentiments = [...newActiveSentiments, ...additionalActiveSentiments];
      }
    });
    setHighlightedSentiments(newHighlightedSentiments);
    setActiveSentiments(newActiveSentiments);
  }, [JSON.stringify(selectedSentiments)]);
  React.useEffect(() => {
    if (!combinedTags_deprecated.length) return;
    const newActiveTags = [];
    selectedTagIds.forEach(tag => {
      const combinedTagIndex = combinedTags_deprecated.findIndex(combinedTags_deprecated => combinedTags_deprecated.tagId === tag);
      if (combinedTags_deprecated[combinedTagIndex]) newActiveTags.push(combinedTags_deprecated[combinedTagIndex]);
    });
    activeTagRef_deprecated.current = newActiveTags;
    forceUpdate(uuidv4());
  }, [JSON.stringify(selectedTagIds), combinedTags_deprecated]);
  React.useEffect(() => {
    if (!(tags === null || tags === void 0 ? void 0 : tags.length)) return;
    const newActiveTags = [];
    selectedTagIds.forEach(tagId => {
      const tagIndex = tags.findIndex(tag => tag.id === tagId);
      if (tagIndex !== -1) {
        newActiveTags.push(tags[tagIndex]);
      }
    });
    setActiveTags(newActiveTags);
  }, [JSON.stringify(selectedTagIds), tags]);
  // some tags use the same tag for words at multiple positions. Combine them for singular display
  React.useEffect(() => {
    var _a, _b;
    const tempCombinedTags = [];
    for (let i = 0; deprecatedTags && i < ((_a = deprecatedTags === null || deprecatedTags === void 0 ? void 0 : deprecatedTags.length) !== null && _a !== void 0 ? _a : 0); i++) {
      // setting all keys so function will catch undefined properties
      errorOnUndefinedData({
        beginIndex: deprecatedTags[i].beginIndex,
        endIndex: deprecatedTags[i].endIndex,
        sentiment: deprecatedTags[i].sentiment,
        tagId: deprecatedTags[i].tagId,
        tag: deprecatedTags[i].tag,
        userAdded: (_b = deprecatedTags[i].userAdded) !== null && _b !== void 0 ? _b : false
      });
      const beginIndex = deprecatedTags[i].beginIndex || 0;
      const endIndex = deprecatedTags[i].endIndex || 0;
      const sentimentNum = deprecatedTags[i].sentiment || 0;
      const existingTagIndex = tempCombinedTags.findIndex(item => item.tagName === deprecatedTags[i].tag && item.userAdded === deprecatedTags[i].userAdded);
      if (existingTagIndex >= 0) {
        tempCombinedTags[existingTagIndex].positions.push({
          beginIndex,
          endIndex,
          sentimentType: 'tag',
          userAdded: deprecatedTags[i].userAdded
        });
      } else {
        tempCombinedTags.push({
          tagName: deprecatedTags[i].tag,
          tagId: deprecatedTags[i].tagId,
          sentiment: SentimentEnum[sentimentNum],
          positions: [{
            beginIndex,
            endIndex,
            sentimentType: 'tag',
            userAdded: deprecatedTags[i].userAdded
          }],
          userAdded: deprecatedTags[i].userAdded || false
        });
      }
    }
    setCombinedTags_deprecated(tempCombinedTags);
  }, [deprecatedTags]);
  const commentOnlyClass = !_isOnExperienceCard ? 'kxi-comment-isolated' : '';
  const sentimentScoreDisplay = typeof sentimentScore === 'number' && !isNaN(sentimentScore) ? `: ${sentimentScore}` : ': —';
  const onResize = React.useCallback(() => setShowCondensedView(document.body.getBoundingClientRect().width < screenBreakSize), []);
  const {
    ref: outerResizeRef
  } = useResizeDetector({
    onResize,
    refreshMode: 'debounce',
    refreshRate: 0,
    handleHeight: false
  });
  return jsxs("div", Object.assign({}, debugService.componentProps, ingredients, {
    className: `kxi-comment-wrapper orca ${commentOnlyClass}`,
    id: uuid,
    ref: mergeRefs([ref, outerResizeRef]),
    children: [jsx(Header, {
      _isOnExperienceCard: _isOnExperienceCard,
      _setHighlightedCommentId: _setHighlightedCommentId,
      answerId: answerId,
      comments: comments,
      highlightedCommentId: highlightedCommentId,
      promptLabel: promptLabel,
      showMoreMomentsLink: showMoreMomentsLink
    }), jsx(Body, {
      activeSentiments: activeSentiments,
      activeTagRef_deprecated: activeTagRef_deprecated,
      activeTags: activeTags,
      audioSrcUrl: audioSrcUrl,
      combinedTags_deprecated: combinedTags_deprecated,
      comments: comments,
      emptyCommentText: emptyCommentText,
      genericModalProps: genericModalProps,
      highlightedText: highlightedText,
      imageSrcUrls: imageSrcUrls,
      mediaActions: mediaActions,
      sentimentAnnotations: sentimentAnnotations,
      tags: tags,
      text: text,
      uuid: uuid,
      videoSrcUrl: videoSrcUrl
    }), jsx(Footer, {
      _internalOnTagChange_deprecated: _internalOnTagChange_deprecated,
      _isOnExperienceCard: _isOnExperienceCard,
      activeSentiments: activeSentiments,
      activeTagRef_deprecated: activeTagRef_deprecated,
      activeTags: activeTags,
      addTagVisible: addTagVisible,
      answerId: answerId,
      combinedTags_deprecated: combinedTags_deprecated,
      commentOnlyClass: commentOnlyClass,
      comments: comments,
      deprecatedTags: deprecatedTags,
      getTagSuggestions: getTagSuggestions,
      highlightedSentiments: highlightedSentiments,
      maxSuggestions: maxSuggestions,
      observer: observer,
      onSentimentClick: onSentimentClick,
      onTagChange: onTagChange,
      onTagEvent: onTagEvent,
      selectedTagIds: selectedTagIds,
      sentimentAnnotations: sentimentAnnotations,
      sentimentBarExpanded: sentimentBarExpanded,
      sentimentButtons: sentimentButtons,
      sentimentConfidence: sentimentConfidence,
      sentimentScoreDisplay: sentimentScoreDisplay,
      setActiveSentiments: setActiveSentiments,
      setActiveTags: setActiveTags,
      setHighlightedSentiments: setHighlightedSentiments,
      shouldPreventExpressTag: shouldPreventExpressTag,
      shouldPreventUserTag: shouldPreventUserTag,
      showCondensedView: showCondensedView,
      tabs: tabs,
      tagLocalization: tagLocalization,
      tags: tags,
      tagSearch: tagSearch,
      text: text
    })]
  }));
});
CommentImpl.displayName = 'CommentImpl';

/**
 * This function was adapted from https://stackoverflow.com/questions/161738/what-is-the-best-regular-expression-to-check-if-a-string-is-a-valid-url
 * It takes a string and outputs wether or not its a URL
 *
 * @param possibleURL The URL to test
 * @returns A boolean value stating wether or not the tested string is a valid URL
 */
function isURL(possibleURL) {
  return /^(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?,&!/=]*)$/.test(possibleURL) && !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(possibleURL);
}

/**
 * The props to pass into the TextToLink Micro Component
 */
class TextToLinkProps {}
class TextToLinkEvents {}
/**
 * Description: This micro component takes text and outputs the correct properties to pass into either
 * an anchor tag or span tag. If the text is a link all of the correct anchor properties
 * get set including style.
 *
 * Note: if truncated wrap the anchor tag in a ToolTipWrapper and use the wrapper props
 *
 * Note: you need to include the `kxi-text-to-link` or `kxi-text-to-link-bold` classes for
 * the component to display properly
 */
const TextToLink = props => {
  const {
    text,
    truncate,
    textStyle = 'meta',
    framework = 'angular',
    rel = 'prefetch'
  } = props;
  const className = `kxi-text-to-link${textStyle === 'bold' ? '-bold' : ''}`;
  const htmlClass = framework === 'angular' ? {
    class: className
  } : {
    className
  };
  if (isURL(text)) {
    if (truncate && text.length > truncate) {
      return {
        linkProps: Object.assign({
          href: /^https?/.test(text) ? text : `//${text}`,
          target: '_blank',
          rel
        }, htmlClass),
        isLink: true,
        // We truncate in code vs CSS due to the requirement for ... to be a part of the link
        text: `${text.substr(0, truncate)}...`,
        isTruncating: true,
        wrapperProps: {
          direction: 12,
          text: `<span style="word-break: break-all">${text}</span>`,
          maxWidth: 'calc((100vw - 6px)/2)'
        }
      };
    } else {
      return {
        isLink: true,
        linkProps: Object.assign({
          href: /^https?/.test(text) ? text : `//${text}`,
          target: '_blank',
          rel
        }, htmlClass),
        text,
        isTruncating: false
      };
    }
  } else {
    return Object.assign({
      isLink: false,
      text,
      isTruncating: false
    }, textStyle === 'bold' ? htmlClass : '');
  }
};

const MetaMap = props => {
  const {
    meta = []
  } = props;
  return jsx(Fragment, {
    children: meta.map((metaTag, i) => {
      const pipe = meta.length - 1 !== i ? ' | ' : ' ';
      const metaStrings = metaTag.split(' ');
      const fullMeta = metaStrings.map((metaStr, i) => {
        const textToLink = TextToLink({
          text: metaStr,
          framework: 'react',
          truncate: 35,
          rel: 'noreferrer'
        });
        if (textToLink.isLink) {
          const anchor = jsx("a", Object.assign({}, textToLink.linkProps, {
            onClick: e => {
              var _a, _b;
              e.stopPropagation();
              (_b = (_a = textToLink.linkProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
            },
            children: textToLink.text
          }));
          return jsx(React.Fragment, {
            children: textToLink.isTruncating ? jsx(ToolTipWrapper, Object.assign({}, textToLink.wrapperProps, {
              children: anchor
            })) : anchor
          }, metaStr + i);
        } else {
          return jsx("span", {
            className: textToLink.className,
            children: metaStr + `${i < metaStrings.length - 1 ? ' ' : ''}`
          }, metaStr + i);
        }
      });
      return jsxs("span", {
        children: [fullMeta, pipe]
      }, i);
    })
  });
};

const IconButtons = props => {
  const {
    dropdownDirection,
    iconButtons,
    isFavorite,
    limitedIconButtonNumber,
    limitedIconButtonNumberFallback = 4,
    limitIconButtonOverride,
    setKabobActiveClick,
    tooltipDirection = 1,
    isNotesOpen,
    setIsNotesOpen
  } = props;
  const [mappedIconButtons, setMappedIconButtons] = React.useState([]);
  const hasWarnedHiddenDropdown = React.useRef(false);
  React.useEffect(() => {
    const alwaysDisplayCount = iconButtons.filter(item => item.alwaysDisplay).length;
    iconButtons.sort((a, b) => {
      const isTrueNum = a.alwaysDisplay ? -1 : 1;
      return a.alwaysDisplay === b.alwaysDisplay ? 0 : isTrueNum;
    });
    const handleNotesClick = (e, onClick) => {
      onClick === null || onClick === void 0 ? void 0 : onClick(e);
      setIsNotesOpen === null || setIsNotesOpen === void 0 ? void 0 : setIsNotesOpen(!isNotesOpen);
    };
    const handleOnValueChange = item => {
      item.onClick(item);
    };
    const tempArrIconButtons = [];
    const kabobItems = [];
    const addVisibleItems = j => {
      const {
        type,
        title,
        dropdownData,
        onClick
      } = iconButtons[j];
      let {
        icon
      } = iconButtons[j];
      if (type === 'dropdown') {
        tempArrIconButtons.push(jsx(Dropdown, {
          color: "orca",
          select: "none",
          type: "icon",
          internalProps: {
            iconButton: {
              icon,
              onClick: e => e.stopPropagation()
            }
          },
          data: dropdownData || [],
          onValueChange: eventData => onClick(eventData.selected)
        }, j));
      } else {
        if (type === 'favorite') {
          icon = isFavorite ? 'favorite_filled' : 'favorite';
        }
        if (type === 'notes') {
          icon = isNotesOpen ? 'comment_filled' : 'comment';
        }
        tempArrIconButtons.push(jsx(IconButton, {
          icon: icon,
          tooltip: {
            text: title,
            direction: tooltipDirection
          },
          onClick: e => {
            e.stopPropagation();
            if (type === 'notes') {
              handleNotesClick(e, onClick);
            } else {
              onClick(e);
            }
          }
        }, j));
      }
      if (j !== iconButtons.length - 1) {
        tempArrIconButtons.push(jsx("p", {
          className: "kxi-pipe",
          children: "|"
        }, 'kxi-pipe-' + j));
      }
    };
    const addHiddenItems = j => {
      const {
        type,
        icon,
        title,
        onClick
      } = iconButtons[j];
      if (type === 'dropdown' && !hasWarnedHiddenDropdown.current) {
        hasWarnedHiddenDropdown.current = true;
        devOnlyWarn(`The Kitchen: You have passed an iconButton into the ExperienceCard with a type of "dropdown" that is either not one of the first 4 items, or does not have "alwaysDisplay" set to true – this will render under the kabob icon dropdown, and the self contained dropdown items related to the "${icon}" icon will not show. It must be one of the first four icons in the array, or have the alwaysDisplay set to true, to take advantage of the "dropdown" type functionality. Reach out to the Kitchen team for questions.`);
      }
      kabobItems.push({
        title: title || icon,
        id: `${j}`,
        // Couldn't get TS to cooperate on this one
        onClick: type === 'notes' ? e => handleNotesClick(e, onClick) : onClick
      });
    };
    // depending on the responsiveness and space left on the card
    if (limitIconButtonOverride) {
      const numberOfVisibleIcons = limitedIconButtonNumber > 0 ? limitedIconButtonNumber : limitedIconButtonNumberFallback;
      const showRemainder = iconButtons.length - numberOfVisibleIcons <= 1;
      for (let j = 0; j < iconButtons.length; j++) {
        if (j < numberOfVisibleIcons || showRemainder) {
          addVisibleItems(j);
        } else {
          addHiddenItems(j);
        }
      }
    } else {
      for (let j = 0; j < iconButtons.length; j++) {
        const showRemainder = iconButtons.length < 6 || iconButtons.length - alwaysDisplayCount <= 1;
        if (j < 4 || iconButtons[j].alwaysDisplay || showRemainder) {
          addVisibleItems(j);
        } else {
          addHiddenItems(j);
        }
      }
    }
    if (kabobItems.length) {
      tempArrIconButtons.push(jsx(Dropdown, {
        color: "orca",
        select: "none",
        type: "icon",
        internalProps: {
          iconButton: {
            icon: 'kabob',
            onClick: e => e.stopPropagation()
          }
        },
        data: kabobItems,
        direction: dropdownDirection,
        itemSearchCount: Infinity,
        onOpenChange: eventData => setKabobActiveClick(eventData.isOpen),
        onValueChange: eventData => handleOnValueChange(eventData.selected)
      }, "leftover_items_kabob_dropdown"));
    }
    setMappedIconButtons(tempArrIconButtons);
  }, [isFavorite, iconButtons, dropdownDirection, limitIconButtonOverride, limitedIconButtonNumber, isNotesOpen]);
  return jsx(Fragment, {
    children: mappedIconButtons
  });
};

const NoteFooter = props => {
  const {
    onClick,
    cancel,
    add,
    disabled
  } = props;
  const debugService = useDebugService(NoteFooter, props);
  return jsxs("div", Object.assign({
    className: "kxi-note-footer orca"
  }, debugService.componentProps, {
    children: [jsx(DefaultButton, {
      fill: "text",
      text: cancel,
      onClick: () => onClick('cancel'),
      "data-testid": "note-cancel-button"
    }), jsx(DefaultButton, {
      text: add,
      disabled: disabled,
      onClick: () => onClick('add'),
      "data-testid": "note-add-button"
    })]
  }));
};
NoteFooter.displayName = 'NoteFooter';

function getMaxForUnit(o, unit) {
  var _a, _b, _c;
  switch (unit) {
    case 'day':
      return (_a = o === null || o === void 0 ? void 0 : o.maxDays) !== null && _a !== void 0 ? _a : 365;
    case 'week':
      return (_b = o === null || o === void 0 ? void 0 : o.maxWeeks) !== null && _b !== void 0 ? _b : 104;
    case 'month':
      return (_c = o === null || o === void 0 ? void 0 : o.maxMonths) !== null && _c !== void 0 ? _c : 36;
  }
}
function isCustomRelativeDateValid(option) {
  const {
    unit,
    min,
    value
  } = option;
  const max = getMaxForUnit(option, unit);
  return value !== null && min <= value && value <= max;
}
Date.prototype.addDays = function (days) {
  const value = this.valueOf();
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
};
function addDaysToDate(date, days) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}
function addWeeksToDate(date, weeks) {
  return addDaysToDate(date, 7 * weeks);
}
function addMonthsToDate(date, months) {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
}
function validateLocale(locale) {
  return locale.replaceAll('_', '-');
}
function getDateTimeFormat(locale, options = {
  month: '2-digit',
  year: 'numeric',
  day: '2-digit'
}) {
  /**
   * DEV-34296: Workaround for https://bugs.chromium.org/p/chromium/issues/detail?id=1427119&q=datetimeformat&can=2
   */
  if (locale === 'ar') {
    options = Object.assign(Object.assign({}, options), {
      numberingSystem: 'arab'
    });
  }
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch (e) {
    devOnlyError(`Something went wrong with the locale "${locale}".`, `Is it a valid locale string?`, `See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl for locale format requirements.`);
    throw e;
  }
}
function formatDate(date, locale, localeOptions) {
  return getDateTimeFormat(locale, localeOptions).format(date);
}
// gets dates in between two dates
const getDates = (startDate, stopDate) => {
  const dateArray = [];
  let currentDate = startDate;
  while (currentDate <= stopDate) {
    dateArray.push(new Date(currentDate));
    currentDate = addDaysToDate(currentDate, 1);
  }
  return dateArray;
};
const returnShortDayArray = locale => {
  const currDate = new Date();
  const DAYS = [0, 1, 2, 3, 4, 5, 6];
  // It's not straightforward to start at Sunday, so get seven consecutive days
  // and sort them by day of the week afterward.
  const mappedDays = DAYS.map(day => {
    const date = new Date(currDate.getFullYear(), currDate.getMonth(), day);
    return {
      dayOfWeek: date.getDay(),
      localization: formatDate(date, locale, {
        weekday: 'narrow'
      })
    };
  });
  return mappedDays.sort((first, second) => first.dayOfWeek - second.dayOfWeek);
};
const formatControlledInput = (e, property, propertySetter, locale) => {
  const {
    value
  } = e.target;
  // If the user is deleting rather than typing
  if (value.length === property.length - 1) {
    propertySetter(value);
    return;
  }
  const newLetter = value[value.length - 1];
  const typingSection = getTypingSection(value, locale);
  // Separators can be more than one character
  // (e.g., for the Czech locale `cs-CZ`, the separator is '. ')
  const nextLiteral = typingSection.nextLiteral;
  const firstCharOfNextLiteral = nextLiteral.substring(0, 1);
  const restOfNextLiteral = nextLiteral.substring(1);
  switch (typingSection.section) {
    case 'DD':
      if (newLetter === firstCharOfNextLiteral && typingSection.sectionData.length > 0 && typingSection.hasAdditional) {
        //allow a slash when length > 0 and there are more sections
        propertySetter(`${value}${restOfNextLiteral}`);
      } else if (Number(typingSection.sectionData) > 3 && typingSection.sectionData.length === 1) {
        //fast forward to the next section when an impossible 2 digit number is entered
        if (typingSection.hasAdditional) {
          propertySetter(`${value}${nextLiteral}`);
        } else {
          propertySetter(value);
        }
      } else if ((typingSection.sectionData === '0' || Number(typingSection.sectionData)) && typingSection.sectionData.length === 1) {
        // allow a leading 0
        propertySetter(value);
      } else if (Number(typingSection.sectionData) && typingSection.sectionData.length === 2) {
        // fast forward onto the next section once this section is filled out
        if (typingSection.hasAdditional) {
          propertySetter(`${value}${nextLiteral}`);
        } else {
          propertySetter(value);
        }
      } else if (Number(typingSection.sectionData.slice(0, 2)) && typingSection.sectionData.length > 2 && typingSection.hasAdditional) {
        // if the user gets themselves into a position where they must enter a '/' allow
        // them a way out
        propertySetter(`${value.slice(0, value.length - 1)}${firstCharOfNextLiteral}`);
      }
      break;
    case 'MM':
      if (newLetter === firstCharOfNextLiteral && typingSection.sectionData.length > 0 && typingSection.hasAdditional) {
        propertySetter(`${value}${restOfNextLiteral}`);
      } else if (Number(typingSection.sectionData) > 1 && typingSection.sectionData.length === 1) {
        if (typingSection.hasAdditional) {
          propertySetter(`${value}${nextLiteral}`);
        } else {
          propertySetter(value);
        }
      } else if ((typingSection.sectionData === '0' || Number(typingSection.sectionData)) && typingSection.sectionData.length === 1) {
        propertySetter(value);
      } else if (Number(typingSection.sectionData) && typingSection.sectionData.length === 2) {
        if (typingSection.hasAdditional) {
          propertySetter(`${value}${nextLiteral}`);
        } else {
          propertySetter(value);
        }
      } else if (Number(typingSection.sectionData.slice(0, 2)) && typingSection.sectionData.length > 2 && typingSection.hasAdditional) {
        propertySetter(`${value.slice(0, value.length - 1)}${firstCharOfNextLiteral}`);
      }
      break;
    case 'YYYY':
      if (newLetter === firstCharOfNextLiteral && (typingSection.sectionData.length === 2 || typingSection.sectionData.length === 4) && typingSection.hasAdditional) {
        propertySetter(`${value}${restOfNextLiteral}`);
      } else if (Number(typingSection.sectionData) && typingSection.sectionData.length < 4) {
        propertySetter(value);
      } else if (Number(typingSection.sectionData) && typingSection.sectionData.length === 4) {
        if (typingSection.hasAdditional) {
          propertySetter(`${value}${nextLiteral}`);
        } else {
          propertySetter(value);
        }
      } else if (Number(typingSection.sectionData.slice(0, 2)) && typingSection.sectionData.length > 2 && typingSection.hasAdditional) {
        propertySetter(`${value.slice(0, value.length - 1)}${firstCharOfNextLiteral}`);
      }
      break;
  }
};
const parsePartiallyTypedDate = (value, locale) => {
  const formatParts = getDateFormatParts(locale);
  const sectionsInPartiallyTypedDate = [];
  /** The index of the character in `value` that we're currently examining */
  let valueIndex = 0;
  // Iterate through each part of the format and attempt to match it to
  // the portion of `value` that we haven't already examined.
  for (const formatPart of formatParts) {
    if (valueIndex >= value.length) break;
    if (formatPart.type === 'literal') {
      const literalString = formatPart.value;
      const correspondingValueSubstring = value.substring(valueIndex, valueIndex + literalString.length);
      const mismatch = lazy(literalString).zip(correspondingValueSubstring).some(([literalChar, valueChar]) => literalChar !== valueChar);
      if (mismatch) break;
      valueIndex += literalString.length;
    } else if (formatPart.type === 'year' || formatPart.type === 'month' || formatPart.type === 'day') {
      const startIndex = valueIndex;
      const maxDigits = formatPart.value.length;
      for (let i = 0; i < maxDigits; i++) {
        const currentDigit = Number(value[valueIndex]);
        if (Number.isNaN(currentDigit)) {
          break;
        }
        valueIndex++;
      }
      const data = value.substring(startIndex, valueIndex);
      sectionsInPartiallyTypedDate.push({
        section: formatPart.value,
        data
      });
    } else {
      ensureExhaustive(formatPart);
    }
  }
  if (hasOneOrMore(sectionsInPartiallyTypedDate)) {
    return sectionsInPartiallyTypedDate;
  }
  const firstNonLiteralPart = formatParts.find(part => part.type !== 'literal');
  return [{
    section: firstNonLiteralPart.value,
    data: ''
  }];
};
const getTypingSection = (value, locale) => {
  var _a, _b;
  const sections = parsePartiallyTypedDate(value, locale);
  const {
    section,
    data: sectionData
  } = last(sections);
  const parts = getDateFormatParts(locale);
  const currentSectionIndex = parts.findIndex(part => section === part.value);
  const nextLiteral = (_b = (_a = parts.find((part, i) => part.type === 'literal' && i > currentSectionIndex)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
  return {
    section,
    sectionData,
    hasAdditional: sections.length < 3,
    nextLiteral
  };
};
const parseToDate = (dateString, locale) => {
  const sections = parsePartiallyTypedDate(dateString, locale);
  const getSection = target => {
    var _a, _b;
    return Number((_b = (_a = sections.find(({
      section
    }) => section === target)) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : '');
  };
  let localYear = getSection('YYYY');
  const month = getSection('MM');
  const day = getSection('DD');
  const isValid = localYear > 0 && isBetween(month, 1, 12) && isBetween(day, 1, 31);
  if (!isValid) {
    return undefined;
  }
  const now = new Date();
  const getCurrentYear = options => {
    const year = getDateTimeFormat(locale, options).formatToParts(now).find(({
      type
    }) => type === 'year').value;
    return Number(year);
  };
  // Account for calendars which do not use Gregorian years (e.g., the calendar for the Thai locale `th`)
  const localCurrentYear = getCurrentYear();
  const gregorianCurrentYear = getCurrentYear({
    calendar: 'gregory'
  });
  if (localYear < 100) {
    const localCurrentCentury = Math.floor(localCurrentYear / 100) * 100;
    localYear += localCurrentCentury;
  }
  const gregorianYear = localYear + (gregorianCurrentYear - localCurrentYear);
  return new Date(gregorianYear, month - 1, day);
};
const getDateFormatParts = locale => {
  const parts = getDateTimeFormat(locale).formatToParts(new Date());
  return parts.map(part => {
    switch (part.type) {
      case 'literal':
        return {
          type: 'literal',
          value: part.value
        };
      case 'day':
        return {
          type: 'day',
          value: 'DD'
        };
      case 'month':
        return {
          type: 'month',
          value: 'MM'
        };
      case 'year':
        return {
          type: 'year',
          value: 'YYYY'
        };
      default:
        devOnlyError('Unexpected DateTimeFormatPart type: ' + part.type, part);
        return null;
    }
  }).filter(isNotNullish);
};
const extractDateFormatPlaceholder = locale => getDateFormatParts(locale).map(part => part.value).join('');
const getInitialDates = dateObject => {
  const dateArray = [];
  const {
    selectedDateRange
  } = dateObject;
  let initialDateTemp = new Date();
  initialDateTemp = selectedDateRange.first || new Date();
  const initialDate = new Date(initialDateTemp);
  // initial date is getting reset, so it's a running update.
  // back one
  initialDate.setMonth(initialDate.getMonth() - 1, 1);
  const zero = new Date(initialDate);
  // current
  initialDate.setMonth(initialDate.getMonth() + 1, 1);
  const first = new Date(initialDate);
  // next
  initialDate.setMonth(initialDate.getMonth() + 1, 1);
  const second = new Date(initialDate);
  // two forward
  initialDate.setMonth(initialDate.getMonth() + 1, 1);
  const third = new Date(initialDate);
  dateArray.push(zero, first, second, third);
  return dateArray;
};

const Note = props => {
  const {
    note: {
      text,
      meta,
      id: idInput
    },
    localization: {
      edit,
      cancel,
      save,
      locale,
      youWrote
    },
    addDisabled,
    onNoteChanged,
    onNoteDelete
  } = props;
  const debugService = useDebugService(Note, props);
  const [isEditingNoteInContext, setIsEditingNoteInContext] = React.useContext(NotesContext);
  const id = useOptionalId(idInput);
  const [editingText, setEditingText] = React.useState(false);
  const [areaText, setAreaText] = React.useState(text);
  const [value, setValue] = React.useState(text);
  const editButtonRef = React.useRef(null);
  const editAreaRef = React.useRef(null);
  const isFirstRender = useIsFirstRender();
  React.useEffect(() => {
    setAreaText(text);
    !editingText && setValue(text);
  }, [text]);
  const onFooterClick = type => {
    if (type === 'add' && value !== areaText) {
      const submittedValue = value.trim();
      setAreaText(submittedValue);
      onNoteChanged({
        text: submittedValue,
        meta,
        id
      });
    } else if (type === 'cancel') {
      setValue(text);
    }
    setEditingText(false);
  };
  React.useEffect(() => {
    setIsEditingNoteInContext(editingText);
  }, [editingText]);
  React.useEffect(() => {
    var _a;
    if (isFirstRender) {
      return;
    }
    (_a = editingText ? editAreaRef.current : editButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  }, [editingText]);
  const editButton = jsx(DefaultButton, {
    disabled: isEditingNoteInContext,
    ref: editButtonRef,
    onClick: () => setEditingText(true),
    fill: "link",
    text: edit
  });
  const mappedMeta = meta.map((_metaItem, i) => {
    const pipe = i !== meta.length - 1 ? '|' : '';
    const dateAndMonth = formatDate(meta[0], locale, {
      day: 'numeric',
      month: 'short'
    });
    const time = formatDate(meta[0], locale, {
      timeStyle: 'short'
    });
    return jsx("p", {
      children: i === 0 ? jsxs(Fragment, {
        children: [dateAndMonth, "\u00A0\u00A0", pipe, "\u00A0\u00A0", time, "\u00A0\u00A0", pipe, "\u00A0\u00A0"]
      }) : jsxs(Fragment, {
        children: [" ", meta[1] === 'self' ? youWrote : meta[1], "\u00A0\u00A0 "]
      })
    }, i);
  });
  return jsxs("div", Object.assign({
    className: "kxi-text-area kxi-text-normal"
  }, debugService.componentProps, {
    children: [jsxs("div", {
      className: "kxi-note-meta kxi-text-small",
      children: [jsxs("div", {
        className: "kxi-note-meta-wrapper",
        children: [mappedMeta, " ", meta[1] === 'self' && !editingText ? editButton : null]
      }), meta[1] === 'self' && !editingText ? jsx("div", {
        className: "kxi-trash-wrapper",
        children: jsx(IconButton, {
          icon: "trash",
          onClick: () => onNoteDelete({
            text: value,
            meta,
            id
          })
        })
      }) : null]
    }), editingText ? jsx("textarea", {
      ref: editAreaRef,
      onChange: e => setValue(e.target.value),
      value: value,
      className: "kxi-text-area-wrap-text"
    }) : jsx("pre", {
      className: "kxi-note-text",
      children: areaText
    }), editingText ? jsx(NoteFooter, {
      disabled: isEditingNoteInContext ? shouldNoteDisable(value) : !!addDisabled,
      add: save,
      cancel: cancel,
      onClick: onFooterClick
    }) : null]
  }));
};
Note.displayName = 'Note';

// Most property comments are copied from `ExperienceCard.tsx`
class NotesProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Array)], NotesProps.prototype, "notesData", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], NotesProps.prototype, "isAddingNote", void 0);
__decorate([Bound(), __metadata("design:type", Object)], NotesProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], NotesProps.prototype, "hasSeparator", void 0);
class NotesEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], NotesEvents.prototype, "onNotesDelete", void 0);
__decorate([Bound(), __metadata("design:type", Function)], NotesEvents.prototype, "onNotesChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], NotesEvents.prototype, "onCancelAddingNote", void 0);
/** Stores whether any note in the current list of notes is being edited. */
const NotesContext = /*#__PURE__*/React.createContext([false, () => {}]);
/**
 * Internal use only: Determines whether or not the note is valid
 * @private
 */
const shouldNoteDisable = value => {
  return value.trim().length === 0;
};
/**
 * Description: A list of notes.
 */
const Notes = props => {
  const debugService = useDebugService(Notes, props);
  const {
    className,
    isAddingNote = false,
    hasSeparator = false,
    localization: localizationInput = {},
    notesData,
    observer: observerInput,
    onCancelAddingNote,
    onNotesChange,
    onNotesDelete
  } = props;
  const observer = observerInput;
  const localization = mergeObjects(localizationInput, notesLocalization);
  const [notes, setNotes] = React.useState(notesData !== null && notesData !== void 0 ? notesData : []);
  const [value, setValue] = React.useState('');
  const [isAddingDisabled] = useValueBackedState(shouldNoteDisable(value));
  const [isEditingNote, setIsEditingNote] = React.useState(false);
  const notesContext = React.useMemo(() => [isEditingNote, setIsEditingNote], [isEditingNote]); // referential stability
  const notesRef = React.useRef(null);
  const nextKeypressValid = React.useRef(true); // This ensures that a race condition does not exist between creating a new note and setting value
  React.useEffect(() => {
    setNotes(notesData !== null && notesData !== void 0 ? notesData : []);
  }, [notesData]);
  React.useEffect(() => {
    var _a;
    if (isAddingNote) {
      (_a = notesRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }, [isAddingNote]);
  const updateWithNewNote = () => {
    var _a;
    const temp = notes.slice();
    const newItem = {
      text: value.trim(),
      meta: [new Date(), 'self']
    };
    temp.push(newItem);
    if (observer) {
      observer.notesData = temp;
    }
    onNotesChange === null || onNotesChange === void 0 ? void 0 : onNotesChange({
      notes: temp,
      noteId: ''
    });
    setNotes(temp);
    (_a = notesRef === null || notesRef === void 0 ? void 0 : notesRef.current) === null || _a === void 0 ? void 0 : _a.focus(); // focus new note created after this one
  };
  const updateNotes = (obj, i) => {
    var _a;
    const newNotes = notes.slice();
    newNotes[i] = obj;
    if (observer) {
      observer.notesData = newNotes;
    }
    onNotesChange === null || onNotesChange === void 0 ? void 0 : onNotesChange({
      notes: newNotes,
      noteId: (_a = newNotes[i].id) !== null && _a !== void 0 ? _a : ''
    });
    setNotes(newNotes);
  };
  const deleteNotes = (_obj, i) => {
    var _a;
    const temp = notes.slice();
    const deletedObj = temp[i];
    temp.splice(i, 1);
    onNotesDelete === null || onNotesDelete === void 0 ? void 0 : onNotesDelete({
      notes: temp,
      noteId: (_a = deletedObj.id) !== null && _a !== void 0 ? _a : ''
    });
    setNotes(temp);
  };
  const handleButtonClick = type => {
    if (type === 'add') {
      if (isAddingDisabled) {
        return;
      }
      updateWithNewNote();
    } else {
      onCancelAddingNote === null || onCancelAddingNote === void 0 ? void 0 : onCancelAddingNote();
      if (observer) {
        observer.isAddingNote = false;
      }
    }
    setValue('');
  };
  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleButtonClick('add');
      nextKeypressValid.current = false;
    }
  };
  const mappedNotes = notes.map((note, i) => {
    var _a;
    return jsx(Note, {
      note: note,
      localization: localization,
      onNoteChanged: n => updateNotes(n, i),
      onNoteDelete: n => deleteNotes(n, i)
    }, (_a = note.id) !== null && _a !== void 0 ? _a : i);
  });
  const newNote = jsxs("div", {
    className: "kxi-new-note",
    "data-testid": "notes-new-note",
    children: [jsx("textarea", {
      ref: notesRef,
      value: value,
      placeholder: localization.placeholder,
      onChange: e => {
        if (nextKeypressValid.current) setValue(e.target.value);else nextKeypressValid.current = true;
      },
      onKeyPress: handleKeyPress,
      className: "kxi-text-area-wrap-text"
    }), jsx(NoteFooter, {
      add: localization.add,
      cancel: localization.cancel,
      disabled: isAddingDisabled,
      onClick: handleButtonClick
    })]
  });
  return jsxs("div", Object.assign({
    className: `kxi-notes-wrapper text-wrapper orca ${className}`
  }, debugService.componentProps, {
    children: [(notes.length > 0 || isAddingNote) && jsx("p", {
      className: classNames('kxi-notes-header-text', {
        'kxi-notes-has-separator': hasSeparator
      }),
      children: jsx("strong", {
        children: localization.notes
      })
    }), jsx(NotesContext.Provider, {
      value: notesContext,
      children: mappedNotes
    }), isAddingNote ? newNote : null]
  }));
};
Notes.displayName = 'Notes';

const useResponsiveIconButtons = ({
  kabobActiveClick,
  cardRef,
  iconButtonsWrapperRef,
  iconButtons,
  cardWidth,
  leftTitleAreaWidth,
  setLimitIconButtonOverride,
  setLimitedIconButtonNumber
}) => {
  React__default.useEffect(() => {
    var _a, _b, _c, _d, _e, _f;
    if (!(cardRef.current && iconButtonsWrapperRef.current && cardWidth && iconButtons.length && !kabobActiveClick)) {
      return;
    }
    const iconButtonsWithAlwaysDisplay = iconButtons.filter(i => i.alwaysDisplay).length;
    const iconButtonCount = Math.max(4, iconButtonsWithAlwaysDisplay);
    const cardStyles = window.getComputedStyle(cardRef.current);
    const cardPadding = parseFloat(cardStyles.paddingLeft) + parseFloat(cardStyles.paddingRight);
    const cardWithoutPadding = cardWidth - cardPadding;
    const iconWidth = (_c = (_b = (_a = iconButtonsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.clientWidth) !== null && _c !== void 0 ? _c : 0;
    const pipeWidth = (_f = (_e = (_d = iconButtonsWrapperRef.current) === null || _d === void 0 ? void 0 : _d.children.item(1)) === null || _e === void 0 ? void 0 : _e.clientWidth) !== null && _f !== void 0 ? _f : 0;
    const iconAndPipeWidth = iconWidth + pipeWidth;
    const maxIconButtonLength = iconButtons.length * iconAndPipeWidth;
    const sumOfInternals = leftTitleAreaWidth + maxIconButtonLength;
    const availableSpace = cardWithoutPadding - leftTitleAreaWidth;
    const numberOfIconsThatCanFit = availableSpace / iconAndPipeWidth;
    const limitIconButtonOverride = maxIconButtonLength < sumOfInternals;
    const limitedIconButtonNumber = Math.min(Math.floor(numberOfIconsThatCanFit - 1), iconButtonCount);
    setLimitIconButtonOverride(limitIconButtonOverride);
    setLimitedIconButtonNumber(limitedIconButtonNumber);
  }, [leftTitleAreaWidth, cardWidth, iconButtons, kabobActiveClick]);
};
const useHighlightText = (highlightedText, textContainer) => {
  const [highlightedCommentText, setHighlightedCommentText] = React__default.useState([]);
  const [rawText, setRawText] = React__default.useState('');
  const rawTextRef = React__default.useRef(null);
  React__default.useLayoutEffect(() => {
    var _a;
    setRawText(((_a = rawTextRef.current) === null || _a === void 0 ? void 0 : _a.innerText) || '');
  }, [textContainer, rawTextRef, highlightedText]);
  React__default.useEffect(() => {
    if (highlightedText) {
      const delimiter = uuidv4();
      let highlightedTextArr = !Array.isArray(highlightedText) ? [highlightedText.trim()] : [...highlightedText].map(x => x.trim());
      highlightedTextArr = highlightedTextArr.map(value => {
        return escapeRegex(value);
      });
      // look for any of the highlighted search words within the raw text
      const regex = new RegExp(`(${highlightedTextArr.join('|')})`, 'gi');
      const foundLetters = rawText.replace(regex, `${delimiter}$1${delimiter}`).split(delimiter);
      const mapped = foundLetters.map((item, i) => {
        if (highlightedTextArr.find(text => text.toLowerCase() === escapeRegex(item).toLowerCase())) {
          return jsx("mark", {
            children: item
          }, i);
        } else {
          return jsx(React__default.Fragment, {
            children: item
          }, i);
        }
      });
      setHighlightedCommentText(mapped);
    } else {
      setHighlightedCommentText([]);
    }
  }, [highlightedText, rawText]);
  return [rawTextRef, highlightedCommentText];
};

class CondensedExperienceCardProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Object)], CondensedExperienceCardProps.prototype, "comment", void 0);
__decorate([Bound(), __metadata("design:type", String)], CondensedExperienceCardProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], CondensedExperienceCardProps.prototype, "bannerSentiment", void 0);
__decorate([Bound(), __metadata("design:type", String)], CondensedExperienceCardProps.prototype, "caseState", void 0);
__decorate([Bound(), __metadata("design:type", String)], CondensedExperienceCardProps.prototype, "dueDateSentiment", void 0);
__decorate([Bound(), __metadata("design:type", String)], CondensedExperienceCardProps.prototype, "dueDateTitle", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CondensedExperienceCardProps.prototype, "dropdownDirection", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "hasAttachments", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CondensedExperienceCardProps.prototype, "iconButtons", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "isFavorite", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "isInteractable", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "isOverdue", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "isSelectable", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "selected", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "isTruncated", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CondensedExperienceCardProps.prototype, "kabobOptions", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CondensedExperienceCardProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Number)], CondensedExperienceCardProps.prototype, "newMessageCount", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CondensedExperienceCardProps.prototype, "meta", void 0);
__decorate([Bound(), __metadata("design:type", Number)], CondensedExperienceCardProps.prototype, "titleScore", void 0);
__decorate([Bound(), __metadata("design:type", String)], CondensedExperienceCardProps.prototype, "titleSentiment", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], CondensedExperienceCardProps.prototype, "dueDateTooltip", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CondensedExperienceCardProps.prototype, "highlightedText", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "hasNotes", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CondensedExperienceCardProps.prototype, "notesData", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "isFlagged", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CondensedExperienceCardProps.prototype, "showViewDetails", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CondensedExperienceCardProps.prototype, "metaIcon", void 0);
class CondensedExperienceCardEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], CondensedExperienceCardEvents.prototype, "onAttachmentsClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CondensedExperienceCardEvents.prototype, "onCardClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CondensedExperienceCardEvents.prototype, "onKabobChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CondensedExperienceCardEvents.prototype, "onSelectedChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CondensedExperienceCardEvents.prototype, "onNotesChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CondensedExperienceCardEvents.prototype, "onNotesDelete", void 0);
/**
 * Description: A more condensed version of the Experience Card.
 */
const CondensedExperienceCard = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a, _b, _c, _d, _e;
  const debugService = useDebugService(CondensedExperienceCard, props);
  const {
      bannerSentiment = 'neutral',
      caseState,
      className,
      comment,
      dropdownDirection,
      dueDateSentiment = 'neutral',
      dueDateTitle,
      dueDateTooltip,
      hasAttachments,
      hasNotes = false,
      highlightedText,
      iconButtons = [],
      isFavorite,
      isFlagged = false,
      isInteractable = true,
      isOverdue,
      isSelectable = true,
      isTruncated = false,
      kabobOptions,
      localization: localizationInput = {},
      meta,
      metaIcon: metaIconInput,
      newMessageCount = 0,
      notesData,
      observer: observerInput,
      selected = false,
      showViewDetails = true,
      title,
      titleScore,
      titleSentiment = 'neutral',
      onAttachmentsClick,
      onCardClick,
      onKabobChange,
      onNotesChange,
      onNotesDelete,
      onSelectedChange: onSelectedChangeInput
    } = props,
    ingredients = __rest(props, ["bannerSentiment", "caseState", "className", "comment", "dropdownDirection", "dueDateSentiment", "dueDateTitle", "dueDateTooltip", "hasAttachments", "hasNotes", "highlightedText", "iconButtons", "isFavorite", "isFlagged", "isInteractable", "isOverdue", "isSelectable", "isTruncated", "kabobOptions", "localization", "meta", "metaIcon", "newMessageCount", "notesData", "observer", "selected", "showViewDetails", "title", "titleScore", "titleSentiment", "onAttachmentsClick", "onCardClick", "onKabobChange", "onNotesChange", "onNotesDelete", "onSelectedChange"]);
  const observer = observerInput;
  const {
    dueDate,
    attachments,
    details,
    newMessage,
    notes: notesLocalization,
    additionalCount
  } = mergeObjects(localizationInput, condensedExperienceCardLocalization);
  const [checkedState, setCheckedState] = useValueBackedState(selected);
  const [isNotesOpen, setIsNotesOpen] = React.useState(false);
  const commentMarkup = React.useMemo(() => {
    let newCommentMarkup = comment.text;
    if (comment.tagAnnotations) {
      const spanStart = '<span class="kxi-text-shadow orca">';
      const spanEnd = '</span>';
      let modifications = 0;
      comment.tagAnnotations.slice().sort((a, b) => a.beginIndex - b.beginIndex).forEach(annotation => {
        const beginIndex = annotation.beginIndex + modifications * (spanStart.length + spanEnd.length);
        const endIndex = annotation.endIndex + modifications * (spanStart.length + spanEnd.length);
        const stringStart = newCommentMarkup.slice(0, beginIndex);
        const stringMiddle = newCommentMarkup.slice(beginIndex, endIndex);
        const stringEnd = newCommentMarkup.slice(endIndex, newCommentMarkup.length);
        newCommentMarkup = `${stringStart}${spanStart}${stringMiddle}${spanEnd}${stringEnd}`;
        modifications++;
      });
      if (!isTruncated && additionalCount) {
        newCommentMarkup += `&nbsp;&nbsp;<b>${additionalCount}</b>`;
      }
    }
    return newCommentMarkup;
  }, [additionalCount, comment.tagAnnotations, comment.text, isTruncated]);
  const displayNotes = isNotesOpen && (hasNotes || (notesData === null || notesData === void 0 ? void 0 : notesData.notes) && notesData.notes.length > 0);
  const caseIsOpen = caseState === 'open';
  const caseIsExpired = caseState === 'expired';
  const caseIsClosed = caseState === 'closed';
  // prettier-ignore
  const metaIcon = metaIconInput || metaIconInput === false ? metaIconInput : caseIsExpired ? 'clock_2' : caseIsOpen ? 'email' : 'email_open';
  const viewDetailsRef = React.useRef(null);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('CondensedExperienceCard', {
      comment,
      title
    });
  }, [comment, title]);
  const [rawTextRef, highlightedCommentText] = useHighlightText(highlightedText);
  const truncatedTextFade = (((_a = rawTextRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0) < (((_b = rawTextRef.current) === null || _b === void 0 ? void 0 : _b.scrollWidth) || 0);
  const [limitedIconButtonNumber, setLimitedIconButtonNumber] = React.useState(0);
  const [kabobActiveClick, setKabobActiveClick] = React.useState(false);
  const [limitIconButtonOverride, setLimitIconButtonOverride] = React.useState(false);
  const {
    width: cardWidth,
    ref: cardRef
  } = useResizeDetector();
  const leftTitleAreaRef = React.useRef(null);
  const iconButtonsWrapperRef = React.useRef(null);
  const leftTitleAreaWidth = (_d = (_c = leftTitleAreaRef.current) === null || _c === void 0 ? void 0 : _c.clientWidth) !== null && _d !== void 0 ? _d : 0;
  useResponsiveIconButtons({
    kabobActiveClick,
    iconButtons,
    cardRef,
    cardWidth,
    iconButtonsWrapperRef,
    leftTitleAreaWidth,
    setLimitIconButtonOverride,
    setLimitedIconButtonNumber
  });
  const getDueDate = () => {
    const sentiment = isOverdue ? 'negative' : dueDateSentiment;
    const inner = jsxs(Fragment, {
      children: [!caseIsClosed && !caseIsExpired && jsx(XIIcon, {
        icon: "clock",
        color: sentiment
      }), jsx("span", {
        className: `kxi-title kxi-text-600${kabobOptions ? ' kxi-kabob-menu' : ''} kxi-${sentiment}`,
        children: `${dueDateTitle}`
      })]
    });
    return dueDateTooltip ? jsx(ToolTipWrapper, Object.assign({}, dueDateTooltip, {
      children: inner
    })) : inner;
  };
  const handleNotesChange = eventData => {
    onNotesChange === null || onNotesChange === void 0 ? void 0 : onNotesChange(eventData);
    if (observer) {
      observer.notesData = Object.assign(Object.assign({}, notesData), {
        notes: eventData.notes
      });
    }
  };
  const newMessageText = `${newMessageCount} ${newMessage}`;
  const textClassName = `kxi-comment-area${isTruncated ? ` kxi-truncated${hasAttachments ? '' : ' kxi-truncated-details'}` : ''}`;
  const cardClassNames = classNames(!caseIsExpired && !caseIsClosed && isOverdue ? 'kxi-overdue' : caseState ? normalizeColor(caseState) : bannerSentiment ? normalizeColor(bannerSentiment) : '', {
    'kxi-color-accent': !caseIsClosed && (caseState || bannerSentiment)
  }, 'kxi-normal-contrast', 'kxi-condensed-experience-card', className);
  useWarnOnceIf(bannerSentiment === 'noSentiment' || bannerSentiment === undefined, debugService.format('The bannerSentiment options will be changing. The noSentiment option will be removed and changed to the undefined option. Note: the default will be changing to undefined in a future major version.'));
  const backgroundAreaClassNames = classNames('kxi-background-area', caseState ? `kxi-case-${caseState}` : '', {
    cx: isInteractable,
    'kxi-interactable': isInteractable,
    'kxi-neutral': !isInteractable,
    'kxi-with-kabob': kabobOptions,
    'kxi-truncated': isTruncated && !hasAttachments,
    'kxi-has-color-accent': !caseIsClosed && (caseState || bannerSentiment)
  });
  const viewDetailsElement = showViewDetails && jsx("div", {
    className: classNames('kxi-details', {
      'kxi-footer-area': !caseIsClosed,
      'kxi-truncated': isTruncated && !hasAttachments && !caseIsClosed
    }),
    ref: viewDetailsRef,
    children: jsx(DefaultButton, {
      fill: "link",
      text: details,
      className: "kxi-link-button",
      onClick: isInteractable ? undefined : onCardClick
    })
  });
  return jsx("div", Object.assign({}, debugService.componentProps, ingredients, {
    className: cardClassNames,
    ref: ref,
    onClick: isInteractable ? onCardClick : undefined,
    children: jsxs("div", {
      ref: cardRef,
      className: backgroundAreaClassNames,
      children: [jsxs("div", {
        ref: leftTitleAreaRef,
        className: "kxi-title-area",
        children: [isSelectable && jsx(Checkbox, {
          checked: checkedState,
          color: "CX",
          internalProps: {
            outerWrapper: {
              onClick: e => e.stopPropagation()
            }
          },
          onClick: e => e.stopPropagation(),
          onChange: event => {
            const newSelected = !selected;
            if (observer) {
              observer.selected = newSelected;
            }
            onSelectedChangeInput === null || onSelectedChangeInput === void 0 ? void 0 : onSelectedChangeInput({
              event
            });
            setCheckedState(newSelected);
          }
        }), isFlagged && jsx(XIIcon, {
          className: "kxi-header-icon",
          icon: "flag_filled",
          color: "yellow_light_15"
        }), caseIsClosed && jsx(XIIcon, {
          className: "kxi-header-icon",
          icon: "circle_check_filled",
          color: "green_primary"
        }), titleScore !== undefined && jsx("span", {
          className: `kxi-title-score kxi-title kxi-${titleSentiment}`,
          children: titleScore
        }), jsx("span", {
          className: titleScore ? undefined : `kxi-title kxi-text-600 kxi-${titleSentiment}`,
          children: title
        }), !caseIsClosed && !caseIsExpired && newMessageCount > 0 && jsxs(Fragment, {
          children: [jsx(XIIcon, {
            className: "kxi-new-message-icon",
            icon: "feedback",
            color: "CX"
          }), jsx("span", {
            className: "kxi-new-message-text cx",
            children: newMessageText
          })]
        }), caseIsClosed && meta && meta.length > 0 && jsxs(Fragment, {
          children: [jsx("span", {
            className: "kxi-text-600",
            children: "\u00A0|\u00A0"
          }), jsx(MetaMap, {
            meta: meta
          })]
        })]
      }), jsxs("div", {
        className: classNames(`kxi-title-area kxi-due-date kxi-${dueDateSentiment}`, {
          'kxi-title-area-has-quick-actions': iconButtons.length > 0
        }),
        children: [dueDateTitle && jsxs(Fragment, {
          children: [!caseIsClosed && !caseIsExpired && jsx("span", {
            className: "kxi-text-600",
            children: `${dueDate}: `
          }), getDueDate()]
        }), kabobOptions && jsx(Dropdown, {
          type: "icon",
          internalProps: {
            iconButton: {
              icon: 'kabob',
              onClick: e => e.stopPropagation()
            }
          },
          direction: dropdownDirection,
          color: "orca",
          data: kabobOptions,
          select: "none",
          onValueChange: onKabobChange
        }), iconButtons.length > 0 && jsx("div", {
          ref: iconButtonsWrapperRef,
          className: "kxi-title-area kxi-quick-actions kxi-icon-buttons-wrapper",
          children: jsx(IconButtons, {
            limitIconButtonOverride: limitIconButtonOverride,
            limitedIconButtonNumber: limitedIconButtonNumber,
            limitedIconButtonNumberFallback: 0,
            iconButtons: iconButtons,
            tooltipDirection: 6,
            dropdownDirection: dropdownDirection,
            isFavorite: isFavorite,
            setKabobActiveClick: setKabobActiveClick,
            // Notes
            isNotesOpen: isNotesOpen,
            setIsNotesOpen: setIsNotesOpen
          })
        }), caseIsClosed ? viewDetailsElement : null]
      }), !caseIsClosed && jsxs(Fragment, {
        children: [jsx("div", {
          className: classNames('kxi-meta kxi-text-small', {
            'kxi-expired-meta': caseIsExpired
          }),
          children: meta && meta.length > 0 && jsxs(Fragment, {
            children: [metaIcon ? jsx(XIIcon, {
              className: "kxi-metadata-icon",
              icon: metaIcon,
              color: "orca"
            }) : null, jsx(MetaMap, {
              meta: meta
            })]
          })
        }), jsxs("div", {
          className: textClassName,
          style: isTruncated ? {
            width: hasAttachments || !showViewDetails ? '100%' : `calc(100% - ${((_e = viewDetailsRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect().width) || 0}px - 6px)`,
            display: 'flex',
            marginBottom: 1
          } : {},
          children: [jsx("div", {
            ref: rawTextRef,
            className: `kxi-visible-text kxi-condensed-comment-text ${truncatedTextFade ? 'kxi-truncated-text-fade' : ''}`,
            style: {
              whiteSpace: isTruncated ? 'nowrap' : 'unset'
            },
            dangerouslySetInnerHTML: {
              __html: sanitizeHtmlString(commentMarkup)
            }
          }), additionalCount && isTruncated && jsx("div", {
            className: "kxi-condensed-addition-count",
            children: jsx("b", {
              children: additionalCount
            })
          })]
        }), jsx("div", {
          className: textClassName + ' kxi-comment-highlighted kxi-warning',
          children: highlightedCommentText
        }), hasAttachments && jsx("div", {
          className: "kxi-footer-area kxi-attachments",
          children: jsx(DefaultButton, {
            iconLeft: "attach",
            className: "kxi-disabled kxi-link-button",
            color: "orca",
            fill: "link",
            text: attachments,
            onClick: e => {
              e.stopPropagation();
              onAttachmentsClick === null || onAttachmentsClick === void 0 ? void 0 : onAttachmentsClick();
            }
          })
        })]
      }), displayNotes ? jsx(Notes, {
        hasSeparator: true,
        notesData: notesData === null || notesData === void 0 ? void 0 : notesData.notes,
        localization: Object.assign({}, notesLocalization),
        onNotesChange: handleNotesChange,
        onNotesDelete: onNotesDelete,
        isAddingNote: true,
        onCancelAddingNote: () => setIsNotesOpen(false)
      }) : null, caseIsClosed ? null : viewDetailsElement]
    })
  }));
});
CondensedExperienceCard.displayName = 'CondensedExperienceCard';

class ExperienceHeaderProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], ExperienceHeaderProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", Number)], ExperienceHeaderProps.prototype, "titleScore", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceHeaderProps.prototype, "sentiment", void 0);
__decorate([Bound(), __metadata("design:type", ButtonObject)], ExperienceHeaderProps.prototype, "viewDetailsButton", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ExperienceHeaderProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", ButtonObject)], ExperienceHeaderProps.prototype, "headerButton", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceHeaderProps.prototype, "selectable", void 0);
__decorate([Bound(), __metadata("design:type", Array)], ExperienceHeaderProps.prototype, "iconButtons", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceHeaderProps.prototype, "subtitleLogo", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceHeaderProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceHeaderProps.prototype, "isFavorite", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceHeaderProps.prototype, "isNotesOpen", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ExperienceHeaderProps.prototype, "dropdownDirection", void 0);
__decorate([Bound(), __metadata("design:type", Array)], ExperienceHeaderProps.prototype, "meta", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceHeaderProps.prototype, "canMarkValid", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceHeaderProps.prototype, "selected", void 0);
class ExperienceHeaderEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ExperienceHeaderEvents.prototype, "onMarkValidClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ExperienceHeaderEvents.prototype, "onSelectionChange", void 0);
/**
 * Description: The header for the experience card, to be used in areas where experience card styling is needed, but where the experience card does not fill that need.
 */
const ExperienceHeader = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ExperienceHeader, props);
  const {
      _setIsNotesOpen: setIsNotesOpen,
      canMarkValid = true,
      className,
      dropdownDirection,
      headerButton,
      iconButtons = [],
      isFavorite,
      isNotesOpen,
      localization,
      meta = [],
      observer,
      onMarkValidClick,
      onSelectionChange,
      selectable = true,
      selected,
      sentiment = '',
      subtitle,
      subtitleLogo,
      title,
      titleScore,
      viewDetailsButton
    } = props,
    ingredients = __rest(props, ["_setIsNotesOpen", "canMarkValid", "className", "dropdownDirection", "headerButton", "iconButtons", "isFavorite", "isNotesOpen", "localization", "meta", "observer", "onMarkValidClick", "onSelectionChange", "selectable", "selected", "sentiment", "subtitle", "subtitleLogo", "title", "titleScore", "viewDetailsButton"]);
  const {
    markValid
  } = mergeObjects(localization !== null && localization !== void 0 ? localization : {}, experienceHeaderLocalization);
  const hasHeader = headerButton === null || headerButton === void 0 ? void 0 : headerButton.text;
  const [limitedIconButtonNumber, setLimitedIconButtonNumber] = React.useState(0);
  const [kabobActiveClick, setKabobActiveClick] = React.useState(false);
  const [limitIconButtonOverride, setLimitIconButtonOverride] = React.useState(false);
  const [checkedState, setCheckedState] = useValueBackedState(!!selected);
  const {
    width: subtitleWidth,
    ref: subtitleRef
  } = useResizeDetector({
    handleHeight: false
  });
  const {
    width: cardWidth,
    ref: cardWidthRef
  } = useResizeDetector({
    handleHeight: false
  });
  React.useEffect(() => {
    // preventing a bug that changes icon number based on dropdown opening
    if (!kabobActiveClick) {
      if (cardWidth && subtitleWidth && iconButtons.length) {
        const iconButtonsWithAlwaysDisplay = iconButtons.filter(i => i.alwaysDisplay).length;
        const iconButtonCount = iconButtonsWithAlwaysDisplay <= 4 ? 4 : iconButtonsWithAlwaysDisplay;
        // all of these sizes correlate with our scss files
        const cardWithPadding = cardWidth - 48;
        const checkboxWidth = selectable ? 36 : 0;
        const logoWidth = subtitleWidth ? 30 : 0;
        // 36 + 5
        const iconAndPipeWidth = 41;
        const maxIconButtonLength = iconButtons.length * iconAndPipeWidth;
        const sumOfLeftSide = subtitleWidth + checkboxWidth + logoWidth;
        const sumOfInternals = sumOfLeftSide + maxIconButtonLength;
        const availableSpace = cardWithPadding - sumOfLeftSide;
        const numberOfIconsThatCanFit = availableSpace / iconAndPipeWidth;
        if (maxIconButtonLength < sumOfInternals) {
          setLimitIconButtonOverride(true);
        } else {
          setLimitIconButtonOverride(false);
        }
        setLimitedIconButtonNumber(Math.min(Math.floor(numberOfIconsThatCanFit - 1), iconButtonCount));
      }
    }
  }, [cardWidth]);
  const handleCheck = e => {
    const newIsChecked = !checkedState;
    if (observer) {
      observer.selected = !checkedState;
    }
    onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange({
      event: e
    });
    setCheckedState(newIsChecked);
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, ingredients, {
    className: classNames(className, `kxi-experience-header orca ${sentiment === 'invalid' ? 'kxi-invalid' : !sentiment ? 'kxi-no-sentiment' : ''}`),
    ref: mergeRefs([cardWidthRef, ref]),
    children: [jsxs("div", {
      className: `kxi-score-bar  ${!sentiment ? 'kxi-no-sentiment' : normalizeColor(sentiment)}`,
      children: [jsxs("div", {
        className: "kxi-score-bar-text",
        children: [jsx("h3", {
          className: "kxi-nps-score",
          children: titleScore
        }), " ", jsx("p", {
          className: "kxi-nps-title",
          children: title
        })]
      }), sentiment === 'invalid' ? jsxs("div", {
        className: "kxi-invalid-sentiment-actions",
        children: [canMarkValid && jsx(DefaultButton, {
          color: "CX",
          fill: "link",
          buttonSize: "normal",
          text: markValid,
          onClick: () => onMarkValidClick === null || onMarkValidClick === void 0 ? void 0 : onMarkValidClick(),
          style: {
            margin: '0 30px'
          }
        }), canMarkValid && (viewDetailsButton === null || viewDetailsButton === void 0 ? void 0 : viewDetailsButton.text) && jsx("span", {
          className: "kxi-pipe",
          children: "|"
        }), (viewDetailsButton === null || viewDetailsButton === void 0 ? void 0 : viewDetailsButton.text) ? jsx(DefaultButton, {
          style: {
            margin: '0 30px'
          },
          fill: "link",
          color: "CX",
          buttonSize: "normal",
          text: viewDetailsButton.text,
          onClick: viewDetailsButton.onClick
        }) : null]
      }) : hasHeader ? jsx("div", {
        className: "kxi-score-card-button",
        children: jsx(DefaultButton, Object.assign({
          style: {
            margin: '0 30px'
          },
          fill: "link",
          color: "CX",
          buttonSize: "normal"
        }, headerButton))
      }) : null]
    }), jsxs("div", {
      className: classNames('kxi-inner-wrapper', {
        'kxi-stacked-view': limitedIconButtonNumber <= 0 && limitIconButtonOverride
      }),
      children: [jsx("div", {
        className: "kxi-checkbox-wrapper",
        children: selectable ? jsx(Checkbox, {
          checked: checkedState,
          color: "CX",
          onChange: handleCheck
        }) : null
      }), jsx("div", {
        className: "kxi-text-wrapper",
        children: jsxs("p", {
          className: "kxi-experience-card-subtitle orca kxi-text-medium",
          children: [subtitleLogo ? jsx("span", {
            className: "kxi-logo-wrapper",
            children: jsx(XISVG, {
              svg: subtitleLogo
            })
          }) : null, jsx("span", {
            ref: subtitleRef,
            children: subtitle
          })]
        })
      }), jsx("p", {
        className: "kxi-meta kxi-text-small",
        children: jsx(MetaMap, {
          meta: meta
        })
      }), jsx("div", {
        className: "kxi-icon-buttons-wrapper",
        children: jsx(IconButtons, {
          limitIconButtonOverride: limitIconButtonOverride,
          limitedIconButtonNumber: limitedIconButtonNumber,
          iconButtons: iconButtons,
          dropdownDirection: dropdownDirection,
          isFavorite: isFavorite,
          isNotesOpen: isNotesOpen,
          setIsNotesOpen: setIsNotesOpen,
          setKabobActiveClick: setKabobActiveClick
        })
      })]
    })]
  }));
});
ExperienceHeader.displayName = 'ExperienceHeader';

class ExperienceCardProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceCardProps.prototype, "addTagVisible", void 0);
__decorate([Bound(), __metadata("design:type", Number)], ExperienceCardProps.prototype, "titleScore", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceCardProps.prototype, "sentiment", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ExperienceCardProps.prototype, "dropdownDirection", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceCardProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceCardProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceCardProps.prototype, "subtitleLogo", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceCardProps.prototype, "selectable", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceCardProps.prototype, "selected", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceCardProps.prototype, "isFavorite", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceCardProps.prototype, "collapsed", void 0);
__decorate([Bound(), __metadata("design:type", String)], ExperienceCardProps.prototype, "highlightedCommentId", void 0);
__decorate([Bound(), __metadata("design:type", Array)], ExperienceCardProps.prototype, "meta", void 0);
__decorate([Bound(), __metadata("design:type", Array)], ExperienceCardProps.prototype, "comments", void 0);
__decorate([Bound(), __metadata("design:type", Array)], ExperienceCardProps.prototype, "iconButtons", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ExperienceCardProps.prototype, "notesData", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceCardProps.prototype, "hasNotes", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ExperienceCardProps.prototype, "canMarkValid", void 0);
__decorate([Bound(), __metadata("design:type", ButtonObject)], ExperienceCardProps.prototype, "viewDetailsButton", void 0);
__decorate([Bound(), __metadata("design:type", ButtonObject)], ExperienceCardProps.prototype, "footerButton", void 0);
__decorate([Bound(), __metadata("design:type", ButtonObject)], ExperienceCardProps.prototype, "headerButton", void 0);
__decorate([Bound(), __metadata("design:type", Number)], ExperienceCardProps.prototype, "maxSuggestions", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ExperienceCardProps.prototype, "localization", void 0);
class ExperienceCardEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ExperienceCardEvents.prototype, "onSelectionChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ExperienceCardEvents.prototype, "onNotesChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ExperienceCardEvents.prototype, "onMoreClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ExperienceCardEvents.prototype, "onMarkValidClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ExperienceCardEvents.prototype, "onTagChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ExperienceCardEvents.prototype, "onNotesDelete", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ExperienceCardEvents.prototype, "onTagEvent", void 0);
/**
 * Description: Smart Experience Card - highly configurable for all your Experience needs.
 */
const ExperienceCard = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ExperienceCard, props);
  const {
      addTagVisible = true,
      canMarkValid,
      collapsed: collapsedInput,
      comments = [],
      dropdownDirection,
      footerButton,
      hasNotes = false,
      headerButton,
      highlightedCommentId,
      iconButtons = [],
      isFavorite,
      localization = {},
      maxSuggestions = 5,
      meta = [],
      notesData,
      observer: observerInput,
      onMarkValidClick,
      onMoreClick,
      onNotesChange,
      onNotesDelete,
      onSelectionChange,
      onTagChange,
      onTagEvent,
      selectable = true,
      selected,
      sentiment,
      subtitle,
      subtitleLogo,
      title,
      titleScore,
      viewDetailsButton
    } = props,
    ingredients = __rest(props, ["addTagVisible", "canMarkValid", "collapsed", "comments", "dropdownDirection", "footerButton", "hasNotes", "headerButton", "highlightedCommentId", "iconButtons", "isFavorite", "localization", "maxSuggestions", "meta", "notesData", "observer", "onMarkValidClick", "onMoreClick", "onNotesChange", "onNotesDelete", "onSelectionChange", "onTagChange", "onTagEvent", "selectable", "selected", "sentiment", "subtitle", "subtitleLogo", "title", "titleScore", "viewDetailsButton"]);
  /** Default to `true` if sentiment is invalid, else `false` */
  const collapsed = collapsedInput !== null && collapsedInput !== void 0 ? collapsedInput : sentiment === 'invalid';
  const observer = observerInput;
  const headerBtn = (footerButton === null || footerButton === void 0 ? void 0 : footerButton.text) ? Object.assign({}, footerButton) : (headerButton === null || headerButton === void 0 ? void 0 : headerButton.text) ? Object.assign({}, headerButton) : undefined;
  const [isAddingNote, setIsAddingNote] = React.useState(false);
  const displayNotes = hasNotes && isAddingNote || (notesData === null || notesData === void 0 ? void 0 : notesData.notes) && notesData.notes.length > 0;
  const [highlightedCommentIdState, setHighlightedCommentIdState] = React.useState('');
  React.useEffect(() => {
    if (footerButton === null || footerButton === void 0 ? void 0 : footerButton.text) {
      devOnlyWarn('The Kitchen: The footerButton will be deprecated in a later release. Please change your configuration to use `headerButton` for your Experience Card.');
      if (!(footerButton === null || footerButton === void 0 ? void 0 : footerButton.onClick)) {
        devOnlyWarn('The Kitchen: You did not pass an onClick event to the footerButton on the ExperienceCard. Was this intended?');
      }
    }
  }, [footerButton]);
  React.useEffect(() => {
    if ((headerButton === null || headerButton === void 0 ? void 0 : headerButton.text) && !(headerButton === null || headerButton === void 0 ? void 0 : headerButton.onClick)) {
      devOnlyWarn('The Kitchen: You did not pass an onClick event to the headerButton on the ExperienceCard. Was this intended?');
    }
  }, [headerButton]);
  React.useEffect(() => {
    if (!(viewDetailsButton === null || viewDetailsButton === void 0 ? void 0 : viewDetailsButton.onClick)) {
      devOnlyWarn('The Kitchen: You did not pass an onClick event to the viewDetailsButton on the ExperienceCard. Was this intended?');
    }
  }, [viewDetailsButton]);
  React.useEffect(() => {
    setHighlightedCommentIdState(highlightedCommentId || '');
  }, [highlightedCommentId]);
  const handleMoreMomentsClick = id => {
    if (id === highlightedCommentIdState) {
      setHighlightedCommentIdState(undefined);
      if (observer) observer.highlightedCommentId = undefined;
      if (onMoreClick) onMoreClick({
        commentId: undefined
      });
    } else {
      setHighlightedCommentIdState(id);
      if (observer) observer.highlightedCommentId = id;
      if (onMoreClick) onMoreClick({
        commentId: id
      });
    }
  };
  const handleNotesChange = eventData => {
    onNotesChange === null || onNotesChange === void 0 ? void 0 : onNotesChange(eventData);
    if (observer) {
      observer.notesData = Object.assign(Object.assign({}, notesData), {
        notes: eventData.notes
      });
    }
  };
  let showHighlightedState = false;
  let hasInitialPromptLabel = false;
  const mappedComments = comments.map((comment, i) => {
    if (comment.answerId === highlightedCommentIdState) showHighlightedState = true;
    const {
        onTagChange: onIndividualCommentTagChange
      } = comment,
      rest = __rest(comment, ["onTagChange"]);
    if (comment.promptLabel && i === 0) {
      hasInitialPromptLabel = true;
    }
    const handleTagChange_deprecated = (tags, tagId, answerId, selectedTagIds) => {
      const index = comments.findIndex(item => item.answerId === answerId);
      const tempArr = comments.slice();
      tempArr[index].tagAnnotations = tags;
      if (onIndividualCommentTagChange) onIndividualCommentTagChange({
        tags,
        tagId,
        answerId,
        selectedTagIds
      });
      if (observer) observer.comments = tempArr;
    };
    return jsx(Comment$1, Object.assign({
      _highlightedCommentId: highlightedCommentIdState,
      _internalOnTagChange_deprecated: handleTagChange_deprecated,
      _isOnExperienceCard: true,
      _setHighlightedCommentId: handleMoreMomentsClick,
      addTagVisible: addTagVisible,
      localization: localization,
      maxSuggestions: maxSuggestions,
      onTagChange: onTagChange,
      onTagEvent: onTagEvent
    }, rest), i);
  });
  const isHighlightedClass = showHighlightedState ? 'kxi-selected' : '';
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: `kxi-experience-card orca ${isHighlightedClass} ${collapsed ? 'collapsed' : ''} ${sentiment === 'invalid' ? 'kxi-invalid' : !sentiment ? 'kxi-no-sentiment' : ''}`
  }, ingredients, {
    ref: ref,
    children: [jsx(ExperienceHeader, {
      _setIsNotesOpen: setIsAddingNote,
      canMarkValid: canMarkValid,
      dropdownDirection: dropdownDirection,
      headerButton: headerBtn,
      iconButtons: iconButtons,
      isFavorite: isFavorite,
      isNotesOpen: isAddingNote,
      localization: localization,
      meta: meta,
      observer: observer,
      onMarkValidClick: onMarkValidClick,
      onSelectionChange: onSelectionChange,
      selectable: selectable,
      selected: selected,
      sentiment: sentiment,
      subtitle: subtitle,
      subtitleLogo: subtitleLogo,
      title: title,
      titleScore: titleScore,
      viewDetailsButton: viewDetailsButton
    }), hasInitialPromptLabel ? jsx("div", {
      className: "kxi-extra-header-padding"
    }) : null, jsx("div", {
      className: "kxi-card-inner-wrapper",
      children: collapsed ? null : jsxs(Fragment, {
        children: [jsx("div", {
          className: `${selectable ? 'kxi-comment-indent-left' : ''} kxi-comments-wrapper`,
          children: mappedComments
        }), displayNotes ? jsx(Notes, {
          hasSeparator: true,
          isAddingNote: isAddingNote,
          localization: localization.notes,
          notesData: notesData === null || notesData === void 0 ? void 0 : notesData.notes,
          onCancelAddingNote: () => setIsAddingNote(false),
          onNotesChange: handleNotesChange,
          onNotesDelete: onNotesDelete
        }) : null]
      })
    })]
  }));
});
ExperienceCard.displayName = 'ExperienceCard';

const _ExperienceCardIconButtonTypesOptions = ['dropdown', 'tooltip', 'favorite', 'notes'];
const _ExperienceCardSentimentsOptions = [..._SentimentsOptions, ..._InvalidOptions];
const _ExperienceCardLogosOptions = ['inmoment_favicon_blue', 'inmoment_favicon_orca', 'inmoment_favicon_purple', 'inmoment_favicon_teal'];
// Condensed Experience Card
const _ExperienceCardCasesOptions = ['open', 'inProgress', 'closed', 'expired'];
const _CondensedExperienceCardSentimentsOptions = [..._SentimentsOptions, 'noSentiment'];

const toTimeUnit = Object.freeze({
  days: 'day',
  hours: 'hour',
  minutes: 'minute',
  seconds: 'second',
  milliseconds: 'millisecond'
});
// `formatDuration()` depends on these units being ordered from largest to smallest.
const durationKeys = Object.freeze(['days', 'hours', 'minutes', 'seconds', 'milliseconds']);
const durationUnitRolloverValues = Object.freeze({
  days: Number.POSITIVE_INFINITY,
  hours: 24,
  minutes: 60,
  seconds: 60,
  milliseconds: 1000
});
function formatDurationBetweenNowAnd(...args) {
  return formatDurationBetween(new Date(), ...args);
}
function formatDurationBetween(baseDate, targetDate, locale, numberOfUnits) {
  const duration = getDurationBetween(baseDate, targetDate);
  const isCountdown = targetDate.getTime() - baseDate.getTime() > 0;
  return formatDuration(duration, locale, numberOfUnits, isCountdown);
}
function formatDuration(_duration, locale, numberOfUnits, roundUp = false) {
  var _a, _b, _c, _d, _e;
  const duration = {
    days: (_a = _duration.days) !== null && _a !== void 0 ? _a : 0,
    hours: (_b = _duration.hours) !== null && _b !== void 0 ? _b : 0,
    minutes: (_c = _duration.minutes) !== null && _c !== void 0 ? _c : 0,
    seconds: (_d = _duration.seconds) !== null && _d !== void 0 ? _d : 0,
    milliseconds: (_e = _duration.milliseconds) !== null && _e !== void 0 ? _e : 0
  };
  let primaryUnit;
  let secondaryUnit;
  function determineUnitsToDisplay() {
    // The larger (or only) unit to return in the formatted string.
    // E.g., if the formatted string shows days and hours ("2d 13h"), this would be "days".
    primaryUnit = duration.days > 0 ? 'days' : duration.hours > 0 ? 'hours' : 'minutes';
    // Either `primaryUnit` or the next-smaller unit.
    // E.g., if the formatted string shows days and hours ("2d 13h"), this would be "hours".
    //
    // We don't want to include any unit smaller than minutes in the formatted string.
    secondaryUnit = numberOfUnits === 1 ? primaryUnit : primaryUnit === 'days' ? 'hours' : 'minutes';
  }
  determineUnitsToDisplay();
  if (roundUp) {
    const secondaryUnitIndex = durationKeys.indexOf(secondaryUnit);
    const isUnshownTimeNonZero = durationKeys.slice(secondaryUnitIndex + 1).map(key => duration[key]).some(value => value > 0);
    if (isUnshownTimeNonZero) {
      duration[secondaryUnit]++;
      for (let i = secondaryUnitIndex; i > 0; i--) {
        const currKey = durationKeys[i];
        const preceedingKey = durationKeys[i - 1];
        if (duration[currKey] < durationUnitRolloverValues[currKey]) {
          break;
        }
        duration[currKey] = 0;
        duration[preceedingKey]++;
      }
      determineUnitsToDisplay(); // A unit with value 0 may now have value 1, so re-check which units to use.
    }
  }
  const formattedPrimaryUnit = formatTimeUnit(duration[primaryUnit], toTimeUnit[primaryUnit], locale);
  if (numberOfUnits === 1 || primaryUnit === secondaryUnit) {
    return formattedPrimaryUnit;
  }
  const formattedSecondaryUnit = formatTimeUnit(duration[secondaryUnit], toTimeUnit[secondaryUnit], locale);
  const listFormat = new Intl.ListFormat(locale, {
    type: 'unit',
    style: 'narrow'
  });
  return listFormat.format([formattedPrimaryUnit, formattedSecondaryUnit]);
}
function getDurationBetween(baseDate, targetDate) {
  let days, hours, minutes, seconds, milliseconds;
  milliseconds = Math.abs(targetDate.getTime() - baseDate.getTime());
  [seconds, milliseconds] = divmod(milliseconds, 1000);
  [minutes, seconds] = divmod(seconds, 60);
  [hours, minutes] = divmod(minutes, 60);
  [days, hours] = divmod(hours, 24); // eslint-disable-line prefer-const
  return {
    days,
    hours,
    minutes,
    seconds,
    milliseconds
  };
}
function divmod(n, m) {
  return [Math.floor(n / m), (n % m + m) % m];
}
function formatTimeUnit(amount, unit, locale) {
  const numberFormat = new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'narrow',
    maximumFractionDigits: 0,
    notation: 'compact'
  });
  return numberFormat.format(amount);
}

const largeNumberThreshold = 999;
const BigNumberDisplay = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const {
    bigNumber,
    color: bigNumberColor,
    fontSize,
    locale,
    localization = {},
    numberOfDurationUnits: numberOfDurationUnitsInput,
    showPercent
  } = props;
  const {
    noDataFound = 'No Data Found',
    noDataFoundSubtext = ''
  } = localization;
  // The minute value of the current time (e.g., 45 if the current time is 3:45)
  const lastRenderMinute = React__default.useRef();
  const forceRerender = useForceRerender();
  // If bigNumber is a Date, then the value displayed represents the amount of time until
  // or since the provided date.  We expect this to be used most commonly to show a countdown,
  // so we want to keep the rendered amount of time up-to-date as the current time changes.
  React__default.useEffect(() => {
    if (!(bigNumber instanceof Date)) {
      return;
    }
    const intervalId = setInterval(() => {
      const currentMinute = new Date().getMinutes();
      if (lastRenderMinute.current !== currentMinute) {
        lastRenderMinute.current = currentMinute;
        forceRerender();
      }
    }, 1000);
    return () => {
      clearInterval(intervalId);
      lastRenderMinute.current = undefined;
    };
  }, [bigNumber instanceof Date]);
  if (bigNumber == null) {
    return jsxs("div", {
      className: "kxi-big-number-no-data-wrapper",
      children: [jsx("p", {
        className: " kxi-big-number-no-data",
        children: noDataFound
      }, "no-data"), noDataFoundSubtext ? jsx("p", {
        className: "kxi-text-normal kxi-text-600",
        children: noDataFoundSubtext
      }, "no-data") : null]
    });
  }
  /*
    returns a (localized) shortened version of large numbers
    1,000 => 1k
    10,500 => 11k
  */
  const abbreviateNumber = num => {
    return Intl.NumberFormat(locale, {
      notation: 'compact'
    }).format(num);
  };
  const numberOfDurationUnits = clamp(Math.floor(numberOfDurationUnitsInput), 1, 2);
  const bigNumberText = bigNumber instanceof Date ? formatDurationBetweenNowAnd(bigNumber, locale, numberOfDurationUnits) : typeof bigNumber === 'object' && bigNumber != null ? formatDuration(bigNumber, locale, numberOfDurationUnits) : bigNumber === null || bigNumber === void 0 ? void 0 : bigNumber.toLocaleString(locale);
  const bigNumberColorClass = normalizeColor(bigNumberColor);
  const renderedText = typeof bigNumber !== 'object' && bigNumber > largeNumberThreshold ? abbreviateNumber(bigNumber) : bigNumberText;
  return jsxs("h1", {
    ref: ref,
    className: bigNumberColorClass,
    style: {
      fontSize: `${fontSize / 16}rem`
    },
    children: [renderedText, showPercent ? '%' : '']
  });
});

const ContextDatum = ({
  children,
  componentId,
  context,
  contextDataAlignment,
  contextNumberIsBig,
  iconElementRef: iconElementRefInput,
  locale,
  nameElementRef: nameElementRefInput,
  numberElementRef: numberElementRefInput,
  rowIndex,
  showContextTrendArrows,
  stackedContext
}) => {
  var _a, _b, _c, _d, _e, _f, _g;
  const nameElementRef = React__default.useRef(null);
  const iconElementRef = React__default.useRef(null);
  const numberElementRef = React__default.useRef(null);
  const {
    ref: nameResizeRef
  } = useResizeDetector();
  const icon = showContextTrendArrows ? jsx(XIIcon, {
    icon: context.icon,
    color: context.color,
    iconSize: contextNumberIsBig ? 30 : 24,
    ref: mergeRefs([iconElementRef, iconElementRefInput])
  }) : null;
  const contextNum = jsxs("p", {
    className: `${normalizeColor(context.color || 'orca')} ${contextNumberIsBig ? 'kxi-big-number-large-context' : ''}`,
    style: stackedContext && contextDataAlignment === 'center' ? {} : {
      paddingRight: 18
    },
    ref: mergeRefs([numberElementRef, numberElementRefInput]),
    children: [(_a = context.contextNumber) === null || _a === void 0 ? void 0 : _a.toLocaleString(locale), context.isPercentage ? '%' : '']
  });
  const nameBounding = (_b = nameElementRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
  const iconBounding = (_c = iconElementRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
  const parentBounding = (_f = (_e = (_d = nameElementRef.current) === null || _d === void 0 ? void 0 : _d.parentElement) === null || _e === void 0 ? void 0 : _e.parentElement) === null || _f === void 0 ? void 0 : _f.getBoundingClientRect();
  const numBounding = (_g = numberElementRef.current) === null || _g === void 0 ? void 0 : _g.getBoundingClientRect();
  // We need to align the items to a grid, but the tooltip because it wraps the items
  // prevents us from doing so, so we create another invisible div on top of the items
  // to wrap with the tooltip to create the illusion of a wrapped grid.
  return jsxs(React__default.Fragment, {
    children: [jsx("div", {
      ref: nameResizeRef,
      style: {
        display: 'flex',
        justifyContent: 'flex-end'
      },
      children: jsx("p", Object.assign({
        className: classNames('kxi-big-number-context-heading', 'kxi-gray', {
          'kxi-big-number-context-heading-stacked': stackedContext,
          'kxi-big-number-context-heading-stacked-center': stackedContext && contextDataAlignment === 'center'
        }),
        ref: mergeRefs([nameElementRef, nameElementRefInput])
      }, context.tooltip ? {
        'aria-describedby': `kxi-bigNumber${componentId}${rowIndex}`
      } : {}, {
        children: context.heading
      }))
    }), contextDataAlignment === 'center' && !stackedContext ? icon : undefined, jsx("div", {
      style: {
        display: 'flex',
        justifyContent: contextDataAlignment === 'center' ? stackedContext ? 'center' : 'flex-start' : 'flex-end',
        alignItems: 'center'
      },
      children: contextDataAlignment === 'center' && !stackedContext ? contextNum : jsxs(Fragment, {
        children: [context.icon ? icon : null, contextNum]
      })
    }), context.tooltip && parentBounding && nameBounding && numBounding ? jsx("div", {
      style: {
        position: 'absolute',
        left: Math.min(nameBounding.left - parentBounding.left, iconBounding ? iconBounding.left - parentBounding.left : Infinity,
        // Use Infinity so Math.min never uses this value
        numBounding.left - parentBounding.left),
        right: Math.min(parentBounding.right - numBounding.right, iconBounding ? parentBounding.right - iconBounding.right : Infinity,
        // Use Infinity so Math.min never uses this value
        parentBounding.right - nameBounding.right),
        top: Math.min(nameBounding.top - parentBounding.top, iconBounding ? iconBounding.top - parentBounding.top : Infinity,
        // Use Infinity so Math.min never uses this value
        numBounding.top - parentBounding.top),
        bottom: Math.min(parentBounding.bottom - numBounding.bottom, iconBounding ? parentBounding.bottom - iconBounding.bottom : Infinity,
        // Use Infinity so Math.min never uses this value
        parentBounding.bottom - nameBounding.bottom)
      },
      children: jsx(ToolTipWrapper, Object.assign({
        enabled: context.tooltip !== undefined
      }, context.tooltip, {
        stretch: true,
        style: {
          height: '100%'
        },
        innerStyle: {
          height: '100%'
        },
        id: `kxi-bigNumber${componentId}${rowIndex}`,
        children: children
      }))
    }) : null]
  }, rowIndex);
};

const CONTEXT_DATA_MAX_LENGTH = 4;
const ContextData = ({
  children,
  containerlessView,
  contextData: contextDataInput = [],
  contextDataAlignment,
  contextNumberSize,
  locale,
  showContextTrendArrows,
  tileRef,
  width,
  wrapperRef
}) => {
  const contextNumberIsBig = contextNumberSize === 'big';
  const stackedInflectionPoint = React__default.useRef(0);
  const contextDataRef = React__default.useRef(null);
  const isOverflown = el => {
    if (!el) {
      return false;
    }
    const {
      clientWidth,
      scrollWidth
    } = el;
    return scrollWidth > clientWidth;
  };
  const {
    ref: contextResizeRef,
    width: contextWrapperWidth = 0
  } = useResizeDetector({
    handleHeight: false
  });
  const [contextData, setContextData] = React__default.useState([]);
  const [contextNumberWidth] = React__default.useState(0);
  const [componentId] = React__default.useState(uuidv4());
  const [stackedContext, setStackedContext] = React__default.useReducer((_prevState, newState) => {
    var _a;
    const dataWidth = ((_a = contextDataRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) || 0;
    if (newState) {
      if (dataWidth > stackedInflectionPoint.current) {
        stackedInflectionPoint.current = dataWidth;
      }
      return true;
    } else {
      if (stackedInflectionPoint.current >= dataWidth) {
        return true;
      } else {
        return false;
      }
    }
  }, false);
  const contextElementArrRef = React__default.useRef([]);
  React__default.useLayoutEffect(() => {
    if (contextDataInput.length > 0) {
      setContextData(contextDataInput.map(item => {
        var _a;
        return Object.assign(Object.assign({}, item), {
          id: (_a = item.id) !== null && _a !== void 0 ? _a : uuidv4()
        });
      }));
    } else if (contextData.length > 0) {
      //prevent new empty arrays from causing a component rerender
      setContextData([]);
    }
  }, [contextDataInput]);
  React__default.useLayoutEffect(() => {
    stackedInflectionPoint.current = 0;
  }, [contextData, contextNumberIsBig, contextDataAlignment]);
  React__default.useLayoutEffect(() => {
    var _a, _b;
    let largestWidth = 0;
    (_a = contextElementArrRef.current) === null || _a === void 0 ? void 0 : _a.forEach(val => {
      var _a;
      const bboxWidth = ((_a = val === null || val === void 0 ? void 0 : val.contextNumElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) || 0;
      if (bboxWidth > largestWidth) largestWidth = bboxWidth;
    });
    if (contextDataRef.current && (largestWidth / (((_b = contextDataRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width) || 1) > (contextDataAlignment === 'center' ? 0.5 : 0.65) || isOverflown(contextDataRef.current) || tileRef.current && !containerlessView && isOverflown(tileRef.current) || wrapperRef.current && containerlessView && isOverflown(wrapperRef.current))) {
      setStackedContext(true);
    } else {
      setStackedContext(false);
    }
  }, [width, stackedContext, contextNumberIsBig, contextDataAlignment]);
  React__default.useEffect(() => {
    if (contextData.length > CONTEXT_DATA_MAX_LENGTH) {
      devOnlyWarn(`The Kitchen: You have passed in more than the maximum number of contexts to the BigNumber component.`, `Only ${CONTEXT_DATA_MAX_LENGTH} will be displayed.`);
    }
  }, [contextData]);
  const isContextGroupStacked = () => {
    /*
      if the context number width (with icon) is greater than 55% of the context wrapper width,
      stack the groups instead of having them on rows;
      Stacked class is on the whole wrapper because if one row should be stacked, stack all the rows
    */
    return contextNumberWidth / contextWrapperWidth > 0.55;
  };
  const getContextColumns = () => {
    if (contextDataAlignment === 'center') {
      return `1fr ${showContextTrendArrows ? contextNumberIsBig ? '30px' : '24px' : ''} 1fr`;
    } else {
      return `1fr auto`;
    }
  };
  const mappedContextData = contextData.slice(0, CONTEXT_DATA_MAX_LENGTH).map((context, j) => jsx(ContextDatum, {
    children: children,
    componentId: componentId,
    context: context,
    contextDataAlignment: contextDataAlignment,
    contextNumberIsBig: contextNumberIsBig,
    locale: locale,
    rowIndex: j,
    showContextTrendArrows: showContextTrendArrows,
    stackedContext: stackedContext,
    iconElementRef: ref => {
      if (!contextElementArrRef.current[j]) {
        contextElementArrRef.current[j] = {};
      }
      contextElementArrRef.current[j].contextIconElement = ref;
    },
    nameElementRef: ref => {
      if (!contextElementArrRef.current[j]) {
        contextElementArrRef.current[j] = {};
      }
      contextElementArrRef.current[j].contextNameElement = ref;
    },
    numberElementRef: ref => {
      if (!contextElementArrRef.current[j]) {
        contextElementArrRef.current[j] = {};
      }
      contextElementArrRef.current[j].contextNumElement = ref;
    }
  }, j));
  return contextData.length > 0 ? jsx("div", {
    ref: mergeRefs([contextDataRef, contextResizeRef]),
    className: `kxi-big-number-context-data-wrapper kxi-grey kxi-big-number-context-align-${contextDataAlignment} ${isContextGroupStacked() ? 'kxi-big-number-stacked-context' : ''}`,
    style: {
      display: stackedContext ? 'flex' : 'grid',
      gridTemplateColumns: getContextColumns(),
      flexDirection: 'column',
      paddingRight: stackedContext && contextDataAlignment === 'center' ? 18 : 0,
      position: 'relative',
      alignItems: 'center'
    },
    children: mappedContextData
  }, "context-data") : null;
};

const MIN_BIG_NUMBER_FONT_SIZE = 32;
const Chart = props => {
  const {
    bigNumber,
    bigNumberAlignment,
    bigNumberFontSize: bigNumberFontSizeInput,
    children,
    colors,
    contextData,
    contextDataAlignment,
    showContextTrendArrows,
    contextNumberSize,
    contextText,
    locale,
    localization,
    numberOfDurationUnits,
    showPercent,
    tileRef,
    tooltip
  } = props;
  const {
    bigNumber: bigNumberColor = 'orca'
  } = colors !== null && colors !== void 0 ? colors : {};
  const bigNumberFontSizeNumberInput = bigNumberFontSizeInput === 'big' ? 96 : 64;
  const [bigNumberFontSize, setBigNumberFontSize] = useValueBackedState(bigNumberFontSizeNumberInput);
  const {
    ref: wrapperResizeRef,
    width = 0,
    height = 0
  } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 0
  });
  const wrapperRef = React__default.useRef(null);
  const displayRef = React__default.useRef(null);
  /**
   * Finds the largest possible font size matching {@link bigNumberFontSizeInput}
   * that still fits all the content.
   */
  React__default.useEffect(() => {
    const wrapperElement = wrapperRef.current;
    const displayElement = displayRef.current;
    if (!wrapperElement || !displayElement) {
      return;
    }
    const findFittingFontSize = fontSize => {
      if (fontSize === MIN_BIG_NUMBER_FONT_SIZE) {
        // Give up
        return fontSize;
      }
      // Check if it fits
      displayElement.style.fontSize = `${fontSize}px`;
      if (wrapperElement.scrollHeight <= wrapperElement.offsetHeight) {
        // It fits!
        return fontSize;
      }
      // Try the next one
      return findFittingFontSize(fontSize - 1);
    };
    const originalFontSize = displayElement.style.fontSize;
    setBigNumberFontSize(findFittingFontSize(bigNumberFontSizeNumberInput));
    displayElement.style.fontSize = originalFontSize;
  }, [bigNumber, bigNumberFontSizeNumberInput, height, setBigNumberFontSize]);
  const getClassList = () => {
    const getComponentSize = () => {
      if (width > 768) {
        return 'large';
      } else if (width > 336) {
        return 'normal';
      } else {
        return 'small';
      }
    };
    return classNames('kxi-big-number-wrapper', 'kxi-stretch', `kxi-big-number-${getComponentSize()}`);
  };
  return jsx("div", {
    ref: mergeRefs([wrapperRef, wrapperResizeRef]),
    className: getClassList(),
    children: jsxs("div", {
      className: classNames('kxi-big-number-main', `kxi-big-number-${bigNumberFontSizeInput}-font`),
      children: [jsxs("div", {
        className: `kxi-big-number-metric-wrapper kxi-big-number-align-${bigNumberAlignment}`,
        children: [jsxs(ToolTipWrapper, Object.assign({
          enabled: tooltip !== undefined
        }, tooltip, {
          children: [children, jsx(BigNumberDisplay, {
            ref: displayRef,
            bigNumber: bigNumber,
            color: bigNumberColor,
            fontSize: bigNumberFontSize,
            locale: locale,
            localization: localization,
            numberOfDurationUnits: numberOfDurationUnits,
            showPercent: showPercent
          })]
        })), contextText && jsx("p", {
          className: "kxi-big-number-context-text kxi-grey",
          children: contextText
        }, "context-text")]
      }), jsx(ContextData, {
        children: children,
        contextData: contextData,
        contextDataAlignment: contextDataAlignment,
        contextNumberSize: contextNumberSize,
        locale: locale,
        showContextTrendArrows: showContextTrendArrows,
        tileRef: tileRef,
        width: width,
        wrapperRef: wrapperRef
      })]
    })
  });
};

const Links = props => {
  const {
    links
  } = props;
  return links.length > 0 ? jsx("div", {
    className: "kxi-big-number-links",
    children: links.map((link, i) => jsx(Link, Object.assign({}, link, {
      showPipe: i !== links.length - 1
    }), i))
  }) : null;
};
const Link = props => {
  const {
    text,
    href,
    showPipe
  } = props;
  return jsxs("span", {
    children: [jsx("a", {
      className: "cx kxi-text-normal",
      href: href,
      target: "_blank",
      rel: "noreferrer",
      children: text
    }), showPipe ? ' | ' : '']
  });
};

class BigNumberProps extends BasicTileProps {}
__decorate([Bound(), __metadata("design:type", Object)], BigNumberProps.prototype, "bigNumber", void 0);
__decorate([Bound(), __metadata("design:type", String)], BigNumberProps.prototype, "bigNumberAlignment", void 0);
__decorate([Bound(), __metadata("design:type", String)], BigNumberProps.prototype, "bigNumberFontSize", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BigNumberProps.prototype, "colors", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BigNumberProps.prototype, "containerlessView", void 0);
__decorate([Bound(), __metadata("design:type", Array)], BigNumberProps.prototype, "contextData", void 0);
__decorate([Bound(), __metadata("design:type", String)], BigNumberProps.prototype, "contextDataAlignment", void 0);
__decorate([Bound(), __metadata("design:type", String)], BigNumberProps.prototype, "contextNumberSize", void 0);
__decorate([Bound(), __metadata("design:type", String)], BigNumberProps.prototype, "contextText", void 0);
__decorate([Bound(), __metadata("design:type", Array)], BigNumberProps.prototype, "links", void 0);
__decorate([Bound(), __metadata("design:type", String)], BigNumberProps.prototype, "locale", void 0);
__decorate([Bound(), __metadata("design:type", Object)], BigNumberProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Number)], BigNumberProps.prototype, "numberOfDurationUnits", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BigNumberProps.prototype, "selected", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BigNumberProps.prototype, "showContextTrendArrows", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BigNumberProps.prototype, "showPercent", void 0);
__decorate([Bound(), __metadata("design:type", String)], BigNumberProps.prototype, "titleIcon", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], BigNumberProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], BigNumberProps.prototype, "useCondensedPadding", void 0);
class BigNumberEvents extends BasicTileEvents {}
__decorate([Bound(), __metadata("design:type", Function)], BigNumberEvents.prototype, "onBigNumberPress", void 0);
/**
 * Description: Component displaying various numbers in a big fashion. Card onClick event will only fire if isInteractive is true.
 */
const BigNumber = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a, _b;
  const debugService = useDebugService(BigNumber, props);
  const {
      bigNumber,
      bigNumberAlignment,
      bigNumberFontSize,
      children,
      className,
      colors,
      containerlessView: containerlessView_deprecated = false,
      contextData,
      contextDataAlignment = 'center',
      contextNumberSize = 'big',
      contextText,
      hasBorder = false,
      hasTopBar: hasTopBarInput,
      isInteractive = false,
      isSelectable = true,
      isSelected: isSelectedInput,
      links = [],
      locale = 'en-US',
      localization,
      numberOfDurationUnits = 2,
      onBasicTilePress,
      onBigNumberPress,
      selected: selectedInput_deprecated,
      semiboldTitle = true,
      showContextTrendArrows = true,
      showPercent = false,
      stretch = true,
      stretchSlot = true,
      titleColor,
      titleIcon,
      titleIconProps,
      titleSeparator = true,
      tooltip,
      useCondensedPadding,
      visualState
    } = props,
    ingredients = __rest(props, ["bigNumber", "bigNumberAlignment", "bigNumberFontSize", "children", "className", "colors", "containerlessView", "contextData", "contextDataAlignment", "contextNumberSize", "contextText", "hasBorder", "hasTopBar", "isInteractive", "isSelectable", "isSelected", "links", "locale", "localization", "numberOfDurationUnits", "onBasicTilePress", "onBigNumberPress", "selected", "semiboldTitle", "showContextTrendArrows", "showPercent", "stretch", "stretchSlot", "titleColor", "titleIcon", "titleIconProps", "titleSeparator", "tooltip", "useCondensedPadding", "visualState"]);
  const hasTopBar = hasTopBarInput !== null && hasTopBarInput !== void 0 ? hasTopBarInput : !containerlessView_deprecated;
  useWarnIsDeprecated('BigNumber', {
    containerlessView: {
      value: props.containerlessView,
      message: 'containerlessView will now behave as if it is always `false`.'
    },
    selected: {
      value: props.selected,
      message: 'Use `isSelected` instead.'
    },
    titleIcon: {
      value: props.titleIcon,
      message: 'Use `titleIconProps.icon` instead.'
    },
    useCondensedPadding: {
      value: props.useCondensedPadding,
      message: 'Use `visualState` instead.'
    }
  }, 'prop');
  useWarnIsDeprecated('BigNumber', {
    'colors.title': {
      value: (_a = props.colors) === null || _a === void 0 ? void 0 : _a.title,
      message: 'Use `titleColor` instead.'
    },
    'colors.titleIcon': {
      value: (_b = props.colors) === null || _b === void 0 ? void 0 : _b.titleIcon,
      message: 'Use `titleIconProps.color` instead.'
    }
  }, 'prop');
  const tileRef = React.useRef(null);
  return jsx("div", Object.assign({}, debugService.componentProps, {
    style: {
      display: 'contents'
    },
    children: jsxs(BasicTile, Object.assign({
      ref: mergeRefs([ref, tileRef])
    }, ingredients, {
      className: classNames(className, 'kxi-big-number-container'),
      hasBorder: hasBorder,
      hasTopBar: hasTopBar,
      isInteractive: isInteractive,
      isSelectable: isSelectable,
      isSelected: isSelectedInput !== null && isSelectedInput !== void 0 ? isSelectedInput : selectedInput_deprecated,
      onBasicTilePress: onBasicTilePress !== null && onBasicTilePress !== void 0 ? onBasicTilePress : onBigNumberPress,
      semiboldTitle: semiboldTitle,
      stretch: stretch,
      stretchSlot: stretchSlot,
      titleColor: titleColor !== null && titleColor !== void 0 ? titleColor : colors === null || colors === void 0 ? void 0 : colors.title,
      titleIconProps: titleIconProps !== null && titleIconProps !== void 0 ? titleIconProps : titleIcon ? {
        icon: titleIcon,
        color: colors === null || colors === void 0 ? void 0 : colors.titleIcon
      } : undefined,
      titleSeparator: titleSeparator,
      visualState: visualState !== null && visualState !== void 0 ? visualState : useCondensedPadding !== undefined ? useCondensedPadding ? 'condensed' : 'normal' : 'auto',
      children: [jsxs("div", {
        slot: "header",
        children: [jsx(Links, {
          links: links
        }), jsx(Slot, {
          name: "header",
          reactChildren: children
        })]
      }), jsx(Slot, {
        name: "widgets",
        slot: "action",
        reactChildren: children
      }), jsx(Chart, {
        bigNumber: bigNumber,
        bigNumberAlignment: bigNumberAlignment,
        bigNumberFontSize: bigNumberFontSize,
        colors: colors,
        contextData: contextData,
        contextDataAlignment: contextDataAlignment,
        showContextTrendArrows: showContextTrendArrows,
        contextNumberSize: contextNumberSize,
        contextText: contextText,
        locale: locale,
        localization: localization,
        numberOfDurationUnits: numberOfDurationUnits,
        showPercent: showPercent,
        tileRef: tileRef,
        tooltip: tooltip,
        children: children
      })]
    }))
  }));
});
BigNumber.displayName = 'BigNumber';

const HighchartsAngularStyle = 'width: 100%; height: 100%; display: block; overflow: visible;';
const HighchartsDefaultTheme = {
  chart: {
    reflow: true,
    events: {
      load: function (event) {
        setTimeout(() => {
          event.target.reflow();
        }, 100);
      }
    }
  },
  credits: {
    enabled: false
  },
  legend: {
    itemStyle: {
      color: XIColors.orca.primary,
      fontWeight: '400',
      fontFamily: 'IBM Plex Sans',
      fontSize: '12px'
    },
    itemHoverStyle: {
      color: XIColors.orca.light[30]
    },
    itemHiddenStyle: {
      color: XIColors.grey.light[60]
    },
    title: {
      style: {
        color: XIColors.orca.light[30],
        fontWeight: '400',
        fontFamily: 'IBM Plex Sans',
        fontSize: '12px'
      },
      text: ''
    },
    align: 'left'
  },
  title: false,
  subtitle: false,
  xAxis: [{
    labels: {
      style: {
        color: XIColors.orca.light[30],
        fontWeight: '400',
        fontFamily: 'IBM Plex Sans',
        fontSize: '12px'
      },
      align: 'center'
    },
    gridLineDashStyle: 'Dash',
    tickLength: 8,
    gridLineColor: XIColors.grey.light[90],
    gridLineWidth: 1,
    lineColor: XIColors.grey.light[90],
    title: {
      text: '',
      style: {
        color: XIColors.orca.light[30],
        fontWeight: '400',
        fontFamily: 'IBM Plex Sans',
        fontSize: '14px'
      }
    }
  }],
  yAxis: [{
    labels: {
      style: {
        color: XIColors.orca.light[30],
        fontSize: '12px',
        fontWeight: '400',
        fontFamily: 'IBM Plex Sans'
      },
      align: 'center'
    },
    gridLineDashStyle: 'Solid',
    tickLength: 8,
    gridLineColor: XIColors.grey.light[90],
    gridLineWidth: 1,
    title: {
      style: {
        color: XIColors.orca.light[30],
        fontWeight: '400',
        fontFamily: 'IBM Plex Sans',
        fontSize: '14px'
      }
    }
  }],
  tooltip: {
    backgroundColor: XIColorUsage.silver,
    borderRadius: 6,
    borderColor: XIColors.grey.light[80],
    borderWidth: 1,
    padding: 8,
    shadow: false,
    shape: 'callout',
    style: {
      color: XIColors.orca.primary,
      fontWeight: '400',
      fontFamily: 'IBM Plex Sans',
      fontSize: '14px'
    }
  }
};
const HighchartsStyleMerge = (...args) => {
  if (!Array.isArray(args)) return args;
  const recursiveMerge = (base, override) => {
    const output = base;
    if (override === null) return null;
    Object.keys(override).forEach(element => {
      if (Array.isArray(override[element])) {
        if (typeof base[element] === 'object') {
          output[element] = recursiveMerge(base[element], override[element]);
        } else {
          output[element] = [...(base[element] || []), ...override[element]];
        }
      } else if (typeof override[element] === 'object') {
        output[element] = recursiveMerge(base[element] || {}, override[element]);
      } else {
        output[element] = override[element];
      }
    });
    return output;
  };
  const output = {};
  args.reduce((prev, curr) => {
    return recursiveMerge(prev, curr);
  }, output);
  return output;
};

class ChartWrapperProps extends BasicTileProps {}
__decorate([Bound(), __metadata("design:type", Boolean)], ChartWrapperProps.prototype, "containerView", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ChartWrapperProps.prototype, "hasSeparatorBar", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ChartWrapperProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ChartWrapperProps.prototype, "minChartHeight", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ChartWrapperProps.prototype, "useCondensedPadding", void 0);
class ChartWrapperEvents extends BasicTileEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ChartWrapperEvents.prototype, "onChartWrapperPress", void 0);
/**
 * Description: This component wraps a Highchart chart component (not included) to provide standard looking charts
 */
const ChartWrapper = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ChartWrapper, props);
  const {
      children,
      className,
      containerView = false,
      hasBorder = false,
      hasSeparatorBar,
      hasTopBar: hasTopBarInput,
      isInteractive = false,
      localization = {},
      minChartHeight,
      minContentHeight,
      onBasicTilePress,
      onChartWrapperPress,
      semiboldTitle = true,
      stretch = true,
      stretchSlot = true,
      titleSeparator,
      useCondensedPadding = false,
      visualState
    } = props,
    ingredients = __rest(props, ["children", "className", "containerView", "hasBorder", "hasSeparatorBar", "hasTopBar", "isInteractive", "localization", "minChartHeight", "minContentHeight", "onBasicTilePress", "onChartWrapperPress", "semiboldTitle", "stretch", "stretchSlot", "titleSeparator", "useCondensedPadding", "visualState"]);
  const hasTopBar = hasTopBarInput !== null && hasTopBarInput !== void 0 ? hasTopBarInput : containerView;
  useWarnIsDeprecated('ChartWrapper', {
    useCondensedPadding: {
      value: props.useCondensedPadding,
      message: 'Use `visualState` instead.'
    },
    containerView: {
      value: props.containerView,
      message: 'The component will act as if this prop is always set to `true`.'
    },
    hasSeparatorBar: {
      value: props.hasSeparatorBar,
      message: 'Use `titleSeparator` instead.'
    },
    minChartHeight: {
      value: props.minChartHeight,
      message: 'Use `minContentHeight` instead.'
    }
  }, 'prop');
  useWarnOnceIf(props.useCondensedPadding !== undefined && props.visualState !== undefined, debugService.format('Both `useCondensedPadding` and `visualState` were provided. `visualState` will be used.'));
  useWarnOnceIf(props.hasSeparatorBar !== undefined && props.titleSeparator !== undefined, debugService.format('Both `hasSeparatorBar` and `titleSeparator` were provided. `titleSeparator` will be used.'));
  useWarnOnceIf(props.minChartHeight !== undefined && props.minContentHeight !== undefined, debugService.format('Both `minChartHeight` and `minContentHeight` were provided. `minContentHeight` will be used.'));
  const {
    noDataFound = 'No Data Found',
    noDataFoundSubtext = 'No Data Found Subtext'
  } = localization;
  const errorMessage = jsxs("div", {
    className: "kxi-chart-wrapper-no-data-wrapper",
    children: [jsx("p", {
      className: " kxi-chart-wrapper-no-data",
      children: noDataFound
    }), noDataFoundSubtext ? jsx("p", {
      className: "kxi-text-normal kxi-chart-wrapper-no-data-subtext",
      children: noDataFoundSubtext
    }) : null]
  });
  return jsx("div", Object.assign({}, debugService.componentProps, {
    style: {
      display: 'contents'
    },
    children: jsxs(BasicTile, Object.assign({}, ingredients, {
      ref: ref,
      className: classNames(className, 'kxi-chart-wrapper'),
      hasBorder: hasBorder,
      hasTopBar: hasTopBar,
      isInteractive: isInteractive,
      minContentHeight: minContentHeight !== null && minContentHeight !== void 0 ? minContentHeight : minChartHeight,
      onBasicTilePress: onBasicTilePress !== null && onBasicTilePress !== void 0 ? onBasicTilePress : onChartWrapperPress,
      semiboldTitle: semiboldTitle,
      stretch: stretch,
      stretchSlot: stretchSlot,
      titleSeparator: titleSeparator !== null && titleSeparator !== void 0 ? titleSeparator : hasSeparatorBar,
      visualState: visualState !== null && visualState !== void 0 ? visualState : useCondensedPadding ? 'condensed' : 'normal',
      children: [jsx(Slot, {
        name: "header",
        reactChildren: children,
        slot: "header"
      }), jsx(Slot, {
        name: "filter",
        reactChildren: children,
        slot: "action"
      }), jsx(Slot, {
        name: "chart",
        reactChildren: children,
        slot: "children",
        children: errorMessage
      })]
    }))
  }));
});
ChartWrapper.displayName = 'ChartWrapper';

var CHART_TYPE;
(function (CHART_TYPE) {
  CHART_TYPE["LINE"] = "line";
  CHART_TYPE["BAR"] = "bar";
  CHART_TYPE["COLUMN"] = "column";
})(CHART_TYPE || (CHART_TYPE = {}));
var CHART_FONT_SIZES;
(function (CHART_FONT_SIZES) {
  CHART_FONT_SIZES["SMALL"] = "8px";
  CHART_FONT_SIZES["DEFAULT"] = "11px";
  CHART_FONT_SIZES["MEDIUM"] = "13px";
  CHART_FONT_SIZES["LARGE"] = "15px";
  CHART_FONT_SIZES["EXTRA_LARGE"] = "17px";
})(CHART_FONT_SIZES || (CHART_FONT_SIZES = {}));
const _BigNumberSizesOptions = ['small', 'big'];
const _BigNumberAlignmentsOptions = ['center', 'right'];
const _BigNumberColorsOptions = [..._ColorsOptions, ..._SentimentsOptions, ..._ErrorStatesOptions, ..._StatusStatesOptions];

class AppSideDisplayProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], AppSideDisplayProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", String)], AppSideDisplayProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", String)], AppSideDisplayProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], AppSideDisplayProps.prototype, "title", void 0);
class AppSideDisplayEvents extends DivHTMLEvents {}
/**
 * Description: Shows a large blue box with an icon, placed on the App Pages. Size is it's parent's container.
 */
const AppSideDisplay = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(AppSideDisplay, props);
  const {
      icon,
      className,
      color = 'CX',
      title,
      text,
      children
    } = props,
    ingredients = __rest(props, ["icon", "className", "color", "title", "text", "children"]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('AppSideDisplay', {
      icon
    });
  }, [icon]);
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: `${normalizeColor(color)} kxi-gradient ${className || ''} kxi-app-side-display-clear-container`
  }, ingredients, {
    ref: ref,
    children: [jsxs("div", {
      className: "kxi-app-side-display-content-container",
      children: [jsx("div", {
        style: {
          marginBottom: 24,
          width: 72,
          height: 72
        },
        children: jsx(XIAppIcon, {
          icon: icon
        })
      }), title && jsx("h2", {
        children: title
      }), text && jsx("div", {
        className: "kxi-app-side-display-body-text",
        children: jsx("p", {
          children: text
        })
      })]
    }), jsx("div", {
      className: "kxi-app-side-display-button-wrapper",
      children: children
    })]
  }));
});
AppSideDisplay.displayName = 'AppSideDisplay';

function getSlot(name, children, slotProps) {
  return jsx(Slot, Object.assign({}, slotProps, {
    name: name,
    reactChildren: children
  }));
}

class DrawerProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], DrawerProps.prototype, "size", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DrawerProps.prototype, "open", void 0);
__decorate([Bound(), __metadata("design:type", String)], DrawerProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DrawerProps.prototype, "silverBg", void 0);
__decorate([Bound(), __metadata("design:type", String)], DrawerProps.prototype, "closeButtonIcon", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], DrawerProps.prototype, "interceptClose", void 0);
class DrawerEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], DrawerEvents.prototype, "onClose", void 0);
const openAnimationDurationMs = 100;
const closeAnimationDurationMs = 500;
/**
 * Description: Generic drawer component that animates out from the right side of the screen, place whatever you want inside!
 */
const Drawer = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(Drawer, props);
  const {
      children,
      color = 'CX',
      size = 'large',
      open = false,
      silverBg = false,
      observer,
      closeButtonIcon = 'cancel',
      interceptClose = false,
      onClose
    } = props,
    ingredients = __rest(props, ["children", "color", "size", "open", "silverBg", "observer", "closeButtonIcon", "interceptClose", "onClose"]);
  const [dialogElement, setDialogElement] = React.useState(null);
  const {
    ref: focusTrapRef
  } = useFocusTrap();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSliderOpen, setIsSliderOpen] = React.useState(false);
  React.useEffect(() => {
    const animationTimeoutId = (() => {
      if (open) {
        setIsOpen(true);
        return setTimeout(() => {
          setIsSliderOpen(true);
        }, openAnimationDurationMs);
      } else {
        setIsSliderOpen(false);
        return setTimeout(() => {
          setIsOpen(false);
        }, closeAnimationDurationMs);
      }
    })();
    return () => {
      clearTimeout(animationTimeoutId);
    };
  }, [open]);
  useFocusFirstTabbableElement(isSliderOpen ? dialogElement : null);
  const handleClose = () => {
    if (interceptClose) {
      onClose === null || onClose === void 0 ? void 0 : onClose();
    } else {
      setIsSliderOpen(false);
      setTimeout(() => {
        setIsOpen(false);
        if (observer) {
          observer.open = false;
        }
        onClose === null || onClose === void 0 ? void 0 : onClose();
      }, closeAnimationDurationMs);
    }
  };
  return /*#__PURE__*/createPortal(jsx("div", Object.assign({}, debugService.componentProps, {
    role: "dialog",
    "aria-hidden": !isOpen,
    "data-testid": "drawer-wrapper",
    className: `kxi-drawer-wrapper kxi-grey-overlay ${isOpen ? 'open' : ''}`,
    ref: mergeRefs([setDialogElement, focusTrapRef]),
    children: jsxs("div", Object.assign({
      "data-testid": "drawer",
      className: `kxi-drawer kxi-${size} ${isSliderOpen ? 'open' : ''} ${silverBg ? 'kxi-drawer-silver-background' : ''}`
    }, ingredients, {
      ref: ref,
      children: [jsxs("div", {
        "data-testid": "drawer-header",
        className: `kxi-drawer-header ${normalizeColor(color)}`,
        children: [jsx(IconButton, {
          className: "kxi-drawer-close-button",
          icon: closeButtonIcon,
          onClick: handleClose
        }), jsx("div", {
          className: "kxi-drawer-title",
          children: getSlot('title', children)
        }), jsx("div", {
          className: "kxi-drawer-menu",
          children: getSlot('menu', children)
        })]
      }), jsx("div", {
        className: "kxi-drawer-body",
        children: getSlot('body', children)
      }), jsx("div", {
        className: "kxi-drawer-footer",
        children: getSlot('footer', children)
      })]
    }))
  })), document.body);
});
Drawer.displayName = 'Drawer';

class SidePanelProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Boolean)], SidePanelProps.prototype, "open", void 0);
__decorate([Bound(), __metadata("design:type", String)], SidePanelProps.prototype, "closeButtonIcon", void 0);
__decorate([Bound(), __metadata("design:type", String)], SidePanelProps.prototype, "openButtonIcon", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SidePanelProps.prototype, "interceptClose", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], SidePanelProps.prototype, "interceptOpen", void 0);
__decorate([Bound(), __metadata("design:type", Number)], SidePanelProps.prototype, "panelWidth", void 0);
__decorate([Bound(), __metadata("design:type", Number)], SidePanelProps.prototype, "closedPanelWidth", void 0);
class SidePanelEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], SidePanelEvents.prototype, "onClose", void 0);
__decorate([Bound(), __metadata("design:type", Function)], SidePanelEvents.prototype, "onOpen", void 0);
/**
 * Description: Left side panel that allows you to control the internal components!
 */
const SidePanel = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const debugService = useDebugService(SidePanel, props);
  const {
      children,
      open = false,
      observer,
      closeButtonIcon = 'single_arrow_left',
      openButtonIcon = 'single_arrow_right',
      interceptClose = false,
      interceptOpen = false,
      panelWidth = 300,
      closedPanelWidth = 50,
      onClose,
      onOpen
    } = props,
    ingredients = __rest(props, ["children", "open", "observer", "closeButtonIcon", "openButtonIcon", "interceptClose", "interceptOpen", "panelWidth", "closedPanelWidth", "onClose", "onOpen"]);
  const [isOpen, setIsOpen] = useValueBackedState(open);
  const screenType = useScreenType();
  const leftPanelWidth = (() => {
    if (screenType === 'mobile') {
      return isOpen ? '100%' : 0;
    } else {
      return isOpen ? panelWidth : closedPanelWidth;
    }
  })();
  return jsxs(Fragment, {
    children: [jsxs("div", Object.assign({}, debugService.componentProps, {
      "data-testid": includeTestId('side-panel-wrapper'),
      className: `kxi-side-panel-wrapper ${isOpen ? 'open' : ''}`,
      children: [jsxs("div", Object.assign({
        "data-testid": includeTestId('side-panel'),
        className: "kxi-side-panel"
      }, ingredients, {
        ref: ref,
        style: {
          minWidth: leftPanelWidth,
          width: leftPanelWidth
        },
        children: [jsx("div", {
          "data-testid": includeTestId('panel-header'),
          className: "kxi-side-panel-header",
          style: {
            minWidth: closedPanelWidth
          },
          children: jsx(IconButton, {
            className: "kxi-side-panel-toggle-button",
            icon: isOpen ? closeButtonIcon : openButtonIcon,
            onClick: () => {
              if (isOpen) {
                onClose === null || onClose === void 0 ? void 0 : onClose();
                if (!interceptClose) {
                  setIsOpen(false);
                  if (observer) {
                    observer.open = false;
                  }
                }
              } else {
                onOpen === null || onOpen === void 0 ? void 0 : onOpen();
                if (!interceptOpen) {
                  setIsOpen(true);
                  if (observer) {
                    observer.open = true;
                  }
                }
              }
            }
          })
        }), getSlot('side-panel', children)]
      })), jsx("div", {
        className: "kxi-side-panel-main-content",
        children: getSlot('main-content', children)
      })]
    })), jsx("div", {
      className: "kxi-grey-overlay kxi-side-panel-overlay"
    })]
  });
});
SidePanel.displayName = 'SidePanel';

const _DrawerSizesOptions = ['small', 'large'];

class LiHTMLAttributes extends HTMLAttributes {}
class LiHTMLEvents extends DOMEvents {}

class ListItemProps extends ObservablePropsMixin(LiHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Object)], ListItemProps.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", String)], ListItemProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListItemProps.prototype, "checked", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListItemProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", String)], ListItemProps.prototype, "select", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListItemProps.prototype, "hasDragging", void 0);
__decorate([Bound(), __metadata("design:type", String)], ListItemProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], ListItemProps.prototype, "tooltip", void 0);
class ListItemEvents extends LiHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ListItemEvents.prototype, "onItemClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ListItemEvents.prototype, "onCheckedChange", void 0);
/**
 * Description: For use inside of the List component, this contains an individual item.
 */
const ListItem = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ListItem, props);
  const {
      children: childrenInput,
      contrast = 'normal',
      disabled = false,
      value,
      select = 'none',
      hasDragging = false,
      color = 'CX',
      checked: checkedInput = false,
      onItemClick,
      _isIndeterminate: isIndeterminate,
      onCheckedChange: onCheckedChangeInput,
      observer,
      _canBeDeselected: canBeDeselected = true,
      tooltip,
      className
    } = props,
    ingredients = __rest(props, ["children", "contrast", "disabled", "value", "select", "hasDragging", "color", "checked", "onItemClick", "_isIndeterminate", "onCheckedChange", "observer", "_canBeDeselected", "tooltip", "className"]);
  const isMulti = ['multi', 'multiple', 'multiSelectAll'].includes(select);
  const [isChecked, setIsChecked] = useComputedState(() => checkedInput, [checkedInput, isIndeterminate]);
  const onCheckedChange = newIsChecked => {
    if (!newIsChecked && !canBeDeselected) {
      return;
    }
    setIsChecked(newIsChecked);
    onCheckedChangeInput === null || onCheckedChangeInput === void 0 ? void 0 : onCheckedChangeInput({
      checked: newIsChecked
    });
    if (observer) {
      observer.checked = newIsChecked;
    }
  };
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('ListItem', {
      value
    });
  }, [value]);
  const handleOnClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (select !== 'none') {
      onCheckedChange(!isChecked);
    }
    onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick({
      event: e,
      value
    });
  };
  const handleCheckKeyPress = e => {
    if (disabled) {
      return;
    }
    if (e.key === 'Enter') {
      onCheckedChange(!isChecked);
    }
  };
  const handleActivation = ifActivation(handleOnClick, {
    onlyIfFocused: true,
    disabled
  });
  const handleNavigation = makeEventHandler(e => ['Home', 'End', 'ArrowUp', 'ArrowDown'].includes(e.key), e => {
    const el = e.currentTarget;
    const listItems = Array.from(el.closest('.kxi-list-wrapper').querySelectorAll('.kxi-list-item'));
    const currentItemIndex = listItems.indexOf(el);
    const indexToFocus = {
      Home: 0,
      End: listItems.length - 1,
      ArrowUp: currentItemIndex - 1,
      ArrowDown: currentItemIndex + 1
    }[e.key];
    if (0 <= indexToFocus && indexToFocus < listItems.length) {
      listItems[indexToFocus].focus();
    }
  }, {
    onlyIfFocused: true
  });
  const listItem = /*#__PURE__*/createElement("li", Object.assign({}, debugService.componentProps, ingredients, {
    ref: ref,
    className: classNames('kxi-list-item', className, normalizeColor(color), {
      'kxi-high-contrast': contrast === 'high',
      'kxi-list-item-draggable': hasDragging,
      'kxi-list-item-multi': isMulti,
      'kxi-list-item-active': isChecked && !isMulti && !disabled,
      'kxi-list-item-disabled': disabled,
      'kxi-list-item-none': select === 'none'
    }),
    key: value,
    value: value,
    onClick: e => {
      if (!disabled) {
        handleOnClick(e);
      }
    },
    onKeyDown: e => {
      handleActivation(e);
      handleNavigation(e);
    },
    "data-testid": "listItem",
    "data-is-checked": isChecked,
    tabIndex: 0
  }), hasDragging && jsx("div", {
    className: "kxi-list-item-drag-handle-wrapper",
    children: jsx(XIIcon, {
      contrast: contrast,
      icon: "drag_handle",
      color: disabled ? 'disabled' : 'orca'
    })
  }), isMulti && jsx("div", {
    className: "kxi-list-item-checkbox-wrapper",
    children: jsx(Checkbox, {
      contrast: contrast,
      checked: isChecked,
      isIndeterminate: isIndeterminate,
      color: color,
      onKeyPress: handleCheckKeyPress,
      _externallyControlledState: true
    })
  }), jsx("div", {
    className: `kxi-list-item-children-wrapper ${disabled ? 'disabled' : ''}`,
    children: jsx(Slot, {
      name: "children",
      reactChildren: childrenInput
    })
  }), !isMulti && isChecked && jsx("div", {
    className: "kxi-list-item-checkmark-wrapper",
    children: jsx(XIIcon, {
      contrast: contrast,
      icon: "circle_check_filled",
      color: color
    })
  }));
  return tooltip ? jsx(ToolTipWrapper, Object.assign({}, tooltip, {
    innerStyle: {
      width: '100%'
    },
    style: {
      width: '100%'
    },
    children: listItem
  })) : listItem;
});
ListItem.displayName = 'ListItem';

function useLazyLoadedVerticalList(props) {
  const {
    listElement,
    isFetchingData = false,
    endOfScrollOffset = 0,
    onEndOfScroll
  } = props;
  const isAtEndOfScrollRef = React__default.useRef(false);
  const listItemsRef = React__default.useRef([]);
  const onListChange = () => {
    if (!listElement || isFetchingData || !onEndOfScroll) {
      return;
    }
    const cannotScroll = !canScroll(listElement);
    const isAtEndOfScroll = getIsAtEndOfScroll(listElement, listItemsRef.current, endOfScrollOffset);
    if (cannotScroll || isAtEndOfScroll) {
      if (!isAtEndOfScrollRef.current) {
        onEndOfScroll === null || onEndOfScroll === void 0 ? void 0 : onEndOfScroll();
        isAtEndOfScrollRef.current = true;
      }
    } else {
      isAtEndOfScrollRef.current = false;
    }
  };
  /**
   * This has to be debounced or the delays in our rendering may cause `onEndOfScroll` to fire prematurely.
   */
  const onListChangeDebounced = useDebouncedCallback(onListChange);
  // When list items change
  useOnChildNodesChange(listElement, nodes => {
    isAtEndOfScrollRef.current = false;
    listItemsRef.current = nodes.filter(el => el instanceof Element);
    onListChangeDebounced();
  });
  // When list size changes
  useResizeObserver(listElement, onListChangeDebounced);
  return {
    onListScroll: onListChangeDebounced
  };
}
function getElementHeight(el) {
  return el instanceof HTMLElement ? el.offsetHeight : el.clientHeight;
}
function canScroll(target) {
  const targetHeight = getElementHeight(target);
  return target.scrollHeight > targetHeight;
}
/**
 * Don't subtract `1` from `listItems.length' or you will introduce an off-by-one bug.
 */
function getIsAtEndOfScroll(list, listItems, endOfScrollOffset) {
  const length = listItems.length;
  const endOfScrollIndex = length > 0 ? clamp(length - endOfScrollOffset, 0, length) : 0;
  const offsetHeight = sum(listItems.slice(endOfScrollIndex).map(getElementHeight));
  return list.scrollTop >= list.scrollHeight - getElementHeight(list) - offsetHeight;
}

const isFirefox = () => {
  return /firefox/gim.test(navigator.userAgent);
};

class ListProps extends ObservablePropsMixin(UListHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], ListProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], ListProps.prototype, "select", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListProps.prototype, "hasBorder", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListProps.prototype, "hasSearch", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListProps.prototype, "hasDragging", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListProps.prototype, "hasZebraStriping", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ListProps.prototype, "isFetchingData", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ListProps.prototype, "maxHeight", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ListProps.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", String)], ListProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ListProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Number)], ListProps.prototype, "endOfScrollOffset", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ListProps.prototype, "internalProps", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ListProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", String)], ListProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", String)], ListProps.prototype, "size", void 0);
class ListEvents extends UListHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ListEvents.prototype, "onSelectionChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ListEvents.prototype, "onEndOfScroll", void 0);
/**
 * Description: Displays a list of items in a pretty way.
 */
const List = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a;
  const debugService = useDebugService(List, props);
  const [defaultValue] = React.useState([]);
  const {
      children: childrenInput,
      className,
      color,
      contrast = 'normal',
      hasDragging = false,
      hasBorder = true,
      hasSearch = false,
      hasZebraStriping = false,
      isFetchingData = false,
      endOfScrollOffset = 0,
      onEndOfScroll,
      maxHeight = 'auto',
      internalProps,
      label,
      size = 'large',
      localization,
      observer,
      onSelectionChange: onSelectionChangeInput,
      select = 'none',
      status,
      value = defaultValue
    } = props,
    ingredients = __rest(props, ["children", "className", "color", "contrast", "hasDragging", "hasBorder", "hasSearch", "hasZebraStriping", "isFetchingData", "endOfScrollOffset", "onEndOfScroll", "maxHeight", "internalProps", "label", "size", "localization", "observer", "onSelectionChange", "select", "status", "value"]);
  const children = childrenInput;
  const {
    noResultsFound = 'No Results Found',
    selectAll = 'Select All'
  } = localization !== null && localization !== void 0 ? localization : {};
  useWarnOnceIf(props.size === undefined, debugService.format(['"size" was not provided.', 'This will default to "large", but in the next major version release, the default will change to "normal".', 'Explicitly set "size" to "large" to silence this warning.'].join(' ')));
  useWarnIsDeprecated('List', {
    clearSearch: {
      value: 'clearSearch',
      message: 'The clearSearch button is no longer shown with the No Results Found in the List search.'
    }
  }, 'prop');
  const arrayValue = React.useMemo(() => Array.isArray(value) ? value : [value], [value]);
  const [activeValues, setActiveValues] = useValueBackedState(arrayValue);
  const [searchValue, setSearchValue] = useValueBackedState((_a = internalProps === null || internalProps === void 0 ? void 0 : internalProps.searchBox.value) !== null && _a !== void 0 ? _a : '');
  const isMulti = ['multi', 'multiple', 'multiSelectAll'].includes(select);
  const listRef = React.useRef(null);
  const listItemsWrapperRef = React.useRef(null);
  const reactListItemElementsRef = React.useRef([]);
  const [xiListItemElements, setXiListItemElements] = React.useState([]);
  const allSlottedValues = xiListItemElements.map(li => li.value);
  const firstSelectedItemRef = React.useRef(null);
  const activeValuesRef = useRefToValue(activeValues);
  const onActiveValuesChange = React.useCallback(newActiveValues => {
    setActiveValues(newActiveValues);
    const newValue = isMulti ? newActiveValues : newActiveValues.length > 0 ? newActiveValues[0] : undefined;
    onSelectionChangeInput === null || onSelectionChangeInput === void 0 ? void 0 : onSelectionChangeInput({
      selectedItems: newValue
    });
    if (observer) {
      observer.value = newValue;
    }
  }, [isMulti, observer, onSelectionChangeInput, setActiveValues]);
  const onSelectionChange = React.useCallback((value, isChecked) => {
    if (select === 'none') {
      return;
    }
    const newActiveValues = (() => {
      const activeValuesSet = new Set(activeValuesRef.current);
      isChecked ? activeValuesSet.add(value) : activeValuesSet.delete(value);
      const activeValues = Array.from(activeValuesSet);
      return isMulti ? activeValues : activeValues.length > 0 ? [activeValues[activeValues.length - 1]] : [];
    })();
    onActiveValuesChange(newActiveValues);
  }, [activeValuesRef, isMulti, onActiveValuesChange, select]);
  const {
    mergedChildren,
    allValues: allReactValues
  } = (() => {
    const allValues = [];
    const mergedChildren = React.Children.map(children, (child, i) => {
      if (! /*#__PURE__*/React.isValidElement(child)) {
        return child;
      }
      const childProps = child.props;
      const reactValue = childProps.value;
      allValues.push(reactValue);
      const props = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, childProps), {
        checked: arrayValue.length > 0 ? arrayValue.includes(reactValue) : childProps.checked,
        contrast,
        select,
        onCheckedChange: eventData => {
          var _a;
          (_a = childProps.onCheckedChange) === null || _a === void 0 ? void 0 : _a.call(childProps, eventData);
          onSelectionChange(value, eventData.checked);
        }
      }), color && {
        color
      }), hasDragging !== undefined && {
        hasDragging
      }), {
        ref: el => {
          reactListItemElementsRef.current[i] = el;
        }
      });
      return /*#__PURE__*/React.cloneElement(child, props);
    });
    return {
      mergedChildren,
      allValues
    };
  })();
  React.useEffect(() => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    let newFirstSelectedItem;
    if (hasChildren(children)) {
      if (arrayValue.length > 0) {
        newFirstSelectedItem = (_a = reactListItemElementsRef.current.find(item => arrayValue.includes(item === null || item === void 0 ? void 0 : item.id))) !== null && _a !== void 0 ? _a : null;
      } else {
        newFirstSelectedItem = (_b = reactListItemElementsRef.current.find(item => (item === null || item === void 0 ? void 0 : item.getAttribute('data-is-checked')) === 'true')) !== null && _b !== void 0 ? _b : null;
      }
    } else {
      newFirstSelectedItem = (_c = xiListItemElements.find(item => {
        var _a;
        return arrayValue.includes((_a = item.children[0]) === null || _a === void 0 ? void 0 : _a.getAttribute('value'));
      })) !== null && _c !== void 0 ? _c : null;
    }
    if (newFirstSelectedItem !== firstSelectedItemRef.current) {
      firstSelectedItemRef.current = newFirstSelectedItem;
    } else {
      return;
    }
    const firefoxBasedRef = isFirefox() && (((_d = listRef.current) === null || _d === void 0 ? void 0 : _d.scrollHeight) || 0) > (((_e = listRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect().height) || 0) ? listRef : listItemsWrapperRef;
    // get position of first selected list item
    const itemBoundingClientRect = (_f = firstSelectedItemRef.current) === null || _f === void 0 ? void 0 : _f.getBoundingClientRect();
    const listBoundingClientRect = (_g = firefoxBasedRef.current) === null || _g === void 0 ? void 0 : _g.getBoundingClientRect();
    if (!itemBoundingClientRect || !listBoundingClientRect) return;
    const itemScrollPosition = listBoundingClientRect.y + listBoundingClientRect.height - itemBoundingClientRect.y;
    if (itemScrollPosition < 0 || itemScrollPosition > listBoundingClientRect.height) {
      // if item is completely hidden or item is partially visible (top)
      (_h = firefoxBasedRef.current) === null || _h === void 0 ? void 0 : _h.scrollTo({
        top: listBoundingClientRect.height + firefoxBasedRef.current.scrollTop - itemScrollPosition,
        behavior: 'smooth'
      });
    } else if (itemScrollPosition < itemBoundingClientRect.height) {
      // if item is partially visible (bottom)
      (_j = firefoxBasedRef.current) === null || _j === void 0 ? void 0 : _j.scrollTo({
        top: itemBoundingClientRect.height - itemScrollPosition + firefoxBasedRef.current.scrollTop,
        behavior: 'smooth'
      });
    }
  }, [arrayValue, mergedChildren, xiListItemElements, children]);
  const allValues = [...allSlottedValues, ...allReactValues];
  const selectAllState = activeValues.length === 0 ? SelectAllState.UNSELECTED : activeValues.length === allValues.length ? SelectAllState.SELECTED : SelectAllState.UNSPECIFIED;
  // #region Slotted List
  const onSlotChildrenChangeRef = useOnChildNodesChange_ref(children => {
    const xiListItems = children.filter(c => c instanceof Element && c.tagName === 'XI-LIST-ITEM');
    if (!areEqual$1(xiListItems, xiListItemElements)) {
      setXiListItemElements(Object.freeze(xiListItems));
    }
  });
  // Checked change listeners
  React.useEffect(() => {
    const cleanupFns = [];
    xiListItemElements.forEach(li => {
      const listener = event => {
        const value = li.value;
        const isChecked = event.detail;
        onSelectionChange(value, isChecked);
      };
      li.addEventListener('checked-change', listener);
      cleanupFns.push(() => li.removeEventListener('checked-change', listener));
    });
    return () => {
      cleanupFns.forEach(fn => fn());
    };
  }, [onSelectionChange, xiListItemElements]);
  // Update checked status
  React.useEffect(() => {
    xiListItemElements.forEach(li => {
      const expectedIsChecked = activeValues.includes(li.value);
      li.checked = expectedIsChecked;
      if (color !== undefined) {
        li.color = color;
      }
      li.contrast = contrast;
      li.select = select;
      li.hasDragging = hasDragging;
    });
  }, [activeValues, color, contrast, hasDragging, select, xiListItemElements]);
  // #endregion Slotted List
  const {
    onListScroll
  } = useLazyLoadedVerticalList({
    listElement: isFirefox() ? listRef.current : listItemsWrapperRef.current,
    endOfScrollOffset,
    isFetchingData,
    onEndOfScroll
  });
  const contrastClass = contrast === 'high' ? 'kxi-high-contrast' : '';
  const errorStateClass = (status === null || status === void 0 ? void 0 : status.state) ? `kxi-${status.state}` : '';
  const errorState = status;
  const textSize = size === 'large' ? 'normal' : 'small';
  return jsxs(Fragment, {
    children: [label && jsx("label", {
      className: classNames('kxi-label kxi-text-normal', contrastClass),
      children: label
    }), jsxs("ul", Object.assign({
      style: {
        maxHeight
      },
      className: classNames(className, 'kxi-list-wrapper', `kxi-${size}`, `kxi-text-${textSize}`, normalizeColor(color !== null && color !== void 0 ? color : 'orca'), errorStateClass, contrastClass, {
        'kxi-border': hasBorder,
        'kxi-list-has-status': errorState === null || errorState === void 0 ? void 0 : errorState.state,
        'kxi-list-has-search': hasSearch,
        'kxi-zebra-striping': hasZebraStriping
      }),
      ref: mergeRefs([ref, listRef])
    }, debugService.componentProps, ingredients, {
      onScroll: isFirefox() ? onListScroll : undefined,
      children: [hasSearch && jsx("div", {
        className: "kxi-list-search",
        children: jsx(SearchBox, Object.assign({
          contrast: contrast,
          hasOutline: false,
          value: searchValue
        }, internalProps === null || internalProps === void 0 ? void 0 : internalProps.searchBox, {
          onChangeOverride: eventData => {
            var _a, _b;
            (_b = (_a = internalProps === null || internalProps === void 0 ? void 0 : internalProps.searchBox) === null || _a === void 0 ? void 0 : _a.onChangeOverride) === null || _b === void 0 ? void 0 : _b.call(_a, eventData);
            setSearchValue(eventData.searchText);
          },
          stretch: true,
          textSize: textSize,
          _stretchHeight: true,
          _wrapperProps: {
            style: {
              borderRadius: 'inherit'
            }
          },
          _iconOffset: size === 'large' ? 15 : 9,
          _iconInputSpacing: 9
        }))
      }), jsxs("div", {
        className: "kxi-list-items-wrapper",
        ref: listItemsWrapperRef,
        onScroll: isFirefox() ? undefined : onListScroll,
        children: [select === 'multiSelectAll' && jsx(ListItem, {
          className: "kxi-list-select-all-item",
          value: "kxi-select-all",
          checked: selectAllState !== SelectAllState.UNSELECTED,
          _isIndeterminate: selectAllState === SelectAllState.UNSPECIFIED,
          select: select,
          color: color,
          contrast: contrast,
          hasDragging: hasDragging,
          onCheckedChange: () => {
            onActiveValuesChange(selectAllState !== SelectAllState.SELECTED ? allValues : []);
          },
          children: selectAll
        }), hasChildren(children) ? mergedChildren : jsx(Slot, {
          ref: onSlotChildrenChangeRef,
          name: "children",
          reactChildren: mergedChildren
        }), isFetchingData && jsx(ListItem, {
          className: "kxi-list-item-loading-animation",
          value: "kxi-list-item-loading-animation",
          select: "none",
          children: jsx(LoadingAnimation, {})
        }), hasSearch && !isFetchingData && !hasChildren(children) && xiListItemElements.length === 0 && jsx("div", {
          className: "kxi-list-no-results-found",
          id: "no-results-div",
          onMouseDown: e => e.preventDefault(),
          children: noResultsFound
        })]
      })]
    })), jsx(ErrorStateUI, {
      errorStateClass: errorStateClass,
      errorState: errorState,
      prePopulateStatus: false,
      contrastClass: contrastClass,
      disabled: false
    })]
  });
});
List.displayName = 'List';

const _ListSizeOptions = ['small', 'normal', 'large'];

const {
  orca,
  grey,
  white: white$1
} = XIColors;
const today = new Date();
today.setHours(0, 0, 0, 0);
const CalendarGrid = props => {
  const debugService = useDebugService(CalendarGrid, props);
  const {
    setDateRange,
    dateRange,
    dates,
    date,
    handleDateClick,
    selectedDates,
    contrast,
    mouseToggle,
    locale,
    minDate,
    maxDate,
    visible
  } = props;
  const [hoveredDate, setHoveredDate] = React.useState();
  const mappedDays = returnShortDayArray(locale).map(({
    dayOfWeek,
    localization
  }) => {
    return jsx("p", {
      className: "kxi-text-small kxi-text-center kxi-text-400 kxi-calendar-day",
      children: localization
    }, dayOfWeek);
  });
  const mappedDates = dates.map((dateNumber, i) => {
    //  date is just to get the month - and it reassigns the date with the appropriate number.
    let curr = new Date(date.getFullYear(), date.getMonth(), dateNumber);
    const disabled = curr < minDate || curr > maxDate;
    const daysInMonth = new Date(curr.getFullYear(), curr.getMonth() + 1, 0).getDate();
    const isPlaceholder = dateNumber === ' ';
    // we don't want curr highlighted if dateNumber is merely a placeholder
    curr = isPlaceholder ? new Date(NaN) : curr;
    const day = curr.getDay();
    const monthDate = curr.getDate();
    const {
      first,
      last
    } = selectedDates;
    let background = '';
    const highlighted = [];
    const isSund = day === 0;
    const isSat = day === 6;
    const isFirst = monthDate === 1;
    const isSameDay = today.getDate() === monthDate;
    const isSameMonth = today.getMonth() === curr.getMonth();
    const isSameYear = today.getFullYear() === curr.getFullYear();
    const isSameAll = isSameDay && isSameMonth && isSameYear && (hoveredDate === null || hoveredDate === void 0 ? void 0 : hoveredDate.valueOf()) !== today.valueOf();
    const borders = {
      borderTopLeftRadius: isSund || isFirst || isSameAll ? '50%' : '0%',
      borderBottomLeftRadius: isSund || isFirst || isSameAll ? '50%' : '0%',
      borderTopRightRadius: isSat || isSameAll ? '50%' : '0%',
      borderBottomRightRadius: isSat || isSameAll ? '50%' : '0%'
    };
    let highlightSpan = false;
    // setting the orca span for to go on top of hovered
    if (hoveredDate && !disabled) {
      if (hoveredDate.getMonth() === curr.getMonth() && hoveredDate.getFullYear() === curr.getFullYear() && hoveredDate.getDate() === curr.getDate()) {
        highlightSpan = true;
      }
    }
    // still working on optimizing, loops through and finds ranges that should be highlighted
    if (first && !disabled) {
      dateRange.forEach(d => {
        // loop++;
        if (d.valueOf() === curr.valueOf()) {
          highlighted.push(i);
        }
      });
    }
    if (highlighted.includes(i) && !isPlaceholder) {
      const border = `1px solid ${grey.light[80]}`;
      if (isSameAll) {
        borders.borderTopLeftRadius = '0%';
        borders.borderTopRightRadius = '0%';
        borders.borderBottomRightRadius = '0%';
        borders.borderBottomLeftRadius = '0%';
      }
      if (!last) {
        borders.borderTop = border;
        borders.borderBottom = border;
        borders.borderLeft = isSund || isFirst ? border : '';
        borders.borderRight = isSat ? border : '';
      } else {
        background = contrast === 'normal' ? orca.light[80] : orca.light[30];
      }
    }
    // creating the flag for the highlighted span to go on top of the first and last divs in the date range
    if (first) {
      if (curr.valueOf() === first.valueOf()) {
        borders.borderTopLeftRadius = '50%';
        borders.borderBottomLeftRadius = '50%';
        highlightSpan = true;
      }
    }
    if (last) {
      curr.setHours(23, 59, 59, 999);
      if (curr.valueOf() === last.valueOf()) {
        borders.borderTopRightRadius = '50%';
        borders.borderBottomRightRadius = '50%';
        highlightSpan = true;
      }
    }
    if (curr.getDate() === daysInMonth) {
      borders.borderTopRightRadius = '50%';
      borders.borderBottomRightRadius = '50%';
    }
    // prevents right side border from showing on the hovered item;
    if (hoveredDate) {
      if (hoveredDate.valueOf() === curr.valueOf() && !last) {
        borders.borderTopRightRadius = '50%';
        borders.borderBottomRightRadius = '50%';
      }
    }
    let inactiveDateSpanColor = contrast === 'normal' ? orca.primary : white$1.primary;
    if (disabled) {
      inactiveDateSpanColor = contrast === 'normal' ? grey.light[80] : grey.light[15];
    }
    const dateSpanStyles = Object.assign(Object.assign({
      color: inactiveDateSpanColor
    }, borders), {
      background
    });
    const dateType = first ? 'second' : 'first';
    return jsx("div", {
      id: !isPlaceholder ? `kxi-calendar-day-${curr.getFullYear()}-${curr.getMonth() + 1}-${dateNumber}` : undefined,
      className: classNames('kxi-date-span', {
        'kxi-hidden-date-span': isPlaceholder,
        'kxi-date-disabled': disabled,
        'kxi-high-contrast': contrast === 'high',
        'kxi-calendar-grid-selected': highlightSpan,
        [`kxi-${dateType}-date-hover`]: !disabled,
        [`kxi-todays-date-${contrast}`]: isSameAll && !highlighted.includes(i)
      }),
      "data-testid": !isPlaceholder && includeTestId(formatDate(new Date(date.getFullYear(), date.getMonth(), +dateNumber))),
      style: dateSpanStyles,
      onMouseOver: () => {
        if (first) {
          setHoveredDate(curr);
        }
      },
      onClick: () => {
        if (!disabled) handleDateClick(date, dateNumber);
      },
      onKeyDown: e => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault(); // needed so space key doesn't move the page
          handleDateClick(date, dateNumber);
        }
      },
      tabIndex: !isPlaceholder && visible ? 0 : -1,
      children: highlightSpan ? jsx("p", {
        style: {
          height: '100%',
          width: '100%',
          background: contrast === 'normal' ? orca.primary : white$1.primary,
          borderRadius: '50%',
          position: 'absolute',
          color: contrast === 'normal' ? white$1.primary : orca.primary,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: 0
        },
        children: dateNumber
      }) : jsx("p", {
        style: {
          color: 'inherit'
        },
        children: dateNumber
      })
    }, i);
  });
  // controls the hover coloring of the dates
  React.useEffect(() => {
    // only highlights if the first date is selected and something is being hovered
    if (selectedDates.first && hoveredDate && !selectedDates.last) {
      setDateRange(getDates(selectedDates.first, hoveredDate));
    }
    if (selectedDates.first && selectedDates.last) {
      setDateRange(getDates(selectedDates.first, selectedDates.last));
    }
  }, [hoveredDate, mouseToggle, selectedDates, setDateRange]);
  const keyHandler = e => {
    var _a;
    const key = e.key;
    const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    const allowedKeys = [...arrowKeys, 'Escape'];
    const focused = (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.id.match(/kxi-calendar-day-(?<year>\d{4})-(?<month>\d{1,2})-(?<day>\d{1,2})/);
    if (allowedKeys.includes(key) && focused) {
      if (arrowKeys.includes(key)) e.preventDefault(); // needed so arrow key doesn't move page
      if (focused === null || focused === void 0 ? void 0 : focused.groups) {
        const year = Number(focused.groups.year);
        const month = Number(focused.groups.month);
        const day = Number(focused.groups.day);
        const daysInMonth = new Date(year, month, 0).getDate();
        const isFirstDay = day === 1;
        const isLastDay = day === daysInMonth;
        const inLastWeek = day + 7 > daysInMonth;
        const inFirstWeek = day - 7 < 1;
        let toFocus = document.activeElement;
        switch (key) {
          case 'ArrowUp':
            if (inFirstWeek) toFocus = document.getElementById(`kxi-calendar-day-${year}-${month}-1`);else toFocus = document.getElementById(`kxi-calendar-day-${year}-${month}-${day - 7}`);
            break;
          case 'ArrowDown':
            if (inLastWeek) toFocus = document.getElementById(`kxi-calendar-day-${year}-${month}-${daysInMonth}`);else toFocus = document.getElementById(`kxi-calendar-day-${year}-${month}-${day + 7}`);
            break;
          case 'ArrowLeft':
            if (!isFirstDay) toFocus = toFocus.previousElementSibling;
            break;
          case 'ArrowRight':
            if (!isLastDay) toFocus = toFocus.nextElementSibling;
            break;
          case 'Escape':
            toFocus.blur();
            return;
        }
        toFocus === null || toFocus === void 0 ? void 0 : toFocus.focus();
      }
    }
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    tabIndex: !visible ? -1 : undefined,
    onKeyDown: keyHandler,
    id: "kxi-calendar-grid-wrapper",
    className: "kxi-calendar-grid-wrapper",
    onMouseLeave: () => setHoveredDate(null),
    children: [mappedDays, jsx("hr", {
      className: `kxi-calendar-hr ${contrast === 'high' ? 'kxi-high-contrast' : ''}`
    }), jsx("span", {
      children: mappedDates
    })]
  }));
};
CalendarGrid.displayName = 'CalendarGrid_Internal';

const monthNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const yearNumberDiff = [-11, -10, -9, -8, -7, -6, -5, -4, -3, -2, -1, 0];
const CalendarModal = props => {
  const debugService = useDebugService(CalendarModal, props);
  const {
    locale,
    minDate,
    maxDate,
    date,
    contrast,
    yearDiff,
    isMonth,
    index,
    isStartFocused,
    selectedRangeDates: {
      first,
      last
    },
    onDateClick,
    visible
  } = props;
  let mappedDates = [];
  if (isMonth) {
    const monthOptions = {
      month: 'short'
    };
    mappedDates = monthNumbers.map(month => {
      const text = formatDate(new Date(date.setMonth(month)), locale, monthOptions);
      const year = date.getFullYear();
      // setting limit to the very end of the month, so that the min works properly
      const limit = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      limit.setHours(23, 59, 59, 999);
      let disabled = !visible;
      if (limit.valueOf() < minDate.valueOf() || date.valueOf() > maxDate.valueOf()) {
        disabled = true;
      }
      let match = false;
      if (isStartFocused) {
        if (first && date.getMonth() === first.getMonth() && year === first.getFullYear()) {
          match = true;
        }
      } else if (last && date.getMonth() === last.getMonth() && year === last.getFullYear()) {
        match = true;
      }
      let buttonTextColor = contrast === 'normal' ? 'orca' : 'disabled';
      if (disabled) {
        // counter-intuitive, but due to the background color it ends up being correct.
        buttonTextColor = contrast === 'normal' ? 'disabled' : 'orca';
      }
      return jsx(HeadingButton, {
        className: classNames('kxi-calendar-modal-heading-button', 'kxi-calendar-modal-heading-button-month', {
          'kxi-calendar-modal-heading-button-selected': match
        }),
        contrast: contrast,
        title: text.toUpperCase(),
        onClick: () => onDateClick(month, year),
        color: buttonTextColor,
        disabled: disabled
      }, month);
    });
  } else {
    const yearOptions = {
      year: 'numeric'
    };
    mappedDates = yearNumberDiff.map(number => {
      const dateCopy = new Date();
      dateCopy.setFullYear(dateCopy.getFullYear() + yearDiff);
      const year = dateCopy.getFullYear() + number;
      const month = dateCopy.getMonth();
      let disabled = !visible;
      if (year < minDate.getFullYear() || year > maxDate.getFullYear()) {
        disabled = true;
      }
      const text = formatDate(new Date(dateCopy.setFullYear(year)), locale, yearOptions);
      let buttonTextColor = contrast === 'normal' ? 'orca' : 'disabled';
      if (disabled) {
        // counter-intuitive, but due to the background color it ends up being correct.
        buttonTextColor = contrast === 'normal' ? 'disabled' : 'orca';
      }
      let match = false;
      if (isStartFocused) {
        if (first && year === first.getFullYear()) {
          match = true;
        }
      } else if (last && year === last.getFullYear()) {
        match = true;
      }
      return jsx(HeadingButton, {
        className: classNames('kxi-calendar-modal-heading-button', 'kxi-calendar-modal-heading-button-year', {
          'kxi-calendar-modal-heading-button-selected': match
        }),
        contrast: contrast,
        title: text,
        onClick: () => onDateClick(month, year),
        color: buttonTextColor,
        disabled: disabled
      }, year);
    });
  }
  return /*#__PURE__*/createElement("div", Object.assign({}, debugService.componentProps, {
    key: index,
    className: "kxi-calendar-modal-wrapper"
  }), jsx("div", {
    className: "kxi-calendar-modal-option-wrapper",
    children: mappedDates
  }));
};
CalendarModal.displayName = 'CalendarModal_Internal';

const monthsPerYear = 12;
const Calendars = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(Calendars, props);
  const {
    selectedRangeDates,
    dateArray,
    modalType,
    contrast,
    locale,
    minDate,
    maxDate,
    isStartFocused,
    generateId,
    setSelectedRangeDates,
    setDateArray,
    setIsStartFocused,
    setModalType,
    onDateClick
  } = props;
  const [dateRange, setDateRange] = React.useState([]);
  const [mouseToggle, setMouseToggle] = React.useState(new Date().toString());
  const [disabled, setDisabled] = React.useState(false);
  const [yearDiff, setYearDiff] = React.useState(0);
  const [hasTransition, setHasTransition] = React.useState(true);
  React.useEffect(() => {
    if (selectedRangeDates.first && selectedRangeDates.last) {
      setSelectedRangeDates(selectedRangeDates);
    }
  }, [selectedRangeDates]);
  React.useEffect(() => {
    const timeoutIdOne = setTimeout(() => setDisabled(false), 500);
    const timeoutIdTwo = setTimeout(() => setHasTransition(true), 100);
    return () => {
      clearTimeout(timeoutIdOne);
      clearTimeout(timeoutIdTwo);
    };
  }, [disabled, hasTransition]);
  const mappedCalendars = dateArray.map((date, i) => {
    const arrowDisabledClass = `kxi-arrow-disabled`;
    const contrastClass = `${contrast === 'high' ? 'kxi-high-contrast' : ''}`;
    const [onLeftArrowClick, onRightArrowClick] = (() => {
      const onRight = () => {
        // prevents spam clicking
        setDisabled(true);
        const copy = dateArray.slice();
        copy[0] = copy[1];
        copy[1] = copy[2];
        copy[2] = copy[3];
        const newDateCopy = new Date(copy[2]);
        newDateCopy.setMonth(newDateCopy.getMonth() + 1);
        copy[3] = new Date(newDateCopy);
        setDateArray(copy);
        setTimeout(() => {
          var _a;
          // need to (re)add focus to the "new" arrow button when we transition months
          (_a = document.getElementById('kxi-calendar-right-arrow')) === null || _a === void 0 ? void 0 : _a.focus();
        }, 750); // enough time for the sliding transition to complete
      };
      const onMonthRight = years => {
        // instead of updating the year chart based on clicked date, it will base it on
        // todays date + the yearDiff - that way the current date will always stay in the
        // same spot on the grid
        if (years === monthsPerYear) {
          setYearDiff(v => v + monthsPerYear);
        }
        setDateArray(dateArray.map(d => new Date(d.getFullYear() + years, d.getMonth())));
      };
      const onLeft = () => {
        // prevents spam clicking
        setDisabled(true);
        const copy = dateArray.slice();
        copy[3] = copy[2];
        copy[2] = copy[1];
        copy[1] = copy[0];
        const newDateCopy = new Date(copy[1]);
        newDateCopy.setMonth(newDateCopy.getMonth() - 1);
        copy[0] = new Date(newDateCopy);
        setDateArray(copy);
        setTimeout(() => {
          var _a;
          // need to (re)add focus to the "new" arrow button when we transition months
          (_a = document.getElementById('kxi-calendar-left-arrow')) === null || _a === void 0 ? void 0 : _a.focus();
        }, 750); // enough time for the sliding transition to complete
      };
      const onMonthLeft = years => {
        // instead of updating the year chart based on clicked date, it will base it on
        // todays date + the yearDiff - that way the current date will always stay in the
        // same spot on the grid
        if (years === monthsPerYear) {
          setYearDiff(v => v - monthsPerYear);
        }
        setDateArray(dateArray.map(d => new Date(d.getFullYear() - years, d.getMonth())));
      };
      switch (modalType) {
        case 'month':
          return [() => onMonthLeft(1), () => onMonthRight(1)];
        case 'year':
          return [() => onMonthLeft(monthsPerYear), () => onMonthRight(monthsPerYear)];
        default:
          return [() => onLeft(), () => onRight()];
      }
    })();
    // disabled arrows based on min and max dates
    const {
      isLeftArrowDisabled,
      isRightArrowDisabled
    } = (() => {
      let isLeftArrowDisabled = false;
      let isRightArrowDisabled = false;
      if (modalType === 'day') {
        const currDate = dateArray[1];
        const minLimit = new Date(currDate.getFullYear(), currDate.getMonth(), 0);
        const maxLimit = new Date(currDate.getFullYear(), currDate.getMonth() + 1, 0);
        minLimit.setHours(23, 59, 59, 59);
        maxLimit.setHours(23, 59, 59, 59);
        if (minLimit.valueOf() < minDate.valueOf()) {
          isLeftArrowDisabled = true;
        }
        if (maxLimit.valueOf() > maxDate.valueOf()) {
          isRightArrowDisabled = true;
        }
      } else if (modalType === 'month') {
        const currDate = dateArray[1];
        const endOfLastYear = new Date(currDate.getFullYear() - 1, 11, 31);
        const startOfNextYear = new Date(currDate.getFullYear() + 1, 0, 1);
        endOfLastYear.setHours(23, 59, 59, 59);
        if (endOfLastYear.valueOf() < minDate.valueOf()) {
          isLeftArrowDisabled = true;
        }
        if (startOfNextYear.valueOf() > maxDate.valueOf()) {
          isRightArrowDisabled = true;
        }
      } else if (modalType === 'year') {
        const beginningOfTimeFrame = new Date(new Date().getFullYear(), 0, 1);
        const endOfTimeFrame = new Date(new Date().getFullYear(), 11, 31);
        beginningOfTimeFrame.setFullYear(beginningOfTimeFrame.getFullYear() + yearDiff - 11);
        endOfTimeFrame.setFullYear(endOfTimeFrame.getFullYear() + yearDiff);
        if (beginningOfTimeFrame.valueOf() < minDate.valueOf()) {
          isLeftArrowDisabled = true;
        }
        if (endOfTimeFrame.valueOf() > maxDate.valueOf()) {
          isRightArrowDisabled = true;
        }
      }
      return {
        isLeftArrowDisabled,
        isRightArrowDisabled
      };
    })();
    const populateArray = (day, currTotal) => {
      // how many numbers are displayed in the grid
      const length = 42;
      const originalDay = day;
      let date = 1;
      const arr = new Array(length);
      for (let i = day; i < arr.length; i++) {
        // populating up until the last day of the current month
        if (date <= currTotal) {
          arr[i] = date;
          date++;
        }
      }
      // adding in the prior days of the month
      for (let j = originalDay - 1; j >= 0; j--) {
        arr[j] = ' ';
      }
      return arr;
    };
    const dateTimeFormat = getDateTimeFormat(locale, {
      month: 'short',
      year: 'numeric'
    });
    const calendarWidth = 328;
    const padding = 12;
    const defaultLefts = [-calendarWidth - padding * 2, undefined, calendarWidth + padding * 2, calendarWidth * 2 + padding * 2 // right hidden
    ];
    const monthAndYear = dateTimeFormat.format(date);
    const day = new Date(date.getFullYear(), date.getMonth(), 1);
    const totalDaysCurr = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const populatedArray = populateArray(day.getDay(), totalDaysCurr);
    const isActiveCalendar = i === 1;
    const gridStyles = {
      left: defaultLefts[i],
      height: modalType === 'day' ? '100%' : '80%'
    };
    const handleHeadingButtonClick = () => {
      setModalType(preValue => {
        switch (preValue) {
          case 'day':
            return 'month';
          case 'month':
            return 'year';
          default:
            return 'day';
        }
      });
      setHasTransition(false);
    };
    const {
      display,
      title
    } = (() => {
      let display = jsx("div", {});
      let title = '';
      const handleDateRangeClick = (date, day) => {
        var _a;
        setMouseToggle(new Date().toString());
        generateId();
        const clicked = new Date(date.getFullYear(), date.getMonth(), day);
        const clickedValue = clicked.valueOf();
        const dateToSet = {
          first: undefined,
          last: undefined
        };
        if (isStartFocused) {
          if (selectedRangeDates.last && clickedValue > selectedRangeDates.last.valueOf()) {
            dateToSet.first = clicked;
            dateToSet.last = undefined;
          } else {
            dateToSet.first = clicked;
            dateToSet.last = selectedRangeDates.last;
          }
          if (setIsStartFocused) setIsStartFocused(false);
        } else {
          if (selectedRangeDates.first && clickedValue < ((_a = selectedRangeDates.first) === null || _a === void 0 ? void 0 : _a.valueOf())) {
            dateToSet.first = clicked;
            dateToSet.last = undefined;
          } else {
            dateToSet.first = selectedRangeDates.first;
            clicked.setHours(23, 59, 59, 999);
            dateToSet.last = clicked;
          }
        }
        setSelectedRangeDates(dateToSet);
        if (onDateClick) onDateClick(dateToSet);
      };
      const handleYearClick = (month, year) => {
        const newDate = new Date(year, month);
        const newDateYear = newDate.getFullYear();
        const newDateMonth = newDate.getMonth();
        const copy = [];
        copy.push(new Date(newDateYear, newDateMonth - 1));
        copy.push(new Date(newDateYear, newDateMonth));
        copy.push(new Date(newDateYear, newDateMonth + 1));
        copy.push(new Date(newDateYear, newDateMonth + 2));
        setDateArray(copy);
        setModalType('month');
      };
      const handleMonthClick = (month, year) => {
        const date = new Date(year, month);
        date.setHours(0, 0, 0, 0);
        setModalType('day');
        setDateArray(getInitialDates({
          selectedDateRange: {
            first: date,
            last: undefined
          }
        }));
        setHasTransition(false);
      };
      switch (modalType) {
        case 'day':
          display = jsx(CalendarGrid, {
            contrast: contrast,
            minDate: minDate,
            maxDate: maxDate,
            locale: locale,
            dateRange: dateRange,
            setDateRange: setDateRange,
            mouseToggle: mouseToggle,
            selectedDates: selectedRangeDates,
            date: date,
            dates: populatedArray,
            visible: isActiveCalendar,
            handleDateClick: handleDateRangeClick
          });
          title = monthAndYear.toUpperCase();
          break;
        case 'month':
        case 'year':
        default:
          display = jsx(CalendarModal, {
            contrast: contrast,
            minDate: minDate,
            maxDate: maxDate,
            locale: locale,
            date: new Date(date),
            isMonth: modalType === 'month',
            yearDiff: yearDiff,
            index: i,
            isStartFocused: isStartFocused,
            onDateClick: modalType === 'month' ? handleMonthClick : handleYearClick,
            selectedRangeDates: selectedRangeDates,
            visible: isActiveCalendar
          });
          title = modalType === 'month' ? dateTimeFormat.formatToParts(date).find(part => part.type === 'year').value : '';
      }
      return {
        display,
        title
      };
    })();
    return /*#__PURE__*/createElement("div", Object.assign({}, debugService.componentProps, {
      tabIndex: !isActiveCalendar ? -1 : undefined,
      className: `kxi-grids-wrapper ${hasTransition ? 'kxi-calendar-transition' : ''}`,
      "aria-hidden": !isActiveCalendar,
      "data-testid": isActiveCalendar ? includeTestId('active-calendar') : undefined,
      key: date.toString(),
      style: gridStyles
    }), jsxs("div", {
      className: "kxi-calendars-title",
      id: "kx-calendar-title",
      children: [jsx("div", {
        className: `kxi-calendar-arrow-wrapper kxi-calendar-arrow-left ${contrastClass}`,
        children: jsx(IconButton, {
          "aria-label": isActiveCalendar ? 'calendar-left-arrow' : undefined,
          "data-testid": isActiveCalendar ? 'calendar-left-arrow' : undefined,
          id: isActiveCalendar ? 'kxi-calendar-left-arrow' : undefined,
          className: `kxi-calendar-arrow ${isLeftArrowDisabled || disabled ? arrowDisabledClass : ''}`,
          disabled: !isActiveCalendar || isLeftArrowDisabled || disabled,
          contrast: contrast,
          hasState: false,
          icon: "single_arrow_left",
          buttonSize: "normal",
          onClick: onLeftArrowClick,
          ref: isActiveCalendar ? ref : undefined
        })
      }), title ? null : jsx("div", {
        style: {
          height: 20
        }
      }), jsx(HeadingButton, {
        disabled: !isActiveCalendar,
        className: "kxi-stretch-width",
        dangerouslySetInnerHTML: {
          __html: sanitizeHtmlString(`<span class="kxi-calendar-html">${title.replace(' ', '<br/>')}</span>`)
        },
        color: contrast === 'normal' ? 'orca' : 'white',
        style: {
          display: 'flex',
          justifyContent: 'center'
        },
        onClick: handleHeadingButtonClick,
        contrast: contrast
      }, i), jsx("div", {
        className: `kxi-calendar-arrow-wrapper kxi-calendar-arrow-right ${contrastClass}`,
        children: jsx(IconButton, {
          "aria-label": isActiveCalendar ? 'calendar-right-arrow' : undefined,
          "data-testid": isActiveCalendar ? 'calendar-right-arrow' : undefined,
          id: isActiveCalendar ? 'kxi-calendar-right-arrow' : undefined,
          className: `kxi-calendar-arrow ${isRightArrowDisabled || disabled ? arrowDisabledClass : ''}`,
          disabled: !isActiveCalendar || isRightArrowDisabled || disabled,
          hasState: false,
          contrast: contrast,
          icon: "single_arrow_right",
          buttonSize: "normal",
          onClick: onRightArrowClick
        })
      })]
    }), display);
  });
  return jsx("div", {
    className: "kxi-calendars-wrapper",
    "data-testid": includeTestId('calendars-wrapper'),
    onMouseLeave: () => setMouseToggle(new Date().toString()),
    children: mappedCalendars
  });
});
Calendars.displayName = 'Calendars_Internal';

// past date functions
const getPastWeeks = week => {
  const day = lastWeek.getDay();
  const weekStart = new Date(week.getFullYear(), week.getMonth(), week.getDate() - day);
  const weekEnd = new Date(week.getFullYear(), week.getMonth(), week.getDate() + (6 - day));
  weekEnd.setHours(23, 59, 59, 999);
  return {
    weekStart,
    weekEnd
  };
};
const getPastMonths = month => {
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  monthEnd.setHours(23, 59, 59, 999);
  return {
    monthStart,
    monthEnd
  };
};
// the end month gets set one extra, because 0 in the date constructor goes back to the last day of the previous month
// going back three months to ensure last quarter.
const getPastQuarters = quarter => {
  let quarterStart, quarterEnd;
  switch (quarter.getMonth()) {
    case 0:
    case 1:
    case 2:
      quarterStartMonth = 0;
      quarterEndMonth = 3;
      break;
    case 3:
    case 4:
    case 5:
      quarterStartMonth = 3;
      quarterEndMonth = 6;
      break;
    case 6:
    case 7:
    case 8:
      quarterStartMonth = 6;
      quarterEndMonth = 9;
      break;
    default:
      quarterStartMonth = 9;
      quarterEndMonth = 11;
  }
  if (quarterEndMonth === 11) {
    quarterStart = new Date(lastMonth.getFullYear(), quarterStartMonth, 1);
    quarterEnd = new Date(lastMonth.getFullYear() + 1, 0, 0);
    quarterEnd.setHours(23, 59, 59, 999);
  } else {
    quarterStart = new Date(lastMonth.getFullYear(), quarterStartMonth, 1);
    quarterEnd = new Date(lastMonth.getFullYear(), quarterEndMonth, 0);
    quarterEnd.setHours(23, 59, 59, 999);
  }
  return {
    quarterStart,
    quarterEnd
  };
};
// date initializers
const currDate = new Date();
currDate.setHours(0, 0, 0, 0);
const currYear = currDate.getFullYear();
const yesterday = new Date();
yesterday.setHours(0, 0, 0, 0);
const lastWeek = new Date();
const lastFourWeeks = new Date();
const thisWeek = new Date();
const lastQuarter = new Date();
const thisQuarter = new Date();
//day
yesterday.setDate(yesterday.getDate() - 1);
const lastYear = new Date(currDate.getFullYear() - 1, currDate.getMonth());
const lastSeven = new Date(currDate.getFullYear(), currDate.getMonth(), currDate.getDate() - 6);
const last30 = new Date(currDate.getFullYear(), currDate.getMonth(), currDate.getDate() - 29);
// week
lastWeek.setDate(currDate.getDate() - 7 * 1);
lastFourWeeks.setDate(currDate.getDate() - 7 * 4);
const {
  weekStart: thisWeekStart,
  weekEnd: thisWeekEnd
} = getPastWeeks(thisWeek);
const {
  weekStart: lastWeekStart,
  weekEnd: lastWeekEnd
} = getPastWeeks(lastWeek);
const {
  weekStart: fourWeeksStart
} = getPastWeeks(lastFourWeeks);
// month
const currMonth = currDate.getMonth();
const threeMonths = currDate.getMonth() - 2;
const lastMonth = new Date(currYear, currMonth, 0);
const lastThreeMonths = new Date(currYear, threeMonths, 0);
const {
  monthStart: thisMonthStart,
  monthEnd: thisMonthEnd
} = getPastMonths(currDate);
const {
  monthStart: lastMonthStart,
  monthEnd: lastMonthEnd
} = getPastMonths(lastMonth);
const {
  monthStart: threeMonthStart
} = getPastMonths(lastThreeMonths);
// quarter
let quarterStartMonth = 0;
let quarterEndMonth = 0;
lastQuarter.setMonth(lastQuarter.getMonth() - 3);
const {
  quarterStart: lastQuarterStart,
  quarterEnd: lastQuarterEnd
} = getPastQuarters(lastQuarter);
const {
  quarterStart: thisQuarterStart,
  quarterEnd: thisQuarterEnd
} = getPastQuarters(thisQuarter);
var DefaultPreselectedCalendarListIds;
(function (DefaultPreselectedCalendarListIds) {
  DefaultPreselectedCalendarListIds["TODAY"] = "_today";
  DefaultPreselectedCalendarListIds["YESTERDAY"] = "_yesterday";
  DefaultPreselectedCalendarListIds["LAST_7_DAYS"] = "_last7Days";
  DefaultPreselectedCalendarListIds["LAST_30_DAYS"] = "_last30Days";
  DefaultPreselectedCalendarListIds["THIS_WEEK"] = "_thisWeek";
  DefaultPreselectedCalendarListIds["LAST_WEEK"] = "_lastWeek";
  DefaultPreselectedCalendarListIds["LAST_4_WEEKS"] = "_last4Weeks";
  DefaultPreselectedCalendarListIds["THIS_MONTH"] = "_thisMonth";
  DefaultPreselectedCalendarListIds["LAST_MONTH"] = "_lastMonth";
  DefaultPreselectedCalendarListIds["LAST_3_MONTHS"] = "_last3Months";
  DefaultPreselectedCalendarListIds["THIS_QUARTER"] = "_thisQuarter";
  DefaultPreselectedCalendarListIds["LAST_QUARTER"] = "_lastQuarter";
  DefaultPreselectedCalendarListIds["THIS_YEAR"] = "_thisYear";
  DefaultPreselectedCalendarListIds["LAST_YEAR"] = "_lastYear";
})(DefaultPreselectedCalendarListIds || (DefaultPreselectedCalendarListIds = {}));
const defaultPreselectedCalendarListOptions = [{
  title: 'Today',
  first: new Date(currDate),
  last: new Date(currDate),
  id: DefaultPreselectedCalendarListIds.TODAY
}, {
  title: 'Yesterday',
  first: new Date(yesterday),
  last: new Date(yesterday),
  id: DefaultPreselectedCalendarListIds.YESTERDAY
}, {
  title: 'Last 7 Days',
  first: new Date(lastSeven),
  last: new Date(currDate),
  id: DefaultPreselectedCalendarListIds.LAST_7_DAYS
}, {
  title: 'Last 30 Days',
  first: new Date(last30),
  last: new Date(currDate),
  id: DefaultPreselectedCalendarListIds.LAST_30_DAYS
}, {
  title: 'This Week',
  first: new Date(thisWeekStart),
  last: new Date(thisWeekEnd),
  id: DefaultPreselectedCalendarListIds.THIS_WEEK
}, {
  title: 'Last Week',
  first: new Date(lastWeekStart),
  last: new Date(lastWeekEnd),
  id: DefaultPreselectedCalendarListIds.LAST_WEEK
}, {
  title: 'Last 4 Weeks',
  first: new Date(fourWeeksStart),
  last: new Date(lastWeekEnd),
  id: DefaultPreselectedCalendarListIds.LAST_4_WEEKS
}, {
  title: 'This Month',
  first: new Date(thisMonthStart),
  last: new Date(thisMonthEnd),
  id: DefaultPreselectedCalendarListIds.THIS_MONTH
}, {
  title: 'Last Month',
  first: new Date(lastMonthStart),
  last: new Date(lastMonthEnd),
  id: DefaultPreselectedCalendarListIds.LAST_MONTH
}, {
  title: 'Last 3 Months',
  first: new Date(threeMonthStart),
  last: new Date(lastMonthEnd),
  id: DefaultPreselectedCalendarListIds.LAST_3_MONTHS
}, {
  title: 'This Quarter',
  first: new Date(thisQuarterStart),
  last: new Date(thisQuarterEnd),
  id: DefaultPreselectedCalendarListIds.THIS_QUARTER
}, {
  title: 'Last Quarter',
  first: new Date(lastQuarterStart),
  last: new Date(lastQuarterEnd),
  id: DefaultPreselectedCalendarListIds.LAST_QUARTER
}, {
  title: 'This Year',
  first: new Date(currDate.getFullYear(), 0, 1),
  last: new Date(currDate.getFullYear(), 11, 31),
  id: DefaultPreselectedCalendarListIds.THIS_YEAR
}, {
  title: 'Last Year',
  first: new Date(lastYear.getFullYear(), 0, 1),
  last: new Date(lastYear.getFullYear(), 11, 31),
  id: DefaultPreselectedCalendarListIds.LAST_YEAR
}];
const nullDates = {
  first: undefined,
  last: undefined
};

class ToggleSwitchProps extends ObservablePropsMixin(ButtonHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], ToggleSwitchProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "toggleMatchesColor", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "changeColorOnToggle", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "isToggled", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleSwitchProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "stretchToggle", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleSwitchProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "isLabelInline", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "isLabelLeft", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ToggleSwitchProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleSwitchProps.prototype, "leftText", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleSwitchProps.prototype, "rightText", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ToggleSwitchProps.prototype, "size", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleSwitchProps.prototype, "hideInactiveText", void 0);
__decorate([Bound(), __metadata("design:type", Function)], ToggleSwitchProps.prototype, "tooltip", void 0);
class ToggleSwitchEvents extends ButtonHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ToggleSwitchEvents.prototype, "onToggle", void 0);
/**
 * Description: Classic InMoment branded ToggleSwitch.
 */
const ToggleSwitch = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ToggleSwitch, props);
  const {
      changeColorOnToggle = true,
      className,
      color = 'orca',
      contrast = 'normal',
      disabled,
      hideInactiveText = false,
      id = uuidv4(),
      isLabelInline = true,
      isLabelLeft,
      isToggled = false,
      label,
      leftText = 'OFF',
      observer,
      onClick,
      onMouseDown,
      onMouseOut,
      onMouseUp,
      onToggle,
      rightText = 'ON',
      size = 'small',
      status,
      stretch,
      stretchToggle,
      toggleMatchesColor = true,
      type = 'button',
      // DEV-28190: This prevents the browser from treating this like a submit when inside a form
      tooltip
    } = props,
    ingredients = __rest(props, ["changeColorOnToggle", "className", "color", "contrast", "disabled", "hideInactiveText", "id", "isLabelInline", "isLabelLeft", "isToggled", "label", "leftText", "observer", "onClick", "onMouseDown", "onMouseOut", "onMouseUp", "onToggle", "rightText", "size", "status", "stretch", "stretchToggle", "toggleMatchesColor", "type", "tooltip"]);
  const isLeftOn = !isToggled;
  const [on, setOn] = useValueBackedState(isLeftOn);
  const [primaryWidth, setPrimaryWidth] = React.useState(0);
  const [secondaryWidth, setSecondaryWidth] = React.useState(0);
  const primaryTextWrapper = React.useRef(null);
  const secondaryTextWrapper = React.useRef(null);
  const wrapper = React.useRef(null);
  const {
    width: wrapperWidth
  } = useResizeDetector({
    handleHeight: false,
    refreshMode: 'debounce',
    refreshRate: 0,
    targetRef: wrapper
  });
  React.useEffect(() => {
    if (primaryTextWrapper.current && secondaryTextWrapper.current) {
      setPrimaryWidth(primaryTextWrapper.current.offsetWidth);
      setSecondaryWidth(secondaryTextWrapper.current.offsetWidth);
    }
  }, [stretch, stretchToggle, leftText, rightText, label, wrapperWidth]);
  const stretchControl = stretchToggle ? ' kxi-stretch-toggle' : '';
  const togglePosition = size === 'small' ? '0.08rem' : '0.18rem';
  const isLeftTextHidden = !on && hideInactiveText;
  const isRightTextHidden = on && hideInactiveText;
  const toggleSwitch = jsxs("div", Object.assign({}, debugService.componentProps, {
    "data-testid": "kxi-toggle-switch",
    className: classNames('kxi-switch', `kxi-toggle-switch-${size}`, normalizeColor(color), {
      'kxi-disabled': disabled,
      [`kxi-stretch kxi-toggle-switch-label-inline`]: stretch,
      'kxi-stretch-toggle': stretchToggle,
      'kxi-toggle-switch-label-inline': isLabelInline,
      'kxi-toggle-switch-label-left': isLabelLeft
    }),
    children: [label && jsx("label", {
      className: classNames('kxi-label', {
        'kxi-high-contrast': contrast === 'high'
      }),
      children: label
    }), jsxs("button", Object.assign({}, ingredients, {
      "aria-disabled": disabled,
      "aria-label": label,
      role: "switch",
      type: type,
      "aria-checked": on,
      id: id,
      className: classNames('kxi-toggle-switch-button kxi-border', className, {
        'kxi-disabled': disabled,
        'kxi-high-contrast': contrast === 'high',
        'kxi-stretch-toggle': stretchToggle,
        'kxi-fit-toggle': !stretchToggle,
        [`kxi-${status}`]: status
      }),
      ref: mergeRefs([wrapper, ref]),
      disabled: disabled,
      onClick: e => {
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        if (!disabled) {
          setOn(!on);
          if (observer) {
            observer.isToggled = on;
          }
          onToggle === null || onToggle === void 0 ? void 0 : onToggle({
            isToggled: on,
            text: !on ? leftText : rightText
          });
        }
      },
      onMouseDown: e => {
        onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(e);
        if (!disabled) {
          on ? setPrimaryWidth(primaryWidth + 12) : setSecondaryWidth(secondaryWidth + 12);
        }
      },
      onMouseUp: e => {
        onMouseUp === null || onMouseUp === void 0 ? void 0 : onMouseUp(e);
        if (primaryTextWrapper.current && secondaryTextWrapper.current && !disabled) {
          on ? setPrimaryWidth(primaryTextWrapper.current.offsetWidth) : setSecondaryWidth(secondaryTextWrapper.current.offsetWidth);
        }
      },
      onMouseOut: e => {
        onMouseOut === null || onMouseOut === void 0 ? void 0 : onMouseOut(e);
        if (e.target === wrapper.current && !disabled) {
          if (primaryTextWrapper.current && secondaryTextWrapper.current) {
            setPrimaryWidth(primaryTextWrapper.current.offsetWidth);
            setSecondaryWidth(secondaryTextWrapper.current.offsetWidth);
          }
        }
      },
      children: [jsx(motion.div, {
        transition: {
          duration: 0.1,
          ease: 'easeOut'
        },
        initial: {
          opacity: 1
        },
        className: classNames('kxi-toggle', {
          'kxi-disabled': disabled,
          'kxi-toggle-lighten-color': on && !disabled && changeColorOnToggle,
          [normalizeColor(color)]: toggleMatchesColor && contrast !== 'high'
        }),
        style: {
          minWidth: on ? primaryWidth : secondaryWidth,
          left: on ? togglePosition : 'unset',
          right: on ? 'unset' : togglePosition,
          originX: 0.5,
          originY: !on ? 0 : 1
        },
        animate: true
      }), jsx("div", {
        role: "presentation",
        className: `orca kxi-toggle-text${on ? '-black' : '-white'}${stretchControl}`,
        ref: primaryTextWrapper,
        children: jsx("span", {
          className: classNames({
            'kxi-hidden': isLeftTextHidden
          }),
          "aria-hidden": isLeftTextHidden,
          children: leftText
        })
      }), jsx("div", {
        role: "presentation",
        className: `orca kxi-toggle-text${on ? '-white' : '-black'}${stretchControl}`,
        ref: secondaryTextWrapper,
        children: jsx("span", {
          className: classNames({
            'kxi-hidden': isRightTextHidden
          }),
          "aria-hidden": isRightTextHidden,
          children: rightText
        })
      })]
    }))]
  }));
  const component = tooltip ? jsx(ToolTipWrapper, Object.assign({}, tooltip, {
    children: toggleSwitch
  })) : toggleSwitch;
  return component;
});
ToggleSwitch.displayName = 'ToggleSwitch';

const CustomRelativeDateSelector = props => {
  const {
    contrast,
    min,
    maxDays,
    maxWeeks,
    maxMonths,
    value,
    unit,
    onValueChange,
    onUnitChange,
    localization,
    onDropdownOpenChange,
    dropdownMenuRef
  } = props;
  const max = {
    day: maxDays,
    week: maxWeeks,
    month: maxMonths
  }[unit];
  const minValueStr = `${min}`;
  const maxValueStr = `${max}`;
  const [inputValue, setInputValue] = useValueBackedState(`${value !== null && value !== void 0 ? value : ''}`);
  const numericInputValue = Number(inputValue);
  const inputRef = React__default.useRef(null);
  const status = inputValue !== '' ? numericInputValue > max ? {
    state: 'error',
    text: `${localization.max} ${maxValueStr}`
  } : numericInputValue < min ? {
    state: 'error',
    text: `${localization.min} ${minValueStr}`
  } : undefined : undefined;
  const errorState = status === null || status === void 0 ? void 0 : status.state;
  React__default.useEffect(() => {
    var _a;
    if (errorState === 'error') {
      (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }, [errorState]);
  return jsxs("div", {
    className: "kxi-custom-relative-date-selector",
    children: [jsx("div", {
      className: "kxi-custom-relative-date-selector-last",
      children: localization.last
    }), jsx("div", {
      className: "kxi-custom-relative-date-selector-value-wrapper",
      children: jsx(InputField, {
        ref: inputRef,
        contrast: contrast,
        placeholder: `${minValueStr} - ${maxValueStr}`,
        value: inputValue,
        "data-min": minValueStr,
        "data-max": maxValueStr,
        pattern: "\\d*",
        minLength: 0,
        maxLength: maxValueStr.length,
        name: "custom-relative-date-value",
        status: status,
        _controlledState: true,
        onClick: e => e.stopPropagation(),
        onChange: e => {
          const newValue = e.target.value;
          if (/^\d*$/.test(newValue)) {
            setInputValue(newValue);
            onValueChange(newValue !== '' ? Number(newValue) : null);
          }
        }
      })
    }), jsx("div", {
      className: "kxi-custom-relative-date-selector-unit-wrapper",
      children: jsx(Dropdown, {
        contrast: contrast,
        value: unit,
        data: [{
          id: 'day',
          title: localization.days
        }, {
          id: 'week',
          title: localization.weeks
        }, {
          id: 'month',
          title: localization.months
        }],
        _menuRef: dropdownMenuRef,
        onValueChange: ({
          selected
        }) => {
          onUnitChange(selected.id);
        },
        onOpenChange: onDropdownOpenChange,
        onClick: e => e.stopPropagation(),
        internalProps: {
          wrapper: {
            onClick: e => e.stopPropagation()
          },
          menu: {
            onClick: e => e.stopPropagation()
          }
        },
        name: "custom-relative-date-unit"
      })
    })]
  });
};

const FixedCalendarInputs = props => {
  const {
    calendarsArrowRef,
    contrast,
    endDate,
    endLabelId,
    firstDateFormatted,
    firstDateStatus,
    firstInputPlaceholder,
    handleInputFieldFocus,
    isStartFocused,
    lastDateFormatted,
    lastDateStatus,
    locale,
    secondInputPlaceholder,
    setFirstDateFormatted,
    setIsStartFocused,
    setLastDateFormatted,
    startDate,
    startDateRef,
    startLabelId,
    updateInputField
  } = props;
  return jsx(Fragment, {
    children: jsxs("div", {
      className: "kxi-calendar-input-fields orca",
      children: [jsxs("div", {
        children: [jsx("label", {
          className: `kxi-calendar-labels kxi-text-small ${contrast === 'high' ? 'kxi-high-contrast' : ''} orca`,
          htmlFor: startLabelId,
          children: startDate
        }), jsx(InputField, {
          _controlledState: true,
          inputId: startLabelId,
          contrast: contrast,
          onChange: e => formatControlledInput(e, firstDateFormatted, setFirstDateFormatted, locale),
          value: firstDateFormatted,
          status: firstDateStatus,
          onClick: () => setIsStartFocused(true),
          onBlur: e => updateInputField(e, true),
          onFocus: () => {
            setIsStartFocused(true);
            handleInputFieldFocus(true);
          },
          onKeyDown: e => {
            var _a;
            if (e.key === 'Enter') {
              e.preventDefault();
              (_a = calendarsArrowRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
          },
          maxLength: 10,
          placeholder: firstInputPlaceholder,
          className: `kxi-calendar-input kxi-condensed ${isStartFocused ? 'kxi-focused' : ''} orca`,
          prePopulateStatus: true,
          "data-testid": "kxi-calendar-first",
          ref: startDateRef
        })]
      }), jsx("p", {
        className: `kxi-text-xsmall ${contrast === 'high' ? 'kxi-high-contrast' : ''} orca`,
        children: "\u2013"
      }), jsxs("div", {
        children: [jsx("label", {
          className: `kxi-calendar-labels kxi-text-small ${contrast === 'high' ? 'kxi-high-contrast' : ''} orca`,
          htmlFor: endLabelId,
          children: endDate
        }), jsx(InputField, {
          _controlledState: true,
          inputId: endLabelId,
          contrast: contrast,
          onChange: e => formatControlledInput(e, lastDateFormatted, setLastDateFormatted, locale),
          status: lastDateStatus,
          value: lastDateFormatted,
          onClick: () => setIsStartFocused(false),
          onBlur: e => updateInputField(e, false),
          onFocus: () => {
            setIsStartFocused(false);
            handleInputFieldFocus(false);
          },
          maxLength: 10,
          placeholder: secondInputPlaceholder,
          className: `kxi-calendar-input kxi-condensed ${!isStartFocused ? 'kxi-focused' : ''} orca`,
          prePopulateStatus: true,
          "data-testid": "kxi-calendar-last"
        })]
      })]
    })
  });
};

const RelativeCalendarInputs = props => {
  const {
    checkedListIdState,
    contrast,
    firstListDateFormatted,
    lastListDateFormatted,
    firstInputPlaceholder,
    secondInputPlaceholder
  } = props;
  const datesAreValid = !!checkedListIdState && !!firstListDateFormatted && !!lastListDateFormatted;
  return jsxs("div", {
    className: classNames('kxi-calendar-relative-inputs orca', contrast === 'high' && 'kxi-high-contrast'),
    children: [jsx("span", {
      className: classNames('kxi-calendar-dark kxi-calendar-relative-inputs-value kxi-calendar-relative-inputs-start-value', contrast === 'high' && 'kxi-high-contrast'),
      children: datesAreValid ? firstListDateFormatted : firstInputPlaceholder
    }), jsx("span", {
      className: classNames('kxi-calendar-relative-inputs-label kxi-calendar-relative-inputs-to-label', contrast === 'high' && 'kxi-high-contrast'),
      children: "\u2013"
    }), jsx("span", {
      className: classNames('kxi-calendar-dark kxi-calendar-relative-inputs-value kxi-calendar-relative-inputs-end-value', contrast === 'high' && 'kxi-high-contrast'),
      children: datesAreValid ? lastListDateFormatted : secondInputPlaceholder
    })]
  });
};

class CalendarProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Object)], CalendarProps.prototype, "data", void 0);
__decorate([Bound(), __metadata("design:type", Array)], CalendarProps.prototype, "groups", void 0);
__decorate([Bound(), __metadata("design:type", String)], CalendarProps.prototype, "mode", void 0);
__decorate([Bound(), __metadata("design:type", String)], CalendarProps.prototype, "outline", void 0);
__decorate([Bound(), __metadata("design:type", Date)], CalendarProps.prototype, "maxDate", void 0);
__decorate([Bound(), __metadata("design:type", Date)], CalendarProps.prototype, "minDate", void 0);
__decorate([Bound(), __metadata("design:type", String)], CalendarProps.prototype, "locale", void 0);
__decorate([Bound(), __metadata("design:type", String)], CalendarProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CalendarProps.prototype, "checkedListId", void 0);
__decorate([Bound(), __metadata("design:type", String)], CalendarProps.prototype, "listColor", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CalendarProps.prototype, "hasInternalPadding", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CalendarProps.prototype, "popoutView", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CalendarProps.prototype, "direction", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], CalendarProps.prototype, "condensedPopoutDropper", void 0);
__decorate([Bound(), __metadata("design:type", Object)], CalendarProps.prototype, "localization", void 0);
class CalendarEvents extends DOMEvents {}
__decorate([Bound(), __metadata("design:type", Function)], CalendarEvents.prototype, "onDateClick", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CalendarEvents.prototype, "onValueChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CalendarEvents.prototype, "onDateError", void 0);
__decorate([Bound(), __metadata("design:type", Function)], CalendarEvents.prototype, "onToggle", void 0);
function isCheckedListIdValid(id) {
  return typeof id === 'string' ? id !== '' : id === null;
}
/**
 * Description: Standard XI themed Custom Date Range selector.
 */
const Calendar = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a, _b;
  const debugService = useDebugService(Calendar, props);
  const today = React.useMemo(() => new Date(), []);
  const defaultMaxDate = new Date(today.getFullYear(), today.getMonth() + 2, today.getDate());
  const {
      data = {},
      groups,
      outline = 'none',
      mode = 'fixed',
      observer,
      localization = {},
      minDate = new Date('01/01/1970'),
      maxDate = defaultMaxDate,
      className,
      checkedListId,
      popoutView = false,
      direction = 6,
      condensedPopoutDropper = false,
      hasInternalPadding = true,
      contrast = 'normal',
      locale: localeInput = 'en-US',
      onDateClick,
      onValueChange,
      onDateError,
      onToggle
    } = props,
    ingredients = __rest(props, ["data", "groups", "outline", "mode", "observer", "localization", "minDate", "maxDate", "className", "checkedListId", "popoutView", "direction", "condensedPopoutDropper", "hasInternalPadding", "contrast", "locale", "onDateClick", "onValueChange", "onDateError", "onToggle"]);
  const locale = validateLocale(localeInput);
  const newMinDate = new Date(minDate);
  const newMaxDate = new Date(maxDate);
  const {
    initialDateRange,
    preselectedCalendarListOptions: preselectedCalendarListOptionsInput
  } = data;
  const getDateFromCustomRelativeValue = React.useCallback((value, unit) => {
    return unit === 'month' ? addMonthsToDate(today, -value) : unit === 'week' ? addWeeksToDate(today, -value) : addDaysToDate(today, -value);
  }, [today]);
  const customCalendarListOptionInput = React.useMemo(() => {
    const {
      customCalendarListOption
    } = data;
    if (!customCalendarListOption) {
      return undefined;
    }
    const {
        id = 'custom',
        title = 'Custom',
        unit = 'day',
        maxDays = 365,
        maxWeeks = 104,
        maxMonths = 36,
        value = 1
      } = customCalendarListOption,
      rest = __rest(customCalendarListOption, ["id", "title", "unit", "maxDays", "maxWeeks", "maxMonths", "value"]);
    const internalOption = Object.assign(Object.assign({}, rest), {
      id,
      title,
      unit,
      min: 1,
      maxDays,
      maxWeeks,
      maxMonths,
      value
    });
    return Object.assign(Object.assign({}, internalOption), isCustomRelativeDateValid(internalOption) && {
      first: getDateFromCustomRelativeValue(value, unit),
      last: today
    });
  }, [data, getDateFromCustomRelativeValue, today]);
  const [customCalendarListOption, setCustomCalendarListOption] = useValueBackedState(customCalendarListOptionInput);
  const preselectedCalendarListOptions = React.useMemo(() => {
    return preselectedCalendarListOptionsInput ? preselectedCalendarListOptionsInput.map(option => Object.assign(Object.assign({}, option), {
      id: `${option.id || uuidv4()}`
    })) : defaultPreselectedCalendarListOptions;
  }, [preselectedCalendarListOptionsInput]);
  const calendarsArrowRef = React.useRef(null);
  const startDateRef = React.useRef(null);
  const {
    custom,
    firstInputPlaceholder = extractDateFormatPlaceholder(locale),
    secondInputPlaceholder = extractDateFormatPlaceholder(locale),
    startDate = 'Start Date',
    endDate = 'End Date',
    errorString = 'Invalid Date',
    leftText = 'Relative',
    rightText = 'Fixed',
    to: _to,
    applyBtn = 'Apply',
    cancelBtn = 'Cancel',
    noSelection: _noSelection = 'No selection',
    min = 'Min',
    max = 'Max',
    last = 'Last',
    days = 'Days',
    weeks = 'Weeks',
    months = 'Months'
  } = localization;
  useWarnIsDeprecated('Calendar', {
    'localization.noSelection': {
      value: (_a = props.localization) === null || _a === void 0 ? void 0 : _a.noSelection
    }
  }, 'prop');
  useWarnIsDeprecated('Calendar', {
    'localization.to': {
      value: (_b = props.localization) === null || _b === void 0 ? void 0 : _b.to
    }
  }, 'prop');
  useWarnIsDeprecated('Calendar', {
    groups: {
      value: groups
    }
  }, 'prop');
  useWarnIsDeprecated('Calendar', {
    onDateClick: {
      message: 'Use onValueChange instead.',
      value: onDateClick
    }
  }, 'prop');
  useWarnIsDeprecated('Calendar', {
    'localization.custom': {
      value: custom,
      message: 'If you are trying to localize the custom relative date selector, use `data.customCalendarListOption.title`.'
    }
  }, 'prop');
  // won't change
  const [startLabelId] = React.useState('startDate');
  const [endLabelId] = React.useState('endDate');
  // Calendar Side
  const [selectedDateRange, setSelectedDateRange] = React.useState(initialDateRange || {
    first: undefined,
    last: undefined
  });
  const [firstDateFormatted, setFirstDateFormatted] = React.useState('');
  const [lastDateFormatted, setLastDateFormatted] = React.useState('');
  const [firstDateStatus, setFirstDateStatus] = React.useState(null);
  const [lastDateStatus, setLastDateStatus] = React.useState(null);
  const [modalType, setModalType] = React.useState('day');
  const [isStartFocused, setIsStartFocused] = React.useState(true);
  // List Side
  const defaultCheckedListId = React.useMemo(() => {
    var _a;
    return isCheckedListIdValid(checkedListId) ? checkedListId : ((_a = preselectedCalendarListOptions[0]) === null || _a === void 0 ? void 0 : _a.id) || DefaultPreselectedCalendarListIds.TODAY;
  }, [checkedListId, preselectedCalendarListOptions]);
  const [checkedListIdState, setCheckedListIdState] = React.useState(defaultCheckedListId);
  React.useEffect(() => {
    if (isCheckedListIdValid(checkedListId)) {
      setCheckedListIdState(checkedListId);
    }
  }, [checkedListId]);
  const selectedListItem = React.useMemo(() => [customCalendarListOption, ...preselectedCalendarListOptions].find(x => (x === null || x === void 0 ? void 0 : x.id) === checkedListIdState), [checkedListIdState, customCalendarListOption, preselectedCalendarListOptions]);
  const [firstListDateFormatted, setFirstListDateFormatted] = React.useState(() => (selectedListItem === null || selectedListItem === void 0 ? void 0 : selectedListItem.first) && formatDate(selectedListItem.first, locale));
  const [lastListDateFormatted, setLastListDateFormatted] = React.useState(() => (selectedListItem === null || selectedListItem === void 0 ? void 0 : selectedListItem.last) && formatDate(selectedListItem.last, locale));
  // Entire Component
  const [modeState, setModeState] = useValueBackedState(mode);
  const [dateArray, setDateArray] = React.useState(getInitialDates({
    selectedDateRange
  }));
  // needed for when the calendar is in a modal, as the onValueChange emitter should not fire
  // unless the user has hit apply
  const [selectedValueObj, setSelectedValueObj] = React.useState({
    dates: {
      first: undefined,
      last: undefined
    },
    mode: mode
  });
  const [hasBeenModified, setHasBeenModified] = React.useState(false);
  const [popoutAnchorElement, setPopoutAnchorElement] = React.useState(null);
  const [isPopoutOpen, setIsPopoutOpen] = React.useState(false);
  const customRelativeDateUnitDropdownRef = React.useRef(null);
  const isCustomRelativeDateUnitDropdownOpen = React.useRef(false);
  React.useEffect(() => {
    if (mode !== 'relative') {
      return;
    }
    const first = selectedListItem === null || selectedListItem === void 0 ? void 0 : selectedListItem.first;
    const last = selectedListItem === null || selectedListItem === void 0 ? void 0 : selectedListItem.last;
    setFirstListDateFormatted(first && formatDate(first, locale));
    setLastListDateFormatted(last && formatDate(last, locale));
  }, [locale, mode, selectedListItem === null || selectedListItem === void 0 ? void 0 : selectedListItem.first, selectedListItem === null || selectedListItem === void 0 ? void 0 : selectedListItem.last]);
  const prevSelectedRangeRef = React.useRef();
  const listRef = React.useRef(null);
  React.useEffect(() => {
    prevSelectedRangeRef.current = selectedDateRange;
  });
  const prevSelectedValueRef = React.useRef();
  React.useEffect(() => {
    prevSelectedValueRef.current = selectedValueObj;
  });
  // shifts the visible focus onto the current month
  const handleInputFieldFocus = isFirst => {
    let currDate = null;
    if (isFirst && selectedDateRange.first) {
      currDate = new Date(selectedDateRange.first);
    } else if (!isFirst && selectedDateRange.last) {
      currDate = new Date(selectedDateRange.last);
    }
    if (currDate) {
      const copyArray = [];
      copyArray.push(new Date(currDate.getFullYear(), currDate.getMonth() - 1, 1));
      copyArray.push(new Date(currDate.getFullYear(), currDate.getMonth(), 1));
      copyArray.push(new Date(currDate.getFullYear(), currDate.getMonth() + 1, 1));
      copyArray.push(new Date(currDate.getFullYear(), currDate.getMonth() + 2, 1));
      setDateArray(copyArray);
      setModalType('day');
    }
  };
  const handleListChange = (option, isValid = true) => {
    setHasBeenModified(true);
    const {
      first,
      last,
      id,
      title
    } = option;
    setFirstListDateFormatted(first && formatDate(first, locale));
    setLastListDateFormatted(last && formatDate(last, locale));
    setCheckedListIdState(id);
    if (observer) {
      observer.checkedListId = id;
    }
    if (!popoutView && isValid && onValueChange) {
      const customOption = option;
      onValueChange(Object.assign({
        dates: {
          first,
          last
        },
        title,
        optionId: id
      }, 'unit' in customOption && {
        unit: customOption.unit,
        value: customOption.value
      }));
    }
  };
  React.useEffect(() => {
    const {
      first,
      last
    } = selectedDateRange;
    const firstDateString = first ? formatDate(first, locale) : '';
    setFirstDateFormatted(firstDateString);
    setFirstDateStatus(null);
    const lastDateString = last ? formatDate(last, locale) : '';
    setLastDateFormatted(lastDateString);
    setLastDateStatus(null);
    handleInputFieldFocus(isStartFocused);
  }, [selectedDateRange, locale]);
  const setInitialDates = dates => {
    const {
      first,
      last
    } = dates;
    if (first && last) {
      try {
        first.setHours(0, 0, 0, 0);
        last.setHours(23, 59, 59, 999);
        setSelectedDateRange({
          first,
          last
        });
      } catch (_a) {
        devOnlyError('Dates passed are invalid');
      }
    }
    setSelectedDateRange(dates);
    setSelectedValueObj({
      dates,
      mode: selectedValueObj.mode
    });
  };
  React.useEffect(() => {
    setInitialDates(initialDateRange || {
      first: undefined,
      last: undefined
    });
    handleInputFieldFocus(true);
  }, [initialDateRange]);
  React.useEffect(() => {
    if (!listRef.current) return;
    const activeElement = checkedListIdState && listRef.current.querySelector(`#${CSS.escape(checkedListIdState)}`);
    if (!activeElement) return;
    const activeOffset = activeElement.offsetTop;
    const activeHeight = activeElement.getBoundingClientRect().height;
    const heightOfBox = listRef.current.getBoundingClientRect().height;
    const offsetOfBox = listRef.current.offsetTop;
    const scroll = listRef.current.scrollTop;
    //if the element is not currently visible
    if (activeOffset - scroll > heightOfBox || activeOffset < scroll) {
      listRef.current.scrollTo({
        top: activeOffset - offsetOfBox,
        behavior: 'smooth'
      });
    } else if (activeOffset - scroll + activeHeight > heightOfBox) {
      //if the element is only partially visible
      listRef.current.scrollTo({
        top: scroll + activeHeight,
        behavior: 'smooth'
      });
    }
  }, [checkedListIdState, modeState, isPopoutOpen]);
  let calendarClassNames = className || '';
  calendarClassNames += contrast === 'high' ? 'kxi-high-contrast' : '';
  if (hasInternalPadding) calendarClassNames += ' kxi-calendar-padding';
  calendarClassNames += ` kxi-calendar-component-wrapper kxi-${outline} orca`;
  const dropperValue = () => {
    var _a;
    let valueText;
    if (modeState === 'fixed') {
      const startDate = firstDateFormatted || firstInputPlaceholder;
      const endDate = lastDateFormatted || secondInputPlaceholder;
      valueText = startDate && endDate ? `${startDate} - ${endDate}` : '';
    } else if (customCalendarListOption && customCalendarListOption.id === checkedListIdState) {
      const {
        value,
        unit,
        min
      } = customCalendarListOption;
      const max = getMaxForUnit(customCalendarListOption, customCalendarListOption.unit);
      valueText = value !== null && min <= value && value <= max ? `${last} ${value} ${{
        day: days,
        week: weeks,
        month: months
      }[unit]}` : `${firstInputPlaceholder} - ${secondInputPlaceholder}`;
    } else {
      const selectedRelativeRange = preselectedCalendarListOptions.find(({
        id
      }) => id === checkedListIdState);
      valueText = (_a = selectedRelativeRange === null || selectedRelativeRange === void 0 ? void 0 : selectedRelativeRange.title) !== null && _a !== void 0 ? _a : '';
    }
    return valueText && jsx("span", {
      className: "kxi-text-normal kxi-calendar-dropper-value",
      children: valueText
    });
  };
  const isApplyDisabled = () => {
    const hasStartDate = modeState === 'fixed' ? firstDateFormatted.length > 0 : firstListDateFormatted !== undefined;
    const hasEndDate = modeState === 'fixed' ? lastDateFormatted.length > 0 : lastListDateFormatted !== undefined;
    return !hasStartDate || !hasEndDate || !hasBeenModified;
  };
  const calendarView = jsxs("div", Object.assign({}, debugService.componentProps, {
    ref: ref,
    className: calendarClassNames,
    "data-testid": "0"
  }, ingredients, {
    children: [jsxs("div", {
      className: "kxi-calendar-input-wrapper",
      children: [jsx("div", {
        className: "kxi-calendar-switch-wrapper",
        children: jsx(ToggleSwitch, {
          isToggled: modeState === 'fixed',
          contrast: contrast,
          stretchToggle: true,
          size: "xxlarge",
          leftText: leftText,
          rightText: rightText,
          onToggle: () => {
            setModeState(v => {
              const newMode = v === 'relative' ? 'fixed' : 'relative';
              if (observer) {
                observer.mode = newMode;
              }
              if (onToggle) onToggle({
                mode: newMode
              });
              return newMode;
            });
          },
          changeColorOnToggle: false,
          id: "kxi-calendar-mode-toggle"
        })
      }), modeState === 'fixed' ? jsx(FixedCalendarInputs, {
        calendarsArrowRef: calendarsArrowRef,
        contrast: contrast,
        endDate: endDate,
        endLabelId: endLabelId,
        firstDateFormatted: firstDateFormatted,
        firstDateStatus: firstDateStatus,
        firstInputPlaceholder: firstInputPlaceholder,
        handleInputFieldFocus: handleInputFieldFocus,
        isStartFocused: isStartFocused,
        lastDateFormatted: lastDateFormatted,
        lastDateStatus: lastDateStatus,
        locale: locale,
        secondInputPlaceholder: secondInputPlaceholder,
        setFirstDateFormatted: setFirstDateFormatted,
        setIsStartFocused: setIsStartFocused,
        setLastDateFormatted: setLastDateFormatted,
        startDate: startDate,
        startDateRef: startDateRef,
        startLabelId: startLabelId,
        updateInputField: (e, isFirst) => {
          const inputDate = e.target.value ? new Date(e.target.value) : undefined;
          const {
            first,
            last
          } = selectedDateRange;
          const dateToCompare = isFirst ? first : last;
          // only triggers if there's a difference
          if ((inputDate === null || inputDate === void 0 ? void 0 : inputDate.valueOf()) !== (dateToCompare === null || dateToCompare === void 0 ? void 0 : dateToCompare.valueOf())) {
            const date = parseToDate(isFirst ? firstDateFormatted : lastDateFormatted, locale);
            if (date && date >= newMinDate && date <= newMaxDate) {
              isFirst ? setFirstDateStatus(null) : setLastDateStatus(null);
              if (isFirst) {
                if (selectedDateRange.last && date > selectedDateRange.last) {
                  selectedDateRange.last = undefined;
                  setLastDateFormatted('');
                }
                const dates = {
                  first: date,
                  last: selectedDateRange.last
                };
                setSelectedDateRange(dates);
                if (!popoutView) {
                  if (onValueChange) onValueChange({
                    dates
                  });else if (onDateClick) onDateClick({
                    dates
                  });
                }
              } else {
                date.setHours(23, 59, 59, 999);
                if (selectedDateRange.first && date < selectedDateRange.first) {
                  selectedDateRange.first = undefined;
                  setFirstDateFormatted('');
                }
                const dates = {
                  first: selectedDateRange.first,
                  last: date
                };
                setSelectedDateRange(dates);
                if (!popoutView) {
                  if (onValueChange) onValueChange({
                    dates
                  });else if (onDateClick) onDateClick({
                    dates
                  });
                }
              }
            } else {
              if (isFirst) {
                setFirstDateStatus({
                  state: 'error',
                  text: errorString
                });
                if (lastDateStatus && lastDateStatus.state === 'error') {
                  if (onDateError) onDateError({
                    dates: {
                      first: errorString,
                      last: errorString
                    }
                  });
                } else {
                  if (onDateError) onDateError({
                    dates: {
                      first: errorString,
                      last
                    }
                  });
                }
              } else {
                setLastDateStatus({
                  state: 'error',
                  text: errorString
                });
                if (firstDateStatus && firstDateStatus.state === 'error') {
                  if (onDateError) onDateError({
                    dates: {
                      first: errorString,
                      last: errorString
                    }
                  });
                } else {
                  if (onDateError) onDateError({
                    dates: {
                      first,
                      last: errorString
                    }
                  });
                }
              }
            }
            setHasBeenModified(true);
          }
        }
      }) : jsx(RelativeCalendarInputs, {
        checkedListIdState: checkedListIdState,
        contrast: contrast,
        firstListDateFormatted: firstListDateFormatted,
        lastListDateFormatted: lastListDateFormatted,
        firstInputPlaceholder: firstInputPlaceholder,
        secondInputPlaceholder: secondInputPlaceholder
      })]
    }), jsx("div", {
      className: `kxi-calendar-wrapper ${modeState === 'relative' ? 'kxi-calendar-list' : ''}  ${contrast === 'high' ? 'kxi-high-contrast' : ''}`,
      children: modeState === 'fixed' ? jsx(Calendars, {
        dateArray: dateArray,
        setDateArray: setDateArray,
        modalType: modalType,
        isStartFocused: isStartFocused,
        setModalType: setModalType,
        minDate: newMinDate,
        maxDate: newMaxDate,
        locale: locale,
        contrast: contrast,
        selectedRangeDates: selectedDateRange,
        setIsStartFocused: setIsStartFocused,
        setSelectedRangeDates: dates => {
          if (popoutView && !isPopoutOpen) return;
          setSelectedDateRange(dates);
        },
        onDateClick: dates => {
          if (!popoutView) {
            if (onValueChange) onValueChange({
              dates
            });else if (onDateClick) onDateClick({
              dates
            });
          } else {
            setHasBeenModified(true);
          }
        },
        generateId: () => {
          setSelectedDateRange(nullDates);
        },
        ref: calendarsArrowRef
      }) : jsxs(List, {
        select: "single",
        className: "kxi-text-small",
        size: "large",
        contrast: contrast,
        ref: listRef,
        value: checkedListIdState,
        children: [customCalendarListOption && jsx(ListItem, {
          className: "kxi-calendar-list-item kxi-calendar-custom-list-item",
          tabIndex: 0,
          value: customCalendarListOption.id,
          id: customCalendarListOption.id,
          onItemClick: () => {
            if (checkedListIdState !== customCalendarListOption.id) {
              handleListChange(customCalendarListOption);
            }
          },
          _canBeDeselected: false,
          disabled: customCalendarListOption.disabled,
          tooltip: customCalendarListOption.tooltip,
          children: jsxs("div", {
            children: [jsx("div", {
              children: customCalendarListOption.title
            }), customCalendarListOption.id === checkedListIdState && jsx(CustomRelativeDateSelector, Object.assign({}, customCalendarListOption, {
              contrast: contrast,
              localization: {
                min,
                max,
                last,
                days,
                weeks,
                months
              },
              dropdownMenuRef: customRelativeDateUnitDropdownRef,
              onDropdownOpenChange: ({
                isOpen
              }) => {
                isCustomRelativeDateUnitDropdownOpen.current = isOpen;
              },
              onUnitChange: unit => {
                const {
                  min,
                  value
                } = customCalendarListOption;
                const max = getMaxForUnit(customCalendarListOptionInput, unit);
                const isValid = value !== null && min <= value && value <= max;
                const updatedOption = Object.assign(Object.assign({}, customCalendarListOption), {
                  unit,
                  max,
                  first: isValid ? getDateFromCustomRelativeValue(value, unit) : undefined,
                  last: isValid ? today : undefined
                });
                setCustomCalendarListOption(updatedOption);
                handleListChange(updatedOption, isValid);
              },
              onValueChange: value => {
                const {
                  min,
                  unit
                } = customCalendarListOption;
                const max = getMaxForUnit(customCalendarListOption, unit);
                const isValid = value !== null && min <= value && value <= max;
                const updatedOption = Object.assign(Object.assign({}, customCalendarListOption), {
                  value,
                  first: isValid ? getDateFromCustomRelativeValue(value, unit) : undefined,
                  last: isValid ? today : undefined
                });
                setCustomCalendarListOption(updatedOption);
                handleListChange(updatedOption, isValid);
              }
            }))]
          })
        }, customCalendarListOption.id), preselectedCalendarListOptions.map(option => jsx(ListItem, {
          className: "kxi-calendar-list-item",
          tabIndex: 0,
          value: option.id,
          id: option.id,
          onItemClick: () => {
            if (checkedListIdState !== option.id) {
              handleListChange(option);
            }
          },
          _canBeDeselected: false,
          disabled: option.disabled,
          tooltip: option.tooltip,
          children: option.title
        }, option.id))]
      })
    })]
  }));
  const onCancel = () => {
    var _a, _b, _c;
    setIsPopoutOpen(false);
    setSelectedDateRange(((_a = selectedValueObj.dates) === null || _a === void 0 ? void 0 : _a.first) && ((_b = selectedValueObj.dates) === null || _b === void 0 ? void 0 : _b.last) ? selectedValueObj.dates : initialDateRange || {
      first: undefined,
      last: undefined
    });
    setCheckedListIdState((selectedValueObj === null || selectedValueObj === void 0 ? void 0 : selectedValueObj.id) || checkedListId || ((_c = preselectedCalendarListOptions[0]) === null || _c === void 0 ? void 0 : _c.id) || DefaultPreselectedCalendarListIds.TODAY);
    setModeState(selectedValueObj.mode || mode);
    setCustomCalendarListOption(customCalendarListOptionInput);
  };
  const popoutCalendar = jsxs("div", {
    className: "kxi-calendar-popout-wrapper",
    ref: setPopoutAnchorElement,
    children: [jsxs("div", {
      tabIndex: 0,
      className: classNames('kxi-calendar-popout-dropper kxi-input', contrast === 'high' && 'kxi-high-contrast', 'kxi-border orca', condensedPopoutDropper && 'kxi-condensed'),
      onClick: () => setIsPopoutOpen(!isPopoutOpen),
      onKeyDown: e => {
        if (e.target !== startDateRef.current && (e.key === 'Enter' || e.key === ' ')) {
          setIsPopoutOpen(!isPopoutOpen);
        }
      },
      children: [dropperValue(), jsx("span", {
        className: "kxi-calendar-popout-dropper-icon",
        children: jsx(XIIcon, {
          icon: "calendar",
          contrast: contrast,
          iconSize: 24
        })
      })]
    }), jsx(Popout, {
      internalPadding: 0,
      className: "kxi-card-shadow",
      anchorElement: popoutAnchorElement,
      isVisible: isPopoutOpen,
      direction: direction,
      onExternalClick: e => {
        const unitDropdownElement = customRelativeDateUnitDropdownRef.current;
        if (!isCustomRelativeDateUnitDropdownOpen.current && (unitDropdownElement === null || !unitDropdownElement.contains(e.event.target))) {
          onCancel();
        }
      },
      onEscape: () => {
        if (!isCustomRelativeDateUnitDropdownOpen.current) {
          onCancel();
        }
      },
      onFocusOut: ({
        event: e
      }) => {
        const unitDropdownElement = customRelativeDateUnitDropdownRef.current;
        const rt = e.relatedTarget;
        const t = e.target;
        if (!isCustomRelativeDateUnitDropdownOpen.current && unitDropdownElement && !unitDropdownElement.contains(rt) && (rt === null ? !unitDropdownElement.contains(t) : true)) {
          onCancel();
        }
      },
      children: jsxs(Fragment, {
        children: [calendarView, jsxs("div", {
          className: `kxi-calendar-popout-footer-buttons ${contrast === 'high' ? 'kxi-high-contrast' : ''} orca`,
          children: [jsx(DefaultButton, {
            color: "orca",
            contrast: contrast,
            buttonSize: "normal",
            text: cancelBtn,
            fill: "no-fill",
            onClick: onCancel
          }), jsx(DefaultButton, {
            color: "orca",
            contrast: contrast,
            buttonSize: "normal",
            text: applyBtn,
            fill: "fill",
            disabled: isApplyDisabled(),
            onClick: () => {
              setIsPopoutOpen(false);
              setHasBeenModified(false);
              if (modeState === 'fixed') {
                setSelectedValueObj({
                  dates: selectedDateRange,
                  id: undefined,
                  title: undefined,
                  mode: modeState
                });
                onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange({
                  dates: selectedDateRange
                });
              } else if (modeState === 'relative') {
                const selectedItem = [customCalendarListOption, ...preselectedCalendarListOptions].find(x => (x === null || x === void 0 ? void 0 : x.id) === checkedListIdState);
                const title = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.title;
                const first = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.first;
                const last = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.last;
                setSelectedValueObj(Object.assign({
                  dates: {
                    first,
                    last
                  },
                  title,
                  mode: modeState
                }, checkedListIdState ? {
                  id: checkedListIdState
                } : {}));
                onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(Object.assign({
                  dates: {
                    first,
                    last
                  },
                  title,
                  optionId: checkedListIdState !== null && checkedListIdState !== void 0 ? checkedListIdState : undefined
                }, selectedItem && 'unit' in selectedItem && selectedItem.value !== null && {
                  unit: selectedItem.unit,
                  value: selectedItem.value
                }));
              }
            }
          })]
        })]
      })
    })]
  });
  return !popoutView ? calendarView : popoutCalendar;
});
Calendar.displayName = 'Calendar';

const ColorPickerKnob = /*#__PURE__*/React.forwardRef(function ColorPickerKnob(props, ref) {
  const {
      className,
      onKeyboardBasedChange
    } = props,
    ingredients = __rest(props, ["className", "onKeyboardBasedChange"]);
  const handleKeyDown = e => {
    if (!onKeyboardBasedChange) {
      return;
    }
    const {
      key
    } = e;
    if (!(key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight')) {
      return;
    }
    e.preventDefault();
    onKeyboardBasedChange({
      xDirection: key === 'ArrowLeft' ? -1 : key === 'ArrowRight' ? 1 : 0,
      yDirection: key === 'ArrowUp' ? -1 : key === 'ArrowDown' ? 1 : 0
    });
  };
  const classes = classNames('kxi-color-picker-knob-wrapper', className);
  return jsx("div", Object.assign({}, ingredients, {
    className: classes,
    ref: ref,
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    children: jsx("div", {
      className: "kxi-color-picker-knob"
    })
  }));
});

// Algorithm source: https://www.w3.org/WAI/WCAG21/Techniques/general/G17.html#procedure
const BLACK = Object.freeze({
  red: 0,
  green: 0,
  blue: 0
});
const WHITE = Object.freeze({
  red: 255,
  green: 255,
  blue: 255
});
function getColorWithHighestContrast(base, ...options) {
  const [highestContrastColor] = options.map(color => ({
    color,
    contrastRatio: getContrastRatio(base, color)
  })).sort((a, b) => b.contrastRatio - a.contrastRatio);
  return highestContrastColor.color;
}
function getContrastRatio(color1, color2) {
  const relLum1 = getRelativeLuminance(color1);
  const relLum2 = getRelativeLuminance(color2);
  const [darker, lighter] = sortNumbers([relLum1, relLum2]);
  return (lighter + 0.05) / (darker + 0.05);
}
/** The higher the return value, the lighter the color */
function getRelativeLuminance(color) {
  const [r, g, b] = [color.red, color.green, color.blue].map(value => value / 255).map(value => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function rgbToCss({
  red,
  green,
  blue,
  alpha = 1
}) {
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/** @deprecated This does not clean up after itself appropriately. Use `useDebouncedCallback` hook. */
/** Calls the provided function asynchronously, rather than immediately */
function callAsync(f) {
  void Promise.resolve().then(f);
}
/**
 * Limits calls to the provided function to at most every `ms` milliseconds
 *
 * @deprecated This does not clean up after itself appropriately. Use `useThrottledCallback` hook.
 * */
function throttle(fn, ms = 100) {
  let throttling = false;
  let deferredArgs;
  return function (...args) {
    if (throttling) {
      deferredArgs = args;
      return;
    }
    // Ensure we're consistent about `fn()` being called asynchronously
    callAsync(() => fn(...args));
    throttling = true;
    deferredArgs = undefined;
    setTimeout(() => {
      throttling = false;
      if (deferredArgs) {
        fn(...deferredArgs);
      }
    }, ms);
  };
}

function worker() {
  const PHYSICAL_CANVAS_WIDTH = 303;
  const PHYSICAL_CANVAS_HEIGHT = 142;
  const LOGICAL_CANVAS_WIDTH = Math.ceil(PHYSICAL_CANVAS_WIDTH / 2);
  const LOGICAL_CANVAS_HEIGHT = Math.ceil(PHYSICAL_CANVAS_HEIGHT / 2);
  const OkColor = okColor();
  if (globalThis.importScripts != null) {
    // If this code is executing in a web worker
    // eslint-disable-next-line no-restricted-globals
    addEventListener('message', ({
      data: hue
    }) => {
      const BYTES_PER_PIXEL = 4; // RGBA
      const SIZE = LOGICAL_CANVAS_WIDTH * LOGICAL_CANVAS_HEIGHT * BYTES_PER_PIXEL;
      const data = new Uint8ClampedArray(SIZE);
      for (let y = 0; y < LOGICAL_CANVAS_HEIGHT; y++) {
        for (let x = 0; x < LOGICAL_CANVAS_WIDTH; x++) {
          const pixelIndex = (y * LOGICAL_CANVAS_WIDTH + x) * BYTES_PER_PIXEL;
          const {
            red,
            green,
            blue
          } = getColorAtCanvasPosition(hue, x, y, false).rgb;
          data[pixelIndex] = red;
          data[pixelIndex + 1] = green;
          data[pixelIndex + 2] = blue;
          data[pixelIndex + 3] = 255;
        }
      }
      postMessage(new ImageData(data, LOGICAL_CANVAS_WIDTH, LOGICAL_CANVAS_HEIGHT), [data.buffer]);
    });
  }
  return {
    LOGICAL_CANVAS_WIDTH,
    LOGICAL_CANVAS_HEIGHT,
    PHYSICAL_CANVAS_WIDTH,
    PHYSICAL_CANVAS_HEIGHT,
    getColorAtCanvasPosition,
    rgbToOkHsv,
    okHsvToRgb
  };
  // =================================================================================================
  function rgbToOkHsv({
    red,
    green,
    blue
  }) {
    if (red === 0 && green === 0 && blue === 0) {
      // Avoid NaN in the result (presumably due to 0 being divided by 0)
      return {
        h: 0,
        s: 0,
        v: 0
      };
    }
    return OkColor.srgb_to_okhsv({
      r: red / 255,
      g: green / 255,
      b: blue / 255
    });
  }
  function okHsvToRgb(hsv) {
    if (hsv.v === 0) {
      // Avoid NaN in the result (presumably due to 0 being divided by 0)
      return {
        red: 0,
        green: 0,
        blue: 0
      };
    }
    const {
      r,
      g,
      b
    } = OkColor.okhsv_to_srgb(hsv);
    return {
      red: normalizeRgbValue(r * 255),
      green: normalizeRgbValue(g * 255),
      blue: normalizeRgbValue(b * 255)
    };
  }
  function normalizeRgbValue(value) {
    return Math.min(Math.max(Math.round(value), 0), 255);
  }
  function getColorAtCanvasPosition(hue, x, y, usePhysicalSize = true) {
    const s = x / (usePhysicalSize ? PHYSICAL_CANVAS_WIDTH : LOGICAL_CANVAS_WIDTH);
    const v = 1 - y / (usePhysicalSize ? PHYSICAL_CANVAS_HEIGHT : LOGICAL_CANVAS_HEIGHT);
    const hsv = {
      h: hue,
      s,
      v
    };
    const rgb = okHsvToRgb(hsv);
    return {
      hsv,
      rgb
    };
  }
  function okColor() {
    /* eslint-disable */
    const pi = Math.PI;
    function srgb_transfer_function(a) {
      return 0.0031308 >= a ? 12.92 * a : 1.055 * Math.pow(a, 0.4166666666666667) - 0.055;
    }
    function srgb_transfer_function_inv(a) {
      return 0.04045 < a ? Math.pow((a + 0.055) / 1.055, 2.4) : a / 12.92;
    }
    function linear_srgb_to_oklab(c) {
      let l = 0.4122214708 * c.r + 0.5363325363 * c.g + 0.0514459929 * c.b;
      let m = 0.2119034982 * c.r + 0.6806995451 * c.g + 0.1073969566 * c.b;
      let s = 0.0883024619 * c.r + 0.2817188376 * c.g + 0.6299787005 * c.b;
      let l_ = Math.cbrt(l);
      let m_ = Math.cbrt(m);
      let s_ = Math.cbrt(s);
      return {
        L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
        a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
        b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_
      };
    }
    function oklab_to_linear_srgb(c) {
      let l_ = c.L + 0.3963377774 * c.a + 0.2158037573 * c.b;
      let m_ = c.L - 0.1055613458 * c.a - 0.0638541728 * c.b;
      let s_ = c.L - 0.0894841775 * c.a - 1.291485548 * c.b;
      let l = l_ * l_ * l_;
      let m = m_ * m_ * m_;
      let s = s_ * s_ * s_;
      return {
        r: +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
      };
    }
    // Finds the maximum saturation possible for a given hue that fits in sRGB
    // Saturation here is defined as S = C/L
    // a and b must be normalized so a^2 + b^2 == 1
    function compute_max_saturation(a, b) {
      // Max saturation will be when one of r, g or b goes below zero.
      // Select different coefficients depending on which component goes below zero first
      let k0, k1, k2, k3, k4, wl, wm, ws;
      if (-1.88170328 * a - 0.80936493 * b > 1) {
        // Red component
        k0 = +1.19086277;
        k1 = +1.76576728;
        k2 = +0.59662641;
        k3 = +0.75515197;
        k4 = +0.56771245;
        wl = +4.0767416621;
        wm = -3.3077115913;
        ws = +0.2309699292;
      } else if (1.81444104 * a - 1.19445276 * b > 1) {
        // Green component
        k0 = +0.73956515;
        k1 = -0.45954404;
        k2 = +0.08285427;
        k3 = +0.1254107;
        k4 = +0.14503204;
        wl = -1.2684380046;
        wm = +2.6097574011;
        ws = -0.3413193965;
      } else {
        // Blue component
        k0 = +1.35733652;
        k1 = -0.00915799;
        k2 = -1.1513021;
        k3 = -0.50559606;
        k4 = +0.00692167;
        wl = -0.0041960863;
        wm = -0.7034186147;
        ws = +1.707614701;
      }
      // Approximate max saturation using a polynomial:
      let S = k0 + k1 * a + k2 * b + k3 * a * a + k4 * a * b;
      // Do one step Halley's method to get closer
      // this gives an error less than 10e6, except for some blue hues where the dS/dh is close to infinite
      // this should be sufficient for most applications, otherwise do two/three steps
      let k_l = +0.3963377774 * a + 0.2158037573 * b;
      let k_m = -0.1055613458 * a - 0.0638541728 * b;
      let k_s = -0.0894841775 * a - 1.291485548 * b;
      {
        let l_ = 1 + S * k_l;
        let m_ = 1 + S * k_m;
        let s_ = 1 + S * k_s;
        let l = l_ * l_ * l_;
        let m = m_ * m_ * m_;
        let s = s_ * s_ * s_;
        let l_dS = 3 * k_l * l_ * l_;
        let m_dS = 3 * k_m * m_ * m_;
        let s_dS = 3 * k_s * s_ * s_;
        let l_dS2 = 6 * k_l * k_l * l_;
        let m_dS2 = 6 * k_m * k_m * m_;
        let s_dS2 = 6 * k_s * k_s * s_;
        let f = wl * l + wm * m + ws * s;
        let f1 = wl * l_dS + wm * m_dS + ws * s_dS;
        let f2 = wl * l_dS2 + wm * m_dS2 + ws * s_dS2;
        S = S - f * f1 / (f1 * f1 - 0.5 * f * f2);
      }
      return S;
    }
    // finds L_cusp and C_cusp for a given hue
    // a and b must be normalized so a^2 + b^2 == 1
    function find_cusp(a, b) {
      // First, find the maximum saturation (saturation S = C/L)
      let S_cusp = compute_max_saturation(a, b);
      // Convert to linear sRGB to find the first point where at least one of r,g or b >= 1:
      let rgb_at_max = oklab_to_linear_srgb({
        L: 1,
        a: S_cusp * a,
        b: S_cusp * b
      });
      let L_cusp = Math.cbrt(1 / Math.max(Math.max(rgb_at_max.r, rgb_at_max.g), rgb_at_max.b));
      let C_cusp = L_cusp * S_cusp;
      return {
        L: L_cusp,
        C: C_cusp
      };
    }
    function toe(x) {
      const k_1 = 0.206;
      const k_2 = 0.03;
      const k_3 = (1 + k_1) / (1 + k_2);
      return 0.5 * (k_3 * x - k_1 + Math.sqrt((k_3 * x - k_1) * (k_3 * x - k_1) + 4 * k_2 * k_3 * x));
    }
    function toe_inv(x) {
      const k_1 = 0.206;
      const k_2 = 0.03;
      const k_3 = (1 + k_1) / (1 + k_2);
      return (x * x + k_1 * x) / (k_3 * (x + k_2));
    }
    function to_ST(cusp) {
      let L = cusp.L;
      let C = cusp.C;
      return {
        S: C / L,
        T: C / (1 - L)
      };
    }
    function okhsv_to_srgb(hsv) {
      let h = hsv.h;
      let s = hsv.s;
      let v = hsv.v;
      let a_ = Math.cos(2 * pi * h);
      let b_ = Math.sin(2 * pi * h);
      let cusp = find_cusp(a_, b_);
      let ST_max = to_ST(cusp);
      let S_max = ST_max.S;
      let T_max = ST_max.T;
      let S_0 = 0.5;
      let k = 1 - S_0 / S_max;
      // first we compute L and V as if the gamut is a perfect triangle:
      // L, C when v==1:
      let L_v = 1 - s * S_0 / (S_0 + T_max - T_max * k * s);
      let C_v = s * T_max * S_0 / (S_0 + T_max - T_max * k * s);
      let L = v * L_v;
      let C = v * C_v;
      // then we compensate for both toe and the curved top part of the triangle:
      let L_vt = toe_inv(L_v);
      let C_vt = C_v * L_vt / L_v;
      let L_new = toe_inv(L);
      C = C * L_new / L;
      L = L_new;
      let rgb_scale = oklab_to_linear_srgb({
        L: L_vt,
        a: a_ * C_vt,
        b: b_ * C_vt
      });
      let scale_L = Math.cbrt(1 / Math.max(Math.max(rgb_scale.r, rgb_scale.g), Math.max(rgb_scale.b, 0)));
      L = L * scale_L;
      C = C * scale_L;
      let rgb = oklab_to_linear_srgb({
        L: L,
        a: C * a_,
        b: C * b_
      });
      return {
        r: srgb_transfer_function(rgb.r),
        g: srgb_transfer_function(rgb.g),
        b: srgb_transfer_function(rgb.b)
      };
    }
    function srgb_to_okhsv(rgb) {
      let lab = linear_srgb_to_oklab({
        r: srgb_transfer_function_inv(rgb.r),
        g: srgb_transfer_function_inv(rgb.g),
        b: srgb_transfer_function_inv(rgb.b)
      });
      let C = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
      let a_ = lab.a / C;
      let b_ = lab.b / C;
      let L = lab.L;
      let h = 0.5 + 0.5 * Math.atan2(-lab.b, -lab.a) / pi;
      let cusp = find_cusp(a_, b_);
      let ST_max = to_ST(cusp);
      let S_max = ST_max.S;
      let T_max = ST_max.T;
      let S_0 = 0.5;
      let k = 1 - S_0 / S_max;
      // first we find L_v, C_v, L_vt and C_vt
      let t = T_max / (C + L * T_max);
      let L_v = t * L;
      let C_v = t * C;
      let L_vt = toe_inv(L_v);
      let C_vt = C_v * L_vt / L_v;
      // we can then use these to invert the step that compensates for the toe and the curved top part of the triangle:
      let rgb_scale = oklab_to_linear_srgb({
        L: L_vt,
        a: a_ * C_vt,
        b: b_ * C_vt
      });
      let scale_L = Math.cbrt(1 / Math.max(Math.max(rgb_scale.r, rgb_scale.g), Math.max(rgb_scale.b, 0)));
      L = L / scale_L;
      L = toe(L);
      // we can now compute v and s:
      let v = L / L_v;
      let s = (S_0 + T_max) * C_v / (T_max * S_0 + T_max * k * C_v);
      return {
        h,
        s,
        v
      };
    }
    return {
      srgb_to_okhsv,
      okhsv_to_srgb
    };
    /* eslint-enable */
  }
}
const {
  LOGICAL_CANVAS_WIDTH,
  LOGICAL_CANVAS_HEIGHT,
  PHYSICAL_CANVAS_WIDTH,
  PHYSICAL_CANVAS_HEIGHT,
  getColorAtCanvasPosition,
  rgbToOkHsv,
  okHsvToRgb
} = worker();

/**
 * Description: The 2d plane that will be the visual representation of the selected color and can be used to select a color.
 */
const ColorPickerGradientPlane = props => {
  const {
    color,
    knobRef: propKnobRef,
    isVisible,
    onMouseDown,
    onColorChange
  } = props;
  const colorRef = useRefToValue(color);
  const isVisibleRef = useRefToValue(isVisible);
  // Position of the center of the knob element relative to the canvas element
  const [knobPos, setKnobPos] = React.useState({
    x: 0,
    y: 0
  });
  const wrapperRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const knobRef = React.useRef(null);
  const isMouseDown = React.useRef(false);
  const focusColor = React.useMemo(() => {
    const rgb = getColorAtCanvasPosition(color.h, knobPos.x, knobPos.y).rgb;
    const highestContrastColor = getColorWithHighestContrast(rgb, BLACK, WHITE);
    return rgbToCss(highestContrastColor);
  }, [knobPos, color]);
  const imageDataWorker = React.useRef();
  const [imageData, setImageData] = React.useState();
  React.useEffect(() => {
    const messageListener = new AbortController();
    imageDataWorker.current = createImageDataWorker();
    imageDataWorker.current.addEventListener('message', ({
      data
    }) => {
      setImageData(data);
    }, {
      signal: messageListener.signal
    });
    return () => {
      var _a;
      messageListener.abort();
      (_a = imageDataWorker.current) === null || _a === void 0 ? void 0 : _a.terminate();
    };
  }, []);
  const postMessageToImageDataWorker = throttle(hue => {
    var _a;
    (_a = imageDataWorker.current) === null || _a === void 0 ? void 0 : _a.postMessage(hue);
  }, 100);
  React.useEffect(() => {
    postMessageToImageDataWorker(color.h);
  }, [color.h]);
  React.useEffect(() => {
    if (!imageData || !canvasRef.current) {
      return;
    }
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.imageSmoothingEnabled = false;
    ctx.putImageData(imageData, 0, 0);
  }, [imageData]);
  React.useEffect(() => {
    const onMouseUp = () => {
      isMouseDown.current = false;
    };
    const onMouseMove = e => {
      selectedPositionMovement(e);
    };
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    return () => {
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);
  React.useEffect(() => {
    const {
      s,
      v
    } = color;
    const xPct = s;
    const yPct = 1 - v;
    const x = PHYSICAL_CANVAS_WIDTH * xPct;
    const y = PHYSICAL_CANVAS_HEIGHT * yPct;
    // draw();
    setKnobPos({
      x,
      y
    });
    emitColorChange(x, y);
  }, [color]);
  function updateKnobPos(x, y) {
    if (!(wrapperRef.current && canvasRef.current)) {
      return;
    }
    // Keep the coordinates inside the canvas area
    x = clamp(x, 0, PHYSICAL_CANVAS_WIDTH);
    y = clamp(y, 0, PHYSICAL_CANVAS_HEIGHT);
    setKnobPos({
      x,
      y
    });
    emitColorChange(x, y);
  }
  const emitColorChange = throttle((x, y) => {
    if (isVisibleRef.current) {
      const color = colorRef.current;
      const updatedColor = getColorAtCanvasPosition(color.h, x, y).hsv;
      if (updatedColor.h !== color.h || updatedColor.s !== color.s || updatedColor.v !== color.v) {
        onColorChange === null || onColorChange === void 0 ? void 0 : onColorChange(updatedColor);
      }
    }
  }, 50);
  const selectedPositionMovement = event => {
    if (!(wrapperRef.current && canvasRef.current && isMouseDown.current)) {
      return;
    }
    const canvasRect = canvasRef.current.getBoundingClientRect();
    // Get coordinates relative to the canvas
    const x = event.clientX - canvasRect.left;
    const y = event.clientY - canvasRect.top;
    updateKnobPos(x, y);
  };
  const handleKeyboardBasedChange = ({
    xDirection,
    yDirection
  }) => {
    const MOVEMENT_FACTOR = 3;
    updateKnobPos(knobPos.x + MOVEMENT_FACTOR * xDirection, knobPos.y + MOVEMENT_FACTOR * yDirection);
  };
  const adjustedKnobPos = React.useMemo(() => {
    var _a, _b, _c;
    const wrapperRect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    const canvasRect = (_b = canvasRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
    const knobRect = (_c = knobRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
    if (!(wrapperRect && canvasRect && knobRect)) {
      return {
        left: knobPos.x,
        top: knobPos.y
      };
    }
    let {
      x,
      y
    } = knobPos;
    // Adjust values from being relative to the canvas to being relative to the wrapper
    x += canvasRect.x - wrapperRect.x;
    y += canvasRect.y - wrapperRect.y;
    // Adjust values from being relative to the center of the knob
    // to being relative to the top-left of the knob
    x -= knobRect.width / 2;
    y -= knobRect.height / 2;
    return {
      left: x,
      top: y
    };
  }, [knobPos, wrapperRef.current, canvasRef.current, knobRef.current]);
  /**
   * See comment at {@link React.CSSProperties} definition.
   *     Direct link: {@link https://github.com/frenic/csstype#what-should-i-do-when-i-get-type-errors}
   * */
  const knobStyle = Object.assign(Object.assign(Object.assign({
    position: 'absolute'
  }, adjustedKnobPos), {
    pointerEvents: 'none'
  }), {
    '--kxi-focus-custom-color': focusColor
  });
  return jsxs("div", {
    className: "kxi-2d-picker-wrapper orca",
    ref: wrapperRef,
    style: {
      height: PHYSICAL_CANVAS_HEIGHT,
      width: PHYSICAL_CANVAS_WIDTH
    },
    children: [jsx("canvas", {
      className: "kxi-2d-picker",
      "data-testid": "color-picker-canvas",
      ref: canvasRef,
      width: LOGICAL_CANVAS_WIDTH,
      height: LOGICAL_CANVAS_HEIGHT,
      onMouseUp: () => {
        isMouseDown.current = false;
      },
      onMouseDown: event => {
        isMouseDown.current = true;
        selectedPositionMovement(event);
        onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown();
      },
      style: {
        width: `${PHYSICAL_CANVAS_WIDTH}px`,
        height: `${PHYSICAL_CANVAS_HEIGHT}px`
      }
    }), jsx(ColorPickerKnob, {
      "data-testid": "color-picker-2d-knob",
      style: knobStyle,
      ref: mergeRefs([knobRef, propKnobRef]),
      onKeyboardBasedChange: handleKeyboardBasedChange
    })]
  });
};
function createImageDataWorker() {
  const blob = new Blob([`(${worker})()`], {
    type: 'text/javascript'
  });
  return new Worker(URL.createObjectURL(blob));
}

const HUE_SLIDER_BACKGROUND_STYLE = (() => {
  const numSections = 16;
  const hues = Array(numSections + 1).fill(0).map((_, i) => i / numSections);
  const gradientStops = hues.map(hue => ({
    h: hue,
    s: 1,
    v: 1
  })).map(okHsvToRgb).map(rgbToCss).join(',');
  return `linear-gradient(${gradientStops})`;
})();
const ColorSlider = props => {
  const {
      className,
      isAlphaSlider = false,
      isVisible,
      initialValue = 1,
      color = rgbToOkHsv({
        red: 255,
        green: 0,
        blue: 0
      }),
      onColorChange,
      onAlphaChange,
      onMouseDown,
      onMouseUp
    } = props,
    ingredients = __rest(props, ["className", "isAlphaSlider", "isVisible", "initialValue", "color", "onColorChange", "onAlphaChange", "onMouseDown", "onMouseUp"]);
  const isVisibleRef = useRefToValue(isVisible);
  const track = React.useRef(null);
  const knob = React.useRef(null);
  const [sliding, setSliding] = React.useState(false);
  const [mouseY, setMouseY] = React.useState(0); //keeps track of the mouse position always
  const [sliderPosition, setSliderPosition] = React.useState(0);
  const [slideStart, setSlideStart] = React.useState(0);
  const sliderRef = React.useRef(null);
  let newSliderPosition = 0;
  React.useEffect(() => {
    var _a, _b;
    if (initialValue > 0) {
      const trackHeight = (((_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) || 0) - 2;
      const knobHeight = ((_b = knob === null || knob === void 0 ? void 0 : knob.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
      const useableTrack = trackHeight - knobHeight;
      if (isAlphaSlider) {
        newSliderPosition = (1 - initialValue) * useableTrack;
      } else {
        newSliderPosition = initialValue * useableTrack;
      }
      setSliderPosition(newSliderPosition);
    }
    const updateMousePosition = event => {
      setMouseY(event.clientY);
    };
    const stopSliding = () => {
      setSliding(false);
    };
    window.addEventListener('mousemove', updateMousePosition);
    window.addEventListener('mouseup', stopSliding);
    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      window.removeEventListener('mouseup', stopSliding);
    };
  }, []);
  React.useEffect(() => {
    var _a, _b;
    const trackHeight = (((_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) || 0) - 2;
    const knobHeight = ((_b = knob === null || knob === void 0 ? void 0 : knob.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
    const useableTrack = trackHeight - knobHeight;
    if (isAlphaSlider) {
      if (initialValue === 100) {
        newSliderPosition = 100 * useableTrack;
      } else {
        newSliderPosition = (1 - initialValue) * useableTrack;
      }
    } else {
      newSliderPosition = initialValue * useableTrack;
    }
    setSliderPosition(newSliderPosition);
  }, [initialValue]);
  function normalizeSliderPosition(newSliderPosition) {
    var _a, _b;
    const height = (_a = track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
    const knobHeight = (_b = knob.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height;
    if (!(height && knobHeight)) {
      return null;
    }
    return clamp(newSliderPosition, 0, height - knobHeight - 2 /* account for border */);
  }
  React.useEffect(() => {
    var _a;
    const top = (_a = track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top;
    if (!(sliding && top)) {
      return;
    }
    const point = Math.floor(mouseY - (top || 0));
    const newSliderPosition = point - slideStart;
    const normalizedPosition = normalizeSliderPosition(newSliderPosition);
    if (normalizedPosition != null) {
      setSliderPosition(normalizedPosition);
    }
  }, [sliding, mouseY, slideStart]);
  React.useEffect(() => {
    var _a, _b;
    if (sliding) {
      const top = ((_b = (_a = knob === null || knob === void 0 ? void 0 : knob.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.top) || 0;
      setSlideStart(mouseY - top + 1);
    }
  }, [sliding]);
  React.useEffect(() => {
    var _a, _b;
    if (!isVisibleRef.current) {
      return;
    }
    const handler = isAlphaSlider ? onAlphaChange : onColorChange;
    if (!handler) {
      return;
    }
    const trackHeight = (((_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) || 0) - 2;
    const knobHeight = ((_b = knob === null || knob === void 0 ? void 0 : knob.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
    const usableTrack = trackHeight - knobHeight;
    const positionAsPercentage = sliderPosition / usableTrack;
    if (isAlphaSlider) {
      onAlphaChange(1 - positionAsPercentage);
    } else {
      onColorChange(Object.assign(Object.assign({}, color), {
        h: positionAsPercentage
      }));
    }
  }, [sliderPosition]);
  let sliderStyle;
  if (isAlphaSlider) {
    const rgb = okHsvToRgb(color);
    const opaque = rgbToCss(Object.assign(Object.assign({}, rgb), {
      alpha: 1
    }));
    const transparent = rgbToCss(Object.assign(Object.assign({}, rgb), {
      alpha: 0
    }));
    sliderStyle = {
      background: `linear-gradient(${opaque}, ${transparent})`
    };
  } else {
    sliderStyle = {
      background: HUE_SLIDER_BACKGROUND_STYLE
    };
  }
  const handleKeyboardBasedChange = ({
    yDirection
  }) => {
    const MOVEMENT_FACTOR = 3;
    setSliderPosition(pos => normalizeSliderPosition(pos + yDirection * MOVEMENT_FACTOR));
  };
  return jsx("div", Object.assign({}, ingredients, {
    className: `kxi-color-slider-wrapper orca ${className || ''}`,
    ref: sliderRef,
    children: jsxs("div", {
      ref: track,
      className: `kxi-color-slider-track ${isAlphaSlider ? 'kxi-color-slider-alpha' : 'kxi-color-slider-hue'}`,
      children: [jsx(ColorPickerKnob, {
        style: {
          position: 'absolute',
          top: sliderPosition,
          zIndex: 2
        },
        "data-testid": "color-picker-knob",
        onMouseDown: () => {
          setSliding(true);
          onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown();
        },
        onMouseUp: () => {
          setSliding(false);
          onMouseUp === null || onMouseUp === void 0 ? void 0 : onMouseUp();
        },
        onKeyboardBasedChange: handleKeyboardBasedChange,
        ref: knob
      }), jsx("div", {
        className: "kxi-color-slider-overlay",
        style: sliderStyle,
        onMouseDown: event => {
          var _a, _b, _c;
          const top = ((_b = (_a = track === null || track === void 0 ? void 0 : track.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.top) || 0;
          const knobOffset = (((_c = knob.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().height) || 0) / 2 + 2;
          setSliderPosition(event.clientY - top - knobOffset);
          setSliding(true);
          onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown();
        },
        onMouseUp: () => {
          onMouseUp === null || onMouseUp === void 0 ? void 0 : onMouseUp();
        }
      })]
    })
  }));
};

const normalizeRgbaColor = rgba => {
  return {
    red: Number(rgba.red),
    green: Number(rgba.green),
    blue: Number(rgba.blue),
    alpha: Number(rgba.alpha)
  };
};

class ColorPickerProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Object)], ColorPickerProps.prototype, "selectedColor", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ColorPickerProps.prototype, "direction", void 0);
__decorate([Bound(), __metadata("design:type", String)], ColorPickerProps.prototype, "inputType", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], ColorPickerProps.prototype, "hexContainsAlpha", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], ColorPickerProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ColorPickerProps.prototype, "localization", void 0);
class ColorPickerEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ColorPickerEvents.prototype, "onColorChange", void 0);
/**
 * Description: Allows the user to pick a color from a 2D chart
 */
const ColorPicker = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ColorPicker, props);
  const {
      selectedColor,
      onColorChange,
      hexContainsAlpha,
      observer,
      tooltip,
      direction = 7,
      inputType: colorTypeInput = 'RGB',
      localization = {}
    } = props,
    ingredients = __rest(props, ["selectedColor", "onColorChange", "hexContainsAlpha", "observer", "tooltip", "direction", "inputType", "localization"]);
  const defaultColor = {
    red: 226,
    green: 226,
    blue: 226,
    alpha: 1
  };
  const [currentColor, setCurrentColor] = React.useState(rgbToOkHsv(defaultColor));
  const [currentAlpha, setCurrentAlpha] = React.useState(defaultColor.alpha);
  const [isPortalVisible, setPortalVisibility] = React.useState(false);
  const [colorType, setColorType] = React.useState(colorTypeInput);
  const [hueControl, setHueControl] = React.useState(false);
  const [alphaControl, setAlphaControl] = React.useState(false);
  const [hexValue, setHexValue] = React.useState('');
  const [initialized, setInitialized] = React.useState(false);
  const [popoutAnchorElement, setPopoutAnchorElement] = React.useState(null);
  const squareRef = React.useRef(null);
  const squareWrapperRef = React.useRef(null);
  const disableColorChange = React.useRef(false);
  const popoutRef = React.useRef(null);
  const isHexValid = hexToRgb(hexValue) !== undefined;
  const rgba = React.useMemo(() => Object.assign(Object.assign({}, okHsvToRgb(currentColor)), {
    alpha: currentAlpha
  }), [currentColor, currentAlpha]);
  const planeKnobRef = useConstant({
    _el: null,
    get current() {
      return this._el;
    },
    set current(value) {
      var _a;
      this._el = value;
      (_a = this._el) === null || _a === void 0 ? void 0 : _a.addEventListener('keydown', e => {
        if (e.key === 'Tab' && e.shiftKey && squareWrapperRef.current) {
          e.preventDefault();
          squareWrapperRef.current.focus();
        }
      });
    }
  });
  const {
    red = 'R',
    green = 'G',
    blue = 'B',
    alpha = 'A',
    rgb = 'RGB',
    hex = 'HEX'
  } = localization;
  const resetHexValue = () => {
    setHexValue(rgbaToHex(rgba, !!hexContainsAlpha));
  };
  React.useEffect(() => resetHexValue(), [rgba, hexContainsAlpha]);
  React.useEffect(() => {
    setColorType(colorTypeInput);
  }, [colorTypeInput]);
  React.useEffect(() => {
    if (isPortalVisible) {
      disableColorChange.current = false;
    }
  }, [isPortalVisible]);
  React.useEffect(() => {
    setColorType(colorTypeInput);
  }, [colorTypeInput]);
  React.useEffect(() => {
    const color = getInternalColor();
    setCurrentColor(rgbToOkHsv(color));
    setCurrentAlpha(color.alpha);
    setInitialized(true);
  }, [selectedColor]);
  React.useEffect(() => {
    if (observer && initialized) {
      observer.selectedColor = typeof selectedColor === 'string' ? rgbaToHex(rgba, !!hexContainsAlpha) : rgba;
    }
  }, [rgba, selectedColor, hexContainsAlpha]);
  const warnInvalidColor = useOneTimeWarning('Invalid color provided to color picker component', false);
  const getInternalColor = () => {
    var _a;
    let color = selectedColor;
    if (!color) {
      return defaultColor;
    }
    if (typeof color === 'string') {
      color = hexToRgb(color);
      if (!color) {
        warnInvalidColor();
        return defaultColor;
      }
    }
    return (_a = validateRgba(color)) !== null && _a !== void 0 ? _a : defaultColor;
  };
  function validateRgba(colorInput) {
    const color = normalizeRgbaColor(colorInput);
    if (!isValidColor(color)) {
      warnInvalidColor();
      return undefined;
    }
    const putColorInBounds = (color, isAlpha = false) => {
      const clampedColor = clamp(color, 0, isAlpha ? 1 : 255);
      if (clampedColor !== color) {
        warnInvalidColor();
      }
      return clampedColor;
    };
    return {
      red: putColorInBounds(color.red),
      green: putColorInBounds(color.green),
      blue: putColorInBounds(color.blue),
      alpha: putColorInBounds(color.alpha, true)
    };
  }
  const closeOnEscapeOrEnter = e => {
    var _a;
    if (e.key === 'Escape' || e.key === 'Enter') {
      setPortalVisibility(false);
      (_a = squareWrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  };
  function emitColorChange({
    color = currentColor,
    alpha = currentAlpha
  }) {
    onColorChange === null || onColorChange === void 0 ? void 0 : onColorChange({
      color: Object.assign(Object.assign({}, okHsvToRgb(color)), {
        alpha
      })
    });
  }
  function onRgbInputChange(event, field) {
    const value = Number.parseInt(event.target.value, 10);
    if (Number.isNaN(value)) {
      return;
    }
    if (field === 'alpha') {
      const normalizedValue = clamp(value, 0, 100) / 100;
      setCurrentAlpha(normalizedValue);
      emitColorChange({
        alpha: normalizedValue
      });
    } else {
      const normalizedValue = clamp(value, 0, 255);
      const updatedRgba = Object.assign(Object.assign({}, rgba), {
        [field]: normalizedValue
      });
      const updatedColor = rgbToOkHsv(updatedRgba);
      setCurrentColor(updatedColor);
      emitColorChange({
        color: updatedColor
      });
    }
  }
  const tooltipDisplayColor = (() => {
    if (colorTypeInput === 'HEX' || colorTypeInput === 'none') {
      return !hexContainsAlpha ? `${hexValue} <b>A</b> ${Math.round(Number(rgba.alpha) * 100).toString()}` : hexValue;
    } else {
      return `<b>R</b> ${rgba.red} <b>G</b> ${rgba.green} <b>B</b> ${rgba.blue} <b>A</b> ${Math.round(Number(rgba.alpha) * 100).toString()}`;
    }
  })();
  const dropdownMenuRef = React.useRef(null);
  const colorPickerPortal = jsxs("div", {
    className: "kxi-color-picker",
    children: [jsxs("div", {
      "aria-hidden": !isPortalVisible,
      className: "kxi-color-selector-container",
      "data-testid": "color-picker-modal",
      onKeyDown: closeOnEscapeOrEnter,
      children: [jsx(ColorPickerGradientPlane, {
        color: currentColor,
        knobRef: planeKnobRef,
        isVisible: isPortalVisible,
        onColorChange: color => {
          if (!disableColorChange.current) {
            setCurrentColor(color);
            emitColorChange({
              color
            });
          }
        },
        onMouseDown: () => {
          disableColorChange.current = false;
        }
      }), jsx(ColorSlider, {
        "data-testid": "color-picker-hue",
        color: currentColor,
        isVisible: isPortalVisible,
        initialValue: hueControl ? undefined : currentColor.h,
        onColorChange: color => {
          if (!disableColorChange.current) {
            setCurrentColor(color);
            emitColorChange({
              color
            });
          }
        },
        onMouseDown: () => {
          disableColorChange.current = false;
          setHueControl(true);
        },
        onMouseUp: () => {
          setHueControl(false);
        }
      }), jsx(ColorSlider, {
        "data-testid": "color-picker-alpha",
        isAlphaSlider: true,
        isVisible: isPortalVisible,
        initialValue: alphaControl ? undefined : currentAlpha,
        color: currentColor,
        onAlphaChange: alpha => {
          if (!disableColorChange.current) {
            setCurrentAlpha(alpha);
            emitColorChange({
              alpha
            });
          }
        },
        onMouseDown: () => {
          disableColorChange.current = false;
          setAlphaControl(true);
        },
        onMouseUp: () => {
          setAlphaControl(false);
        }
      })]
    }), colorTypeInput !== 'none' ? jsxs("div", {
      className: "kxi-color-selector-container inputs",
      onKeyDown: closeOnEscapeOrEnter,
      children: [jsx("div", {
        className: "kxi-menu-color-square",
        style: {
          backgroundColor: `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${rgba.alpha})`
        }
      }), jsx(Dropdown, {
        data: [{
          title: rgb,
          id: 'RGB'
        }, {
          title: hex,
          id: 'HEX'
        }],
        select: "single",
        className: "kxi-color-type",
        onValueChange: ({
          selected
        }) => {
          setColorType(selected.id);
        },
        _menuRef: dropdownMenuRef,
        value: colorType !== 'none' ? colorType : undefined,
        visualState: "condensed"
      }), colorType === 'RGB' ? jsxs(Fragment, {
        children: [jsx(InputField, {
          className: "kxi-color-input",
          "data-testid": "red",
          value: rgba.red.toString(),
          label: red,
          mask: "999",
          errorOnIncompleteMask: false,
          onFocus: () => {
            disableColorChange.current = true;
          },
          onBlur: () => {
            disableColorChange.current = false;
          },
          onInput: e => onRgbInputChange(e, 'red'),
          visualState: "condensed"
        }), jsx(InputField, {
          className: "kxi-color-input",
          "data-testid": "green",
          value: rgba.green.toString(),
          label: green,
          mask: "999",
          errorOnIncompleteMask: false,
          onFocus: () => {
            disableColorChange.current = true;
          },
          onBlur: () => {
            disableColorChange.current = false;
          },
          onInput: e => onRgbInputChange(e, 'green'),
          visualState: "condensed"
        }), jsx(InputField, {
          className: "kxi-color-input",
          "data-testid": "blue",
          value: rgba.blue.toString(),
          label: blue,
          mask: "999",
          errorOnIncompleteMask: false,
          onFocus: () => {
            disableColorChange.current = true;
          },
          onBlur: () => {
            disableColorChange.current = false;
          },
          onInput: e => onRgbInputChange(e, 'blue'),
          visualState: "condensed"
        })]
      }) : jsx(Fragment, {
        children: jsx(InputField, {
          className: `kxi-hex-color-input ${hexContainsAlpha ? 'kxi-hex-has-alpha' : ''}`,
          "data-testid": "hex",
          value: hexValue,
          label: "#",
          status: isHexValid ? undefined : {
            state: 'error'
          },
          onFocus: () => {
            disableColorChange.current = true;
          },
          onBlur: () => {
            disableColorChange.current = false;
            const hexAsRgb = hexToRgb(hexValue);
            if (!hexAsRgb) {
              resetHexValue();
              return;
            }
            const updatedColor = rgbToOkHsv(hexAsRgb);
            setCurrentColor(updatedColor);
            const updatedAlpha = hexContainsAlpha ? hexAsRgb.alpha : currentAlpha;
            setCurrentAlpha(updatedAlpha);
            emitColorChange({
              color: updatedColor,
              alpha: updatedAlpha
            });
          },
          onChange: event => {
            const newHexValue = event.target.value;
            const regex = new RegExp(`^#?[0-9a-fA-F]{0,${hexContainsAlpha ? '8' : '6'}}$`);
            if (regex.test(newHexValue)) {
              setHexValue(newHexValue);
            }
          },
          _controlledState: true,
          visualState: "condensed"
        })
      }), (!hexContainsAlpha || colorType === 'RGB') && jsx(InputField, {
        "data-testid": "alpha",
        className: "kxi-color-input",
        label: alpha,
        value: Math.round(Number(rgba.alpha) * 100).toString(),
        min: 0,
        max: 100,
        mask: "999",
        errorOnIncompleteMask: false,
        onInput: e => onRgbInputChange(e, 'alpha'),
        visualState: "condensed"
      })]
    }) : jsx("div", {
      style: {
        marginBottom: '12px'
      }
    })]
  });
  const togglePortalVisibility = () => {
    disableColorChange.current = true;
    setPortalVisibility(!isPortalVisible);
  };
  const handleActivation = ifActivation(togglePortalVisibility);
  const handleKeyDown = e => {
    handleActivation(e);
    if (e.key === 'Tab' && !e.shiftKey && isPortalVisible && planeKnobRef.current) {
      e.preventDefault();
      planeKnobRef.current.focus();
    } else if (e.key === 'Escape' && isPortalVisible) {
      togglePortalVisibility();
    }
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    children: [jsx(ToolTipWrapper, Object.assign({}, tooltip, {
      text: tooltipDisplayColor,
      children: jsx("div", {
        className: "kxi-color-square-wrapper",
        "aria-haspopup": true,
        tabIndex: 0,
        onKeyDown: handleKeyDown,
        ref: squareWrapperRef,
        children: jsx("div", {
          className: "kxi-color-square",
          "data-testid": "color-picker-square",
          style: {
            backgroundColor: `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${rgba.alpha})`
          },
          onClick: togglePortalVisibility,
          ref: mergeRefs([squareRef, setPopoutAnchorElement])
        })
      })
    })), jsx(Popout, Object.assign({
      anchorElement: popoutAnchorElement,
      isVisible: isPortalVisible,
      internalPadding: 0,
      ref: mergeRefs([popoutRef, ref]),
      direction: direction,
      onExternalClick: e => {
        var _a;
        if ((_a = dropdownMenuRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.event.target)) {
          return;
        }
        setPortalVisibility(false);
      },
      onEscape: () => setPortalVisibility(false)
    }, ingredients, {
      children: colorPickerPortal
    }))]
  }));
});
ColorPicker.displayName = 'ColorPicker';

class DatePickerProps extends InputFieldProps {}
__decorate([Bound(), __metadata("design:type", Date)], DatePickerProps.prototype, "minDate", void 0);
__decorate([Bound(), __metadata("design:type", Date)], DatePickerProps.prototype, "maxDate", void 0);
__decorate([Bound(), __metadata("design:type", String)], DatePickerProps.prototype, "locale", void 0);
__decorate([Bound(), __metadata("design:type", Date)], DatePickerProps.prototype, "dateValue", void 0);
__decorate([Bound(), __metadata("design:type", Object)], DatePickerProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", Object)], DatePickerProps.prototype, "direction", void 0);
__decorate([Bound(), __metadata("design:type", String)], DatePickerProps.prototype, "placeholder", void 0);
class DatePickerEvents extends InputFieldEvents {}
__decorate([Bound(), __metadata("design:type", Function)], DatePickerEvents.prototype, "onDateChange", void 0);
/**
 * Description: Standard InMoment branded Date Picker.
 */
const DatePicker = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(DatePicker, props);
  const today = new Date();
  // arbitrarily setting it 2 months in the future to avoid a bug (revert back to default max date being today after BZ-5944)
  const defaultMaxDate = new Date(today.getFullYear(), today.getMonth() + 2);
  const {
      minDate = new Date('01/01/1970'),
      maxDate = defaultMaxDate,
      className,
      onDateChange,
      onClick,
      onChange,
      onBlur,
      onIconClick,
      onKeyDown,
      direction = 7,
      observer,
      value,
      dateValue,
      locale: localeInput = 'en-US',
      contrast = 'normal',
      status,
      placeholder,
      tabIndex = 0
    } = props,
    ingredients = __rest(props, ["minDate", "maxDate", "className", "onDateChange", "onClick", "onChange", "onBlur", "onIconClick", "onKeyDown", "direction", "observer", "value", "dateValue", "locale", "contrast", "status", "placeholder", "tabIndex"]);
  const locale = validateLocale(localeInput);
  const intl = getDateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const [isOpen, setIsOpen] = React.useState(false);
  const [isValidDate, setIsValidDate] = React.useState(false);
  const [isDateSelecting, setIsDateSelecting] = React.useState(false);
  const [selectedRangeDates, setSelectedRangeDates] = useComputedState(() => {
    if (!dateValue) {
      return nullDates;
    }
    const first = new Date(dateValue);
    first.setHours(0, 0, 0, 0);
    const last = new Date(dateValue);
    last.setHours(23, 59, 59, 999);
    return {
      first,
      last
    };
  }, [dateValue]);
  const [dateArray, setDateArray] = useComputedState(() => getInitialDates({
    selectedDateRange: selectedRangeDates
  }), [selectedRangeDates]);
  const [modalType, setModalType] = React.useState('day');
  const [text, setText] = useValueBackedState(dateValue ? intl.format(dateValue) : '');
  const [popoutAnchorElement, setPopoutAnchorElement] = React.useState(null);
  const wrapperRef = React.useRef(null);
  const calendarRef = React.useRef(null);
  const originalDateOnOpen = React.useRef('');
  const lastValidDate = React.useRef('');
  const lastDateChange = React.useRef('');
  const popoutRef = React.useRef(null);
  const inputRef = React.useRef(null);
  // warn about input events being possibly overwritten
  useWarnUnexpectedBehavior({
    onClick
  }, 'DatePicker');
  useWarnUnexpectedBehavior({
    onChange
  }, 'DatePicker');
  useWarnUnexpectedBehavior({
    onBlur
  }, 'DatePicker');
  useWarnUnexpectedBehavior({
    onIconClick
  }, 'DatePicker');
  useWarnUnexpectedBehavior({
    onKeyDown
  }, 'DatePicker');
  const onOpenChange = isOpen => {
    if (isOpen) {
      originalDateOnOpen.current = text;
    }
    setIsOpen(isOpen);
  };
  React.useEffect(() => {
    lastValidDate.current = text;
  }, [text]);
  React.useEffect(() => {
    const onEnterListener = e => {
      if (isOpen && e.key === 'Enter' && document.activeElement === document.body) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', onEnterListener);
    return () => document.removeEventListener('keydown', onEnterListener);
  }, [isOpen]);
  const {
    regex,
    fullRegex
  } = (() => {
    if (value !== undefined) {
      devOnlyWarn('The Kitchen: you are using the value property of the Date Picker.  You should use the dateValue property as it is type safe.');
    }
    const parts = intl.formatToParts().filter(p => p.type !== 'literal');
    /**
     * Okay, so this is the complicated part. Don't mess around here if you don't have to.
     *
     * First we take a look at the intl library and from that we can retreive the local
     * date formatting structure (day, month, year). Those are put into the parts
     * variable and then we iterate over the parts so we get the correct regex in the
     * correct part of the string for the correct locale.
     *
     * Seems easy enough, right?
     *    WRONG. This is where we get into regex hell
     *
     * We have different regex for each part and different regex for an in-progress date
     * and one that is finalized.
     *
     * Any currently supported date format is in the form of
     * <transMid> [separator] <transMid> [separator] <transEnd>
     *    (good luck to you if you need to break this pattern)
     *
     * And the final string will always be in the form of
     * <transMid...Full> [separator] <transMid...Full> [separator] <transEnd...Full>
     *
     * The tricky part is that we need 2 different regex strings, the final output
     * string is easy(ish) in that it just has to conform to the localized MM-DD-YY(YY)
     * format but the in-progress string is tougher because it has to optionally match
     * anything not present while prohibiting invalid inputs. That is where the transEnd
     * and transMid parts come into play
     *
     * The transEnd is basically (almost) just the transMid part without a separator at
     * the end. This is so that someone can't enter a date like 1/24/2023/. However
     * there are also some negative look-behinds to help with matching everything properly
     *
     * The in-progress regex will end up with 3 parts/sets (day, month, year) regex in
     * order and optional. For example lets take the US date structure of MM/DD/YYYY in
     * the in progress regex you will end up with a regex that accepts the following:
     * MM/DD/YYYY or MM/DD or MM. Represented as set 3, 2, and 1 respectively. This
     * allows significantly less complicated regex but does increase the logic work.
     *
     * GOOD LUCK!
     */
    // these represent the regex that covers the cases of in-progress typing and final evaluation
    let set1 = '',
      // 1st part only
      set2 = '',
      // 1st and 2nd part
      set3 = '',
      // All 3 parts
      fullRegex = ''; // All 3 parts finalized
    const transEnd = {
      // The end of string
      day: '(0?[1-9]?|[12][0-9]|3[01])?(?!\\d)(?: г)?.?',
      dayFull: '(0?[1-9]?|[12][0-9]|3[01])(?: г)?.?',
      month: '(0?[1-9]?|1[0-2])?(?!\\d)(?: г)?.?',
      monthFull: '(0?[1-9]?|1[0-2])(?: г)?.?',
      year: '(\\d{0,4})?(?!\\d)(?: г)?.?',
      yearFull: '(\\d{2}|\\d{4})(?: г)?.?'
    };
    const transMid = {
      // middle parts
      day: '(0?[1-9]|[12][0-9]|3[01])(?:\\. |[-/.])',
      dayFull: '(0?[1-9]|[12][0-9]|3[01])(?:\\. |[-/.])',
      month: '(0?[1-9]|1[0-2])(?:\\. |[-/.])',
      monthFull: '(0?[1-9]|1[0-2])(?:\\. |[-/.])',
      year: '(\\d{1,4})(?:\\. |[-/.])',
      yearFull: '(\\d{2}|\\d{4}})(?:\\. |[-/.])'
    };
    parts.forEach((p, i) => {
      switch (i) {
        case 0:
          set1 += transEnd[p.type];
          set2 += transMid[p.type];
          set3 += transMid[p.type];
          fullRegex += transMid[`${p.type}Full`];
          break;
        case 1:
          set2 += transEnd[p.type];
          set3 += transMid[p.type];
          fullRegex += transMid[`${p.type}Full`];
          break;
        case 2:
          set3 += transEnd[p.type];
          fullRegex += transEnd[`${p.type}Full`];
          break;
      }
    });
    return {
      regex: new RegExp(`^(${set3})$|^(${set2})$|^(${set1})$`),
      fullRegex: new RegExp(`^(${fullRegex})$`)
    };
  })();
  /**
   * This function takes text and returns a date object that most closely aligns to the
   * text. For instance if given 10/12 with the locale of en-gb (DD/MM/YYYY) it will
   * return Dec. 10, of the current year. If its a partial string as in the above
   * example, isPartial will be true otherwise false.
   *
   * @param text The text string to test
   * @returns If the date can't be parsed undefined. Otherwise, An object containing a
   * numeric value for the day month and year, a newly created date object with the date
   * and a boolean value stating wether or not the date was partial
   */
  const textToDate = text => {
    const parts = intl.formatToParts().filter(p => p.type !== 'literal');
    if (fullRegex === null || fullRegex === void 0 ? void 0 : fullRegex.test(text)) {
      const fullRegexGlobal = new RegExp(fullRegex, 'g');
      const regexResult = Array.from(text.matchAll(fullRegexGlobal))[0];
      const dateNums = {
        day: Number(regexResult[parts.findIndex(item => item.type === 'day') + 2]),
        month: Number(regexResult[parts.findIndex(item => item.type === 'month') + 2]) - 1,
        year: Number(regexResult[parts.findIndex(item => item.type === 'year') + 2])
      };
      if (dateNums.day > 0) {
        // the incomplete form of full regex can return 0 for year (not an issue because 2000), 0 for the month (not an issue because of how Date handles months), or 0 for days (issue)
        return Object.assign(Object.assign({
          textAsDate: new Date(dateNums.year < 100 ? dateNums.year + 2000 : dateNums.year,
          // ensure that a date of XX/XX/23 doesn't return 1923 but rather 2023
          dateNums.month, dateNums.day)
        }, dateNums), {
          isPartial: false
        });
      }
    }
    if (regex === null || regex === void 0 ? void 0 : regex.test(text)) {
      const regexGlobal = new RegExp(regex, 'g');
      const regexResult = Array.from(text.matchAll(regexGlobal))[0].filter(value => value !== undefined);
      const calcMonth = Number(regexResult === null || regexResult === void 0 ? void 0 : regexResult[parts.findIndex(item => item.type === 'month') + 2]) - 1; // Date() takes in months 0 indexed
      const calcDay = Number(regexResult === null || regexResult === void 0 ? void 0 : regexResult[parts.findIndex(item => item.type === 'day') + 2]);
      const calcYear = Number(regexResult === null || regexResult === void 0 ? void 0 : regexResult[parts.findIndex(item => item.type === 'year') + 2]);
      const dateNums = {
        day: calcDay > 0 ? calcDay : undefined,
        month: calcMonth >= 0 ? calcMonth : undefined,
        year: calcYear >= 0 ? calcYear : undefined
      };
      return Object.assign(Object.assign({
        textAsDate: new Date(dateNums.year ? dateNums.year < 100 ? dateNums.year + 2000 : dateNums.year : new Date().getFullYear(), dateNums.month === undefined || dateNums.month < 0 ? new Date().getMonth() : dateNums.month, dateNums.day || new Date().getDate())
      }, dateNums), {
        isPartial: true
      });
    }
  };
  // need this function to be passed down because it always needs to set the dates, and pass the results via props
  const generateID = () => setSelectedRangeDates(nullDates);
  const handleInput = e => {
    const newText = e.target.value;
    updateCalendarValue(newText, e);
  };
  const updateCalendarValue = (newText, e) => {
    var _a;
    if (regex === null || regex === void 0 ? void 0 : regex.test(newText)) {
      setText(newText);
      const textAsDate = (_a = textToDate(newText)) === null || _a === void 0 ? void 0 : _a.textAsDate;
      if (textAsDate && minDate <= textAsDate && textAsDate <= maxDate) {
        setDateArray(getInitialDates({
          selectedDateRange: {
            first: textAsDate,
            last: undefined
          }
        }));
        updateDateRange(textAsDate);
        setIsValidDate(true);
        lastValidDate.current = intl.format(textAsDate);
        textAsDate.setHours(0, 0, 0, 0);
      } else {
        setIsValidDate(false);
      }
      if (onChange && e) onChange(e);
    }
  };
  const handleKeyDown = e => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (!isValidDate) {
          updateCalendarValue(lastValidDate.current);
        }
        if (isOpen) {
          ensureValidDate(lastValidDate.current);
        } else {
          onOpenChange(true);
        }
        break;
      case 'Escape':
        updateCalendarValue(originalDateOnOpen.current);
        onOpenChange(false);
        break;
      case 'Tab':
        if (isOpen) {
          focusOnNthTabbableElement(popoutRef.current, 0);
          e.preventDefault();
        }
        break;
      default:
        if (!isOpen) {
          onOpenChange(true);
        }
    }
  };
  const handleClick = e => {
    e.stopPropagation();
    onOpenChange(true);
    onClick === null || onClick === void 0 ? void 0 : onClick(e);
  };
  const handleBlur = e => {
    var _a;
    const rt = e.relatedTarget;
    if ((_a = popoutRef.current) === null || _a === void 0 ? void 0 : _a.contains(rt)) {
      return;
    }
    if (text === '' || isDateSelecting) return;
    if (!isValidDate) {
      updateCalendarValue(lastValidDate.current);
    }
    ensureValidDate(lastValidDate.current);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  };
  const handleDateClick = dates => {
    var _a;
    dates.first.setHours(0, 0, 0, 0);
    const text = intl.format(dates.first);
    updateCalendarValue(text);
    ensureValidDate(text);
    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  };
  const updateDateRange = date => {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    setSelectedRangeDates({
      first: date,
      last: endOfDay
    });
  };
  const ensureValidDate = textIn => {
    var _a;
    if (!text && !textIn) {
      onOpenChange(false);
      return;
    }
    const textAsDate = (_a = textToDate(textIn !== null && textIn !== void 0 ? textIn : text)) === null || _a === void 0 ? void 0 : _a.textAsDate;
    if (!textAsDate) {
      return;
    }
    const formattedDate = intl.format(textAsDate);
    onOpenChange(false);
    if (textAsDate >= minDate && textAsDate <= maxDate) {
      setText(formattedDate);
      textAsDate.setHours(0, 0, 0, 0);
      if (onDateChange && lastDateChange.current !== formattedDate) {
        onDateChange({
          date: textAsDate
        });
        lastDateChange.current = formattedDate;
      }
      if (observer) {
        observer.dateValue = textAsDate;
      }
    }
  };
  const getDateFormat = () => {
    var _a;
    const trans = {
      day: 'DD',
      month: 'MM',
      year: 'YYYY'
    };
    const parts = intl.formatToParts();
    const literal = (_a = parts.find(p => p.type === 'literal')) === null || _a === void 0 ? void 0 : _a.value;
    return parts.filter(p => p.type !== 'literal').map(p => trans[p.type]).join(literal);
  };
  const close = () => onOpenChange(false);
  const onExternal = eventData => {
    var _a;
    if (!((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(eventData.event.target))) {
      close();
    }
  };
  const onFocusOut = eventData => {
    var _a;
    onExternal(eventData);
    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  };
  return jsxs("div", Object.assign({
    className: "kxi-date-picker-wrapper",
    ref: mergeRefs([wrapperRef, setPopoutAnchorElement])
  }, debugService.componentProps, {
    children: [jsx(InputField, Object.assign({
      tabIndex: tabIndex,
      _controlledState: true,
      onClick: handleClick,
      onChange: handleInput,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onIconClick: () => {
        var _a;
        onOpenChange(!isOpen);
        onIconClick === null || onIconClick === void 0 ? void 0 : onIconClick();
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      },
      icon: "calendar",
      value: text,
      className: classNames('kxi-date-picker', className),
      contrast: contrast,
      placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : getDateFormat(),
      status: status,
      ref: mergeRefs([ref, inputRef])
    }, ingredients)), jsx(Popout, {
      isVisible: isOpen,
      direction: direction,
      internalPadding: 0,
      onEscape: close,
      onExternalClick: onExternal,
      onFocusOut: onFocusOut,
      anchorElement: popoutAnchorElement,
      preventFocus: true,
      ref: popoutRef,
      children: jsx(motion.div, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        },
        transition: {
          duration: 0.075
        },
        onMouseDown: () => setIsDateSelecting(true),
        onMouseUp: () => setIsDateSelecting(false),
        ref: calendarRef,
        className: "kxi-date-picker-popout-content kxi-calendar-component-wrapper kxi-calendar-wrapper orca kxi-normal-contrast",
        style: {
          height: 'auto',
          minHeight: 'auto'
        },
        children: jsx(Calendars, {
          dateArray: dateArray,
          setDateArray: setDateArray,
          modalType: modalType,
          setModalType: setModalType,
          minDate: minDate,
          maxDate: maxDate,
          locale: locale,
          contrast: contrast,
          selectedRangeDates: selectedRangeDates,
          setSelectedRangeDates: setSelectedRangeDates,
          onDateClick: handleDateClick,
          generateId: generateID,
          isStartFocused: true
        })
      })
    })]
  }));
});
DatePicker.displayName = 'DatePicker';

const _CalendarOutlinesOptions = ['shadow', 'border', 'none'];
const _CalendarModesOptions = ['relative', 'fixed'];
const _CalendarCustomListItemUnitOptions = ['day', 'week', 'month'];
const _TreePickerSelectionModeOptions = ['single-option', 'multiple'];
var TreePickerSelectionMode;
(function (TreePickerSelectionMode) {
  /** Only one option may be selected (or none) */
  TreePickerSelectionMode["SingleOption"] = "single-option";
  /** Any number of items (0+) may be selected */
  TreePickerSelectionMode["Multiple"] = "multiple";
})(TreePickerSelectionMode || (TreePickerSelectionMode = {}));

const ToolTipOverflowText = props => {
  const {
      text,
      title,
      tooltip,
      disabled = false,
      willAccountForIcon = false,
      spaceOnRight = 0,
      style,
      searchText,
      textRef: externalTextRef
    } = props,
    ingredients = __rest(props, ["text", "title", "tooltip", "disabled", "willAccountForIcon", "spaceOnRight", "style", "searchText", "textRef"]);
  const textRef = React.useRef(null);
  const {
    ref: resizeRef
  } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 0,
    handleHeight: false,
    handleWidth: true
  });
  const overflow = textRef.current !== null && textRef.current.clientWidth < textRef.current.scrollWidth;
  const toolTipWidth = textRef.current ? textRef.current.clientWidth - 12 : undefined;
  const textOverflow = jsx("p", {
    ref: mergeRefs([textRef, resizeRef, ...(externalTextRef ? [externalTextRef] : [])]),
    className: "kxi-tooltip-overflow-text",
    color: disabled ? 'disabled' : undefined,
    children: searchText ? jsx(EmphasizedText, {
      text: title,
      emphasized: searchText
    }) : title
  });
  return overflow || text ? jsx(ToolTipWrapper, Object.assign({}, tooltip, {
    text: text ? text : overflow ? title : undefined,
    title: overflow && text ? title : undefined,
    style: Object.assign({
      display: 'block',
      width: `calc(100% - ${willAccountForIcon ? 24 + spaceOnRight : spaceOnRight}px)`,
      height: '100%',
      whiteSpace: 'nowrap'
    }, style),
    innerStyle: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    maxWidth: toolTipWidth,
    space: 12
  }, ingredients, {
    children: textOverflow
  })) : textOverflow;
};
const EmphasizedText = props => {
  const {
    text,
    emphasized
  } = props;
  if (!text) {
    return null;
  }
  const lowercaseText = text.toLocaleLowerCase();
  const lowercaseEmphasized = emphasized.toLocaleLowerCase();
  const textArray = text.length === lowercaseText.length && emphasized.length === lowercaseEmphasized.length ? lowercaseText.split(lowercaseEmphasized) : text.split(emphasized); // Fall back to case-sensitive comparison
  const elements = [];
  let start = 0;
  for (const [i, lowercaseText] of textArray.entries()) {
    const substringLength = lowercaseText.length;
    const originalText = text.substring(start, start + substringLength);
    start += substringLength;
    const originalEmphasized = text.substring(start, start + emphasized.length);
    start += emphasized.length;
    elements.push(jsxs("span", {
      children: [originalText, i !== textArray.length - 1 && jsx("span", {
        className: "kxi-picker-emphasized-text",
        children: originalEmphasized
      })]
    }, `ttoverflow_${originalText}_${i}`));
  }
  return jsx(Fragment, {
    children: elements
  });
};

const SELECTION_MODE_PROP_DEFAULT_VALUE = TreePickerSelectionMode.Multiple;
const CATEGORY_SELECTION_BAR_WIDTH_PX = 6;
/**
 * Debounces {@link TreePickerEvents.onEndOfScroll onEndOfScroll} events.
 *
 * **NOTE**: The debounced event is fired with the props for the option/category with the **highest index**, NOT the most recent event.
 */
function useOnEndOfScrollDebounced(onEndOfScrollInput) {
  const onEndOfScrollDebouncedIndex = React__default.useRef(-1);
  const onEndOfScrollDebounced = useDebouncedCallback(props => {
    onEndOfScrollInput === null || onEndOfScrollInput === void 0 ? void 0 : onEndOfScrollInput(props);
    onEndOfScrollDebouncedIndex.current = -1;
  });
  return (index, props) => {
    if (index > onEndOfScrollDebouncedIndex.current) {
      onEndOfScrollDebouncedIndex.current = index;
      onEndOfScrollDebounced(props);
    }
  };
}

/**
 * @deprecated We shouldn't be using contexts here.
 * @see https://blog.jannikwempe.com/when-to-not-use-react-context-api-for-state
 */
const TreePickerContext = /*#__PURE__*/React__default.createContext(undefined);
function useTreePickerContext() {
  return React__default.useContext(TreePickerContext);
}
const reducer = (prevState, action) => {
  const state = clone(prevState);
  switch (action.type) {
    case 'categories_prop_change':
      handleCategoriesPropChange(state, action);
      break;
    case 'selected_ids_prop_change':
      handleSelectedIdsPropChange(state, action);
      break;
    case 'selection_mode_prop_change':
      handleSelectionModePropChange(state, action);
      break;
    case 'selection_toggle':
      handleSelectionToggle(state, action);
      break;
    case 'expansion_toggle':
      handleExpansionToggle(state, action);
      break;
    case 'query_change':
      handleQueryChange(state, action);
      break;
    default:
      ensureExhaustive(action);
  }
  updateSelections(state);
  updateExpansions(state);
  updateHasResults(state);
  return state;
};
/**
 * @deprecated This reducer complicates the TreePicker logic and hides that complexity.
 */
function useTreePickerReducer() {
  const [state, dispatch] = React__default.useReducer(reducer, {
    categories: [],
    selectedIds: new Set(),
    selectionMode: SELECTION_MODE_PROP_DEFAULT_VALUE,
    expandedIds: new Set(),
    visibleIds: new Set(),
    disabledIds: new Set(),
    query: '',
    selections: {},
    expansions: {},
    hasResults: true
  });
  return {
    state,
    dispatch
  };
}
function allIds(categories) {
  return lazy(bottomUpTraversal(categories)).map(({
    id
  }) => id).toSet();
}
function handleCategoriesPropChange(state, action) {
  const previousIds = allIds(state.categories);
  state.categories = action.categories.map(convertCategory);
  const currentIds = allIds(state.categories);
  const removedIds = difference(previousIds, currentIds);
  if (removedIds.size > 0) {
    state.selectedIds = difference(state.selectedIds, removedIds);
    state.expandedIds = difference(state.expandedIds, removedIds);
    state.visibleIds = difference(state.visibleIds, removedIds);
  }
  const newIds = difference(currentIds, previousIds);
  if (newIds.size === 0) return;
  resetVisibility(state);
  updateDisabledIds(state);
}
function convertCategory(category) {
  var _a, _b;
  return Object.assign(Object.assign({}, category), {
    id: (_a = category.id) !== null && _a !== void 0 ? _a : `${category.title} ${uuidv4()}`,
    options: clone(category.options),
    subcategories: ((_b = category.subcategories) !== null && _b !== void 0 ? _b : []).map(convertCategory)
  });
}
function handleSelectedIdsPropChange(state, action) {
  if (!action.selectedIds) return;
  state.selectedIds = new Set(action.selectedIds);
}
function handleSelectionModePropChange(state, action) {
  var _a;
  state.selectionMode = (_a = action.selectionMode) !== null && _a !== void 0 ? _a : SELECTION_MODE_PROP_DEFAULT_VALUE;
  if (state.selectionMode === TreePickerSelectionMode.SingleOption && state.selectedIds.size > 1) {
    const selectedId = first(state.selectedIds);
    state.selectedIds = new Set([selectedId]);
  }
}
function handleSelectionToggle(state, action) {
  if (state.disabledIds.has(action.id)) {
    return;
  }
  const item = lazy(bottomUpTraversal(state.categories)).find(({
    id
  }) => id === action.id);
  if (state.selectionMode === TreePickerSelectionMode.SingleOption) {
    if (item.type === 'option') {
      const nowSelected = !state.selectedIds.has(action.id);
      state.selectedIds = new Set(nowSelected ? [action.id] : []);
    }
    return;
  }
  if (item.type === 'option') {
    toggle(state.selectedIds, action.id);
    return;
  }
  const {
    category
  } = item;
  const categorySelectionState = state.selections[category.id];
  const isEnabled = item => !state.disabledIds.has(item.id);
  const isSelected = item => state.selectedIds.has(item.id);
  const shouldSelect = function () {
    switch (categorySelectionState) {
      case 'unchecked':
        return true;
      case 'checked':
        return false;
      case 'indeterminate':
        {
          const enabledUnselectedOption = lazy(bottomUpTraversal(category)).find(item => item.type === 'option' && isEnabled(item) && !isSelected(item));
          return enabledUnselectedOption != null;
        }
      default:
        ensureExhaustive(categorySelectionState);
    }
  }();
  for (const item of bottomUpTraversal(category)) {
    if (item.type === 'option' && isEnabled(item)) {
      addOrDelete(state.selectedIds, item.id, shouldSelect);
    }
  }
}
function handleExpansionToggle(state, action) {
  const shouldExpand = state.expansions[action.id] !== 'expanded';
  addOrDelete(state.expandedIds, action.id, shouldExpand);
  const category = lazy(bottomUpTraversal(state.categories)).map(item => item.type === 'category' ? item.category : undefined).filter(isNotNullish).find(category => category.id === action.id);
  // If collapsing, hide all descendants.
  if (!shouldExpand) {
    const descendantIds = Array.from(bottomUpTraversal(category)).map(({
      id
    }) => id).filter(id => id !== category.id);
    state.visibleIds = difference(state.visibleIds, descendantIds);
    return;
  }
  // If expanding while searching, show only the direct children of the expanded category.
  // We don't change the visibility state of the other descendants, so they'll continue
  // to be shown or hidden based on the current query.
  if (state.query !== '') {
    for (const childId of getDirectChildIds(category)) {
      state.visibleIds.add(childId);
    }
    return;
  }
  // If expanding while not searching, show all descendants whose ancestor categories are all expanded.
  const expandedCategories = Array.from(topDownTraversal(category)).map(item => item.type === 'category' ? item.category : undefined).filter(isNotNullish).filter(category => state.expandedIds.has(category.id));
  for (const expandedCategory of expandedCategories) {
    if (!state.visibleIds.has(expandedCategory.id)) {
      continue;
    }
    state.visibleIds = union(state.visibleIds, getDirectChildIds(expandedCategory));
  }
}
function handleQueryChange(state, {
  query
}) {
  state.query = query;
  resetVisibility(state);
}
function updateSelections(state) {
  const isSelected = id => state.selectedIds.has(id);
  state.selections = {};
  for (const child of bottomUpTraversal(state.categories)) {
    if (child.type === 'option') {
      state.selections[child.id] = isSelected(child.id) ? 'checked' : 'unchecked';
      continue;
    }
    const {
      category
    } = child;
    const childSelections = getDirectChildIds(category).map(id => state.selections[id]);
    state.selections[category.id] = childSelections.length > 0 ? childSelections.every(s => s === 'checked') ? 'checked' : childSelections.some(s => s !== 'unchecked') ? 'indeterminate' : 'unchecked' : 'unchecked';
  }
}
function updateExpansions(state) {
  state.expansions = {};
  for (const item of bottomUpTraversal(state.categories)) {
    if (item.type === 'option') continue;
    const {
      category,
      id
    } = item;
    state.expansions[id] = !state.expandedIds.has(id) ? 'collapsed' : getDirectChildIds(category).every(childId => state.visibleIds.has(childId)) ? 'expanded' : 'indeterminate';
  }
}
function updateHasResults(state) {
  state.hasResults = state.visibleIds.size > 0;
}
function updateDisabledIds(state) {
  state.disabledIds = new Set();
  for (const id of getDisabledIds(state.categories, false)) {
    state.disabledIds.add(id);
  }
}
function* getDisabledIds(itemOrItems, forceDisabled) {
  if (Array.isArray(itemOrItems)) {
    for (const item of itemOrItems) {
      yield* getDisabledIds(item, forceDisabled);
    }
    return;
  }
  const item = itemOrItems;
  forceDisabled || (forceDisabled = !!item.disabled);
  if (forceDisabled) {
    yield item.id;
  }
  if ('options' in item) {
    yield* getDisabledIds(item.options, forceDisabled);
    yield* getDisabledIds(item.subcategories, forceDisabled);
  }
}
function resetVisibility(state) {
  if (state.query !== '') {
    resetVisibilityBasedOnQuery(state);
    resetExpansionBasedOnVisibility(state);
  } else {
    resetVisibilityBasedOnExpansion(state);
  }
}
function resetVisibilityBasedOnExpansion(state) {
  state.visibleIds = new Set();
  const childToParent = {};
  for (const item of bottomUpTraversal(state.categories)) {
    if (item.type === 'option') continue;
    const {
      category
    } = item;
    for (const childId of getDirectChildIds(category)) {
      childToParent[childId] = category.id;
    }
  }
  for (const child of topDownTraversal(state.categories)) {
    const parentId = childToParent[child.id];
    if (parentId == null || state.visibleIds.has(parentId) && state.expandedIds.has(parentId)) {
      state.visibleIds.add(child.id);
    }
  }
}
function resetVisibilityBasedOnQuery(state) {
  const query = state.query.toLocaleLowerCase();
  const titleMatchesQuery = obj => obj.title.toLocaleLowerCase().includes(query);
  state.visibleIds = new Set();
  const isVisible = id => state.visibleIds.has(id);
  const categoryMatchesQuery = category => titleMatchesQuery(category) || getDirectChildIds(category).some(isVisible);
  for (const child of bottomUpTraversal(state.categories)) {
    const matchesQuery = child.type === 'option' ? titleMatchesQuery(child.option) : categoryMatchesQuery(child.category);
    if (matchesQuery) {
      state.visibleIds.add(child.id);
    }
  }
}
function resetExpansionBasedOnVisibility(state) {
  for (const item of bottomUpTraversal(state.categories)) {
    if (item.type === 'option') continue;
    const {
      category
    } = item;
    const areAnyChildrenVisible = getDirectChildIds(category).some(id => state.visibleIds.has(id));
    addOrDelete(state.expandedIds, category.id, areAnyChildrenVisible);
  }
}
function getDirectChildIds(category) {
  return [...category.options, ...category.subcategories].map(({
    id
  }) => id);
}
function* bottomUpTraversal(category) {
  var _a;
  if (Array.isArray(category)) {
    for (const cat of category) {
      yield* bottomUpTraversal(cat);
    }
    return;
  }
  yield* bottomUpTraversal((_a = category.subcategories) !== null && _a !== void 0 ? _a : []);
  for (const option of category.options) {
    yield {
      type: 'option',
      id: option.id,
      option
    };
  }
  yield Object.assign({
    type: 'category',
    category
  }, 'id' in category ? {
    id: category.id
  } : {});
}
function* topDownTraversal(category) {
  var _a;
  if (Array.isArray(category)) {
    for (const cat of category) {
      yield* topDownTraversal(cat);
    }
    return;
  }
  yield {
    type: 'category',
    id: category.id,
    category
  };
  for (const option of category.options) {
    yield {
      type: 'option',
      id: option.id,
      option
    };
  }
  yield* topDownTraversal((_a = category.subcategories) !== null && _a !== void 0 ? _a : []);
}

const TreePickerOption = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(TreePickerOption, props);
  const {
    color,
    option,
    level,
    containerElement,
    onVisibilityChange,
    onKeyboardNavigation
  } = props;
  const {
    state,
    dispatch
  } = useTreePickerContext();
  const [textElement, setTextElement] = React.useState(null);
  const {
    ref: visibilityRef
  } = useVisibilityDetectorRef({
    root: containerElement,
    threshold: [1],
    onChange: visibility => onVisibilityChange({
      visibility,
      path: [option.id],
      type: 'option'
    })
  });
  const dispatchSelectionToggle = () => dispatch({
    type: 'selection_toggle',
    id: option.id
  });
  const onClick = e => {
    e.stopPropagation();
    e.preventDefault();
    dispatchSelectionToggle();
  };
  const singleSelect = state.selectionMode === TreePickerSelectionMode.SingleOption;
  const isVisible = state.visibleIds.has(option.id);
  const isSelected = state.selectedIds.has(option.id);
  const isDisabled = state.disabledIds.has(option.id);
  const optionRow = jsxs("div", Object.assign({}, debugService.componentProps, {
    ref: mergeRefs([ref, visibilityRef]),
    className: classNames('kxi-tree-picker-option', {
      'kxi-tree-picker-hidden': !isVisible,
      'kxi-tree-picker-item-single-select': singleSelect,
      'kxi-tree-picker-option-selected': isSelected,
      'kxi-disabled-alpha': isDisabled
    }),
    "data-id": option.id,
    "data-option-id": option.id,
    //Level * 27px accounts for 24px from the icon + 3px of spacing.
    //63px lines up with the padding for a TreePickerItem that is one level lower.
    style: {
      paddingLeft: 63 + level * 27
    },
    onClick: onClick,
    role: "treeitem",
    "aria-disabled": isDisabled,
    "aria-expanded": undefined,
    "aria-hidden": !isVisible,
    "aria-selected": isSelected,
    "aria-setsize": -1,
    "aria-label": option.title,
    "aria-level": level + 1,
    tabIndex: -1,
    onKeyDown: e => {
      switch (e.key) {
        case ' ':
        case 'Enter':
          onClick(e);
          break;
        case 'Home':
          e.preventDefault();
          onKeyboardNavigation('first');
          break;
        case 'ArrowUp':
          e.preventDefault();
          onKeyboardNavigation('previous');
          break;
        case 'ArrowDown':
          e.preventDefault();
          onKeyboardNavigation('next');
          break;
        case 'End':
          e.preventDefault();
          onKeyboardNavigation('last');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onKeyboardNavigation('ascend');
          break;
      }
    },
    children: [!singleSelect && jsx(Checkbox, {
      color: color,
      checked: state.selectedIds.has(option.id),
      onClick: onClick,
      onChange: dispatchSelectionToggle,
      disabled: isDisabled,
      tabIndex: -1
    }), jsx("div", {
      className: classNames('kxi-text-wrapper', {
        'kxi-tree-picker-item-bold': option.bold
      }),
      children: jsx(ToolTipOverflowText, {
        title: option.title,
        tooltip: {
          text: option.title
        },
        spaceOnRight: 12,
        searchText: state.query,
        textRef: setTextElement
      })
    }), singleSelect && isSelected && jsx(XIIcon, {
      icon: "circle_check_filled",
      color: "blue_primary",
      className: "kxi-tree-picker-option-check-icon"
    })]
  }));
  return option.tooltip ? jsx(ToolTipWrapper, Object.assign({
    stretch: true,
    anchorElement: textElement,
    direction: 7
  }, option.tooltip, {
    children: optionRow
  })) : optionRow;
});
TreePickerOption.displayName = 'TreePickerOption';

const TreePickerItem = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _a, _b, _c;
  const debugService = useDebugService(TreePickerItem, props);
  const {
    boldCategoryTitles,
    category,
    categoryIsFetchingDataMap,
    color,
    containerElement,
    endOfScrollOffset,
    hasGreyBackground,
    level,
    onExpansionChange: onExpansionChangeInput,
    onVisibilityChange,
    onEndOfScroll: onEndOfScrollInput,
    onKeyboardNavigation
  } = props;
  const {
    state,
    dispatch
  } = useTreePickerContext();
  debugService.watch(state);
  const [textElement, setTextElement] = React.useState(null);
  const treeItemRef = React.useRef(null);
  const {
    ref: optionsRef,
    callbackRefs: optionRefs
  } = useArrayRef(category.options.length, () => null);
  const {
    ref: subcategoriesRef,
    callbackRefs: subcategoryRefs
  } = useArrayRef(category.subcategories.length, () => null);
  const {
    ref: visibilityRef
  } = useVisibilityDetectorRef({
    root: containerElement,
    threshold: [1],
    onChange: visibility => onVisibilityChange === null || onVisibilityChange === void 0 ? void 0 : onVisibilityChange({
      visibility,
      path: [category.id],
      type: 'category'
    })
  });
  const isSingleSelect = state.selectionMode === TreePickerSelectionMode.SingleOption;
  const expansionState = state.expansions[category.id];
  const isExpanded = expansionState === 'expanded';
  const selectionState = state.selections[category.id];
  const isCategorySelectedInSingleSelect = isSingleSelect && expansionState === 'collapsed' && selectionState !== 'unchecked';
  const onExpansionChange = () => {
    onExpansionChangeInput === null || onExpansionChangeInput === void 0 ? void 0 : onExpansionChangeInput({
      isExpanded: !isExpanded,
      id: category.id
    });
    dispatch({
      type: 'expansion_toggle',
      id: category.id
    });
  };
  const onTitleClick = e => {
    e.stopPropagation();
    e.preventDefault();
    if (isSingleSelect) {
      onExpansionChange();
    } else {
      dispatch({
        type: 'selection_toggle',
        id: category.id
      });
    }
  };
  const isFetchingOptionData = ((_a = categoryIsFetchingDataMap[category.id]) === null || _a === void 0 ? void 0 : _a.options) === true;
  const isFetchingCategoryData = ((_b = categoryIsFetchingDataMap[category.id]) === null || _b === void 0 ? void 0 : _b.categories) === true;
  const isDisabled = state.disabledIds.has(category.id);
  const isVisible = state.visibleIds.has(category.id);
  /** Level * 27px accounts for 24px from the icon + 3px of spacing */
  const left = 6 + level * 27 + (isSingleSelect && !isCategorySelectedInSingleSelect ? CATEGORY_SELECTION_BAR_WIDTH_PX : 0);
  const currentCategoryRow = jsxs("div", {
    ref: mergeRefs([ref, treeItemRef, visibilityRef]),
    className: classNames('kxi-tree-picker-category', {
      'kxi-tree-picker-hidden': !isVisible,
      'kxi-disabled-alpha': isDisabled,
      'kxi-tree-picker-item-single-select': isSingleSelect,
      'kxi-tree-picker-category-selected': isCategorySelectedInSingleSelect
    }),
    "data-expansion-state": expansionState,
    "data-selection-state": selectionState,
    "data-id": category.id,
    "data-category-id": category.id,
    style: Object.assign({
      paddingLeft: left
    }, {
      '--category-selection-bar-width': CATEGORY_SELECTION_BAR_WIDTH_PX + 'px'
    }),
    onClick: onTitleClick,
    role: "treeitem",
    "aria-disabled": isDisabled,
    "aria-expanded": isExpanded,
    "aria-selected": selectionState === 'checked',
    "aria-setsize": -1,
    "aria-label": category.title,
    "aria-level": level + 1,
    tabIndex: -1,
    onKeyDown: e => {
      var _a;
      switch (e.key) {
        case ' ':
        case 'Enter':
          onTitleClick(e);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (isExpanded) {
            onExpansionChange();
          } else {
            onKeyboardNavigation('ascend', e);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (isExpanded) {
            (_a = optionsRef.current.concat(subcategoriesRef.current)[0]) === null || _a === void 0 ? void 0 : _a.focus();
          } else {
            onExpansionChange();
          }
          break;
        case 'Home':
          e.preventDefault();
          onKeyboardNavigation('first', e);
          break;
        case 'ArrowUp':
          e.preventDefault();
          onKeyboardNavigation('previous', e);
          break;
        case 'ArrowDown':
          e.preventDefault();
          onKeyboardNavigation('next', e);
          break;
        case 'End':
          e.preventDefault();
          onKeyboardNavigation('last', e);
          break;
      }
    },
    children: [jsx(XIIcon, {
      icon: !isExpanded ? 'tree_picker_expand' : 'tree_picker_collapse',
      onClick: e => {
        e.stopPropagation();
        onExpansionChange();
      },
      id: `kxi-tree-picker-expand-icon-${category.id}`
    }), !isSingleSelect && jsx(Checkbox, {
      tabIndex: -1,
      color: color,
      isIndeterminate: selectionState === 'indeterminate',
      checked: selectionState !== 'unchecked',
      disabled: isDisabled,
      onClick: e => {
        e.stopPropagation();
        dispatch({
          type: 'selection_toggle',
          id: category.id
        });
      }
    }), jsx("div", {
      className: classNames('kxi-text-wrapper', {
        'kxi-tree-picker-item-bold': (_c = category.bold) !== null && _c !== void 0 ? _c : boldCategoryTitles
      }),
      children: jsx(ToolTipOverflowText, {
        title: category.title,
        tooltip: {
          text: category.title
        },
        spaceOnRight: 12,
        searchText: state.query,
        textRef: setTextElement
      })
    })]
  });
  const loadingAnimation = jsx(LoadingAnimation, {
    className: classNames('kxi-tree-picker-loading-animation', {
      'kxi-tree-picker-hidden': expansionState !== 'expanded'
    }),
    width: "fit-content",
    style: {
      paddingLeft: 63 + level * 27 - 9
    }
  });
  const onEndOfScrollOptionDebounced = useOnEndOfScrollDebounced(onEndOfScrollInput);
  const onEndOfScrollCategoryDebounced = useOnEndOfScrollDebounced(onEndOfScrollInput);
  const onDescendantKeyboardNavigation = (type, i) => {
    var _a, _b, _c, _d, _e;
    const items = optionsRef.current.concat(subcategoriesRef.current);
    switch (type) {
      case 'ascend':
        (_a = treeItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        break;
      case 'first':
        (_b = items[0]) === null || _b === void 0 ? void 0 : _b.focus();
        break;
      case 'previous':
        if (i !== 0) {
          (_c = items[i - 1]) === null || _c === void 0 ? void 0 : _c.focus();
        }
        break;
      case 'next':
        if (i !== items.length - 1) {
          (_d = items[i + 1]) === null || _d === void 0 ? void 0 : _d.focus();
        }
        break;
      case 'last':
        (_e = items[items.length - 1]) === null || _e === void 0 ? void 0 : _e.focus();
        break;
    }
  };
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: classNames('kxi-tree-picker-item', 'kxi-grey', {
      'kxi-active': hasGreyBackground
    }),
    children: [category.tooltip ? jsx(ToolTipWrapper, Object.assign({
      stretch: true,
      anchorElement: textElement,
      direction: 7
    }, category.tooltip, {
      children: currentCategoryRow
    })) : currentCategoryRow, isVisible && jsxs("div", {
      children: [category.options.map((option, i) => jsx(TreePickerOption, {
        ref: optionRefs[i],
        containerElement: containerElement,
        option: option,
        color: color,
        level: level,
        onVisibilityChange: ({
          visibility,
          path,
          type
        }) => {
          if (!isExpanded) {
            return;
          }
          const newPath = [...path, category.id];
          if (visibility === 'full' && i >= category.options.length - 1 - endOfScrollOffset && !isFetchingOptionData) {
            onEndOfScrollOptionDebounced(i, {
              path: newPath,
              type
            });
          }
        },
        onKeyboardNavigation: type => {
          onDescendantKeyboardNavigation(type, i);
        }
      }, option.id)), isFetchingOptionData && loadingAnimation, category.subcategories.map((subcategory, i) => jsx(TreePickerItem, {
        ref: subcategoryRefs[i],
        boldCategoryTitles: boldCategoryTitles,
        category: subcategory,
        categoryIsFetchingDataMap: categoryIsFetchingDataMap,
        color: color,
        containerElement: containerElement,
        endOfScrollOffset: endOfScrollOffset,
        hasGreyBackground: hasGreyBackground,
        level: level + 1,
        onExpansionChange: onExpansionChangeInput,
        onEndOfScroll: ({
          path,
          type
        }) => onEndOfScrollInput({
          path: [...path, category.id],
          type
        }),
        onVisibilityChange: ({
          visibility,
          path,
          type
        }) => {
          if (!isExpanded) {
            return;
          }
          const newPath = [...path, category.id];
          if (visibility === 'full' && i >= category.subcategories.length - 1 - endOfScrollOffset && !isFetchingCategoryData) {
            onEndOfScrollCategoryDebounced(i, {
              path: newPath,
              type
            });
          }
        },
        onKeyboardNavigation: type => {
          onDescendantKeyboardNavigation(type, i + optionsRef.current.length);
        }
      }, subcategory.id)), isFetchingCategoryData && loadingAnimation]
    })]
  }));
});
TreePickerItem.displayName = 'TreePickerItem';

class TreePickerProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", Array)], TreePickerProps.prototype, "categories", void 0);
__decorate([Bound(), __metadata("design:type", String)], TreePickerProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TreePickerProps.prototype, "categoryIsFetchingDataMap", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TreePickerProps.prototype, "endOfScrollOffset", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TreePickerProps.prototype, "isFetchingData", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TreePickerProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", Array)], TreePickerProps.prototype, "selectedIds", void 0);
__decorate([Bound(), __metadata("design:type", String)], TreePickerProps.prototype, "selectionMode", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TreePickerProps.prototype, "boldCategoryTitles", void 0);
class TreePickerEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], TreePickerEvents.prototype, "onItemsChanged", void 0);
__decorate([Bound(), __metadata("design:type", Function)], TreePickerEvents.prototype, "onSearchChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], TreePickerEvents.prototype, "onExpansionChange", void 0);
__decorate([Bound(), __metadata("design:type", Function)], TreePickerEvents.prototype, "onEndOfScroll", void 0);
/**
 * Description: A picker of items sorted by categories.
 */
const TreePicker = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(TreePicker, props);
  const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      className,
      children,
      categoryIsFetchingDataMap = {},
      categories: categoriesInput,
      color = 'CX',
      endOfScrollOffset = 0,
      isFetchingData,
      stretch = false,
      selectedIds: selectedIdsInput,
      selectionMode = SELECTION_MODE_PROP_DEFAULT_VALUE,
      boldCategoryTitles = false,
      _query: queryInput,
      onSearchChange,
      onEndOfScroll: onEndOfScrollInput,
      onItemsChanged,
      onExpansionChange
    } = props,
    ingredients = __rest(props, ['aria-label', 'aria-labelledby', "className", "children", "categoryIsFetchingDataMap", "categories", "color", "endOfScrollOffset", "isFetchingData", "stretch", "selectedIds", "selectionMode", "boldCategoryTitles", "_query", "onSearchChange", "onEndOfScroll", "onItemsChanged", "onExpansionChange"]);
  const stateAndDispatch = useTreePickerReducer();
  const {
    state,
    dispatch
  } = stateAndDispatch;
  const [scrollElement, setScrollElement] = React.useState(null);
  useWarnOnceIf(typeof ariaLabel !== typeof ariaLabelledBy, debugService.format('Either aria-label or aria-labelledby (but not both) should be provided for accessibility'));
  useOnChangeCallback(selectionMode, {
    emitOnInit: true
  }, selectionMode => dispatch({
    type: 'selection_mode_prop_change',
    selectionMode
  }));
  useOnChangeCallback(categoriesInput, {
    emitOnInit: true
  }, categories => dispatch({
    type: 'categories_prop_change',
    categories
  }));
  useOnChangeCallback(selectedIdsInput, {
    emitOnInit: true,
    deepEqualityCheck: true
  }, selectedIds => dispatch({
    type: 'selected_ids_prop_change',
    selectedIds
  }));
  useOnChangeCallback(state.selectedIds, {
    deepEqualityCheck: true
  }, selectedIds => onItemsChanged === null || onItemsChanged === void 0 ? void 0 : onItemsChanged({
    selectedIds: Array.from(selectedIds).sort()
  }));
  React.useEffect(() => {
    dispatch({
      type: 'query_change',
      query: queryInput !== null && queryInput !== void 0 ? queryInput : ''
    });
  }, [queryInput]);
  const onQueryChange = query => {
    onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange({
      query
    });
    dispatch({
      type: 'query_change',
      query
    });
  };
  const onEndOfScrollDebounced = useOnEndOfScrollDebounced(onEndOfScrollInput);
  const {
    ref: categoriesRef,
    callbackRefs: categoryRefs
  } = useArrayRef(state.categories.length, () => null);
  const [containsFocus, setContainsFocus] = React.useState(false);
  const treeRef = React.useRef(null);
  return jsx(TreePickerContext.Provider, {
    value: stateAndDispatch,
    children: jsxs("div", Object.assign({}, debugService.componentProps, ingredients, {
      ref: ref,
      className: classNames('kxi-tree-picker', className, {
        'kxi-stretch': stretch
      }),
      children: [jsx("div", {
        className: "kxi-search",
        children: jsx(SearchBox, {
          hasOutline: false,
          stretch: true,
          _stretchHeight: true,
          onChange: e => onQueryChange(e.target.value),
          onCancelClick: () => onQueryChange(''),
          value: state.query
        })
      }), jsxs("div", {
        className: "kxi-tree-picker-content",
        ref: mergeRefs([treeRef, setScrollElement]),
        role: "tree",
        "aria-label": ariaLabel,
        "aria-multiselectable": selectionMode === TreePickerSelectionMode.Multiple,
        tabIndex: containsFocus ? -1 : 0,
        onBlur: e => {
          var _a;
          if (!((_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
            setContainsFocus(false);
          }
        },
        onFocus: e => {
          const treeElement = treeRef.current;
          if (!treeElement) {
            return;
          }
          if (e.target === treeElement && !(treeElement === null || treeElement === void 0 ? void 0 : treeElement.contains(e.relatedTarget))) {
            setContainsFocus(true);
            // Move focus to first selected item or first item
            (() => {
              var _a;
              const treeItemSelector = '[role=treeitem]:not([aria-hidden=true])';
              const firstSelectedOption = treeElement.querySelector(`${treeItemSelector}[aria-selected=true]`);
              const firstSelectedCategory = treeElement.querySelector(`${treeItemSelector}[data-selection-state=checked],${treeItemSelector}[data-selection-state=indeterminate]`);
              const firstItem = categoriesRef.current[0];
              const toFocus = (_a = firstSelectedOption !== null && firstSelectedOption !== void 0 ? firstSelectedOption : firstSelectedCategory) !== null && _a !== void 0 ? _a : firstItem;
              toFocus === null || toFocus === void 0 ? void 0 : toFocus.focus();
            })();
          }
        },
        children: [jsx("div", {
          style: {
            height: '100%',
            minWidth: 'fit-content'
          },
          children: state.categories.map((category, index) => jsx(TreePickerItem, {
            ref: categoryRefs[index],
            category: category,
            categoryIsFetchingDataMap: categoryIsFetchingDataMap,
            color: color,
            containerElement: scrollElement,
            endOfScrollOffset: endOfScrollOffset,
            hasGreyBackground: index % 2 === 0,
            boldCategoryTitles: boldCategoryTitles,
            onExpansionChange: onExpansionChange,
            level: 0,
            onEndOfScroll: ({
              path,
              type
            }) => onEndOfScrollInput === null || onEndOfScrollInput === void 0 ? void 0 : onEndOfScrollInput({
              path,
              type
            }),
            onVisibilityChange: ({
              visibility,
              type,
              path
            }) => {
              if (visibility === 'full' && index >= state.categories.length - 1 - endOfScrollOffset && !isFetchingData) {
                onEndOfScrollDebounced(index, {
                  path,
                  type
                });
              }
            },
            onKeyboardNavigation: type => {
              var _a, _b, _c, _d;
              switch (type) {
                case 'first':
                  (_a = categoriesRef.current[0]) === null || _a === void 0 ? void 0 : _a.focus();
                  break;
                case 'previous':
                  if (index !== 0) {
                    (_b = categoriesRef.current[index - 1]) === null || _b === void 0 ? void 0 : _b.focus();
                  }
                  break;
                case 'next':
                  if (index !== categoriesRef.current.length - 1) {
                    (_c = categoriesRef.current[index + 1]) === null || _c === void 0 ? void 0 : _c.focus();
                  }
                  break;
                case 'last':
                  (_d = categoriesRef.current[categoriesRef.current.length - 1]) === null || _d === void 0 ? void 0 : _d.focus();
                  break;
              }
            }
          }, category.id))
        }), isFetchingData && jsx(LoadingAnimation, {
          className: "kxi-tree-picker-loading-animation"
        }), jsx("div", {
          style: {
            display: state.hasResults || isFetchingData ? 'none' : 'flex'
          },
          className: classNames('kxi-no-results', stretch && 'kxi-stretch'),
          children: jsx(Slot, {
            "data-testid": "slot",
            name: "children",
            reactChildren: children
          })
        })]
      })]
    }))
  });
});
TreePicker.displayName = 'TreePicker';
/**
 * @deprecated This is unintuitive. Prefer using useEffect.
 */
function useOnChangeCallback(value, callbackOrOptions, possibleCallback) {
  const {
    options,
    callback
  } = typeof callbackOrOptions === 'function' ? {
    options: {},
    callback: callbackOrOptions
  } : {
    options: callbackOrOptions,
    callback: possibleCallback
  };
  const {
    emitOnInit = false,
    deepEqualityCheck = false
  } = options;
  const prevValueRef = React.useRef(value);
  const isMountRef = React.useRef(true);
  React.useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      if (emitOnInit) {
        callback(value);
      } else {
        return;
      }
    }
    const prevValue = prevValueRef.current;
    const prevEqualsCurrent = deepEqualityCheck ? deepEqual(value, prevValue) : value === prevValue;
    if (!prevEqualsCurrent) {
      callback(value);
    }
    prevValueRef.current = value;
  });
}

/**
 * Displays a toggle that controls a {@link Popout}.
 *
 * @example
 * ```tsx
 * <PopoutToggle
 *   Toggle={DefaultButton}
 *   toggleProps={{ text: "My button's text" }}
 *   isOpen={isOpen}
 *   onIsOpenChange={({ isOpen }) => setIsOpen(isOpen)}
 *   popoutProps={{ direction: 'bottom-middle' }}
 * >
 *   This is shown in the Popout.
 * </PopoutToggle>
 * ```
 */
function PopoutToggle(props) {
  const {
    children,
    toggleProps,
    isOpen,
    Toggle,
    popoutProps,
    onIsOpenChange: onIsOpenChangeInput
  } = props;
  throwIfRequiredPropNotProvided('PopoutToggle', {
    toggleProps
  });
  const [toggleElement, setToggleElement] = React__default.useState(null);
  const toggleRef = ref => setToggleElement(ref instanceof HTMLElement || ref === null ? ref : ref.current);
  const onIsOpenChange = (isOpen, withKeyboard, source) => onIsOpenChangeInput === null || onIsOpenChangeInput === void 0 ? void 0 : onIsOpenChangeInput({
    isOpen,
    withKeyboard,
    source
  });
  const onClose = (withKeyboard, source) => onIsOpenChange(false, withKeyboard, source);
  const onToggleIsOpen = (withKeyboard, source) => onIsOpenChange(!isOpen, withKeyboard, source);
  return jsxs(Fragment, {
    children: [jsx(Toggle, Object.assign({}, toggleProps, {
      ref: toggleProps.ref ? mergeRefs([toggleProps.ref, toggleRef]) : toggleRef,
      onClick: e => {
        var _a;
        (_a = toggleProps.onClick) === null || _a === void 0 ? void 0 : _a.call(toggleProps, e);
        onToggleIsOpen(false, 'toggle');
      },
      onKeyDown: e => {
        var _a;
        (_a = toggleProps.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(toggleProps, e);
        if (e.key === 'Enter' || e.key === ' ') {
          onToggleIsOpen(true, 'toggle');
        }
      }
    })), jsx(Popout, Object.assign({}, popoutProps, {
      anchorElement: toggleElement,
      children: children,
      isVisible: isOpen,
      onExternalClick: ({
        event
      }) => {
        var _a;
        (_a = popoutProps === null || popoutProps === void 0 ? void 0 : popoutProps.onExternalClick) === null || _a === void 0 ? void 0 : _a.call(popoutProps, {
          event
        });
        if (!(toggleElement === null || toggleElement === void 0 ? void 0 : toggleElement.contains(event.target))) {
          onClose(false, 'popout');
        }
      },
      onEscape: e => {
        var _a;
        (_a = popoutProps === null || popoutProps === void 0 ? void 0 : popoutProps.onEscape) === null || _a === void 0 ? void 0 : _a.call(popoutProps, e);
        onClose(true, 'popout');
      }
    }))]
  });
}
PopoutToggle.displayName = 'PopoutToggle';

const Picker = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  var _a;
  const {
      categories,
      color,
      filterProps,
      hasButtons = false,
      id: treePickerId,
      localization: {
        cancel,
        apply,
        clearAll
      },
      onCancel,
      onConfirm,
      onSearchChange,
      onItemsChanged,
      placeholder,
      wrapperRef,
      selectedIds,
      stretch
    } = props,
    treePickerIngredients = __rest(props, ["categories", "color", "filterProps", "hasButtons", "id", "localization", "onCancel", "onConfirm", "onSearchChange", "onItemsChanged", "placeholder", "wrapperRef", "selectedIds", "stretch"]);
  return jsxs("div", {
    ref: wrapperRef,
    className: `kxi-hierarchy-picker ${stretch ? 'kxi-stretch' : ''}`,
    children: [jsxs("div", {
      className: "kxi-hierarchy-picker-header",
      children: [jsx(Dropdown, Object.assign({}, filterProps, {
        visualState: "condensed",
        id: `${treePickerId}-dropdown`,
        internalProps: Object.assign(Object.assign({}, filterProps === null || filterProps === void 0 ? void 0 : filterProps.internalProps), {
          menu: Object.assign(Object.assign({}, (_a = filterProps === null || filterProps === void 0 ? void 0 : filterProps.internalProps) === null || _a === void 0 ? void 0 : _a.menu), {
            id: `${treePickerId}-dropdown-menu`
          })
        })
      })), jsx(DefaultButton, {
        fill: "link",
        text: clearAll,
        onClick: () => {
          onItemsChanged === null || onItemsChanged === void 0 ? void 0 : onItemsChanged({
            selectedIds: []
          });
        }
      })]
    }), jsx("div", {
      className: "kxi-hierarchy-picker-tree",
      children: jsx(TreePicker, Object.assign({}, treePickerIngredients, {
        ref: ref,
        categories: categories,
        color: color,
        id: treePickerId,
        onItemsChanged: onItemsChanged,
        onSearchChange: onSearchChange,
        placeholder: placeholder,
        selectedIds: selectedIds,
        stretch: stretch
      }))
    }), hasButtons ? jsxs("div", {
      className: "kxi-hierarchy-picker-footer",
      children: [jsx(DefaultButton, {
        text: cancel,
        fill: "text",
        buttonSize: "small",
        onClick: onCancel
      }), jsx(DefaultButton, {
        text: apply,
        buttonSize: "small",
        onClick: onConfirm
      })]
    }) : null]
  });
});

const defaultSelectedIds$1 = Object.freeze([]);
const PickerPopout = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const {
      categories: treePickerCategories,
      color = 'CX',
      contrast = 'normal',
      disabled = false,
      id: treePickerId,
      label,
      onItemsChanged,
      onOpenChange: onOpenChangeInput,
      onSearchChange: onSearchChangeInput,
      placeholder,
      selectedIds: selectedIdsInput = defaultSelectedIds$1,
      stretch: treePickerStretch = false,
      toggleProps,
      toggleType = 'pill',
      tooltip
    } = props,
    treePickerIngredients = __rest(props, ["categories", "color", "contrast", "disabled", "id", "label", "onItemsChanged", "onOpenChange", "onSearchChange", "placeholder", "selectedIds", "stretch", "toggleProps", "toggleType", "tooltip"]);
  const [isOpen, setIsOpen] = React__default.useState(false);
  const [query, setQuery] = React__default.useState(undefined);
  const [selectedIds, setSelectedIds] = useValueBackedState(selectedIdsInput);
  const popoutContentWrapperRef = React__default.useRef(null);
  const toggleRef = React__default.useRef(null);
  const {
    ref: focusTrapRef
  } = useFocusTrap();
  /**
   * If a category and all of its child options and subcategories are selected, then only the category's title is included.
   */
  const selectedTitles = React__default.useMemo(() => {
    const selectedIdsSet = new Set(selectedIds);
    const isItemSelected = item => {
      if ('options' in item) {
        const {
          id,
          options,
          subcategories = []
        } = item;
        const hasOptions = options.length > 0;
        const areOptionsSelected = options.every(isItemSelected);
        const hasSubcategories = subcategories.length > 0;
        const areSubcategoriesSelected = subcategories.every(isItemSelected);
        return id !== undefined && selectedIdsSet.has(id) || (hasOptions ? hasSubcategories ? areOptionsSelected && areSubcategoriesSelected // options and subcategories
        : areOptionsSelected // only options
        : hasSubcategories ? areSubcategoriesSelected // only subcategories
        : false) // neither
        ;
      }
      return selectedIdsSet.has(item.id);
    };
    const selectedItems = function getSelected(items) {
      return items.flatMap(category => {
        var _a;
        if (isItemSelected(category)) {
          return [category];
        }
        if ('options' in category) {
          return [...getSelected(category.options), ...getSelected((_a = category.subcategories) !== null && _a !== void 0 ? _a : [])];
        }
        return [];
      });
    }(treePickerCategories);
    return selectedItems.map(({
      title
    }) => title);
  }, [selectedIds, treePickerCategories]);
  const canOpenOrClose = newIsOpen => {
    var _a;
    return !(disabled || (toggleType === 'pill' || toggleType === 'layered') && ((_a = toggleProps === null || toggleProps === void 0 ? void 0 : toggleProps.inputField) === null || _a === void 0 ? void 0 : _a.readOnly) || newIsOpen === isOpen);
  };
  const onIsOpenChange = newIsOpen => {
    if (!canOpenOrClose(newIsOpen)) {
      return false;
    }
    onOpenChangeInput === null || onOpenChangeInput === void 0 ? void 0 : onOpenChangeInput({
      isOpen: newIsOpen
    });
    setIsOpen(newIsOpen);
    return true;
  };
  const onClose = () => {
    var _a, _b;
    const succeeded = onIsOpenChange(false);
    if (succeeded) {
      setQuery(undefined);
      (_b = (_a = toggleRef.current) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
    }
    return succeeded;
  };
  const onCancel = () => {
    if (onClose()) {
      setSelectedIds(selectedIdsInput);
    }
  };
  const onOpenOrCancel = newIsOpen => {
    if (!canOpenOrClose(newIsOpen)) {
      return;
    }
    if (newIsOpen) {
      onIsOpenChange(true);
    } else {
      onCancel();
    }
  };
  return jsx(PopoutToggle, {
    Toggle: Control,
    toggleProps: {
      canSearchInputToggle: true,
      color,
      contrast,
      defaultButtonProps: toggleProps === null || toggleProps === void 0 ? void 0 : toggleProps.defaultButton,
      disabled,
      fabProps: toggleProps === null || toggleProps === void 0 ? void 0 : toggleProps.fab,
      iconButtonProps: toggleProps === null || toggleProps === void 0 ? void 0 : toggleProps.iconButton,
      id: `${treePickerId}-toggle`,
      inputFieldProps: Object.assign(Object.assign({}, toggleProps === null || toggleProps === void 0 ? void 0 : toggleProps.inputField), {
        disabled,
        value: selectedTitles
      }),
      isOpen,
      label,
      nonWhiteColor: color,
      onKeyboardNavigation: ({
        target,
        event
      }) => {
        if (!isOpen) {
          return;
        }
        if (target === 'ExitNext') {
          event.stopPropagation();
          event.preventDefault();
          focusFirstTabbableElement(popoutContentWrapperRef.current);
        } else {
          onCancel();
        }
      },
      onSearch: query => {
        onOpenOrCancel(true);
        setQuery(query);
        onSearchChangeInput === null || onSearchChangeInput === void 0 ? void 0 : onSearchChangeInput({
          query
        });
      },
      onToggle: withKeyboard => {
        const newIsOpen = !isOpen;
        ReactDOM__default.flushSync(() => onOpenOrCancel(newIsOpen));
        if (newIsOpen && withKeyboard) {
          focusFirstTabbableElement(popoutContentWrapperRef.current);
        }
      },
      ref: toggleRef,
      query,
      selectedIcon: undefined,
      stretch: treePickerStretch,
      textSize: 'normal',
      tooltip,
      type: toggleType
    },
    isOpen: isOpen,
    onIsOpenChange: ({
      isOpen,
      source
    }) => {
      // We handle toggle-sourced changes in the toggle itself
      if (source === 'popout') {
        onOpenOrCancel(isOpen);
      }
    },
    popoutProps: {
      containedPopoutIds: [`${treePickerId}-dropdown-menu`],
      preventFocus: true,
      internalPadding: 0,
      _renderContentWhenNotVisible: true
    },
    children: jsx("div", {
      className: "kxi-hierarchy-picker-popout",
      children: jsx(Picker, Object.assign({}, treePickerIngredients, {
        ref: ref,
        categories: treePickerCategories,
        color: color,
        onSearchChange: ({
          query
        }) => {
          setQuery(query);
          onSearchChangeInput === null || onSearchChangeInput === void 0 ? void 0 : onSearchChangeInput({
            query
          });
        },
        selectedIds: selectedIds,
        placeholder: placeholder,
        _query: query,
        stretch: treePickerStretch,
        hasButtons: true,
        onItemsChanged: ({
          selectedIds
        }) => {
          setSelectedIds(selectedIds);
        },
        onCancel: onCancel,
        onConfirm: () => {
          onItemsChanged === null || onItemsChanged === void 0 ? void 0 : onItemsChanged({
            selectedIds: [...selectedIds]
          });
          onClose();
        },
        wrapperRef: mergeRefs([popoutContentWrapperRef, focusTrapRef])
      }))
    })
  });
});

const defaultSelectedIds = Object.freeze([]);
class HierarchyPickerProps extends TreePickerProps {}
__decorate([Bound(), __metadata("design:type", String)], HierarchyPickerProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HierarchyPickerProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HierarchyPickerProps.prototype, "filterProps", void 0);
__decorate([Bound(), __metadata("design:type", String)], HierarchyPickerProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HierarchyPickerProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], HierarchyPickerProps.prototype, "popoutView", void 0);
__decorate([Bound(), __metadata("design:type", Object)], HierarchyPickerProps.prototype, "toggleProps", void 0);
__decorate([Bound(), __metadata("design:type", String)], HierarchyPickerProps.prototype, "toggleType", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], HierarchyPickerProps.prototype, "tooltip", void 0);
class HierarchyPickerEvents extends TreePickerEvents {}
__decorate([Bound(), __metadata("design:type", Function)], HierarchyPickerEvents.prototype, "onOpenChange", void 0);
/**
 * Description: A picker of items sorted by categories and filtered by hierarchy.
 */
const HierarchyPicker = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(HierarchyPicker, props);
  const {
      color = 'CX',
      contrast = 'normal',
      disabled = false,
      id = debugService.id,
      label,
      localization: localizationInput = {},
      onItemsChanged: onItemsChangedInput,
      onOpenChange,
      popoutView = true,
      toggleProps,
      toggleType = 'pill',
      stretch = false,
      selectedIds: selectedIdsInput = defaultSelectedIds
    } = props,
    treePickerIngredients = __rest(props, ["color", "contrast", "disabled", "id", "label", "localization", "onItemsChanged", "onOpenChange", "popoutView", "toggleProps", "toggleType", "stretch", "selectedIds"]);
  const localization = Object.assign({
    clearAll: 'Clear all',
    cancel: 'Cancel',
    apply: 'Apply'
  }, localizationInput);
  useWarnOnceIf(!!props.placeholder, debugService.format('You provided the `placeholder` prop to HierarchyPicker. Use toggleProps.inputField.placeholder instead.'));
  const onItemsChanged = ({
    selectedIds
  }) => {
    const selectedIdsSet = new Set(selectedIds);
    const selectedIdsInputSet = new Set(selectedIdsInput);
    // Only notify if selection changes
    if (!areEqual(selectedIdsSet, selectedIdsInputSet)) {
      onItemsChangedInput === null || onItemsChangedInput === void 0 ? void 0 : onItemsChangedInput({
        selectedIds: [...selectedIds]
      });
    }
  };
  return jsx("div", Object.assign({}, debugService.componentProps, {
    className: "kxi-hierarchy-picker-wrapper",
    children: popoutView ? jsx(PickerPopout, Object.assign({}, treePickerIngredients, {
      ref: ref,
      color: color,
      contrast: contrast,
      disabled: disabled,
      id: id,
      label: label,
      localization: localization,
      onItemsChanged: onItemsChanged,
      onOpenChange: onOpenChange,
      selectedIds: selectedIdsInput,
      stretch: stretch,
      toggleProps: toggleProps,
      toggleType: toggleType
    })) : jsx(Picker, Object.assign({}, treePickerIngredients, {
      ref: ref,
      color: color,
      localization: localization,
      id: id,
      onItemsChanged: onItemsChanged,
      selectedIds: selectedIdsInput,
      stretch: stretch
    }))
  }));
});
HierarchyPicker.displayName = 'HierarchyPicker';

const DraggableItem = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const {
    getTTOverflow,
    localization,
    draggable = true,
    item,
    moveOne,
    moveItem,
    dragEnd,
    onKeyDown,
    index,
    style
  } = props;
  const isDragging = React__default.useRef(false);
  const draggedRecent = React__default.useRef(false);
  const [drag2, setDrag2] = React__default.useState(false);
  const dragDelta = React__default.useRef(0);
  React__default.useLayoutEffect(() => {
    dragDelta.current = 0;
  }, [index]);
  const wrapperClick = () => {
    if (!draggedRecent.current) {
      moveOne(false, item.id);
    }
  };
  const displayItem = () => jsxs(Fragment, {
    children: [draggable && jsx(XIIcon, {
      icon: "drag_handle",
      style: {
        marginRight: 6,
        paddingTop: 2
      }
    }), item.component || getTTOverflow(item, index, true, isDragging.current), !drag2 && jsx("div", {
      className: "kxi-picker-button",
      children: jsx(DefaultButton, {
        disabled: item.disabled || false,
        hasShadows: true,
        text: (localization === null || localization === void 0 ? void 0 : localization.removeButton) || defaultText.removeButton,
        onClick: () => {
          if (draggedRecent.current) {
            moveOne(false, item.id);
          }
        },
        buttonSize: "small",
        fill: "fill",
        color: "orca",
        tabIndex: -1
      })
    })]
  });
  return draggable ? jsx(motion.div, {
    ref: ref,
    initial: {
      opacity: 1
    },
    animate: true,
    drag: "y",
    dragConstraints: {
      top: 0,
      bottom: 0
    },
    dragElastic: 1,
    transition: {
      ease: 'easeInOut',
      duration: 0.075
    },
    onDrag: (_e, {
      delta
    }) => {
      dragDelta.current += delta.y;
      moveItem === null || moveItem === void 0 ? void 0 : moveItem(index, dragDelta.current);
      setDrag2(true);
    },
    onDragStart: () => {
      draggedRecent.current = true;
      isDragging.current = true;
    },
    onDragEnd: () => {
      setTimeout(() => draggedRecent.current = false, 100);
      isDragging.current = false;
      setDrag2(false);
      dragEnd === null || dragEnd === void 0 ? void 0 : dragEnd();
    },
    className: `kxi-picker-list-item-selected ${drag2 ? 'kxi-picker-list-item-dragging' : ''}`,
    style: Object.assign({
      cursor: item.disabled ? 'auto' : 'pointer',
      top: index * 36
    }, style),
    onClick: wrapperClick,
    onKeyDown: onKeyDown,
    tabIndex: 0,
    children: displayItem()
  }) : jsx("div", {
    className: "kxi-picker-list-item-selected kxi-picker-list-item-no-dragging",
    style: Object.assign({
      cursor: item.disabled ? 'auto' : 'pointer',
      top: index * 36
    }, style),
    ref: ref,
    onClick: wrapperClick,
    onKeyDown: onKeyDown,
    tabIndex: 0,
    children: displayItem()
  });
});

/** Implementation adapted from `@popmotion/popcorn@0.4.4` library `distance` and `distance1D` functions. */
function distance(a, b) {
  return Math.abs(b - a);
}
const searchItems = (query, items) => {
  if (!query) {
    return [items, items.length];
  }
  const matchingItems = items.filter(item => {
    // avoided using another array for O(n) search time
    const searchTags = item.searchTags ? [...item.searchTags, item.title].join(' ') : item.title;
    return searchTags.toLowerCase().includes(query.toLowerCase());
  });
  return [matchingItems, matchingItems.length];
};
const defaultSort = (a, b) => {
  const nameA = a.title.toUpperCase();
  const nameB = b.title.toUpperCase();
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};
const sortItems = (items, compareFn) => {
  return items.sort(compareFn !== null && compareFn !== void 0 ? compareFn : defaultSort);
};
const findIndex = (index, length, yOffset) => {
  let target = index;
  const top = index * 36;
  const height = 36;
  const bottom = top + height;
  // If moving down
  if (yOffset > 0) {
    if (index + 1 >= length) {
      return index;
    }
    const swapOffset = distance(bottom, (index + 1) * 36 + 18) + 18;
    if (yOffset > swapOffset) {
      target = index + 1;
    }
    // If moving up
  } else if (yOffset < 0) {
    if (index - 1 < 0) {
      return index;
    }
    const prevBottom = (index - 1) * 36 + 36;
    const swapOffset = distance(top, prevBottom - 18) + 18;
    if (yOffset < -swapOffset) {
      target = index - 1;
    }
  }
  return clamp(0, length, target);
};

class LeftRightPickerProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], LeftRightPickerProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", Object)], LeftRightPickerProps.prototype, "data", void 0);
__decorate([Bound(), __metadata("design:type", Object)], LeftRightPickerProps.prototype, "availableLoaderSettings", void 0);
__decorate([Bound(), __metadata("design:type", Object)], LeftRightPickerProps.prototype, "selectedLoaderSettings", void 0);
__decorate([Bound(), __metadata("design:type", Number)], LeftRightPickerProps.prototype, "selectedItemsThreshold", void 0);
__decorate([Bound(), __metadata("design:type", Object)], LeftRightPickerProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], LeftRightPickerProps.prototype, "dragAndDropEnabled", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], LeftRightPickerProps.prototype, "useLegacyFormControl", void 0);
__decorate([Bound(), __metadata("design:type", Function)], LeftRightPickerProps.prototype, "compareFunction", void 0);
const defaultText = {
  available: 'Available',
  addButton: 'Add',
  removeButton: 'Remove',
  addAll: 'Add All',
  selected: 'Selected',
  removeAll: 'Remove All',
  searchPlaceholder: 'Search',
  results: 'Results'
};
class LeftRightPickerEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], LeftRightPickerEvents.prototype, "onItemsChanged", void 0);
/**
 * Description: Move items from one side of any given list to another, all done from within the component.
 */
const LeftRightPicker = /*#__PURE__*/React__default.forwardRef((props, ref) => {
  const debugService = useDebugService(LeftRightPicker, props);
  const {
      title,
      data = {},
      onItemsChanged,
      className,
      localization,
      availableLoaderSettings,
      selectedLoaderSettings,
      selectedItemsThreshold = 30,
      dragAndDropEnabled = true,
      useLegacyFormControl = true,
      compareFunction
    } = props,
    ingredients = __rest(props, ["title", "data", "onItemsChanged", "className", "localization", "availableLoaderSettings", "selectedLoaderSettings", "selectedItemsThreshold", "dragAndDropEnabled", "useLegacyFormControl", "compareFunction"]);
  useErrorIfRequiredPropIsNotProvided(debugService, props, 'data');
  const itemHeightPx = 36;
  const {
    ref: leftWrapper,
    height: availableFixedListHeight = 0
  } = useResizeDetector({
    handleWidth: false
  });
  const {
    ref: rightWrapper,
    height: selectedFixedListHeight = 0
  } = useResizeDetector({
    handleWidth: false
  });
  // #region State
  const [availableItems, setAvailableItems] = React__default.useState([]);
  const [selectedItems, setSelectedItems] = React__default.useState([]);
  const [query, setQuery] = React__default.useState('');
  // #endregion State
  // #region Refs
  const availableLoader = React__default.useRef(null);
  const selectedLoader = React__default.useRef(null);
  const removeAllButtonRef = React__default.useRef(null);
  /**
   * Contains all references needed to perform keyboard navigation.
   */
  const keyNavRefs = React__default.useRef({
    available: {
      rows: [],
      lastFocusedIndex: null,
      focusIndexOnNextRender: null,
      fixedSizeList: null,
      onScroll: () => {}
    },
    selected: {
      rows: [],
      lastFocusedIndex: null,
      focusIndexOnNextRender: null,
      fixedSizeList: null,
      onScroll: () => {}
    }
  });
  // #endregion Refs
  // #region Memos
  const [queryItems, resultCount] = React__default.useMemo(() => searchItems(query, availableItems), [query, availableItems]);
  const [selectedQueryItems, selectedResultCount] = React__default.useMemo(() => searchItems(query, selectedItems), [query, selectedItems]);
  /**
   * When not using FixedSizeList, we need to manually handle removing our refs when deselecting an item.
   * This is because FixedSizeList re-renders all of the list items even when given a key.
   */
  const manuallyRemoveSelectedRefs = selectedQueryItems.length < selectedItemsThreshold;
  // eslint react-hooks/exhaustive-deps doesn't like passing an IIFE to useCallback, so this is implemented with useMemo instead
  const focusItem = React__default.useMemo(() => {
    // I could have used another ref for this, but since it's only needed for this callback, I just made it local and used a closure.
    let isFocusingItem = false;
    return (column, index) => {
      var _a;
      if (isFocusingItem) {
        return;
      }
      isFocusingItem = true;
      const refs = keyNavRefs.current[column];
      if (refs.rows.length === 0) {
        refs.lastFocusedIndex = null;
        return;
      }
      const targetIndex = (_a = index !== null && index !== void 0 ? index : refs.lastFocusedIndex) !== null && _a !== void 0 ? _a : 0;
      const focusIndex = 0 <= targetIndex && targetIndex < refs.rows.length ? targetIndex : 0;
      const focus = () => {
        refs.rows[focusIndex].focus();
        refs.lastFocusedIndex = focusIndex;
        isFocusingItem = false;
      };
      // If we need to scroll to an item, we need to wait for the ref to update
      if (refs.fixedSizeList && refs.rows[focusIndex] === null) {
        refs.onScroll = () => {
          focus();
          refs.onScroll = null;
        };
        refs.fixedSizeList.scrollToItem(focusIndex);
      } else {
        focus();
      }
    };
  }, []);
  // #endregion Memos
  // #region Effects
  React__default.useEffect(() => {
    const ids = [];
    const initialAvailableItems = [];
    const initialSelectedItems = [];
    if (data.initialAvailableItems) {
      for (let i = 0; i < data.initialAvailableItems.length; i++) {
        if (data.initialAvailableItems[i].id) {
          const pickerItem = data.initialAvailableItems[i];
          pickerItem.id = data.initialAvailableItems[i].id.toString();
          ids.push(pickerItem.id);
          initialAvailableItems.push(pickerItem);
        } else {
          const pickerItem = data.initialAvailableItems[i];
          pickerItem.id = uuidv4();
          initialAvailableItems.push(pickerItem);
        }
      }
    }
    if (data.initialSelectedItems) {
      for (let i = 0; i < data.initialSelectedItems.length; i++) {
        if (data.initialSelectedItems[i].id) {
          const pickerItem = data.initialSelectedItems[i];
          pickerItem.id = data.initialSelectedItems[i].id.toString();
          ids.push(pickerItem.id);
          initialSelectedItems.push(pickerItem);
        } else {
          const pickerItem = data.initialSelectedItems[i];
          pickerItem.id = uuidv4();
          initialSelectedItems.push(pickerItem);
        }
      }
    }
    // Use Set to make finding duplicates O(n)
    const previouslySeenIds = new Set();
    const duplicates = new Set();
    ids.forEach(id => {
      if (previouslySeenIds.has(id)) {
        duplicates.add(id);
      }
      previouslySeenIds.add(id);
    });
    if (duplicates.size > 0) {
      devOnlyError(`Items with duplicate IDs detected in LR Picker: ${JSON.stringify(Array.from(duplicates))}`);
    }
    setAvailableItems(sortItems(initialAvailableItems, compareFunction));
    setSelectedItems(initialSelectedItems);
  }, [data.initialAvailableItems, data.initialSelectedItems, compareFunction]);
  React__default.useEffect(() => {
    var _a;
    resizeArray(keyNavRefs.current.available.rows, queryItems.length, () => null);
    (_a = availableLoader.current) === null || _a === void 0 ? void 0 : _a.resetloadMoreItemsCache();
  }, [queryItems]);
  React__default.useEffect(() => {
    var _a;
    resizeArray(keyNavRefs.current.selected.rows, selectedQueryItems.length, () => null);
    (_a = selectedLoader.current) === null || _a === void 0 ? void 0 : _a.resetloadMoreItemsCache();
  }, [selectedQueryItems]);
  React__default.useEffect(() => {
    const {
      available,
      selected
    } = keyNavRefs.current;
    if (available.focusIndexOnNextRender !== null) {
      focusItem('available', available.focusIndexOnNextRender);
      available.focusIndexOnNextRender = null;
    } else if (selected.focusIndexOnNextRender !== null) {
      focusItem('selected', selected.focusIndexOnNextRender);
      selected.focusIndexOnNextRender = null;
    }
  });
  // #endregion Effects
  // #region Callbacks
  const availableFixedSizeListRef = React__default.useCallback(list => keyNavRefs.current.available.fixedSizeList = list, []);
  const selectedFixedSizeListRef = React__default.useCallback(list => keyNavRefs.current.selected.fixedSizeList = list, []);
  const moveItem = (i, dragOffset) => {
    var _a, _b;
    // Scroll list if needed
    if (rightWrapper.current) {
      const containerHeight = (_a = rightWrapper.current) === null || _a === void 0 ? void 0 : _a.clientHeight;
      const indexLength = Math.floor((containerHeight || 0) / itemHeightPx);
      const amountScrolledBottom = (_b = rightWrapper.current) === null || _b === void 0 ? void 0 : _b.scrollTop;
      const minIndexScrolled = Math.floor(amountScrolledBottom / itemHeightPx);
      const maxIndexScrolled = minIndexScrolled + indexLength;
      if (i < minIndexScrolled + 3 && dragOffset < 10) rightWrapper.current.scrollTo(0, amountScrolledBottom - 8);
      if (i > maxIndexScrolled - 3 && dragOffset > 10) rightWrapper.current.scrollTo(0, amountScrolledBottom + 8);
    }
    if (selectedItems.length < selectedItemsThreshold) {
      const targetIndex = findIndex(i, selectedItems.length, dragOffset);
      if (targetIndex !== i) setSelectedItems(arrayMove(selectedItems, i, targetIndex));
    }
  };
  const dragEnd = () => {
    onItemsChanged === null || onItemsChanged === void 0 ? void 0 : onItemsChanged({
      availableItems: sortItems(availableItems, compareFunction),
      selectedItems,
      _useLegacyFormControl: useLegacyFormControl
    });
  };
  const moveOne = (willMoveToSelected, id) => {
    const copy = [...(willMoveToSelected ? availableItems : selectedItems)];
    const index = copy.findIndex(i => `${i.id}` === `${id}` && !i.disabled);
    if (index > -1) {
      const item = copy.splice(index, 1)[0];
      if (manuallyRemoveSelectedRefs && !willMoveToSelected) {
        const refIndex = selectedQueryItems.findIndex(i => `${i.id}` === `${id}` && !i.disabled);
        refIndex >= 0 && keyNavRefs.current.selected.rows.splice(refIndex, 1);
      }
      const selected = willMoveToSelected ? [...selectedItems, item] : [...copy];
      const available = sortItems(willMoveToSelected ? [...copy] : [...availableItems, item], compareFunction);
      setAvailableItems(available);
      setSelectedItems(selected);
      onItemsChanged === null || onItemsChanged === void 0 ? void 0 : onItemsChanged({
        availableItems: sortItems(available, compareFunction),
        selectedItems: selected,
        _useLegacyFormControl: useLegacyFormControl
      });
    }
  };
  const moveAll = willMoveToSelected => {
    const copy = [...(willMoveToSelected ? availableItems : selectedItems)];
    const remaining = [];
    copy.forEach(i => {
      const itemInQueryItems = query && query !== '' ? willMoveToSelected ? queryItems.findIndex(queryItem => queryItem.id === i.id) > -1 : selectedQueryItems.findIndex(queryItem => queryItem.id === i.id) > -1 : true;
      if (i.disabled || !itemInQueryItems) remaining.push(i);
    });
    remaining.forEach(i => copy.splice(copy.indexOf(i), 1));
    const selected = sortItems(willMoveToSelected ? [...selectedItems, ...copy] : remaining, compareFunction);
    const available = sortItems(willMoveToSelected ? remaining : [...copy, ...availableItems], compareFunction);
    setSelectedItems(selected);
    setAvailableItems(available);
    onItemsChanged === null || onItemsChanged === void 0 ? void 0 : onItemsChanged({
      availableItems: sortItems(available, compareFunction),
      selectedItems: selected,
      _useLegacyFormControl: useLegacyFormControl
    });
  };
  const getTTOverflow = (item, index, willAccountForIcon, isDragging = false) => {
    const ttDirectTextOverflow = queryItems.length > 4 && index > queryItems.length - 3 ? 1 : 7;
    return jsx(Fragment, {
      children: isDragging ? jsx("p", {
        className: "kxi-tooltip-overflow-text",
        color: item.disabled ? 'disabled' : undefined,
        children: item.title
      }) : jsx(ToolTipOverflowText, {
        disabled: item.disabled,
        title: item.title,
        text: item.tooltip,
        style: {
          position: 'relative'
        },
        tooltip: {
          text: item.title,
          direction: ttDirectTextOverflow
        },
        spaceOnRight: 72,
        willAccountForIcon: willAccountForIcon
      })
    });
  };
  const onKeyDownRow = (column, item, index, e) => {
    var _a, _b;
    const isAvailableColumn = column === 'available';
    const {
      available,
      selected
    } = keyNavRefs.current;
    const [thisKeyNavRefs, thatKeyNavRefs] = isAvailableColumn ? [available, selected] : [selected, available];
    const thisRows = thisKeyNavRefs.rows;
    const thatRows = thatKeyNavRefs.rows;
    let caught = false;
    switch (e.key) {
      case 'Enter':
      case ' ':
        caught = true;
        if (!item.disabled) {
          // Only one, focus other column
          if (thisRows.length === 1 && thatRows.length > 0) {
            thatKeyNavRefs.focusIndexOnNextRender = (_a = thatKeyNavRefs.lastFocusedIndex) !== null && _a !== void 0 ? _a : 0;
          } else {
            // Try to focus same index on render, because next item will move up to this index
            // If no next item, focus previous
            thisKeyNavRefs.focusIndexOnNextRender = index === thisRows.length - 1 ? index - 1 : index;
          }
          moveOne(isAvailableColumn, item.id);
        }
        break;
      case 'ArrowUp':
        caught = true;
        if (index > 0) {
          focusItem(column, index - 1);
        }
        break;
      case 'ArrowDown':
        caught = true;
        if (index < thisRows.length - 1) {
          focusItem(column, index + 1);
        }
        break;
      case 'Home':
        caught = true;
        focusItem(column, 0);
        break;
      case 'End':
        caught = true;
        focusItem(column, thisRows.length - 1);
        break;
      case 'PageUp':
        caught = true;
        {
          const index = thisRows.findIndex(e => e !== null);
          index >= 0 && focusItem(column, index);
        }
        break;
      case 'PageDown':
        caught = true;
        {
          const index = thisRows.length - 1 - [...thisRows].reverse().findIndex(e => e !== null);
          index < thisRows.length && focusItem(column, index);
        }
        break;
      case 'Tab':
        {
          const thatRowIsEmpty = thatRows.every(e => e === null);
          // Exit left
          if (e.shiftKey && (isAvailableColumn || thatRowIsEmpty)) {
            caught = true;
            (_b = removeAllButtonRef.current) === null || _b === void 0 ? void 0 : _b.focus();
          }
          // Switch columns
          else if (isAvailableColumn !== e.shiftKey && !thatRowIsEmpty) {
            caught = true;
            focusItem(isAvailableColumn ? 'selected' : 'available');
          }
          // Exit right
          else if (!e.shiftKey && (!isAvailableColumn || thatRowIsEmpty)) {
            // Do not capture
            // Focus last list item in DOM so that default tab behavior takes us out of component
            const exitRows = selected.rows.some(e => e !== null) ? selected.rows : available.rows;
            const lastItem = [...exitRows].reverse().find(e => e !== null);
            lastItem === null || lastItem === void 0 ? void 0 : lastItem.focus({
              preventScroll: true
            });
          }
        }
        break;
    }
    if (caught) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const updateRowRef = (column, index, el) => {
    if (column === 'available' || el !== null || !manuallyRemoveSelectedRefs) {
      keyNavRefs.current[column].rows[index] = el;
    }
  };
  const AvailableRow = props => {
    const {
      index,
      style
    } = props;
    const item = queryItems[index];
    return jsxs(motion.div, {
      className: "kxi-picker-list-item-available",
      style: Object.assign({
        cursor: item.disabled ? 'auto' : 'pointer'
      }, style),
      onClick: () => moveOne(true, item.id),
      onKeyDown: e => onKeyDownRow('available', item, index, e),
      ref: ref => updateRowRef('available', index, ref),
      tabIndex: 0,
      animate: true,
      children: [item.component || getTTOverflow(item, index), jsx("div", {
        className: "kxi-picker-button",
        children: jsx(DefaultButton, {
          disabled: item.disabled || false,
          hasShadows: true,
          text: (localization === null || localization === void 0 ? void 0 : localization.addButton) || defaultText.addButton,
          onClick: e => {
            e.stopPropagation();
            moveOne(true, item.id);
          },
          buttonSize: "small",
          fill: "fill",
          color: "orca",
          tabIndex: -1
        })
      })]
    }, `LRPicker_Available_Item_${item.id}`);
  };
  const getAvailableList = () => {
    if (availableLoaderSettings) {
      const {
        isItemLoaded,
        itemCount,
        loadMoreItems,
        threshold,
        minimumBatchSize
      } = availableLoaderSettings;
      return jsx(InfiniteLoader, {
        ref: availableLoader,
        isItemLoaded: isItemLoaded,
        itemCount: itemCount,
        loadMoreItems: loadMoreItems,
        threshold: threshold,
        minimumBatchSize: minimumBatchSize,
        children: ({
          onItemsRendered,
          ref
        }) => jsx(FixedSizeList, {
          height: availableFixedListHeight,
          itemCount: queryItems.length,
          itemSize: itemHeightPx,
          width: "100%",
          onItemsRendered: onItemsRendered,
          ref: mergeRefs([ref, availableFixedSizeListRef]),
          onScroll: () => {
            var _a, _b;
            return (_b = (_a = keyNavRefs.current.available).onScroll) === null || _b === void 0 ? void 0 : _b.call(_a);
          },
          children: AvailableRow
        })
      });
    } else {
      return jsx(FixedSizeList, {
        height: availableFixedListHeight,
        itemCount: queryItems.length,
        itemSize: itemHeightPx,
        width: "100%",
        ref: availableFixedSizeListRef,
        onScroll: () => {
          var _a, _b;
          return (_b = (_a = keyNavRefs.current.available).onScroll) === null || _b === void 0 ? void 0 : _b.call(_a);
        },
        children: AvailableRow
      });
    }
  };
  const SelectedRow = props => {
    const {
      index,
      style
    } = props;
    const item = selectedQueryItems[index];
    const ttDirectDisabled = selectedQueryItems.length > 4 && index > selectedQueryItems.length - 3 ? 1 : 5;
    return jsx(DraggableItem, {
      draggable: false,
      localization: localization,
      index: index,
      item: item,
      moveOne: moveOne,
      ttDirectDisabled: ttDirectDisabled,
      getTTOverflow: getTTOverflow,
      onKeyDown: e => onKeyDownRow('selected', item, index, e),
      style: style,
      ref: ref => updateRowRef('selected', index, ref)
    }, `LRPicker_Selected_Item_${item.id}`);
  };
  const getSelectedList = () => {
    if (selectedQueryItems.length >= selectedItemsThreshold) {
      if (selectedLoaderSettings) {
        const {
          isItemLoaded,
          itemCount,
          loadMoreItems,
          threshold,
          minimumBatchSize
        } = selectedLoaderSettings;
        return jsx(InfiniteLoader, {
          ref: selectedLoader,
          isItemLoaded: isItemLoaded,
          itemCount: itemCount,
          loadMoreItems: loadMoreItems,
          threshold: threshold,
          minimumBatchSize: minimumBatchSize,
          children: ({
            onItemsRendered,
            ref
          }) => jsx(FixedSizeList, {
            height: selectedFixedListHeight,
            itemCount: selectedQueryItems.length,
            itemSize: itemHeightPx,
            width: "100%",
            onItemsRendered: onItemsRendered,
            ref: mergeRefs([ref, selectedFixedSizeListRef]),
            onScroll: () => {
              var _a, _b;
              return (_b = (_a = keyNavRefs.current.selected).onScroll) === null || _b === void 0 ? void 0 : _b.call(_a);
            },
            children: SelectedRow
          })
        });
      } else {
        return jsx(FixedSizeList, {
          height: selectedFixedListHeight,
          itemCount: selectedQueryItems.length,
          itemData: selectedQueryItems,
          itemSize: itemHeightPx,
          ref: selectedFixedSizeListRef,
          width: "100%",
          onScroll: () => {
            var _a, _b;
            return (_b = (_a = keyNavRefs.current.selected).onScroll) === null || _b === void 0 ? void 0 : _b.call(_a);
          },
          children: SelectedRow
        });
      }
    } else {
      const items = selectedQueryItems;
      return items.map((item, index) => {
        const ttDirectDisabled = items.length > 4 && index > items.length - 3 ? 1 : 5;
        return jsx(DraggableItem, {
          draggable: dragAndDropEnabled && !(query && query !== ''),
          moveItem: moveItem,
          localization: localization,
          index: index,
          item: item,
          moveOne: moveOne,
          dragEnd: dragEnd,
          onKeyDown: e => onKeyDownRow('selected', item, index, e),
          ref: ref => updateRowRef('selected', index, ref),
          ttDirectDisabled: ttDirectDisabled,
          getTTOverflow: getTTOverflow
        }, `LRPicker_Selected_Item_${item.id}`);
      });
    }
  };
  // #endregion Callbacks
  useWarnIsDeprecated('LeftRightPicker', {
    useLegacyFormControl: {
      value: useLegacyFormControl,
      message: 'This may cause the component to work incorrectly with Angular forms. Pass `true` to `useLegacyFormControl` to use the behavior compatible with Angular forms.'
    }
  }, 'prop');
  useErrorOnceIf(useLegacyFormControl === true, debugService.format('useLegacyFormControl was provided as (or defaulted to) `false`.', 'This may cause the component to work incorrectly with Angular forms.', 'Pass `true` to `useLegacyFormControl` to silence this warning.'));
  return jsxs("div", Object.assign({
    className: `kxi-picker kxi-neutral ${className || ''}`
  }, ingredients, {
    ref: ref
  }, debugService.componentProps, {
    children: [title ? jsx("p", {
      className: "kxi-picker-text",
      children: title
    }) : null, jsxs("div", {
      className: "kxi-picker-content",
      children: [jsx("div", {
        className: "kxi-picker-search",
        children: jsx(SearchBox, {
          onCancelClick: () => setQuery(''),
          onChangeOverride: eventData => setQuery(eventData.searchText),
          placeholder: (localization === null || localization === void 0 ? void 0 : localization.searchPlaceholder) || defaultText.searchPlaceholder,
          hasOutline: false,
          _stretchHeight: true
        })
      }), jsxs("div", {
        className: "kxi-picker-header-container",
        children: [jsxs("div", {
          className: "kxi-picker-header",
          children: [jsx("p", {
            className: "kxi-picker-header-text",
            children: query !== '' && resultCount !== availableItems.length ? `${resultCount} ${(localization === null || localization === void 0 ? void 0 : localization.results) || defaultText.results}` : `${availableItems.length} ${(localization === null || localization === void 0 ? void 0 : localization.available) || defaultText.available}`
          }), jsx(DefaultButton, {
            text: (localization === null || localization === void 0 ? void 0 : localization.addAll) || defaultText.addAll,
            onClick: () => moveAll(true),
            buttonSize: "small",
            fill: "fill",
            color: "orca"
          })]
        }), jsxs("div", {
          className: "kxi-picker-header",
          children: [jsx("p", {
            className: "kxi-picker-header-text",
            children: query !== '' && selectedResultCount !== selectedItems.length ? `${selectedResultCount} ${(localization === null || localization === void 0 ? void 0 : localization.results) || defaultText.results}` : `${selectedItems.length} ${(localization === null || localization === void 0 ? void 0 : localization.selected) || defaultText.selected}`
          }), jsx(DefaultButton, {
            text: (localization === null || localization === void 0 ? void 0 : localization.removeAll) || defaultText.removeAll,
            onClick: () => moveAll(false),
            buttonSize: "small",
            fill: "fill",
            color: "orca",
            ref: removeAllButtonRef
          })]
        })]
      }), jsxs("div", {
        className: "kxi-picker-items-wrapper",
        children: [jsx("div", {
          className: "kxi-picker-items",
          children: jsx("div", {
            className: "kxi-picker-items-inner",
            tabIndex: -1,
            ref: leftWrapper,
            children: getAvailableList()
          })
        }), jsx("div", {
          className: "kxi-picker-items",
          children: jsx("div", {
            className: "kxi-picker-items-inner",
            tabIndex: -1,
            ref: rightWrapper,
            children: getSelectedList()
          })
        })]
      })]
    })]
  }));
});
LeftRightPicker.displayName = 'LeftRightPicker';

class RadioProps extends ObservablePropsMixin(InputHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Boolean)], RadioProps.prototype, "checked", void 0);
__decorate([Bound(), __metadata("design:type", String)], RadioProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], RadioProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", String)], RadioProps.prototype, "inputId", void 0);
__decorate([Bound(), __metadata("design:type", String)], RadioProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], RadioProps.prototype, "isLabelLeft", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], RadioProps.prototype, "isInlineFlex", void 0);
__decorate([Bound(), __metadata("design:type", Object)], RadioProps.prototype, "status", void 0);
class RadioEvents extends InputHTMLEvents {}
const deselectEvent = new Event('deselect');
/**
 * Description: Standard InMoment branded Radio button, works the same as an HTML radio button.
 */
const Radio = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(Radio, props);
  const {
      checked,
      color = 'orca',
      contrast = 'normal',
      disabled,
      id,
      inputId,
      internalProps,
      isInlineFlex = true,
      isLabelLeft = false,
      label,
      name,
      observer,
      onChange,
      onClick,
      status,
      tabIndex = 0
    } = props,
    ingredients = __rest(props, ["checked", "color", "contrast", "disabled", "id", "inputId", "internalProps", "isInlineFlex", "isLabelLeft", "label", "name", "observer", "onChange", "onClick", "status", "tabIndex"]);
  const {
    wrapper
  } = internalProps !== null && internalProps !== void 0 ? internalProps : {};
  const radioEl = React.useRef(null);
  const finalInputId = React.useMemo(() => {
    if (inputId && inputId.trim() !== '') {
      return inputId.trim();
    } else if (id && id.trim() !== '') {
      return id.trim();
    } else {
      return `${uuidv4()}-input`;
    }
  }, [id, inputId]);
  React.useEffect(() => {
    const el = radioEl.current;
    if (!el) {
      return;
    }
    if (checked !== undefined) {
      el.checked = checked;
    }
    const runDeselect = e => {
      var _a, _b;
      if (observer) {
        observer.checked = (_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.checked) !== null && _b !== void 0 ? _b : false;
      }
    };
    el.addEventListener('deselect', runDeselect);
    return () => el.removeEventListener('deselect', runDeselect);
  }, [checked, finalInputId, observer]);
  useWarnOnceIf(isInlineFlex, 'The Kitchen: Radio Button - The layout styling of Radio Buttons is changing. Please pass in isInlineFlex = false to the component to take advantage of the new styling.', 'Note: the new styling will be the default styling in a future major version.');
  return jsxs("div", Object.assign({
    "data-testid": includeTestId('radio-wrapper')
  }, wrapper, debugService.componentProps, {
    className: classNames('kxi-radio-wrapper', wrapper === null || wrapper === void 0 ? void 0 : wrapper.className, {
      'kxi-label-left': isLabelLeft,
      'kxi-radio-inline-flex': isInlineFlex
    }),
    children: [label && jsx("label", {
      id: `label-${finalInputId}`,
      htmlFor: finalInputId,
      className: classNames('kxi-radio-label orca', {
        disabled: disabled && contrast !== 'high',
        'kxi-high-contrast': contrast === 'high'
      }),
      "data-testid": includeTestId('radio-label'),
      dangerouslySetInnerHTML: {
        __html: sanitizeHtmlString(label)
      }
    }), jsx("input", Object.assign({
      "data-testid": includeTestId('radio')
    }, ingredients, {
      ref: mergeRefs([radioEl, ref]),
      className: classNames('kxi-radio', 'kxi-border', {
        'kxi-high-contrast': contrast === 'high',
        [`kxi-${status}`]: status,
        [`${normalizeColor(color)}`]: !status
      }),
      disabled: disabled,
      id: finalInputId,
      type: "radio",
      tabIndex: tabIndex,
      name: name,
      onChange: e => {
        onChange === null || onChange === void 0 ? void 0 : onChange(e);
        if (observer) {
          observer.checked = e.target.checked;
        }
      },
      onClick: e => {
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        document.querySelectorAll('input[type=radio]').forEach(radio => {
          if (!radio.checked && radio.name === name && radio.id !== finalInputId) {
            radio.dispatchEvent(deselectEvent);
          }
        });
      }
    }))]
  }));
});
Radio.displayName = 'Radio';

const {
  colorDisabled
} = XIColorUsage;
const {
  white
} = XIColors;
class ToggleTextProps extends ObservablePropsMixin(ButtonHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Boolean)], ToggleTextProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleTextProps.prototype, "leftText", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleTextProps.prototype, "rightText", void 0);
__decorate([Bound(), __metadata("design:type", Object)], ToggleTextProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleTextProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleTextProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleTextProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToggleTextProps.prototype, "side", void 0);
class ToggleTextEvents extends ButtonHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], ToggleTextEvents.prototype, "onToggle", void 0);
/**
 * Description: DEPRECATED. Use toggle switch instead. All stylings of this component are available as toggle switch. Works like ToggleSwitch, but with customizable text.
 */
const ToggleText = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(ToggleText, props);
  const {
      leftText,
      rightText,
      disabled = false,
      color = 'orca',
      contrast = 'normal',
      onToggle,
      onMouseDown,
      onMouseUp,
      onMouseOut,
      onClick,
      status,
      className,
      side = 'left',
      stretch,
      label,
      observer
    } = props,
    ingredients = __rest(props, ["leftText", "rightText", "disabled", "color", "contrast", "onToggle", "onMouseDown", "onMouseUp", "onMouseOut", "onClick", "status", "className", "side", "stretch", "label", "observer"]);
  React.useEffect(() => {
    devOnlyWarn('The Kitchen - ToggleText: The ToggleText component is deprecated and will be removed in a future major version. You can recreate your switches using ToggleSwitch.');
  }, []);
  const isLeftOn = side === 'left';
  const [on, setOn] = React.useState(isLeftOn);
  const [primaryWidth, setPrimaryWidth] = React.useState(0);
  const [secondaryWidth, setSecondaryWidth] = React.useState(0);
  const primaryTextWrapper = React.useRef(null);
  const secondaryTextWrapper = React.useRef(null);
  const wrapper = React.useRef(null);
  React.useEffect(() => {
    if (primaryTextWrapper.current && secondaryTextWrapper.current) {
      setPrimaryWidth(primaryTextWrapper.current.offsetWidth);
      setSecondaryWidth(secondaryTextWrapper.current.offsetWidth);
    }
  }, [stretch, leftText, rightText, label]);
  React.useEffect(() => {
    if (isLeftOn !== on) {
      setOn(isLeftOn);
    }
  }, [side]);
  const handleClick = e => {
    if (!disabled) {
      setOn(v => !v);
      if (observer) {
        observer.side = on ? 'right' : 'left';
      }
      if (onToggle) onToggle({
        text: !on ? leftText : rightText
      });
    }
    if (onClick) onClick(e);
  };
  const handleMouseDown = e => {
    if (!disabled) {
      on ? setPrimaryWidth(primaryWidth + 12) : setSecondaryWidth(secondaryWidth + 12);
    }
    if (onMouseDown) onMouseDown(e);
  };
  const handleMouseUp = e => {
    if (primaryTextWrapper.current && secondaryTextWrapper.current && !disabled) {
      on ? setPrimaryWidth(primaryTextWrapper.current.offsetWidth) : setSecondaryWidth(secondaryTextWrapper.current.offsetWidth);
    }
    if (onMouseUp) onMouseUp(e);
  };
  const handleMouseOut = e => {
    if (e.target === wrapper.current && !disabled) {
      if (primaryTextWrapper.current && secondaryTextWrapper.current) {
        setPrimaryWidth(primaryTextWrapper.current.offsetWidth);
        setSecondaryWidth(secondaryTextWrapper.current.offsetWidth);
      }
    }
    if (onMouseOut) onMouseOut(e);
  };
  const backgroundColor = contrast === 'normal' ? getColor(color) : white.primary;
  const disabledClass = disabled ? ' kxi-disabled' : '';
  const stretchClass = stretch ? ' kxi-stretch' : '';
  const contrastClass = contrast === 'high' ? ' kxi-high-contrast' : '';
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    "aria-label": label,
    className: `kxi-switch${disabledClass}${stretchClass} orca`,
    children: [label ? jsx("label", {
      className: `kxi-label${contrastClass}`,
      children: label
    }) : null, jsxs("button", Object.assign({
      className: `kxi-toggle-switch-button kxi-border${status ? ` kxi-${status}` : ''}${contrastClass}${stretchClass}${disabledClass} ${className || ''} ${stretch ? '' : 'kxi-fit-toggle'}`,
      "aria-disabled": disabled,
      ref: mergeRefs([wrapper, ref]),
      disabled: disabled,
      onClick: handleClick,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseOut: handleMouseOut
    }, ingredients, {
      children: [jsx(motion.div, {
        transition: {
          duration: 0.1,
          ease: 'easeOut'
        },
        initial: {
          opacity: 1
        },
        className: "kxi-toggle",
        style: {
          backgroundColor: disabled ? colorDisabled : backgroundColor,
          minWidth: on ? primaryWidth : secondaryWidth,
          left: on ? '0.1875rem' : 'unset',
          right: on ? 'unset' : '0.1875rem',
          originX: 0.5,
          originY: !on ? 0 : 1
        },
        animate: true
      }), jsx("div", {
        role: "switch",
        "aria-checked": on,
        className: `orca kxi-toggle-text${on ? '-black' : '-white'}${stretchClass}`,
        ref: primaryTextWrapper,
        children: leftText
      }), jsx("div", {
        role: "switch",
        "aria-checked": !on,
        className: `orca kxi-toggle-text${on ? '-white' : '-black'}${stretchClass}`,
        ref: secondaryTextWrapper,
        children: rightText
      })]
    }))]
  }));
});
ToggleText.displayName = 'ToggleText';

const _OnOffOptions = ['on', 'off'];
const _SideOptions = ['left', 'right'];

const TabBarItem = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      title,
      id,
      disabled,
      alert,
      tabSpacing,
      onClick,
      selected,
      color,
      contrast,
      maxCharsAllowed = 30,
      tooltip,
      onWidthChange,
      style,
      isVisible = true,
      className,
      icon,
      disabledReason,
      category
    } = props,
    ingredients = __rest(props, ["title", "id", "disabled", "alert", "tabSpacing", "onClick", "selected", "color", "contrast", "maxCharsAllowed", "tooltip", "onWidthChange", "style", "isVisible", "className", "icon", "disabledReason", "category"]);
  const debugService = useDebugService(TabBarItem, props);
  const widthRef = React.useRef(null);
  const generateText = () => {
    return jsxs("div", {
      children: [category ? jsx("div", {
        style: {
          fontSize: 14
        },
        children: `${category.length > maxCharsAllowed ? `${category.substr(0, maxCharsAllowed)}...` : category}`
      }) : undefined, jsx("div", {
        style: category ? {
          fontWeight: 'bold'
        } : undefined,
        children: `${title.length > maxCharsAllowed ? `${title.substr(0, maxCharsAllowed)}...` : title}`
      })]
    });
  };
  const getTextOverflow = () => {
    if (disabled && disabledReason && isVisible) {
      if (title.length > maxCharsAllowed || category && category.length > maxCharsAllowed) {
        return jsx(ToolTipWrapper, Object.assign({}, tooltip, {
          title: `${disabledReason}<br><br><span style="font-weight: 200">${category ? `${category}<br>` : ''}</span>${title}`
        }, ingredients, {
          style: {
            cursor: 'pointer',
            alignItems: 'center'
          },
          ref: ref,
          children: generateText()
        }));
      } else {
        return jsx(ToolTipWrapper, Object.assign({}, tooltip, {
          text: `${disabledReason}`
        }, ingredients, {
          style: {
            cursor: 'pointer',
            alignItems: 'center'
          },
          ref: ref,
          children: generateText()
        }));
      }
    } else if ((title.length > maxCharsAllowed || category && category.length > maxCharsAllowed) && isVisible) {
      return jsx(ToolTipWrapper, Object.assign({}, tooltip, {
        title: `<span style="font-weight: normal">${category ? `${category}</span><br>` : ''}${category ? `<span style="font-weight: bold;">${title}</span>` : title}</span>`
      }, ingredients, {
        style: {
          cursor: 'pointer',
          alignItems: 'center'
        },
        ref: ref,
        children: generateText()
      }));
    } else {
      return jsx("span", {
        children: generateText()
      });
    }
  };
  const handleKeyPress = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // prevent page from scrolling on space
      if (onClick && !disabled) {
        onClick(id);
      }
    }
  };
  const textOverflow = getTextOverflow();
  const hitBox = jsxs("span", {
    tabIndex: isVisible ? 0 : -1,
    onKeyPress: handleKeyPress,
    onClick: () => {
      if (onClick && !disabled) {
        onClick(id);
      }
    },
    children: [textOverflow, alert ? jsx("div", {
      role: "alert",
      className: "kxi-tab-bar-alert"
    }, `alertDiv-${id}`) : null]
  });
  return jsx(ReactResizeDetector, {
    handleWidth: true,
    onResize: React.useCallback(width => {
      if (onWidthChange && width) {
        onWidthChange(width);
      }
    }, []),
    targetRef: widthRef,
    children: /*#__PURE__*/createElement("div", Object.assign({
      "data-testid": id
    }, debugService.componentProps, {
      ref: mergeRefs([widthRef, ref]),
      className: `kxi-tab-bar-item-wrapper ${isVisible ? '' : 'kxi-tab-bar-hidden-tab'}`,
      key: `outerDiv-${id}`
    }), jsxs("div", Object.assign({
      role: "tab",
      "aria-label": title,
      "aria-hidden": !isVisible,
      id: `tab-${id}`,
      className: `${color ? normalizeColor(color) : ''} ${contrast === 'high' ? ' kxi-high-contrast' : ''} kxi-tab-bar-item ${className}`,
      style: Object.assign({
        marginRight: tabSpacing
      }, style)
    }, ingredients, {
      children: [jsx("div", {
        className: "kxi-tab-bar-text",
        children: jsxs("div", {
          className: `kxi-tab-bar-text-container kxi-text-normal ${disabled ? 'kxi-disabled-alpha' : ''}`,
          children: [hitBox, icon]
        }, `textDiv-${id}`)
      }, `textContainer-${id}`), jsx("div", {
        className: "kxi-tab-bar-indicator-wrapper",
        children: jsx("div", {
          className: "kxi-tab-bar-indicator",
          style: {
            height: selected ? 6 : 0,
            width: selected ? '100%' : 0
          }
        }, `indicatorDiv-${id}`)
      })]
    }), `innerDiv-${id}`))
  }, `resizeDetector-${id}`);
});
TabBarItem.displayName = 'TabBarItem';

class TabBarProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Array)], TabBarProps.prototype, "data", void 0);
__decorate([Bound(), __metadata("design:type", String)], TabBarProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], TabBarProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", String)], TabBarProps.prototype, "activeTabId", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TabBarProps.prototype, "tabSpacing", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TabBarProps.prototype, "hasTransparentBackground", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TabBarProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TabBarProps.prototype, "maxTabs", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TabBarProps.prototype, "localization", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TabBarProps.prototype, "maxCharsAllowed", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], TabBarProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", DropdownProps)], TabBarProps.prototype, "dropdown", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TabBarProps.prototype, "componentControlledTabChange", void 0);
class TabBarEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], TabBarEvents.prototype, "onTabChange", void 0);
/**
 * Description: Navigational component that contains clickable data.
 */
const TabBar = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(TabBar, props);
  const {
      data,
      color,
      contrast,
      activeTabId,
      tabSpacing,
      hasTransparentBackground,
      stretch,
      maxTabs = 10,
      localization: localizationInput = {
        more: 'More'
      },
      maxCharsAllowed = 30,
      tooltip,
      observer,
      dropdown,
      componentControlledTabChange = true,
      onTabChange
    } = props,
    ingredients = __rest(props, ["data", "color", "contrast", "activeTabId", "tabSpacing", "hasTransparentBackground", "stretch", "maxTabs", "localization", "maxCharsAllowed", "tooltip", "observer", "dropdown", "componentControlledTabChange", "onTabChange"]);
  const [tabArray, setTabArray] = React.useState([]);
  const [tabBarId] = React.useState(uuidv4());
  const [width, setWidth] = React.useState(undefined);
  const [height, setHeight] = React.useState(undefined);
  const [menuWidth, setMenuWidth] = React.useState(undefined);
  const [selectedTabId, setSelectedTabId] = React.useState(undefined);
  const [localization, setLocalization] = React.useState({
    more: 'More'
  });
  const [triggerRerender, setTriggerRerender] = React.useState('');
  const widthRef = React.useRef(null);
  const tabWidths = React.useRef(null);
  const inputTabArray = React.useRef(null);
  const menuTabRef = React.useRef(null);
  React.useEffect(() => {
    warnIsDeprecated('TabBar', {
      component: {
        message: 'TabBar will be replaced by HorizontalTabBar.',
        value: undefined
      }
    }, 'component');
  }, []);
  React.useEffect(() => {
    if (localizationInput && JSON.stringify(localizationInput) !== JSON.stringify(localization)) {
      setLocalization(localizationInput);
    }
  }, [localizationInput, localization]);
  React.useEffect(() => {
    setSelectedTabId(activeTabId);
  }, [activeTabId]);
  React.useEffect(() => {
    const newTabArray = [];
    for (let i = 0; i < data.length; i++) {
      const tab = {
        title: data[i].title,
        id: data[i].id,
        disabled: data[i].disabled,
        alert: data[i].alert,
        isSelected: false
      };
      newTabArray.push(tab);
    }
    inputTabArray.current = newTabArray;
    setTriggerRerender(uuidv4());
  }, [data]);
  React.useEffect(() => {
    if (observer) {
      observer.activeTabId = selectedTabId;
    }
  }, [selectedTabId]);
  React.useEffect(() => {
    var _a;
    const newTabArray = (_a = inputTabArray.current) === null || _a === void 0 ? void 0 : _a.map((item, i) => {
      return Object.assign(Object.assign({}, item), {
        width: (tabWidths === null || tabWidths === void 0 ? void 0 : tabWidths.current) ? tabWidths.current[i] : 0
      });
    });
    if (newTabArray) {
      setTabArray(processTabArray(newTabArray, width, menuWidth));
    }
  }, [width, JSON.stringify(tabWidths.current), selectedTabId, menuWidth, tabSpacing, JSON.stringify(inputTabArray.current), triggerRerender]);
  /**
   * Takes in the tabs with widths and computes what tabs are displayed and which are in the menu
   */
  const processTabArray = (tabs, parentWidth, menuWidth) => {
    var _a;
    if (!parentWidth || !((_a = tabs[0]) === null || _a === void 0 ? void 0 : _a.width) || !menuWidth) {
      return [];
    }
    const outputTabs = tabs.map(tab => {
      return Object.assign(Object.assign({}, tab), {
        isInMenu: false,
        isSelected: tab.id === selectedTabId
      });
    });
    //determine if a menu is required
    const isMenuRequired = tabs.length > maxTabs || tabs.reduce((previous, tab) => previous + tab.width, 0) > parentWidth;
    if (!isMenuRequired) {
      return outputTabs;
    }
    //make sure selected tab and menu are always visible
    let widthRemaining = parentWidth - menuWidth;
    const selectedItemIndex = tabs.findIndex(tab => tab.id === selectedTabId);
    let currentTabs = 0;
    if (selectedItemIndex >= 0) {
      currentTabs++;
      widthRemaining -= tabs[selectedItemIndex].width;
      if (!widthRemaining) {
        return [];
      }
    }
    //go from 0 to end, skipping selectedItem and determine where to put the tab. Make sure not to exceed max tabs
    for (let i = 0; i < tabs.length; i++) {
      if (i === selectedItemIndex) continue;
      widthRemaining -= tabs[i].width;
      if (currentTabs < maxTabs && !widthRemaining) {
        return [];
      }
      if (widthRemaining <= 0 || currentTabs >= maxTabs) {
        outputTabs[i].isInMenu = true;
      }
      currentTabs++;
    }
    return outputTabs;
  };
  const generateMenu = (rough = false) => {
    const menu = jsx("div", {
      ref: rough ? menuTabRef : undefined,
      className: `kxi-menu-item${rough ? '-rough' : ''}`,
      children: jsx(Dropdown, Object.assign({
        data: tabArray.filter(tab => tab.isInMenu),
        placeholder: localization.more,
        inMenuSearch: true,
        onValueChange: item => {
          if (!item.disabled) {
            if (componentControlledTabChange) {
              setSelectedTabId(item.id);
            }
            if (onTabChange) {
              onTabChange({
                tab: tabArray.find(tab => tab.id === item.id)
              });
            }
          }
        },
        select: "none",
        color: "orca",
        contrast: contrast,
        localization: localization.dropdown
      }, dropdown), "menuDropdown")
    }, "menuItem");
    return rough ? jsx(ReactResizeDetector, {
      handleWidth: true,
      onResize: width => setMenuWidth(width),
      targetRef: menuTabRef,
      children: menu
    }, `resizeDetector-${rough ? 'rough-menu' : 'menu'}`) : menu;
  };
  const generateRoughTabs = () => {
    if (inputTabArray.current) {
      let roughTabsToGenerate = inputTabArray.current.slice(0, maxTabs);
      const selectedTabIndex = inputTabArray.current.findIndex(tab => tab.id === selectedTabId);
      if (selectedTabIndex >= maxTabs) {
        roughTabsToGenerate = [...roughTabsToGenerate, inputTabArray.current[selectedTabIndex]];
      }
      const roughTabs = roughTabsToGenerate.map(tab => {
        return jsx(TabBarItem, {
          title: tab.title,
          id: `layout-${tab.id}`,
          tabSpacing: tabSpacing,
          style: {
            marginRight: tabSpacing
          },
          alert: tab.alert,
          className: "kxi-tab-bar-item-rough",
          onWidthChange: width => {
            var _a, _b;
            const inputIndex = (_a = inputTabArray.current) === null || _a === void 0 ? void 0 : _a.findIndex(inputTab => tab.id === inputTab.id);
            if (inputIndex === undefined || inputIndex < 0) {
              return;
            }
            // We use a useRef to keep the tab widths because updating a ref does not trigger a re-render.
            // This optimizes the code and prevents lost updates
            if (!tabWidths.current) {
              tabWidths.current = new Array((_b = inputTabArray.current) === null || _b === void 0 ? void 0 : _b.length);
            }
            if (tabWidths.current[inputIndex] !== width) {
              setTriggerRerender(uuidv4());
            }
            tabWidths.current[inputIndex] = width;
          },
          color: color,
          tooltip: tooltip,
          selected: tab.isSelected,
          contrast: contrast,
          maxCharsAllowed: maxCharsAllowed,
          disabled: tab.disabled
        }, `layout-${tab.id}-${uuidv4()}`);
      });
      roughTabs.push(generateMenu(true));
      return roughTabs;
    }
  };
  const generateFinalTabs = () => {
    const visibleTabs = tabArray.map(tab => {
      return tab.isInMenu ? null : jsx(TabBarItem, {
        title: tab.title,
        id: tab.id,
        tabSpacing: tabSpacing,
        color: color,
        onClick: tabId => {
          if (componentControlledTabChange) {
            setSelectedTabId(tabId);
          }
          if (onTabChange) {
            onTabChange({
              tab: tabArray.find(tab => tab.id === tabId)
            });
          }
        },
        tooltip: tooltip,
        alert: tab.alert,
        selected: tab.isSelected,
        contrast: contrast,
        maxCharsAllowed: maxCharsAllowed,
        disabled: tab.disabled,
        style: stretch ? {
          height
        } : {}
      }, `final-${tab.id}`);
    });
    const hasMenu = tabArray.findIndex(tab => tab.isInMenu) >= 0;
    return jsxs(Fragment, {
      children: [visibleTabs, hasMenu ? generateMenu() : null]
    });
  };
  return jsx(ReactResizeDetector, {
    handleWidth: true,
    onResize: React.useCallback((w, h) => {
      setWidth(w);
      setHeight(h);
    }, []),
    targetRef: widthRef,
    children: /*#__PURE__*/createElement("div", Object.assign({}, debugService.componentProps, {
      id: tabBarId,
      ref: mergeRefs([widthRef, ref]),
      className: `kxi-tab-bar ${hasTransparentBackground ? 'kxi-tab-bar-transparent' : ''} ${color ? normalizeColor(color) : ''} ${stretch ? 'kxi-stretch-height' : ''} ${contrast === 'high' ? ' kxi-high-contrast' : ''}`,
      "data-testid": "outerDiv",
      key: `overallOuterDiv-${tabBarId}`
    }, ingredients), generateFinalTabs(), jsx("div", {
      id: `${tabBarId}-layout`,
      className: "kxi-tab-bar-layout-rough",
      children: generateRoughTabs()
    }, `${tabBarId}-layout`))
  }, `resizeDetector-${tabBarId}`);
});
TabBar.displayName = 'TabBar';

class VerticalTabBarProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", Array)], VerticalTabBarProps.prototype, "data", void 0);
__decorate([Bound(), __metadata("design:type", String)], VerticalTabBarProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], VerticalTabBarProps.prototype, "activeTabId", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], VerticalTabBarProps.prototype, "tooltipOptions", void 0);
class VerticalTabBarEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], VerticalTabBarEvents.prototype, "onTabChange", void 0);
/**
 * Description: A vertical version of the tab bar using icons only
 */
const VerticalTabBar = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(VerticalTabBar, props);
  const {
      className,
      data,
      activeTabId,
      color = 'orca',
      onTabChange,
      tooltipOptions,
      observer
    } = props,
    ingredients = __rest(props, ["className", "data", "activeTabId", "color", "onTabChange", "tooltipOptions", "observer"]);
  const [selectedTabId, setSelectedTabId] = React.useState();
  const [tabList, setTabList] = React.useState([]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('VerticalTabBar', {
      data
    });
  }, [data]);
  React.useEffect(() => {
    setSelectedTabId(activeTabId);
  }, [activeTabId]);
  React.useEffect(() => {
    if (observer) {
      observer.activeTabId = selectedTabId;
    }
  }, [observer, selectedTabId]);
  React.useEffect(() => {
    setTabList(data.map(tab => {
      return Object.assign(Object.assign({}, tab), {
        id: tab.id || uuidv4()
      });
    }));
  }, [data]);
  const handleKeyPress = (e, tab) => {
    if (e.key === 'Enter' && (tab === null || tab === void 0 ? void 0 : tab.id)) {
      setSelectedTabId(tab === null || tab === void 0 ? void 0 : tab.id);
      if (onTabChange) onTabChange({
        tab
      });
    }
  };
  const generateTabs = tabs => {
    return tabs.map(tab => {
      var _a;
      if (!tab.icon) {
        devOnlyWarn(`The Kitchen - Vertical Tab Bar: You have not passed in an icon for tab id ${tab.id}.`);
      }
      let tabDOM = jsxs("div", {
        tabIndex: 0,
        role: !tab.hint && !tab.hasSeparator ? 'tab' : 'button',
        "aria-label": tab.hint || tab.icon,
        "aria-disabled": tab.disabled,
        className: classNames(`kxi-vertical-tab-bar-tab ${normalizeColor(color)}`, {
          'kxi-vertical-tab-bar-selected': tab.id === selectedTabId,
          'kxi-vertical-tab-disabled': tab.disabled
        }),
        onClick: () => {
          if (!tab.disabled) {
            setSelectedTabId(tab.id);
            if (onTabChange) onTabChange({
              tab
            });
          }
        },
        onKeyPress: e => handleKeyPress(e, tab),
        "data-testid": tab.id,
        children: [jsx("div", {
          className: `kxi-vertical-tab-bar-indicator ${tab.id === selectedTabId ? 'kxi-vertical-tab-bar-selected' : ''}`,
          "data-testid": tab.id === selectedTabId ? 'vertical-tab-indicator' : ''
        }), jsx(XIIcon, {
          color: tab.disabled ? 'disabled' : 'orca',
          icon: tab.icon,
          iconSize: 30
        })]
      }, `tab-${tab.id}`);
      if (tab.hint || tab.disabled && tab.disabledReason) {
        tabDOM = /*#__PURE__*/createElement(ToolTipWrapper, Object.assign({
          role: !tab.hasSeparator ? 'tab' : undefined,
          direction: 3
        }, tooltipOptions, {
          text: (_a = tab.disabledReason) !== null && _a !== void 0 ? _a : tab.hint,
          innerStyle: {
            display: undefined,
            width: '100%'
          },
          key: `tabWrapper-${tab.id}`,
          stretch: true
        }), tabDOM);
      }
      if (tab.hasSeparator) {
        tabDOM = jsxs("span", {
          onKeyPress: e => handleKeyPress(e, tab),
          role: "tab",
          className: "kxi-separator-container",
          children: [tabDOM, jsx("div", {
            className: "kxi-separator",
            "data-testid": "vertical-tab-separator"
          }, `separator-${tab.id}`)]
        }, `separatorContainer-${tab.id}`);
      }
      return tabDOM;
    });
  };
  return jsx("div", Object.assign({}, debugService.componentProps, {
    role: "tablist",
    "data-testid": "vertical-tab-root"
  }, ingredients, {
    className: `kxi-vertical-tab-bar kxi-stretch-height ${className || ''}`,
    ref: ref,
    children: generateTabs(tabList)
  }));
});
VerticalTabBar.displayName = 'VerticalTabBar';

const _HorizontalTabBarVisualStateOptions = ['normal', 'stacked'];

class TableProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], TableProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], TableProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TableProps.prototype, "semiboldTitle", void 0);
__decorate([Bound(), __metadata("design:type", Array)], TableProps.prototype, "headerData", void 0);
__decorate([Bound(), __metadata("design:type", Array)], TableProps.prototype, "rowData", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TableProps.prototype, "sortable", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TableProps.prototype, "total", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TableProps.prototype, "containerView", void 0);
class TableEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], TableEvents.prototype, "onSort", void 0);
/**
 * Description: A standard table component that can display data in a formatted way.
 */
const Table = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(Table, props);
  const {
      className,
      title,
      subtitle,
      semiboldTitle = true,
      sortable,
      onSort = undefined,
      headerData,
      rowData = [],
      containerView
    } = props,
    ingredients = __rest(props, ["className", "title", "subtitle", "semiboldTitle", "sortable", "onSort", "headerData", "rowData", "containerView"]);
  const [width, setWidth] = React.useState(0);
  const [activeSort, setActiveSort] = React.useState({
    key: '',
    dir: ''
  });
  const wrapperRef = React.useRef(null);
  React.useEffect(() => {
    if (onSort && sortable) onSort(activeSort);
  }, [activeSort]);
  const changeSort = key => {
    let dir = 'asc';
    if (activeSort && key === activeSort.key) {
      if (activeSort.dir === 'asc') dir = 'desc';else {
        setActiveSort({
          key: '',
          dir: ''
        });
        return;
      }
    }
    setActiveSort({
      key,
      dir
    });
  };
  const table = jsx(ReactResizeDetector, {
    onResize: React.useCallback(width => setWidth(width !== null && width !== void 0 ? width : 0), []),
    handleWidth: true,
    targetRef: wrapperRef,
    refreshMode: "debounce",
    refreshRate: 0,
    children: jsxs("div", Object.assign({}, debugService.componentProps, ingredients, {
      className: `kxi-table-wrapper ${className || ''}`,
      ref: mergeRefs([wrapperRef, ref]),
      children: [jsxs("div", {
        className: "kxi-table-header",
        children: [title ? jsx("h5", {
          className: `${semiboldTitle ? 'kxi-text-600' : ''}`,
          children: title
        }) : null, subtitle ? jsx("h6", {
          className: "orca",
          children: subtitle
        }) : null]
      }), jsxs("table", {
        className: "kxi-table kxi-grey",
        children: [jsx("thead", {
          children: jsx("tr", {
            children: headerData.map(header => jsx("td", {
              className: `${onSort && sortable ? 'kxi-table-header-sortable' : ''}`,
              style: {
                width: width / headerData.length,
                background: header.bgColor || '',
                color: header.textColor || ''
              },
              id: header.id,
              onClick: onSort && sortable ? () => changeSort(header.field) : undefined,
              children: jsxs("div", {
                children: [jsx("span", {
                  children: header.title
                }), activeSort.dir && header.field === activeSort.key ? jsx(XIIcon, {
                  style: {
                    color: header.textColor || XIColors.orca.primary
                  },
                  icon: activeSort.dir === 'asc' ? 'sort_order_asc' : 'sort_order_desc',
                  iconSize: 18
                }) : jsx("span", {
                  style: {
                    width: 18,
                    height: 18
                  }
                }) // styling so the icon doesn't shift the column
                ]
              })
            }, header.id))
          })
        }), jsx("tbody", {
          children: rowData.map(row => jsx("tr", {
            id: row.id,
            children: headerData.map(header => jsx("td", {
              style: {
                maxWidth: width / headerData.length
              },
              children: row[header.field]
            }, row[header.field]))
          }, row.id))
        })]
      })]
    }))
  });
  return containerView ? jsx(BasicTile, {
    color: "orca",
    stretch: true,
    padding: 0,
    children: table
  }) : table;
});
Table.displayName = 'Table';

class EditableTextProps extends ObservablePropsMixin(InputHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], EditableTextProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", String)], EditableTextProps.prototype, "placeholder", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], EditableTextProps.prototype, "prePopulateStatus", void 0);
__decorate([Bound(), __metadata("design:type", String)], EditableTextProps.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], EditableTextProps.prototype, "stretch", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], EditableTextProps.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", Object)], EditableTextProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", String)], EditableTextProps.prototype, "type", void 0);
class EditableTextEvents extends InputHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], EditableTextEvents.prototype, "onInputEnter", void 0);
/**
 * Description: TextField where the input grows with the text typed.
 */
const EditableText = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(EditableText, props);
  const {
      placeholder,
      contrast = 'normal',
      stretch = false,
      status,
      value,
      disabled,
      type,
      className,
      style,
      observer,
      onChange,
      onClick,
      onInputEnter,
      prePopulateStatus = false
    } = props,
    ingredients = __rest(props, ["placeholder", "contrast", "stretch", "status", "value", "disabled", "type", "className", "style", "observer", "onChange", "onClick", "onInputEnter", "prePopulateStatus"]);
  const validType = checkIfTextInputTypeIsValid('EditableText', type);
  if (status) {
    if (!status.state && status.text) {
      devOnlyWarn('The Kitchen: You must pass a state into EditableText.');
    }
  }
  const inputEl = React.useRef(null);
  const [text, setText] = React.useState('');
  React.useEffect(() => {
    setText(value || '');
  }, [value]);
  const [errorState,, errorStateClass] = useTextFieldStatus(text, 'EditableText', status);
  const contrastClass = contrast === 'high' ? ' kxi-high-contrast' : '';
  const handleChange = e => {
    const newText = e.target.value;
    setText(newText);
    if (observer) {
      observer.value = newText;
    }
    if (onChange) onChange(e);
  };
  const handleEnter = e => {
    if (e.key === 'Enter' || e.which === 13 || e.keyCode === 13) {
      inputEl.current.blur();
      if (onInputEnter) onInputEnter({
        text
      });
    }
  };
  const handleClick = e => {
    inputEl.current.select();
    if (onClick) onClick(e);
  };
  let state;
  if (status) {
    state = status.state;
  }
  let inputClasses = 'kxi-editable-text-input kxi-text-normal orca';
  let inputWrapperClasses = 'kxi-editable-text-input-wrapper orca';
  if (disabled) {
    inputClasses += ' disabled';
    inputWrapperClasses += ' kxi-disabled disabled';
  }
  if (state) {
    inputWrapperClasses += ` kxi-status kxi-${state}`;
  }
  if (contrast === 'high') {
    inputWrapperClasses += ' kxi-high-contrast';
    inputClasses += ' kxi-high-contrast';
  }
  if (className) inputClasses += ` ${className}`;
  if (stretch) {
    inputWrapperClasses += ' kxi-stretch-width';
  }
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    className: `kxi-editable-text ${stretch ? 'kxi-stretch-width' : ''}`,
    children: [jsx("div", {
      className: inputWrapperClasses,
      children: jsx("input", Object.assign({
        className: inputClasses,
        style: Object.assign({}, style),
        ref: mergeRefs([inputEl, ref]),
        value: text,
        placeholder: placeholder,
        disabled: disabled,
        readOnly: disabled,
        type: validType ? type : undefined,
        "data-testid": "input"
      }, ingredients, {
        onClick: handleClick,
        onKeyPress: handleEnter,
        onChange: handleChange
      }))
    }), jsx(ErrorStateUI, {
      prePopulateStatus: prePopulateStatus,
      errorStateClass: errorStateClass,
      contrastClass: contrastClass,
      errorState: errorState,
      disabled: disabled || false,
      text: text
    })]
  }));
});
EditableText.displayName = 'EditableText';

class TextAreaHTMLAttributes extends HTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], TextAreaHTMLAttributes.prototype, "autoComplete", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TextAreaHTMLAttributes.prototype, "autoFocus", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TextAreaHTMLAttributes.prototype, "cols", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaHTMLAttributes.prototype, "dirName", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TextAreaHTMLAttributes.prototype, "disabled", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaHTMLAttributes.prototype, "form", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TextAreaHTMLAttributes.prototype, "maxLength", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TextAreaHTMLAttributes.prototype, "minLength", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaHTMLAttributes.prototype, "name", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaHTMLAttributes.prototype, "placeholder", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TextAreaHTMLAttributes.prototype, "readOnly", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TextAreaHTMLAttributes.prototype, "required", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TextAreaHTMLAttributes.prototype, "rows", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TextAreaHTMLAttributes.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaHTMLAttributes.prototype, "wrap", void 0);
class TextAreaHTMLEvents extends DOMEvents {}
__decorate([Bound(), __metadata("design:type", Function)], TextAreaHTMLEvents.prototype, "onChange", void 0);

const Label = props => {
  const {
    contrast,
    inputId,
    label
  } = props;
  return jsx(Fragment, {
    children: label ? jsx("label", {
      className: `kxi-input-label kxi-${contrast}-contrast`,
      htmlFor: inputId,
      children: label
    }) : null
  });
};

class TextAreaProps extends ObservablePropsMixin(TextAreaHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], TextAreaProps.prototype, "placeholder", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaProps.prototype, "value", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaProps.prototype, "contrast", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TextAreaProps.prototype, "textSize", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TextAreaProps.prototype, "status", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TextAreaProps.prototype, "prePopulateStatus", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaProps.prototype, "label", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaProps.prototype, "inputId", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TextAreaProps.prototype, "maxCharsAllowed", void 0);
__decorate([Bound(), __metadata("design:type", Number)], TextAreaProps.prototype, "height", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TextAreaProps.prototype, "resize", void 0);
__decorate([Bound(), __metadata("design:type", String)], TextAreaProps.prototype, "visualState", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], TextAreaProps.prototype, "selectAllOnFocus", void 0);
__decorate([Bound(), __metadata("design:type", Object)], TextAreaProps.prototype, "localization", void 0);
class TextAreaEvents extends TextAreaHTMLEvents {}
/**
 * Description: XI Themed textarea.
 */
const TextArea = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(TextArea, props);
  const {
      className,
      placeholder,
      resize = false,
      label,
      value,
      contrast = 'normal',
      textSize = 'normal',
      prePopulateStatus = false,
      selectAllOnFocus = true,
      maxCharsAllowed,
      disabled = false,
      inputId,
      status,
      observer,
      id,
      visualState = 'normal',
      _controlledState = false,
      onChange,
      onClick,
      style,
      height
    } = props,
    ingredients = __rest(props, ["className", "placeholder", "resize", "label", "value", "contrast", "textSize", "prePopulateStatus", "selectAllOnFocus", "maxCharsAllowed", "disabled", "inputId", "status", "observer", "id", "visualState", "_controlledState", "onChange", "onClick", "style", "height"]);
  const [finalInputId] = React.useState(inputId ? inputId : `${id ? id : uuidv4()}-input`);
  const [text, setText] = React.useState('');
  const textAreaRef = React.useRef(null);
  // custom hooks
  const [errorState, setErrorState, errorStateClass] = useTextFieldStatus(text, 'TextArea', status, maxCharsAllowed);
  useTextFieldWarning('TextArea', _controlledState, onChange);
  const resizeClass = resize ? 'kxi-input-resize' : '';
  React.useEffect(() => {
    if (!_controlledState) {
      setText(value || '');
    }
  }, [value, _controlledState]);
  const handleTextAreaChange = e => {
    const fireFunction = handleInput(disabled, _controlledState, e, setErrorState, setText, maxCharsAllowed, observer);
    if (fireFunction && onChange) {
      onChange(e);
    }
  };
  const inputValue = _controlledState ? value || '' : text;
  const contrastClass = `kxi-${contrast}-contrast`;
  return jsxs("div", Object.assign({
    className: "kxi-input-outer-wrapper orca"
  }, debugService.componentProps, {
    children: [jsx(Label, {
      contrast: contrast,
      label: label,
      inputId: finalInputId
    }), jsx("textarea", Object.assign({
      style: Object.assign(Object.assign({}, style), {
        height
      }),
      ref: mergeRefs([textAreaRef, ref]),
      id: finalInputId,
      className: `kxi-input kxi-border ${contrastClass} ${errorStateClass} ${resizeClass} ${className || ''} kxi-text-${textSize} ${visualState === 'condensed' ? 'kxi-condensed-textarea' : ''}`,
      disabled: disabled,
      placeholder: placeholder,
      value: inputValue,
      onChange: handleTextAreaChange,
      "data-testid": "textArea",
      maxLength: !(maxCharsAllowed === null || maxCharsAllowed === void 0 ? void 0 : maxCharsAllowed.allowOvertype) ? maxCharsAllowed === null || maxCharsAllowed === void 0 ? void 0 : maxCharsAllowed.max : undefined
    }, ingredients, {
      onClick: e => {
        if ((textAreaRef === null || textAreaRef === void 0 ? void 0 : textAreaRef.current) && selectAllOnFocus) {
          textAreaRef.current.select();
        }
        if (onClick) onClick(e);
      }
    })), jsx(ErrorStateUI, {
      prePopulateStatus: prePopulateStatus,
      errorStateClass: errorStateClass,
      contrastClass: contrastClass,
      errorState: errorState,
      maxCharsAllowed: maxCharsAllowed,
      disabled: disabled,
      text: text
    })]
  }));
});
TextArea.displayName = 'TextArea';

const Wave = props => {
  const {
    opacity,
    top,
    left,
    cloud,
    testId
  } = props;
  return jsx("svg", {
    className: `kxi-wave-tile-waves ${normalizeColor(cloud)}`,
    "data-testid": testId,
    style: {
      top,
      left
    },
    xmlns: "http://www.w3.org/2000/svg",
    children: jsx("path", {
      d: "M130.34467,0C185.69017,0,217.118,25.192,269.199,25.192,321.279,25.192,371.906,0,422.775,0,473.643,0,533.96,25.192,575.381,25.192,616.803,25.192,312.485,0,712.485,0V50.385H.562s-2.602-13.72,3.391-16.23C65.148,8.526,94.97768,0,130.34467,0Z",
      opacity: opacity
    })
  });
};

class WaveTileProps extends ButtonHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], WaveTileProps.prototype, "title", void 0);
__decorate([Bound(), __metadata("design:type", String)], WaveTileProps.prototype, "subtitle", void 0);
__decorate([Bound(), __metadata("design:type", String)], WaveTileProps.prototype, "size", void 0);
__decorate([Bound(), __metadata("design:type", String)], WaveTileProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", String)], WaveTileProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], WaveTileProps.prototype, "isActive", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], WaveTileProps.prototype, "hasWave", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], WaveTileProps.prototype, "hideSubtitleOnMobile", void 0);
class WaveTileEvents extends ButtonHTMLEvents {}
/**
 * Description: Standard, convenient tile that shows a lovely wave.
 */
const WaveTile = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(WaveTile, props);
  const {
      className,
      subtitle,
      title,
      size = 'large',
      isActive = false,
      hasWave = true,
      icon,
      color = 'CX',
      hideSubtitleOnMobile,
      onMouseOver,
      onMouseLeave
    } = props,
    ingredients = __rest(props, ["className", "subtitle", "title", "size", "isActive", "hasWave", "icon", "color", "hideSubtitleOnMobile", "onMouseOver", "onMouseLeave"]);
  React.useEffect(() => {
    throwIfRequiredPropNotProvided('WaveTile', {
      title,
      subtitle,
      icon
    });
  }, [icon, subtitle, title]);
  const [firstWave, setFirstWave] = React.useState(-96);
  const [secondWave, setSecondWave] = React.useState(-20);
  const [thirdWave, setThirdWave] = React.useState(-115);
  const handleHover = e => {
    setFirstWave(-212);
    setSecondWave(20);
    setThirdWave(-75);
    if (onMouseOver) onMouseOver(e);
  };
  const handleLeave = e => {
    setFirstWave(-96);
    setSecondWave(-20);
    setThirdWave(-115);
    if (onMouseLeave) onMouseLeave(e);
  };
  const waveTop = size === 'large' ? -100 : -120;
  return jsxs("button", Object.assign({}, debugService.componentProps, {
    className: `kxi-button kxi-tile-shadows kxi-tile kxi-wave-tile-button kxi-${size} kxi-${isActive ? 'active' : 'inactive'} ${className || ''} ${normalizeColor(color)} ${hasWave ? 'kxi-wave-tile-wave' : ''}`,
    onMouseOver: handleHover,
    onMouseLeave: handleLeave
  }, ingredients, {
    ref: ref,
    children: [hasWave && jsxs(Fragment, {
      children: [jsx(Wave, {
        top: waveTop,
        left: firstWave,
        opacity: 1,
        cloud: color,
        testId: "Wave 1"
      }), jsx(Wave, {
        top: waveTop,
        left: secondWave,
        opacity: 0.2,
        cloud: color,
        testId: "Wave 2"
      }), jsx(Wave, {
        top: waveTop,
        left: thirdWave,
        opacity: 0.2,
        cloud: color,
        testId: "Wave 3"
      })]
    }), jsx("div", {
      className: "kxi-wave-tile-mobile-icon",
      children: size === 'large' && icon && jsx(XIIcon, {
        icon: icon,
        iconSize: 30,
        contrast: isActive ? 'high' : 'normal'
      })
    }), jsxs("div", {
      className: "kxi-wave-tile-body kxi-padding-left-normal kxi-padding-right-normal",
      children: [size === 'large' && icon && jsx(XIIcon, {
        icon: icon,
        iconSize: 60
      }), title]
    }), jsxs("div", {
      className: `kxi-wave-tile-footer kxi-align-center ${isActive ? normalizeColor(color) : ''} ${hideSubtitleOnMobile ? 'kxi-wave-tile-subtitle-hidden' : ''}`,
      children: [jsx("span", {
        className: "kxi-wave-tile-subtitle",
        children: subtitle
      }), size === 'large' && jsx(XIIcon, {
        icon: "arrow_right",
        contrast: isActive ? 'high' : 'normal',
        iconSize: 30
      })]
    }), jsx("div", {
      className: `kxi-wave-tile-mobile ${isActive ? normalizeColor(color) : ''}`,
      children: jsx(XIIcon, {
        icon: "arrow_right",
        contrast: isActive ? 'high' : 'normal',
        iconSize: 30
      })
    })]
  }));
});
WaveTile.displayName = 'WaveTile';

const _WaveTileSizeOptions = ['small', 'large'];
const _BasicTileTitleColorOptions = [..._ColorsOptions, ..._SentimentsOptions, ..._ErrorStatesOptions, ..._StatusStatesOptions];
const _BasicTileVisualStateOptions = ['normal', 'condensed', 'minimal', 'auto'];

class ToolTipContentProps extends DivHTMLAttributes {}
__decorate([Bound(), __metadata("design:type", String)], ToolTipContentProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToolTipContentProps.prototype, "link", void 0);
__decorate([Bound(), __metadata("design:type", ToolTipProps)], ToolTipContentProps.prototype, "tooltip", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToolTipContentProps.prototype, "iconSize", void 0);
__decorate([Bound(), __metadata("design:type", String)], ToolTipContentProps.prototype, "contrast", void 0);
class ToolTipContentEvents extends DivHTMLEvents {}
/**
 * Description: Render this tooltip, and have a convenient informational popup!
 */
const ToolTipContent = /*#__PURE__*/React.forwardRef((props, ref) => {
  useDebugService(ToolTipContent, props);
  const {
      text,
      link,
      iconSize,
      tooltip,
      contrast = 'normal'
    } = props,
    ingredients = __rest(props, ["text", "link", "iconSize", "tooltip", "contrast"]);
  const [isOpen, setIsOpen] = React.useState(false);
  throwIfRequiredPropNotProvided('ToolTipContent', {
    tooltip
  });
  return jsx(ToolTipWrapper, Object.assign({}, tooltip, ingredients, {
    ref: ref,
    onOpenChange: setIsOpen,
    children: text && link ? jsx("a", {
      className: "kxi-tooltip-link",
      href: link,
      children: text
    }) : text ? jsx("span", {
      children: text
    }) : jsx(XIIcon, {
      iconSize: iconSize,
      icon: tooltip.isOpenOnClick && isOpen ? 'info_filled' : 'info',
      contrast: contrast
    })
  }));
});
ToolTipContent.displayName = 'ToolTipContent';

class AppTitleBarProps extends ObservablePropsMixin(DivHTMLAttributes) {}
__decorate([Bound(), __metadata("design:type", String)], AppTitleBarProps.prototype, "text", void 0);
__decorate([Bound(), __metadata("design:type", String)], AppTitleBarProps.prototype, "color", void 0);
__decorate([Bound(), __metadata("design:type", String)], AppTitleBarProps.prototype, "icon", void 0);
__decorate([Bound(), __metadata("design:type", String)], AppTitleBarProps.prototype, "appIcon", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], AppTitleBarProps.prototype, "disableButton", void 0);
__decorate([Bound(), __metadata("design:type", Boolean)], AppTitleBarProps.prototype, "isTextButton", void 0);
class AppTitleBarEvents extends DivHTMLEvents {}
__decorate([Bound(), __metadata("design:type", Function)], AppTitleBarEvents.prototype, "onButtonClick", void 0);
/**
 * Description: Previously GradientTopBar, this slick gradient bar can display icons, texts, and anything you want in a cloud-based fashion.
 */
const AppTitleBar = /*#__PURE__*/React.forwardRef((props, ref) => {
  const debugService = useDebugService(AppTitleBar, props);
  const {
      children,
      color,
      icon,
      text,
      onButtonClick,
      isTextButton = true,
      appIcon,
      style,
      className,
      disableButton
    } = props,
    ingredients = __rest(props, ["children", "color", "icon", "text", "onButtonClick", "isTextButton", "appIcon", "style", "className", "disableButton"]);
  const textToDisplay = isTextButton ? jsx(HeadingButton, {
    title: text,
    onClick: event => onButtonClick === null || onButtonClick === void 0 ? void 0 : onButtonClick({
      event
    }),
    color: "white",
    contrast: "high",
    className: `kxi-title-bar-heading ${icon || appIcon ? '' : 'kxi-title-bar-no-icon'}`,
    disabled: disableButton
  }) : jsx("p", {
    role: "heading",
    "aria-level": 1,
    className: `kxi-high-contrast kxi-text-large kxi-text-400 kxi-text-wrap-nowrap ${icon || appIcon ? 'kxi-with-icon' : ''}`,
    children: text
  });
  return jsxs("div", Object.assign({}, debugService.componentProps, {
    role: "banner",
    style: Object.assign({}, style),
    className: `kxi-title-bar kxi-gradient ${className || ''} ${color ? normalizeColor(color) : ''}`
  }, ingredients, {
    ref: ref,
    children: [jsx("div", {
      className: `kxi-title-wrapper ${!textToDisplay || !text ? 'no-text' : ''}`,
      children: jsxs("div", {
        className: `kxi-title-reserved ${!textToDisplay || !text ? 'no-text' : ''}`,
        children: [(icon || appIcon) && jsxs("div", {
          role: `${isTextButton ? 'button' : 'img'}`,
          className: `kxi-title-bar-icon ${isTextButton ? 'kxi-pointer' : ''}`,
          onClick: isTextButton ? event => onButtonClick === null || onButtonClick === void 0 ? void 0 : onButtonClick({
            event
          }) : undefined,
          children: [icon && !appIcon && jsx(XIIcon, {
            icon: icon,
            iconSize: 30,
            contrast: "high"
          }), appIcon && jsx(XIAppIcon, {
            icon: appIcon,
            iconSize: 30
          })]
        }), text && textToDisplay]
      })
    }), jsx("div", {
      className: `kxi-child-content ${(!textToDisplay || !text) && !icon && !appIcon ? 'kxi-no-padding' : ''}`,
      children: jsx(Slot, {
        name: "children",
        reactChildren: children
      })
    })]
  }));
});
AppTitleBar.displayName = 'AppTitleBar';

class AccordionComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Accordion,
      props: new AccordionProps(),
      events: new AccordionEvents()
    });
  }
}
AccordionComponent.tag = 'xi-accordion';

class AccordionItemComponent extends KitchenComponent {
  constructor() {
    super({
      Component: AccordionItem,
      props: new AccordionItemProps(),
      events: new AccordionItemEvents()
    });
  }
}
AccordionItemComponent.tag = 'xi-accordion-item';

class AlertInlineMainComponent extends KitchenComponent {
  constructor() {
    super({
      Component: AlertInlineMain,
      props: new AlertInlineMainProps(),
      events: new AlertInlineMainEvents()
    });
  }
}
AlertInlineMainComponent.tag = 'xi-alert-inline-main';

class SnackBarComponent extends KitchenComponent {
  constructor() {
    super({
      Component: SnackBar,
      props: new SnackBarProps(),
      events: new SnackBarEvents()
    });
  }
}
SnackBarComponent.tag = 'xi-snack-bar';

class SuccessToasterComponent extends KitchenComponent {
  constructor() {
    super({
      Component: SuccessToaster,
      props: new SuccessToasterProps(),
      events: new SuccessToasterEvents()
    });
  }
}
SuccessToasterComponent.tag = 'xi-success-toaster';

class WarningPopupComponent extends KitchenComponent {
  constructor() {
    super({
      Component: WarningPopup,
      props: new WarningPopupProps(),
      events: new WarningPopupEvents()
    });
  }
}
WarningPopupComponent.tag = 'xi-warning-popup';

class CheckboxComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Checkbox,
      props: new CheckboxProps(),
      events: new CheckboxEvents()
    });
  }
}
CheckboxComponent.tag = 'xi-checkbox';

class DefaultButtonComponent extends KitchenComponent {
  constructor() {
    super({
      Component: DefaultButton,
      props: new DefaultButtonProps(),
      events: new DefaultButtonEvents()
    });
  }
}
DefaultButtonComponent.tag = 'xi-default-button';

class FABComponent extends KitchenComponent {
  constructor() {
    super({
      Component: FAB,
      props: new FABProps(),
      events: new FABEvents()
    });
  }
}
FABComponent.tag = 'xi-fab';

class FilterButtonComponent extends KitchenComponent {
  constructor() {
    super({
      Component: FilterButton,
      props: new FilterButtonProps(),
      events: new FilterButtonEvents()
    });
  }
}
FilterButtonComponent.tag = 'xi-filter-button';

class HeadingButtonComponent extends KitchenComponent {
  constructor() {
    super({
      Component: HeadingButton,
      props: new HeadingButtonProps(),
      events: new HeadingButtonEvents()
    });
  }
}
HeadingButtonComponent.tag = 'xi-heading-button';

class IconButtonComponent extends KitchenComponent {
  constructor() {
    super({
      Component: IconButton,
      props: new IconButtonProps(),
      events: new IconButtonEvents()
    });
  }
}
IconButtonComponent.tag = 'xi-icon-button';

class SentimentButtonComponent extends KitchenComponent {
  constructor() {
    super({
      Component: SentimentButton,
      props: new SentimentButtonProps(),
      events: new SentimentButtonEvents()
    });
  }
}
SentimentButtonComponent.tag = 'xi-sentiment-button';

class TileButtonComponent extends KitchenComponent {
  constructor() {
    super({
      Component: TileButton,
      props: new TileButtonProps(),
      events: new TileButtonEvents()
    });
  }
}
TileButtonComponent.tag = 'xi-tile-button';

class CommentComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Comment$1,
      props: new CommentProps(),
      events: new CommentEvents()
    });
  }
}
CommentComponent.tag = 'xi-comment';

class CondensedExperienceCardComponent extends KitchenComponent {
  constructor() {
    super({
      Component: CondensedExperienceCard,
      props: new CondensedExperienceCardProps(),
      events: new CondensedExperienceCardEvents()
    });
  }
}
CondensedExperienceCardComponent.tag = 'xi-condensed-experience-card';

class ExperienceCardComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ExperienceCard,
      props: new ExperienceCardProps(),
      events: new ExperienceCardEvents()
    });
  }
}
ExperienceCardComponent.tag = 'xi-experience-card';

class ExperienceHeaderComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ExperienceHeader,
      props: new ExperienceHeaderProps(),
      events: new ExperienceHeaderEvents()
    });
  }
}
ExperienceHeaderComponent.tag = 'xi-experience-header';

class NotesComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Notes,
      props: new NotesProps(),
      events: new NotesEvents()
    });
  }
}
NotesComponent.tag = 'xi-notes';

class BigNumberComponent extends KitchenComponent {
  constructor() {
    super({
      Component: BigNumber,
      props: new BigNumberProps(),
      events: new BigNumberEvents()
    });
  }
}
BigNumberComponent.tag = 'xi-big-number';

class ChartWrapperComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ChartWrapper,
      props: new ChartWrapperProps(),
      events: new ChartWrapperEvents()
    });
  }
}
ChartWrapperComponent.tag = 'xi-chart-wrapper';

class Attributes {}
class ClassAttributes extends Attributes {}

/**
 * Runs ALL Kitchen components outside of Angular zones (similar to `NgZone.runOutsideAngular`).
 *
 * Note that this means you must run NgZone.run in event handlers for Angular change detection to run.
 *
 * This MUST be run before ANY Kitchen component is added to the DOM.
 * The best place to run this is in the constructor of your NgModule before any of the {ComponentName}Component.define() calls.
 */
function runAllComponentsOutsideAngular() {
  KitchenComponent.runOutsideAngular();
}

class AppSideDisplayComponent extends KitchenComponent {
  constructor() {
    super({
      Component: AppSideDisplay,
      props: new AppSideDisplayProps(),
      events: new AppSideDisplayEvents()
    });
  }
}
AppSideDisplayComponent.tag = 'xi-app-side-display';

class DrawerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Drawer,
      props: new DrawerProps(),
      events: new DrawerEvents()
    });
  }
}
DrawerComponent.tag = 'xi-drawer';

class SidePanelComponent extends KitchenComponent {
  constructor() {
    super({
      Component: SidePanel,
      props: new SidePanelProps(),
      events: new SidePanelEvents()
    });
  }
}
SidePanelComponent.tag = 'xi-side-panel';

class DropdownComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Dropdown,
      props: new DropdownProps(),
      events: new DropdownEvents()
    });
  }
}
DropdownComponent.tag = 'xi-dropdown';

class ListComponent extends KitchenComponent {
  constructor() {
    super({
      Component: List,
      props: new ListProps(),
      events: new ListEvents()
    });
  }
}
ListComponent.tag = 'xi-list';

/**
 * @todo Remove bug_setInternalPropertyOnObservedChange and bug_rerenderOnObservedChange
 *       when working on {@link https://jira.inmoment.com/browse/DEV-33331 DEV-33331}
 */
class ListItemComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ListItem,
      props: new ListItemProps(),
      events: new ListItemEvents()
    });
  }
}
ListItemComponent.tag = 'xi-list-item';

class AudioPlayerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: AudioPlayer,
      props: new AudioPlayerProps(),
      events: new AudioPlayerEvents()
    });
  }
}
AudioPlayerComponent.tag = 'xi-audio-player';

class ImageCarouselComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ImageCarousel,
      props: new ImageCarouselProps(),
      events: new ImageCarouselEvents()
    });
  }
}
ImageCarouselComponent.tag = 'xi-image-carousel';

class VideoPlayerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: VideoPlayer,
      props: new VideoPlayerProps(),
      events: new VideoPlayerEvents()
    });
  }
}
VideoPlayerComponent.tag = 'xi-video-player';

class GenericModalComponent extends KitchenComponent {
  constructor() {
    super({
      Component: GenericModal,
      props: new GenericModalProps(),
      events: new GenericModalEvents()
    });
  }
}
GenericModalComponent.tag = 'xi-generic-modal';

class PopoutComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Popout,
      props: new PopoutProps(),
      events: new PopoutEvents()
    });
  }
}
PopoutComponent.tag = 'xi-popout';

class CalendarComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Calendar,
      props: new CalendarProps(),
      events: new CalendarEvents()
    });
  }
}
CalendarComponent.tag = 'xi-calendar';

/**
 * @todo Remove bug_setInternalPropertyOnObservedChange after completing
 *       {@link https://jira.inmoment.com/browse/DEV-33279 DEV-33279}
 */
class ColorPickerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ColorPicker,
      props: new ColorPickerProps(),
      events: new ColorPickerEvents(),
      bug_setInternalPropertyOnObservedChange: true,
      bug_disableReactStrictMode: true
    });
  }
}
ColorPickerComponent.tag = 'xi-color-picker';

class DatePickerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: DatePicker,
      props: new DatePickerProps(),
      events: new DatePickerEvents()
    });
  }
}
DatePickerComponent.tag = 'xi-date-picker';

class HierarchyPickerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: HierarchyPicker,
      props: new HierarchyPickerProps(),
      events: new HierarchyPickerEvents()
    });
  }
}
HierarchyPickerComponent.tag = 'xi-hierarchy-picker';

class LeftRightPickerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: LeftRightPicker,
      props: new LeftRightPickerProps(),
      events: new LeftRightPickerEvents()
    });
    this.__onItemsChangedParams__ = ['availableItems', 'selectedItems', '_useLegacyFormControl'];
  }
}
LeftRightPickerComponent.tag = 'xi-left-right-picker';

class TreePickerComponent extends KitchenComponent {
  constructor() {
    super({
      Component: TreePicker,
      props: new TreePickerProps(),
      events: new TreePickerEvents()
    });
  }
}
TreePickerComponent.tag = 'xi-tree-picker';

class CloudPillComponent extends KitchenComponent {
  constructor() {
    super({
      Component: CloudPill,
      props: new CloudPillProps(),
      events: new CloudPillEvents()
    });
  }
}
CloudPillComponent.tag = 'xi-cloud-pill';

class CustomPillComponent extends KitchenComponent {
  constructor() {
    super({
      Component: CustomPill,
      props: new CustomPillProps(),
      events: new CustomPillEvents()
    });
  }
}
CustomPillComponent.tag = 'xi-custom-pill';

class RadioComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Radio,
      props: new RadioProps(),
      events: new RadioEvents()
    });
  }
}
RadioComponent.tag = 'xi-radio';

class SearchBoxComponent extends KitchenComponent {
  constructor() {
    super({
      Component: SearchBox,
      props: new SearchBoxProps(),
      events: new SearchBoxEvents()
    });
  }
}
SearchBoxComponent.tag = 'xi-search-box';

class SliderComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Slider,
      props: new SliderProps(),
      events: new SliderEvents(),
      bug_setInternalPropertyOnObservedChange: true
    });
  }
}
SliderComponent.tag = 'xi-slider';

class ToggleSwitchComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ToggleSwitch,
      props: new ToggleSwitchProps(),
      events: new ToggleSwitchEvents()
    });
  }
}
ToggleSwitchComponent.tag = 'xi-toggle-switch';

class ToggleTextComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ToggleText,
      props: new ToggleTextProps(),
      events: new ToggleTextEvents()
    });
  }
}
ToggleTextComponent.tag = 'xi-toggle-text';

class HorizontalTabBarComponent extends KitchenComponent {
  constructor() {
    super({
      Component: HorizontalTabBar,
      props: new HorizontalTabBarProps(),
      events: new HorizontalTabBarEvents()
    });
  }
}
HorizontalTabBarComponent.tag = 'xi-horizontal-tab-bar';

class TabBarComponent extends KitchenComponent {
  constructor() {
    super({
      Component: TabBar,
      props: new TabBarProps(),
      events: new TabBarEvents(),
      bug_disableReactStrictMode: true
    });
  }
}
TabBarComponent.tag = 'xi-tab-bar';

class VerticalTabBarComponent extends KitchenComponent {
  constructor() {
    super({
      Component: VerticalTabBar,
      props: new VerticalTabBarProps(),
      events: new VerticalTabBarEvents()
    });
  }
}
VerticalTabBarComponent.tag = 'xi-vertical-tab-bar';

class TableComponent extends KitchenComponent {
  constructor() {
    super({
      Component: Table,
      props: new TableProps(),
      events: new TableEvents()
    });
  }
}
TableComponent.tag = 'xi-table';

class EditableTextComponent extends KitchenComponent {
  constructor() {
    super({
      Component: EditableText,
      props: new EditableTextProps(),
      events: new EditableTextEvents()
    });
  }
}
EditableTextComponent.tag = 'xi-editable-text';

class InputFieldComponent extends KitchenComponent {
  constructor() {
    super({
      Component: InputField,
      props: new InputFieldProps(),
      events: new InputFieldEvents()
    });
  }
}
InputFieldComponent.tag = 'xi-input-field';

class TextAreaComponent extends KitchenComponent {
  constructor() {
    super({
      Component: TextArea,
      props: new TextAreaProps(),
      events: new TextAreaEvents()
    });
  }
}
TextAreaComponent.tag = 'xi-text-area';

class BasicTileComponent extends KitchenComponent {
  constructor() {
    super({
      Component: BasicTile,
      props: new BasicTileProps(),
      events: new BasicTileEvents()
    });
  }
}
BasicTileComponent.tag = 'xi-basic-tile';

class WaveTileComponent extends KitchenComponent {
  constructor() {
    super({
      Component: WaveTile,
      props: new WaveTileProps(),
      events: new WaveTileEvents()
    });
  }
}
WaveTileComponent.tag = 'xi-wave-tile';

class ToolTipContentComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ToolTipContent,
      props: new ToolTipContentProps(),
      events: new ToolTipContentEvents()
    });
  }
}
ToolTipContentComponent.tag = 'xi-tool-tip-content';

class ToolTipWrapperComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ToolTipWrapper,
      props: new ToolTipProps(),
      events: new ToolTipEvents()
    });
  }
}
ToolTipWrapperComponent.tag = 'xi-tool-tip-wrapper';

class AppTitleBarComponent extends KitchenComponent {
  constructor() {
    super({
      Component: AppTitleBar,
      props: new AppTitleBarProps(),
      events: new AppTitleBarEvents()
    });
  }
}
AppTitleBarComponent.tag = 'xi-app-title-bar';

class ProgressBarComponent extends KitchenComponent {
  constructor() {
    super({
      Component: ProgressBar,
      props: new ProgressBarProps(),
      events: new ProgressBarEvents()
    });
  }
}
ProgressBarComponent.tag = 'xi-progress-bar';

class XIAnimationComponent extends KitchenComponent {
  constructor() {
    super({
      Component: XIAnimation,
      props: new XIAnimationProps(),
      events: new XIAnimationEvents()
    });
  }
}
XIAnimationComponent.tag = 'xi-animation';

class XIAppIconComponent extends KitchenComponent {
  constructor() {
    super({
      Component: XIAppIcon,
      props: new XIAppIconProps(),
      events: new XIAppIconEvents()
    });
  }
}
XIAppIconComponent.tag = 'xi-app-icon';

class XIIconComponent extends KitchenComponent {
  constructor() {
    super({
      Component: XIIcon,
      props: new XIIconProps(),
      events: new XIIconEvents()
    });
  }
}
XIIconComponent.tag = 'xi-icon';

class XISVGComponent extends KitchenComponent {
  constructor() {
    super({
      Component: XISVG,
      props: new XISVGProps(),
      events: new XISVGEvents()
    });
  }
}
XISVGComponent.tag = 'xi-svg';

// IMPORTANT: This file is auto generated.
// Any changes you make to this file here will be overwritten.
watchForEvents();

export { Accordion, AccordionComponent, AccordionEvents, AccordionItem, AccordionItemComponent, AccordionItemEvents, AccordionItemProps, AccordionProps, AlertInlineMain, AlertInlineMainComponent, AlertInlineMainEvents, AlertInlineMainProps, AppSideDisplay, AppSideDisplayComponent, AppSideDisplayEvents, AppSideDisplayProps, AppTitleBar, AppTitleBarComponent, AppTitleBarEvents, AppTitleBarProps, AriaAttributes, Attributes, AudioPlayer, AudioPlayerComponent, AudioPlayerEvents, AudioPlayerProps, BasicTile, BasicTileComponent, BasicTileEvents, BasicTileProps, BigNumber, BigNumberComponent, BigNumberEvents, BigNumberProps, ButtonHTMLAttributes, ButtonHTMLEvents, ButtonObject, CHART_FONT_SIZES, CHART_TYPE, Calendar, CalendarComponent, CalendarEvents, CalendarProps, ChartWrapper, ChartWrapperComponent, ChartWrapperEvents, ChartWrapperProps, Checkbox, CheckboxComponent, CheckboxEvents, CheckboxProps, ClassAttributes, CloudPill, CloudPillComponent, CloudPillEvents, CloudPillProps, ColorPicker, ColorPickerComponent, ColorPickerEvents, ColorPickerProps, Comment$1 as Comment, CommentComponent, CommentEvents, CommentProps, CondensedExperienceCard, CondensedExperienceCardComponent, CondensedExperienceCardEvents, CondensedExperienceCardProps, CustomPill, CustomPillComponent, CustomPillEvents, CustomPillProps, DOMAttributes, DOMEvents, DatePicker, DatePickerComponent, DatePickerEvents, DatePickerProps, DefaultButton, DefaultButtonComponent, DefaultButtonEvents, DefaultButtonProps, DefaultPreselectedCalendarListIds, DivHTMLAttributes, DivHTMLEvents, Drawer, DrawerComponent, DrawerEvents, DrawerProps, Dropdown, DropdownButtons, DropdownComponent, DropdownEvents, DropdownGroup, DropdownObject, DropdownProps, DropdownTextObject, EditableText, EditableTextComponent, EditableTextEvents, EditableTextProps, EnumClouds, EnumErrorStates, EnumLottiesProd, ErrorObject, ExperienceCard, ExperienceCardComponent, ExperienceCardEvents, ExperienceCardProps, ExperienceHeader, ExperienceHeaderComponent, ExperienceHeaderEvents, ExperienceHeaderProps, FAB, FABComponent, FABEvents, FABProps, FilterButton, FilterButtonComponent, FilterButtonEvents, FilterButtonProps, GenericModal, GenericModalComponent, GenericModalEvents, GenericModalProps, HTMLAttributes, HeadingButton, HeadingButtonComponent, HeadingButtonEvents, HeadingButtonProps, HierarchyPicker, HierarchyPickerComponent, HierarchyPickerEvents, HierarchyPickerProps, HighchartsAngularStyle, HighchartsDefaultTheme, HighchartsStyleMerge, HorizontalTabBar, HorizontalTabBarComponent, HorizontalTabBarEvents, HorizontalTabBarProps, IconButton, IconButtonComponent, IconButtonEvents, IconButtonProps, ImageCarousel, ImageCarouselComponent, ImageCarouselEvents, ImageCarouselProps, ImgHTMLAttributes, ImgHTMLEvents, InputField, InputFieldComponent, InputFieldEvents, InputFieldProps, InputHTMLAttributes, InputHTMLEvents, InternalDropdownProps, LeftRightPicker, LeftRightPickerComponent, LeftRightPickerEvents, LeftRightPickerProps, LiHTMLAttributes, LiHTMLEvents, List, ListComponent, ListEvents, ListItem, ListItemComponent, ListItemEvents, ListItemProps, ListProps, MotionDiv, Notes, NotesComponent, NotesContext, NotesEvents, NotesProps, Popout, PopoutComponent, PopoutEvents, PopoutProps, ProgressBar, ProgressBarComponent, ProgressBarEvents, ProgressBarProps, Radio, RadioComponent, RadioEvents, RadioProps, SearchBox, SearchBoxComponent, SearchBoxEvents, SearchBoxProps, SelectAllState, SentimentButton, SentimentButtonComponent, SentimentButtonEvents, SentimentButtonProps, SentimentEnum, SidePanel, SidePanelComponent, SidePanelEvents, SidePanelProps, Slider, SliderComponent, SliderEvents, SliderProps, SnackBar, SnackBarComponent, SnackBarEvents, SnackBarProps, SpanHTMLAttributes, SpanHTMLEvents, SuccessToaster, SuccessToasterComponent, SuccessToasterEvents, SuccessToasterProps, TabBar, TabBarComponent, TabBarEvents, TabBarProps, Table, TableComponent, TableEvents, TableProps, TextArea, TextAreaComponent, TextAreaEvents, TextAreaHTMLAttributes, TextAreaHTMLEvents, TextAreaProps, TextToLink, TextToLinkEvents, TextToLinkProps, TileButton, TileButtonComponent, TileButtonEvents, TileButtonProps, ToggleSwitch, ToggleSwitchComponent, ToggleSwitchEvents, ToggleSwitchProps, ToggleText, ToggleTextComponent, ToggleTextEvents, ToggleTextProps, ToolTipContent, ToolTipContentComponent, ToolTipContentEvents, ToolTipContentProps, ToolTipEvents, ToolTipProps, ToolTipWrapper, ToolTipWrapperComponent, TreePicker, TreePickerComponent, TreePickerEvents, TreePickerProps, TreePickerSelectionMode, UListHTMLAttributes, UListHTMLEvents, VerticalTabBar, VerticalTabBarComponent, VerticalTabBarEvents, VerticalTabBarProps, VideoPlayer, VideoPlayerComponent, VideoPlayerEvents, VideoPlayerProps, WarningPopup, WarningPopupComponent, WarningPopupEvents, WarningPopupProps, WaveTile, WaveTileComponent, WaveTileEvents, WaveTileProps, XIAnimation, XIAnimationComponent, XIAnimationEvents, XIAnimationProps, XIAppIcon, XIAppIconComponent, XIAppIconEvents, XIAppIconProps, XIColorUsage, XIColors, XIGradients, XIIcon, XIIconComponent, XIIconEvents, XIIconProps, XISVG, XISVGComponent, XISVGEvents, XISVGProps, XIShadowsDown, XIShadowsLeft, XIShadowsRight, XIShadowsUp, XITransitions, _AdminOptions, _AlertsColorsOptions, _AllColorsOptions, _AppIconsColorOptions, _AppIconsOptions, _AppIconsWhiteOptions, _BasicTileTitleColorOptions, _BasicTileVisualStateOptions, _BigNumberAlignmentsOptions, _BigNumberColorsOptions, _BigNumberSizesOptions, _ButtonColorsOptions, _ButtonInputType, _ButtonSizesOptions, _ButtonTypesOptions, _CXICOptions, _CXOptions, _CalendarCustomListItemUnitOptions, _CalendarModesOptions, _CalendarOutlinesOptions, _ClockDirectionOptions, _CloudsOptions, _ColorNamesOptions, _ColorsOptions, _ComponentSizesOptions, _CondensedExperienceCardSentimentsOptions, _ContrastsOptions, _DarkColorOptions, _DisabledOptions, _DrawerSizesOptions, _DropdownButtonTypesOptions, _DropdownSelectsOptions, _DropdownVisualStateOptions, _EXOptions, _ErrorStatesOptions, _ExperienceCardCasesOptions, _ExperienceCardIconButtonTypesOptions, _ExperienceCardLogosOptions, _ExperienceCardSentimentsOptions, _FabSizesOptions, _FilterButtonFocus, _FilterButtonTypes, _HorizontalTabBarVisualStateOptions, _IconColorsOptions, _IconsOptions, _InputFieldVisualStateOptions, _InvalidOptions, _LeftRightOptions, _LightColorOptions, _ListSizeOptions, _LottiesOptions, _MXOptions, _MenuButtonSizesOptions, _NamedClockDirectionOptions, _NumberedClockDirectionOptions, _OnOffOptions, _PXOptions, _PrimaryColorOptions, _SVGsOptions, _SentimentsOptions, _ShadowsOptions, _SideOptions, _SnackBarModesOptions, _StatusColorNamesOptions, _StatusStatesOptions, _SystemOptions, _TextAlignmentsOptions, _TextAreaVisualStateOptions, _TextColorsOptions, _TextInputTypeOptions, _TextLevelsOptions, _TextSizesOptions, _TextWeightsOptions, _TextWrapsOptions, _TileButtonCornerControlTypesOptions, _TileButtonSizesOptions, _TileButtonTypesOptions, _TonesOptions, _TreePickerSelectionModeOptions, _WaveTileSizeOptions, _WhiteOptions, _XICloudsOptions, defaultLocalization$1 as defaultLocalization, defaultPreselectedCalendarListOptions, defaultText, fns, isAllColors, isAppIcons, isClockDirection, isClouds, isIcons, isNamedClockDirection, isNumberedClockDirection, isURL, isXIClouds, progressBarWidthDurationMilliseconds, runAllComponentsOutsideAngular, shouldNoteDisable };
